// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { daModuleTips } from './da-extras.js';
// Sprint 62.57.0 Slice E — R-57: the English, for structural parity.
import { enServiceCertificates, enVendorServiceCertificate } from './en-extras.js';
import { enVehicleMonitoring, enRecordLinks } from './en-extras.js';
export const da = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Log ind på din konto',
        email: 'E-mail',
        password: 'Adgangskode',
        sign_in: 'Log ind',
        signing_in: 'Logger ind...',
        mfa_code: 'MFA-kode',
        mfa_placeholder: 'Indtast 6-cifret kode',
        mfa_instruction: 'Indtast den 6-cifrede kode fra din autentificeringsapp.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Bekræft',
        verifying: 'Bekræfter...',
        forgot_password: 'Glemt adgangskode?',
        account_locked: 'Kontoen er låst. Prøv venligst igen senere.',
        account_deactivated: 'Kontoen er deaktiveret. Kontakt din administrator.',
        invalid_mfa: 'Ugyldig MFA-kode. Prøv venligst igen.',
        invalid_credentials: 'Ugyldig e-mail eller adgangskode.',
        login_failed: 'Login mislykkedes',
        session_limit_reached: 'Din konto har nået grænsen for aktive sessioner. Log ud på en anden enhed, eller bed din administrator om at tilbagekalde en eksisterende session, og prøv igen.',
        session_expired: 'Din session er udløbet. Genindlæs venligst siden og prøv igen.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Glemt Adgangskode',
            subtitle: 'Indtast din e-mailadresse, og vi sender dig et link til at nulstille din adgangskode.',
            send: 'Send Nulstillingslink',
            sending: 'Sender...',
            success: 'Hvis den e-mail findes, er der blevet sendt et link til nulstilling af adgangskode.',
            back_to_login: 'Tilbage til Login',
        },
        reset_password: {
            title: 'Nulstil Adgangskode',
            subtitle: 'Indtast din nye adgangskode nedenfor.',
            new_password: 'Ny Adgangskode',
            confirm_password: 'Bekræft Adgangskode',
            reset: 'Nulstil Adgangskode',
            resetting: 'Nulstiller...',
            success: 'Din adgangskode er blevet nulstillet.',
            go_to_login: 'Gå til Login',
            password_mismatch: 'Adgangskoderne stemmer ikke overens.',
            invalid_token: 'Ugyldigt eller manglende nulstillingstoken. Anmod venligst om et nyt link til nulstilling af adgangskode.',
            requirements_title: 'Krav til adgangskode:',
            password_requirements: 'Adgangskoden skal opfylde følgende krav:',
            rules: {
                min_length: 'Mindst 12 tegn',
                uppercase: 'Mindst ét stort bogstav',
                lowercase: 'Mindst ét lille bogstav',
                digit: 'Mindst ét ciffer',
                special: 'Mindst ét specialtegn',
            },
        },
        verify_email: {
            title: 'E-mailbekræftelse',
            verifying: 'Bekræfter din e-mailadresse...',
            success: 'Din e-mail er blevet bekræftet.',
            invalid_token: 'Ugyldigt eller manglende bekræftelsestoken.',
            failed: 'E-mailbekræftelse mislykkedes. Tokenet kan være ugyldigt eller udløbet.',
            go_to_login: 'Gå til Login',
        },
        offline: {
            title: 'Du er offline',
            message: 'Det ser ud til, at du har mistet din internetforbindelse. Kontrollér venligst dit netværk og prøv igen.',
        },
    },
    intake: {
        title: 'Anmodning om Datarettigheder',
        subtitle: 'Indsend en anmodning om datarettigheder til vores organisation',
        fields: {
            full_name: 'Fulde Navn',
            email: 'E-mailadresse',
            phone: 'Telefonnummer (valgfrit)',
            request_type: 'Anmodningstype',
            description: 'Beskriv Din Anmodning',
            preferred_language: 'Foretrukket Sprog',
            consent: 'Jeg giver samtykke til behandling af denne anmodning',
        },
        submit: 'Indsend Anmodning',
        validation: {
            name_required: 'Fulde navn er påkrævet',
            email_required: 'En gyldig e-mailadresse er påkrævet',
            type_required: 'Vælg venligst en anmodningstype',
            description_required: 'Beskriv venligst din anmodning',
            consent_required: 'Du skal give samtykke til behandling for at fortsætte',
        },
        error_title: 'Fejl',
        config_load_error: "Kunne ikke indlæse konfigurationen. Kontrollér venligst URL'en.",
        organization_label: 'Organisation',
        customer_reference: 'Kundereferencenummer',
        country: 'Land / Jurisdiktion',
        preferred_channel: 'Foretrukken svarskanal',
        channels: {
            email: 'E-mail',
            postal: 'Post',
            phone: 'Telefon',
            portal: 'Portal',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Anmodning Modtaget',
            message: 'Din anmodning er blevet registreret. Dit referencenummer er:',
            next_steps: 'Vi vil behandle din anmodning hurtigst muligt i overensstemmelse med gældende lovgivning. Den forventede behandlingstid er 30 arbejdsdage.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Indsigt (DSAR)',
        deletion: 'Sletning',
        correction: 'Berigtigelse / Opdatering',
        objection: 'Indsigelse',
        portability: 'Dataportabilitet',
        restriction: 'Begrænsning af Behandling',
        consent_withdrawal: 'Tilbagekaldelse af samtykke',
        protective_measures: 'Anmod om foranstaltninger til beskyttelse af personoplysninger',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Ny',
        in_review: 'Under Gennemgang',
        waiting_on_requester: 'Afventer Anmoder',
        waiting_on_internal_data: 'Afventer Interne Data',
        approved: 'Godkendt',
        closed: 'Lukket',
        rejected: 'Afvist',
    },
    admin: {
        nav: {
            rights_requests: 'Anmodning om Rettigheder',
            templates: 'Skabeloner',
            response_templates: 'Svarskabeloner',
            data_mapping: 'Datakortlægning',
            governance: 'Styring',
            consent: 'Samtykke',
            assessments: 'Vurderinger',
            classification: 'Klassificering',
            monitoring: 'Overvågning',
            incidents: 'Hændelser',
            onboarding: 'Onboarding',
            connectors: 'Konnektorer',
            vendor_risk: 'Leverandørrisiko',
            deletion_orchestration: 'Sletningsorkestrering',
            discovery: 'Dataopdagelse',
            incident_ops: 'Hændelsesoperationer',
            program_governance: 'Programstyring',
            access_accountability: 'Adgangsansvar',
            consent_properties: 'Samtykkeegenskaber',
            localization: 'Lokalisering',
            regulatory: 'Regulatorisk',
            partner_workspace: 'Partnerarbejdsområde',
            audit_trail: 'Revisionsspor',
            forseti: 'Forseti',
            settings: 'Indstillinger',
            help_center: 'Hjælpecenter',
            dashboard: 'Kontrolpanel',
            platform: 'Platform',
            integrations: 'Integrationer',
            cmp_dsar_sync: 'CMP DSAR-synkronisering',
            cmp_integration: 'CMP-integration',
            purpose_mapping: 'Formålskortlægning',
            webhook_config: 'Webhook-konfiguration',
            bulk_sync: 'Massesynkronisering',
            privacy_scanner: 'Privatlivsscanner',
            privacy_monitoring: 'Privatlivsovervågning',
            real_time_scanner: 'Realtidsscanner',
            compliance_forms: 'Complianceformularer',
            forms_dashboard: 'Formularkontrolpanel',
            form_templates: 'Skabeloner',
            form_catalog: 'Formularkatalog',
            pack_catalog: 'Pakkekatalog',
            org_groups: 'Organisationsgrupper',
            consolidation: 'Konsolidering',
            organizations: 'Organisationer',
            org_list: 'Alle organisationer',
            intake_configs: 'Indtagskonfigurationer',
            task_management: 'Opgavestyring',
            unified_audit: 'Samlet Revision',
            evidence_packs: 'Bevispakker',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Revisionsperioder',
            regulatory_updates: 'Lovgivningsopdateringer',
            departments: 'Afdelinger',
        },
        sidebar_category_data_privacy_rights: 'Databeskyttelse & Rettigheder',
        sidebar_category_vendor_third_party: 'Leverandør & Tredjepartsstyring',
        sidebar_category_risk_assessment: 'Risiko & Vurdering',
        sidebar_category_operations_automation: 'Drift & Automatisering',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Opgave & Arbejdsgang',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP & Webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'RoPA Register',
            data_mapping_classification: 'Klassifikation',
            data_mapping_governance: 'Governance',
            access_live_monitoring: 'Live overvågning',
            access_reviews_investigations: 'Anmeldelser og undersøgelser',
            access_evidence_policies: 'Beviser og politikker',
            classification_taxonomies: 'Taksonomier og etiketter',
            classification_workflow: 'Arbejdsgang',
            classification_insights: 'Indsigt',
            connectors_runtime: 'Runtime',
            connectors_due_diligence: 'Due Diligence',
            connectors_coverage: 'Dækning',
            cp_definitions: 'Definitioner',
            cp_lifecycle: 'Livscyklus',
            cp_quality: 'Kvalitet & Evidens',
            deletion_execution: 'Udførelse',
            deletion_vendors: 'Sælgere',
            deletion_evidence: 'Evidens og integration',
            discovery_rules: 'Regler og skabeloner',
            discovery_scanning: 'Scanning',
            discovery_review: 'Gennemgå arbejdsgang',
            discovery_insights: 'Indsigt',
            governance_oversight: 'Tilsyn',
            governance_reporting: 'Indberetning',
            governance_vendors: 'Leverandører og grænseoverskridende',
            governance_lifecycle: 'Livscyklus',
            incident_ops_dashboards: 'Dashboards',
            incident_ops_sla: 'SLA Konfig',
            incident_ops_playbooks: 'Playbooks',
            incident_ops_vendor_regulator: 'Sælger & Regulator',
            incident_ops_evidence: 'Beviser og gennemgang',
            incidents_workload: 'Arbejdsbyrde',
            incidents_regulatory: 'Regulatorisk',
            incidents_reporting: 'Indberetning',
            localization_content: 'Indhold',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Observerbarhed',
            monitoring_metrics: 'Metrics',
            monitoring_programs: 'Programmer',
            monitoring_tasks: 'Opgaver',
            monitoring_controls: 'Kontrolelementer',
            org_entities: 'Enheder',
            org_config: 'Konfiguration',
            org_frameworks: 'Frameworks & Rollup',
            partner_overview: 'Oversigt',
            partner_partners: 'Partnere',
            partner_delivery: 'Levering',
            partner_support: 'Support',
            platform_identity: 'Identitet og roller',
            platform_access_control: 'Adgangskontrol',
            platform_auth_session: 'Autentificering og session',
            platform_access_reviews: 'Få adgang til anmeldelser',
            platform_files: 'Filer',
            platform_notifications: 'Meddelelser',
            platform_module_config: 'Modul Konfig',
            platform_ops: 'Ops & Logs',
            pg_health: 'Program sundhed',
            pg_maturity: 'Modenhed',
            pg_audit: 'Revision & Evidens',
            pg_training: 'Uddannelse',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Forpligtelser',
            pg_documents: 'Dokumenter',
            vr_overview: 'Oversigt',
            vr_onboarding: 'Onboarding',
            vr_assessment: 'Vurdering',
            vr_oversight: 'Tilsyn',
        },
        tickets: {
            title: 'Rettighedsanmodninger',
            create: 'Opret anmodning',
            filters: 'Filtre',
            search_placeholder: 'Søg i sager...',
            export_csv: 'Eksporter CSV',
            exporting: 'Eksporterer...',
            bulk_export_failed: 'Masseeksport mislykkedes',
            bulk_export: {
                label: 'Eksporter',
                format: {
                    pdf: 'PDF Resumé',
                    zip: 'ZIP (bevispakker pr. sag)',
                    csv: 'CSV',
                },
                warning_over_50: 'Dette vil generere ca. {count} sagspakker - kan tage flere minutter. Fortsætte?',
                blocked_over_500: 'Indsnæv filteret til 500 eller færre sager før eksport af ZIP (nuværende: {count}).',
                success: 'Masseeksport gemt i filbrowseren ({count} tilfælde)',
                failed: 'Masseeksport mislykkedes',
                continue: 'Fortsæt',
                cancel: 'Annuller',
            },
            no_results: 'Ingen anmodninger fundet',
            ticket_id: 'Sags-ID',
            request_type: 'Anmodningstype',
            requester: 'Anmoder',
            source: 'Kilde',
            all_sources: 'Alle Kilder',
            assigned_to: 'Tildelt til',
            created: 'Oprettet',
            load_error: 'Kunne ikke indlæse sager. Prøv venligst igen.',
            case_creation_failed: 'Oprettelse af sag mislykkedes',
            business_unit: 'Forretningsenhed',
            reference: 'Reference',
            sla: {
                title: 'SLA-status',
                on_track: '{days} dage tilbage',
                approaching: 'Nærmer sig deadline',
                overdue: '{days} dage forsinket',
                ack_overdue: '{hours}t over bekræftelsesfristen',
                ack_warning: 'Bekræftelse snart forfalden',
                closed: 'Lukket',
                completed_in: 'Afsluttet på {days} dage',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Svarskabeloner',
            new: 'Ny skabelon',
            edit: 'Rediger skabelon',
            all_types: 'Alle typer',
            all_locales: 'Alle lokaliteter',
            type_access: 'Adgang/DSAR',
            type_deletion: 'Sletning',
            type_correction: 'Rettelse',
            type_objection: 'Indsigelse',
            type_portability: 'Portabilitet',
            type_other: 'Andet',
            type_restriction: 'Begrænsning',
            type_all: 'All',
            locale_en: 'Engelsk',
            locale_vi: 'Vietnamesisk',
            locale_all: 'Begge',
            name: 'Navn',
            request_type: 'Anmodningstype',
            locale: 'Lokalitet',
            subject: 'Emne',
            body: 'Brødtekst',
            name_placeholder: 'Skabelonnavn...',
            subject_placeholder: 'E-mail/brevemne...',
            body_placeholder: 'Skabelonens krop. Brug {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} som pladsholdere.',
            available_placeholders: 'Tilgængelige pladsholdere:',
            create_template: 'Opret skabelon',
            preview: 'Forhåndsvisning',
            close_preview: 'Luk forhåndsvisning',
            default_badge: 'Standard',
            no_templates: 'Ingen skabeloner fundet',
            confirm_delete: 'Slet denne skabelon?',
            load_error: 'Kunne ikke indlæse skabeloner. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette skabelon',
            update_error: 'Kunne ikke opdatere skabelon',
            delete_error: 'Kunne ikke slette skabelon',
            preview_error: 'Kunne ikke forhåndsvise skabelon',
            response_templates_badge: 'Svarskabeloner',
            browse_form_templates: 'Gennemse Overholdelses Formularskabeloner',
            templates_loaded: 'Skabeloner indlæst',
            sample_organization: 'Eksempelorganisation',
        },
        detail: {
            requester_info: 'Anmoderinformation',
            status: 'Status',
            assignment: 'Tildeling',
            notes: 'Noter',
            audit_trail: 'Revisionsspor',
            add_note: 'Tilføj note',
            case_not_found: 'Sag ikke fundet',
            load_error: 'Kunne ikke indlæse sag. Prøv venligst igen.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Kunne ikke tilføje note. Prøv venligst igen.',
            submitted_from: 'Indsendt fra',
            via_cmp: 'via AesirX CMP',
            cmp_ticket: 'CMP-billet:',
            select_template: 'Vælg en skabelon',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Detaljer',
                evidence: 'Dokumentation',
                communications: 'Kommunikationer',
                escalations: 'Eskaleringer',
                audit_trail: 'Revisionsspor',
                fulfillment: 'Opfyldelse',
                response: 'Svar',
                extension: 'Forlængelse',
            },
            requester: {
                name: 'Navn',
                email: 'E-mail',
                phone: 'Telefon',
                language: 'Sprog',
                source: 'Kilde',
                customer_ref: 'Kundereference',
                country: 'Land',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Foretrukken kanal',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'På sporet',
                due_soon: 'Snart forfaldent',
                overdue: 'Forsinket',
                days_remaining: 'd resterende',
                due_date: 'Forfaldsdato',
                breached: 'Overtrådt',
                paused: 'Sat på pause',
                total_paused_days: 'Samlet antal pausedage',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Dage forløbet',
                progress: 'Fremskridt',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Identitetsverifikation',
                verified: 'Verificeret',
                not_verified: 'Ikke verificeret',
                method: 'Metode',
                notes: 'Noter',
                verified_by: 'Verificeret af',
                verified_at: 'Verificeret den',
                warning: 'Identitet ikke yet verificeret.',
                verify_button: 'Verificer identitet',
                select_method: '-- Vælg --',
                methods: {
                    email_confirmation: 'E-mailbekræftelse',
                    id_document: 'ID-dokument',
                    phone_call: 'Telefonopkald',
                    in_person: 'Personlig',
                    other: 'Andet',
                },
            },
            closure: {
                reason: 'Lukningsårsag',
                summary: 'Lukningsresumé',
                summary_placeholder: 'Resumé af hvordan anmodningen blev løst...',
                reason_required: 'Lukningsresumé og årsag er påkrævet',
                response_not_sent: 'Et formelt svar skal sendes til den registrerede, før denne sag kan lukkes',
                reasons: {
                    fulfilled: 'Opfyldt',
                    partially_fulfilled: 'Delvist opfyldt',
                    unfounded: 'Ubegrundet',
                    excessive: 'Overdreven',
                    withdrawn: 'Trukket tilbage',
                    duplicate: 'Duplikér',
                    identity_unverified: 'Identitet ikke verificeret',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Eksport',
            export_pdf: 'Eksporter PDF',
            export_zip: 'Eksporter ZIP',
            case_file_pdf: 'Eksporter PDF (sagsfil)',
            evidence_pack_zip: 'Eksporter ZIP (Evidence Pack)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Flag som brudforårsagende',
                modal: {
                    title: 'Flagsag som brudforårsagende',
                    type_label: 'Overtrædelsestype',
                    type: {
                        misdirected_response: 'Forkert svar',
                        unauthorised_disclosure: 'Uautoriseret videregivelse',
                        identity_mismatch: 'Identitetsmismatch',
                        other: 'Andet',
                    },
                    description: 'Beskrivelse (valgfrit)',
                    description_placeholder: 'Hvad skete der? (max 1000 tegn)',
                    confirm: 'Markér og start anmeldelsesuret',
                    cancel: 'Annuller',
                    flagging: 'Flagger...',
                },
                banner: {
                    title: 'Overtrædelse markeret: {type}',
                    detected_at: 'Registreret ved {timestamp}',
                    filing_deadline: 'Anmeldelsesfrist: om {hours} timer',
                    overdue: 'Anmeldelsesfrist: OVERSKREDET',
                },
                toasts: {
                    flagged: 'Sag markeret som brudforårsagende - anmeldelsesur startet',
                    flag_failed: 'Sagen kunne ikke markeres som brudforårsagende',
                },
            },
            export: {
                generating: 'Genererer... vent venligst',
                success: 'Eksporten er gemt i filbrowseren',
                failed: 'Eksport mislykkedes - tjek revisionslog',
            },
            no_notes: 'Ingen noter endnu',
            assign_placeholder: 'Bruger ID to tildel...',
            reassign: 'Omtildel',
            evidence: {
                checklist_title: 'Dokumentationstjekliste',
                complete_pct: '% fuldført',
                no_checklist: 'Ingen tjekliste elementer',
                attachments: 'Vedhæftninger',
                upload_attachment: 'Upload vedhæftet fil',
                upload_button: 'Upload',
                uploading: 'Uploader...',
                attachment_uploaded: 'Vedhæftet fil uploadet',
                upload_error: 'Upload fejlede',
                category: 'Kategori',
                categories: {
                    identity_proof: 'Identitetsbevis',
                    correspondence: 'Korrespondance',
                    internal_data: 'Interne data',
                    legal: 'Juridisk',
                    other: 'Andet',
                },
                no_attachments: 'Ingen vedhæftninger',
                references_title: 'Dokumentationsreferencer',
                no_references: 'Ingen referencer',
                add_reference: 'Tilføj reference',
                ref_type: 'Type',
                ref_value: 'Værdi',
                ref_desc: 'Beskrivelse',
                ref_value_placeholder: 'URL, e-mail ID, eller reference...',
                ref_desc_placeholder: 'Beskrivelse...',
                ref_types: {
                    url: 'URL',
                    email_id: 'E-mail-ID',
                    crm_record: 'CRM-post',
                    ticket_ref: 'Sagsreference',
                    other: 'Andet',
                },
            },
            communications: {
                title: 'Kommunikationer',
                add: 'Tilføj besked',
                no_records: 'Ingen communications recorded',
                direction: 'Retning',
                channel: 'Kanal',
                visibility: 'Synlighed',
                subject: 'Emne',
                body: 'Brødtekst',
                send: 'Send',
                inbound: 'Indgående',
                outbound: 'Udgående',
                internal: 'Intern',
                external: 'Ekstern',
                tab_all: 'Alle',
                tab_internal: 'Internt',
                tab_external: 'Ekstern',
                subject_placeholder: 'Emne (valgfrit)',
                body_placeholder: 'Beskedtekst...',
                channels: {
                    email: 'E-mail',
                    portal: 'Portal',
                    phone: 'Telefon',
                    postal: 'Post',
                    in_person: 'Personlig',
                },
                apply_template: 'Anvend Skabelon',
                select_template: 'Vælg en skabelon...',
                no_templates: 'Ingen skabeloner tilgængelige',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Skabelon anvendt på svar.',
                template_apply_error: 'Kunne ikke anvende skabelon.',
            },
            escalations: {
                title: 'Eskaleringshistorik',
                no_events: 'Ingen eskalering hændelser',
                tier: 'Niveau',
                threshold: 'Tærskel',
                action: 'Handling',
                target: 'Mål',
                triggered_at: 'Udløst den',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'AesirX CMP-synkronisering',
                sync_status: 'Synkroniseringsstatus',
                last_synced: 'Senest synkroniseret',
                not_synced: 'Ikke synkroniseret',
                resync: 'Gensynkroniser nu',
                syncing: 'Synkroniserer...',
                sync_history: 'Synkroniseringshistorik (seneste 10)',
                direction: 'Retning',
                time: 'Tid',
                retries: 'Genforsøg',
                error_col: 'Fejl',
            },
            audit: {
                filter_event_placeholder: 'Filter efter hændelse type...',
                filter_actor_placeholder: 'Filtrer efter aktør...',
            },
            fulfillment: {
                title: 'Afdelingsopfyldelse',
                assign_departments: 'Tildel afdelinger',
                department: 'Afdeling',
                assignee: 'Ansvarlig',
                deadline: 'Frist',
                evidence_count: 'Dokumentation',
                signoff: 'Godkendelse',
                signed_off: 'Godkendt',
                pending_signoff: 'Afventer',
                start_button: 'Start',
                complete_button: 'Marker som fuldført',
                signoff_button: 'Afmeld',
                no_tasks: 'Ingen opfyldelsesopgaver tildelt endnu.',
                tasks_table: 'Opfyldelsesopgaver',
                progress_label: 'Opfyldelsesfremskridt',
                overdue: 'forsinket',
                assigned_success: 'Afdelingsopfyldelsesopgave tildelt succesfuldt.',
                assign_error: 'Kunne ikke tildele opfyldelsesopgave.',
                signoff_success: 'Afdelingsgodkendelse registreret.',
                signoff_error: 'Kunne ikke registrere godkendelse.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Tilføj bevis',
                evidence_uploaded: 'Bevis uploadet og tilknyttet',
                evidence_upload_error: 'Upload af bevis fejlede',
                suggest_data_stores: 'Foreslå datalagre',
                suggest_error: 'Kunne ikke indlæse datalagringsforslag.',
                suggest_stores_title: 'Foreslåede datalagre',
                suggest_stores_none: 'Ingen datalagre matcher denne sag. Konfigurer systemer i Data Mapping for at forbedre forslag.',
                suggest_stores_add: 'Tilføj til sag',
                suggest_stores_added: 'Datalager tilføjet til sag',
                no_departments_title: 'Ingen afdelinger konfigureret',
                no_departments_body: 'Afdelinger lader dig dirigere DSR-opfyldelsesopgaver til en funktion (Privatliv, Juridisk, HR, IT Ops). Installer eksempelafdelinger, eller bed din administrator om at konfigurere afdelinger i Organisationsindstillingerne.',
                no_departments_cta: 'Installer eksempeldata',
                department_placeholder: 'Afdelingsnavn...',
                assignee_placeholder: 'Bruger-ID til tildeling...',
                assign_button: 'Tildel',
                status_pending: 'Afventende',
                status_in_progress: 'I gang',
                status_completed: 'Fuldført',
                status_verified: 'Verificeret',
                confirm_approve: 'Er du sikker på, at du vil godkende denne forlængelse?',
                confirm_deny: 'Er du sikker på, at du vil afvise denne forlængelse?',
            },
            response: {
                title: 'Svargenerering',
                select_template: 'Svarskabelon',
                choose_template: 'Vælg en skabelon...',
                generate: 'Generer svar',
                preview: 'Svarforhåndsvisning',
                send: 'Send svar',
                delivery_method: {
                    label: 'Leveringsmetode',
                    email: 'E-mail',
                    portal: 'Portal',
                    postal: 'Postpost',
                    in_person: 'Personligt',
                },
                recipient: {
                    email: 'Modtager e-mail',
                    portal: 'Portal bruger-id',
                    address: 'Postadresse',
                },
                auto_generate_btn: 'Generer og send svar',
                auto_generate_success: 'Respons genereret og leveret',
                auto_generate_missing_recipient: 'Angiv venligst en modtager inden afsendelse.',
                auto_fields: 'Automatisk udfyldte felter',
                case_id: 'Sags-ID',
                requester_name: 'Anmoders navn',
                rights_type: 'Rettighedstype',
                history: 'Sendte svar',
                generated_at: 'Genereret',
                confirmation: 'Bekræftelse',
                sent_label: 'Sendt',
                draft_label: 'Kladde',
                generated_success: 'Svar genereret succesfuldt.',
                generate_error: 'Kunne ikke generere svar.',
                sent_success: 'Svar sendt succesfuldt.',
                send_error: 'Kunne ikke sende svar.',
                load_error: 'Kunne ikke indlæse svarskabeloner.',
                pdf_title: 'Svarbrev til registreret person',
                download_pdf: 'Download PDF',
                download_all: 'Download alle',
                download_error: 'Kunne ikke downloade svar',
            },
            extension: {
                title: 'Fristforlængelser',
                acknowledgment_deadline: 'Kvitteringsfrist',
                fulfillment_deadline: 'Opfyldelsesfrist',
                request_extension: 'Anmod om forlængelse',
                reason: 'Begrundelse',
                reason_placeholder: 'Begrundelse for anmodning om ekstra tid...',
                additional_days: 'Yderligere dage',
                submit_request: 'Indsend forlængelsesanmodning',
                history: 'Forlængelseshistorik',
                requested_at: 'Anmodet',
                approve: 'Godkend',
                deny: 'Afvis',
                request_success: 'Forlængelsesanmodning indsendt.',
                request_error: 'Kunne ikke indsende forlængelsesanmodning.',
                approved_success: 'Forlængelse godkendt.',
                denied_success: 'Forlængelse afvist.',
                decision_error: 'Kunne ikke behandle forlængelsesbeslutning.',
                status_pending: 'Afventende',
                status_approved: 'Godkendt',
                status_denied: 'Afvist',
                confirm_approve: 'Er du sikker på, at du vil godkende denne anmodning?',
                confirm_deny: 'Er du sikker på, at du vil afvise denne anmodning?',
                decided_by: 'Besluttet af',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Eksporter CSV',
            exporting: 'Eksporterer...',
            search_placeholder: 'Søg revision detaljer...',
            filter_by_actor: 'Filtrer efter aktør...',
            all_event_types: 'Alle hændelsestyper',
            seq: '#',
            timestamp: 'Tidsstempel',
            event: 'Hændelse',
            case_col: 'Sag',
            actor: 'Aktør',
            summary: 'Resumé',
            no_events: 'Ingen revision hændelser fundet',
            export_success: 'Revisionssporet blev eksporteret.',
            export_error: 'Kunne ikke eksportere revisions-CSV',
            load_error: 'Kunne ikke indlæse revisionsspor. Prøv venligst igen.',
            announce_loaded: 'Revisionsspor indlæst',
            start_date: 'Startdato',
            end_date: 'Slutdato',
            per_page: '{count} / side',
            unknown: 'Ukendt',
            source_filter: {
                label: 'Kilde',
                all: 'Alle',
                module: 'modul',
                forseti: 'Forseti',
                aria_label: 'Filtrer revisionsbegivenheder efter kilde',
            },
            event_types: {
                case_created: 'Sag oprettet',
                status_changed: 'Status ændret',
                case_assigned: 'Sag tildelt',
                note_added: 'Note tilføjet',
                attachment_added: 'Vedhæftning tilføjet',
                attachment_deleted: 'Vedhæftning slettet',
                reference_added: 'Reference tilføjet',
                checklist_updated: 'Tjekliste opdateret',
                identity_verified: 'Identitet verificeret',
                closure_summary_added: 'Lukningsresumé',
                approval_granted: 'Godkendelse givet',
                template_applied: 'Skabelon anvendt',
                export_generated: 'Eksport genereret',
                sla_warning: 'SLA-advarsel',
                sla_overdue: 'SLA forsinket',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Samlet Revisionsspor',
                module_filter: 'Modul',
                framework_filter: 'Rammeværk',
                select_framework: 'Vælg et rammeværk...',
                entity_type_filter: 'Entitetstype',
                action_filter: 'Handling',
                actor_filter: 'Aktør',
                select_actor: 'Vælg en aktør...',
                search_filter: 'Søg',
                search_placeholder: 'Søg i revisionshændelser...',
                filters_heading: 'Filtre',
                date_range: 'Datointerval',
                no_events_found: 'Ingen revisionshændelser fundet.',
                event_count: 'Antal hændelser',
                action: 'Handling',
                actor: 'Aktør',
                date_range_start: 'Startdato for interval',
                date_range_end: 'Slutdato for interval',
                filtering_events: 'Filtrerer revisionshændelser',
                framework_codes: 'Rammeværkskoder',
                entity_type_col: 'Enhedstype',
                aria_table: 'Samlede revisionshændelser',
                page_of: 'Side {page} af {total}',
                entity_types: {
                    user: 'Bruger',
                    case: 'Sag',
                    incident: 'Hændelse',
                    assessment: 'Vurdering',
                    vendor: 'Sælger',
                    consent: 'Samtykke',
                    policy: 'Politik',
                    data_map: 'Datakort',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Opret',
                    update: 'Opdatering',
                    delete: 'Slet',
                    view: 'Visning',
                    export: 'Eksporter',
                    status_change: 'Statusændring',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Revisionsbevispakker',
                assemble_pack: 'Sammensæt Bevispakke',
                framework: 'Rammeværk',
                select_framework: 'Vælg et rammeværk...',
                framework_code_label: 'Rammeværkskode',
                entity_types: 'Entitetstyper',
                select_entity_types: 'Vælg entitetstyper...',
                period: 'Periode',
                period_start: 'Periodestart',
                period_end: 'Periodeslut',
                completeness: 'Fuldstændighed',
                no_packs_found: 'Ingen bevispakker fundet.',
                verify: 'Verificer',
                export_pack: 'Eksporter',
                exporting: 'Eksporterer...',
                pack_assembled: 'Evidenspakke samlet',
                pack_exported: 'Evidenspakke eksporteret',
                aria_table: 'Revisionsevidenspakker',
                aria_export_pack: 'Eksportér pakke',
                export_format: 'Eksportformat',
                export_in_progress: 'Eksport i gang...',
                status_draft: 'Kladde',
                status_assembled: 'Sammensat',
                status_verified: 'Verificeret',
                status_submitted: 'Indsendt',
                entity_type_labels: {
                    user: 'Bruger',
                    case: 'Sag',
                    incident: 'Hændelse',
                    assessment: 'Vurdering',
                    vendor: 'Sælger',
                    consent: 'Samtykke',
                    policy: 'Politik',
                    data_map: 'Datakort',
                },
                template_id: 'Skabelon-ID',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'modul',
                    action: 'Handling',
                    actor_id: 'Skuespiller',
                    actor_role: 'Actor Role',
                    entity_type: 'Enhedstype',
                    entity_id: 'Enheds-id',
                    summary: 'Resumé',
                    timestamp: 'Tidsstempel',
                    record_type: 'Record Type',
                    framework_code: 'Ramme',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Evidenspakke — Rammeværk: {framework}',
                export_header_period: 'Periode: {start} til {end}',
                export_header_completeness: 'Fuldstændighed: {score}%',
                export_header_status: 'Status: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Revisionsperioder',
                create_period: 'Opret Revisionsperiode',
                framework_code_label: 'Rammeværkskode',
                select_framework: 'Vælg et rammeværk...',
                period_label: 'Periodelabel',
                period_start: 'Periodestart',
                period_end: 'Periodeslut',
                start: 'Start',
                end: 'Slut',
                framework: 'Rammeværk',
                retention_days: 'Opbevaringsdage',
                section_period_dates: 'Periodedatoer',
                no_periods_found: 'Ingen revisionsperioder fundet.',
                period_created: 'Revisionsperiode oprettet',
                aria_table: 'Revisionsperioder',
                suggested_boundaries: 'Foreslåede grænser',
                suggested_boundaries_info: 'Foreslåede periodegrænser er afledt af hvert rammeværks regulatoriske rapporteringscyklus. Juster efter behov for din organisation.',
                reporting_cycle: 'Rapporteringscyklus',
                suggested_start: 'Foreslået start',
                suggested_end: 'Foreslået slut',
                suggested_next: 'Foreslået næste periode',
                cycle_monthly: 'Månedligt',
                cycle_quarterly: 'Kvartalsvis',
                cycle_semi_annual: 'Halvårligt',
                cycle_annual: 'Årlig',
                cycle_custom: 'Brugerdefineret',
                period_deleted: 'Revisionsperiode slettet',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Kundereference',
            country: 'Land / Jurisdiktion',
            preferred_channel: 'Foretrukken kanal',
            channels: {
                email: 'E-mail',
                postal: 'Post',
                phone: 'Telefon',
                portal: 'Portal',
            },
            source: 'Kilde',
            sources: {
                admin_manual: 'Admin manual',
                public_form: 'Offentlig Form',
                cmp_gateway: 'CMP Gateway',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Telefon modtaget',
                postal_received: 'Post modtaget',
                email_received: 'E-mail modtaget',
                in_person: 'Personlig',
                legacy_import: 'Ældre import',
            },
            required_fields: 'Anmodning type, navn, og e-mail er påkrævet',
            create_error: 'Kunne ikke oprette sag',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Indstillinger',
            request_types: 'Anmodningstyper',
            request_types_desc: 'Konfigurer hvilke anmodningstyper der er aktiveret for den offentlige indtagsformular.',
            notifications: 'Notifikationer',
            ticket_numbering: 'Sagsnummerering',
            ticket_format: 'Nuværende format',
            ticket_prefix: 'Præfiks',
            ticket_prefix_note: 'ikke konfigurerbart',
            sla_deadlines: 'SLA-frister',
            sla_desc: 'Indstil den maksimale svartid (i kalenderdage) for hver anmodningstype. Sager der nærmer sig tærsklen vil vise advarsler.',
            days: 'dage',
            warning_threshold: 'Advarselstærskel',
            escalation_label: 'Aktivér automatisk eskalering ved SLA-brud',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Gem SLA-konfiguration',
            saving: 'Gemmer ...',
            saved: 'Gemt med succes',
            announce_loaded: 'Indstillinger indlæst',
            email_on_creation: 'E-mailnotifikation ved oprettelse af sag',
            send_confirmation: 'Send bekræftelses-e-mail til anmoderen',
            users: 'Brugere',
            roles: 'Roller',
            api_keys: 'API Taster',
            mfa: 'MFA',
            sessions: 'Sessioner',
            webhooks: 'Webhooks',
            modules: 'Moduler',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Indsigt (DSAR)',
                deletion: 'Sletning',
                correction: 'Berigtigelse',
                portability: 'Portabilitet',
                objection: 'Indsigelse',
                restriction: 'Begrænsning',
                consent_withdrawal: 'Tilbagekaldelse af samtykke',
                protective_measures: 'Beskyttelsesforanstaltninger',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Sektionsopgaver',
            assign_section: 'Tildel sektion',
            section_ref: 'Sektionsreference',
            section_label: 'Sektionsnavn',
            department_id: 'Afdeling',
            assignee_id: 'Ansvarlig',
            mandatory: 'Obligatorisk',
            assign: 'Tildel',
            no_assignments: 'Ingen sektionsopgaver',
            status: 'Status',
            completion: 'Fuldførelse',
            mark_complete: 'Fuldfør',
        },
        consolidation: {
            title: 'Konsolideringsforløb',
            add_child: 'Tilføj underopgave',
            child_task_id: 'Underopgave-id',
            department_id: 'Afdeling',
            link: 'Link',
            overall_completion: 'Samlet fuldførelse',
            departments_complete: 'Afdelinger fuldført',
            overdue: 'Overskredet',
            ready: 'Klar',
            not_ready: 'Ikke klar',
            master_status: 'Overordnet status',
            department_breakdown: 'Afdelingsoversigt',
            department: 'Afdeling',
            status: 'Status',
            completion: 'Fuldførelse',
            due_date: 'Forfaldsdato',
            overdue_flag: 'Overskredet',
            child_tasks: 'Underopgaver',
            task: 'Opgave',
        },
        conflict_detection: {
            title: 'Konfliktregistrering',
            unresolved: 'uløst',
            no_conflicts: 'Ingen konflikter fundet',
            section: 'Sektion',
            field: 'Felt',
            dept_a: 'Afdeling A',
            value_a: 'Værdi A',
            dept_b: 'Afdeling B',
            value_b: 'Værdi B',
            severity: 'Alvorlighed',
            status: 'Status',
            resolved: 'Løst',
            open: 'Åben',
            resolve: 'Løs',
        },
        org_maturity: {
            title: 'Organisatorisk Modenhed',
            departments: 'Afdelinger',
            department_hierarchy: 'Afdelingshierarki',
            create_department: 'Opret Afdeling',
            department_name: 'Afdelingsnavn',
            department_code: 'Kode',
            level: 'Niveau',
            division: 'Division',
            department: 'Afdeling',
            team: 'Hold',
            parent_department: 'Overordnet Afdeling',
            head_user: 'Afdelingsleder',
            no_departments: 'Ingen afdelinger konfigureret.',
            user_mappings: 'Brugertilknytninger',
            map_user: 'Tilknyt Bruger',
            user_id: 'Bruger-ID',
            role: 'Rolle',
            no_users: 'Ingen brugere tilknyttet denne afdeling.',
            remove: 'Fjern',
            framework_assignments: 'Rammetildelinger',
            assign_framework: 'Tildel Ramme',
            entity_id: 'Enheds-ID',
            entity_type: 'Enhedstype',
            framework_id: 'Ramme-ID',
            framework_code: 'Rammekode',
            no_assignments: 'Ingen rammetildelinger.',
            compliance_rollup: 'Overholdelsesopsummering',
            entity_name: 'Enhed',
            total_assessments: 'Samlede Vurderinger',
            completed: 'Afsluttet',
            completion_pct: 'Fuldførelse %',
            open_gaps: 'Åbne Mangler',
            critical_gaps: 'Kritiske',
            compliance_score: 'Score',
            no_rollup: 'Ingen data for overholdelsesopsummering tilgængelig.',
            entity_summary: 'Enhedens Overholdelsesresumé',
            frameworks_assigned: 'Rammer',
            overall_score: 'Samlet Score',
            load_departments_error: 'Kunne ikke indlæse afdelinger',
            load_users_error: 'Kunne ikke indlæse brugere',
            department_created: 'Afdeling oprettet',
            department_created_announce: 'Afdeling oprettet',
            create_department_error: 'Kunne ikke oprette afdeling',
            user_mapped: 'Bruger tilknyttet',
            user_mapped_announce: 'Bruger tilknyttet afdeling',
            map_user_error: 'Kunne ikke tilknytte bruger',
            user_removed: 'Bruger fjernet',
            load_rollup_error: 'Kunne ikke indlæse opsummering',
            load_assignments_error: 'Kunne ikke indlæse tildelinger',
            framework_assigned: 'Rammeværk tildelt',
            framework_assigned_announce: 'Rammeværk tildelt til enhed',
            assign_framework_error: 'Kunne ikke tildele rammeværk',
            assignment_removed: 'Tildeling fjernet',
            enter_entity_id_placeholder: 'Indtast enheds-ID for at se tildelinger',
            entity_type_organization: 'Organisation',
            entity_type_department: 'Afdeling',
        },
        onboarding_maturity: {
            title: 'Onboarding modenhed',
            quick_starts: 'Hurtige starter',
            progress: 'Onboarding fremskridt',
            personas: 'Persona-stier',
            persona_onboarding: 'Persona Onboarding',
            quick_start_scenarios: 'Quick-Start-scenarier',
            start: 'Start',
            restart: 'Genstart',
            estimated_minutes: 'Anslåede minutter',
            steps: 'Trin',
            scenario: 'Scenarie',
            no_scenarios: 'Ingen hurtigstartsscenarier tilgængelige.',
            persona_id: 'Persona ID',
            persona_label: 'Persona',
            role_description: 'Rollebeskrivelse',
            primary_modules: 'Primære moduler',
            recommended_tours: 'Anbefalede ture',
            sample_data_modules: 'Eksempel på datamoduler',
            quick_start_scenario: 'Quick-Start Scenario',
            no_personas: 'Ingen persona-stier tilgængelige.',
            framework_setup: 'Regulatoriske rammer',
            framework_code: 'Rammekode',
            framework_name: 'Rammenavn',
            related_modules: 'Relaterede moduler',
            persona_relevance: 'Relevante personas',
            no_frameworks: 'Der er ingen tilgængelige rammeindstillinger.',
            tier_config: 'Konfiguration af niveaumodul',
            tier: 'Tier',
            available_modules: 'Tilgængelige moduler',
            module_count: 'Modulantal',
            overall_completion: 'Samlet færdiggørelse',
            wizard_completed: 'Wizard afsluttet',
            tours_completed: 'Rundvisninger afsluttet',
            quick_starts_completed: 'Hurtig start fuldført',
            selected_frameworks: 'Udvalgte rammer',
            no_progress: 'Der er ingen tilgængelige data om onboarding.',
            detected_persona: 'Opdaget persona',
            current_tier: 'Nuværende niveau',
            completion_pct: 'Fuldførelse',
            progress_page: {
                loaded: 'Fremskridt indlæst',
                no_progress: 'Ingen onboarding-fremskridtsdata tilgængelige.',
                overall: 'Samlet fremskridt',
                detected_persona: 'Registreret persona',
                tier: 'Niveau',
                modules: 'moduler',
                wizard: 'Guide',
                completed: 'Fuldført',
                pending: 'Afventer',
                tours: 'Rundvisninger',
                quick_starts: 'Hurtigstart',
                frameworks: 'Valgte rammeværker',
                no_frameworks: 'Ingen rammeværker valgt.',
                go_to_wizard: 'Fuldfør opsætningsguiden',
                framework_selection: 'Rammeværksvalg',
                framework_toggle: 'Aktivér eller deaktivér rammeværker for dit complianceprogram.',
                framework_updated: 'Rammeværksvalg opdateret.',
                view_tours: 'Se rundvisninger',
                view_quick_starts: 'Se hurtigstarter',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Vælg en persona for at se anbefalede moduler, rundvisninger og hurtigstart-scenarier.',
                no_personas: 'Ingen persona-stier tilgængelige.',
                modules: 'Moduler',
                tours: 'Rundvisninger',
                quick_start: 'Hurtigstart',
                details: 'Detaljer',
                primary_modules: 'Primære moduler',
                recommended_tours: 'Anbefalede rundvisninger',
                framework_options: 'Regulatoriske rammer',
                no_frameworks: 'Ingen rammeværk-opsætningsmuligheder tilgængelige.',
                no_frameworks_explain: 'Der er endnu ikke installeret nogen regulatoriske rammer for din organisation, så der er intet at konfigurere her.',
                no_frameworks_action: 'Installer en markedspakke for at tilføje de rammer, der gælder for dig',
                start_tour: 'Start',
                no_tours: 'Ingen rundvisninger tilgængelige.',
                help_center: 'Hjælpecenter',
                no_articles: 'Ingen artikler fundet.',
                primary: 'Primær',
                framework_code: 'Rammeværkskode',
                framework_label: 'Etiket',
                region: 'Region',
                category: 'Kategori',
                enabled: 'Aktiveret',
                disabled: 'Deaktiveret',
                tier_modules: 'Niveaumodulkonfiguration',
                tiers_loaded: 'Niveaukonfigurationer indlæst',
                no_tier_config: 'Ingen niveaukonfiguration tilgængelig.',
                view_tours: 'Se modulrundvisninger',
                view_quick_start: 'Start hurtigstart',
                toggle_framework: 'Skift rammeværk',
                framework_updated: 'Rammeværksvalg opdateret.',
                tier_desc_light: 'Grundlæggende privatlivsværktøjer — PDPL-compliance-basis',
                tier_desc_standard: 'Komplet compliancepakke — alle moduler med arbejdsgange',
                tier_desc_enterprise: 'Avanceret governance og automatisering — multi-enhed, grupperapportering',
                persona_highlight: 'Dine moduler',
                aria_go_to_module: '{name} — gå til modul',
                aria_start_tour: 'Start {title}',
                aria_quick_start: 'Hurtig start: {title}',
                aria_help_article: 'Hjælpeartikel: {title}',
                aria_view_framework: '{label}{primary} — se rammedetaljer',
                aria_primary_for_persona: '{name} (primært for denne persona)',
                primary_for_persona_suffix: ' (primært for denne persona)',
                aria_view_in_persona_paths: '{name} — se i Persona Paths',
                progress_of_total: '{completed} af {total} fuldført',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Øv almindelige arbejdsgange med guidede hurtigstart-scenarier.',
                no_scenarios: 'Ingen hurtigstart-scenarier tilgængelige.',
                started: 'Hurtigstart startet',
                status_completed: 'Fuldført',
                status_in_progress: 'I gang',
                status_not_started: 'Ikke startet',
                minutes: 'min',
                steps: 'trin',
                step: 'Trin',
                restart: 'Genstart',
                start: 'Start',
                go_to_module: 'Gå til modul',
                next_step: 'Næste trin',
                complete: 'Fuldfør',
                reset: 'Nulstil',
                step_of: 'af',
                action_navigate: 'Navigér',
                action_form_fill: 'Udfyld formular',
                action_review: 'Gennemgå',
                action_confirm: 'Bekræft',
                step_changed: 'Trin {current} af {total}: {title}',
                completed_message: 'Hurtigstart fuldført!',
                reset_message: 'Hurtigstarten er nulstillet.',
            },
            persona_p_vn_01_label: 'Enterprise DPO',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Afdelingsejer',
            persona_p_vn_02_description: 'Afdelingsleder med ansvar for sektionsrapportering',
            persona_p_vn_03_label: 'Overholdelse af e-handel',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Leder for datastyring',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Cybersikkerhed og juridiske ops',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AI Leder for ledelse',
            persona_p_vn_07_description: 'Lead tilsyn med AI systemstyring, risikovurderinger og klassificering',
            persona_p_vn_08_label: 'Grænseoverskridende & Sælger Gov',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Breach Response Lead',
            persona_p_vn_09_description: 'Lead koordinering af brudmeddelelse og hændelsesrespons',
            persona_p_vn_10_label: 'Leder for intern revision',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'HR- & medarbejderdata-ansvarlig',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Compliance-leder for multinational datterselskab',
            persona_p_vn_12_description: 'DPO i vietnamesisk datterselskab af udenlandsk virksomhed med behov for lokalt compliance-lag',
            persona_p_vn_13_label: 'Compliance-leder for uddannelse',
            persona_p_vn_13_description: 'Compliance-leder på udenlandsk skole der håndterer databeskyttelse for elever og forældre',
            qs_hr_consent_title: 'Registrer medarbejdersamtykker',
            qs_hr_consent_description: 'Gennemgang af registrering af medarbejdersamtykke og generering af dækningsrapport.',
            qs_subsidiary_dpia_title: 'Opret DPIA-vurdering for datterselskab',
            qs_subsidiary_dpia_description: 'Gennemgang af oprettelse af DPIA for en databehandlingsaktivitet i datterselskab.',
            qs_student_consent_title: 'Opsæt indsamling af elevsamtykke',
            qs_student_consent_description: 'Gennemgang af oprettelse af forældresamtykkepolitikker for elevdata.',
            persona_p_vn_14_label: 'Ekstern juridisk rådgiver',
            persona_p_vn_14_description: 'Senior advokatfirmapartner rådgiver flere klientorganisationer om PDPL overholdelse',
            persona_p_vn_15_label: 'Advokatfirmaets driftsleder',
            persona_p_vn_15_description: 'Kundeengagementkoordinator, der administrerer leveringssporing og deadlinekoordinering',
            persona_p_vn_16_label: 'Virksomhedsejer / CEO',
            persona_p_vn_16_description: 'CEO eller GM som juridisk er dataansvarlig under PDPL, ansvarlig for overholdelsesinvestering',
            qs_dpia_filing_title: 'Gennemfør din første DPIA arkivering',
            qs_dept_compliance_title: 'Indsend dit første afsnit om overholdelse af afdelingen',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Opsæt teleabonnentdatahåndtering',
            qs_data_flow_title: 'Kortlæg dit første dataflow',
            qs_incident_response_title: 'Konfigurer hændelsesrespons arbejdsgang',
            qs_ai_risk_title: 'Kør en AI systemrisikovurdering',
            qs_cross_border_title: 'Vurder en grænseoverskridende dataoverførsel',
            qs_breach_notification_title: 'Simuler en arbejdsgang for notifikation om brud',
            qs_audit_trail_title: 'Gennemgå et overholdelsesrevisionsspor',
            qs_legal_advisor_portfolio_title: 'Gennemgå multi-klient compliance-portefølje',
            qs_legal_advisor_portfolio_description: 'Gennemgå overholdelsesstatus på tværs af flere kundeorganisationer.',
            qs_law_firm_ops_title: 'Spor klientens leveringsstatus',
            qs_law_firm_ops_description: 'Overvåg fremskridt i rådgivningsleverancer og klientengagement.',
            qs_ceo_dashboard_title: 'Se kontrolpanelet for overholdelse af ledelsen',
            qs_ceo_dashboard_description: 'Gennemgå overholdelsesstatus og investeringsmålinger på højt niveau.',
        },
        rights_requests: {
            response_templates: {
                title: 'Svarskabeloner',
                subtitle: 'Administrer DSR-svarbrevsskabelonerne, som ResponseGenerator bruger til at udarbejde svar til de registrerede.',
                create_btn: 'Ny skabelon',
                edit_btn: 'Rediger skabelon',
                filter_right_type: 'Filtrer efter højre type',
                filter_locale: 'Filtrer efter lokalitet',
                filter_status: 'Filtrer efter status',
                all_right_types: 'Alle rigtige typer',
                any_right_type: 'Enhver (gælder alle typer)',
                all_locales: 'Alle lokaliteter',
                all_statuses: 'Alle',
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                empty_state: 'Ingen skabeloner matcher de aktuelle filtre.',
                columns: {
                    template_id: 'Skabelon-id',
                    outcome: 'Udfald',
                    locale: 'Lokalitet',
                    right_type: 'Højre type',
                    is_active: 'Status',
                    last_updated: 'Sidst opdateret',
                    actions: 'Handlinger',
                },
                actions: {
                    edit: 'Rediger',
                    clone: 'Klon',
                    deactivate: 'Deaktiver',
                    reactivate: 'Genaktiver',
                    preview: 'Forhåndsvisning',
                },
                form: {
                    template_id: 'Skabelon-id',
                    template_id_help: 'Små bogstaver, f.eks. dsr_access_fulfilled_vi',
                    outcome: 'Udfald',
                    locale: 'Lokalitet',
                    right_type: 'Højre type',
                    subject: 'Emne',
                    body: 'Krop skabelon',
                    body_help: 'Brug pladsholdere som {case_reference}, {data_subject_name}, {fulfilled_at}. Minimum 15 linjer for flersektionsbogstaver.',
                    is_active: 'Aktiv',
                    official_form_id: 'Officielt formular-id (valgfrit)',
                },
                outcomes: {
                    fulfilled: 'Opfyldt',
                    partially_fulfilled: 'Delvist opfyldt',
                    denied: 'Afvist',
                },
                clone_modal_title: 'Klon skabelon',
                clone_target_locale: 'Mållokalitet',
                clone_help: 'En ny skabelon vil blive oprettet i mållokaliteten ved at bruge denne skabelon som udgangspunkt.',
                preview_modal_title: 'Forhåndsvisning af skabelon',
                preview_note: 'Forhåndsvisning bruger demo-case DSR-DEMO-0020 pladsholdere. Reel levering erstatter live case-data.',
                preview_unavailable: 'Forhåndsvisning kræver en eksisterende sag. Vedhæft denne skabelon til en sag, og brug handlingen Udkast til svar på sagsdetaljesiden.',
                deactivate_confirm: 'Vil du deaktivere denne skabelon? Den vil fortsat gælde for eksisterende sager, der henviser til den.',
                toasts: {
                    created: 'Skabelon oprettet',
                    updated: 'Skabelonen er opdateret',
                    deactivated: 'Skabelon deaktiveret',
                    reactivated: 'Skabelonen genaktiveret',
                    cloned: 'Skabelon klonet',
                    load_error: 'Kunne ikke indlæse skabeloner',
                    save_error: 'Skabelonen kunne ikke gemmes',
                    clone_error: 'Kunne ikke klone skabelon',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Afdelinger',
                subtitle: 'Administrer de afdelinger, der ruter DSR-sager, DPIA opgaver og compliance-arbejdsgange.',
                create_btn: 'Ny afdeling',
                edit_btn: 'Rediger afdeling',
                columns: {
                    name: 'Navn',
                    description: 'Beskrivelse',
                    active_cases: 'Aktive sager',
                    members: 'Medlemmer',
                    status: 'Status',
                    actions: 'Handlinger',
                },
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                form: {
                    name: 'Navn',
                    description: 'Beskrivelse',
                },
                actions: {
                    edit: 'Rediger',
                    deactivate: 'Deaktiver',
                    reactivate: 'Genaktiver',
                    delete: 'Slet',
                    manage_members: 'Administrer medlemmer',
                },
                delete_modal_title: 'Slet afdeling',
                delete_warning: 'Denne afdeling har stadig aktive sager. Vælg en afdeling, som de skal gentildeles til før sletning.',
                delete_no_cases: 'Denne afdeling har ingen aktive sager og kan slettes sikkert.',
                reassign_prompt: 'Gentildel aktive sager til:',
                reassign_none_available: 'Ingen andre aktive afdelinger tilgængelige. Opret en anden afdeling først.',
                member_section_title: 'Medlemmer',
                add_member: 'Tilføj medlem',
                remove_member: 'Fjern',
                member_role: 'Rolle',
                no_members: 'Ingen medlemmer knyttet til denne afdeling.',
                toasts: {
                    created: 'Afdeling oprettet',
                    updated: 'Afdeling opdateret',
                    deactivated: 'Afdeling deaktiveret',
                    reactivated: 'Afdeling genaktiveret',
                    deleted: 'Afdeling slettet',
                    reassigned_count: '{count} sag(er) omfordelt',
                    member_added: 'Medlem tilføjet',
                    member_removed: 'Medlem fjernet',
                    load_error: 'Kunne ikke indlæse afdelinger',
                    save_error: 'Afdelingen kunne ikke gemmes',
                    delete_error: 'Afdelingen kunne ikke slettes',
                },
            },
            retention_policies: {
                title: 'Opbevaringspolitikker',
                subtitle: 'Konfigurer per-target-type opbevaring vinduer. Identitetsbekræftelsesdokumenter bruger dette til at stemple retention_expires_at på fremtidige uploads.',
                create_btn: 'Ny politik',
                edit_btn: 'Rediger politik',
                columns: {
                    target_type: 'Måltype',
                    retention_days: 'Retention (dage)',
                    owner: 'Forsikringsejer',
                    effective_from: 'Gælder fra',
                    last_reviewed: 'Sidst anmeldt',
                    status: 'Status',
                    actions: 'Handlinger',
                },
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                form: {
                    target_type: 'Måltype',
                    target_type_help: 'Hvilken datakategori opbevaringsvinduet gælder for. I øjeblikket understøttes kun DSR-identitetsverifikationsdokumenter.',
                    retention_days: 'Retentionsperiode (dage)',
                    retention_days_help: 'Mellem 1 og 3650 dage (maks. 10 år).',
                    owner: 'Forsikringsejer',
                    owner_help: 'Normalt DPO. Ansvarlig for beslutningen om opbevaring under DG-2.',
                    effective_from: 'Gælder fra',
                },
                target_types: {
                    dsr_identity_verification_document: 'DSR identitetsbekræftende dokumenter',
                },
                deactivate_notice: 'Deaktivering påvirker ikke allerede stemplede vedhæftede filer – kun fremtidige uploads vil ikke længere modtage et opbevaringsvindue.',
                deactivate_confirm: 'Vil du deaktivere denne opbevaringspolitik?',
                toasts: {
                    created: 'Opbevaringspolitik oprettet',
                    updated: 'Opbevaringspolitik opdateret',
                    deactivated: 'Opbevaringspolitik deaktiveret',
                    load_error: 'Opbevaringspolitikker kunne ikke indlæses',
                    save_error: 'Opbevaringspolitikken kunne ikke gemmes',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Systemer',
            categories: 'Datakategorier',
            registry: 'Dataregister',
            purposes: 'Behandlingsformål',
            retention: 'Opbevaring',
            flows: 'Datastrømme',
            activities: 'Behandlingsaktiviteter',
            evidence: 'Dokumentation',
            exports: 'Eksporter',
            attestation: 'Attestationscyklusser',
            governance: 'Governance',
            review: 'Gennemgangsopgaver',
            inventory: 'Inventar',
            exceptions: 'Undtagelser',
            history: 'Historik',
            ropa_completeness: 'ROPA Fuldstændighed',
        },
        ropa_completeness_autofix: {
            ask_button: 'Spørg Forseti',
            drafting: 'Udarbejdelse...',
            accept: 'Accepter',
            reject: 'Afvis',
            accepted: 'Accepteret — ansøg via aktivitetseditoren.',
            rejected: 'Afvist.',
            needs_human_input: 'Forseti har brug for mere aktivitetskontekst (f.eks. et dokumenteret formål), før han udarbejder dette felt.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'ROPA fuldstændighedsrapport',
            subtitle: 'Hygiejnerevision pr. felt af dine registreringer af behandlingsaktiviteter, omfattet af lovgivningsmæssige rammer.',
            framework_label: 'Ramme',
            kpi_total: 'Aktiviteter scoret',
            kpi_critical: 'Kritisk',
            kpi_high: 'Høj',
            kpi_conditional: 'Betinget',
            field_chart_heading: 'Manglende felter efter frekvens',
            drilldown_heading: 'Aktiviteter med huller',
            col_activity: 'Aktivitet',
            col_field: 'Felt',
            col_severity: 'Sværhedsgrad',
            col_condition: 'Tilstand',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Kritisk',
            severity_high: 'Høj',
            severity_conditional: 'Betinget',
            severity_informational: 'Oplysende',
            include_informational: 'Inkluder information',
            export_csv: 'Download CSV',
            exporting: 'Eksporterer...',
            loading: 'Indlæser⬦',
            empty_state: 'Alle aktive aktiviteter har fuldstændige ROPA-registreringer for denne ramme.',
            error_fetch: 'Fuldstændighedsrapporten kunne ikke indlæses.',
            error_unsupported_framework: 'Denne ramme understøttes ikke.',
            error_framework_not_installed: 'Denne ramme er ikke installeret for din organisation.',
            no_misses_for_field: 'Ingen aktiviteter mangler dette felt.',
            framework_PDPL: 'PDPL (lov 91/2025 + dekret 356)',
            framework_GDPR: 'GDPR Artikel 30',
            framework_DECREE_165: 'Dekret 165 (Vietnams datalov)',
            framework_ISO_27701: 'ISO 27701 (information)',
        },
        registry: {
            // Sprint 64.72.0 — kolonneoverskrifter for dataregisterets eksport.
            export_col: {
                registry_item_id: 'Register-post-ID',
                name: 'Navn',
                description: 'Beskrivelse',
                field_identifiers: 'Feltidentifikatorer',
                category_id: 'Kategori-ID',
                category_name: 'Kategorinavn',
                data_type: 'Datatype',
                effective_sensitivity: 'Gældende følsomhed',
                sensitivity_override: 'Tilsidesat følsomhed',
                data_subjects: 'Registrerede',
                volume_band: 'Mængdeinterval',
                source_system_ids: 'Kildesystem-ID’er',
                processing_activity_ids: 'Behandlingsaktivitets-ID’er',
                retention_policy_id: 'Opbevaringspolitik-ID',
                effective_retention_policy_id: 'Gældende opbevaringspolitik-ID',
                purposes: 'Formål',
                collection_method: 'Indsamlingsmetode',
                legal_basis: 'Retsgrundlag',
                data_tier: 'Dataniveau',
                data_tier_source: 'Kilde til dataniveau',
                storage_locations: 'Opbevaringssteder',
                cross_border: 'Grænseoverskridende',
                status: 'Status',
                source_form_submission_id: 'Kildeformular-indsendelses-ID',
                source_form_template_id: 'Kildeformular-skabelon-ID',
                last_reviewed_at: 'Senest gennemgået',
                next_review_at: 'Næste gennemgang',
                created_at: 'Oprettet',
                updated_at: 'Opdateret',
                data_element: 'Dataelement',
                data_category: 'Datakategori',
                systems: 'Systemer',
                retention_policy: 'Opbevaringspolitik',
                name_of_processing: 'Behandlingens navn',
                categories_of_personal_data: 'Kategorier af personoplysninger',
                categories_of_data_subjects: 'Kategorier af registrerede',
                recipients: 'Modtagere',
                third_country_transfers: 'Tredjelandsoverførsler',
                retention_period: 'Opbevaringsperiode',
                doc_title: 'Dataregister',
                doc_generated: 'Eksport genereret {timestamp}',
                doc_item_count: '{count} registerpost(er)',
                doc_template_full: 'Fuld',
            },
            export_vocab: {
                data_type: {
                    text: 'Tekst',
                    number: 'Tal',
                    date: 'Dato',
                    datetime: 'Dato og klokkeslæt',
                    boolean: 'Ja/nej',
                    binary: 'Binær',
                    structured: 'Struktureret',
                    file: 'Fil',
                    identifier: 'Identifikator',
                    geolocation: 'Geoplacering',
                },
                collection_method: {
                    direct_from_subject: 'Direkte fra den registrerede',
                    third_party: 'Fra tredjepart',
                    derived: 'Udledt af andre data',
                    observed: 'Observeret',
                    inferred: 'Sluttet',
                    public_source: 'Offentlig kilde',
                },
                data_subject_type: {
                    customer: 'Kunde',
                    employee: 'Medarbejder',
                    contractor: 'Entreprenør',
                    prospect: 'Potentiel kunde',
                    minor: 'Mindreårig',
                    patient: 'Patient',
                    student: 'Studerende',
                    beneficiary: 'Begunstiget',
                    vendor_contact: 'Leverandørkontakt',
                    website_visitor: 'Hjemmesidebesøgende',
                    other: 'Andet',
                },
                volume_band: {
                    lt_1k: 'Færre end 1.000 poster',
                    '1k_to_10k': '1.000 til 10.000 poster',
                    '10k_to_100k': '10.000 til 100.000 poster',
                    '100k_to_1m': '100.000 til 1 million poster',
                    gt_1m: 'Mere end 1 million poster',
                    unknown: 'Ukendt',
                },
                tier_source: {
                    assessed_and_approved: 'Vurderet og godkendt',
                    manual_legacy: 'Overtaget fra en ældre værdi',
                    legacy_platform_field: 'Nedarvet platformsværdi',
                    scheme_scoped_only: 'Vurderet efter ordningsspecifikke niveauer — ingen enkelt kode dækker flere lovbestemte niveauskalaer',
                },
            },
            page: {
                title: 'Dataregister',
                subtitle: 'Personlige dataelementer, som din organisation har, organiseret under Datakategorier.',
                empty_state: 'Ingen registreringselementer endnu. Tilføj en vare manuelt eller importer fra et regulatorisk register.',
                empty_filter_state: 'Ingen varer matcher dine filtre.',
            },
            filter: {
                category: 'Kategori',
                sensitivity: 'Følsomhed',
                data_subject: 'Datasubjekt',
                status: 'Status',
                data_tier: 'Datalag',
                cross_border: 'Grænseoverskridende',
                has_owner: 'Har ejer',
                has_form_source: 'Importeret fra formular',
                search_placeholder: 'Søg efter navn, beskrivelse eller felt-id...',
                clear: 'Ryd filtre',
                option_all: 'Alle',
            },
            col: {
                name: 'Navn',
                category: 'Kategori',
                sensitivity: 'Følsomhed',
                data_subjects: 'Datasubjekter',
                systems: 'Systemer',
                activities: 'Aktiviteter',
                owner: 'Ejer',
                retention: 'Fastholdelse',
                data_tier: 'Datalag',
                status: 'Status',
                last_reviewed: 'Sidst anmeldt',
                actions: 'Handlinger',
            },
            action: {
                add_manual: 'Tilføj element',
                add_from_form: 'Tilføj fra regulatorisk register',
                import_history: 'Importer tidligere indsendelser',
                export: 'Eksporter',
                archive: 'Arkiv',
                restore: 'Gendan',
                edit: 'Rediger',
                assign_owner: 'Tildel ejer',
                attach_evidence: 'Vedhæft beviser',
                open_lineage: 'Åben slægt',
            },
            modal_editor: {
                section_identity: 'Identitet',
                section_classification: 'Klassifikation',
                section_subjects: 'Emner og bind',
                section_location: 'Placering & Overførsel',
                section_systems: 'Systemer og aktiviteter',
                section_retention: 'Opbevaring & juridisk',
                section_governance: 'Governance',
                section_notes: 'Noter',
                hint_inherited: '(arvet fra kategori)',
                hint_overridden: '(tilsidesat)',
                placeholder_field_identifiers: 'f.eks. kunder.primary_email, hris.employee_id',
                validation_name_required: 'Navn er påkrævet',
                validation_category_required: 'Kategori er påkrævet',
                validation_data_subjects_required: 'Der kræves mindst én registreret type',
                label_data_type: 'Datatype',
                label_field_identifiers: "Felt-id'er (kommasepareret)",
                label_data_subject_types: 'Datasubjekttyper',
                label_volume_band: 'Volumen bånd',
                label_storage_locations: 'Lagerplaceringer (kommasepareret)',
                placeholder_storage_locations: 'ISO-3166 koder eller fri tekst (f.eks. VN, Hetzner EU)',
                label_cross_border: 'Grænseoverskridende overførsel',
                label_source_systems: 'Kildesystemer',
                label_processing_activities: 'Bearbejdningsaktiviteter',
                label_collection_method: 'Indsamlingsmetode',
                label_purposes: 'Formål',
                hint_multi_select: 'Hold Cmd/Ctrl nede for at vælge flere.',
                option_inherit_category: '— (arve fra kategori)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Importér forhåndsvisning',
                label_to_create: 'At skabe',
                label_to_update: 'At opdatere',
                label_skipped_archived: 'Sprang over (arkiveret)',
                label_conflicts: 'Konflikter',
                commit: 'Importer',
                cancel: 'Annuller',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Kategori',
                section_systems: 'Kildesystemer',
                section_activities: 'Bearbejdningsaktiviteter',
                section_flows: 'Datastrømme',
                section_retention: 'Effektiv fastholdelse',
                section_governance: 'Governance',
                section_evidence: 'Beviser',
                section_audit: 'Seneste revisionsspor',
                section_source_form: 'Indsendelse af kildeformular',
                badge_inherited: 'Nedarvet',
                badge_overridden: 'Tilsidesat',
            },
            toast: {
                created: 'Registreringselement oprettet.',
                updated: 'Registreringselement opdateret.',
                archived: 'Registreringselement arkiveret.',
                imported: '{count} elementer er importeret.',
                conflict_warning: '{count} felt(er) beholdt dine redigeringer — se konfliktliste.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Systemoversigt',
            new: 'Nyt System',
            edit: 'Rediger System',
            name: 'Navn',
            owner: 'Ejer',
            criticality: 'Kritikalitet',
            dpa_status: 'DPA-status',
            location: 'Placering',
            system_type: 'Systemtype',
            metadata: 'Metadata (JSON)',
            department: 'Afdeling',
            hosting_location: 'Hostingplacering',
            hosting_provider: 'Hostingudbyder',
            data_residency_country: 'Dataresidensland',
            hosting: 'Hosting',
            no_systems: 'Ingen systemer fundet',
            confirm_archive: 'Arkiver system "{name}"?',
            count: 'systemer',
            count_one: 'system',
            type_internal: 'Intern',
            type_external: 'Ekstern',
            type_cloud_service: 'Skytjeneste',
            type_on_premise: 'Lokalt',
            type_hybrid: 'Hybrid',
            status_active: 'Aktiv',
            status_decommissioning: 'Under Nedlukning',
            status_decommissioned: 'Nedlukket',
            status_planned: 'Planlagt',
            dpa_not_required: 'Ikke Påkrævet',
            dpa_required_missing: 'Påkrævet Mangler',
            dpa_in_progress: 'I Gang',
            dpa_signed: 'Underskrevet',
            all_criticalities: 'Alle Kritikaliteter',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Datakategorier',
            new: 'Ny Kategori',
            edit: 'Rediger Kategori',
            name: 'Navn',
            sensitivity: 'Følsomhed',
            tags: 'Tags',
            used_in: 'Anvendt I',
            examples: 'Eksempler',
            examples_hint: 'Eksempler (kommasepareret)',
            default: 'Standard',
            all_sensitivities: 'Alle Følsomheder',
            all_statuses: 'Alle Statusser',
            no_categories: 'Ingen kategorier fundet',
            confirm_delete: 'Slet kategori "{name}"?',
            count: 'kategorier',
            count_one: 'kategori',
            status_draft: 'Kladde',
            status_proposed: 'Foreslået',
            status_under_review: 'Under gennemgang',
            status_approved: 'Godkendt',
            status_deprecated: 'Forældet',
            status_updated: 'Kategoristatus opdateret',
            propose: 'Foreslå',
            approve: 'Godkend',
            deprecate: 'Foræld',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Behandlingsformål',
            new: 'Nyt Formål',
            edit: 'Rediger Formål',
            name: 'Navn',
            legal_basis: 'Retsgrundlag',
            retention_period: 'Opbevaringsperiode (dage)',
            justification: 'Begrundelse for Legitim Interesse',
            legal_basis_detail: 'Retsgrundlagsdetalje',
            all_legal_bases: 'Alle Retsgrundlag',
            no_purposes: 'Ingen formål fundet',
            confirm_delete: 'Slet "{name}"?',
            default: 'Standard',
            count: '{count} formål',
            count_one: '{count} formål',
        },
        retention: {
            title: 'Opbevaringspolitikker',
            new: 'Ny Politik',
            edit: 'Rediger Politik',
            name: 'Navn',
            retention_days: 'Opbevaringsdage',
            disposal_method: 'Bortskaffelsesmetode',
            data_categories: 'Datakategorier',
            activities: 'Aktiviteter',
            review_frequency: 'Gennemgangsfrekvens',
            review_frequency_days: 'Gennemgangsfrekvens (dage)',
            no_policies: 'Ingen opbevaringspolitikker fundet',
            confirm_delete: 'Slet "{name}"?',
            count: '{count} politikker',
            count_one: '{count} politik',
            days: 'dage',
            disposal_deletion: 'Sletning',
            disposal_anonymization: 'Anonymisering',
            disposal_archival: 'Arkivering',
            review_overdue: 'Gennemgang forsinket',
            review_status: 'Gennemgangsstatus',
            review_ok: 'På sporet',
            department: 'Afdeling',
            all_departments: 'Alle afdelinger',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Udsigt over strømme',
            view_list: 'Liste',
            view_map: 'Kort',
            title: 'Datastrømme',
            new: 'Ny Strøm',
            edit: 'Rediger Strøm',
            name: 'Navn',
            from_system: 'Fra System',
            to_system: 'Til System',
            flow_type: 'Strømtype',
            cross_border: 'Grænseoverskridende',
            risk_level: 'Risikoniveau',
            mechanism: 'Mekanisme',
            transfer_mechanism: 'Overførselsmekanisme',
            frequency: 'Frekvens',
            source_country: 'Afsenderland',
            destination_country: 'Modtagerland',
            transfer_safeguard: 'Overførselssikkerhed',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Alle Risikoniveauer',
            no_flows: 'Ingen datastrømme fundet',
            confirm_archive: 'Arkiver "{name}"?',
            count: '{count} strømme',
            count_one: '{count} strøm',
            status_active: 'Aktiv',
            status_inactive: 'Inaktiv',
            status_under_review: 'Under Gennemgang',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Filoverførsel',
            mechanism_database_replication: 'Databasereplikering',
            mechanism_manual: 'Manuel',
            mechanism_email: 'E-mail',
            mechanism_other: 'Andet',
            frequency_real_time: 'Realtid',
            frequency_hourly: 'Hver Time',
            frequency_daily: 'Daglig',
            frequency_weekly: 'Ugentlig',
            frequency_monthly: 'Månedlig',
            frequency_on_demand: 'Efter Behov',
            frequency_one_time: 'Engangs',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Jurisdiktion',
        },
        activities: {
            title: 'Behandlingsaktiviteter',
            new: 'Ny Aktivitet',
            edit: 'Rediger Aktivitet',
            activity_title: 'Titel',
            category: 'Kategori',
            legal_basis: 'Retsgrundlag',
            dpa_required: 'DPIA Required',
            ropa_generated: 'RoPA Genereret',
            version: 'version',
            generate_ropa: 'Generer RoPA',
            controller: 'Dataansvarlig',
            controller_name: 'Dataansvarligs Navn',
            controller_contact: 'Dataansvarligs Kontakt',
            legal_basis_detail: 'Retsgrundlagsdetalje',
            organization_info: 'Organization Information',
            org_address: 'Organisationsadresse',
            tax_code: 'Skattekode',
            contact_person: 'Kontaktperson',
            contact_email: 'Kontakt-e-mail',
            contact_phone: 'Kontakttelefon',
            select_entity: 'Vælg...',
            no_entities: 'Ingen varer fundet',
            dpo_name: 'DPO-navn',
            dpo_contact: 'DPO-kontakt',
            processor_name: 'Databehandlernavn',
            processor_contact: 'Databehandlerkontakt',
            data_subjects: 'Registrerede',
            data_subjects_hint: 'Registrerede (kommasepareret)',
            recipients: 'Modtagere',
            recipients_hint: 'Modtagere (kommasepareret)',
            security_measures: 'Sikkerhedsforanstaltninger',
            dpia_reference: 'DPIA-reference',
            no_activities: 'Ingen behandlingsaktiviteter fundet',
            confirm_archive: 'Arkiver "{name}"?',
            count: '{count} aktiviteter',
            count_one: '{count} aktivitet',
            ropa_success: 'RoPA-post genereret. Gå til Eksporter for at downloade den.',
            ropa_error: 'Kunne ikke generere RoPA',
            required_label: 'Påkrævet',
            status_draft: 'Udkast',
            status_active: 'Aktiv',
            status_under_review: 'Under Gennemgang',
            status_archived: 'Arkiveret',
            decree_165_heading: 'Dekret 165 datalag (Vietnam Data Law)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Datalag',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Datalag',
            data_tier_regular: 'Regelmæssig',
            data_tier_important: 'Vigtigt',
            data_tier_core: 'Kerne',
            transfer_mechanism_label: 'Grænseoverskridende overførselsmekanisme',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Sikkerhedsvurderingsreference',
            security_assessment_reference_placeholder: 'f.eks. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Dokumentationsboks',
            new: 'Ny Dokumentation',
            edit: 'Rediger Dokumentation',
            name: 'Navn',
            evidence_type: 'Dokumentationstype',
            linked_entities: 'Tilknyttede Enheder',
            uploaded: 'Uploadet',
            expiry: 'Udløb',
            file_url: 'Fil-URL',
            source_mode_label: 'Bevissk ilde',
            source_mode_file: 'Upload fil',
            source_mode_url: 'Link ekstern URL',
            attachment_label: 'Vedhæftet fil',
            link_to: 'Knyt Dokumentation Til',
            entity_type: 'Enhedstype',
            entity_id: 'Enheds-ID',
            no_evidence: 'Ingen dokumentation fundet',
            manage_links: 'Administrer links',
            unlink: 'Fjern linket',
            confirm_delete: 'Slet dokumentation "{name}"?',
            count: '{count} elementer',
            count_one: '{count} element',
            type_document: 'Dokument',
            type_screenshot: 'Skærmbillede',
            type_policy: 'Politik',
            type_contract: 'Kontrakt',
            type_assessment: 'Vurdering',
            type_correspondence: 'Korrespondance',
            type_other: 'Andet',
            linked_type_system: 'System',
            linked_type_data_flow: 'Dataflow',
            linked_type_processing_activity: 'Behandlingsaktivitet',
            linked_type_data_category: 'Datakategori',
            linked_type_purpose: 'Formål',
            linked_type_retention_policy: 'Opbevaringspolitik',
            link_entity: 'Link til enhed',
            link_success: 'Enheden blev linket',
            confirm_unlink: 'Er du sikker på, at du vil fjerne linket til denne enhed?',
            unlink_success: 'Enheden blev fjernet',
            no_linked_entities: 'Ingen tilknyttede enheder',
            select_entity: 'Vælg...',
            no_entities: 'Ingen varer',
        },
        exports: {
            title: 'Dataeksporter',
            recent: 'Seneste Eksporter',
            export_type: 'Eksporttype',
            format: 'Format',
            requested_by: 'Anmodet Af',
            requested_at: 'Anmodet Den',
            download: 'Hent',
            csv_export: 'CSV-eksport',
            csv_description: 'Download data i CSV-format til regnearksanalyse.',
            pdf_reports: 'PDF-rapporter',
            pdf_description: 'Generer formaterede rapporter til compliancedokumentation.',
            csv_systems: 'Systemoversigt',
            csv_categories: 'Datakategorier',
            csv_flows: 'Datastrømme',
            csv_activities: 'Behandlingsaktiviteter',
            csv_cross_border: 'Grænseoverskridende Strømme',
            csv_retention_policies: 'Opbevaringspolitikker',
            csv_evidence: 'Beviselementer',
            csv_governance_assignments: 'Governance opgaver',
            pdf_ropa: 'RoPA-register',
            pdf_systems: 'Systemrapport',
            pdf_cross_border: 'Grænseoverskridende Rapport',
            pdf_flow_map: 'Strømkortdata',
            export_failed: 'Eksport mislykkedes',
            pdf_generated: 'PDF genereret med succes.',
            ropa: 'RoPA Register',
            retention_policies: 'Opbevaringspolitikker',
            governance_assignments: 'Governance opgaver',
            col_entity: 'Eksporter enhed',
            col_description: 'Beskrivelse',
            desc_ropa: 'Fuldt ROPA-register over alle behandlingsaktiviteter med juridisk grundlag, DPIA status og kontroller.',
            desc_systems: 'Systembeholdning med ejer, datakategori-tags og overholdelsesstatus.',
            desc_activities: 'Individuelle behandlingsaktiviteter med dataansvarlig, juridisk grundlag og DPIA klassificering.',
            desc_categories: 'Datakategorier og følsomhedsniveauer, der bruges på tværs af klassificering og revisioner.',
            desc_purposes: 'Behandlingsformål med det retsgrundlag, der er registreret for hvert formål.',
            desc_flows: 'Datastrømme mellem systemer, herunder interne og grænseoverskridende ruter.',
            desc_cross_border: 'Kun grænseoverskridende strømme med overførselsmekanisme og risikovurdering.',
            desc_retention_policies: 'Opbevaringspolitikker, bortskaffelsesmetoder og gennemgangskadence.',
            desc_evidence: 'Beviser knyttet til behandlingsaktiviteter: certifikater, DPA-kontrakter, DPIA rapporter.',
            desc_governance_assignments: 'Governance-opgaver: ejere, godkendere og ISO 27001-referencer.',
            unified_heading: 'Eksportere enheder',
            unified_description: 'Eksporter enhver data-mapping-enhed i CSV, XLSX, PDF eller DOCX-format. Alle eksporter gemmes i filbrowseren.',
            export_button_label: 'Eksporter',
            csv_download_success: 'CSV downloadet med succes.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Attestationscyklusser',
            create: 'Ny cyklus',
            name: 'Cyklusnavn',
            year: 'År',
            deadline: 'Frist',
            scope: 'Omfang',
            status: 'Status',
            tasks: 'Afdelingsopgaver',
            assign_tasks: 'Tildel opgaver',
            department: 'Afdeling',
            assignee: 'Ansvarlig',
            progress: 'Fremgang',
            stale_entries: 'Forældede poster',
            detect_stale: 'Find forældede',
            export_bundle: 'Eksporter pakke',
            no_cycles: 'Ingen attestationscyklusser endnu.',
            create_success: 'Attestationscyklus oprettet.',
            assign_success: 'Opgaver tildelt.',
            complete_success: 'Opgave fuldført.',
            stale_detected: 'Forældede poster fundet.',
            stale_systems: 'Forældede systemer',
            stale_flows: 'Forældede flows',
            no_stale: 'Ingen forældede poster fundet.',
            stale_name: 'Navn',
            stale_announcement: 'Fandt {0} forældede systemer og {1} forældede flows',
            last_updated: 'Sidst opdateret',
            status_draft: 'Kladde',
            status_active: 'Aktiv',
            status_completed: 'Fuldført',
            task_status_pending: 'Afventer',
            task_status_in_progress: 'I gang',
            task_status_completed: 'Fuldført',
            scope_systems: 'Systemer',
            scope_flows: 'Datastrømme',
            scope_activities: 'Aktiviteter',
            scope_categories: 'Kategorier',
            scope_purposes: 'Formål',
            scope_retention: 'Opbevaring',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Styringstildelinger',
            assign: 'Tildel ejerskab',
            entity_type: 'Entitetstype',
            entity: 'Entitet',
            role: 'Rolle',
            assigned_by: 'Tildelt af',
            assigned_at: 'Tildelt den',
            remove: 'Fjern',
            no_assignments: 'Ingen styringstildelinger.',
            assign_success: 'Ejerskab tildelt.',
            remove_success: 'Tildeling fjernet.',
            assignee: 'Tildelt person',
            department: 'Afdeling',
            all_departments: 'Alle afdelinger',
            roles: {
                data_owner: 'Dataejer',
                data_steward: 'Dataforvalter',
                data_custodian: 'Dataansvarlig',
                informed: 'Informeret',
            },
            entity_types: {
                system: 'System',
                flow: 'Datastrøm',
                activity: 'Aktivitet',
                category: 'Kategori',
                purpose: 'Formål',
                retention_policy: 'Opbevaringspolitik',
            },
        },
        risk_assessment: {
            title: 'Risikovurdering',
            create: 'Ny vurdering',
            safeguard_type: 'Beskyttelsestype',
            adequacy_status: 'Tilstrækkelighedsstatus',
            risk_factors: 'Risikofaktorer',
            mitigations: 'Afbødningsforanstaltninger',
            reviewer: 'Anmelder',
            decision: 'Beslutning',
            approve: 'Godkend',
            reject: 'Afvis',
            no_assessments: 'Ingen risikovurderinger.',
            create_success: 'Risikovurdering oprettet.',
            approve_success: 'Vurdering godkendt.',
            reject_success: 'Vurdering afvist.',
            one_per_line: 'Én per linje',
            adequacy_adequate: 'Tilstrækkelig',
            adequacy_inadequate: 'Utilstrækkelig',
            adequacy_under_review: 'Under gennemgang',
            adequacy_not_assessed: 'Ikke vurderet',
            decision_approved: 'Godkendt',
            decision_rejected: 'Afvist',
        },
        review: {
            title: 'Gennemgangsopgaver',
            description: 'Gennemgangsopgaver oprettes automatisk, når en datakategori foreslås. For at oprette en ny opgave skal du gå til Datakategorier og klikke på Foreslå på en kladde-kategori.',
            count: 'gennemgangsopgaver',
            summary: 'Gennemgangsoversigt',
            pending: 'Afventende',
            resolved_today: 'Løst i dag',
            escalated: 'Eskaleret',
            overdue: 'Forsinket (>5 dage)',
            all_statuses: 'Alle Statusser',
            status_pending: 'Afventende',
            status_resolved: 'Løst',
            status_escalated: 'Eskaleret',
            category_name: 'Kategori',
            system_name: 'System',
            owner: 'Ejer',
            resolve: 'Løs',
            resolve_task: 'Løs gennemgangsopgave',
            resolution: 'Løsning',
            resolution_approve: 'Godkend',
            resolution_modify: 'Modificer og godkend',
            resolution_reject: 'Afvis',
            resolution_escalate: 'Eskaler',
            notes: 'Løsningsnoter',
            notes_required: 'Noter påkrævet (minimum 10 tegn) for Modificer, Afvis eller Eskaler',
            resolved_success: 'Gennemgangsopgave løst',
            bulk_approve: 'Massegodkendelse',
            bulk_reject: 'Masseafvisning',
            bulk_success: '{count} opgaver behandlet',
            no_tasks: 'Ingen gennemgangsopgaver fundet',
        },
        inventory: {
            title: 'Klassifikationsinventar',
            count: 'systemer',
            summary: 'Inventaroversigt',
            total_systems: 'Samlede systemer',
            total_labels: 'Samlede etiketter',
            system_name: 'System',
            label_count: 'Etiketter',
            approved_count: 'Godkendt',
            pending_count: 'Afventende',
            no_items: 'Ingen inventarelementer fundet',
        },
        exceptions: {
            title: 'Klassifikationsundtagelser',
            count: 'undtagelser',
            all_severities: 'Alle Alvorligheder',
            all_statuses: 'Alle Statusser',
            system_name: 'System',
            severity_label: 'Alvorlighed',
            status_open: 'Åben',
            status_resolved: 'Løst',
            status_accepted_risk: 'Accepteret risiko',
            resolve: 'Løs',
            resolve_exception: 'Løs undtagelse',
            resolution_type: 'Løsningstype',
            notes: 'Noter',
            justification_required: 'Begrundelse påkrævet (minimum 10 tegn) for accepteret risiko',
            resolved_success: 'Undtagelse løst',
            no_exceptions: 'Ingen undtagelser fundet',
        },
        history: {
            title: 'Klassifikationshistorik',
            count: 'hændelser',
            actor: 'Aktør',
            action: 'Handling',
            entity_type: 'Entitetstype',
            changes: 'Ændringer',
            view_lineage: 'Vis oprindelse',
            lineage_title: 'Klassifikationsoprindelse',
            no_events: 'Ingen historiske hændelser fundet',
            no_lineage: 'Ingen oprindelsesdata tilgængelig',
            modified: 'Ændret',
            created: 'Oprettet',
            removed: 'Fjernet',
            action_created: 'Oprettet',
            action_updated: 'Opdateret',
            action_archived: 'Arkiveret',
            action_approved: 'Godkendt',
            action_deprecated: 'Forældet',
            entity_label: 'Etiket',
            entity_taxonomy: 'Taksonomi',
            entity_review_task: 'Gennemgangsopgave',
            entity_exception: 'Undtagelse',
            before: 'Før',
            after: 'Efter',
        },
    },
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        audit_trail_title: 'Revisionsspor for governance',
        audit_trail_subtitle: 'Flere entiteter — revisionsflow for governance-handlinger: godkendelser, playbook-udførelser, dokumentationslinks og mere.',
        audit_trail_item_count: 'revisionsbegivenheder',
        audit_trail_fetch_error: 'Kunne ikke hente revisionsbegivenheder.',
        audit_trail_no_events: 'Ingen revisionsbegivenheder fundet med de aktuelle filtre.',
        audit_trail_entity_type: 'Entitetstype',
        audit_trail_entity_id: 'Entitets-ID',
        audit_trail_action: 'Handling',
        audit_trail_actor: 'Aktør',
        audit_trail_timestamp: 'Tidsstempel',
        audit_trail_date_from: 'Fra',
        audit_trail_date_to: 'Til',
        audit_trail_no_rows_to_export: 'Ingen rækker at eksportere.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Leverandører',
            subprocessors: 'Underdatabehandlere',
            cross_border: 'Grænseoverskridende',
            drift: 'Afvigelsesovervågning',
            scores: 'Compliancescore',
            playbooks: 'Playbooks',
            approvals: 'Godkendelser',
            audit_trail: 'Revisionsspor',
            board_report: 'Bestyrelsesrapport',
            evidence: 'Dokumentation',
            onboarding: 'Onboarding-pipeline',
            dpa_reviews: 'DPA-gennemgange',
            periodic_reviews: 'Periodiske gennemgange',
            offboarding: 'Offboarding',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Leverandørregister',
            new: 'Ny Leverandør',
            edit: 'Rediger Leverandør',
            name: 'Navn',
            jurisdiction: 'Jurisdiktion',
            dpa_status: 'DPA-status',
            risk_level: 'Risikoniveau',
            onboarding_status: 'Onboardingstatus',
            contact_email: 'Kontakt-e-mail',
            contact_name: 'Kontaktperson',
            contact_phone: 'Kontakttelefon',
            website: 'Hjemmeside',
            services_csv: 'Tjenester (kommasepareret)',
            country: 'Land',
            country_placeholder: 'f.eks. DK',
            last_reviewed: 'Sidst Gennemgået',
            offboard: 'Afslut',
            offboard_vendor: 'Afslut Leverandør',
            offboard_reason: 'Årsag til afslutning',
            no_vendors: 'Ingen leverandører fundet',
            vendor_count: '{count} leverandør(er)',
            all_dpa_status: 'Alle DPA-statusser',
            all_risk_levels: 'Alle Risikoniveauer',
            all_onboarding: 'Alle Onboardingstatusser',
            fetch_error: 'Mislykkedes. Prøv venligst igen.',
            save_error: 'Kunne ikke gemme leverandør',
            offboard_error: 'Kunne ikke afslutte',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Databehandler',
            sub_processor: 'Underdatabehandler',
            controller: 'Dataansvarlig',
            joint_controller: 'Fælles Dataansvarlig',
        },
        onboarding_statuses: {
            pending_review: 'Afventer Gennemgang',
            approved: 'Godkendt',
            active: 'Aktiv',
            suspended: 'Suspenderet',
            offboarded: 'Afsluttet',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Ikke Påkrævet',
            required_missing: 'Påkrævet Mangler',
            draft: 'Udkast',
            under_review: 'Under Gennemgang',
            signed: 'Underskrevet',
            expired: 'Udløbet',
        },
        risk_levels: {
            critical: 'Kritisk',
            high: 'Høj',
            medium: 'Middel',
            low: 'Lav',
        },
        approval_statuses: {
            pending: 'Afventer',
            approved: 'Godkendt',
            rejected: 'Afvist',
            expired: 'Udløbet',
        },
        approval_action_types: {
            vendor_onboarding: 'Leverandørtilknytning',
            vendor_offboarding: 'Leverandørafslutning',
            cross_border_acceptance: 'Grænseoverskridende Godkendelse',
            playbook_activation: 'Playbook Aktivering',
            drift_accept_risk: 'Afvigelse Accepteret Risiko',
            activity_activation: 'Aktivitetsaktivering',
        },
        drift_check_types: {
            dpa_expiry: 'DPA-udløb',
            dpa_missing: 'DPA Mangler',
            review_overdue: 'Gennemgang Forfalden',
            cross_border_gap: 'Grænseoverskridende Mangel',
            sub_processor_chain: 'Underdatabehandlerkæde',
            risk_assessment_overdue: 'Risikovurdering Forfalden',
            retention_policy_missing: 'Opbevaringspolitik Mangler',
            evidence_expiring: 'Dokumentation Udløber',
        },
        drift_statuses: {
            open: 'Åben',
            acknowledged: 'Bekræftet',
            in_progress: 'I Gang',
            resolved: 'Løst',
            accepted_risk: 'Accepteret Risiko',
        },
        drift_entity_types: {
            vendor: 'Leverandør',
            system: 'System',
            data_flow: 'Datastrøm',
            processing_activity: 'Behandlingsaktivitet',
            evidence_item: 'Dokumentation',
        },
        playbook_statuses: {
            draft: 'Udkast',
            active: 'Aktiv',
            archived: 'Arkiveret',
        },
        playbook_action_types: {
            delete_data: 'Slet Data',
            verify_deletion: 'Bekræft Sletning',
            notify_vendor: 'Underret Leverandør',
            confirm_receipt: 'Bekræft Modtagelse',
            document_evidence: 'Dokumenter',
            other: 'Andet',
        },
        playbook_target_types: {
            system: 'System',
            vendor: 'Leverandør',
        },
        dpa_chain_statuses: {
            complete: 'Komplet',
            incomplete: 'Ukomplet',
            unknown: 'Ukendt',
        },
        sub_processor_statuses: {
            active: 'Aktiv',
            terminated: 'Opsagt',
        },
        evidence_types: {
            dpa_agreement: 'DPA-aftale',
            risk_assessment: 'Risikovurdering',
            audit_report: 'Revisionsrapport',
            certification: 'Certificering',
            deletion_confirmation: 'Sletningsbekræftelse',
            training_record: 'Uddannelsesjournal',
            incident_report: 'Hændelsesrapport',
            document: 'Dokument',
            screenshot: 'Skærmbillede',
            policy: 'Politik',
            contract: 'Kontrakt',
            assessment: 'Vurdering',
            correspondence: 'Korrespondance',
            other: 'Andet',
        },
        linkable_entity_types: {
            vendor: 'Leverandør',
            transfer_mechanism: 'Overførselsmekanisme',
            playbook: 'Legebog',
            execution: 'Udførelse',
            system: 'System',
            activity: 'Aktivitet',
        },
        subprocessors: {
            title: 'Underdatabehandlerrelationer',
            new: 'Ny Forbindelse',
            vendor: 'Leverandør',
            subprocessor: 'Underdatabehandler',
            relationship_type: 'Relationstype',
            countries: 'Lande',
            countries_csv: 'Lande (kommasepareret)',
            dpa_chain: 'DPA-kæde',
            dpa_chain_status: 'DPA-kædestatus',
            no_processors: 'Ingen databehandlere fundet.',
            no_subprocessors: 'Ingen underdatabehandlere fundet.',
            processor_vendor: 'Databehandlerleverandør',
            select_processor: 'Vælg Databehandler',
            select_vendor: 'Vælg Leverandør',
            services: 'Tjenester',
            sub_processor_vendor_id: 'Underdatabehandler Leverandør-ID',
            terminate: 'Opsig',
            confirm_terminate: 'Er du sikker på, at du vil opsige denne relation?',
            create_error: 'Kunne ikke oprette forbindelse.',
            fetch_error: 'Kunne ikke hente underdatabehandlere.',
            processor: 'Databehandler',
            services_csv: 'Tjenester (kommasepareret)',
            terminate_error: 'Kunne ikke opsige.',
            version_history_heading: 'Sub-processor ændring versionering',
            change_tracking_note: 'Alle ændringer af underdatabehandlerforholdet er versioneret. Hver ændring registrerer før/efter-tilstanden for revisionsspor.',
            history_retention_note: 'Ændringshistorikken gemmes i hele dataopbevaringsperioden. Gennemgå ændringslogfiler via leverandørens detaljevisning.',
            vendor_risk_cross_view_heading: 'Underbehandlerregister (Vendor Risk)',
            vendor_risk_cross_view_source: 'Kilde: Vendor Risk Register',
            vendor_risk_cross_view_empty: 'Ingen poster i Vendor Risk-registret.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Rapport over Grænseoverskridende Styring',
            flows: 'Grænseoverskridende Strømme',
            transfers: 'Grænseoverskridende Overførsler',
            detected_gaps: 'Identificerede Mangler',
            safeguards: 'Sikkerhedsforanstaltninger',
            cross_border_vendors: 'Grænseoverskridende Leverandører',
            destination: 'Destination',
            no_cross_border_vendors: 'Ingen grænseoverskridende leverandører fundet.',
            no_safeguard: 'Ingen sikkerhedsforanstaltning',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Behandlingslokationer',
            export_error: 'Kunne ikke eksportere rapport.',
            export_pdf: 'Eksporter PDF',
            export_success: 'Rapport eksporteret med succes.',
            exporting: 'Eksporterer...',
            fetch_error: 'Kunne ikke hente grænseoverskridende data.',
            source: 'Kilde',
            pdpl_p2_heading: 'PDPL-P2 Overholdelse af grænseoverskridende overførsler',
            dossier_prep_note: 'Udarbejdelse af dossier: Saml konsekvensvurdering, retsgrundlag og sikkerhedsdokumentation før indgivelse.',
            filing_tracking_note: 'Registreringssporing: Overvåg indsendelsesstatus hos Myndigheden (PDPC) fra første ansøgning til godkendelse.',
            supplement_loop_note: 'Supplerende loop: svar på anmodninger om myndighedsoplysninger og genindsend ændrede dossierer efter behov.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Leverandørsøgning',
            vendor_search_placeholder: 'Søg efter leverandørnavn⬦',
            jurisdiction_filter_label: 'Jurisdiktion',
            filings_section_heading: 'Grænseoverskridende ansøgninger',
            filings_empty: 'Ingen ansøgninger endnu. Opret en for at starte et dossier.',
            filings_create_button: 'Ny arkivering',
            filings_table_id: 'Indleverings-id',
            filings_table_jurisdiction: 'Jurisdiktion',
            filings_table_type: 'Formulartype',
            filings_table_status: 'Status',
            filings_table_updated: 'Opdateret',
            filings_table_open: 'Åbn',
            filings_detail_title: 'Grænseoverskridende arkivering',
            filings_back_to_list: 'Tilbage til grænseoverskridende',
            filings_status_heading: 'Livscyklus',
            filings_evidence_heading: 'Beviser',
            filings_history_heading: 'Revisionsspor',
            filings_field_filing_id: 'Indleverings-id',
            filings_field_jurisdiction: 'Jurisdiktion',
            filings_field_type: 'Formulartype',
            filings_field_status: 'Status',
            filings_field_reference: 'Myndighedsreference',
            filings_field_mps_receipt: 'MPS kvittering',
            filings_field_submitted_at: 'Indsendt kl',
            filings_field_acknowledged_at: 'Kvitteres kl',
            filings_field_archived_at: 'Arkiveret kl',
            filings_field_version: 'version',
            filings_field_notes: 'Noter',
            filings_action_advance: 'Forhåndsstatus',
            filings_action_reject: 'Afvis',
            filings_action_archive: 'Arkiv',
            filings_action_submit: 'Mærke indsendt',
            filings_action_acknowledge: 'Mark kvitterede',
            filings_action_resume_draft: 'Genoptag som kladde',
            filings_status_draft: 'Udkast',
            filings_status_assessed: 'Vurderet',
            filings_status_pending_review: 'Afventer gennemgang',
            filings_status_ready_to_file: 'Klar til at arkivere',
            filings_status_submitted: 'Indsendt',
            filings_status_acknowledged: 'Anerkendt',
            filings_status_rejected: 'Afvist',
            filings_status_archived: 'Arkiveret',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Ny grænseoverskridende arkivering',
            filings_new_form_jurisdiction: 'Jurisdiktion',
            filings_new_form_type: 'Formulartype',
            filings_new_form_notes: 'Noter (valgfrit)',
            filings_new_submit: 'Opret',
            filings_new_cancel: 'Annuller',
            filings_reason_label: 'Årsag',
            filings_reference_label: 'Myndighedens referencenummer',
            filings_mps_receipt_label: 'MPS kvitteringsreference',
            filings_create_error: 'Kunne ikke oprette arkivering.',
            filings_create_success: 'Arkiv oprettet.',
            filings_update_error: 'Kunne ikke opdatere arkiveringen.',
            filings_update_success: 'Arkiv opdateret.',
            filings_fetch_error: 'Filerne kunne ikke hentes.',
            filings_archive_confirm: 'Arkiver denne arkivering? Dette kan ikke fortrydes.',
            filings_status_rejected_detour: 'Ansøgningen blev afvist efter gennemgang. Genoptag som kladde for at genstarte flowet.',
            recent_exports_heading: 'Seneste eksport',
            recent_exports_empty: 'Ingen eksport endnu. Klik på "Eksporter PDF" for at generere en.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Konfigurationsafvigelsesovervågning',
            drift_type: 'Afvigelsestype',
            severity: 'Alvorlighedsgrad',
            detected_at: 'Registreret Den',
            resolve: 'Afhjælp',
            all_check_types: 'Alle Kontroltyper',
            all_entity_types: 'Alle Enhedstyper',
            event_count: 'Antal Hændelser',
            accept_risk: 'Accepter Risiko',
            acknowledge: 'Bekræft',
            assign_error: 'Kunne ikke tildele.',
            assign_event: 'Tildel',
            assigned: 'Tildelt',
            enter_resolution_notes: 'Indtast afhjælpningsnoter...',
            enter_user_id: 'Indtast bruger-ID...',
            entity: 'Enhed',
            fetch_error: 'Kunne ikke hente afvigelseshændelser.',
            no_events: 'Ingen afvigelseshændelser fundet.',
            resolution_notes: 'Afhjælpningsnoter',
            update_status_error: 'Kunne ikke opdatere status.',
        },
        scores: {
            title: 'Compliancescore Kontrolpanel',
            overall: 'Samlet Compliancescore',
            breakdown: 'Scoreopdeling pr. Modul',
            trend: 'Historisk Tendens',
            module: 'Modul',
            score: 'Score',
            grade: 'Karakter',
            calculated_at: 'Beregnet Den',
            generate_board_report: 'Generer Bestyrelsesrapport',
            lang_english: 'Engelsk',
            lang_vietnamese: 'Vietnamesisk',
            fetch_error: 'Kunne ikke hente compliancescorer.',
            generate_report: 'Generer Rapport',
            generating: 'Genererer...',
            recommendations: 'Anbefalinger',
            report_error: 'Kunne ikke generere rapport.',
            factor_owner_assigned: 'Ejer tildelt',
            factor_data_categories_linked: 'Datakategorier tilknyttet',
            factor_purposes_linked: 'Formål tilknyttet',
            factor_data_flows_documented: 'Datastrømme dokumenteret',
            factor_processing_activity_linked: 'Behandlingsaktivitet tilknyttet',
            factor_dpa_status: 'DPA-status',
            factor_retention_policy_linked: 'Opbevaringspolitik tilknyttet',
            factor_last_reviewed: 'Sidst gennemgået inden for tærskel',
            factor_risk_assessment_current: 'Risikovurdering aktuel',
            factor_sub_processors_documented: 'Underdatabehandlere dokumenteret',
            factor_cross_border_safeguards: 'Grænseoverskridende sikkerhedsforanstaltninger',
            factor_contact_info_complete: 'Kontaktoplysninger fuldstændige',
            factor_onboarding_approved: 'Onboarding godkendt',
            factor_avg_system_score: 'Gennemsnitlig systemscore',
            factor_avg_vendor_score: 'Gennemsnitlig leverandørscore',
            factor_processing_activity_coverage: 'Dækning af behandlingsaktiviteter',
            factor_ropa_completeness: 'RoPA-fuldstændighed',
            factor_open_drift_events: 'Åbne afvigelseshændelser',
        },
        playbooks: {
            title: 'Sletnings-Playbooks',
            new: 'Ny Playbook',
            edit: 'Rediger Playbook',
            steps: 'Trin',
            add_step: 'Tilføj Trin',
            remove_step: 'Fjern Trin',
            data_categories_csv: 'Datakategorier (kommasepareret)',
            estimated_duration: 'Estimeret Varighed',
            estimated_duration_label: 'Estimeret Varighed',
            evidence_description: 'Dokumentationsbeskrivelse',
            no_playbooks: 'Ingen playbooks fundet.',
            playbook_count: 'Antal Playbooks',
            requires_evidence: 'Kræver Dokumentation',
            step_label: 'Trinetiket',
            target_name: 'Målnavn',
            target_type: 'Måltype',
            action_type: 'Handlingstype',
            archive_error: 'Kunne ikke arkivere.',
            confirm_archive: 'Er du sikker på, at du vil arkivere denne playbook?',
            data_categories: 'Datakategorier',
            days: 'dage',
            fetch_error: 'Kunne ikke hente playbooks.',
            sla_days: 'SLA (dage)',
            target_id: 'Mål-ID',
            template_class_heading: 'Skabelon klassificering',
            official: 'Officiel',
            internal: 'Internt',
            classification: 'Klassifikation',
            official_note: 'Officielle: lovpligtige skabeloner tilpasset PDPL, GDPR, eller sektorspecifikke krav.',
            internal_note: 'Internt: organisationsspecifikke procedurer, der ikke er afledt af regulatoriske skabeloner.',
            active_executions_heading: 'Aktive kørsler',
            no_active_executions: 'Ingen aktive kørsler.',
            playbook_label: 'Legebog',
            ticket_id_label: 'Sag',
            progress_label: 'Fremskridt',
            actions_label: 'Handlinger',
            view_steps_label: 'Vis trin',
            execution_detail_title: 'Detaljer for kørsel',
            playbook_missing: 'Playbook ikke fundet.',
            evidence_required_label: 'Bevis påkrævet',
            upload_evidence_label: 'Upload bevis',
            mark_complete_label: 'Marker som fuldført',
            evidence_required_toast: 'Tilføj et bevis før du fuldfører.',
            evidence_blocking_message: 'Vedhæft en bevisfil for at fortsætte.',
            step_completed_toast: 'Trin fuldført.',
            executions_fetch_error: 'Kunne ikke hente kørsler.',
            step_evidence_default_name: 'Bevis for kørselstrin',
            attached_evidence_label: 'Vedhæftede beviser',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Godkendelsesworkflows',
            new: 'Ny Godkendelse',
            edit: 'Rediger Godkendelse',
            request_type: 'Anmodningstype',
            requester: 'Anmoder',
            approve: 'Godkend',
            reject: 'Afvis',
            justification: 'Begrundelse',
            all_action_types: 'Alle Handlingstyper',
            decided_by: 'Besluttet Af',
            no_approvals: 'Ingen godkendelser fundet.',
            approval_count: 'Antal Godkendelser',
            approval_notes_optional: 'Noter (valgfrit)',
            approve_error: 'Kunne ikke godkende.',
            approve_request: 'Godkend Anmodning',
            decision: 'Beslutning',
            entity: 'Enhed',
            fetch_error: 'Kunne ikke hente godkendelser.',
            reject_error: 'Kunne ikke afvise.',
            reject_request: 'Afvis Anmodning',
            rejection_notes_required: 'Afvisningsnoter påkrævet',
            rejection_notes_required_msg: 'Angiv venligst en begrundelse for afvisningen.',
            requested_at: 'Anmodet Den',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Understøttende bevis (valgfrit)',
            decision_evidence_name: 'Beslutningsbevis',
        },
        evidence: {
            title: 'Styringsdokumentation',
            new: 'Ny Dokumentation',
            link_to: 'Knyt Til',
            entity_id_placeholder: 'Enheds-ID',
            file_path_placeholder: '/sti/til/fil',
            evidence_label: 'Dokumentationsetiket',
            file_path: 'Filsti',
            source_mode_label: 'Kilde til bevis',
            source_mode_file: 'Upload fil',
            source_mode_url: 'Link ekstern URL',
            attachment_label: 'Vedhæftet fil',
            item_count: 'Antal Elementer',
            link_button: 'Knyt Dokumentation',
            confirm_delete: 'Er du sikker på, at du vil slette denne dokumentation?',
            enter_entity_id: 'Indtast enheds-ID...',
            entity_id: 'Enheds-ID',
            entity_type: 'Enhedstype',
            expires: 'Udløber',
            expires_at: 'Udløber Den',
            fetch_error: 'Kunne ikke hente dokumentation.',
            link_error: 'Kunne ikke knytte dokumentation.',
            no_evidence: 'Ingen dokumentation fundet.',
            tags_csv: 'Tags (kommasepareret)',
            uploaded: 'Uploadet',
            url: 'URL',
            framework_tag_heading: 'Rammemærket bevis',
            framework_tag_note: 'Hver bevispost fanger rammekode, skuespiller og tidsstempel. Filtrer efter framework for at isolere revisionsrelevante poster.',
            framework_code: 'Rammekode',
            filter_by_framework: 'Filtrer efter rammekode...',
            actor: 'Skuespiller',
        },
        certification: {
            not_applicable: 'Ikke relevant',
            not_certified: 'Ikke certificeret',
            pending: 'Afventer',
            certified: 'Certificeret',
            revoked: 'Tilbagekaldt',
        },
        onboarding_pipeline: {
            title: 'Onboarding-pipeline',
            new_workflow: 'Ny onboarding-arbejdsgang',
            vendor_id: 'Leverandør-ID',
            stages: 'Faser',
            current_stage: 'Nuværende fase',
            advance: 'Fremryk fase',
            advance_success: 'Fase fremrykket',
            workflow_status: 'Arbejdsgangens status',
            initiated: 'Igangsat',
            in_progress: 'I gang',
            completed: 'Afsluttet',
            cancelled: 'Annulleret',
            no_workflows: 'Ingen onboarding-arbejdsgange fundet',
            stages_placeholder: 'Trin 1, Trin 2, Trin 3',
            assigned_to: 'Tildelt til',
            notes: 'Noter',
            pdpl_lifecycle_heading: 'PDPL Leverandørlivscyklus — Onboarding',
            due_diligence_note: 'Due diligence-stadiet er obligatorisk før indsættelse af en processor under PDPL. Bekræft databehandlingens omfang, retsgrundlag og sikkerhedsforanstaltninger.',
            contract_review_note: 'Kontraktgennemgang skal bekræfte DPA-klausuler, dataopbevaringsgrænser og underdatabehandlerrestriktioner.',
            create_dpa_review_cta: 'Opret DPA-gennemgang',
            dpa_review_linked: 'DPA-gennemgang knyttet',
        },
        dpa_review: {
            title: 'DPA-gennemgange',
            new_review: 'Ny DPA-gennemgang',
            review_type: 'Gennemgangstype',
            findings: 'Resultater',
            outcome: 'Udfald',
            complete_review: 'Afslut gennemgang',
            complete_success: 'Gennemgang afsluttet',
            link_assessment: 'Knyt sikkerhedsvurdering',
            link_success: 'Vurdering tilknyttet',
            assessment_id: 'Vurderings-ID',
            combined_title: 'Kombineret vurdering',
            no_reviews: 'Ingen DPA-gennemgange fundet',
            telecom_disclosure_heading: 'Telecom Disclosure Governance',
            disclosure_trigger_note: 'Afsløringsudløste anmeldelser fanger hændelser for deling af teleabonnenter i henhold til VN Telecom Law.',
            telecom_t2_note: 'TELECOM-T2 niveaugodkendelse er påkrævet, før abonnentdata videregives til myndighederne.',
            quarterly_log_note: 'Brug den kvartalsvise log til at dokumentere alle offentliggørelseshændelser og opretholde et kontinuerligt revisionsspor.',
        },
        periodic_review: {
            title: 'Periodiske gennemgange',
            new_review: 'Ny periodisk gennemgang',
            review_type: 'Gennemgangstype',
            due_date: 'Forfaldsdato',
            next_review: 'Næste gennemgangsdato',
            trigger_reassessment: 'Udløs revurdering',
            reassessment_reason: 'Årsag til revurdering',
            reassessment_success: 'Revurdering udløst',
            complete_success: 'Gennemgang afsluttet',
            scheduled: 'Planlagt',
            in_progress: 'I gang',
            completed: 'Afsluttet',
            overdue: 'Forsinket',
            material_change: 'Væsentlig ændring',
            no_reviews: 'Ingen periodiske gennemgange fundet',
            mark_complete: 'Markér som fuldført',
            pdpl_periodic_heading: 'PDPL Leverandørlivscyklus — Periodiske anmeldelser',
            scheduled_review_note: 'Periodiske gennemgange skal planlægges mindst årligt for alle aktive databehandlere. Forsinkede anmeldelser udløser advarsler om overholdelse.',
            material_change_note: 'Væsentlige ændringer i databehandling kræver øjeblikkelig revurdering uanset tidsplan.',
            overdue_badge: 'Forsinket',
            overdue_aria: 'Forsinket gennemgang',
            only_overdue_filter: 'Kun forsinkede',
        },
        offboarding: {
            title: 'Leverandør-offboarding',
            initiate: 'Igangsæt offboarding',
            initiate_success: 'Offboarding igangsat',
            reason: 'Årsag til offboarding',
            data_return_required: 'Datareturnering påkrævet',
            verify_data: 'Bekræft datareturnering',
            verify_success: 'Datareturnering bekræftet',
            sign_off: 'Godkend',
            sign_off_success: 'Offboarding godkendt',
            status_initiated: 'Igangsat',
            status_data_pending: 'Afventer datareturnering',
            status_data_verified: 'Datareturnering bekræftet',
            status_completed: 'Afsluttet',
            status_cancelled: 'Annulleret',
            task_id: 'Opgave-ID',
            no_tasks: 'Ingen offboarding-opgaver fundet',
            evidence_id: 'Dokumentations-ID',
            pdpl_offboarding_heading: 'PDPL Leverandørlivscyklus — Offboarding og sletning',
            deletion_capture_note: 'Offboarding skal indhente bekræftelse af sletning af data fra leverandøren. Bekræft dataretur før afmelding.',
            evidence_attachment_note: 'Vedhæft slettebeviser (certifikater, skærmbilleder, leverandørbekræftelser) for at fuldføre offboarding-registret.',
            verify_mode_label: 'Bekræftelsesmetode',
            verify_mode_upload: 'Upload ny fil',
            verify_mode_link: 'Link eksisterende bevis',
            verify_mode_none: 'Ingen data at returnere',
            upload_data_return_evidence: 'Upload bevis for dataretur',
            no_data_justification_label: 'Begrundelse',
            no_data_justification_placeholder: 'Forklar hvorfor der ikke er data at returnere',
            no_data_justification_hint: 'Mindst 20 tegn',
            upload_required_toast: 'Tilføj en fil for at fortsætte',
            evidence_link_required_toast: 'Vælg et bevis for at fortsætte',
            no_data_justification_required_toast: 'Angiv en begrundelse på mindst 20 tegn',
            data_return_evidence_name: 'Bevis for dataretur',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Indledende',
            annual: 'Årlig',
            renewal: 'Fornyelse',
            incident_triggered: 'Hændelsesudløst',
        },
        periodic_review_types: {
            initial: 'Indledende',
            annual: 'Årlig',
            renewal: 'Fornyelse',
            incident_triggered: 'Hændelsesudløst',
            material_change: 'Væsentlig ændring',
            disclosure_triggered: 'Oplysningsudløst',
            quarterly_log: 'Kvartalslog',
        },
        dpa_review_statuses: {
            pending: 'Afventer',
            in_progress: 'I gang',
            completed: 'Afsluttet',
            cancelled: 'Annulleret',
        },
        onboarding_stage_names: {
            due_diligence: 'Due diligence',
            contract_review: 'Kontraktgennemgang',
            security_assessment: 'Sikkerhedsvurdering',
            data_mapping: 'Datakortlægning',
            approval: 'Godkendelse',
            integration: 'Integration',
        },
        access_denied_title: 'Adgang nægtet',
        access_denied: 'Du har ikke tilladelse til at se denne side. Kontakt venligst din administrator.',
    },
    consent: {
        nav: {
            policies: 'Politikker',
            purposes: 'Formålskatalog',
            workflows: 'Arbejdsgange',
            snapshots: 'CMP-snapshots',
            change_awareness: 'Ændringsbevidsthed',
            audit: 'Samtykkerevision',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Gab Analyse',
            page_title: 'Analyse af samtykkegab',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Rammer',
            org_flags_label: 'Organisationskontekst',
            flag_withdrawal: 'Har global tilbagetrækningsmekanisme dokumenteret',
            flag_parental: 'Har forældresamtykke flow for under 16 år',
            flag_cross_border: 'Har grænseoverskridende arkivering registreret',
            run_label: 'Kør gap-analyse',
            running_label: 'Kører...',
            requires_advisor_label: 'Kræver ai_advisor:chattilladelse.',
            results_heading: 'Der opstod huller ({count})',
            tokens_label: '≈ {count} tokens',
            empty_label: 'Der blev ikke fundet huller for de valgte rammer.',
            remediation_label: 'Foreslået afhjælpning:',
            dismiss_label: 'Afvis som ikke relevant',
            resolve_label: 'Løs',
            dismissed_label: 'Afvist som ikke relevant',
            resolved_label: 'Løst',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Importér samtykkearbejdsgange',
            nav_title: 'Masseimport',
            description: 'Upload en CSV-fil for at masseimportere samtykkearbejdsgange.',
            select_file: 'Vælg CSV-fil',
            file_help: 'CSV med overskrifter der matcher arbejdsgangsfelter.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Påkrævede kolonner: Arbejdsgangstype, Registrerede, Kanal.',
            file_too_large: 'Filen er for stor. Maksimal størrelse er 2MB.',
            preview: 'Forhåndsvisning',
            columns_detected: 'Kolonner fundet: {count}',
            column_mapping: 'Kolonnetilknytning',
            import_button: 'Importér',
            import_complete: 'Import fuldført',
            import_error: 'Import mislykkedes',
            result: 'Importresultat',
            total: 'I alt',
            imported: 'Importeret',
            skipped: 'Sprunget over',
            failed: 'Fejlet',
            errors: 'Fejl',
            error_row: 'Række',
            error_field: 'Felt',
            error_message: 'Besked',
            history: 'Importhistorik',
            no_history: 'Ingen importhistorik.',
            history_file: 'Filnavn',
            history_date: 'Dato',
            create_profiles_label: 'Opret Registrerede-profiler for nye personer',
            create_profiles_help: 'Import uden en matchende profil opretter automatisk en ny. Fravælg for kun at importere arbejdsgange.',
            profiles_created: 'Profiler oprettet',
            profiles_matched: 'Profiler matchet',
            subject_kind_help: 'Valgfrie kolonner: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Ugyldig e-mail i subject_email-kolonnen.',
            error_subject_unknown_kind: 'Ukendt subject_kind-værdi. Forventet: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key er påkrævet når subject_kind=external_ref',
        },
        policies: {
            title: 'Samtykkepolitikker',
            new: 'Ny Politik',
            edit: 'Rediger Politik',
            name: 'Navn',
            version: 'version',
            policy_text: 'Politiktekst',
            publish: 'Publicer',
            versions: 'Versioner',
            hide_versions: 'Skjul Versioner',
            submit_for_review: 'Indsend til Gennemgang',
            suspend: 'Suspendér',
            retire: 'Pensionér',
            jurisdictions: 'Jurisdiktioner',
            jurisdictions_csv: 'Jurisdiktioner (kommasepareret)',
            approval_mode: 'Godkendelsestilstand',
            reviewer_ids: "Bedømmer-ID'er (kommasepareret)",
            tags_csv: 'Tags (kommasepareret)',
            effective_date: 'Ikrafttrædelsesdato',
            review_date: 'Gennemgangsdato',
            consent_model_statement: 'Samtykkemodelerklæring',
            consent_model_placeholder: 'Valgfri samtykkemodelerklæring...',
            default_settings: 'Standardindstillinger (JSON)',
            changes: 'Ændringer',
            fetch_error: 'Kunne ikke hente samtykkepolitikker. Prøv venligst igen.',
            save_error: 'Kunne ikke gemme samtykkepolitik. Prøv venligst igen.',
            submit_review_error: 'Kunne ikke indsende politik til gennemgang. Prøv venligst igen.',
            activate_error: 'Kunne ikke aktivere politik. Prøv venligst igen.',
            suspend_error: 'Kunne ikke suspendere politik. Prøv venligst igen.',
            retire_error: 'Kunne ikke pensionere politik. Prøv venligst igen.',
            submit_review_success: 'Politik indsendt til gennemgang.',
            activate_success: 'Politik aktiveret.',
            suspend_success: 'Politik suspenderet.',
            retire_success: 'Politiken er gået på pension.',
            versions_error: 'Kunne ikke hente politikversioner. Prøv venligst igen.',
            invalid_json: 'Ugyldig JSON i feltet Standardindstillinger. Prøv venligst igen.',
            page_title: 'Samtykkepolitikker',
            version_updated: 'Opdateret',
            version_initial: 'Indledende version',
            json_format: 'JSON format',
            sensitive: 'Følsom',
            flag_sensitive: 'Flagfølsom',
            flag_sensitive_title: 'Flag politik som følsom',
            require_enhanced_review: 'Kræv forbedret gennemgang',
            enhanced_review_success: 'Krav til udvidet gennemgang er aktiveret.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Vælg de relevante PDPL artikel 2 følsomme datakategorier:',
            no_policies: 'Ingen samtykkepolitikker fundet. Opret din første politik for at komme i gang.',
            approval_success: 'Politik godkendt.',
            reviewers_label: 'Bedømmere',
            enhanced_review_title: 'Udvidet gennemgang påkrævet',
            enhanced_review_badge: 'Udvidet gennemgang påkrævet',
            enhanced_review_status_pending: 'Gennemgang afventer',
            enhanced_review_status_approved: 'Gennemgang godkendt',
            enhanced_review_status_rejected: 'Gennemgang afvist',
            enhanced_review_reviewed_by: 'Gennemgået af',
            enhanced_review_notes: 'Gennemgangsnoter',
            enhanced_review_approve: 'Godkend gennemgang',
            enhanced_review_reject: 'Afvis gennemgang',
            enhanced_review_notes_required: 'Gennemgangsnoter er påkrævet',
            enhanced_review_approved: 'Udvidet gennemgang godkendt',
            enhanced_review_rejected: 'Udvidet gennemgang afvist — politik nulstillet til kladde',
            enhanced_review_reset_to_draft: 'Udvidet gennemgang afvist — politik nulstillet til kladde',
            enhanced_review_reject_warning: 'Afvisning vil nulstille denne politik til Kladde-status. DPO skal genindsende til gennemgang efter at have løst problemerne.',
            submitted_modal_title: 'Indsendt til udvidet gennemgang',
            submitted_modal_body: 'Denne politik er indsendt. En Juridisk/Privatlivsrevisor skal godkende den, før den kan aktiveres.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Sundhedsdata',
            biometric: 'Biometriske data',
            political: 'Politiske holdninger',
            ethnic: 'Etnisk oprindelse',
            genetic: 'Genetiske data',
            criminal: 'Straffeattester',
        },
        purposes: {
            title: 'Formålskatalog',
            new: 'Nyt Formål',
            edit: 'Rediger Formål',
            name: 'Navn',
            legal_basis: 'Retsgrundlag',
            retention_days: 'Opbevaringsperiode (dage)',
            is_active: 'Aktiv',
            filter_by_category: 'Filtrer efter kategori',
            policy_ids: "Politik-ID'er (kommasepareret)",
            ropa_activity_ids: "ROPA-aktivitets-ID'er (kommasepareret)",
            policies: 'Politikker',
            deprecate: 'Udfas',
            fetch_error: 'Kunne ikke hente samtykkeformål. Prøv venligst igen.',
            save_error: 'Kunne ikke gemme samtykkeformål. Prøv venligst igen.',
            deprecate_error: 'Kunne ikke udfase samtykkeformål. Prøv venligst igen.',
            page_title: 'Formål Katalog',
            no_purposes: 'Ingen samtykkeformål fundet. Opret dit første formål for at komme i gang.',
            category_personal_data: 'Personlige data',
            category_sensitive_data: 'Følsomme data',
            category_health_data: 'Sundhedsdata',
            category_financial_data: 'Finansielle data',
            category_biometric_data: 'Biometriske data',
            category_children_data: 'Børns data',
            category_location_data: 'Lokationsdata',
            category_communications_data: 'Kommunikationsdata',
            category_other: 'Andet',
        },
        workflows: {
            title: 'Samtykke-Workflows',
            new: 'Ny Workflow',
            edit: 'Rediger Workflow',
            workflow_type: 'Workflowtype',
            user_id: 'Bruger-ID',
            purpose_id: 'Formåls-ID',
            purpose_ids_csv: 'Formåls-ID (kommasepareret)',
            reason: 'Årsag',
            process: 'Behandl',
            data_subject_ref: 'Registreret Person Reference',
            data_subject: 'Registreret Person',
            channel: 'Kanal',
            policy_id: 'Politik-ID',
            scope: 'Omfang',
            legal_grounds: 'Retsgrundlag',
            assessment: 'Vurdering',
            select_outcome: 'Vælg Resultat',
            partially_approved: 'Delvist Godkendt',
            denied: 'Afvist',
            outcome_upheld: 'Stadfæstet',
            outcome_pending: 'Afventer',
            outcome_denied: 'Afvist',
            status_count: 'Statustal',
            channel_filter: 'Filtrer efter kanal',
            add_action: '+ Handling',
            by_type: 'Efter Type',
            by_status: 'Efter Status',
            downstream_actions: 'Efterfølgende Handlinger',
            downstream_actions_for: 'Efterfølgende Handlinger for {subject} ({type})',
            add_downstream_action: 'Tilføj Efterfølgende Handling',
            assigned_to: 'Tildelt Til',
            fetch_error: 'Kunne ikke hente samtykke-workflows. Prøv venligst igen.',
            dashboard_error: 'Kunne ikke hente workflow-kontrolpanel. Prøv venligst igen.',
            save_error: 'Kunne ikke gemme samtykke-workflow. Prøv venligst igen.',
            status_update_error: 'Kunne ikke opdatere workflowstatus. Prøv venligst igen.',
            assessment_error: 'Kunne ikke opdatere workflowvurdering. Prøv venligst igen.',
            add_action_error: 'Kunne ikke tilføje workflowhandling. Prøv venligst igen.',
            update_action_error: 'Kunne ikke opdatere workflowhandling. Prøv venligst igen.',
            type_refusal: 'Afvisning',
            type_withdrawal: 'Tilbagetrækning',
            type_restriction: 'Begrænsning',
            type_objection: 'Indsigelse',
            status_received: 'Modtaget',
            status_processing: 'Behandler',
            status_under_assessment: 'Under Vurdering',
            status_implemented: 'Implementeret',
            evidence_button: 'Beviser',
            evidence_modal_title: 'Workflow-bevis',
            no_evidence: 'Ingen beviser knyttet til denne arbejdsgang.',
            upload_evidence_label: 'Upload workflow-bevis',
            completed_at: 'Afsluttet kl',
            effective_date: 'Ikrafttrædelsesdato',
            page_title: 'Samtykke arbejdsgange',
            no_workflows: 'Ingen samtykkearbejdsgange fundet. Arbejdsgange oprettes, når registrerede udøver deres rettigheder.',
            type_grant: 'Tildeling',
            propagation_none: 'Ingen',
            propagation_pending: 'Afventer',
            propagation_pending_warning: 'Kan ikke fuldføre — tilbagetrækningspropagering er stadig afventende',
            propagation_completed: 'Fuldført',
            propagation_partial: 'Delvis',
            propagation_incomplete_error: 'Kan ikke fuldføre arbejdsgang, mens tilbagetrækningspropagering stadig afventer',
            propagation_status: 'Propageringsstatus',
        },
        snapshots: {
            title: 'CMP-snapshots',
            new: 'Ny Snapshot',
            label: 'Etiket',
            snapshot_date: 'Snapshotdato',
            purposes_count: 'Antal Formål',
            compare: 'Sammenlign',
            snapshot_data: 'Snapshotdata (JSON)',
            select_left: 'Vælg Venstre Snapshot',
            select_right: 'Vælg Højre Snapshot',
            comparison_results: 'Sammenligningsresultater',
            field: 'Felt',
            left: 'Venstre',
            right: 'Højre',
            close_comparison: 'Luk Sammenligning',
            trigger: 'Udløser',
            trigger_manual: 'Manuel',
            trigger_policy_approval: 'Politikgodkendelse',
            cmp_version: 'CMP-version',
            policy_version_id: 'Politikversions-ID',
            banner_config: 'Bannerkonfiguration (JSON)',
            active_purposes: 'Aktive Formål (JSON)',
            active_legal_bases: 'Aktive Retsgrundlag (JSON)',
            domain_scope: 'Domæneomfang',
            domain_scope_csv: 'Domæneomfang (kommasepareret)',
            raw_config: 'Rå Konfiguration (JSON)',
            checksum: 'Kontrolsum',
            created: 'Oprettet',
            fetch_error: 'Kunne ikke hente CMP-snapshots. Prøv venligst igen.',
            save_error: 'Kunne ikke gemme CMP-snapshot. Prøv venligst igen.',
            compare_error: 'Kunne ikke sammenligne snapshots. Prøv venligst igen.',
            page_title: 'Snapshots af samtykke',
            json_format: 'JSON format',
            json_array_format: 'JSON matrixformat',
            manual_snapshot_note: 'Kun til administratorer/udviklere. I normal drift oprettes snapshots automatisk via CMP-webhook eller ved politikgodkendelse. Indsæt JSON eksporteret fra din CMP-leverandør (f.eks. OneTrust, Cookiebot).',
            banner_config_hint: 'Indsæt bannerkonfigurationsobjektet fra din CMP-eksport.',
            active_purposes_hint: 'JSON-array af aktive formålsobjekter kopieret fra din CMP-eksport.',
            active_legal_bases_hint: 'JSON-array af aktive retsgrundlag-objekter kopieret fra din CMP-eksport.',
            raw_config_hint: 'Komplet rå konfigurations-JSON fra din CMP. Andre JSON-felter udtrækkes herfra. En SHA-256-kontrollsum beregnes automatisk.',
            changed_field: 'Ændret felt:',
            no_snapshots: 'Ingen CMP-snapshots fundet. Snapshots oprettes, når politikker godkendes eller udløses manuelt.',
            detail_title: 'Snapshot-detalje',
            view_snapshot_detail: 'Vis snapshot-detalje',
            policy_version: 'Politikversion',
            removed: 'Før',
            added: 'Efter',
        },
        change_awareness: {
            title: 'Samtykkeændringsbevidsthed',
            change_type: 'Ændringstype',
            entity_type: 'Enhedstype',
            entity_id: 'Enheds-ID',
            detected_at: 'Registreret Den',
            acknowledge: 'Kvittér',
            new_alert: 'Ny Advarsel',
            new_alert_form: 'Ny Ændringsbevidsthed-advarsel',
            total_open: 'Samlet Åbne',
            by_severity: 'Efter Alvorlighedsgrad',
            snapshot_id: 'Snapshot-ID',
            update_status: 'Opdater Status',
            resolution_notes: 'Løsningsnoter',
            resolution_required: 'Løsningsnoter (påkrævet)',
            resolution_required_msg: 'Løsningsnoter er påkrævet ved afslutning af et element.',
            status_open: 'Åben',
            status_under_review: 'Under Gennemgang',
            status_resolved: 'Løst',
            status_accepted: 'Accepteret',
            severity_informational: 'Informativ',
            severity_requires_review: 'Kræver Gennemgang',
            severity_policy_violation: 'Politikovertrædelse',
            create_reconciliation: 'Opret afstemningsopgave',
            reconciliation_created: 'Afstemningsopgave oprettet.',
            reconciliation_error: 'Kunne ikke oprette afstemningsopgave. Prøv venligst igen.',
            fetch_error: 'Kunne ikke hente ændringsbevidsthedsemner. Prøv venligst igen.',
            dashboard_error: 'Kunne ikke hente ændringsbevidsthedskontrolpanel. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette ændringsbevidsthedsemne. Prøv venligst igen.',
            status_update_error: 'Kunne ikke opdatere ændringsbevidsthedsstatus. Prøv venligst igen.',
            page_title: 'Forandringsbevidsthed',
            no_items: 'Ingen ændringsbevidsthedselementer fundet. Elementer oprettes, når ændringer i samtykkekonfigurationen registreres.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Samtykkerevisionsspor',
            event_type: 'Hændelsestype',
            user_id: 'Bruger-ID',
            timestamp: 'Tidsstempel',
            details: 'Detaljer',
            actor: 'Aktør',
            filter_by_entity_type: 'Filtrer efter enhedstype',
            filter_by_action: 'Filtrer efter handling',
            fetch_error: 'Kunne ikke hente samtykkerevisionsspor. Prøv venligst igen.',
            page_title: 'Samtykke revision',
            export_csv: 'Eksporter CSV',
            exporting: 'Eksporterer...',
            export_success: 'Revisionssporet blev eksporteret.',
            export_error: 'Kunne ikke eksportere revisionssporet for samtykke. Prøv venligst igen.',
            no_entries: 'Ingen revisionsposter fundet. Revisionsbegivenheder registreres automatisk, når samtykkedata ændres.',
            entity_policy: 'Politik',
            entity_purpose: 'Formål',
            entity_workflow: 'Arbejdsgang',
            entity_consent_record: 'Samtykkepost',
            entity_change_awareness: 'Ændringsopmærksomhed',
            entity_cmp_snapshot: 'CMP-øjebliksbillede',
            action_created: 'Oprettet',
            action_updated: 'Opdateret',
            action_status_changed: 'Status ændret',
            action_drift_detected: 'Afvigelse registreret',
            action_snapshot_created: 'Øjebliksbillede oprettet',
            action_approval_submitted: 'Godkendelse indsendt',
            action_archived: 'Arkiveret',
            action_purpose_auto_mapped: 'Formål automatisk tilknyttet',
            action_purpose_mapping_updated: 'Formålstilknytning opdateret',
            action_webhook_configured: 'Webhook konfigureret',
            entity_propagation: 'Formering',
            entity_reconciliation: 'Forsoning',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Formering bekræftet',
            action_propagation_failed: 'Formering mislykkedes',
            action_consent_captured: 'Samtykke fanget',
            action_reconciliation_created: 'Afstemning oprettet',
            action_reconciliation_resolved: 'Afstemning løst',
            action_submitted_for_review: 'Indsendt til gennemgang',
            action_activated: 'Aktiveret',
            action_suspended: 'Suspenderet',
            action_retired: 'Udgået',
            action_enhanced_review_required: 'Forbedret gennemgang påkrævet',
            action_enhanced_review_approved: 'Udvidet gennemgang godkendt',
            action_enhanced_review_rejected: 'Udvidet gennemgang afvist — politik nulstillet til kladde',
            action_consented: 'Samtykkede',
            action_consented_customize: 'Samtykke tilpasset',
            action_revoked: 'Tilbagekaldt',
            action_rejected: 'Afvist',
        },
        web_records: {
            title: 'Websamtykkeregistreringer',
            page_title: 'Websamtykkeregistreringer',
            domain: 'Domæne',
            visitor_id: 'Besøgs-id',
            consent_action: 'Samtykke handling',
            received_at: 'Modtaget kl',
            admin_status: 'Admin status',
            revoke: 'Tilbagekald',
            reject: 'Afvis',
            reason: 'Årsag',
            reason_placeholder: 'Årsag til denne handling (valgfrit)',
            revoke_confirm: 'Vil du tilbagekalde denne samtykkeregistrering?',
            reject_confirm: 'Vil du afvise denne samtykkeregistrering?',
            override_success: 'Registreringsstatus blev opdateret.',
            override_error: 'Registreringsstatus kunne ikke opdateres.',
            no_records: 'Ingen websamtykkeregistreringer fundet.',
            fetch_error: 'Kunne ikke indlæse websamtykkeposter.',
            filter_by_domain: 'Filtrer efter domæne',
            filter_by_action: 'Filtrer efter handling',
            active: 'Aktiv',
        },
        legal_bases: {
            all: 'Alle Retsgrundlag',
        },
        common_statuses: {
            suspended: 'Suspenderet',
            retired: 'Pensioneret',
            deprecated: 'Udfaset',
        },
        approval_modes: {
            all: 'Alle',
            any: 'Enhver',
        },
        cmp_integration: {
            title: 'AesirX CMP-integration',
            back_to_consent: 'Tilbage til Samtykke',
            back_to_integration: 'Tilbage til Integration',
            webhook_status: 'Webhook-status',
            events_received: 'Modtagne Hændelser',
            web_consent_records: 'Web-samtykkeposter',
            unmapped_purposes: 'Ikke-kortlagte Formål',
            integration_management: 'Integrationsstyring',
            webhook_configuration: 'Webhook-konfiguration',
            webhook_config_desc: 'Konfigurer CMP-webhook-endepunktet og administrer HMAC-hemmeligheden.',
            purpose_mapping: 'Formålskortlægning',
            purpose_mapping_desc: 'Kortlæg CMP-formålsidentifikatorer til GRC-samtykkekatalogets formål.',
            bulk_sync: 'Massesynkronisering',
            bulk_sync_desc: 'Genanvend formålstilknytninger på alle ikke-tilknyttede samtykkeposter.',
            consent_analytics: 'Web-samtykkeanalyse (Seneste 30 Dage)',
            consent_grant_rate: 'Samtykketildelingsrate',
            top_domain: 'Top Domæne',
            records: 'poster',
            banner_interactions: 'Bannerinteraktioner',
            consent_actions: 'Samtykkehandlinger',
            opt_in_trend: 'Opt-in/Opt-out Tendens',
            records_by_domain: 'Optegnelser efter domæne',
            date: 'Dato',
            grants: 'Tildelinger',
            denials: 'Afvisninger',
            not_configured: 'Ikke Konfigureret',
            load_error: 'Kunne ikke indlæse integrationsdata',
            webhook_not_configured: 'Webhook er ikke konfigureret.',
            webhook_not_configured_msg: 'Venligst {link} først.',
            configure_webhook_link: "konfigurer webhook'en",
            setup_wizard: 'Opsætningsguide',
            step_configure_webhook: 'Konfigurer Webhook',
            step_map_purposes: 'Kortlæg Formål',
            step_run_sync: 'Kør Indledende Massesynkronisering',
            step_verify_data: 'Verificér Data i Kontrolpanelet',
            configure: 'Konfigurer',
            view_dashboard: 'Vis Kontrolpanel',
            syncing: 'Synkroniserer...',
            sync_now: 'Synkronisér Nu',
            sync_completed: 'Synkronisering afsluttet: {imported} importeret, {skipped} sprunget over, {failed} fejlet.',
            sync_failed: 'Synkronisering mislykkedes',
            last_sync_result: 'Seneste Synkroniseringsresultat',
            imported: 'Importeret',
            skipped: 'Sprunget Over',
            errors: 'Fejl',
            index: 'Indeks',
            error: 'Fejl',
            sync_history: 'Synkroniseringshistorik',
            no_sync_jobs: 'Ingen synkroniseringsjobs registreret endnu.',
            load_sync_error: 'Kunne ikke indlæse synkroniseringsdata',
            purpose_mapping_title: 'CMP-formålskortlægning',
            auto_mapping: 'Automatisk Kortlægning...',
            auto_map_purposes: 'Automatisk Kortlæg Formål',
            unmapped_warning: '{count} ikke-kortlagte formål -- disse CMP-formål skal kortlægges til GRC-formål.',
            mapping_saved: 'Kortlægning gemt.',
            mapping_deleted: 'Kortlægning slettet.',
            auto_map_complete: 'Automatisk kortlægning afsluttet: {mapped} kortlagt, {unmatched} ikke-matchet.',
            auto_map_failed: 'Automatisk kortlægning mislykkedes',
            save_mapping_error: 'Kunne ikke gemme kortlægning',
            delete_mapping_error: 'Kunne ikke slette kortlægning',
            load_data_error: 'Kunne ikke indlæse data',
            cmp_purpose: 'CMP-formål',
            grc_purpose: 'GRC-formål',
            select_grc_purpose: '-- Vælg GRC-formål --',
            auto_mapped: 'Automatisk Kortlagt',
            mapped: 'Kortlagt',
            unmapped: 'Ikke Kortlagt',
            no_mappings: 'Ingen formålskortlægninger fundet. Kortlægninger oprettes automatisk, når samtykkehændelser modtages.',
            total_mappings: 'Total: {count} kortlægning(er)',
            webhook_config_title: 'Webhook-konfiguration',
            current_configuration: 'Nuværende Konfiguration',
            setup_webhook: 'Opsæt Webhook',
            setup_webhook_desc: "Konfigurer AesirX CMP-webhook'en til at modtage samtykkehændelser.",
            allowed_domains: 'Tilladte Domæner (ét pr. linje)',
            webhook_secret_warning: 'Webhook-hemmelighed (gem denne nu -- den vises ikke igen):',
            last_event: 'Seneste Hændelse',
            none_received: 'Ingen',
            update_domains: 'Opdater Domæner',
            pause_webhook: 'Sæt webhook på pause',
            resume_webhook: 'Genoptag Webhook',
            regenerate_secret: 'Gendan Hemmelighed',
            configuring: 'Konfigurerer...',
            create_webhook_config: 'Opret Webhook-konfiguration',
            webhook_endpoint: 'Webhook-endepunkt',
            webhook_endpoint_desc: 'Konfigurer din AesirX CMP til at sende samtykkehændelser til:',
            webhook_signature_note: 'Inkludér HMAC-SHA256-signaturen i {header}-headeren.',
            configured_success: 'Webhook konfigureret med succes.',
            config_updated: 'Konfiguration opdateret.',
            config_error: 'Kunne ikke konfigurere webhook',
            update_error: 'Kunne ikke opdatere konfiguration',
            toggle_error: 'Kunne ikke skifte webhook',
            deactivated: 'Webhook deaktiveret.',
            deactivate_error: 'Kunne ikke deaktivere webhook',
            page_title: 'CMP Integration',
            webhook_config_page_title: 'CMP Webhook-konfiguration',
            bulk_sync_page_title: 'Re-map Records',
            purpose_mapping_page_title: 'Formålskortlægning',
            data_loaded: 'CMP integrationsdata blev indlæst',
            sync_all: 'Synkroniser alle',
            sync_selected: 'Synkroniser valgte',
            sync_status: 'Synkroniseringsstatus',
            last_sync: 'Sidste synkronisering',
            no_records: 'Ingen poster fundet.',
            sync_in_progress: 'Synkronisering i gang...',
            sync_complete: 'Synkronisering fuldført.',
            sync_error: 'Synkronisering mislykkedes. Prøv venligst igen.',
            map_purpose: 'Tilknyt formål',
            unmap: 'Fjern tilknytning',
            mapping_status: 'Tilknytningsstatus',
            save_mapping: 'Gem tilknytning',
            remap_unmapped_records: 'Re-map Unmapped Records',
            remap_records: 'Re-map Records',
            add_mappings_first: 'Tilføj formålskortlægninger først (trin 2)',
            remap_result: 'Re-mapping resultat',
            remap_result_desc: 'Behandlede alle samtykkeposter med ikke-kortlagte eller delvist kortlagte formål.',
            total_processed: 'Samlet behandlet',
            newly_mapped: 'Nyligt kortlagt',
            still_unmapped_label: 'Stadig ikke kortlagt',
            still_unmapped_msg: '{count} post(er) har stadig ikke-kortlagte formål. {link} og klik derefter på Re-map igen.',
            add_more_mappings: 'Tilføj flere kortlægninger',
            remap_complete: 'Gentilknytning fuldført: {remapped} nyligt kortlagt, {still_unmapped} stadig ikke kortlagt.',
            add_mapping: 'Tilføj kortlægning',
            cmp_purpose_id_label: 'CMP Formåls-id *',
            cmp_purpose_id_required: 'CMP Formåls-id er påkrævet',
            cmp_purpose_name_label: 'CMP Formålsnavn',
            grc_purpose_label: 'GRC Formål',
        },
        propagation: {
            title: 'Tilbagetrækningsudbredelse',
            systems: 'Tilknyttede systemer',
            status: 'Status',
            confirmed: 'Bekræftet',
            pending: 'Afventer',
            failed: 'Fejlet',
            confirm: 'Bekræft tilbagetrækning',
            fail: 'Markér som fejlet',
            failure_reason: 'Fejlårsag',
            can_close: 'Alle systemer bekræftet — arbejdsgang kan lukkes',
            cannot_close: 'Venter på systembekræftelser',
            no_tasks: 'Ingen udbredelsesopgaver.',
            confirm_success: 'Systemtilbagetrækning bekræftet.',
            fail_success: 'System markeret som fejlet.',
            propagate_success: 'Tilbagetrækning udbredt til tilknyttede systemer.',
            propagate: 'Forplant til systemer',
            add_system: 'Tilføj system',
            remove_system: 'Fjern',
            system_id: 'System-id',
            system_name: 'Systemnavn',
            owner_id: 'Ejer ID',
            propagate_error: 'Det lykkedes ikke at udbrede tilbagetrækningen. Prøv venligst igen.',
            no_systems: 'Tilføj mindst ét system.',
            page_title: 'Udbredelsesforplantning',
            workflow_id: 'Vælg Workflow for tilbagetrækning',
            system_id_hint: 'f.eks. crm-001, erp-prod',
            owner_id_hint: 'Systemejer',
            elapsed: 'Forløbet',
        },
        sensitive: {
            title: 'Følsomme datapolitikker',
            flag: 'Markér som følsom',
            categories: 'Følsomme kategorier',
            enhanced_review: 'Kræv udvidet gennemgang',
            enhanced_review_success: 'Udvidet gennemgang påkrævet.',
            flag_success: 'Politik markeret som følsom.',
            no_sensitive: 'Ingen følsomme datapolitikker.',
        },
        reconciliation: {
            title: 'Uoverensstemmelsesafstemning',
            create: 'Ny afstemningsopgave',
            system: 'System',
            owner: 'Ejer',
            description: 'Beskrivelse',
            resolution: 'Løsning',
            resolve: 'Løs',
            update_grc: 'Opdatér GRC',
            update_cmp: 'Opdatér CMP',
            accept_difference: 'Acceptér forskel',
            escalate: 'Eskalér',
            resolution_notes: 'Løsningsnoter',
            no_tasks: 'Ingen afstemningsopgaver.',
            resolve_success: 'Afstemningsopgave løst.',
            create_success: 'Afstemningsopgave oprettet.',
            open: 'Åben',
            resolved: 'Løst',
            escalated: 'Eskaleret',
            dashboard_open: 'Åbne opgaver',
            dashboard_resolved: 'Løste opgaver',
            dashboard_escalated: 'Eskalerede opgaver',
            page_title: 'Mismatch afstemning',
            my_tasks: 'Mine opgaver',
            all_tasks: 'Alle opgaver',
            severity: 'Sværhedsgrad',
        },
        sync: {
            title: 'CMP-synkronisering',
            direction: 'Retning',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Konflikt',
            resolve_conflict: 'Løs konflikt',
            no_events: 'Ingen synkroniseringshændelser.',
            resolve_success: 'Synkroniseringskonflikt løst.',
            create_success: 'Synkroniseringshændelse registreret.',
            page_title: 'CMP Synkroniser',
            detect_drift: 'Opdag konflikter',
            detecting: 'Registrerer...',
            drift_result: '{detected} konflikt(er) fundet ud af {total} formål(er) sammenlignet.',
            no_drift: 'Ingen nye konflikter opdaget.',
            drift_error: 'Konfliktregistrering kunne ikke køres.',
            resolve_modal_title: 'Løs konflikt',
            resolve_field: 'Felt',
            resolve_grc_value: 'GRC værdi',
            resolve_cmp_value: 'CMP Værdi',
            accept_cmp: 'Accepter CMP værdi',
            accept_cmp_desc: 'Opdater GRC til at matche CMP - værdien CMP bliver den nye GRC-værdi.',
            keep_grc: 'Behold GRC-værdi',
            keep_grc_desc: 'Luk denne konflikt uden at ændre GRC – giv ejeren CMP besked om at justere.',
            resolve_applied: 'Konflikt løst. GRC-formål opdateret til at matche CMP.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Denne parathedsrapport er vejledende. Den godkender, samler, materialiserer, indgiver eller sender ikke dossieret.',
            run_label: 'Kør pre-flight',
            running: 'Kører før flyvning...',
            assemble_label: 'Autosamlet pakke',
            assembling: 'Samling⬦',
            verdict_label: 'Dommen før flyvningen',
            tokens_label: '≈ {count} tokens',
            remediation_label: 'Udbedring',
            no_gaps: 'Ingen huller før flyvningen registreret. Arkivet er klar til at samles.',
            override_label: 'Tilsidesæt blokafgørelse - samling vil være revisionsstemplet.',
            accept_label: 'Accepter rapporten',
            reject_label: 'Afvis rapport',
            assembled_label: 'Indleveringspakke samlet',
            package_id_label: 'Pakke-id',
            package_filename_label: 'Filnavn',
            package_hash_label: 'Pakkehash',
            evidence_count_label: '{count} beviselementer samlet',
            error_generic: 'Kunne ikke køre pre-flight eller samle pakken. Prøv igen, eller kontroller arkiveringsstatus.',
        },
        errors: {
            fetch: 'Kunne ikke indlæse vurderinger.',
            save: 'Kunne ikke gemme vurdering.',
            fetch_gap_report_failed: 'Kunne ikke hente mangelrapport.',
            fetch_transfers_failed: 'Kunne ikke hente overførsler.',
            save_transfer_failed: 'Kunne ikke gemme overførsel.',
            submit_review_failed: 'Kunne ikke indsende til gennemgang.',
            process_decision_failed: 'Kunne ikke behandle beslutning.',
            deprecate_failed: 'Kunne ikke udfase.',
            evaluate_risk_failed: 'Kunne ikke evaluere risiko.',
            fetch_safeguards_failed: 'Kunne ikke hente sikkerhedsforanstaltninger.',
            save_safeguard_failed: 'Kunne ikke gemme sikkerhedsforanstaltning.',
            update_safeguard_status_failed: 'Kunne ikke opdatere status for sikkerhedsforanstaltning.',
        },
        nav: {
            dpias: 'Konsekvensanalyser',
            transfers: 'Overførselsvurderinger',
            dashboard: 'Kontrolpanel',
            gap_report: 'Mangelrapport',
        },
        risk_level_critical: 'Kritisk risiko',
        risk_level_high: 'Høj risiko',
        risk_level_medium: 'Middel risiko',
        risk_level_low: 'Lav risiko',
        severity_critical: 'Kritisk alvorlighed',
        severity_high: 'Høj alvorlighed',
        severity_medium: 'Middel alvorlighed',
        severity_low: 'Lav alvorlighed',
        overdue: 'Forsinket',
        framework_filter_label: 'Ramme',
        aria_filter_by_framework: 'Filtrer efter rammer',
        dashboard: {
            title: 'Vurderingskontrolpanel',
            page_title: 'Vurderingsdashboard',
            announce_loaded: 'Vurderingsdashboard indlæst',
            announce_chart_view: 'Viser diagramvisning',
            announce_table_view: 'Viser datatabelvisning',
            aria_metrics_table: 'Vurderingsdashboard-målinger',
            metric_col: 'Metrik',
            value_col: 'Værdi',
            dpia_prefix: 'DPIA',
            transfer_prefix: 'Overførsel',
            risk_prefix: 'Risiko',
            aria_dpias_by_status: 'DPIAer efter status',
            aria_transfers_by_status: 'Overførsler efter status',
            aria_top_risks: 'Toprisici',
            aria_top_risks_table: 'Toprisici på tværs af DPIAer',
            overdue_dpias_attention: '{count} forsinkede DPIA-vurderinger kræver opmærksomhed',
            no_overdue_dpias: 'Ingen forsinkede DPIA-vurderinger',
            overdue_transfers_attention: '{count} forsinkede overførselsvurderinger kræver opmærksomhed',
            no_overdue_transfers: 'Ingen forsinkede overførselsvurderinger',
            dpias_overview: 'Oversigt over Konsekvensanalyser',
            transfers_overview: 'Oversigt over Overførselsvurderinger',
            overdue_items: 'Forfaldne Emner',
            overdue_dpias: 'Forfaldne Konsekvensanalyser',
            overdue_transfers: 'Forfaldne Overførselsvurderinger',
            count: 'Antal',
            no_data: 'Ingen data tilgængelige.',
            no_risks: 'Ingen risici fundet.',
            no_dashboard_data: 'Ingen kontrolpaneldata tilgængelige.',
            fetch_error: 'Kunne ikke hente kontrolpaneldata. Prøv venligst igen.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'DPIAer',
            announce_submitted_for_review: 'DPIA indsendt til gennemgang',
            announce_deprecated: 'DPIA forældet',
            announce_risks_loaded: '{count} risiko(er) indlæst',
            announce_mitigations_loaded: '{count} afbødning(er) indlæst',
            announce_risk_deleted: 'Risiko slettet',
            announce_mitigation_status_updated: 'Afbødningsstatus opdateret',
            aria_filter: 'Filtrer DPIAer',
            aria_filter_by_status: 'Filtrer efter status',
            aria_filter_by_lead: 'Filtrer efter vurderingsleder',
            aria_table: 'Konsekvensanalyser for databeskyttelse',
            announce_found: '{count} DPIA(er) fundet',
            announce_decision: 'DPIA {decision}',
            mitigation_start: 'Start',
            mitigation_mark_implemented: 'Markér implementeret',
            mitigation_verify: 'Verificér',
            title: 'Konsekvensanalyser for Databeskyttelse',
            new: 'Ny Konsekvensanalyse',
            edit: 'Rediger Konsekvensanalyse',
            dpia_title: 'Titel',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Aktivitet',
            risk_score: 'Risikoscore',
            risks: 'Risici',
            mitigations: 'Afbødninger',
            add_risk: 'Tilføj Risiko',
            add_mitigation: 'Tilføj Afbødning',
            necessity: 'Nødvendighed / Proportionalitet',
            likelihood: 'Sandsynlighed',
            impact: 'Konsekvens',
            severity_label: 'Alvorlighedsgrad',
            purpose: 'Formål',
            consultation_notes: 'Høringsnoter',
            dpo_opinion: 'DPO-udtalelse',
            processing_activity_ids: "Behandlingsaktivitets-ID'er (kommasepareret)",
            system_ids: "System-ID'er (kommasepareret)",
            vendor_ids: "Leverandør-ID'er (kommasepareret)",
            data_types: 'Datatyper (kommasepareret)',
            data_types_short: 'Datatyper',
            data_subjects: 'Registrerede Personer (kommasepareret)',
            assessment_lead_id: 'Vurderingsansvarlig-ID',
            assessment_lead: 'Vurderingsansvarlig',
            dpo_id: 'DPO-ID',
            reviewer_ids: "Bedømmer-ID'er (kommasepareret)",
            approval_mode: 'Godkendelsestilstand',
            approval_all: 'Alle',
            approval_any: 'Enhver',
            require_dpo_signoff: 'Kræv DPO-underskrift',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Gennemgangsdato',
            submit_for_review: 'Indsend til Gennemgang',
            approve_reject: 'Godkend/Afvis',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Udfas',
            deprecated: 'Udfaset',
            hide_risks: 'Skjul Risici',
            hide_mitigations: 'Skjul Afbødninger',
            comments: 'Kommentarer',
            submit_decision: 'Indsend Beslutning',
            category_label: 'Kategori',
            category_data_breach: 'Databrud',
            category_unauthorized_access: 'Uautoriseret Adgang',
            category_function_creep: 'Funktionsglidning',
            category_re_identification: 'Genidentificering',
            category_discrimination: 'Diskrimination',
            category_surveillance: 'Overvågning',
            category_other: 'Anden',
            affected_rights: 'Berørte Rettigheder (kommasepareret)',
            no_risks: 'Ingen risici fundet.',
            no_mitigations: 'Ingen afbødninger fundet.',
            no_dpias: "Ingen DPIA'er fundet.",
            mitigation_type: 'Type',
            mitigation_technical: 'Teknisk',
            mitigation_organizational: 'Organisatorisk',
            mitigation_contractual: 'Kontraktuel',
            risk_ids: "Risiko-ID'er (kommasepareret)",
            responsible_party: 'Ansvarlig Part',
            responsible_party_placeholder: 'Eller indtast navn manuelt',
            affected_rights_placeholder: 'Valgte rettigheder vises her',
            affected_rights_freetext_placeholder: 'Indtast berørte rettigheder (kommasepareret)',
            responsible: 'Ansvarlig',
            target_date: 'Måldato',
            created: 'Oprettet',
            fetch_error: "Kunne ikke hente DPIA'er. Prøv venligst igen.",
            save_error: 'Kunne ikke gemme DPIA. Prøv venligst igen.',
            submit_review_error: 'Kunne ikke indsende DPIA til gennemgang. Prøv venligst igen.',
            decision_error: 'Kunne ikke behandle beslutning. Prøv venligst igen.',
            deprecate_error: 'Kunne ikke udfase DPIA. Prøv venligst igen.',
            fetch_risks_error: 'Kunne ikke hente risici. Prøv venligst igen.',
            save_risk_error: 'Kunne ikke gemme risiko. Prøv venligst igen.',
            delete_risk_error: 'Kunne ikke slette risiko. Prøv venligst igen.',
            fetch_mitigations_error: 'Kunne ikke hente afbødninger. Prøv venligst igen.',
            save_mitigation_error: 'Kunne ikke gemme afbødning. Prøv venligst igen.',
            update_mitigation_status_error: 'Kunne ikke opdatere afbødningsstatus. Prøv venligst igen.',
            link_form_template: 'Link Regulatory Form Skabelon',
            link_form_template_placeholder: 'Vælg en DPIA formularskabelon (valgfrit)',
            link_form_template_none: 'Ingen',
            linked_form: 'Sammenkædet formular',
            linked_form_creating: 'Opretter linket formular...',
            linked_form_error: 'Kunne ikke oprette linket formularforekomst. DPIA blev oprettet uden en linket formular.',
            view_linked_form: 'Se formular',
            announce_created_with_form: 'DPIA oprettet med linket formularforekomst',
            creation_mode: 'DPIA Oprettelsestilstand',
            mode_standard: 'Standard DPIA',
            mode_template: 'Brug Regulatory Form Skabelon',
            select_form_template: 'Vælg formularskabelon',
            create_and_fill_form: 'Opret og udfyld formular',
            template_sections_title: 'Skabelonafsnit',
            field_singular: 'felt',
            field_plural: 'felter',
            no_sections: 'Ingen sektioner fundet i denne skabelon.',
            template_mode_hint: 'Efter oprettelsen vil du blive omdirigeret til at udfylde den fulde reguleringsformular.',
            linked_dossier_filings: 'Tilknyttede dossierarkiveringer',
            no_linked_dossier_filings: 'Ingen dossierarkiveringer tilknyttet denne DPIA.',
            view_dossier_filing: 'Vis arkivering',
            hide_dossier_filings: 'Skjul dossierarkiveringer',
            supporting_evidence: 'Understøttende bevis',
            hide_supporting_evidence: 'Skjul beviser',
            no_supporting_evidence: 'Ingen støttende beviser vedhæftet.',
            upload_supporting_evidence: 'Upload understøttende beviser',
            view_form_responses: 'Se formularsvar',
            regulatory_framework: 'Regulatorisk ramme',
            all_frameworks: 'Alle rammer',
            no_templates_for_market: 'Ingen skabeloner tilgængelige for det valgte marked. Vælg venligst et andet marked eller opret en skabelon.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Dataoverførsler',
            announce_found: '{count} overførselsvurdering(er) fundet',
            announce_submitted_for_review: 'Overførsel indsendt til gennemgang',
            announce_deprecated: 'Overførsel forældet',
            announce_decision: 'Overførsel {decision}',
            announce_risk_eval_completed: 'Risikovurdering afsluttet',
            announce_safeguard_status_updated: 'Sikkerhedsstatus opdateret',
            announce_safeguards_loaded: '{count} sikkerhedsforanstaltning(er) indlæst',
            aria_filter: 'Filtrer overførselsvurderinger',
            aria_filter_by_status: 'Filtrer efter status',
            aria_filter_by_mechanism: 'Filtrer efter overførselsmekanisme',
            aria_table: 'Dataoverførselsvurderinger',
            title: 'Grænseoverskridende Overførselsvurderinger',
            new: 'Ny Overførsel',
            edit: 'Rediger Overførsel',
            name: 'Navn',
            from_country: 'Fra Land',
            to_country: 'Til Land',
            safeguards: 'Sikkerhedsforanstaltninger',
            risk_level: 'Risikoniveau',
            assessment_notes: 'Vurderingsnoter',
            mechanism: 'Mekanisme',
            transfer_mechanism: 'Overførselsmekanisme',
            transfer_mechanism_detail: 'Overførsel Mekanisme Detalje',
            all_mechanisms: 'Alle mekanismer',
            mechanism_other: 'Andet',
            data_types: 'Datatyper (kommasepareret)',
            data_subjects: 'Registrerede (kommasepareret)',
            purpose: 'Formål',
            recipient_name: 'Modtagernavn',
            create_success: 'Overførselsvurdering oprettet.',
            update_success: 'Overførselsvurdering opdateret.',
            recipient_details: 'Modtageroplysninger',
            recipient_details_placeholder: 'Modtagerens rolle, placering og systemer samt oplysninger om videreoverførsel',
            vendor_id: 'Leverandør-ID',
            processing_activity_ids: 'Behandlingsaktivitets-ID-er (kommasepareret)',
            reviewer_ids: 'Gennemgåer-ID-er (kommasepareret)',
            approval_mode: 'Godkendelsestilstand',
            approval_all: 'Alle',
            approval_any: 'Enkelt godkendelse er nok',
            require_dpo_signoff: 'Kræv DPO-godkendelse',
            review_date: 'Gennemgangsdato',
            created: 'Oprettet',
            deprecated: 'Udfaset',
            submit_for_review: 'Indsend for Gennemgang',
            approve_reject: 'Godkend/Afvis',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Udfas',
            risk_eval: 'Risikoevaluering',
            hide_safeguards: 'Skjul sikkerhedsforanstaltninger',
            mechanism_proof: 'Mekanisme bevis',
            hide_mechanism_proof: 'Skjul mekanismebevis',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Ingen mekanisme bevis vedlagt.',
            upload_mechanism_proof: 'Upload mekanisme bevis',
            comments: 'Kommentarer',
            submit_decision: 'Indsend beslutning',
            data_sensitivity: 'Datafølsomhed (1-5)',
            country_risk: 'Landerisiko (1-5)',
            safeguard_adequacy: 'Sikkerhedsforanstaltning Tilstrækkeligheds (1-5)',
            volume_factor: 'Mængdefaktor (1-5)',
            evaluate: 'Evaluer',
            add_safeguard: 'Tilføj sikkerhedsforanstaltning',
            responsible_party: 'Ansvarlig part',
            responsible_party_placeholder: 'Eller indtast navn manuelt',
            responsible: 'Ansvarlig',
            implementation_date: 'Implementeringsdato',
            implementation: 'Implementering',
            expiry_date: 'Udløbsdato',
            expiry: 'Udløb',
            expire: 'Udløb',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Ingen sikkerhedsforanstaltninger fundet.',
            no_transfers: 'Ingen overførsel vurderinger fundet.',
            fetch_error: 'Kunne ikke hente overførselsvurderinger. Prøv venligst igen.',
            save_error: 'Kunne ikke gemme overførselsvurdering. Prøv venligst igen.',
            submit_review_error: 'Kunne ikke indsende til gennemgang. Prøv venligst igen.',
            decision_error: 'Kunne ikke behandle beslutning. Prøv venligst igen.',
            deprecate_error: 'Kunne ikke udfase overførsel. Prøv venligst igen.',
            risk_eval_error: 'Kunne ikke evaluere risiko. Prøv venligst igen.',
            fetch_safeguards_error: 'Kunne ikke hente sikkerhedsforanstaltninger. Prøv venligst igen.',
            save_safeguard_error: 'Kunne ikke gemme sikkerhedsforanstaltning. Prøv venligst igen.',
            update_safeguard_status_error: 'Kunne ikke opdatere sikkerhedsstatus. Prøv venligst igen.',
            safeguard_type_encryption_in_transit: 'Kryptering under transport',
            safeguard_type_encryption_at_rest: 'Kryptering i hvile',
            safeguard_type_access_controls: 'Adgangskontroller',
            safeguard_type_contractual_clauses: 'Kontraktmæssige klausuler',
            safeguard_type_data_minimization: 'Dataminimering',
            safeguard_type_pseudonymization: 'Pseudonymisering',
            safeguard_type_audit_rights: 'Revisionsrettigheder',
            safeguard_type_breach_notification: 'Brudnotifikation',
            safeguard_type_data_localization: 'Datalokalisering',
            safeguard_type_other: 'Andet',
            activate: 'Aktiver',
            all_statuses: 'Alle statusser',
            any: 'Any',
            mechanism_detail: 'Mekanismedetalje',
            safeguard_types: {
                encryption_in_transit: 'Kryptering under overførsel',
                encryption_at_rest: 'Kryptering i hvile',
                access_controls: 'Adgangskontroller',
                contractual_clauses: 'Kontraktuelle klausuler',
                data_minimization: 'Dataminimering',
                pseudonymization: 'Pseudonymisering',
                audit_rights: 'Revisionsrettigheder',
                breach_notification: 'Brudmeddelelse',
                data_localization: 'Datalokalisering',
                other: 'Andet',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Gap-rapport for vurderinger',
            announce_loaded: 'Gap-rapport indlæst. {count} gap(s) identificeret',
            aria_activities_without_dpia: 'Aktiviteter uden DPIA-vurdering',
            aria_activities_unclassified: 'Aktiviteter med uklassificeret DPIA-krav',
            activities_unclassified_section_title: 'Aktiviteter med uklassificeret DPIA-krav',
            activities_unclassified_description: 'Disse aktiviteter er ikke gennemgået for at afgøre, om en DPIA er lovpligtig. Klassificér hver enkelt for at fjerne signalet.',
            activities_unclassified_empty: 'Alle aktive aktiviteter er klassificeret.',
            aria_unmitigated_risks: 'Uafbødede høje og kritiske risici',
            aria_transfers_without_safeguards: 'Overførsler uden tilstrækkelige sikkerhedsforanstaltninger',
            aria_expired_assessments: 'Udløbne vurderinger der kræver fornyelse',
            title: 'Vurderingsmangelrapport',
            no_dpia: 'Aktiviteter Uden Konsekvensanalyse',
            no_assessment: 'Overførsler Uden Vurdering',
            unmitigated: 'Konsekvensanalyser med Uafbødede Risici',
            activity_id: 'Aktivitets-ID',
            dpia_id: 'DPIA-ID',
            assessment_id: 'Vurderings-ID',
            expired_assessments: 'Udløbne vurderinger',
            no_gaps_activities: 'Ingen mangler fundet. Alle aktiviteter har DPIA-er.',
            no_unmitigated_risks: 'Ingen uafhjulpne høje/kritiske risici fundet.',
            no_gaps_safeguards: 'Ingen mangler fundet. Alle overførsler har tilstrækkelige sikkerhedsforanstaltninger.',
            no_expired: 'Ingen udløbne vurderinger fundet.',
            no_report_data: 'Ingen mangel rapport data tilgængelige.',
            fetch_error: 'Kunne ikke hente mangelrapport. Prøv venligst igen.',
            no_expired_assessments: 'Ingen udløbne vurderinger.',
            no_gaps_dpia: 'Ingen dpia gaps fundet.',
            no_data: 'Ingen data tilgængelige.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Dossieranmeldelsesdetalje',
            page_title_list: 'Dossieranmeldelsessager',
            announce_filings_found: '{count} anmeldelse(r) fundet',
            announce_results: '{count} resultater',
            aria_filter_by_status: 'Filtrer efter status',
            title: 'Dossiersager',
            nav_label: 'Dossierarkivering',
            create: 'Opret dossierarkivering',
            form_type: 'Formulartype',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Reference',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Indsendt dato',
            outcome: 'Udfald',
            parent_filing: 'Forælder',
            no_filings: 'Der blev ikke fundet nogen dossier.',
            fetch_error: 'Kunne ikke hente dossierer. Prøv venligst igen.',
            save_error: 'Kunne ikke oprette dossierarkivering. Prøv venligst igen.',
            created: 'Dossierarkiv oprettet.',
            loaded: 'Dossier arkivering indlæst.',
            not_found: 'Dossierarkivering ikke fundet.',
            back_to_list: 'Tilbage til listen',
            section_assignments: 'Sektionsopgaver',
            assign_sections: 'Tildel sektioner',
            section_id: 'Sektion',
            department: 'afdeling',
            assignee: 'Opdragsgiver',
            deadline: 'Deadline',
            no_assignments: 'Ingen sektionsopgaver endnu.',
            sections_assigned_msg: 'Sektioner tildelt.',
            assign_error: 'Kunne ikke tildele sektioner. Prøv venligst igen.',
            lifecycle: 'Arkiveringslivscyklus',
            submission: 'Indsendelsesdetaljer',
            submission_channel: 'Indsendelseskanal',
            confirmation_number: 'Bekræftelsesnummer',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Indsend til gennemgang',
            submitted_for_review: 'Sendt til intern gennemgang.',
            submit_review_error: 'Kunne ikke sendes til gennemgang. Prøv venligst igen.',
            legal_signoff: 'Juridisk afmelding',
            legal_signoff_done: 'Juridisk afmelding gennemført.',
            legal_signoff_error: 'Det lykkedes ikke at fuldføre juridisk afmelding. Prøv venligst igen.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Registreringen kunne ikke godkendes. Prøv venligst igen.',
            materialisation_status_column: 'Indsendelsesartefakt',
            materialisation_na: 'Ikke relevant',
            materialisation_pending: 'Afventer',
            materialisation_success: 'Materialiseret',
            materialisation_failed: 'Mislykkedes',
            materialisation_banner_title: 'Indsendelsesartefakt kunne ikke oprettes',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Prøv igen',
            materialisation_retry_count: 'Tidligere forsøg igen:',
            materialisation_retry_success: 'Indsendelsesartefakt blev materialiseret.',
            materialisation_retry_announce: 'Forsøg igen lykkedes',
            filter_materialisation_failed: 'Vis kun materialiseringsfejl',
            column_title: 'Titel',
            column_form_type: 'Form',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Tillægsanmodning',
            supplement_requests: 'Supplerende anmodninger',
            supplement_response: 'Tillægssvar',
            record_supplement: 'Anmodning om registreringstillæg',
            supplement_recorded: 'Tillægsanmodning registreret.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Kunne ikke registrere tillægsanmodning. Prøv venligst igen.',
            submit_supplement: 'Indsend tillægssvar',
            supplement_submitted_msg: 'Supplerende svar indsendt.',
            supplement_submit_error: 'Kunne ikke indsende tillægssvar. Prøv venligst igen.',
            response_status: 'Svarstatus',
            response_notes: 'Svarnoter',
            resubmit: 'Indsend igen',
            resubmitted_msg: 'Dossier genindsendt.',
            resubmit_error: 'Kunne ikke genindsende dossier. Prøv venligst igen.',
            outcome_error: 'Resultatet kunne ikke registreres. Prøv venligst igen.',
            accepted: 'Accepteret',
            rejected: 'Afvist',
            close: 'Luk',
            closed_msg: 'Arkiv lukket.',
            close_error: 'Det lykkedes ikke at lukke arkiveringen. Prøv venligst igen.',
            change_filing: 'Opret ændringsarkivering',
            change_filing_created: 'Ændringsarkiv oprettet.',
            change_filing_error: 'Kunne ikke oprette ændringsarkivering. Prøv venligst igen.',
            change_reason: 'Årsag til forandring',
            filing_lineage: 'Arkivering Slægt',
            timers: 'Aktive timere',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Tillægsfrist',
            timer_resubmission_review: 'Genindsendelsesfrist',
            timer_department_input: 'Afdelingens frist',
            days_remaining: 'dage tilbage',
            days_overdue: 'dage forsinket',
            cover_letter: 'Følgebrev',
            checklist: 'Tjekliste',
            evidence_index: 'Evidensindeks',
            status_draft: 'Kladde',
            status_internal_review: 'Intern gennemgang',
            status_legal_signoff: 'Juridisk afmelding',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Supplement anmodet',
            status_supplement_submitted: 'Tillæg indsendt',
            status_resubmitted: 'Genindsendt',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Lukket',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Kladde',
            step_internal_review: 'Intern gennemgang',
            step_legal_signoff: 'Juridisk afmelding',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Supplement anmodet',
            step_supplement_submitted: 'Tillæg indsendt',
            step_resubmitted: 'Genindsendt',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Lukket',
            linked_dpia: 'Tilknyttet DPIA',
            linked_dpia_none: 'Ingen DPIA tilknyttet denne arkivering.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'Vis DPIA',
            linked_dpia_label: 'Denne dossierarkivering er tilknyttet følgende DPIA:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Indsendelse artefakt sundhed',
            page_title: 'Indsendelse Artefact Health',
            page_description: 'Opdag og afhjælp dossiermaterialiseringsproblemer. Listerne er tilpasset din organisation.',
            failed_title: 'Mislykkede materialiseringer',
            failed_empty: 'Ingen mislykkede materialiseringer - godt.',
            pending_title: 'Godkendt, men ikke gennemført',
            pending_empty: 'Alle godkendte ansøgninger har en dokumentation.',
            orphan_title: 'Forældreløse dossierer',
            orphan_description: 'Dossier skabt af materialisering, der ikke længere refereres til i nogen arkivering - typisk taberen af et samtidigt kapløb. Opført til undersøgelse; oprydning er manuel.',
            orphan_empty: 'Ingen forældreløse dossierer fundet.',
            materialise_button: 'Materialiseres nu',
            column_failure_reason: 'Årsag til fiasko',
            column_failed_at: 'Mislykkedes kl',
            column_retries: 'Forsøg igen',
            column_approved_at: 'Godkendt kl',
            column_dossier_id: 'Dossier ID',
            column_source_filing_id: 'Kildearkivering',
            column_dossier_type: 'Type',
            column_created_at: 'Oprettet kl',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Adgang nægtet',
        access_denied: 'Du har ikke de nødvendige tilladelser til at få adgang til Klassifikationsmodulet. Kontakt din administrator.',
        nav: {
            taxonomies: 'Taksonomier',
            labeling: 'Mærkning',
            lifecycle: 'Livscyklus',
            gaps: 'Mangelrapport',
            exports: 'Eksporter',
        },
        exports: {
            title: 'Klassifikationseksporter',
            csv_section: 'CSV-eksporter',
            pdf_section: 'PDF-rapporter',
            inventory: 'Opgørelse',
            classification_register: 'Klassifikationsregister',
            raci: 'RACI-matrix',
            exceptions: 'Undtagelser',
            evidence_pack: 'Dokumentationspakke',
            delta_report: 'Deltarapport',
            executive_summary: 'Ledelsesresumé',
            exporting: 'Eksporterer⬦',
            export_success: 'Eksport gennemført.',
            export_error: 'Eksport mislykkedes. Prøv igen.',
            csv_download: 'CSV-download klar.',
            pdf_download: 'PDF-generering startet.',
            no_data: 'Ingen data tilgængelige til eksport.',
        },
        delta: {
            title: 'Ændringsdetaljer',
            added: 'Tilføjet',
            removed: 'Fjernet',
            changed: 'Ændret',
            added_count: 'Tilføjet ({count})',
            removed_count: 'Fjernet ({count})',
            changed_count: 'Ændret ({count})',
            object_id: 'Objekt-ID',
            object_type: 'Objekttype',
            taxonomy: 'Taksonomi',
            level: 'Niveau',
            previous: 'Tidligere',
            current: 'Nuværende',
            no_records: 'Ingen poster i dette afsnit.',
            empty_section: 'Dette afsnit er tomt.',
        },
        schedule: {
            title: 'Planlæg opdagelsescyklus',
            enabled: 'Aktiveret',
            interval_days: 'Interval (dage)',
            interval_label: 'Kør hver N. dag',
            manual_only: 'Kun manuel',
            every_n_days: 'Hver {n}. dag',
            save: 'Gem tidsplan',
            cancel: 'Annuller',
            schedule_updated: 'Tidsplan opdateret.',
        },
        taxonomies: {
            title: 'Klassificeringstaksonomier',
            new: 'Ny Taksonomi',
            edit: 'Rediger Taksonomi',
            name: 'Navn',
            levels: 'Klassificeringsniveauer',
            seed_defaults: 'Initialiser standarder',
            scope: 'Omfang',
            scope_organization: 'Organisation',
            scope_module_specific: 'Modulspecifik',
            deprecate: 'Udfas',
            hide_levels: 'Skjul niveauer',
            versions: 'Versioner',
            hide_versions: 'Skjul versioner',
            add_level: 'Tilføj niveau',
            new_level: 'Nyt niveau',
            edit_level: 'Rediger niveau',
            code: 'Kode',
            label: 'Etiket',
            color: 'Farve',
            ordinal: 'Ordenstal',
            handling_requirements: 'Håndteringskrav',
            version_history: 'Versionshistorik',
            changes: 'Ændringer',
            failed_fetch: 'Kunne ikke hente taksonomier. Prøv venligst igen.',
            failed_save: 'Kunne ikke gemme taksonomi. Prøv venligst igen.',
            failed_activate: 'Kunne ikke aktivere taksonomi. Prøv venligst igen.',
            failed_deprecate: 'Kunne ikke udfase taksonomi. Prøv venligst igen.',
            failed_seed: 'Kunne ikke initialisere standardtaksonomier. Prøv venligst igen.',
            failed_fetch_levels: 'Kunne ikke hente taksonomiske niveauer. Prøv venligst igen.',
            failed_save_level: 'Kunne ikke gemme taksonomisk niveau. Prøv venligst igen.',
            failed_delete_level: 'Kunne ikke slette taksonomisk niveau. Prøv venligst igen.',
            failed_fetch_versions: 'Kunne ikke hente taksonomiversion. Prøv venligst igen.',
            loaded_count: 'Indlæst {count} taksonomier',
            defaults_seeded: 'Standard-taksonomier initialiseret.',
            no_levels: 'Ingen klassifikationsniveauer defineret — tilføj niveauer for at opbygge dit klassifikationshierarki.',
            no_versions: 'Ingen versionshistorik endnu — versioner oprettes, når en aktiv taksonomi opdateres.',
        },
        labeling: {
            title: 'Datamærkning',
            new: 'Ny Mærkat',
            edit: 'Rediger Mærkat',
            entity_type: 'Enhedstype',
            entity_id: 'Enheds-ID',
            taxonomy: 'Taksonomi',
            classification_level: 'Klassificeringsniveau',
            applied_by: 'Anvendt Af',
            applied_at: 'Anvendt Den',
            bulk_apply: 'Massetilføjelse',
            bulk_remove: 'Massefjernelse',
            all_taxonomies: 'Alle taksonomier',
            system: 'System',
            dataset: 'Datasæt',
            level_code_placeholder: 'Niveaukode...',
            select_taxonomy: 'Vælg taksonomi...',
            select_level: 'Vælg niveau...',
            rationale: 'Begrundelse',
            review_date: 'Gennemgangsdato',
            review: 'Gennemgang',
            selected: 'valgt',
            select_all_labels: 'Vælg alle etiketter',
            loaded_count: 'Indlæst {count} etiketter',
            hide_stale_labels: 'Skjul forældede etiketter',
            show_conflicts: 'Vis konflikter',
            hide_conflicts: 'Skjul konflikter',
            label_conflicts: 'Etiketkonflikter',
            conflict_reason: 'Konfliktårsag',
            labels: 'Etiketter',
            failed_fetch: 'Kunne ikke hente etiketter. Prøv venligst igen.',
            failed_fetch_taxonomies: 'Kunne ikke hente taksonomier. Prøv venligst igen.',
            failed_fetch_levels: 'Kunne ikke hente taksonomiske niveauer. Prøv venligst igen.',
            failed_save: 'Kunne ikke gemme etiket. Prøv venligst igen.',
            failed_review: 'Kunne ikke gennemgå etiket. Prøv venligst igen.',
            failed_bulk: 'Kunne ikke massemærke. Prøv venligst igen.',
            failed_stale: 'Kunne ikke hente forældede etiketter. Prøv venligst igen.',
            failed_conflicts: 'Kunne ikke hente etiketkonflikter. Prøv venligst igen.',
            review_permission_denied: 'Du har ikke tilladelse til at gennemgå dette label.',
            review_not_found: 'Label ikke fundet — det kan være blevet slettet.',
            approve_selected: 'Godkend valgte',
            reject_selected: 'Afvis valgte',
            approved: 'Godkendt',
            rejected: 'Afvist',
            labels_count: 'labels',
            failed_count: 'fejlede',
            confirm_approve: 'Bekræft godkendelse',
            confirm_reject: 'Bekræft afvisning',
            approve_n_labels: 'Godkend {count} labels?',
            reject_n_labels: 'Afvis {count} labels?',
            submit_for_review: 'Send til gennemgang',
            approve: 'Godkend',
            reject: 'Afvis',
            parent_system: 'Overordnet system',
            parent_system_placeholder: 'Vaelg overordnet system...',
            parent_system_help: 'Bruges til konfliktdetektering.',
            dataset_level: 'Datasaetniveau',
            parent_level: 'Overordnet niveau',
            bulk_created: 'Oprettet {created} etiket(ter)',
            bulk_skipped: '{skipped} sprunget over',
            bulk_all_skipped: 'Alle {skipped} sprunget over',
            ids_hint: 'Indtast et eller flere IDer adskilt med kommaer',
            delete_confirm: 'Slet etiket for "{id}"?',
        },
        lifecycle: {
            title: 'Datalivscyklusovervågning',
            new: 'Ny Post',
            current_stage: 'Nuværende Fase',
            last_transition: 'Sidste Overgang',
            transition: 'Overgang',
            view_history: 'Vis Historik',
            stages: {
                creation: 'Oprettelse',
                collection: 'Indsamling',
                processing: 'Behandling',
                storage: 'Opbevaring',
                retention: 'Opbevaringsperiode',
                disposal: 'Bortskaffelse',
                archived: 'Arkiveret',
                active: 'Aktiv',
                pending_deletion: 'Afventer sletning',
                deleted: 'Slettet',
                on_hold: 'Tilbageholdt',
            },
            all_stages: 'Alle faser',
            object_id: 'Objekt-ID',
            object_type: 'Objekttype',
            policy_reference: 'Politikreference',
            retention_period_days: 'Opbevaringsperiode (dage)',
            retention_basis: 'Opbevaringsgrundlag',
            deletion_method: 'Sletningsmetode',
            deletion_secure_erase: 'Sikker sletning',
            deletion_anonymization: 'Anonymisering',
            deletion_physical_destruction: 'Fysisk destruktion',
            deletion_other: 'Andet',
            responsible_party: 'Ansvarlig part',
            responsible_user_id: 'Ansvarlig Bruger ID',
            retention_expiry: 'Opbevaringsudløb',
            hold_reason: 'Tilbageholdelsesårsag',
            hold: 'Tilbagehold',
            release: 'Frigivelse',
            hide_events: 'Skjul hændelser',
            events_timeline: 'Hændelsestidslinje',
            add_event: 'Tilføj hændelse',
            event_type: 'Hændelsestype',
            create_event: 'Opret hændelse',
            from_stage: 'Fra fase',
            to_stage: 'Til fase',
            actor: 'Aktør',
            reason_placeholder: 'Årsag...',
            go: 'Go',
            show_expired_retentions: 'Vis udløbne opbevaringer',
            hide_expired_retentions: 'Skjul udløbne opbevaringer',
            expired_retentions_alert: 'Alarm for udløbne opbevaringer',
            failed_fetch: 'Kunne ikke hente livscyklusposter. Prøv venligst igen.',
            failed_save: 'Kunne ikke gemme livscykluspost. Prøv venligst igen.',
            failed_transition: 'Kunne ikke overføre livscyklusfase. Prøv venligst igen.',
            failed_hold: 'Kunne ikke placere tilbageholdelse. Prøv venligst igen.',
            failed_release: 'Kunne ikke frigive tilbageholdelse. Prøv venligst igen.',
            failed_fetch_events: 'Kunne ikke hente livscyklushændelser. Prøv venligst igen.',
            failed_create_event: 'Kunne ikke oprette livscyklushændelse. Prøv venligst igen.',
            failed_expired: 'Kunne ikke hente udløbne opbevaringer. Prøv venligst igen.',
            hold_reason_prompt: 'Indtast tilbageholdelsesårsag:',
            loaded_count: 'Indlæst {count} livscyklusposter',
            stage_transition_for: 'Faseovergang for {id}',
            event_created: 'Oprettet',
            event_updated: 'Opdateret',
            event_approved: 'Godkendt',
            event_deprecated: 'Forældet',
            event_proposed: 'Foreslået',
            event_reviewed: 'Gennemgået',
            event_archived: 'Arkiveret',
            event_restored: 'Gendannet',
        },
        gap_report: {
            title: 'Klassificeringsmangelrapport',
            unlabeled: 'Umærkede Data',
            missing_lifecycle: 'Manglende Livscyklus',
            stale_labels: 'Forældede Mærkater',
            run_detection: 'Kørsel Mangel Registrering',
            detecting: 'Registrerer...',
            detection_complete: 'Mangelregistrering fuldført. {count} mangel(er) registreret.',
            detection_failed: 'Mangel registrering mislykkede.',
            by_severity: 'Efter alvorlighed',
            by_gap_type: 'Efter mangeltype',
            all_gap_types: 'Alle mangeltyper',
            conflicting_labels: 'Modstridende etiketter',
            expired_retention: 'Udløbet opbevaring',
            missing_handling: 'Manglende håndtering',
            info: 'Info',
            gap_type: 'Mangeltype',
            object_id: 'Objekt-ID',
            object_type: 'Objekttype',
            detected_at: 'Registreret den',
            open: 'Åben',
            acknowledged: 'Bekræftet',
            remediated: 'Afhjulpet',
            accepted: 'Accepteret',
            acknowledge: 'Bekræft',
            remediate: 'Afhjælp',
            accept_risk: 'Accepter risiko',
            accept_risk_prompt: 'Indtast risiko accept begrundelse:',
            failed_fetch: 'Kunne ikke hente mangler. Prøv venligst igen.',
            failed_acknowledge: 'Kunne ikke bekræfte mangel. Prøv venligst igen.',
            failed_remediate: 'Kunne ikke afhjælpe mangel. Prøv venligst igen.',
            failed_accept_risk: 'Kunne ikke acceptere mangelrisiko. Prøv venligst igen.',
            failed_detect: 'Kunne ikke køre mangelregistrering. Prøv venligst igen.',
            loaded_count: 'Indlæst {count} klassifikationshuller',
            related_object_id: 'Relateret objekt-ID',
            related_object_type: 'Relateret objekttype',
            gap_types: {
                unlabeled_asset: 'Umaerket aktiv',
                missing_lifecycle: 'Manglende livscyklus',
                expired_retention: 'Udloebet opbevaring',
                extended_pending_deletion: 'Udvidet afventende sletning',
            },
        },
        label_workflow: {
            title: 'Etiketarbejdsgang',
            proposed: 'Foreslået',
            under_review: 'Under gennemgang',
            approved: 'Godkendt',
            deprecated: 'Udgået',
            submit_review: 'Indsend til gennemgang',
            approve: 'Godkend etiket',
            deprecate: 'Udgå',
            bulk_approve: 'Massegodkend',
            bulk_approve_success: 'Etiketter godkendt.',
            submit_success: 'Etiket indsendt til gennemgang.',
            approve_success: 'Etiket godkendt.',
            deprecate_success: 'Etiket udgået.',
        },
        review_tasks: {
            title: 'Gennemgangsopgaver',
            create: 'Ny gennemgangsopgave',
            system: 'System',
            owner: 'Ejer',
            description: 'Beskrivelse',
            resolution: 'Løsning',
            resolve: 'Løs',
            approve: 'Godkend',
            modify: 'Rediger',
            reject: 'Afvis',
            escalate: 'Eskaler',
            escalated: 'Eskalerede opgaver',
            no_tasks: 'Ingen gennemgangsopgaver.',
            resolve_success: 'Gennemgangsopgave løst.',
            status_pending: 'Afventende',
            status_resolved: 'Løst',
            status_escalated: 'Eskaleret',
            notes: 'Noter',
            create_success: 'Gennemgangsopgave oprettet.',
            no_tasks_detail: 'Ingen gennemgangsopgaver endnu — opret en opgave for at tildele labelgennemgange til systemejere.',
            label_id: 'Label-ID',
            system_id: 'System-ID',
            system_name: 'Systemnavn',
            owner_id: 'Ejer-ID',
            resolution_col: 'Loesning',
            notes_col: 'Noter',
        },
        exceptions: {
            title: 'Undtagelseskø',
            create: 'Ny undtagelse',
            severity: 'Alvorlighed',
            system: 'System',
            owner: 'Ejer',
            resolve: 'Løs',
            no_exceptions: 'Ingen undtagelser fundet.',
            resolve_success: 'Undtagelse løst.',
            dashboard_open: 'Åbne undtagelser',
            dashboard_resolved: 'Løste undtagelser',
            dashboard_total: 'I alt',
            dashboard_by_severity: 'Efter alvorlighed',
            status_open: 'Åben',
            status_resolved: 'Løst',
            status_accepted_risk: 'Accepteret risiko',
            create_success: 'Undtagelse oprettet.',
            no_exceptions_detail: 'Ingen undtagelser endnu — undtagelser oprettes ud fra alvorlige mangler, der kræver særlig håndtering.',
            gap_id: 'Mangel-ID',
            system_id: 'System-ID',
            system_name: 'Systemnavn',
            owner_id: 'Ejer-ID',
            resolution_notes: 'Løsningsnoter',
            resolution_notes_placeholder: 'Beskriv hvordan denne undtagelse blev løst...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Virksomhedsdatakort',
            inventory: 'Dataoversigt',
            total_systems: 'Systemer i alt',
            total_labels: 'Etiketter i alt',
            approved_count: 'Godkendte etiketter',
            proposed_count: 'Foreslåede etiketter',
            no_data: 'Ingen klassifikationsdata tilgængelig.',
            no_approved_labels: 'Ingen mærkede objekter endnu — et objekt vises på datakortet, så snart det har en klassificeringslabel, uanset om den er foreslået, under gennemgang eller godkendt.',
            loaded_count: 'Indlæst virksomhedsdatakort',
        },
        discovery: {
            title: 'Opdagelsescyklusser',
            create: 'Ny cyklus',
            cycle_name: 'Cyklusnavn',
            run_delta: 'Kør deltaregistrering',
            delta_success: 'Deltaregistrering gennemført.',
            create_success: 'Opdagelsescyklus oprettet.',
            no_cycles: 'Ingen opdagelsescyklusser.',
            status_created: 'Oprettet',
            status_scanning: 'Scanning',
            status_review: 'Gennemgå',
            status_approved: 'Godkendt',
            status_published: 'Udgivet',
            status_draft: 'Kladde',
            running: 'Kører',
            completed: 'Gennemført',
            cancelled: 'Annulleret',
            delta_result_summary: 'Fundet {added} tilføjet, {removed} fjernet, {changed} ændrede labels.',
            delta_results: 'Deltaresultater',
            view_results: 'Se resultater',
            scope_type: 'Omfang Type',
            scope_type_all: 'Alle',
            scope_type_systems: 'Systemer',
            scope_type_datasets: 'Datasæt',
            scope_ids: "Omfang ID'er",
            scope_ids_placeholder: "Indtast id'er adskilt af kommaer",
            cycle_explanation: 'En opdagelsescyklus scanner din datainfrastruktur for at identificere, klassificere og spore dataaktiver. Konfigurer omfanget til at målrette mod specifikke systemer eller datasæt.',
            empty_state_title: 'Ingen opdagelsescyklusser endnu',
            empty_state_description: 'Opret din første opdagelsescyklus for at begynde at scanne og klassificere dine dataaktiver.',
            empty_state_cta: 'Opret første cyklus',
            validation_name_required: 'Cyklusnavn er påkrævet',
            validation_scope_type_required: 'Omfangstype er påkrævet',
            validation_scope_ids_required: 'Der kræves mindst ét omfangs-id',
        },
        regulatory: {
            sensitivity_importance_info: 'Dataloven (lov 60/2024/QH15, krav. 18e4bfd1) kræver klassificering efter både følsomhed og betydning som separate dimensioner. Opret én taksonomi for hver dimension for at opfylde dette krav.',
            handling_rules_info: 'Hvert klassifikationsniveau bør definere håndteringsregler pr. b89981ac ("Definer håndteringsregler efter klassifikationsniveau").',
            template_internal_ids_info: "Alle klassifikationsskabeloner bruger interne id'er (f.eks. vn_data_law_data_classification_record). Ingen skabelon præsenteres som en officiel ministeriel formular.",
            ai_risk_tiers_info: 'AI-loven (lov 134/2025/QH15, krav 3a87ad63) definerer fire risikoniveauer for AI-systemer: minimal, begrænset, høj og uacceptabel. Opret en AI-risikotaksonomi med disse niveauer.',
            ai_risk_tier_minimal: 'Minimal',
            ai_risk_tier_limited: 'Begraenset',
            ai_risk_tier_high: 'Hoej',
            ai_risk_tier_unacceptable: 'Uacceptabel',
            ai_register_info: 'AI klassifikationsposter bør linke til en AI systemregisterindgang (req. 0f4475d3), der fanger: systemnavn, ejer, formål, implementeringskontekst, risikoniveau, leverandørinvolvering.',
            ai_template_internal_info: "AI-klassifikationsskabeloner bruger interne id'er (f.eks. AI-RISK-01). Der bruges ingen officielle AI-pladsholder-id'er (AI-HC-01 til AI-HC-08).",
            ai_audit_trail_info: 'AI klassifikationsændringer producerer et versioneret revisionsspor pr. UC-VN-06: skuespiller, tidsstempel, tidligere værdi, ny værdi og begrundelse.',
            lifecycle_data_d1_info: 'Livscyklusovergange kortlægges til Data Law procedure DATA-D1 trin: katalogisere, klassificere, definere håndteringsregler, indhente beviser, planlægge gennemgange.',
            lifecycle_deletion_link_info: 'Et klassificeret dataaktiv, der når udløbet af levetiden, bør udløse en sletteplanpost via Sletningsorkestreringsmodulet.',
            lifecycle_evidence_info: 'Hver livscyklustilstandsændring (udkast, aktiv, undergennemgang, arkiveret, slettet) fanger aktør, tidsstempel og begrundelse som en revisionssporpost.',
            escalation_timers_info: 'UC-VN-10 eskaleringsfrister: 5 hverdage for ejergennemgang, 48 timer for undtagelseseskalering, 72 timer for højrisiko/følsomme genstande.',
            escalation_owner_review: '5 arbejdsdage',
            escalation_exception: '48 timer',
            escalation_high_risk: '72 timer',
            gap_risk_areas_info: 'Datalovkrav. f6c3060a ("Datarisikovurdering") kræver gapanalyse på tværs af 4 risikoområder: privatliv, cybersikkerhed, adgangskontrol og operationelle risici.',
            gap_risk_area_privacy: 'Privatlivsbeskyttelse',
            gap_risk_area_cybersecurity: 'Cybersikkerhed',
            gap_risk_area_access: 'Adgangskontrol',
            gap_risk_area_operational: 'Operationel',
            gap_framework_filter_info: 'Filtrer huller efter lovgivningsmæssige rammer (VN_DATA_LAW eller VN_AI_LAW_2026) for at se relevante huller for hvert reguleringsområde.',
            framework_vn_data_law: 'VN Datalov',
            framework_vn_ai_law: 'VN AI-lov 2026',
            enterprise_map_approved_info: 'Dette datakort viser kun godkendte klassifikationsposter pr. UC-VN-10 workflowtrin 10 ("Godkendte etiketter bliver den aktuelle virksomhedssandhed"). Udkast og foreslåede poster er udelukket.',
            exports_bundle_info: 'Eksportpakker inkluderer: klassifikationsregister, håndteringsregler, ejerskab/RACI og bevispakke – matchende UC-VN-10 eksportkrav og DATA-EVD-01 bevisarkformat.',
        },
    },
    monitoring: {
        no_permission_title: 'Utilstraekkelige tilladelser',
        no_permission_message: 'Du har ikke tilladelse til at tilga dette overvagningsmodul.',
        errors: {
            fetch: 'Kunne ikke indlæse.',
            save: 'Kunne ikke gemme.',
            fetch_programs_failed: 'Kunne ikke hente programmer.',
            fetch_kpi_failed: 'Kunne ikke hente KPI-data.',
            create_snapshot_failed: 'Kunne ikke oprette øjebliksbillede.',
            fetch_periods_failed: 'Kunne ikke hente perioder.',
            create_period_failed: 'Kunne ikke oprette periode.',
            auto_generate_failed: 'Kunne ikke autogenerere perioder.',
            start_period_failed: 'Kunne ikke starte periode.',
            close_period_failed: 'Kunne ikke lukke periode.',
            save_program_failed: 'Kunne ikke gemme program.',
            activate_program_failed: 'Kunne ikke aktivere program.',
            pause_program_failed: 'Kunne ikke sætte på pause program.',
            complete_program_failed: 'Kunne ikke fuldføre program.',
            archive_program_failed: 'Kunne ikke arkivere program.',
            fetch_ric_templates_failed: 'Kunne ikke hente RIC-skabeloner.',
            fetch_ric_cases_failed: 'Kunne ikke hente RIC-sager.',
            create_ric_case_failed: 'Kunne ikke oprette RIC-sag.',
            transition_ric_failed: 'Kunne ikke overføre RIC-sag.',
            add_follow_up_failed: 'Kunne ikke tilføje opfølgning.',
            respond_follow_up_failed: 'Kunne ikke svare på opfølgning.',
            link_evidence_failed: 'Kunne ikke tilknytte dokumentation.',
            fetch_task_templates_failed: 'Kunne ikke hente opgaveskabeloner.',
            save_task_template_failed: 'Kunne ikke gemme opgaveskabelon.',
            delete_task_template_failed: 'Kunne ikke slette opgaveskabelon.',
            delete_framework_link_failed: 'Kunne ikke slette rammeværkslink.',
            seed_defaults_failed: 'Kunne ikke initialisere standarder.',
            fetch_tasks_failed: 'Kunne ikke hente opgaver.',
            create_task_failed: 'Kunne ikke oprette opgave.',
            instantiate_tasks_failed: 'Kunne ikke instantiere opgaver.',
            start_task_failed: 'Kunne ikke starte opgave.',
            complete_task_failed: 'Kunne ikke fuldføre opgave.',
            defer_task_failed: 'Kunne ikke udsætte opgave.',
            block_task_failed: 'Kunne ikke blokere opgave.',
            override_complete_failed: 'Kunne ikke tilsidesætte fuldførelse.',
            assign_task_failed: 'Kunne ikke tildele opgave.',
            update_checklist_failed: 'Kunne ikke opdatere tjekliste.',
        },
        nav: {
            programs: 'Programmer',
            periods: 'Perioder',
            task_templates: 'Opgaveskabeloner',
            tasks: 'Opgaver',
            kpi: 'KPI-kontrolpanel',
            ric: 'Tilsynsforespørgsler',
            framework_link: 'Rammeovervågning',
            authority_readiness: 'Myndighedsparathed',
            change_review: 'Ændringsgennemgang',
            raci: 'RACI Matrix',
        },
        programs: {
            page_title: 'Overvågningsprogrammer',
            title: 'Overvågningsprogrammer',
            new: 'Nyt Program',
            edit: 'Rediger Program',
            name: 'Navn',
            scope: 'Omfang',
            frequency: 'Frekvens',
            owner: 'Ejer',
            activate: 'Aktivér',
            pause: 'Sæt på pause',
            resume: 'Genoptag',
            complete: 'Afslut',
            archive: 'Arkivér',
            custom_frequency_days: 'Brugerdefineret frekvens (dage)',
            select_user: 'Vælg bruger⬦',
            assign_training: 'Tildel uddannelse',
            linked_forms: 'Tilknyttede formularer',
            linked_forms_none: 'Ingen tilknyttede formularer endnu',
            loaded_count: 'Indlæst {0} programmer',
            scope_labels: {
                full: 'Fuld',
                partial: 'Delvis',
                targeted: 'Målrettet',
                consent: 'Samtykke',
                dpia: 'DPIA',
                vendors: 'Leverandører',
                classification: 'Klassifikation',
                rights_requests: 'Rettighedsanmodninger',
                data_mapping: 'Datakortlægning',
                transfers: 'Overførsler',
                security_monitoring: 'Sikkerhedsovervågning',
                authority_response: 'Myndighedens svar',
                evidence_preservation: 'Bevarelse af beviser',
                localization_review: 'Lokaliseringsgennemgang',
                ai_system_registration: 'AI Systemregistrering',
                ai_pre_deployment: 'AI Vurdering før implementering',
                ai_transparency: 'AI Gennemsigtighedstjek',
                ai_incident_review: 'AI Gennemgang af hændelsen',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Overvågningsperioder',
            title: 'Gennemgangsperioder',
            new: 'Ny Periode',
            label: 'Periodeetiket',
            start_date: 'Startdato',
            end_date: 'Slutdato',
            reviewers: 'Bedømmere',
            auto_generate: 'Autogenerer Perioder',
            start: 'Start',
            close: 'Luk',
            change_digest: 'Ændringsoversigt',
            no_digest: 'Ingen sammenfatning tilgængelige.',
            reviewers_count: 'Antal gennemgåere',
            select_program: 'Vælg program',
            select_program_prompt: 'Vælg et program for at se perioder.',
            hide_digest: 'Skjul sammenfatning',
            loaded_count: 'Indlæst {0} perioder',
            periods_auto_generated: 'Perioder autogenereret',
            label_placeholder: 'f.eks. 2026-K1',
        },
        task_templates: {
            page_title: 'Opgaveskabeloner',
            title: 'Opgaveskabeloner',
            new: 'Ny Skabelon',
            edit: 'Rediger Skabelon',
            category: 'Kategori',
            priority: 'Prioritet',
            checklist_items: 'Tjeklistepunkter',
            effort: 'Estimeret Indsats (timer)',
            pre_seeded: 'Forudindlæst',
            seed_defaults: 'Indlæs Standarder',
            add_checklist: 'Tilføj Tjeklistepunkt',
            guidance: 'Vejledningsnoter',
            all_categories: 'Alle kategorier',
            loaded_count: 'Indlæst {0} opgaveskabeloner',
            defaults_seeded: 'Standardopgaveskabeloner oprettet',
            checklist_item_label: 'Tjeklistepunkt {0}',
            remove_checklist_item: 'Fjern tjeklistepunkt {0}',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Gennemgangsopgaver',
            title: 'Gennemgangsopgaver',
            new: 'Ny Opgave',
            assigned_to: 'Tildelt Til',
            due_date: 'Forfaldsdato',
            checklist: 'Tjekliste',
            start: 'Start',
            complete: 'Afslut',
            defer: 'Udskyd',
            block: 'Blokér',
            override_complete: 'Tilsidesæt og Afslut',
            link_evidence: 'Tilknyt Dokumentation',
            direct_upload_label: 'Upload bevisfil',
            instantiate: 'Opret fra Skabeloner',
            defer_reason: 'Årsag til udsættelse',
            override_justification: 'Begrundelse for tilsidesættelse',
            filter_assignee: 'Filtrer efter modtager',
            select_period: 'Vælg periode',
            select_program: 'Vælg program',
            select_program_period_prompt: 'Vælg et program og en periode for at se opgaver.',
            assign_placeholder: 'Tildel til...',
            loaded_count: 'Indlæst {0} opgaver',
            tasks_instantiated: 'Opgaver oprettet fra skabeloner',
            overdue: 'Forsinket',
            checklist_for: 'tjekliste for',
            manage_checklist: 'Administrer tjekliste',
            add_checklist_item: 'Tilføj element',
            checklist_item_label: 'Element',
            checklist_item_description: 'Beskrivelse',
            remove_checklist_item: 'Fjern element',
            save_checklist: 'Gem tjekliste',
            no_checklist_items: 'Ingen tjeklisteelementer endnu.',
            confirm_delete_attachment: 'Er du sikker på, at du vil slette denne bevisfil? Dette kan ikke fortrydes.',
        },
        kpi: {
            page_title: 'KPI-kontrolpanel',
            title: 'KPI-kontrolpanel',
            kpi_name: 'KPI-navn',
            current_value: 'Nuværende Værdi',
            target: 'Mål',
            trends: 'Tendenser',
            capture_snapshot: 'Optag øjebliksbillede',
            select_program: 'Vælg program',
            select_program_prompt: 'Vælg et program for at se KPI-er.',
            snapshot: 'Øjebliksbillede',
            kpi_labels: {
                on_track: 'På sporet',
                at_risk: 'I risiko',
                behind: 'Bagud',
                rights_request_sla_rate: 'Rettighedsanmodning SLA-rate',
                vendor_review_currency: 'Leverandørgennemgang aktualitet',
                dpia_completion_rate: 'DPIA-gennemførelsesrate',
                classification_coverage: 'Klassifikationsdækning',
                consent_policy_currency: 'Samtykkepolitik aktualitet',
                security_event_detection_rate: 'Detektionshastighed for sikkerhedshændelser',
                evidence_preservation_compliance_rate: 'Overholdelsesgrad for bevisbevaring',
                authority_response_timeliness: 'Myndighedens svar rettidigt',
                localization_readiness_score: 'Score for lokaliseringsberedskab',
                ai_register_completeness: 'AI Systemregisterets fuldstændighed',
                pre_deployment_gate_pass_rate: 'Beståelsesrate før implementering af Gate',
                transparency_disclosure_coverage: 'Dækning af gennemsigtighed',
                ai_incident_readiness_score: 'AI Score for hændelsesberedskab',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: "Cybersikkerhedslovgivningens KPI'er: Detektionsrate for sikkerhedshændelser, overholdelsesgrad for bevisbevaring, aktualitet for myndighedssvar, score for lokaliseringsparathed.",
            ai_law_kpi_info: "KPI'er for AI-loven: fuldstændighed af AI-systemregistret, gatebeståelsesrate før implementering, dækning af gennemsigtighedsoplysninger, score for AI-hændelsesberedskab.",
            no_data: 'Ingen data tilgængelige.',
            snapshot_date: 'Øjebliksbilledato',
            loaded_count: 'Indlæst {0} KPI-øjebliksbilleder',
            snapshot_captured: 'KPI-øjebliksbillede optaget',
            showing_chart: 'Viser diagramvisning',
            showing_table: 'Viser datatabelvisning',
            aria_current_values: 'KPI aktuelle værdier',
            kpi_label: 'KPI',
            actual: 'Faktisk',
            trend: 'Tendens',
            notes: 'Noter',
            trend_up: 'Stigende',
            trend_down: 'Faldende',
            trend_stable: 'Stabil',
        },
        ric: {
            page_title: 'RIC-sager',
            title: 'Tilsynsforespørgselssager',
            new: 'Ny Tilsynssag',
            reference: 'Reference',
            regulator: 'Tilsynsmyndighed',
            inquiry_type: 'Forespørgselstype',
            deadline: 'Svarfrist',
            lead: 'Ansvarlig',
            follow_ups: 'Opfølgninger',
            add_follow_up: 'Tilføj Opfølgning',
            respond: 'Besvar',
            question: 'Spørgsmål',
            response: 'Svar',
            asked_at: 'Spurgt den',
            no_follow_ups: 'Ingen follow-ups endnu.',
            received_date: 'Modtagelsesdato',
            responded_at: 'Besvaret den',
            hide_follow_ups: 'Skjul opfølgninger',
            inquiry_types: {
                general: 'Generelt',
                investigation: 'Undersøgelse',
                audit: 'Revision',
                complaint: 'Klage',
                general_audit: 'Generel revision',
                rights_requests_review: 'Gennemgang af rettighedsanmodninger',
                cross_border_transfer_review: 'Gennemgang af grænseoverskridende overførsler',
                vendor_governance_review: 'Gennemgang af leverandørstyring',
                consent_management_review: 'Gennemgang af samtykkehåndtering',
                data_mapping_ropa_review: 'Gennemgang af datakortlægning / ROPA',
                incident_readiness_review: 'Gennemgang af hændelsesparathed',
                security_access_control_review: 'Gennemgang af sikkerhed og adgangskontrol',
                targeted_processing_review: 'Gennemgang af målrettet behandling',
                bank_readiness_pack: 'Bankparathedspakke',
            },
            link_evidence: 'Tilknyt dokumentation',
            loaded_count: 'Indlæst {0} RIC-sager',
            to_follow_up: 'til opfølgningsspørgsmål',
            overdue: 'Forfalden',
            overdue_indicator: 'Svarfrist overskredet',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Modtaget',
                in_triage: 'Under triage',
                evidence_gathering: 'Dokumentationsindsamling',
                response_drafting: 'Svarudarbejdelse',
                internal_review: 'Intern gennemgang',
                submitted: 'Indsendt',
                follow_up: 'Opfølgning',
                closed: 'Lukket',
            },
        },
        framework_link: {
            title: 'Rammelinket overvågning',
            link_program: 'Tilknyt program til ramme',
            framework_code: 'Rammekode',
            select_framework: 'Vælg et rammeværk...',
            select_program: 'Vælg et program...',
            readiness_score: 'Parathedsscore',
            no_links: 'Ingen rammelinkede programmer fundet',
            seed_defaults: 'Opret standardskabeloner',
            program_id: 'Program-ID',
            program: 'Program',
        },
        authority_readiness: {
            title: 'Myndighedsanmodningsparathed',
            create_entry: 'Ny parathedspost',
            select_framework: 'Vælg et rammeværk...',
            framework_code: 'Ramme',
            readiness_status: 'Parathedsstatus',
            readiness_package: 'Parathedspakke',
            run_drill: 'Kør parathedsøvelse',
            drill_success: 'Parathedsøvelse registreret',
            last_drill: 'Seneste øvelse',
            never_drilled: 'Aldrig',
            no_readiness: 'Ingen myndighedsparathedsdata fundet',
            entry_created: 'Parathedspost oprettet',
            ready: 'Klar',
            partial: 'Delvis',
            not_ready: 'Ikke klar',
            items: 'elementer',
        },
        change_review: {
            title: 'Regulatorisk ændringsgennemgang',
            new_review: 'Ny ændringsgennemgang',
            change_id: 'Ændrings-ID',
            select_change: 'Vælg en ændring...',
            select_framework: 'Vælg et rammeværk...',
            impact_assessment: 'Konsekvensvurdering',
            decision: 'Beslutning',
            no_action: 'Ingen handling',
            internal_update: 'Intern opdatering',
            authority_filing: 'Myndighedsindberetning',
            no_reviews: 'Ingen ændringsgennemgange fundet',
            auto_cycle: 'Automatisk gennemgangscyklus',
            cadence: 'Gennemgangsfrekvens',
            framework: 'Rammeværk',
        },
        export_csv: 'Eksporter CSV',
        exporting: 'Eksporterer...',
        export_success: 'CSV blev eksporteret.',
        export_failed: 'CSV eksport mislykkedes.',
        export_attestation_pack: 'Eksporter attestationspakke',
        staleness: {
            badge_label: '{0} uaktuelle genstande',
            days_since: '{0} dage siden sidste opdatering',
            detail_title: 'Forældede genstande',
            accept_justification: 'Accepter med begrundelse',
            justification_label: 'Begrundelse',
            no_stale: 'Ingen forældede varer fundet.',
            threshold_label: 'Staleness-tærskel (dage)',
            threshold_min: 'Minimum 30 dage',
            threshold_max: 'Maksimalt 730 dage',
            threshold_saved: 'Staleness-grænsen er opdateret.',
        },
        csv_columns: {
            name: 'Navn',
            status: 'Status',
            owner: 'Ejer',
            created_at: 'Oprettet kl',
            updated_at: 'Opdateret kl',
            scope: 'Omfang',
            frameworks: 'Rammer',
            period_label: 'Periodemærke',
            start_date: 'Startdato',
            end_date: 'Slutdato',
            task_count: 'Opgavetælling',
            completion_rate: 'Gennemførelsesrate',
            title: 'Titel',
            assigned_to: 'Tildelt til',
            due_date: 'Forfaldsdato',
            priority: 'Prioritet',
            completed_at: 'Afsluttet kl',
            kpi_code: 'KPI Kode',
            kpi_label: 'KPI Etiket',
            target: 'Mål',
            actual: 'Faktisk',
            trend: 'Trend',
            reference: 'Reference',
            regulator: 'Regulator',
            inquiry_type: 'Forespørgselstype',
            lead: 'Bly',
            received_date: 'Modtaget dato',
            deadline: 'Deadline',
            framework_code: 'Rammekode',
            readiness_status: 'Beredskabsstatus',
            package_count: 'Pakkeantal',
            change_id: 'Skift ID',
            impact_assessment: 'Konsekvensanalyse',
            decision: 'Beslutning',
            program: 'Program',
            readiness_score: 'Beredskabsscore',
            category: 'Kategori',
            checklist_count: 'Tjeklisteantal',
            effort_hours: 'Indsats (timer)',
            version: 'version',
            department: 'afdeling',
            function_area: 'Funktionsområde',
            role: 'Rolle',
            assignee: 'Opdragsgiver',
            frequency: 'Frekvens',
        },
        raci: {
            page_title: 'Governance RACI Matrix',
            title: 'Governance RACI Matrix',
            department: 'afdeling',
            function_area: 'Funktionsområde',
            responsible: 'Ansvarlig',
            accountable: 'Ansvarlig',
            consulted: 'Rådgivet',
            informed: 'Informeret',
            assign_role: 'Tildel RACI-rolle',
            select_program: 'Vælg Program',
            select_program_prompt: 'Vælg et program for at se RACI-matrix.',
            no_assignments: 'Ingen RACI-tildelinger fundet.',
            role_updated: 'RACI-opgaven er opdateret.',
            role_labels: {
                R: 'Ansvarlig',
                A: 'Ansvarlig',
                C: 'Rådgivet',
                I: 'Informeret',
            },
            loaded_count: 'Indlæste {0} RACI-opgaver',
            single_accountability_warning: 'Kun én ansvarlig (A) rolle er tilladt pr. funktionsområde. Tildeling af en ny ansvarlig rolle vil erstatte den eksisterende.',
            cybersec_function_areas: 'Cybersikkerhedslovgivningens funktionsområder: Hændelsesbekæmpelse, evidensbevarelse, myndighedssamarbejde, lokaliseringsgennemgang.',
            ai_law_function_areas: 'AI Jura funktionsområder: Systemregistrering, Pre-Deployment Assessment, Transparens, Incident Handling.',
            suggested_function_areas: 'Foreslåede funktionsområder',
        },
        prior_cycle: {
            link_label: 'Link til tidligere cyklus',
            select_prior: 'Vælg tidligere cyklus...',
            linked: 'År for år',
            no_prior: 'Ingen tidligere cyklusser tilgængelige.',
            linked_success: 'Tidligere cyklus forbundet.',
        },
        digest_table: {
            module: 'modul',
            entity_type: 'Enhedstype',
            change_type: 'Skift type',
            description: 'Beskrivelse',
            changed_at: 'Ændret kl',
        },
        view_related_ric: 'Se relaterede RIC-sager',
        linked_program: 'Tilknyttet program',
        nav_raci: 'RACI Matrix',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Udkast til svar',
            drafting: 'Udarbejdelse...',
            results_heading: 'Udkast til svar ({count})',
            tokens_label: '≈ {count} tokens',
            counsel_label: 'Advokatgennemgang påkrævet:',
            counsel_default: 'Mindst ét svar kræver advokatgennemgang før afsendelse.',
            requires_counsel_label: 'Kræver advokatgennemgang.',
            accept_label: 'Accepter udkast',
            reject_label: 'Afvis udkast',
            error_generic: 'Svaret kunne ikke udarbejdes. Prøv igen eller finjuster forespørgselsteksten.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Beregn tidslinje for notifikation om brud',
            computing: 'Beregner...',
            results_heading: 'Overtrædelse af underretningsfrister ({count})',
            tokens_label: '≈ {count} tokens',
            handoff_label: 'Forseti foreslår:',
            empty_label: 'Ingen installeret ramme angiver en anmeldelsespligt for denne hændelse.',
            mandatory_label: 'Obligatoriske felter:',
            draft_label: 'Udkast til meddelelse',
            remaining_label: '{count}t tilbage',
            overdue_label: 'FORSIDEN af {count}h',
            acknowledge_label: 'Anerkend tidslinjen',
            acknowledging: 'Anerkender...',
            error_generic: 'Kunne ikke beregne tidslinjen for brud. Prøv igen.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Bekræftet brud',
            no: 'Ikke en Brud',
            uncertain: 'Afventer',
        },
        errors: {
            fetch: 'Kunne ikke indlæse hændelser.',
            save: 'Kunne ikke gemme hændelse.',
            fetch_failed: 'Kunne ikke hente hændelser.',
            save_failed: 'Kunne ikke gemme hændelse.',
            transition_failed: 'Kunne ikke overføre hændelse.',
            close_failed: 'Kunne ikke lukke hændelse.',
            fetch_timeline_failed: 'Kunne ikke hente tidslinje.',
            fetch_decisions_failed: 'Kunne ikke hente beslutninger.',
            triage_failed: 'Kunne ikke opdatere triage.',
            impact_update_failed: 'Kunne ikke opdatere konsekvensvurdering.',
            breach_update_failed: 'Kunne ikke opdatere brudvurdering.',
            create_timeline_failed: 'Kunne ikke oprette tidslinjepost.',
            create_decision_failed: 'Kunne ikke oprette beslutning.',
            notification_update_failed: 'Kunne ikke opdatere notifikation.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Kunne ikke hente opgaver.',
            create_task_failed: 'Kunne ikke oprette opgave.',
            update_task_status_failed: 'Kunne ikke opdatere opgavestatus.',
            update_checklist_failed: 'Kunne ikke opdatere tjekliste.',
            assign_task_failed: 'Kunne ikke tildele opgave.',
            escalate_task_failed: 'Kunne ikke eskalere opgave.',
            fetch_templates_failed: 'Kunne ikke hente skabeloner.',
            permission_denied: 'Du har ikke tilladelse til at tilga denne sektion. Kontakt din administrator for at anmode om adgang.',
        },
        harm_likelihood: {
            unlikely: 'Usandsynlig',
            possible: 'Mulig',
            likely: 'Sandsynlig',
            very_likely: 'Meget sandsynlig',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'E-mail',
            phone: 'Telefon',
            internal_system: 'Internt system',
            third_party: 'Tredjepart',
            automated_detection: 'Automatiseret registrering',
        },
        task_transition_labels: {
            start: 'Start',
            complete: 'Fuldfør',
            block: 'Bloker',
            defer: 'Udsæt',
            cancel: 'Annuller',
            unblock: 'Fjern blokering',
        },
        transition_labels: {
            start: 'Start',
            complete: 'Fuldfør',
            block: 'Bloker',
            defer: 'Udsæt',
        },
        nav: {
            all: 'Alle Hændelser',
            tasks: 'Responsopgaver',
            reports: 'Rapporter',
            breach_notifications: 'Brudnotifikationer',
            supplements: 'Tillæg',
            remediation: 'Afhjælpning',
            authority_requests: 'Myndighedsanmodninger',
            disclosure_register: 'Offentliggørelsesregister',
        },
        list: {
            page_title: 'Hændelser',
            title: 'Hændelser',
            new: 'Ny Hændelse',
            edit: 'Rediger Hændelse',
            incident_title: 'Titel',
            severity: 'Alvorlighedsgrad',
            reported_at: 'Rapporteret Den',
            reported_by: 'Rapporteret Af',
            assigned_to: 'Tildelt Til',
            view_detail: 'Vis Detaljer',
            reference: 'Reference',
            lead: 'Leder',
            reported_via: 'Rapporteret via',
            discovery_date: 'Opdagelsesdato',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Skjul detaljer',
            close_incident: 'Luk hændelse',
            close_justification_prompt: 'Angiv venligst en begrundelse for at lukke denne hændelse.',
            justification: 'Begrundelse',
            lead_id: 'Leder-ID',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Kontakt',
            category: 'Kategori',
            not_assessed: 'Ikke vurderet',
        },
        detail: {
            page_title: 'Hændelsesdetaljer',
            title: 'Hændelsesdetaljer',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Oversigt',
                triage: 'Triage',
                impact: 'Konsekvensvurdering',
                breach: 'Brudvurdering',
                timeline: 'Tidslinje',
                decisions: 'Beslutninger',
                evidence: 'Beviser',
                audit_trail: 'Revisionsspor',
            },
            evidence_upload_label: 'Vedhæft bevisfil',
            evidence_uploaded: 'Bevis uploadet',
            evidence_deleted: 'Bevis slettet',
            evidence_delete_error: 'Kunne ikke slette beviset',
            evidence_uploaded_at: 'Uploadet',
            evidence_empty: 'Ingen bevisfiler vedhæftet denne hændelse.',
            evidence_size: 'Størrelse',
            triage_notes: 'Triagenoter',
            initial_classification: 'Indledende Klassificering',
            individuals_affected: 'Antal Berørte Individer',
            data_categories: 'Berørte Datakategorier',
            systems_compromised: 'Kompromitterede Systemer',
            geographic_scope: 'Geografisk Omfang',
            breach_determination: 'Brudfastslåelse',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Notifikation Påkrævet',
            notification_deadline: 'Notifikationsfrist',
            pdpc_reportable: 'Datatilsynet Indberetningspligtigt',
            add_timeline: 'Tilføj Tidslinjepost',
            add_decision: 'Tilføj Beslutning',
            not_found: 'Hændelse ikke fundet.',
            team_members: 'Teammedlemmer',
            checklist: 'Tjekliste',
            completed: 'Fuldført',
            impact_severity_score: 'Beregnet konsekvensalvorlighedsscore',
            records_affected: 'Berørte poster',
            potential_consequences: 'Potentielle konsekvenser',
            harm_likelihood: 'Skadesandsynlighed',
            legal_basis: 'Retsgrundlag',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Jurisdiktioner',
            jurisdictions_comma: 'Jurisdiktioner (kommasepareret)',
            jurisdiction: 'Jurisdiktion',
            authority_status: 'Myndighedsstatus',
            subject_status: 'Emnestatus',
            authority_deadline: 'Myndighedsfrist',
            subject_deadline: 'Emnefrist',
            mark_authority_done: 'Marker myndighed færdig',
            mark_subject_done: 'Marker emne færdig',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Ikke vurderet',
            event_type: 'Hændelsestype',
            timestamp: 'Tidsstempel',
            actor: 'Aktør',
            no_timeline_events: 'Ingen tidslinje hændelser endnu.',
            decision_type: 'Beslutningstype',
            decision: 'Beslutning',
            rationale: 'Begrundelse',
            decision_made: 'Beslutning truffet',
            no_decisions: 'Ingen decisions recorded endnu.',
            no_audit_events: 'Ingen revisionsbegivenheder registreret endnu.',
            export_pdf: 'Eksportér PDF',
            pdf_title: 'Hændelsesrapport',
            description: 'Beskrivelse',
            linked_records: 'Tilknyttede Poster',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Incident Response Opgaver',
            title: 'Hændelsesresponsopgaver',
            new: 'Ny Opgave',
            edit: 'Rediger Opgave',
            task_type: 'Opgavetype',
            assignee: 'Ansvarlig',
            due_date: 'Forfaldsdato',
            dependencies: 'Afhængigheder',
            mark_complete: 'Markér som Afsluttet',
            templates: 'Opgaveskabeloner',
            select_incident: 'Vælg Hændelse',
            all_statuses: 'Alle Statusser',
            all_categories: 'Alle Kategorier',
            new_response_task: 'Ny Responsopgave',
            category: 'Kategori',
            priority: 'Prioritet',
            depends_on: "Afhænger Af (kommaseparerede opgave-ID'er)",
            checklist_items: 'Tjeklistepunkter',
            remove: 'Fjern',
            add: 'Tilføj',
            new_checklist_item: 'Nyt tjeklistepunkt',
            no_templates: 'Ingen skabeloner tilgængelige.',
            assign_task: 'Tildel Opgave',
            assign_to: 'Tildel Til',
            assign: 'Tildel',
            select_incident_prompt: 'Vælg venligst en hændelse for at se dens responsopgaver.',
            checklist: 'Tjekliste',
            hide_checklist: 'Skjul Tjekliste',
            escalate: 'Eskalér',
            escalations: 'Eskaleringer',
            no_checklist_items: 'Ingen tjeklistepunkter.',
        },
        reports: {
            page_title: 'Hændelsesrapporter',
            title: 'Hændelsesrapporter',
            summary: 'Opsummeringsstatistik',
            by_severity: 'Efter Alvorlighedsgrad',
            by_status: 'Efter Status',
            recent: 'Seneste Hændelser',
            total_incidents: 'Samlede Hændelser',
            open: 'Åbne',
            closed: 'Lukkede',
            export_csv: 'Eksportér CSV',
            export_pdf: 'Eksportér PDF',
            export_success: 'Rapport eksporteret.',
            export_error: 'Eksport af rapport mislykkedes.',
            reference: 'Reference',
            view_report: 'Vis Rapport',
        },
        statuses: {
            reported: 'Rapporteret',
            triage: 'Triage',
            contained: 'Inddæmmet',
            investigation: 'Efterforskning',
            remediation: 'Afhjælpning',
            closed: 'Lukket',
            false_positive: 'Falsk Positiv',
        },
        task_statuses: {
            pending: 'Afventende',
            in_progress: 'I Gang',
            completed: 'Afsluttet',
            blocked: 'Blokeret',
            cancelled: 'Annulleret',
        },
        severity: {
            low: 'Lav',
            medium: 'Middel',
            high: 'Høj',
            critical: 'Kritisk',
        },
        task_types: {
            containment: 'Inddæmning',
            investigation: 'Efterforskning',
            remediation: 'Afhjælpning',
            communication: 'Kommunikation',
            documentation: 'Dokumentation',
            notification: 'Notifikation',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Databrud',
            unauthorized_access: 'Uautoriseret adgang',
            data_loss: 'Datatab',
            system_compromise: 'Systemkompromittering',
            malware: 'Malware',
            phishing: 'Phishing',
            insider_threat: 'Insidertrussel',
            other: 'Andet',
        },
        reported_via_options: {
            email: 'E-mail',
            phone: 'Telefon',
            internal_system: 'Internt system',
            third_party: 'Tredjepart',
            automated_detection: 'Automatiseret registrering',
        },
        harm_likelihood_options: {
            unlikely: 'Usandsynlig',
            possible: 'Mulig',
            likely: 'Sandsynlig',
            very_likely: 'Meget sandsynlig',
        },
        breach_determination_options: {
            confirmed_breach: 'Bekræftet brud',
            suspected_breach: 'Mistænkt brud',
            not_a_breach: 'Ikke en Brud',
            pending: 'Afventer',
        },
        timeline_event_types: {
            detection: 'Registrering',
            escalation: 'Eskalering',
            containment: 'Inddæmning',
            investigation_start: 'Undersøgelsesstart',
            evidence_collected: 'Dokumentation indsamlet',
            notification_sent: 'Notifikation sendt',
            remediation_action: 'Afhjælpningshandling',
            status_change: 'Statusændring',
            communication: 'Kommunikation',
            other: 'Andet',
        },
        decision_types: {
            containment: 'Inddæmning',
            notification: 'Notifikation',
            remediation: 'Afhjælpning',
            escalation: 'Eskalering',
            closure: 'Lukning',
            other: 'Andet',
        },
        task_transitions: {
            start: 'Start',
            complete: 'Fuldfør',
            block: 'Bloker',
            cancel: 'Annuller',
            unblock: 'Ophæv blokering',
        },
        announce: {
            loaded: '{count} hændelser indlæst',
            reports_loaded: '{count} hændelsesrapporter er indlæst',
            tasks_loaded: '{count} opgaver er indlæst',
            created: 'Hændelsen blev oprettet',
            updated: 'Hændelsen blev opdateret',
            closed: 'Hændelsen blev afsluttet',
            status_changed: 'Hændelsesstatus ændret',
            triage_completed: 'Triage gennemført med succes',
            impact_updated: 'Konsekvensanalyse opdateret',
            breach_updated: 'Overtrædelsesvurdering opdateret',
            timeline_added: 'Tidslinjebegivenhed tilføjet',
            decision_recorded: 'Beslutning registreret',
            notification_updated: 'Notifikationsstatus opdateret',
            task_created: 'Opgaven er oprettet',
            task_status_changed: 'Opgavestatus ændret',
            checklist_completed: 'Tjekliste-emne afsluttet',
            checklist_unchecked: 'Tjeklisteelement er ikke markeret',
            task_assigned: 'Opgaven blev tildelt',
            task_escalated: 'Opgaven eskalerede',
            export_initiated: 'Eksporten påbegyndt',
        },
        breach_notifications: {
            page_title: 'Underretninger om brud',
            title: 'Underretninger om brud',
            new_notification: 'Ny meddelelse',
            no_notifications: 'Ingen notifikationer om brud fundet',
            status: 'Status',
            submit_to_mps: 'Indsend til MPS',
            decision: 'Beslutning',
            decision_maker: 'Beslutningstager',
            notify: 'Giv besked',
            do_not_notify: 'Giv ikke besked',
            rationale: 'Begrundelse',
            filing_deadline: 'Indleveringsfrist',
            overdue: 'Forsinket',
            due_soon: 'Snart forfalden',
            on_track: 'På rette spor',
            legal_review: 'Juridisk gennemgang',
            incident_id: 'Hændelses-id',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Vurdering',
                decision_to_notify: 'Beslutning om at underrette',
                notification_drafted: 'Meddelelse udarbejdet',
                legal_review: 'Juridisk gennemgang',
                submitted_to_authority: 'Indsendt til myndighed',
                supplement_requested: 'Tillæg anmodet',
                supplement_submitted: 'Tillæg indsendt',
                acknowledged: 'Anerkendt',
                closed: 'Lukket',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Supplerende anmodninger',
            title: 'Supplerende anmodninger',
            new_supplement: 'Nyt tillæg',
            no_supplements: 'Ingen tillægsanmodninger fundet',
            request_details: 'Anmod om detaljer',
            response_package: 'Svarpakke',
            submit_response: 'Send svar',
            deadline: 'Deadline',
            notification_id: 'Meddelelses-id',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Indtast et notifikations-id for at se tillæg.',
            supplement_statuses: {
                requested: 'Anmodet',
                drafting: 'Udarbejdelse',
                submitted: 'Indsendt',
                accepted: 'Accepteret',
                rejected: 'Afvist',
            },
        },
        remediation: {
            page_title: 'Afhjælpningsforanstaltninger',
            title: 'Afhjælpningsforanstaltninger',
            new_action: 'Ny handling',
            no_actions: 'Der blev ikke fundet nogen afhjælpende handlinger',
            description: 'Beskrivelse',
            owner: 'Ejer',
            department: 'Afdeling',
            deadline: 'Deadline',
            statuses: {
                planned: 'Planlagt',
                in_progress: 'Igangværende',
                completed: 'Fuldført',
                verified: 'Verificeret',
            },
            verify: 'Bekræft',
            incident_id: 'Hændelses-id',
            enter_incident_id: 'Indtast et hændelses-id for at se afhjælpningshandlinger.',
            start: 'Start',
            complete_action: 'Fuldfør',
        },
        authority_requests: {
            page_title: 'Myndighedsanmodninger',
            title: 'Myndighedsanmodninger',
            new_request: 'Ny anmodning',
            no_requests: 'Ingen myndighedsanmodninger fundet',
            authority_name: 'Myndighedsnavn',
            request_details: 'Anmod om detaljer',
            response_package: 'Svarpakke',
            submission_channel: 'Indsendelseskanal',
            submit_response: 'Send svar',
            deadline: 'Deadline',
            confirmation: 'Bekræftelse',
            incident_id_optional: 'Hændelses-id (valgfrit)',
            response_context_label: 'Svarkontekst (til materialisering af indsendelsesdokument)',
            response_context_none: '(spring materialisering over)',
            response_context_supplement: 'Supplerende svar',
            response_context_information: 'Svar på anmodning om oplysninger',
            response_context_inspection: 'Svar på inspektion',
            materialisation_status_column: 'Indsendelsesdokument',
            materialisation_pending: 'Afventer',
            materialisation_success: 'Materialiseret',
            materialisation_failed: 'Fejlet',
            materialisation_retry_button: 'Prøv igen',
            ar_statuses: {
                received: 'Modtaget',
                verified: 'Verificeret',
                assembling: 'Samling',
                submitted: 'Indsendt',
                closed: 'Lukket',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Juridisk gennemgang',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Tjekliste',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Oplysningsregister',
            title: 'Oplysningsregister',
            new_entry: 'Ny indgang',
            no_entries: 'Ingen oplysningsregisterposter fundet',
            disclosure_type: 'Oplysningstype',
            disclosure_date: 'Offentliggørelsesdato',
            recipient: 'Modtager',
            authority_name: 'Myndighedsnavn',
            channel: 'Kanal',
            content_summary: 'Indholdsoversigt',
            incident_id_optional: 'Hændelses-id (valgfrit)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Opsætningsguide',
            tours: 'Modulrundture',
            sample_data: 'Eksempeldata',
        },
        wizard: {
            title: 'Opsætningsguide',
            welcome: 'Velkommen til AesirX ComplianceOne',
            welcome_message: 'Lad os konfigurere din organisation. Denne guide vil føre dig igennem den indledende opsætning.',
            setup_complete: 'Opsætning Fuldført',
            skipped_message: 'Opsætningsguiden blev sprunget over. Du kan køre den når som helst for at konfigurere din organisation.',
            start_setup: 'Start Opsætning',
            skip_wizard: 'Spring Guiden Over',
            go_to_admin: 'Gå til Admin',
            skip_for_now: 'Spring Over Foreløbigt',
            next_step: 'Næste Trin',
            previous_step: 'Forrige Trin',
            finish_setup: 'Afslut Opsætning',
            rerun: 'Kør Opsætningsguiden Igen',
            step_label: 'Trin {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'Organisationsnavn er påkrævet.',
            validation_admin_email_required: 'Admin-e-mail er påkrævet.',
            validation_admin_email_invalid: 'Indtast venligst en gyldig e-mailadresse.',
            validation_cmp_endpoint_required: 'CMP-endepunkt er påkrævet, når CMP er aktiveret.',
            steps: {
                license_activation: 'Licensaktivering',
                org_profile: 'Organisationsprofil',
                module_selection: 'Modulvalg',
                cmp_integration: 'CMP-integration',
                notification_preferences: 'Notifikationer',
                review: 'Gennemgang og Bekræftelse',
            },
            step_descriptions: {
                license_activation: 'Indtast din licensnøgle for at aktivere din pakke og afgøre, hvilke moduler der er tilgængelige.',
                org_profile: 'Opsæt organisationsnavn, jurisdiktion, branche, størrelse og foretrukket sprog.',
                module_selection: 'Vælg hvilke compliance-moduler der skal aktiveres for din organisation.',
                cmp_integration: 'Konfigurer integration med Consent Management Platform (valgfrit).',
                notification_preferences: 'Konfigurer hvordan du modtager notifikationer om compliance-hændelser.',
                review: 'Gennemgå din konfiguration og afslut opsætningen.',
            },
            license_step: {
                validate_button: 'Valider licens',
                skip_warning: 'Moduladgang vil være begrænset til standardpakkeniveauet. Du kan aktivere en licens senere i Platform > Licensindstillinger.',
            },
            module_descriptions: {
                rights_requests: 'Håndter anmodninger om adgang til og sletning af persondata.',
                data_mapping: 'Kortlæg systemer, datastrømme og behandlingsaktiviteter.',
                governance: 'Før tilsyn med leverandører, godkendelser, afvigelser og dokumentationsregistrene.',
                consent: 'Administrer samtykkepolitikker og formålskataloger.',
                consent_properties: 'Administrer samtykke på tværs af digitale ejendomme.',
                assessments: 'Udfør konsekvensanalyser og grænseoverskridende dataoverførsler.',
                classification: 'Klassificer og mærk dataaktiver med livscyklussporing.',
                monitoring: 'Planlagte compliance-gennemgange og KPI-sporing.',
                incidents: 'Rapporter og håndter sikkerheds- og privatlivshændelser.',
                connectors: 'Forbind til eksterne systemer til automatiseret dataudveksling.',
                vendor_risk: 'Scorer leverandører, spor kontrakter og administrer due diligence.',
                deletion_orchestration: 'Koordiner datasletning på tværs af systemer og leverandører.',
                discovery: 'Opdag og klassificer data på tværs af din infrastruktur.',
                incident_ops: 'Avancerede hændelsesplaybooks, SLA-sporing og eskaleringer.',
                program_governance: 'Modenhedsvurderinger, ledelsesrapportering og revisionsparathed.',
                access_accountability: 'Overvåg brugeradgang, sessioner og begrundelsesworkflows.',
                localization: 'Markedsspecifikke compliancepakker og regulatoriske rammer.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Partner- og forhandlerarbejdsområde.',
                privacy_scanner: 'Realtids privatlivsovervågning af digitale ejendomme.',
                onboarding: 'Opsætningsguide, guidede ture og administration af eksempeldata.',
                compliance_forms: 'Interaktive complianceskabeloner og dokumentgenerering.',
                audit_trail: 'Omfattende uforanderlig revisionslogning og opbevaring.',
                organizations: 'Multi-organisations- og gruppeadministration.',
                task_management: 'Samlet opgaveplanlægning, workflowmotor og kalendervisninger.',
            },
            module_categories: {
                data_privacy_rights: 'Databeskyttelse og rettigheder',
                vendor_third_party: 'Leverandør- og tredjepartsledelse',
                risk_assessment: 'Risiko & vurdering',
                operations_automation: 'Drift & Automation',
                platform_admin: 'Platform & Administration',
            },
            package_tier_label: 'Din Pakke',
            package_module_unavailable: 'Ikke inkluderet i din pakke',
            select_all: 'Vælg Alle',
            deselect_all: 'Fravælg Alle',
            load_sample_data: 'Indlæs eksempeldata efter opsætning',
            load_sample_data_description: 'Installer realistisk demodata til at udforske platformen. Kan fjernes senere fra Onboarding > Eksempeldata.',
            language_label: 'Foretrukket Sprog',
            language_description: 'Vælg standardsproget for din organisation.',
            admin_account_title: 'Administratorkonto',
            admin_account_created: 'Administratorkonto oprettet',
            admin_full_name: 'Fulde navn',
            admin_email: 'E-mailadresse',
            admin_password: 'Adgangskode',
            admin_confirm_password: 'Bekræft adgangskode',
            admin_show_password: 'Vis adgangskode',
            admin_hide_password: 'Skjul adgangskode',
            admin_name_placeholder: 'f.eks. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Minimum 8 tegn',
            admin_password_confirm_placeholder: 'Indtast adgangskode igen',
            validation_admin_name_required: 'Administratornavn er påkrævet',
            validation_admin_password_required: 'Adgangskode er påkrævet',
            validation_admin_password_min_length: 'Adgangskoden skal være mindst 8 tegn',
            validation_passwords_mismatch: 'Adgangskoderne stemmer ikke overens',
            notification_email_enabled: 'Aktivér E-mail-notifikationer',
            notification_email_description: 'Modtag e-mail-notifikationer om opgavetildeling, SLA-brud og compliance-hændelser.',
            notification_digest_frequency: 'Sammendragsfrekvens',
            notification_digest_daily: 'Dagligt',
            notification_digest_weekly: 'Ugentligt',
            notification_digest_none: 'Intet sammendrag',
            notification_in_app_enabled: 'Aktivér Platformnotifikationer',
            notification_in_app_description: 'Vis notifikationsbadge og -skuffe til realtidsalarmer.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'EU/EØS',
                global: 'Globalt',
            },
            languages: {
                en: 'engelsk',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenska',
                nb: 'Norsk bokmål',
                nn: 'Norsk nynorsk',
                de: 'Deutsch',
                fi: 'Suomi',
                nl: 'Nederlands',
                fr: 'Français',
                lb: 'lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Español',
                ca: 'Català',
                eu: 'Euskara',
                pt: 'Português',
            },
            industries: {
                financial_services: 'Finansielle tjenester',
                healthcare: 'Sundhed',
                technology: 'Teknologi',
                telecommunications: 'Telekommunikation',
                government: 'Offentlig sektor',
                education: 'Uddannelse',
                manufacturing: 'Produktion',
                retail: 'Detailhandel',
                other: 'Andet',
            },
            placeholders: {
                org_name: 'f.eks. ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'bruger1@virksomhed.vn, bruger2@virksomhed.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Organisationsnavn',
                jurisdiction: 'Jurisdiktion',
                industry: 'Branche',
                org_size: 'Organisationsstørrelse',
                language: 'Foretrukket Sprog',
                admin_email: 'Admin-e-mail',
                team_emails: 'Teammedlemmers E-mails (kommasepareret)',
                enable_cmp: 'Aktivér CMP-integration',
                cmp_endpoint: 'CMP API-endepunkt',
                email_notifications: 'E-mail-notifikationer',
                in_app_notifications: 'Platformnotifikationer',
                digest_frequency: 'Sammendragsfrekvens',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Modul-onboardingrundture',
            start: 'Start Rundtur',
            continue_tour: 'Fortsæt Rundtur',
            replay: 'Genafspil Rundtur',
            exit: 'Afslut Rundtur',
            skip: 'Spring Rundtur Over',
            next: 'Næste',
            previous: 'Forrige',
            finish: 'Afslut Rundtur',
            step_of: 'Trin {current} af {total}',
            info: 'Tag guidede rundture af hvert modul for at lære vigtige funktioner og arbejdsgange. Rundture kan genafspilles når som helst.',
            autoplay: 'Automatisk afspilning',
            interactive: 'Interaktiv',
            play_all: 'Spil alle',
            search_tours: 'Søg ture...',
            duration: 'Varighed',
            steps_label: '{count} trin',
            mode: 'tilstand',
            pause: 'Pause',
            resume: 'Genoptag',
            speed: 'Hastighed',
            restart: 'Genstart',
            reset: 'Nulstil',
            reset_success: 'Turens fremskridt er blevet nulstillet.',
            category_core_privacy: 'Kernebeskyttelse',
            category_governance: 'Governance',
            category_operations: 'Operationer',
            category_platform: 'Platform',
            category_advanced: 'Avanceret',
            category_data_privacy_rights: 'Databeskyttelse og rettigheder',
            category_vendor_third_party: 'Leverandør- og tredjepartsledelse',
            category_risk_assessment: 'Risiko & vurdering',
            category_operations_automation: 'Drift & Automation',
            category_platform_admin: 'Platform & Administration',
            steps_count: '{count} trin',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Eksempeldatastyring',
            select_modules: 'Vælg Moduler',
            current_status: 'Nuværende Status',
            operation_history: 'Operationshistorik',
            load: 'Indlæs Eksempeldata',
            clear: 'Ryd Eksempeldata',
            processing: 'Behandler...',
            loaded: 'Indlæst',
            not_loaded: 'Ikke Indlæst',
            records: 'Eksempelposter',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Er du sikker på, at du vil rydde alle eksempeldata for de valgte moduler?',
            confirm_clear_title: 'Ryd eksempeldata',
            confirm_load_title: 'Indlæs eksempeldata',
            confirm_load: 'Er du sikker på, at du vil indlæse eksempeldata for de valgte moduler?',
            action_load: 'Indlæs',
            action_clear: 'Ryd',
            info: 'Indlæs realistiske demodata til træning og demonstrationer. Eksempeldata er mærket og kan sikkert fjernes uden at påvirke rigtige data.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Hjælpecenter',
            search: 'Søg',
            search_placeholder: 'Søg i hjælpeartikler...',
            clear: 'Ryd',
            all_categories: 'Alle Kategorier',
            all_modules: 'Alle Moduler',
            back: 'Tilbage til Hjælpecenteret',
            callout_important: 'Vigtigt',
            callout_tip: 'Tip',
            no_results: 'Ingen artikler fundet, der matcher din søgning.',
            no_articles: 'Ingen hjælpeartikler tilgængelige endnu. Artikler vil vises her, efterhånden som indhold tilføjes.',
            results_for: '{count} resultat(er) for "{query}"',
            quick_links: {
                getting_started: 'Kom i Gang',
                getting_started_desc: 'Installation, opsætning og hurtigstartguide.',
                user_guide: 'Brugervejledning',
                user_guide_desc: 'Modul-for-modul brugsdokumentation.',
                admin_guide: 'Administratorvejledning',
                admin_guide_desc: 'Konfiguration, vedligeholdelse og fejlfinding.',
                faq: 'FAQ',
                faq_desc: 'Ofte stillede spørgsmål og svar.',
            },
            search_articles_aria: 'Søg i hjælpeartikler',
            article_metadata_aria: 'Artikelmetadata',
            faq_editor_title: 'FAQ-editor til hjælpecenteret',
            faq_editor_description: 'Tilføj, rediger og fjern spørgsmål-og-svar-par i enhver artikel i hjælpecenteret. Editoren er endnu ikke tilgængelig; denne adresse er reserveret til den.',
            faq_editor_empty: 'FAQ-editoren er endnu ikke tilgængelig. Indtil da kan du bede din administrator om at tilføje FAQ-afsnit til en artikel i hjælpecenteret for dig.',
            articles_list_aria: 'Hjælpeartikler',
            category_nav_aria: 'Kategorinavigation',
            quick_links_aria: 'Hurtige links til hjælpekategorier',
            returned_to_list: 'Returneret til artikelliste',
            opening_article: 'Åbner artikel: {title}',
            read_article_aria: 'Læs artikel: {title}',
            pagination_nav_aria: 'Artikelpaginering',
            error_unavailable: 'Hjælpecenteret er i øjeblikket ikke tilgængeligt. Prøv venligst igen senere.',
            retry: 'Prøv igen',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'f.eks. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Gem',
        cancel: 'Annullér',
        system_actor: 'System',
        delete: 'Slet',
        edit: 'Rediger',
        create: 'Opret',
        update: 'Opdatér',
        loading: 'Indlæser...',
        error: 'Der opstod en fejl',
        success: 'Succes',
        logout: 'Log Ud',
        actions: 'Handlinger',
        retrying: 'Prøver igen...',
        permission_denied: 'Du har ikke adgang til denne side.',
        status: 'Status',
        description: 'Beskrivelse',
        name: 'Navn',
        created_at: 'Oprettet Den',
        updated_at: 'Opdateret Den',
        yes: 'Ja',
        no: 'Nej',
        confirm: 'Bekræft',
        confirm_dialog_title: 'Confirm action',
        back: 'Tilbage',
        next: 'Næste',
        previous: 'Forrige',
        search: 'Søg',
        filter: 'Filtrer',
        page_x_of_y: 'Side {page} af {total}',
        pagination_label: 'Sideinddeling',
        view_mode_label: 'Visningstilstand',
        export: 'Eksporter',
        exporting: 'Eksporterer...',
        sidebar_navigation: 'Admin sidebar navigation',
        items_count: '{count} elementer',
        items_count_one: '{count} element',
        export_success: 'Dokument genereret med succes.',
        generate_pdf: 'Generer PDF',
        import: 'Importer',
        download: 'Hent',
        upload: 'Upload',
        view: 'Vis',
        close: 'Luk',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Indsend',
        saved: 'Gemt',
        saving: 'Gemmer ...',
        submitting: 'Indsender...',
        created: 'Oprettet',
        all: 'Alle',
        none: 'Ingen',
        select: 'Vælg',
        select_user: 'Vælg bruger...',
        custom_input: 'Indtast manuelt',
        required: 'Påkrævet',
        optional: 'Valgfrit',
        notes: 'Noter',
        tags: 'Tags',
        archive: 'Arkivér',
        clone: 'Klon',
        refresh: 'Opdatér',
        retry: 'Prøv Igen',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Nulstil',
        add: 'Tilføj',
        remove: 'Fjern',
        removing: 'Fjerner⬦',
        attachment: 'Vedhæftet fil',
        upload_file: 'Upload fil',
        upload_file_required: 'Upload fil (påkrævet)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Træk og slip, eller klik for at gennemse',
        upload_max_size: 'Maks. størrelse: {size} MB',
        upload_accepted_types: 'Accepteret: {types}',
        upload_in_progress: 'Uploader {name}...',
        upload_size_exceeded: 'Filstørrelsen overskrider den maksimalt tilladte størrelse på {size} MB.',
        upload_type_not_accepted: 'Filtypen accepteres ikke. Accepterede typer: {types}',
        upload_succeeded: 'Filen "{name}" blev uploadet.',
        upload_failed: 'Filen kunne ikke uploades.',
        select_entity_type_first: 'Vælg først en entitetstype',
        failed_to_remove_file: 'Det lykkedes ikke at fjerne filen.',
        entity_type_label: 'Entitetstype',
        entity_label: 'Entitet',
        entity_reference: 'Entitetsreference',
        failed_to_load_entities: 'Det lykkedes ikke at indlæse entiteter',
        enable: 'Aktivér',
        disable: 'Deaktivér',
        no_data: 'Ingen data fundet',
        confirm_delete: 'Er du sikker på, at du vil slette dette element?',
        confirm_action: 'Er du sikker på, at du vil fortsætte?',
        save_success: 'Gemt med succes',
        save_error: 'Kunne ikke gemme',
        delete_success: 'Slettet med succes',
        delete_error: 'Kunne ikke slette',
        hour: 'time',
        hours: 'timer',
        load_error: 'Kunne ikke indlæse data',
        operation_error: 'Handlingen mislykkedes',
        something_went_wrong: 'Noget gik galt',
        go_home: 'Gå til Kontrolpanel',
        access_denied: 'Adgang Nægtet',
        no_permission: 'Du har ikke tilladelse til at tilgå denne side.',
        no_permission_title: 'Utilstrækkelige tilladelser',
        no_permission_message: 'Du har ikke tilladelse til at tilgå denne side.',
        unknown: 'Ukendt',
        back_to_dashboard: 'Tilbage til Kontrolpanel',
        id: 'ID',
        type: 'Type',
        date: 'Dato',
        created_by: 'Oprettet Af',
        updated_by: 'Opdateret Af',
        page_of: 'Side {page} af {total}',
        showing_items: 'Viser {count} elementer',
        items_per_page: 'Elementer pr. side',
        no_results: 'Ingen poster matcher dine filtre',
        no_items: 'Ingen elementer fundet',
        sort_by: 'Sortér efter',
        ascending: 'Stigende',
        descending: 'Faldende',
        all_statuses: 'Alle Statusser',
        all_types: 'Alle Typer',
        all_categories: 'Alle Kategorier',
        all_severities: 'Alle Alvorlighedsgrader',
        date_range: 'Datointerval',
        from_date: 'Fra',
        to_date: 'Til',
        clear_filters: 'Ryd Filtre',
        apply_filters: 'Anvend Filtre',
        save_changes: 'Gem Ændringer',
        discard_changes: 'Kassér Ændringer',
        confirm_discard: 'Du har ikke-gemte ændringer. Vil du kassere dem?',
        copy: 'Kopiér',
        duplicate: 'Duplikér',
        move: 'Flyt',
        assign: 'Tildel',
        unassign: 'Fjern Tildeling',
        approve: 'Godkend',
        reject: 'Afvis',
        deleting: 'Sletter...',
        processing: 'Behandler...',
        loading_page: 'Indlæser side...',
        operation_success: 'Handlingen blev gennemført',
        operation_failed: 'Handlingen mislykkedes. Prøv venligst igen.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
            gov_vendor_read: 'read vendors',
            vendor_questionnaire_read: 'read vendor questionnaires',
            ai_governance_read: 'read AI systems',
            dpia_read: 'read data processing impact assessments',
            incident_read: 'read incidents',
            dm_evidence_read: 'read Data Mapping evidence',
            vehicle_monitoring_read: 'read vehicle monitoring devices',
            forms_instance_read: 'read form records',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Netværksfejl. Kontrollér din forbindelse.',
        session_expired: 'Sessionen er udløbet. Log venligst ind igen.',
        test: 'Test',
        pause: 'Sæt på pause',
        resume: 'Genoptag',
        activate: 'Aktivér',
        last_sync: 'Seneste Synkronisering',
        health: 'Sundhed',
        email: 'E-mail',
        role: 'Rolle',
        title: 'Titel',
        owner: 'Ejer',
        priority: 'Prioritet',
        category: 'Kategori',
        details: 'Detaljer',
        overview: 'Oversigt',
        configuration: 'Konfiguration',
        history: 'Historik',
        new_item: 'Ny',
        edit_item: 'Rediger',
        select_option: 'Vælg...',
        total: 'I alt',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        enabled: 'Aktiveret',
        disabled: 'Deaktiveret',
        last_login: 'Seneste Login',
        never: 'Aldrig',
        password_changed: 'Adgangskode ændret. Du vil blive logget ud.',
        password_change_failed: 'Kunne ikke ændre adgangskode',
        passwords_mismatch: 'Adgangskoderne stemmer ikke overens',
        install: 'Installér',
        install_named: 'Installér {name}',
        uninstall: 'Afinstallér',
        installed: 'Installeret',
        available: 'Tilgængelig',
        catalog: 'Katalog',
        published: 'Publiceret',
        version: 'version',
        market: 'Marked',
        language: 'Sprog',
        framework: 'Rammeværk',
        template: 'Skabelon',
        connector_type: 'Konnektortype',
        polling_interval: 'Afstemningsinterval (minutter)',
        new_instance: 'Ny Instans',
        change_password: 'Skift Adgangskode',
        current_password: 'Nuværende Adgangskode',
        new_password: 'Ny Adgangskode',
        confirm_password: 'Bekræft Adgangskode',
        comments: 'Kommentarer',
        completed: 'Afsluttet',
        contact: 'Kontakt',
        hide: 'Skjul',
        hide_details: 'Skjul Detaljer',
        label: 'Etiket',
        max: 'Maks',
        min: 'Min',
        na: 'N/A',
        of: 'af',
        showing: 'Viser',
        page: 'Side',
        preview: 'Forhåndsvisning',
        print: 'Udskriv',
        show: 'Vis',
        status_active: 'Aktiv',
        status_completed: 'Afsluttet',
        status_draft: 'Kladde',
        status_in_progress: 'I Gang',
        status_pending: 'Afventende',
        view_details: 'Vis Detaljer',
        all_priorities: 'Alle Prioriteter',
        pipeline: 'Rørledning',
        all_requests: 'Alle Anmodninger',
        schedule: 'Planlæg',
        deploy: 'Udsend',
        rollback: 'Tilbagerul',
        advance_stage: 'Fremryk Fase',
        submit_for_approval: 'Indsend til Godkendelse',
        approval_log: 'Godkendelseslog',
        approver: 'Godkender',
        decision: 'Beslutning',
        decided_at: 'Besluttet Den',
        rollout_stages: 'Udrulningsfaser',
        approval_comment: 'Godkendelseskommentar',
        optional_comment: 'Valgfri kommentar...',
        impact_summary: 'Konsekvensoversigt',
        target_property_ids: "Mål-ejendoms-ID'er (kommasepareret)",
        policy_id: 'Politik-ID',
        target_version: 'Målversion',
        target_version_placeholder: 'f.eks. 2.0.0',
        scheduled_label: 'Planlagt',
        scheduled_start: 'Planlagt Start',
        scheduled_end: 'Planlagt Slut',
        properties_count: '{count} ejendommer',
        results: 'Resultater',
        comparison_results: 'Sammenligningsresultater',
        past_comparison_reports: 'Tidligere Sammenligningsrapporter',
        scanning: 'Scanner...',
        run_inconsistency_scan: 'Kør Inkonsistensscanning',
        property_ids_label: "Ejendoms-ID'er (kommasepareret, min 2)",
        comparison_type: 'Sammenligningstype',
        golden_template_id: 'Gylden Skabelon-ID (valgfrit)',
        leave_blank_compare: 'Lad feltet stå tomt for at sammenligne ejendommene indbyrdes',
        add_notes_placeholder: 'Tilføj dine analysenoter her...',
        covered_of_active: '{covered} af {active} aktive ejendommer dækket',
        fetch_error: 'Kunne ikke hente data. Prøv venligst igen.',
        run_comparison_error: 'Kunne ikke udføre sammenligning. Prøv venligst igen.',
        save_notes_error: 'Kunne ikke gemme noter. Prøv venligst igen.',
        scan_complete: 'Inkonsistensscanning gennemført.',
        scan_error: 'Kunne ikke udføre inkonsistensscanning. Prøv venligst igen.',
        min_two_properties: "Angiv venligst mindst 2 ejendoms-ID'er til sammenligning.",
        comma_separated: 'kommasepareret',
        language_english: 'Engelsk',
        language_vietnamese: 'Vietnamesisk',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Forfatter',
        dependencies: 'Afhængigheder',
        checksum: 'Kontrolsum',
        changelog: 'Ændringslog',
        tier: 'Niveau',
        phone: 'Telefon',
        launch: 'Start',
        complete: 'Fuldfør',
        back_to_list: 'Tilbage til liste',
        clear_selection: 'Ryd valg',
        deactivate: 'Deaktiver',
        scope: 'Omfang',
        deadline: 'Frist',
        progress: 'Fremskridt',
        frequency: 'Frekvens',
        reviewer: 'Gennemgår',
        user: 'Bruger',
        campaign: 'Kampagne',
        items: 'Elementer',
        next_run: 'Næste kørsel',
        last_run: 'Seneste kørsel',
        overdue: 'FORSINKET',
        creating: 'Opretter...',
        launching: 'Starter...',
        loaded: 'loaded',
        page_size: 'Sidestørrelse',
        per_page: '/ side',
        urgent: 'Haster',
        pagination: 'Sideinddeling',
        metric: 'Metrisk',
        value: 'Værdi',
        country: 'Land',
        showing_chart_view: 'Viser diagramvisning',
        showing_table_view: 'Viser datatabelvisning',
        policies: 'Politikker',
        compliant: 'Overensstemmende',
        non_compliant_status: 'Ikke-kompatibel',
        all_data: 'Alle data',
        sr_urgent: 'Haster',
        n_per_page: '{n} / side',
        select_user_placeholder: '-- Vælg bruger --',
        field_required: 'Dette felt er påkrævet',
        results_found: 'resultater fundet',
        select_placeholder: '-- Vælg --',
        unknown_user: 'Ukendt bruger',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'Eksportér CSV',
        not_applicable: 'I/T',
        aria_overdue_since: 'Forfalden siden {date}',
        placeholders: {
            enter_justification: 'Indtast begrundelse',
            justification: 'Begrundelse',
            campaign_name_example: 'Q1 2026 Adgang Gennemgang',
            optional_description: 'Valgfri beskrivelse',
            enter_user_id: 'Indtast gennemgåerens bruger-ID',
            enter_user_id_short: 'Indtast bruger ID',
            schedule_name_example: 'Kvartalsvis adgangsattestering',
            enter_property_id: 'Indtast egenskab ID',
            enter_policy_id: 'Indtast politik-ID',
            property_or_group_id: 'Egenskab eller gruppe ID',
            describe_template_purpose: 'Beskriv the skabelon formål...',
            step_title: 'Trintitel...',
            detailed_instructions: 'Detaljerede instruktioner...',
            filter_by_taxonomy_id: 'Filter efter Taksonomi ID...',
            describe_classification_rule: 'Beskriv klassificeringsreglen...',
            describe_dedup_rule: 'Beskriv dedupliceringsreglen...',
            describe_import_template: 'Beskriv the importer skabelon...',
            reason_for_rejection: 'Årsag til afvisning...',
            jurisdiction_codes_example: 'VN, SG, EU',
            filter_by_locale: 'Filter efter lokalitet...',
            filter_by_event_type: 'Filter efter hændelse type',
            filter_by_actor_id: 'Filtrer efter aktør-ID',
            filter_by_market_code: 'Filter efter marked kode...',
            filter_by_framework_id: 'Filter efter rammeværk ID...',
            add_comment: 'Tilføj en kommentar...',
            provide_rejection_reason: 'Angiv en årsag for afvisning af denne indsendelse...',
            filter_by_jurisdiction: 'Filtrer efter jurisdiktionskode...',
            enter_report_title: 'Indtast rapport titel',
            enter_name: 'Indtast navn',
            enter_description: 'Indtast beskrivelse',
            enter_org_id: 'Indtast Org ID to tilføj',
            describe_evidence_gap: 'Beskriv the dokumentation mangel...',
            expected_evidence: 'Hvilken dokumentation forventes',
            entity_identifier: 'Enhedsidentifikator',
            remediation_steps: 'Trin til at afhjælpe denne mangel',
            user_id_or_name: 'Bruger ID eller navn',
            reporting_period_id: 'Rapporteringsperiode-ID',
            maturity_model_id: 'Modenhed model ID',
            describe_maturity_model: 'Beskriv denne modenhedsmodel...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Angiv en årsag for afvisning af denne anmodning',
            describe_emergency: 'Beskriv nødsituationen, der kræver forhøjet adgang (minimum 20 tegn)',
            enter_delegate_id: 'Indtast bruger ID of the deleger',
            provide_delegation_reason: 'Angiv en årsag for denne delegering',
            client_identifier: 'Klient-identifikator',
            client_secret: 'Klienthemmelighed',
            time_format: 'TT:MM',
            describe_sod_rule: 'Beskriv formålet med denne SoD-regel...',
            enter_role_id: 'Indtast rolle-ID',
            explain_exception: 'Forklar hvorfor denne undtagelse er nødvendig...',
            describe_compensating_controls: 'Beskriv kompenserende kontroller...',
            full_name: 'Fulde navn',
            min_chars: 'Min. 12 tegn',
            enter_override_justification: 'Indtast begrundelse for at tilsidesætte denne portevaluering',
            enter_evidence_id: 'Indtast bevis-id for at linke:',
            date_format: 'DD/MM/ÅÅÅÅ',
            describe_pack_contents: 'Beskriv pakkens indhold og formål',
            select_reviewers: 'Vælg bedømmere...',
        },
        aria_previous_page: 'Forrige side',
        aria_next_page: 'Næste side',
        aria_results_per_page: 'Resultater pr. side',
        per_page_25: '25 / side',
        per_page_50: '50 / side',
        per_page_100: '100 / side',
        select_language: 'Vælg sprog',
        today: 'I dag',
        tomorrow: 'I morgen',
        dashboard_load_error: 'Kunne ikke indlæse dashboard. Prøv venligst igen.',
        enum_grade: {
            red: 'Rød',
            amber: 'Amber',
            yellow: 'Gul',
            green: 'Grøn',
        },
        enum_severity: {
            critical: 'Kritisk',
            high: 'Høj',
            medium: 'Middel',
            low: 'Lav',
        },
        enum_priority: {
            critical: 'Kritisk',
            high: 'Høj',
            medium: 'Middel',
            low: 'Lav',
        },
        enum_status: {
            pending: 'Afventer',
            in_progress: 'I gang',
            completed: 'Afsluttet',
            blocked: 'Blokeret',
            escalated: 'Eskaleret',
            cancelled: 'Annulleret',
            overdue: 'Forsinket',
            not_started: 'Ikke startet',
            new: 'Ny',
            open: 'Åbn',
            closed: 'Lukket',
            acknowledged: 'Anerkendt',
            resolved: 'Løst',
        },
        enum_department: {
            unassigned: 'Ikke tildelt',
        },
        enum_form_category: {
            assessment: 'Vurdering',
            assessment_report: 'Vurderingsrapport',
            dpia: 'DPIA',
            authority_submission: 'Myndighedsforelæggelse',
            cross_border_transfer: 'Grænseoverskridende overførsel',
            vendor: 'Sælger',
            breach: 'Overtrædelse',
            consent: 'Samtykke',
            governance: 'Governance',
            register: 'Tilmeld dig',
            monitoring: 'Overvågning',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Privatliv',
            incident: 'Hændelse',
            org: 'Organisation',
            sector: 'Sektor',
            classification: 'Klassifikation',
            discovery: 'Opdagelse',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Rettighedsanmodninger',
            assessments: 'Vurderinger',
            incidents: 'Hændelser',
            incident_ops: 'Incident Ops',
            monitoring: 'Overvågning',
            vendor_risk: 'Sælgerrisiko',
            governance: 'Governance',
            deletion_orchestration: 'Sletning',
            access_review: 'Adgang gennemgang',
            consent: 'Samtykke',
            consent_properties: 'Samtykkeegenskaber',
            forms: 'Formularer',
            localization: 'Lokalisering',
            program_governance: 'Programstyring',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Klassifikation',
            discovery: 'Opdagelse',
            manual: 'Manual',
            data_mapping: 'Datakortlægning',
        },
        enum_risk_level: {
            critical: 'Kritisk',
            high: 'Høj',
            medium: 'Middel',
            low: 'Lav',
        },
        enum_request_type: {
            all: 'Alle',
            access: 'Adgang (DSAR)',
            deletion: 'Sletning',
            correction: 'Rettelse',
            objection: 'Indsigelse',
            portability: 'Bærbarhed',
            restriction: 'Begrænsning',
            consent_withdrawal: 'Tilbagetrækning af samtykke',
            protective_measures: 'Beskyttelsesforanstaltninger',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'engelsk',
            vi: 'vietnamesisk',
            all: 'Begge dele',
        },
        enum_event_type: {
            case_created: 'Sag oprettet',
            status_changed: 'Status ændret',
            case_assigned: 'Sag tildelt',
            note_added: 'Bemærk tilføjet',
            attachment_added: 'Vedhæftet fil tilføjet',
            attachment_deleted: 'Vedhæftet fil slettet',
            reference_added: 'Reference tilføjet',
            checklist_updated: 'Tjekliste opdateret',
            identity_verified: 'Identitet bekræftet',
            closure_summary_added: 'Afslutningsoversigt',
            approval_granted: 'Godkendelse givet',
            template_applied: 'Skabelon anvendt',
            export_generated: 'Eksport genereret',
            sla_warning: 'SLA Advarsel',
            sla_overdue: 'SLA Forsinket',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Eksport mislykkedes',
        you: 'You',
        export_failed: 'Eksport mislykkedes. Prøv igen.',
        filters: 'Filtre',
        failed: 'Fejlet',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Fjernet',
        draft: 'Kladde',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        pending: 'Afventende',
        in_progress: 'I Gang',
        completed: 'Afsluttet',
        archived: 'Arkiveret',
        published: 'Publiceret',
        approved: 'Godkendt',
        rejected: 'Afvist',
        cancelled: 'Annulleret',
        blocked: 'Blokeret',
        deferred: 'Udskudt',
        not_started: 'Ikke Påbegyndt',
        dismissed: 'Afvist',
        failed: 'Fejlet',
        in_review: 'Under Gennemgang',
        deprecated: 'Forældet',
        scheduled: 'Planlagt',
        deploying: 'Udsender',
        deployed: 'Udsendt',
        paused: 'Sat på Pause',
        rolled_back: 'Tilbagerullet',
        received: 'Modtaget',
        processing: 'Behandler',
        under_assessment: 'Under Vurdering',
        implemented: 'Implementeret',
        pending_approval: 'Afventer Godkendelse',
        pending_deletion: 'Afventer Sletning',
        on_hold: 'Sat i Bero',
        deleted: 'Slettet',
        expired: 'Udløbet',
        outdated: 'Forældet',
        drifted: 'Afveget',
        decommissioned: 'Nedlukket',
        match: 'Match',
        different: 'Forskellig',
        missing: 'Manglende',
        confirmed: 'Bekræftet',
        revoked: 'Tilbagekaldt',
        escalated: 'Eskaleret',
        system: 'System',
        custom: 'Brugerdefineret',
        warning: 'Advarsel',
        blocking: 'Blokerende',
        analyst: 'Analytiker',
        viewer: 'Fremviser',
        daily: 'Dagligt',
        weekly: 'Ugentligt',
        policies: 'Politikker',
        denied: 'Afvist',
        verified: 'Verificeret',
        detected: 'Opdaget',
        ignored: 'Ignoreret',
        open: 'Åbn',
        resolved: 'Løst',
        suspended: 'Suspenderet',
        retired: 'Pensioneret',
        review: 'Gennemgå',
        generated: 'Genereret',
        exported: 'Eksporteret',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Lav',
        medium: 'Middel',
        high: 'Høj',
        critical: 'Kritisk',
    },
    sensitivity: {
        standard: 'Standard',
        sensitive: 'Følsom',
        special_category: 'Særlig Kategori',
        badge_sensitive: 'Følsom',
    },
    change_types: {
        policy_update: 'Politikopdatering',
        new_binding: 'Ny Binding',
        remove_binding: 'Fjern Binding',
        configuration_change: 'Konfigurationsændring',
        banner_update: 'Banneropdatering',
    },
    comparison_types: {
        full: 'Fuld',
        banner_only: 'Kun Banner',
        purposes_only: 'Kun Formål',
        legal_bases_only: 'Kun Retsgrundlag',
        consent_settings: 'Samtykkeindstillinger',
    },
    lifecycle_stages: {
        active: 'Aktiv',
        archived: 'Arkiveret',
        pending_deletion: 'Afventer Sletning',
        deleted: 'Slettet',
        on_hold: 'Sat i Bero',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Personoplysninger',
        consent_record: 'Samtykkeregistrering',
        user_account: 'Brugerkonto',
        audit_log: 'Revisionslog',
        document: 'Dokument',
        backup: 'Sikkerhedskopi',
    },
    risk_categories: {
        data_breach: 'Databrud',
        unauthorized_access: 'Uautoriseret Adgang',
        data_loss: 'Datatab',
        regulatory_non_compliance: 'Regulatorisk Manglende Overholdelse',
        third_party_risk: 'Tredjepartsrisiko',
        operational: 'Operationel',
        technical: 'Teknisk',
        legal: 'Juridisk',
    },
    mitigation_types: {
        technical: 'Teknisk',
        organizational: 'Organisatorisk',
        contractual: 'Kontraktuel',
        procedural: 'Proceduremæssig',
    },
    safeguard_types: {
        encryption_in_transit: 'Kryptering i transit',
        encryption_at_rest: 'Kryptering i hvile',
        access_controls: 'Adgangskontrol',
        contractual_clauses: 'Kontraktbestemmelser',
        data_minimization: 'Dataminimering',
        pseudonymization: 'Pseudonymisering',
        audit_rights: 'Revisionsrettigheder',
        breach_notification: 'Underretning om brud',
        data_localization: 'Datalokalisering',
        other: 'Andet',
    },
    scoring_methods: {
        weighted_average: 'Vægtet Gennemsnit',
        simple_average: 'Simpelt Gennemsnit',
        maximum: 'Maksimum',
        manual: 'Manuel',
    },
    dpa_status: {
        not_required: 'Ikke Påkrævet',
        required: 'Påkrævet',
        in_progress: 'I Gang',
        completed: 'Afsluttet',
        signed: 'Underskrevet',
    },
    disposal_methods: {
        anonymize: 'Anonymisér',
        pseudonymize: 'Pseudonymisér',
        aggregate: 'Aggregér',
        delete: 'Slet',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Kontrolpanel',
        no_data: 'Ingen kontrolpaneldata tilgængelige.',
        no_data_heading: 'Ingen livedata endnu',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Samtykkedækning',
        ctia_filings: 'CTIA-indberetninger',
        next_dpia_review: 'Næste DPIA-revision',
        next_review_none: 'Ingen planlagt',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'MPS-indberetninger for hændelser',
        compliance_score: 'Compliancescore',
        grade: 'Karakter',
        no_score: 'Ingen compliancescore beregnet endnu.',
        rights_requests: 'Anmodning om Rettigheder',
        open: 'Åbne',
        sla_breached: 'SLA Overskredet',
        sla_approaching: 'SLA Forfald Snart',
        closed_month: 'Lukkede (Måned)',
        incidents: 'Hændelser og Databrud',
        active: 'Aktive',
        breaches: 'Databrud',
        overdue_tasks: 'Forfaldne Opgaver',
        breach_72h_notice: '72-timers lovpligtig anmeldelsesfrist gælder for aktive brud',
        recommendations: 'Anbefalinger',
        take_action: 'Tag handling',
        drift_events: 'Afvigelser og Godkendelser',
        open_events: 'Åbne Hændelser',
        pending_approvals: 'Afventende Godkendelser',
        monitoring: 'Overvågningsopgaver',
        programs: 'Programmer',
        overdue: 'Forfaldne',
        pending: 'Afventende',
        ric_cases: 'Tilsynssager',
        coverage: 'Datadækning',
        systems: 'Systemer',
        activities: 'Aktiviteter',
        vendors: 'Leverandører',
        dpias: 'Konsekvensanalyser',
        open_gaps: 'åbne mangler',
        view_as_table: 'Vis som tabel',
        view_as_chart: 'Vis som diagram',
        table_metric_header: 'Metrik',
        table_value_header: 'Vaerdi',
        announce_chart_view: 'Viser diagramvisning',
        announce_table_view: 'Viser tabelvisning',
        section_core_metrics: 'Kernemaalinger',
        section_task_management: 'Opgavestyring',
        section_compliance_insights: 'Compliance-indsigter',
        configure_dashboard: 'Konfigurer dashboard',
        config_col_active: 'Aktiv',
        config_col_widget: 'Widget',
        collapse_section: 'Fold sammen',
        expand_section: 'Udfold',
        reset_to_defaults: 'Nulstil til standarder',
        preferences_saved: 'Dashboard-indstillinger gemt',
        preferences_reset: 'Dashboard nulstillet til rollestandard',
        preferences_load_error: 'Kunne ikke indlæse dashboard-indstillinger',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Gennemsnitlig systemscore',
        factor_avg_vendor_score: 'Gennemsnitlig leverandørscore',
        factor_processing_coverage: 'Behandlingsaktivitetsdækning',
        factor_ropa_completeness: 'RoPA fuldstændighed',
        factor_drift_penalty: 'Driftsbegivenheder straf',
        rec_improve_system_gov: 'Forbedre systemstyringsdokumentation',
        rec_improve_vendor_gov: 'Forbedre leverandørstyringsdokumentation',
        rec_document_processing: 'Dokumentbehandlingsaktiviteter for udækkede systemer',
        rec_generate_ropa: 'Generer RoPA-poster for aktive behandlingsaktiviteter',
        rec_resolve_drift: 'Løs åbne kritiske og alvorlige drifthændelser',
        score_good: 'Godt',
        score_fair: 'Fair',
        score_poor: 'Dårlig',
        score_critical: 'Kritisk',
        drag_to_reorder: 'Træk for at omarrangere',
        widget_moved: 'Widget flyttet',
        press_space_to_grab: 'Tryk på mellemrum for at gribe, piletaster for at flytte, mellemrum for at slippe',
        reorder_instructions: 'Brug træk og slip eller tastaturgenveje til at omarrangere widgets',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Opgaveoversigt',
            pending: 'Afventer',
            in_progress: 'Igangværende',
            overdue: 'Forfalden',
            done_7d: 'Udført (7d)',
            by_priority: 'Efter prioritet',
            view_all_tasks: 'Se alle opgaver',
            upcoming_tasks: 'Kommende opgaver (Næste 7 dage)',
            no_upcoming: 'Ingen opgaver forfalder inden for de næste 7 dage.',
            view_calendar: 'Se kalender',
            overdue_alerts: 'Forfalden',
            no_overdue: 'Ingen forfaldne opgaver. Alt er på sporet.',
            assigned_to: 'Tildelt til',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Se alle forfaldne',
            notifications: 'Seneste meddelelser',
            unread: 'unread',
            no_notifications: 'Ingen meddelelser. I er alle fanget!',
            view_all_notifications: 'Se alle meddelelser',
            load_error: 'Kunne ikke indlæse data.',
            day_late: 'dag for sent',
            days_late_plural: 'dage for sent',
            due_today: 'Forfalder i dag',
            just_now: 'lige nu',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Statistik på tværs af organisationer',
            by_organization: 'Efter organisation',
            overdue_across_orgs: 'forfaldne opgaver på tværs af organisationer',
            overdue_count_label: '{count} overdue tasks',
            total: 'I alt',
            tasks: 'tasks',
            organization: 'Organisation',
            current_org: 'Nuværende organisation',
            all_orgs: 'Alle organisationer',
            all_organizations: 'Alle organisationer',
            filter_by_org: 'Filtrer efter organisation',
            cross_org: 'Cross-org',
            empty_calendar: 'Ingen opgaver i denne periode. Opret din første opgave, eller tjek dine filtre.',
            empty_my_tasks: 'Du har ingen opgaver tildelt. Flot arbejde!',
            empty_overdue: 'Ingen forfaldne opgaver. Alt er på sporet.',
            empty_notifications: 'Ingen meddelelser. I er alle fanget!',
            empty_workflows: 'Ingen arbejdsgange konfigureret. Gennemse skabeloner for at komme i gang.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Mine Opgaver',
            no_tasks: 'Ingen opgaver tildelt til dig.',
            load_error: 'Kunne ikke indlæse opgaver.',
            overdue: 'forsinket',
            remaining: 'tilbage',
            view_all: 'Vis Alle Opgaver',
        },
        my_section_status_widget: {
            title: 'Min Sektionsstatus',
            overall_completion: 'Samlet',
            sections_count: 'Sektioner',
            no_sections: 'Ingen sektioner tildelt til din afdeling.',
            load_error: 'Kunne ikke indlæse sektionsstatus.',
            department_tasks_title: 'Afdelingens opgaver',
            no_department_tasks: 'Din afdeling har ingen opgaver.',
            department_tasks_error: 'Kunne ikke indlæse afdelingens opgaver.',
            no_department: 'Du er ikke tilknyttet en afdeling.',
        },
        revision_requests_widget: {
            title: 'Revisionsanmodninger',
            no_revisions: 'Ingen revisionsanmodninger. Alle sektioner er på sporet.',
            load_error: 'Kunne ikke indlæse revisionsanmodninger.',
        },
        consent_summary: {
            title: 'Samtykke',
            active: 'Aktiv',
            withdrawn_30d: 'Tilbagetrukket (30d)',
            expiring_7d: 'Udløber (7d)',
        },
        transfer_assessments: {
            title: 'Overførselsvurderinger',
            open: 'Åbn',
            approved: 'Godkendt',
            gaps_flagged: 'Huller markeret',
        },
        breach_notifications: {
            title: 'Underretninger om brud',
            deadline_72h: '72 timers frist',
            submitted: 'Indsendt',
            pending: 'Afventer',
        },
        authority_requests: {
            title: 'Myndighedsanmodninger',
            pending: 'Afventer',
            overdue: 'Forfalden',
            responded_30d: 'Svarede (30d)',
        },
        data_subjects_widget: {
            title: 'Datasubjekter',
            total: 'I alt',
            high_risk: 'Høj risiko',
            active_30d: 'Aktiv (30d)',
        },
        audit_activity_summary: {
            title: 'Revisionsaktivitet',
            events_today: 'Begivenheder i dag',
            events_7d: 'Begivenheder (7d)',
            unique_actors_30d: 'Unikke skuespillere (30d)',
        },
        onboarding_progress: {
            title: 'Onboarding fremskridt',
            completion_pct: 'Komplet',
            status_label: 'Status',
            current_step: 'Nuværende trin',
            status: {
                in_progress: 'I gang',
                completed: 'Afsluttet',
                skipped: 'Sprang over',
                not_started: 'Ikke startet',
            },
        },
        sector_overlay_reports_due: {
            title: 'Forfaldne sektoroverlay-rapporter',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Filtrer efter rammeværk',
            all_frameworks: 'Alle rammeværker',
            filing_lifecycle: 'Indberetningslivscyklus',
            filing_pipeline: 'Indberetningspipeline',
            draft: 'Udkast',
            internal_review: 'Intern gennemgang',
            approved: 'Godkendt',
            submitted: 'Indsendt',
            supplement_requested: 'Tillæg anmodet',
            resubmitted: 'Genindsendt',
            accepted: 'Accepteret',
            rejected: 'Afvist',
            closed: 'Lukket',
            deadline: 'Deadline',
            days_remaining: 'dage tilbage',
            days_overdue: 'dage forsinket',
            no_filings: 'Ingen aktive indberetninger.',
            department_consolidation: 'Afdelingskonsolidering',
            departments_completed: 'Afdelinger fuldført',
            departments_pending: 'Afdelinger afventende',
            departments_overdue: 'Afdelinger forsinkede',
            no_consolidation_cases: 'Ingen tværafdelingssager i gang.',
            vendor_risk: 'Leverandør- og tredjepartsrisiko',
            pending_assessment: 'Afventende vurdering',
            overdue_questionnaires: 'Forsinkede spørgeskemaer',
            dpa_review_queue: 'DPA-gennemgangskø',
            risk_distribution: 'Risikofordeling',
            total_vendors: 'Samlede leverandører',
            by_risk_level: 'Efter risikoniveau',
            no_vendor_data: 'Ingen leverandørrisikodata tilgængelige.',
            discovery_summary: 'Dataopdagelse',
            discovery_classification: 'Dataopdagelse og -klassificering',
            active_scans: 'Aktive scanninger',
            labels_pending: 'Etiketter afventer gennemgang',
            exceptions_unresolved: 'Undtagelser uløste',
            pending_review: 'Afventende gennemgang',
            exceptions: 'Undtagelser',
            approved_labels: 'Godkendte etiketter',
            total_classified: 'Samlet klassificeret',
            no_discovery_data: 'Ingen opdagelsesdata tilgængelige.',
            ai_governance: 'AI-systemstyring',
            systems_inventory: 'Systemer i inventar',
            systems_in_inventory: 'Systemer i oversigt',
            classification_breakdown: 'Klassifikationsopdeling',
            dossier_completion: 'Dossierfuldførelse',
            by_classification: 'Efter klassificering',
            labeling_status: 'Etiketteringsstatus',
            not_started: 'Ikke påbegyndt',
            no_ai_data: 'Ingen AI-styringsdata tilgængelige.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Brud- og hændelsesrespons',
            active_breaches: 'Aktive brud',
            notification_status: 'Notifikationsstatus',
            remediation_progress: 'Udbedringsfremskridt',
            supplement_loops: 'Supplementsløjfer',
            no_breach_data: 'Ingen aktive brudsager.',
            audit_readiness: 'Revisions- og vurderingsberedskab',
            assessments: 'Vurderinger',
            gap_findings: 'Mangelresultater',
            evidence_completion: 'Bevisfuldførelse',
            export_ready: 'Klar til eksport',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Ikke klar',
            no_audit_data: 'Ingen revisionsberedskabsdata tilgængelige.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Cybersikkerhed 2018',
            framework_label_vn_ecommerce: 'VN E-handel 2023',
            framework_label_vn_telecom: 'VN Telekommunikation 2023',
            framework_label_vn_ai: 'VN AI-lov 2025',
            framework_label_gdpr: 'GDPR',
            framework_label_unspecified: 'Uspecificeret',
            my_department: 'Min afdeling',
            my_tasks: 'Mine opgaver',
            section_status: 'Sektionsstatus',
            revision_requests: 'Revisionsanmodninger',
            completion: 'Fuldførelse',
            reason: 'Årsag',
            requested_at: 'Anmodet den',
            no_tasks_assigned: 'Ingen opgaver tildelt dig.',
            no_sections: 'Ingen sektioner tildelt din afdeling.',
            no_revisions: 'Ingen revisionsanmodninger.',
            load_error: 'Kunne ikke indlæse data.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instanser',
            credentials: 'Legitimationsoplysninger',
            event_bus: 'Hændelsesbus',
            definitions: 'Definitioner',
            brds: 'BRD-opsamling',
            security_reviews: 'Sikkerhedsgennemgange',
            poc_scorecards: 'POC-scorekort',
            automation_planning: 'Automatiseringsplanlægning',
            system_inventory: 'Systemoversigt',
            traceability_matrix: 'Sporbarhedsmatrix',
            gap_register: 'Gap-register',
        },
        instances: {
            title: 'Konnektorinstanser',
            page_title: 'Forbindelser',
            add: 'Tilføj Instans',
            test: 'Test Forbindelse',
            sync_now: 'Synkroniser Nu',
            new_instance: 'Ny Konnektor Instans',
            no_instances: 'Ingen konnektor instanser fundet',
            instance_count: '{count} instans(er)',
            confirm_delete: 'Slet konnektorinstans "{name}"?',
            all_connector_types: 'Alle konnektortyper',
            select_connector: 'Vælg en konnektor...',
            fetch_error: 'Kunne ikke hente konnektorinstanser. Prøv venligst igen.',
            fetch_definitions_error: 'Kunne ikke hente konnektordefinitioner. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette konnektorinstans',
            test_error: 'Kunne ikke teste konnektor. Prøv venligst igen.',
            status_error: 'Kunne ikke opdatere konnektorstatus. Prøv venligst igen.',
            delete_error: 'Kunne ikke slette konnektorinstans. Prøv venligst igen.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Status opdateret',
            reactivate: 'Genaktiver',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Legitimationsoplysninger',
            page_title: 'Forbindelsesoplysninger',
            add: 'Tilføj Legitimation',
            rotate: 'Roter',
            expired: 'Udløbet',
            expiring: 'Udløber Snart',
            orphaned: 'Forældreløs',
            new_credential: 'Ny legitimationsoplysning',
            no_credentials: 'Ingen credentials fundet',
            no_expiry: 'Intet udløb',
            linked_connectors: 'Tilknyttede konnektorer',
            expires_at: 'Udløber den',
            value_label: 'Værdi * (kun skrivning)',
            confirm_delete_title: 'Bekræft sletning',
            confirm_delete_message: 'Slet denne legitimation? Tilknyttede konnektorer mister adgang.',
            rotate_title: 'Rotér legitimation',
            new_credential_value: 'Ny legitimationsværdi',
            fetch_error: 'Kunne ikke indlæse legitimationsoplysninger. Prøv venligst igen.',
            health_error: 'Kunne ikke indlæse sundhed. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette legitimationsoplysninger',
            rotate_error: 'Kunne ikke rotere legitimationsoplysninger',
            delete_error: 'Kunne ikke slette legitimationsoplysninger',
        },
        event_bus: {
            title: 'Hændelsesbus',
            page_title: 'Hændelsesbus',
            events: 'Hændelser',
            subscriptions: 'Abonnementer',
            dlq: 'Dødsbrevkø',
            publish: 'Publicer Hændelse',
            retry: 'Prøv Igen',
            dismiss: 'Afvis',
            search_events: 'Søg hændelser...',
            filter_event_type: 'Filter efter hændelse type...',
            event_type: 'Hændelsestype',
            source_module: 'Kildemodul',
            timestamp: 'Tidsstempel',
            delivery_status: 'Leveringsstatus',
            correlation_id: 'Korrelations-ID',
            tracking_id: 'Sporings-ID',
            delivery_status_hint: 'Leveringsstatus opdateres automatisk, efterhånden som hændelser behandles.',
            no_events: 'Ingen hændelser fundet',
            total_with_count: '{count} i alt',
            prev: 'Forrige',
            new_subscription: '+ Nyt abonnement',
            subscriber_type: 'Abonnenttype',
            subscriber_id: 'Abonnent-ID',
            event_type_pattern: 'Hændelse Type Mønster',
            event_type_pattern_placeholder: 'f.eks. incident.*',
            filter_expression: 'Filterudtryk',
            filter_expression_placeholder: 'Valgfrit filter',
            subscriber_type_hint: 'Vælg hvordan hændelser leveres: webhook sender HTTP-anmodninger, e-mail sender notifikationer, kø sender til en beskedkø.',
            subscriber_id_hint: 'Indtast destinationen for hændelser. For webhooks, brug en URL. For e-mail, brug en adresse. For køer, brug kønavnet.',
            event_type_pattern_hint: 'Match hændelser efter type med jokertegn. Brug * til at matche ethvert segment (f.eks. incident.* matcher incident.created, incident.resolved).',
            filter_expression_hint: 'Valgfrit. Filtrer yderligere hændelser efter indhold (f.eks. severity=high).',
            subscriber_id_placeholder_webhook: 'f.eks. https://din-app.com/webhook',
            subscriber_id_placeholder_email: 'f.eks. alarmer@din-virksomhed.com',
            subscriber_id_placeholder_queue: 'f.eks. haendelses-koe',
            event_pattern: 'Hændelsesmønster',
            filter: 'Filtrer',
            enabled: 'Aktiveret',
            delivery_stats: 'Leveringsstatistik',
            no_subscriptions: 'Ingen abonnementer fundet',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} leveret / {failed} mislykkedes',
            confirm_delete_subscription: 'Slet dette abonnement?',
            unresolved: 'Uløst',
            resolved: 'Løst',
            event_id: 'Hændelses-ID',
            subscription_id: 'Abonnements-ID',
            error_code: 'Fejlkode',
            error_message: 'Fejlmeddelelse',
            attempts: 'Forsøg',
            last_failed: 'Seneste fejl',
            no_dlq_entries: 'Ingen døde brev poster fundet',
            dlq_count: '{count} post(er) i alt',
            fetch_events_error: 'Kunne ikke hente hændelser. Prøv venligst igen.',
            fetch_subscriptions_error: 'Kunne ikke hente abonnementer. Prøv venligst igen.',
            fetch_dlq_error: 'Kunne ikke hente DLQ-poster. Prøv venligst igen.',
            create_subscription_error: 'Kunne ikke oprette abonnement',
            toggle_subscription_error: 'Kunne ikke skifte abonnement',
            delete_subscription_error: 'Kunne ikke slette abonnement',
            retry_error: 'Genforsøg mislykkedes',
            dismiss_error: 'Afvisning mislykkedes',
            event_published: 'Begivenhed offentliggjort',
            publish_event_error: 'Kunne ikke publicere hændelsen. Prøv igen.',
            priority_label: 'Prioritet',
            priority_low: 'Lav',
            priority_normal: 'Normal',
            priority_high: 'Høj',
            payload_helper_text: 'Indtast hændelsesdata som JSON. Eksempel: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Abonnement oprettet',
            subscription_toggled: 'Abonnement slået til',
            subscription_deleted: 'Abonnement slettet',
            dlq_retried: 'DLQ-meddelelse forsøgt igen',
            dlq_dismissed: 'DLQ-meddelelse afvist',
        },
        definitions: {
            title: 'Forbindelsesdefinitioner',
            page_title: 'Forbindelsesdefinitioner',
            add: 'Tilføj definition',
            new_definition: 'Ny konnektordefinition',
            edit_definition: 'Rediger Connector Definition',
            no_definitions: 'Ingen forbindelsesdefinitioner fundet',
            definition_count: '{count} definition(er)',
            all_categories: 'Alle kategorier',
            select_category: 'Vælg en kategori...',
            capabilities: 'Evner',
            capabilities_hint: 'Muligheder (kommasepareret)',
            capabilities_placeholder: 'f.eks. læse, skrive, synkronisere',
            created: 'Connectordefinition oprettet',
            updated: 'Connectordefinition opdateret',
            fetch_error: 'Konnektordefinitioner kunne ikke hentes. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette forbindelsesdefinition',
            update_error: 'Kunne ikke opdatere connectordefinition',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'BRD-opsamling',
            page_title: 'BRD-registrering',
            create_brd: 'Opret BRD',
            brd_title: 'BRD-titel',
            description: 'Beskrivelse',
            requirements: 'Krav',
            status: 'Status',
            stakeholders: 'Interessenter',
            connector_label: 'Konnektor',
            select_connector: 'Vælg en konnektor...',
            approval: 'Godkendelse',
            no_brds_found: "Ingen BRD'er fundet",
            decision_records: 'Beslutningsregistreringer',
            create_decision: 'Opret beslutning',
            alternatives_considered: 'Overvejede alternativer',
            alternative_name: 'Alternativnavn',
            reason_rejected: 'Årsag til afvisning',
            add_alternative: 'Tilføj alternativ',
            remove_alternative: 'Fjern alternativ {name}',
            alternatives: 'Alternativer',
            rationale: 'Begrundelse',
            selected_connector: 'Valgt konnektor',
            comma_separated_names: 'Kommaseparerede navne',
            brd_created: 'BRD oprettet',
            decision_record_created: 'Beslutningspost oprettet',
            no_decision_records_found: 'Ingen beslutningsposter fundet',
            aria_brds_table: "BRD'er",
            aria_decision_records_table: 'Beslutningsposter',
            aria_approve: 'Godkend {name}',
            aria_archive: 'Arkivér {name}',
            brd_title_help: 'Et kort, menneskeligt læsbart navn for denne BRD - f.eks. "Jira ticketing integration", "Okta SSO udrulning".',
            brd_title_placeholder: 'f.eks. Jira billetintegration',
            description_help: 'Forretningsproblemet løser denne forbindelse. Hvilket resultat forventer du, når det er live?',
            description_placeholder: "f.eks. Automatiser oprettelse af hændelsesbilletter fra overvågningsalarmer, så vagtkald bliver søgt inden for 60'erne.",
            connector_help: 'Valgfrit. Vælg et stik, som denne BRD er til – lad det stå tomt, hvis stikket ikke er valgt endnu.',
            requirements_help: 'Kommasepareret liste over must-have-kapaciteter - f.eks. "OAuth2 login, webhook levering, revision eksport".',
            requirements_placeholder: 'OAuth2 login, webhook levering, revision eksport',
            stakeholders_help: 'Kommaseparerede navne eller e-mails på personer, der skal melde sig ud.',
            selected_connector_help: 'Vælg det stik, som denne beslutning gælder for.',
            rationale_help: 'Hvorfor dette stik blev valgt. Dæk de afgørende faktorer, så en fremtidig læser forstår opfordringen.',
            rationale_placeholder: 'f.eks. Valgte Jira, fordi den allerede indeholder vores billethistorik, og holdet er trænet i den.',
            alternatives_considered_help: 'Andre stik vurderet, og hvorfor de ikke blev valgt. Tilføjer forsvarlighed for revision.',
            alternative_name_placeholder: 'f.eks. ServiceNu',
            reason_rejected_placeholder: 'f.eks. Koster 3× højere, ingen fordel for vores use case',
        },
        security_reviews: {
            title: 'Sikkerhedsgennemgange',
            page_title: 'Sikkerhedsgennemgange',
            create_review: 'Opret gennemgang',
            connector_name: 'Konnektornavn',
            connector_label: 'Konnektor',
            select_connector: 'Vælg en konnektor...',
            reviewer: 'Bedømmer',
            select_reviewer: 'Vælg en bedømmer...',
            findings: 'Resultater (én pr. linje)',
            risk_level: 'Risikoniveau',
            risk_level_placeholder: 'f.eks. høj, middel, lav',
            notes: 'Noter',
            review_status: 'Gennemgangsstatus',
            checklist: 'Tjekliste',
            privacy_impact: 'Privatlivskonsekvens',
            approval_gate: 'Godkendelsesport',
            no_reviews_found: 'Ingen gennemgange fundet',
            review_created: 'Sikkerhedsgennemgang oprettet',
            create_control_mapping: 'Opret kontroltilknytning',
            control_mapping_created: 'Kontroltilknytning oprettet',
            connector_id_label: 'Forbindelses-ID',
            control_id_label: 'Kontrol-ID',
            control_name_label: 'Kontrolnavn',
            aria_approve: 'Godkend {name}',
            aria_reject: 'Afvis {name}',
            scan_imported: 'Scanning importeret',
            import_scan: 'Importér scanning',
            scan_format: 'Scanningsformat',
            scan_data: 'Scanningsdata',
            import_results: 'Importresultater',
            recommendations: 'Anbefalinger',
            auto_populated_from: 'Automatisk udfyldt fra {tool}',
            manual_entry: 'Manuel indtastning',
            automation_type: 'Automatiseringstype',
            select_automation_type: 'Vælg automatiseringstype...',
            automation_full: 'Fuld — stik automatiserer denne kontrol fuldt ud',
            automation_partial: 'Delvis — konnektor bidrager med beviser; mennesker handler stadig',
            automation_monitoring: 'Kun overvågning — stik observerer, håndhæver ikke',
            control_id_help: 'Brug koden fra din overholdelsesordning (f.eks. ISO-27001-A.12.1, SOC2-CC6.1 eller dit interne kontrol-id).',
            control_name_help: 'Kort navn, der kan læses af mennesker (f.eks. "Netværksadgangsbegrænsning").',
            description_label: 'Beskrivelse',
            load_sarif_example: 'Indlæs SARIF eksempel',
            load_json_example: 'Indlæs brugerdefineret JSON eksempel',
            scan_format_help: 'SARIF er OASIS-standarden, der udsendes af de fleste scannere (Snyk, CodeQL, Semgrep). Brug tilpasset JSON til værktøjer, der ikke udsender SARIF.',
            scan_data_help: 'Indsæt det fulde scanningsoutput, upload en .sarif/.json-fil, eller klik på knappen Indlæs eksempel for at se formen.',
            upload_file_label: 'Upload .sarif- eller .json-fil',
            invalid_scan_json: 'Kunne ikke parse scanningsdata som JSON. Tjek for syntaksfejl i nærheden af det fremhævede tegn.',
            save_as_review: 'Gem som sikkerhedsgennemgang',
            discard_preview: 'Kasser forhåndsvisning',
            preview_hint: 'Dette er en forhåndsvisning — intet er gemt endnu. Klik på "Gem som sikkerhedsgennemgang" for at oprette en afventende anmeldelse, der er forududfyldt med disse værdier.',
            reviewer_required_for_save: 'Vælg en anmelder for at gemme denne scanning som en sikkerhedsgennemgang.',
        },
        poc_scorecards: {
            title: 'POC-scorekort',
            page_title: 'POC-resultatkort',
            create_scorecard: 'Opret scorekort',
            connector_label: 'Konnektor',
            select_connector: 'Vælg en konnektor...',
            vendor_name: 'Leverandørnavn',
            recommendation: 'Anbefaling',
            recommendation_placeholder: 'f.eks. Fortsæt, Fortsæt ikke, Kræver yderligere evaluering',
            criteria: 'Evalueringskriterier',
            criterion_name: 'Kriterienavn',
            score: 'Score',
            max_score: 'Maks. score',
            notes: 'Noter',
            add_criterion: 'Tilføj kriterium',
            remove_criterion: 'Fjern kriterium {name}',
            total_score: 'Samlet score',
            api_quality: 'API-kvalitet',
            reliability: 'Pålidelighed',
            support_quality: 'Supportkvalitet',
            cost_score: 'Omkostningsscore',
            overall_score: 'Samlet score',
            evidence: 'Dokumentation',
            no_scorecards_found: 'Ingen scorekort fundet',
            scorecard_created: 'POC-resultatkort oprettet',
            connector_help: 'Vælg det stik, denne POC-evaluering er til.',
            vendor_name_help: 'Sælgeren eller produktet, der vurderes — f.eks. "Okta", "Datadog", "Snefnug".',
            vendor_name_placeholder: 'f.eks. Okta, Datadog, Snowflake',
            recommendation_help: 'Dit sidste opkald efter evalueringen. Hold det kort og beslutsomt.',
            criteria_help: 'Score leverandøren på hvert evalueringskriterium. Tilføj én række pr. kriterium (API kvalitet, pålidelighed, support, omkostninger osv.).',
            criterion_name_help: 'Hvad du scorer — f.eks. "API Kvalitet", "Plidelighed", "Support", "Omkostninger".',
            criterion_name_placeholder: 'f.eks. API Kvalitet',
            score_help: 'Score tildelt for dette kriterium. Må ikke overstige Max Score.',
            max_score_help: 'Topscore tilgængelig for dette kriterium. Brug den samme skala på tværs af rækker, så totalerne er sammenlignelige.',
            notes_help: 'Valgfrit. Tilføj beviser, advarsler eller links - alt, hvad der forklarer resultatet.',
        },
        common_labels: {
            aria_filter_instances: 'Filtrer forbindelsesforekomster',
            aria_filter_definitions: 'Filtrer forbindelsesdefinitioner',
            aria_filter_credentials: 'Filtrer legitimationsoplysninger',
            aria_filter_events: 'Filtrer hændelser',
            aria_filter_dlq: 'Filtrer fejlkø',
            aria_instance_summary: 'Forbindelsesforekomstoversigt',
            aria_active_connectors: 'Aktive forbindelser',
            aria_error_connectors: 'Forbindelser med fejl',
            aria_credential_health: 'Sundhedsoversigt for legitimationsoplysninger',
            aria_items_per_page: 'Elementer per side',
            show_password: 'Vis adgangskode',
            hide_password: 'Skjul adgangskode',
            payload_json_label: 'Nyttelast (JSON)',
            payload_invalid_json: 'Nyttelasten skal være gyldig JSON',
            placeholder_event_type: 'f.eks. incident.created',
            placeholder_source_module: 'f.eks. monitoring',
            placeholder_version: 'f.eks. 1.0',
        },
        statuses: {
            configuring: 'Konfigurerer',
            testing: 'Afprøvning',
            active: 'Aktiv',
            paused: 'Pause',
            error: 'Fejl',
            disabled: 'Deaktiveret',
            draft: 'Udkast',
            approved: 'Godkendt',
            archived: 'Arkiveret',
            pending: 'Afventer',
            rejected: 'Afvist',
        },
        system_types: {
            application: 'Applikation',
            database: 'Database',
            service: 'Service',
            infrastructure: 'Infrastruktur',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'On-Premise',
            cloud: 'Sky',
            hybrid: 'Hybrid',
            managed: 'Administreret',
        },
        system_statuses: {
            active: 'Aktiv',
            decommissioned: 'Afviklet',
            planned: 'Planlagt',
            under_review: 'Under gennemgang',
        },
        automation_statuses: {
            planning: 'Planlægning',
            in_progress: 'I gang',
            completed: 'Fuldført',
            on_hold: 'På hold',
        },
        gap_types: {
            functional: 'Funktionel',
            technical: 'Teknisk',
            security: 'Sikkerhed',
            compliance: 'Overholdelse',
            process: 'Proces',
        },
        severity_labels: {
            critical: 'Kritisk',
            high: 'Høj',
            medium: 'Middel',
            low: 'Lav',
        },
        gap_statuses: {
            open: 'Åben',
            in_progress: 'I gang',
            mitigated: 'Afbødet',
            closed: 'Lukket',
            accepted: 'Accepteret',
        },
        verification_statuses: {
            not_started: 'Ikke startet',
            in_progress: 'I gang',
            verified: 'Verificeret',
            failed: 'Fejlet',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Intern',
            email: 'E-mail',
            queue: 'Kø',
        },
        credential_types: {
            password: 'Adgangskode',
            api_key: 'API-nøgle',
            oauth2_client: 'OAuth2-klient',
            certificate: 'Certifikat',
            token: 'Token',
            ssh_key: 'SSH-nøgle',
            imap_credentials: 'IMAP-legitimation',
        },
        categories: {
            hr: 'HR',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Sky',
            security: 'Sikkerhed',
            itsm: 'ITSM',
            custom: 'Brugerdefineret',
        },
        delivery_statuses: {
            pending: 'Afventende',
            delivered: 'Leveret',
            partial: 'Delvis',
            failed: 'Fejlet',
        },
        automation_planning: {
            title: 'Automatiseringsplanlægning',
            page_title: 'Automatiseringsplanlægning',
            program_name: 'Programnavn',
            program_status: 'Status',
            description: 'Beskrivelse',
            linked_artifacts: 'Tilknyttede artefakter',
            create_program: 'Opret program',
            edit_program: 'Rediger program',
            no_programs_found: 'Ingen programmer fundet',
            program_created: 'Program oprettet',
            program_updated: 'Program opdateret',
            target_date: 'Måldato',
            owner: 'Ejer',
            select_status: 'Vælg status...',
            aria_programs_table: 'Automatiseringsprogrammer',
            aria_edit: 'Rediger {name}',
            export_programs: 'Eksporter programmer',
            title_label: 'Program titel',
            title_help: 'Et kort, menneskeligt læsbart navn for automatiseringsprogrammet — f.eks. "Q3 hændelse-til-Jira-udrulning", "PDPL automatisering af adgangsanmodninger".',
            title_placeholder: 'f.eks. Q3 hændelse-til-Jira-udrulning',
            description_help: 'Hvad dette program dækker, og det resultat, du forventer, når det er live. Valgfrit men anbefales.',
            description_placeholder: "f.eks. Ledningsovervågningsadvarsler ind i Jira, så vagtkald bliver søgt inden for 60'erne.",
            owner_label: 'Program ejer',
            owner_help: 'Den person, der er ansvarlig for dette program. De vil modtage meddelelser om fremskridt og porte.',
            select_owner: 'Vælg en ejer...',
            frameworks_in_scope: 'Rammer i omfang',
            frameworks_in_scope_help: 'Overholdelsesrammer, som dette program understøtter. Hjælper revisionssporet med at vise, hvilke regler automatiseringen er underlagt.',
            select_frameworks: 'Vælg rammer...',
            status_draft: 'Udkast',
            status_active: 'Aktiv',
            status_completed: 'Afsluttet',
            status_archived: 'Arkiveret',
            change_status: 'Skift status',
            status_updated: 'Status opdateret',
            aria_change_status: 'Skift status for {name}',
        },
        system_inventory: {
            title: 'Systemoversigt',
            page_title: 'Systemoversigt',
            system_name: 'Systemnavn',
            system_type: 'Systemtype',
            hosting: 'Hosting',
            status: 'Status',
            description: 'Beskrivelse',
            create_system: 'Opret system',
            edit_system: 'Rediger system',
            no_systems_found: 'Ingen systemer fundet',
            system_created: 'System oprettet',
            system_updated: 'System opdateret',
            select_type: 'Vælg type...',
            select_hosting: 'Vælg hosting...',
            select_status: 'Vælg status...',
            owner: 'Ejer',
            department: 'Afdeling',
            data_categories: 'Datakategorier',
            data_categories_placeholder: 'Kommadelt, f.eks. PII, Finansiel, Sundhed',
            jurisdiction: 'Jurisdiktion',
            jurisdiction_placeholder: 'f.eks. EU, US, Global',
            interfaces: 'Grænseflader',
            interfaces_placeholder: 'Kommadelt, f.eks. REST API, SFTP, JDBC',
            owner_placeholder: 'Vælg en ansvarlig ejer...',
            department_placeholder: 'Vælg den ansvarlige organisationsenhed...',
            system_name_hint: 'Brug det forretningsanerkendte system- eller applikationsnavn.',
            system_type_hint: 'Klassificer systemet, så rapportering kan gruppere lignende aktiver.',
            hosting_hint: 'Identificer, hvor systemet kører af hensyn til bopæl, sikkerhed og leverandørgennemgang.',
            status_hint: 'Indstil livscyklustilstanden, der bruges af anmeldelser, eksport og revisionsbeviser.',
            owner_hint: 'Vælg den aktive bruger, der er ansvarlig for systemstyring og opfølgningsopgaver.',
            department_hint: 'Brug den organisationsenhed, der er ansvarlig for at drive eller styre dette system.',
            data_categories_hint: 'Angiv de dataklasser, der behandles af dette system, adskilt af kommaer.',
            jurisdiction_hint: 'Registrer den vigtigste juridiske jurisdiktion eller hosting-jurisdiktion for compliance-mapping.',
            interfaces_hint: 'Angiv connector- eller integrationsstier, der bruges til at udveksle data.',
            description_hint: 'Opsummer, hvad systemet gør, og hvorfor det betyder noget for datamappet.',
            owner_department_required: 'Ejer og Afdeling er påkrævet.',
            aria_systems_table: 'Systemoversigt',
            aria_edit: 'Rediger {name}',
            export_systems: 'Eksporter systemer',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Sporbarhedsmatrix',
            page_title: 'Sporbarhedsmatrix',
            brd_selector: 'Vælg BRD',
            requirement: 'Krav',
            linked_artifact: 'Tilknyttet artefakt',
            verification_status: 'Verifikationsstatus',
            verified_by: 'Verificeret af',
            create_entry: 'Opret post',
            edit_entry: 'Rediger post',
            no_entries_found: 'Ingen poster fundet',
            entry_created: 'Post oprettet',
            entry_updated: 'Post opdateret',
            select_brd: 'Vælg en BRD...',
            select_status: 'Vælg status...',
            aria_matrix_table: 'Sporbarhedsmatrix',
            aria_edit: 'Rediger post for {name}',
            export_matrix: 'Eksporter matrix',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'Krav-ID',
        },
        gap_register: {
            title: 'Gap-register',
            page_title: 'Gap-register',
            gap_title: 'Gap-titel',
            gap_type: 'Gap-type',
            severity: 'Alvorlighed',
            status: 'Status',
            description: 'Beskrivelse',
            remediation_plan: 'Afhjælpningsplan',
            assigned_to: 'Tildelt til',
            due_date: 'Forfaldsdato',
            create_gap: 'Opret gap',
            edit_gap: 'Rediger gap',
            no_gaps_found: 'Ingen gaps fundet',
            gap_created: 'Gap oprettet',
            gap_updated: 'Gap opdateret',
            select_type: 'Vælg type...',
            select_severity: 'Vælg alvorlighed...',
            select_status: 'Vælg status...',
            aria_gaps_table: 'Gap-register',
            aria_edit: 'Rediger {name}',
            export_gaps: 'Eksporter gaps',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'Hvert system i denne oversigt er tilpasset Data Law-virksomhedsstyringsmodellen. Systemtyper svarer til lovgivningsmæssige kategorier, og hostingklassifikationen bestemmer hvilke jurisdiktionsspecifikke kontroller der gælder.',
            automation_planning_workflow_info: 'Automatiseringsprogrammer er tilpasset complianceworkflow-forpligtelser defineret af gældende lovgivningsmæssige rammeværker.',
            traceability_regulatory_source_info: 'Hver sporbarhedspost forbinder et BRD-krav med dets lovgivningsmæssige kilde. Verifikationsstatus sporer om implementeringen opfylder den lovgivningsmæssige forpligtelse.',
            security_reviews_control_mapping_info: 'Sikkerhedsgennemgange evaluerer rammeværksspecifikke kontroller gældende for hver connector.',
            gap_register_framework_info: 'Gap-registret registrerer compliancegab per lovgivningsmæssigt rammeværk. Brug rammeværksfilteret til at se gab for et specifikt rammeværk.',
            poc_scorecards_criteria_info: 'POC-scorecards evaluerer connectors mod lovgivningsmæssige kriterier specifikke for det gældende rammeværk.',
            evidence_trail_info: 'Alle connector-modulhandlinger genererer revisionshændelser for integritet af tværmodulært evidensspor.',
        },
        system_inventory_extra: {
            framework_linkage: 'Rammeværkstilknytning',
            framework_linkage_placeholder: 'f.eks. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Rammeværksomfang',
            framework_scope_placeholder: 'f.eks. GDPR, PCI-DSS',
            procedure_linkage: 'Procedurekobling',
            procedure_linkage_placeholder: 'f.eks. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Lovgivningsmæssig kilde',
            regulatory_source_placeholder: 'f.eks. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Kontrolmapping',
            control_mappings_section: 'Kontrol kortlægninger',
            no_control_mappings: 'Ingen kontrolkortlægninger endnu. Klik på "Create Control Mapping" for at tilføje en.',
            automation_type_col: 'Automatisering',
        },
        gap_register_extra: {
            framework_filter: 'Rammeværk',
            framework_id: 'Rammeværks-ID',
            framework_id_placeholder: 'f.eks. GDPR, ISO 27001',
            all_frameworks: 'Alle rammeværker',
        },
        poc_scorecards_extra: {
            criteria_count: 'Kriterier',
        },
        export_labels: {
            export_brds: 'Eksporter BRD',
            export_decision_records: 'Eksporter beslutningsregistre',
            export_security_reviews: 'Eksporter sikkerhedsgennemgange',
            export_poc_scorecards: 'Eksporter POC-scorekort',
            export_instances: 'Eksporter forekomster',
            export_definitions: 'Eksport definitioner',
            export_credentials: 'Eksporter legitimationsoplysninger',
            export_events: 'Eksporter begivenheder',
            exporting: 'Eksporterer...',
            export_success: 'Eksport gennemført',
            export_error: 'Eksport mislykkedes',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Uddrag forpligtelser',
            extracting: 'Uddrager...',
            results_heading: 'Udtrukne forpligtelser ({count})',
            tokens_label: '≈ {count} tokens',
            no_obligations: 'Ingen forpligtelsesmønstre opdaget i denne kontrakttekst.',
            col_class: 'klasse',
            col_summary: 'Resumé (redigerbar)',
            col_excerpt: 'Klausuluddrag',
            col_due_date: 'Forfaldsdato (kan redigeres)',
            col_priority: 'Prioritet',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Opsigelsesfrist',
            class_audit: 'Revisionsret',
            class_insurance: 'Forsikring',
            class_sla: 'Svar SLA',
            class_breach: 'Underretning om brud',
            class_renewal: 'Fornyelsesvindue',
            citations_label: 'Citater',
            accept_hint: 'Accept vil oprette én opgave pr. forpligtelse (loft 8) med prioritet + forfaldsdato vist.',
            accept_label: 'Accepter forpligtelser',
            reject_label: 'Afvis rapport',
            resolve_summary: 'Opløsning: {status} · {tasks} opgave(r) oprettet',
            error_generic: 'Kunne ikke udtrække forpligtelser. Prøv igen, eller tjek kontraktteksten.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Udkast til begrundelse',
            drafting: 'Udarbejdelse af begrundelse...',
            results_heading: 'AI-udkastet begrundelse ({risk_level})',
            tokens_label: '≈ {count} tokens',
            narrative_label: 'Begrundelsesfortælling',
            controls_heading: 'Kompenserende kontroller',
            no_controls: 'Ingen kompenserende kontroller foreslået.',
            citations_label: 'Citater',
            accept_label: 'Accepter ræsonnement',
            reject_label: 'Afvis udkast',
            accept_hint: 'Accept registrerer begrundelsen for det vedvarende udkast til IAT-afstamning. Fortællende + kontrolelementer opdaterer ikke acceptposten automatisk - operatørindsættelse er påkrævet.',
            resolve_summary: 'Opløsning: {status}',
            error_generic: 'Kunne ikke udarbejde begrundelse. Prøv igen, eller kontroller status for risikoaccept.',
        },
        redline_assistant: {
            run_label: 'Redline med bibliotek',
            running: 'Kører redline...',
            results_heading: 'Redline-forslag ({count})',
            tokens_label: '≈ {count} tokens',
            no_suggestions: 'Der er ikke fundet huller i standardklausulen.',
            jurisdiction_label: 'Jurisdiktion',
            col_kind: 'Venlig',
            col_severity: 'Sværhedsgrad',
            col_title: 'Forslag',
            col_excerpt: 'Matchende uddrag',
            col_apply: 'Ansøg',
            kind_missing: 'Mangler standard',
            kind_weaker: 'Svagere end standard',
            kind_jurisdiction: 'Jurisdiktion overlejring',
            proposed_label: 'Foreslået tekst',
            citations_label: 'Citater',
            accept_label: 'Accepter valgt',
            reject_label: 'Afvis rapport',
            partial_label: 'Anvend valg',
            accept_hint: 'Anvend valg registrerer de valgte forslag på udkastet til IAT-afstamning. Ingen automatisk redigering anvendes på kontraktteksten - operatøren kører den røde linje.',
            resolve_summary: 'Opløsning: {status} · {count} forslag anvendt',
            error_generic: 'Kunne ikke producere en rød linje. Prøv igen, eller kontroller kontraktstatus.',
            clause_library_link: 'Administrer klausulbibliotek',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Klausul Bibliotek',
            add_label: 'Tilføj klausul',
            edit_label: 'Rediger',
            delete_label: 'Slet',
            slug_label: 'Snegl',
            title_label: 'Titel',
            body_label: 'Krop',
            jurisdiction_label: 'Jurisdiktion',
            tags_label: 'Tags (kommasepareret)',
            must_contain_label: 'Skal indeholde markører (kommasepareret)',
            severity_label: 'Sværhedsgrad, når den mangler',
            no_clauses: 'Ingen klausuler endnu. Tilføj en til at starte redline-assistenten.',
            save_label: 'Gem',
            cancel_label: 'Annuller',
            load_error: 'Klausulbiblioteket kunne ikke indlæses.',
            save_error: 'Klausulen kunne ikke gemmes.',
            delete_error: 'Klausulen kunne ikke slettes.',
            delete_confirm: 'Vil du slette klausulen "{slug}"? Dette kan ikke fortrydes.',
        },
        subprocessor_impact: {
            assess_label: 'Vurder påvirkning',
            assessing: 'Vurderer effekt...',
            results_heading: 'Indvirkning på underprocessor ({severity})',
            tokens_label: '≈ {count} tokens',
            risk_delta_label: 'Risiko delta',
            notification_heading: 'Kundemeddelelse',
            subject_label: 'Emne',
            deadline_label: 'Anmeldelsesfrist',
            body_label: 'Notifikationsorgan',
            required_fields_label: 'Underretningsfelter (art.28(2))',
            mitigation_heading: 'Afhjælpningstrin',
            citations_label: 'Citater',
            accept_label: 'Accepter vurdering',
            reject_label: 'Afvise vurdering',
            accept_hint: 'Accept vil oprette en kundemeddelelsesopgave med SLA deadline som forfaldsdato.',
            resolve_summary: 'Opløsning: {status}{task}',
            error_generic: 'Kunne ikke vurdere denne underdatabehandlerændring. Prøv igen, eller tjek ændringsposten.',
        },
        onboarding_evidence: {
            collect_label: 'Kør AI bevisindsamling',
            collecting: 'Indsamler beviser...',
            results_heading: 'Bevisindsamling ({tier})',
            tokens_label: '≈ {count} tokens',
            findings_heading: 'Eksisterende beviser',
            missing_heading: 'Manglende artefakter',
            no_findings: 'Ingen eksisterende beviser fundet.',
            no_missing: 'Der mangler ingen påkrævede artefakter for dette risikoniveau.',
            next_step_label: 'Næste skridt',
            class_dpa: 'Databehandleraftale',
            class_soc2: 'SOC 2 rapport',
            class_iso27001: 'ISO 27001 certifikat',
            class_insurance: 'Cyberforsikring',
            class_incident_history: 'Tidligere hændelse',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Accepter rapporten',
            reject_label: 'Afvis rapport',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'Kunne ikke køre AI bevisindsamling. Prøv igen, eller tjek status for onboarding-sagen.',
        },
        no_permission_title: 'Utilstrækkelige tilladelser',
        no_permission_message: 'Du har ikke tilladelse til at få adgang til leverandørrisikostyring.',
        unknown_vendor: 'Ukendt leverandør',
        nav: {
            dashboard: 'Risikooversigt',
            scoring: 'Risikoscoring',
            questionnaires: 'Spørgeskemaer',
            contracts: 'Kontrakter',
            gates: 'Godkendelsesporte',
            reviews: 'Leverandørvurderinger',
            signals: 'Risikosignaler',
            reassessments: 'Genvurderinger',
            portfolio: 'Risikoportefølje',
            acceptances: 'Risikoaccept',
            review_routing: 'Gennemgangsruting',
            onboarding_cases: 'Onboarding-sager',
            sub_processor_register: 'Register over underdatabehandlere',
        },
        dashboard: {
            title: 'Leverandørrisikooversigt',
            page_title: 'Leverandørrisiko Dashboard',
            loading: 'Indlæser dashboarddata...',
            risk_tier_header: 'Risikoniveau',
            count_header: 'Tæl',
            percentage_header: 'Procentdel',
            sections_nav: 'Leverandørrisikoafsnit',
            urgent_expiry: 'Haster: udløber inden for 14 dage',
            no_previous_tier: 'Ingen tidligere niveau',
            view_as_chart: 'Se som diagram',
            view_as_table: 'Se som tabel',
            showing_chart: 'Viser diagramvisning',
            showing_table: 'Viser datatabelvisning',
            load_error: 'Kunne ikke indlæse leverandørrisiko-dashboard',
            risk_distribution: 'Risikofordelingsoversigt',
            recent_tier_changes: 'Seneste niveauændringer',
            no_tier_changes: 'Ingen seneste niveauændringer.',
            review_kpis: 'Gennemgangs-KPI-er',
            total_vendors_reviewed: 'Gennemgåede leverandører i alt',
            reviews_by_outcome: 'Gennemgange efter resultat',
            contract_coverage: 'Kontraktdækning',
            total_vendors: 'Leverandører i alt',
            with_active_dpa: 'Med aktiv DPA',
            without_dpa: 'Uden DPA',
            dpa_coverage: 'DPA-dækning',
            active_contracts_by_type: 'Aktive kontrakter efter type',
            expiring_contracts: 'Udløbende kontrakter (næste 90 dage)',
            no_expiring_contracts: 'Ingen kontrakter udløber inden for de næste 90 dage.',
            vendor: 'Leverandør',
            previous_tier: 'Forrige niveau',
            current_tier: 'Nuværende niveau',
            changed_at: 'Ændret den',
            expires_at: 'Udløber den',
            days_remaining: 'Resterende dage',
            days: 'dage',
            vendors_label: 'leverandører',
            no_data: 'Ingen data',
            critical: 'Kritisk',
            high: 'Høj',
            medium: 'Middel',
            low: 'Lav',
            expiring_acceptances_heading: 'Udløbende risikogodkendelser',
            expiring_30d_label: 'Udløber om 30 dage',
            expiring_60d_label: 'Udløber om 60 dage',
            expiring_90d_label: 'Udløber om 90 dage',
        },
        scoring: {
            title: 'Risikoscoring',
            page_title: 'Leverandørrisikoscoring',
            filter_scores: 'Filtrer risikoscore',
            filter_by_tier: 'Filtrer efter risikoniveau',
            scores_pagination: 'Sideinddeling af risikoscore',
            config_dimensions: 'Konfiguration af scoringsdimensioner',
            scores_loaded: '{count} risikoscore er indlæst',
            weight_must_equal_100: 'Totalvægt skal svare til 100 (aktuelt: {current})',
            out_of_10: 'ud af 10, vurderet til {tier}',
            dimension_name: 'Dimension {index} navn',
            dimension_weight: '{name} vægt (procent)',
            must_equal_100: 'skal svare til 100',
            config_title: 'Scoringskonfiguration',
            scores_title: 'Leverandør Risiko Scorer',
            new_score_title: 'Score en Leverandør',
            new: 'Ny score',
            vendor_id: 'Leverandør-ID',
            risk_tier: 'Risikoniveau',
            score: 'Score',
            scored_at: 'Scoret den',
            no_scores: 'Ingen risiko scorer fundet.',
            all_tiers: 'Alle niveauer',
            load_config_error: 'Kunne ikke indlæse risikoscoring-konfiguration',
            update_config: 'Opdater scoringskonfiguration',
            update_config_error: 'Kunne ikke opdatere risikoscoring-konfiguration',
            load_scores_error: 'Kunne ikke indlæse leverandørrisikoscorer',
            calculate_error: 'Kunne ikke beregne leverandørrisikoscore',
            weight: 'vægt',
            score_range: 'Score (1-5)',
            method: 'Metode',
        },
        questionnaires: {
            title: 'Spørgeskemaer',
            page_title: 'Spørgeskemaer til leverandører',
            filter_questionnaires: 'Spørgeskemafiltre',
            questionnaire_views: 'Spørgeskemavisninger',
            templates_list: 'Liste over spørgeskemaskabeloner',
            instances_list: 'Liste over forekomster af spørgeskema',
            filter_by_status: 'Filtrer efter spørgeskemastatus',
            templates_per_page: 'Skabeloner pr. side',
            instances_per_page: 'Forekomster pr. side',
            select_template: 'Vælg spørgeskemaskabelon',
            template_category: 'Skabelonkategori',
            templates_tab: 'Skabeloner',
            instances_tab: 'Instanser',
            new: 'Nyt spørgeskema',
            new_template: 'Ny skabelon',
            new_instance: 'Ny instans',
            vendor_id: 'Leverandør-ID',
            template_id: 'Skabelon-ID',
            assigned_to: 'Tildelt til',
            due_date: 'Forfaldsdato',
            responses: 'Svar',
            risk_score: 'Færdiggørelse',
            no_questionnaires: 'Ingen spørgeskemaer fundet.',
            load_templates_error: 'Kunne ikke indlæse spørgeskemaskabeloner',
            load_instances_error: 'Kunne ikke indlæse spørgeskemaer',
            create_template_error: 'Kunne ikke oprette spørgeskemaskabelon',
            initiate_error: 'Kunne ikke starte spørgeskema',
            submit_error: 'Kunne ikke indsende spørgeskema',
            review_error: 'Kunne ikke gennemgå spørgeskema',
            resubmit: 'Genindsend',
            approve_error: 'Kunne ikke godkende spørgeskema',
            reject_error: 'Kunne ikke afvise spørgeskema',
            resubmit_error: 'Kunne ikke genindsende spørgeskema',
            templates_loaded: '{count} spørgeskemaskabeloner indlæst',
            instances_loaded: '{count} spørgeskemaforekomster indlæst',
            fill_responses_cta: 'Udfyld svar',
            fill_responses_title: 'Udfyld spørgeskemasvar',
            template_missing: 'Skabelon ikke fundet.',
            responses_saved: 'Svar gemt.',
            load_template_error: 'Kunne ikke indlæse skabelon.',
            version_history: 'Versionshistorik',
            download_previous_version: 'Download tidligere version',
            trigger: 'Udløser',
            template_label: 'Skabelon',
            reviewer: 'Anmelder',
            reviewer_required: 'Der skal tildeles en korrekturlæser til {trigger} spørgeskemaer (adskillelse af opgaver).',
            select_option: 'Vælg en mulighed',
            multi_choice_hint: 'Vælg alle relevante',
            category_initial_onboarding: 'Indledende onboarding',
            category_annual_review: 'Årlig gennemgang',
            category_incident_triggered: 'Hændelse udløst',
            category_contract_renewal: 'Kontraktfornyelse',
            template_editor: {
                page_title: 'Rediger spørgeskemaskabelon',
                preview_page_title: 'Forhåndsvisning af spørgeskemaskabelon',
                edit_structure: 'Rediger struktur',
                preview: 'Forhåndsvisning',
                actions: 'Handlinger',
                back_to_list: 'Tilbage til skabeloner',
                load_error: 'Skabelonen kunne ikke indlæses',
                save_error: 'Skabelonstrukturen kunne ikke gemmes',
                saved: 'Skabelonstrukturen er gemt.',
                sections_heading: 'Afsnit',
                questions_heading: 'Spørgsmål',
                add_section: 'Tilføj sektion',
                edit_section: 'Rediger sektion',
                delete_section: 'Slet afsnit',
                move_section_up: 'Flyt afsnittet op',
                move_section_down: 'Flyt sektionen ned',
                confirm_delete_section: 'Vil du slette afsnittet "{title}"? Alle dens spørgsmål vil blive fjernet.',
                section_title: 'Afsnits titel',
                section_description: 'Afsnitsbeskrivelse',
                section_id: 'Sektions-id',
                section_id_hint: 'Automatisk genereret, medmindre du udtrykkeligt angiver en.',
                no_sections: 'Ingen sektioner endnu. Tilføj et afsnit for at begynde.',
                no_questions: 'Ingen spørgsmål i dette afsnit endnu.',
                add_question: 'Tilføj spørgsmål',
                edit_question: 'Rediger spørgsmål',
                delete_question: 'Slet spørgsmål',
                move_question_up: 'Flyt spørgsmålet op',
                move_question_down: 'Flyt spørgsmålet ned',
                confirm_delete_question: 'Vil du slette spørgsmålet "{text}"?',
                question_text: 'Spørgsmålstekst',
                question_type: 'Spørgsmålstype',
                question_id: 'Spørgsmåls-id',
                evidence_required: 'Bevis påkrævet',
                risk_weight: 'Risikovægt',
                scoring_map: 'Scoring kort',
                scoring_map_json_hint: 'JSON objektkortlægning svarværdier til numeriske scores.',
                scoring_map_invalid_json: 'Scoringskortet skal være gyldigt JSON.',
                options_heading: 'Svarmuligheder',
                add_option: 'Tilføj mulighed',
                remove_option: 'Fjern mulighed',
                option_label: 'Etiket',
                option_value: 'Værdi',
                option_risk_weight: 'Risikovægt',
                min_options_error: 'Valgspørgsmål skal have mindst 2 muligheder.',
                option_label_required: 'Hver mulighed skal have en etiket.',
                option_value_required: 'Hver mulighed har brug for en værdi.',
                option_value_unique: 'Optionsværdier skal være unikke.',
                branching_rules_heading: 'Forgreningsregler',
                branching_rules_hint: 'Vis eller skjul dette spørgsmål baseret på svaret på et tidligere spørgsmål.',
                add_branching_rule: 'Tilføj forgreningsregel',
                remove_branching_rule: 'Fjern forgreningsreglen',
                branching_condition_question: 'Hvis spørgsmål',
                branching_condition_value: 'har værdi',
                branching_action: 'derefter',
                branching_action_show: 'vis dette spørgsmål',
                branching_action_hide: 'skjul dette spørgsmål',
                branching_select_condition_question: 'Vælg tidligere spørgsmål',
                branching_select_condition_value: 'Vælg værdi',
                save: 'Gem struktur',
                saving: 'Gemmer ...',
                cancel: 'Annuller',
                close: 'Luk',
                question_text_required: 'Spørgsmålstekst er påkrævet.',
                section_title_required: 'Sektionstitel er påkrævet.',
                type_single_choice: 'Enkelt valg',
                type_multi_choice: 'Multiple choice',
                type_free_text: 'Fri tekst',
                type_numeric: 'Numerisk',
                type_date: 'Dato',
                type_file_upload: 'Fil upload',
                type_yes_no: 'Ja/Nej',
                preview_heading: 'Forhåndsvisning af skabelon',
                preview_subtitle: 'Skrivebeskyttet forhåndsvisning af, hvordan spørgeskemaet ser ud for en leverandør.',
                preview_no_content: 'Denne skabelon har endnu ingen sektioner eller spørgsmål.',
                preview_branching_notice: 'Forgreningsregler evalueres under udfyldning; preview viser hvert spørgsmål.',
            },
            review_modal: {
                title: 'Gennemgå spørgeskemabesvarelser',
                open_cta: 'Gennemgå',
                loading: 'Indlæser spørgeskema...',
                template_missing: 'Skabelonen blev ikke fundet.',
                load_error: 'Kunne ikke indlæse spørgeskemaet til gennemgang.',
                annotation_heading: 'Anmærkninger',
                annotation_placeholder: 'Tilføj en anmelderannotering til dette spørgsmål...',
                annotation_status_label: 'Annotationsstatus',
                annotation_status_acceptable: 'Acceptabelt',
                annotation_status_concern: 'Bekymring',
                annotation_status_requires_action: 'Kræver handling',
                review_comments_label: 'Overordnede anmeldelseskommentarer',
                review_comments_placeholder: 'Opsummer dine anmeldelser...',
                justification_label: 'Begrundelse for beslutning',
                justification_placeholder: 'Forklar begrundelsen for din beslutning...',
                reject_requires_comments: 'Afvisning af et spørgeskema kræver gennemgangskommentarer.',
                decision_heading: 'Beslutning',
                decision: {
                    approved: 'Godkend',
                    rejected: 'Afvis',
                    additional_info_requested: 'Anmod om yderligere oplysninger',
                },
                submit: 'Send anmeldelse',
                submitting: 'Sender anmeldelse...',
                cancel: 'Annuller',
                submit_success: 'Anmeldelse indsendt.',
                submit_error: 'Anmeldelsen kunne ikke indsendes.',
                separation_of_duties_error: 'Du kan ikke gennemgå et spørgeskema, du har fået tildelt at udfylde. Adskillelse af opgaver kræver en anden anmelder.',
                decision_required: 'Vælg venligst en revisionsbeslutning.',
                announce_opened: 'Gennemgangsmodal åbnet.',
                announce_submitted: 'Anmeldelse indsendt.',
            },
            instance_detail: {
                submitted_at: 'Indsendt',
                reviewed_at: 'Gennemgået',
                reviewer: 'Anmelder',
                review_decision: 'Beslutning',
                review_comments: 'Gennemgå kommentarer',
                decision_approved: 'Godkendt',
                decision_rejected: 'Afvist',
                decision_additional_info_requested: 'Mere info efterspurgt',
                decision_pending: 'Afventer gennemgang',
                expand_comments: 'Vis hele kommentarer',
                collapse_comments: 'Skjul hele kommentarer',
                no_reviewer: 'Ingen anmelder tildelt',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Kladde',
                in_progress: 'I Gang',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Godkendt',
                rejected: 'Afvist',
                expired: 'Udløbet',
            },
            export: {
                portfolio_label: 'Eksportportefølje',
                instance_label: 'Eksporter',
                portfolio_success: 'Porteføljeeksport klar.',
                instance_success: 'Spørgeskemaeksport klar.',
                portfolio_error: 'Kunne ikke eksportere spørgeskemaporteføljen.',
                instance_error: 'Kunne ikke eksportere spørgeskemaet.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Nogle spørgsmål er skjult af forgreningsregler.',
                cycle_warning: 'En cyklus blev detekteret i forgreningsregler. Berørte spørgsmål er som standard synlige.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Kontrakter',
            page_title: 'Leverandørkontrakter',
            new: 'Ny kontrakt',
            vendor_id: 'Leverandør-ID',
            filter_contracts: 'Filtrere kontrakter',
            filter_by_document_type: 'Filtrer efter dokumenttype',
            filter_by_status: 'Filtrer efter kontraktstatus',
            per_page: 'Kontrakter pr. side',
            expired_notice: 'Denne kontrakt er udløbet',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Dokumenttype',
            expiry_date: 'Udløbsdato',
            no_contracts: 'Ingen kontrakter fundet.',
            all_document_types: 'Alle dokumenttyper',
            load_error: 'Kunne ikke indlæse kontrakter',
            create_error: 'Kunne ikke oprette kontrakt',
            delete_error: 'Kunne ikke slette kontrakt',
            update_error: 'Kunne ikke opdatere status',
            confirm_delete: 'Slet kontrakt "{title}"?',
            renewal_date: 'Fornyelsesdato',
            effective_date: 'Ikrafttrædelsesdato',
            parties: 'Parter',
            summary: 'Resumé',
            n_contracts: '{count} kontrakter',
            n_contract: '{count} kontrakt',
            contracts_loaded: '{count} kontrakter indlæst',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Sikkerhedstillæg',
            doc_type_scc_annex: 'SCC-bilag',
            doc_type_sub_processor_list: 'Underdatabehandlerliste',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Andet',
            status_draft: 'Kladde',
            status_under_negotiation: 'Under forhandling',
            status_pending_signature: 'Afventer underskrift',
            status_active: 'Aktiv',
            status_expired: 'Udløbet',
            status_terminated: 'Opsagt',
            status_superseded: 'Erstattet',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Godkendelsesporte',
            page_title: 'Portevalueringer',
            new: 'Ny Port Evaluering',
            vendor_id: 'Leverandør-ID',
            gate_name: 'Portnavn',
            outcome: 'Resultat',
            evaluator: 'Evaluator',
            no_gates: 'Ingen port evalueringer fundet.',
            load_error: 'Kunne ikke indlæse portevalueringer',
            evaluate_error: 'Kunne ikke evaluere leverandørport',
            detail_error: 'Kunne ikke hente evalueringsdetalje',
            override_error: 'Kunne ikke tilsidesætte portevaluering',
            criteria_results: 'Kriterieresultater',
            evaluate_form: 'Evaluer leverandørporten',
            per_page: 'Evalueringer pr. side',
            passed: 'Bestået',
            failed: 'Mislykket',
            override: 'Tilsidesæt',
            override_justification: 'Tilsidesættelsesbegrundelse',
            override_placeholder: 'Indtast begrundelse for at tilsidesætte denne portevaluering',
            approval_id: 'Godkendelses-ID',
            overall_passed: 'Bestået',
            overall_failed: 'Ikke bestået',
            overall_overridden: 'Tilsidesat',
            evaluations_loaded: '{count} portvurderinger indlæst',
            create_approval_cta: 'Opret godkendelse',
        },
        reviews: {
            title: 'Leverandørvurderinger',
            page_title: 'Leverandøranmeldelser',
            new: 'Ny gennemgang',
            vendor_id: 'Leverandør-ID',
            filter_by_outcome: 'Filtrer efter anmeldelsesresultat',
            per_page: 'Anmeldelser pr. side',
            kpi_summary: 'Gennemgå KPI oversigt',
            review_outcome_label: 'Gennemgå resultatet',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Rediger anmeldelsesresultat',
            reviews_loaded: '{count} leverandøranmeldelser indlæst',
            reviewer: 'Gennemgår',
            review_type: 'Gennemgangstype',
            outcome: 'Resultat',
            findings: 'Fund',
            no_reviews: 'Ingen leverandør gennemgange fundet.',
            load_kpis_error: 'Kunne ikke hente leverandørgennemgangs-KPI-er',
            load_error: 'Kunne ikke hente leverandørgennemgange',
            update_error: 'Kunne ikke opdatere leverandøranmeldelse',
            create_error: 'Kunne ikke oprette leverandørgennemgang',
            total_reviewed: 'Gennemgåede leverandører i alt',
            total_reviews: 'Gennemgange i alt',
            satisfactory: 'Tilfredsstillende',
            concerns_identified: 'Bekymringer identificeret',
            remediation_required: 'Afhjælpning påkrævet',
            escalated: 'Eskaleret',
            program_id: 'Program-ID',
            period_id: 'Periode-ID',
            task_id: 'Opgave-ID',
            recommendations: 'Anbefalinger',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Risikosignaler',
            page_title: 'Risikosignaler',
            new_signal: 'Nyt risikosignal',
            signal_type: 'Signaltype',
            severity: 'Alvorlighed',
            description: 'Beskrivelse',
            source: 'Kilde',
            vendor_id: 'Leverandør-id',
            certification_expiry: 'Certificeringsudløb',
            breach_notification: 'Brudmeddelelse',
            financial_instability: 'Finansiel ustabilitet',
            compliance_change: 'Overholdelsesændring',
            custom: 'Brugerdefineret',
            active: 'Aktiv',
            acknowledged: 'Anerkendt',
            resolved: 'Løst',
            no_signals: 'Ingen risikosignaler fundet',
            source_external_feed: 'Eksternt feed',
            source_manual: 'Manuel',
            source_integration: 'Integration',
            source_regulatory: 'Regulatorisk',
            source_media: 'Medier',
            trigger_reassessment_prompt: 'Dette signal er højt prioriteret. Vil du starte en leverandørgenevaluering?',
            reassessment_triggered: 'Genevaluering startet.',
            reassessment_trigger_error: 'Kunne ikke starte genevaluering.',
        },
        reassessments: {
            title: 'Genvurderingsopgaver',
            page_title: 'Revurderingsopgaver',
            trigger: 'Udløs genvurdering',
            trigger_success: 'Genvurdering udløst med succes',
            reason: 'Årsag',
            deadline: 'Deadline',
            assigned_to: 'Tildelt til',
            vendor_id: 'Leverandør-id',
            outcome: 'Resultat',
            new_risk_level: 'Nyt risikoniveau',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Afslut genvurdering',
            complete_success: 'Genvurdering afsluttet med succes',
            pending: 'Afventer',
            in_progress: 'I gang',
            completed: 'Afsluttet',
            cancelled: 'Annulleret',
            no_tasks: 'Ingen genvurderingsopgaver fundet',
            risk_trend: 'Risikotendens',
            notes: 'Noter',
        },
        portfolio: {
            title: 'Risikoportefølje',
            page_title: 'Risikoportefølje',
            dashboard: 'Porteføljeoversigt',
            vendor_id: 'Leverandør-id',
            total_vendors: 'Samlet antal leverandører',
            average_score: 'Gennemsnitlig risikoscore',
            high_risk_count: 'Højrisikoleverandører',
            trend_direction: 'Tendensretning',
            improved: 'Forbedret',
            stable: 'Stabil',
            degraded: 'Forværret',
            vendor_trend: 'Leverandørtendens',
            outstanding_questionnaires: 'Udestående spørgeskemaer',
            overdue_assessments: 'Forfaldne vurderinger',
            no_outstanding: 'Ingen udestående spørgeskemaer',
            no_overdue: 'Ingen forfaldne vurderinger',
            due_date: 'Forfaldsdato',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Risikoaccept-register',
            page_title: 'Risikoacceptregister',
            new_acceptance: 'Ny risikoaccept',
            vendor_id: 'Leverandør-id',
            risk_level: 'Risikoniveau',
            conditions: 'Betingelser',
            justification: 'Begrundelse',
            expiry_date: 'Udløbsdato',
            create_success: 'Risikoaccept oprettet med succes',
            review_condition: 'Gennemgå betingelse',
            condition_met: 'Betingelse opfyldt',
            condition_not_met: 'Betingelse ikke opfyldt',
            review_success: 'Betingelse gennemgået med succes',
            export_register: 'Eksportér register',
            active: 'Aktiv',
            expired: 'Udløbet',
            revoked: 'Tilbagekaldt',
            no_acceptances: 'Ingen risikoaccept fundet',
            met_count: '{met}/{total} opfyldt',
        },
        review_routing: {
            title: 'Gennemgangsruting',
            page_title: 'Gennemgå routing',
            route_to_reviewers: 'Send til gennemgangere',
            route_success: 'Sendt til gennemgangere med succes',
            vendor_id: 'Leverandør-id',
            reviewer_id: 'Gennemganger-ID',
            reviewer_role: 'Gennemgangerrolle',
            questionnaire_id: 'Spørgeskema-ID',
            review_status: 'Gennemgangsstatus',
            reviewers_label: 'Anmeldere (user_id, rolle pr. linje)',
            pending: 'Afventer',
            in_review: 'Under gennemgang',
            completed: 'Afsluttet',
            no_routing: 'Ingen gennemgangsruting fundet',
            primary_reviewer: 'Primær gennemganger',
            secondary_reviewer: 'Sekundær gennemganger',
            approver: 'Godkender',
        },
        onboarding: {
            title: 'Leverandør-onboarding-sager',
            page_title: 'Leverandør-onboarding-sager',
            new_case: 'Ny onboarding-sag',
            case_detail: 'Onboarding-sag',
            open_mps_dossier_filing: 'Åbn MPS-indsendelse',
            dossier_filing_description: 'Opret en ny MPS-indsendelse, forudfyldt fra denne onboarding-sag. Indsendelsen knyttes tilbage til sagen.',
            dossier_filing_dpia_id: 'DPIA-ID',
            dossier_filing_dpia_id_placeholder: 'fx dpia_01TEST000000000000000001',
            dossier_filing_title: 'Indsendelsestitel',
            dossier_filing_form_type: 'Formulartype',
            dossier_filing_submit: 'Opret indsendelse',
            dossier_filing_validation_error: 'DPIA-ID og indsendelsestitel er påkrævet.',
            dossier_filing_created: 'MPS-indsendelse oprettet',
            dossier_filing_error: 'Kunne ikke oprette MPS-indsendelse.',
            linked_questionnaire_due_date: 'Forfaldsdato',
            linked_questionnaire_assignee: 'Tildelt',
            linked_questionnaire_submitted_at: 'Indsendt',
            score_last_scored: 'Sidst scoret',
            score_dimensions: 'Dimensioner',
            linked_contract_document_type: 'Dokumenttype',
            linked_contract_effective_date: 'Ikrafttrædelsesdato',
            linked_contract_expiry_date: 'Udløbsdato',
            linked_sub_processor_countries: 'Behandlingslande',
            linked_sub_processor_last_assessment: 'Sidste vurdering',
            linked_sub_processor_next_assessment: 'Næste vurdering',
            case_id: 'Sag-ID',
            vendor: 'Leverandør',
            case_type: 'Sagstype',
            service_description: 'Servicebeskrivelse',
            data_categories: 'Datakategorier',
            systems_accessed: 'Tilgåede systemer',
            jurisdiction: 'Jurisdiktion',
            hosting_model: 'Hostingmodel',
            controller_role: 'Dataansvarlig/Databehandler-rolle',
            status_pipeline: 'Statuspipeline',
            tabs_label: 'Onboarding-sagsafsnit',
            per_page: 'Sager per side',
            filter_cases: 'Filtrer onboarding-sager',
            cases_count: 'sager',
            cases_loaded: 'onboarding-sager indlæst',
            no_cases: 'Ingen onboarding-sager fundet.',
            vendor_lookup_placeholder: 'Indtast leverandør-ID',
            data_categories_placeholder: 'f.eks., persondata, finansiel, sundhed',
            systems_placeholder: 'f.eks., CRM, ERP, HR-system',
            status_open: 'Åben',
            status_in_progress: 'I gang',
            status_awaiting_approval: 'Afventer godkendelse',
            status_approved: 'Godkendt',
            status_remediation_required: 'Afhjælpning påkrævet',
            status_closed: 'Lukket',
            type_new_vendor: 'Ny leverandør',
            type_service_change: 'Serviceændring',
            type_renewal: 'Fornyelse',
            type_reassessment: 'Revurdering',
            tab_overview: 'Oversigt',
            tab_due_diligence: 'Due diligence',
            tab_security_assessment: 'Sikkerhedsvurdering',
            tab_contractual_controls: 'Kontraktuelle kontroller',
            tab_sub_processors: 'Underdatabehandlere',
            tab_approval_gates: 'Godkendelsesporte',
            tab_evidence: 'Dokumentation',
            tab_placeholder: 'Indholdet i dette afsnit udfyldes under onboarding-processen.',
            completion_not_started: 'Ikke startet',
            completion_in_progress: 'I gang',
            completion_completed: 'Afsluttet',
            completion_requiring_attention: 'Kræver opmærksomhed',
            gate_name: 'Portnavn',
            approver: 'Godkender',
            decision: 'Beslutning',
            comments: 'Kommentarer',
            decide: 'Tag beslutning',
            decision_approved: 'Godkendt',
            decision_rejected: 'Afvist',
            decision_deferred: 'Udsat',
            decision_pending: 'Pending',
            no_approvals: 'Ingen godkendelsesporte konfigureret for denne sag.',
            approval_success: 'Godkendelsesbeslutning registreret',
            approval_error: 'Kunne ikke registrere godkendelsesbeslutning',
            transition_status: 'Skift status',
            confirm_transition: 'Skift sag fra "{from}" til "{to}"?',
            transition_success: 'Sagsstatus skiftet',
            transition_error: 'Kunne ikke skifte sagsstatus',
            create_success: 'Onboarding-sag oprettet',
            create_error: 'Kunne ikke oprette onboarding-sag',
            load_error: 'Kunne ikke indlæse onboarding-sager',
            detail_error: 'Kunne ikke indlæse sagsdetaljer',
            export: 'Eksportér',
            export_error: 'Kunne ikke eksportere onboarding-sag',
            evidence_tab_description: 'Upload og eksporter bevis for onboarding-sagen.',
            upload_evidence_label: 'Upload bevis',
            attached_evidence_heading: 'Vedhæftede beviser',
            no_evidence_attached: 'Ingen beviser er endnu vedhæftet.',
            export_bundle_button: 'Eksporter bevispakke',
            load_attachments_error: 'Kunne ikke indlæse beviser.',
            linked_questionnaires_heading: 'Tilknyttede spørgeskemaer',
            no_linked_questionnaires: 'Ingen tilknyttede spørgeskemaer.',
            linked_scores_heading: 'Tilknyttede vurderinger',
            no_linked_scores: 'Ingen tilknyttede vurderinger.',
            latest_score_label: 'Seneste vurdering',
            linked_contracts_heading: 'Tilknyttede kontrakter',
            no_linked_contracts: 'Ingen tilknyttede kontrakter.',
            linked_sub_processors_heading: 'Tilknyttede underbehandlere',
            no_linked_sub_processors: 'Ingen tilknyttede underbehandlere.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Marker som fuldført',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Register over underdatabehandlere',
            page_title: 'Register over underdatabehandlere',
            new_record: 'Ny underdatabehandler',
            edit_record: 'Rediger underdatabehandler',
            onboarding_case: 'Onboarding-sag',
            services: 'Leverede tjenester',
            data_categories: 'Datakategorier',
            processing_countries: 'Behandlingslande',
            dpa_chain_status: 'DPA-kædestatus',
            certifications: 'Certificeringer',
            last_assessment: 'Sidste vurdering',
            next_assessment: 'Næste vurdering',
            change_history: 'Ændringshistorik',
            history: 'Historik',
            changed_by: 'Ændret af',
            comma_separated: 'Kommaseparerede værdier',
            filter_records: 'Filtrer registreringer',
            filter_by_dpa: 'Filtrer efter DPA-kædestatus',
            all_dpa_statuses: 'Alle DPA-statusser',
            per_page: 'Poster per side',
            records_count: 'underdatabehandlere',
            records_loaded: 'poster indlæst',
            no_records: 'Ingen underdatabehandlere fundet.',
            no_history: 'Ingen ændringshistorik tilgængelig.',
            dpa_active: 'Aktiv',
            dpa_pending: 'Afventende',
            dpa_expired: 'Udløbet',
            dpa_not_required: 'Ikke påkrævet',
            create_success: 'Underdatabehandler oprettet',
            create_error: 'Kunne ikke oprette underdatabehandler',
            update_success: 'Underdatabehandler opdateret',
            update_error: 'Kunne ikke opdatere underdatabehandler',
            load_error: 'Kunne ikke indlæse registeret',
            history_error: 'Kunne ikke indlæse ændringshistorik',
            export_csv: 'Eksportér CSV',
            export_json: 'Eksportér JSON',
            export_error: 'Kunne ikke eksportere data',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Orkestreringsoversigt',
            runs: 'Orkestreringskørsler',
            tasks: 'Udførelsesopgaver',
            templates: 'Tjeklisteskabeloner',
            vendor_requests: 'Leverandøranmodninger',
            proof_chain: 'Beviskæde',
            dsr_integration: 'DSR-integration',
            vendor_verification: 'Leverandørverifikation',
            completeness: 'Fuldstændighedsoversigt',
        },
        dashboard: {
            title: 'Orkestreringsoversigt',
            loading: 'Indlæser dashboarddata...',
            active_runs: 'Aktive kørsler',
            completion_rate: 'Færdiggørelsesrate',
            avg_completion_time: 'Gns. færdiggørelsestid',
            overdue_tasks: 'Forsinkede opgaver',
            vendor_compliance: 'Leverandøroverholdelse',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Seneste kørsler',
            no_recent_runs: 'Ingen recent orchestration runs.',
            run_id: 'Kørsels-ID',
            ticket_id: 'Sags-ID',
            progress: 'Fremskridt',
            systems: 'Systemer',
            vendors: 'Leverandører',
            load_error: 'Kunne ikke indlæse orkestreringsdashboard. Prøv venligst igen.',
            aria_table: 'Seneste orkestreringsforløb',
        },
        runs: {
            title: 'Orkestreringskørsler',
            new: 'Ny kørsel',
            run_id: 'Kørsels-ID',
            ticket_id: 'Sags-ID',
            progress: 'Fremskridt',
            systems: 'Systemer',
            vendors: 'Leverandører',
            data_subject_ref: 'Data Emne Ref',
            data_categories: 'Datakategorier',
            enable_proof_chain: 'Aktiver Bevis Kæde',
            start: 'Start',
            started: 'Løb startede',
            mark_ready: 'Mark klar',
            marked_ready: 'Kør markeret som klar',
            complete: 'Komplet',
            complete_exceptions: 'Komplet m/ Undtagelser',
            pause: 'Pause',
            resume: 'Genoptag',
            cancel: 'Annuller',
            no_runs: 'Ingen orchestration runs fundet.',
            load_error: 'Kunne ikke indlæse orkestreringskørsler. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette orkestreringskørsel',
            aria_filter_status: 'Filtrer efter kørestatus',
            aria_table: 'Orkestrering kører',
            placeholder_ticket_id: 'f.eks. TKT-000123',
            placeholder_data_subject_ref: 'f.eks. DS-REF-001',
            placeholder_data_categories: 'f.eks. personal_data, økonomisk, sundhed',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Udførelsesopgaver',
            task_id: 'Opgave-ID',
            run_id: 'Kørsels-ID',
            system_name: 'Systemnavn',
            assigned_to: 'Tildelt til',
            sla_deadline: 'SLA-deadline',
            progress: 'Fremskridt',
            enter_run_id: 'Indtast Kørsel ID to filtrer...',
            select_run: 'Vælg en kørsel for at se opgaver',
            unassigned: 'Ikke tildelt',
            overdue: 'Forsinket',
            assign_task: 'Tildel opgave',
            assignee_id: 'Modtager-ID',
            update_status: 'Opdater status',
            new_status: 'Ny status',
            no_transitions: 'Denne opgave er i en endelig tilstand og kan ikke ændres.',
            select_status_placeholder: 'Vælg en ny status',
            skip_justification: 'Begrundelse for spring over',
            failure_reason: 'Årsag til fejl',
            no_tasks: 'Ingen udførelse opgaver fundet.',
            new_task: 'Ny opgave',
            system_id: 'System-id',
            data_classification: 'Dataklassifikation',
            load_error: 'Kunne ikke indlæse execution tasks. Prøv igen.',
            assign_error: 'Kunne ikke assign task',
            status_error: 'Kunne ikke opdatere opgavestatus',
            no_tasks_for_run: 'Denne sletningskørsel har endnu ingen udførelsesopgaver.',
            aria_filter_run_id: 'Filtrer efter kørsels-id',
            aria_filter_status: 'Filtrer efter opgavestatus',
            aria_table: 'Udførelsesopgaver',
            view_steps: 'Vis trin',
            steps_modal_title: 'Udførelsestrin',
            step_title: 'Trin',
            step_status: 'Trinstatus',
            step_notes: 'Noter',
            step_evidence: 'Bevisfiler',
            step_evidence_required: 'Bevis er påkrævet for dette trin',
            step_evidence_missing: 'Ingen beviser uploadet endnu',
            step_save: 'Gem trin',
            step_saved: 'Trin opdateret',
            step_save_error: 'Kunne ikke opdatere trinnet',
            create_success: 'Udførelsesopgave oprettet',
            create_error: 'Kunne ikke oprette udførelsesopgave',
            step_upload_label: 'Vedhæft bevis',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Tjeklisteskabeloner',
            new: 'Ny skabelon',
            template_id: 'Skabelon-ID',
            system_type: 'Systemtype',
            est_duration: 'Ansl. varighed',
            steps: 'Trin',
            action_type: 'Handlingstype',
            instructions: 'Instruktioner',
            mandatory: 'Obligatorisk',
            add_step: 'Tilføj trin',
            step_n: 'Trin',
            no_templates: 'Ingen tjekliste skabeloner fundet.',
            all_system_types: 'Alle systemtyper',
            load_error: 'Kunne ikke indlæse tjeklisteskabeloner. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette tjeklisteskabelon',
            aria_filter_system_type: 'Filtrer efter systemtype',
            aria_filter_status: 'Filtrer efter skabelonstatus',
            aria_table: 'Tjekliste skabeloner',
            placeholder_name: 'f.eks. Tjekliste til sletning af databaser',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Skabelon opdateret',
            create_success_msg: 'Skabelon oprettet',
            update_error: 'Kunne ikke opdatere skabelon',
            activate_success: 'Skabelon aktiveret',
            activate_error: 'Kunne ikke aktivere skabelon',
            archive_success: 'Skabelon arkiveret',
            archive_error: 'Kunne ikke arkivere skabelon',
            clone_success: 'Skabelon klonet',
            clone_error: 'Kunne ikke klone skabelon',
        },
        vendor_requests: {
            title: 'Leverandør Sletning Anmodninger',
            new: 'Ny anmodning',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Anmodnings-ID',
            vendor_name: 'Leverandørnavn',
            vendor_id: 'Leverandør-ID',
            data_subject_ref: 'Data Emne Ref',
            data_categories: 'Datakategorier',
            legal_basis: 'Retsgrundlag',
            sla_days: 'SLA-dage',
            requested_completion: 'Anmodet færdiggørelse',
            communications: 'Kommunikationer',
            total_requests: 'Anmodninger i alt',
            no_requests: 'Ingen leverandør sletning anmodninger fundet.',
            filter_vendor_id: 'Filter efter Leverandør ID...',
            change_status: 'Skift Status',
            new_status: 'Ny Status',
            actions: 'Handlinger',
            load_error: 'Kunne ikke indlæse anmodninger om leverandørsletning. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette anmodning om leverandørsletning',
            dashboard_error: 'Kunne ikke indlæse kontrolpanelet for sælgersletning. Prøv venligst igen.',
            aria_filter_status: 'Filtrer efter anmodningsstatus',
            aria_filter_vendor_id: 'Filtrer efter leverandør-id',
            aria_table: 'Sletningsanmodninger fra leverandører',
            placeholder_vendor_id: 'f.eks. VND-001',
            placeholder_vendor_name: 'f.eks. Acme Analytics',
            placeholder_data_subject_ref: 'f.eks. DS-REF-001',
            placeholder_data_categories: 'f.eks. personal_data, analyser',
            placeholder_legal_basis: 'f.eks. PDPL artikel 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Bevis Kæde Fremviser',
            enter_run_id: 'Indtast Kørsel ID...',
            load_chain: 'Indlæs kæde',
            verify_chain: 'Verificer kæde',
            verifying: 'Verificerer...',
            chain_valid: 'Kæden er gyldig',
            chain_invalid: 'Kæden er brudt',
            chain_length: 'Kædelængde',
            broken_at_index: 'Brudt ved indeks',
            no_proof_records: 'Ingen proof records found for this run.',
            proof_id: 'Bevis-ID',
            entity_type: 'Enhedstype',
            entity_id: 'Enheds-ID',
            event_hash: 'Hændelseshash',
            previous_hash: 'Forrige hash',
            attestation: 'Attestering',
            attestation_actor: 'Udført af',
            attestation_signed_at: 'Signeret den',
            attestation_signature: 'Signatur',
            attestation_evidence_refs: 'Bevisreferencer',
            run_field_label: 'Sletningskørsel',
            select_run: 'Vælg en sletningskørsel…',
            aria_select_run: 'Sletningskørsel til opslag i beviskæden',
            chain_broken_here: 'Kæde brudt her',
            load_error: 'Kunne ikke indlæse den sikre kæde. Prøv venligst igen.',
            verify_error: 'Kunne ikke bekræfte beviskæden. Prøv venligst igen.',
            aria_run_id: 'Kør ID for proof chain lookup',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Skift status',
            request_type: 'Anmodningstype',
            data_subject_hint: 'Identifikator for den registrerede, hvis data slettes (f.eks. e-mail eller kunde-ID). Svarer normalt til anmoderen på den valgte DSR-sag.',
            title: 'DSR-sletningsintegration',
            create_run: 'Opret DSR-sletningskørsel',
            dsr_case_id: 'DSR-sags-ID',
            data_subject_id: 'Registreredes ID',
            systems: 'Målsystemer',
            route_to_department: 'Send til afdeling',
            confirm_deletion: 'Bekræft sletning',
            no_runs: 'Ingen DSR-sletningskørsler fundet',
            status: 'Sletningsstatus',
            deadline: 'Deadline',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Leverandørsletningsverifikation',
            create_followup: 'Opret opfølgning',
            deadline: 'Bekræftelsesfrist',
            escalation_contact: 'Eskaleringskontakt',
            escalate: 'Eskaler',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Ingen leverandøropfølgninger fundet',
            vendor_request: 'Leverandøranmodning',
            vendor: 'Leverandør',
            unknown_vendor: 'Ukendt leverandør',
            overdue_badge: 'Forfalden',
            mark_confirmed: 'Markér som bekræftet',
            mark_confirmed_for: 'Markér sletning som bekræftet for {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Leverandørsletning bekræftet',
            confirmation_failed: 'Kunne ikke bekræfte leverandørsletning',
            select_request: 'Vælg en leverandøranmodning…',
            escalation_contact_placeholder: 'f.eks. dpo@vendor.example',
            create_success: 'Leverandøropfølgning oprettet',
            create_failed: 'Kunne ikke oprette leverandøropfølgning',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Leverandørsletningen blev eskaleret',
            escalation_failed: 'Det lykkedes ikke at eskalere sælgersletning',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Sletningsfuldstændighed',
            check_conflicts: 'Kontroller opbevaringskonflikter',
            no_conflicts: 'Ingen opbevaringskonflikter fundet',
            conflict_resolution: 'Konfliktløsning',
            extend_retention: 'Forlæng opbevaring',
            proceed_deletion: 'Fortsæt med sletning',
            escalate_conflict: 'Eskaler',
            resolve: 'Løs konflikt',
            data_subject_id: 'Datasubjekt-id',
            placeholder_data_subject_id: 'Indtast den registrerede ID',
            data_subject_hint: 'Identifikator for den person, hvis sletning du verificerer (f.eks. e-mail, kunde-ID eller DSR-reference). Vælg et kendt subjekt, eller skriv et.',
            guidance: 'Indtast eller vælg et datasubjekt-ID, vælg eventuelt systemer, og kør kontrollen for at se sletningens fuldstændighed og opbevaringskonflikter.',
            completion: 'Sletningsgrad',
            pending_items: 'Afventende elementer',
            system: 'System',
            policy: 'Politik',
            retention_end: 'Retention Slut',
            completed_items: 'Afsluttede Elementer',
            last_review_label: 'Sidste anmeldelse',
            days_ago: 'dage siden',
            review_overdue: 'Gennemgangscyklus overskredet. Gengennemgang påkrævet.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Afventer',
            planning: 'Planlægning',
            ready: 'Klar',
            in_progress: 'I Gang',
            paused: 'Pauseret',
            completed: 'Fuldført',
            completed_with_exceptions: 'Fuldført med Undtagelser',
            cancelled: 'Annulleret',
        },
        task_status_labels: {
            pending: 'Afventende',
            assigned: 'Tildelt',
            in_progress: 'I Gang',
            awaiting_verification: 'Afventer Verifikation',
            verified: 'Verificeret',
            failed: 'Fejlet',
            skipped: 'Sprunget Over',
        },
        system_type_labels: {
            database: 'Database',
            saas_application: 'SaaS-applikation',
            file_storage: 'Fillagring',
            email_system: 'E-mailsystem',
            analytics_platform: 'Analyseplatform',
            backup_system: 'Backupsystem',
            vendor_managed: 'Leverandørstyret',
            custom: 'Tilpasset',
        },
        template_status_labels: {
            draft: 'Kladde',
            active: 'Aktiv',
        },
        action_type_labels: {
            manual_check: 'Manuel Kontrol',
            api_call: 'API-kald',
            sql_query: 'SQL-forespørgsel',
            script_execution: 'Scriptudførelse',
            file_deletion: 'Filsletning',
            verification: 'Verifikation',
            notification: 'Notifikation',
            approval: 'Godkendelse',
        },
        request_status_labels: {
            draft: 'Kladde',
            sent: 'Sendt',
            acknowledged: 'Bekræftet',
            in_progress: 'I Gang',
            completed: 'Fuldført',
            overdue: 'Forsinket',
            escalated: 'Eskaleret',
            disputed: 'Bestridt',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Initiering',
            data_deletion: 'Datasletning',
            completion: 'Fuldførelse',
            verification: 'Verifikation',
            rollback: 'Tilbagerulning',
        },
        count_labels: {
            one_template: '1 skabelon',
            n_templates: '{n} skabeloner',
            one_run: '1 kørsel',
            n_runs: '{n} kørsler',
            one_task: '1 opgave',
            n_tasks: '{n} opgaver',
            one_request: '1 anmodning',
            n_requests: '{n} anmodninger',
        },
        aria: {
            completion_rate: 'Fuldførelsesrate: {pct}%',
            vendor_compliance: 'Leverandøroverholdelse: {pct}%',
            run_progress: 'Kørselsfremgang: {pct}%',
            run_progress_systems: 'Fremgang: {completed} af {total} systemer behandlet',
            chain_broken: 'Kæde brudt ved denne post',
            chain_valid: 'Kæde gyldig ved denne post',
            chain_unknown: 'Kædestatus ukendt',
            escalate_vendor: 'Eskaler {id}',
            resolve_conflict: 'Løs konflikt for {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'Sletningens fuldstændighed skal gennemgås mindst hver 365. dag. Registreringer, der overstiger denne tærskel, markeres til obligatorisk fornyet gennemgang for at opretholde lovoverholdelse.',
            runs_data_d1_lifecycle_info: 'Orkestreringskørselstilstande er knyttet til DATA-D1 livscyklus: planlægning = katalog, klar = klassificere, in_progress = anvende regler, gennemført = bevis genereret, completed_with_exceptions = gennemgang påkrævet. Hver overgang registreres i proof-kæden.',
            vendor_record_linkage_info: 'Hver anmodning om leverandørsletning er knyttet til dens leverandørpost via vendor_id. Bekræftelse af fuldførelse kræver kryptografisk bevis fra beviskæden, før anmodningen kan markeres som afsluttet.',
            templates_internal_classification_info: "Alle sletningstjeklisteskabeloner er klassificeret som interne organisatoriske dokumenter. De bærer ikke officielle regulatoriske formular-id'er og bør ikke indsendes som regulatoriske ansøgninger.",
            proof_chain_evidence_info: 'Hver proof record fanger det registrerede emne/kategori, tidsstempel, aktør, juridisk grundlag og en SHA-256 hash. Tilsammen producerer disse felter kryptografisk sletningsbevis, der opfylder lovpligtige revisionskrav.',
            retention_data_law_info: 'Opbevaringsplaner på dette dashboard er afledt af datalovklassifikationen, der er tildelt hver datakategori. Når en klassifikation ændres, opdateres tilknyttede opbevaringsregler automatisk for at sikre regulatorisk tilpasning.',
            dsr_erasure_route_info: 'DSR-slettekørsler er knyttet til den oprindelige Rights Request-sag. Den lovpligtige frist (typisk 30 dage i henhold til GDPR art. 17) spores automatisk. Forfaldne sager markeres for eskalering.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Opdagelsesoversigt',
            scans: 'Opdagelsesscanninger',
            staging_area: 'Mellemstation',
            dedup_rules: 'Deduplikeringsregler',
            classification_rules: 'Klassifikationsregler',
            suggestions: 'Forslagskø',
            analytics: 'Forslagsanalyse',
            import_templates: 'Importskabeloner',
            connectors: 'Connectorstyring',
            discovery_cycles: 'Opdagelsescyklusser',
            owner_review: 'Ejergennemgang',
            classification_lineage: 'Klassificeringshistorik',
        },
        dashboard: {
            title: 'Opdagelsesoversigt',
            loading: 'Indlæser dashboarddata...',
            total_scans: 'Scanninger i alt',
            active_scans: 'Aktive scanninger',
            completed_30d: 'Fuldførte (30d)',
            failed_30d: 'Mislykkede (30d)',
            systems_discovered: 'Opdagede systemer',
            pending_staged_records: 'Ventende staged poster',
            recent_scans: 'Seneste scanninger',
            no_recent_scans: 'Ingen seneste scanninger.',
            scan_id: 'Scannings-ID',
            discovered: 'Opdaget',
            view_all_scans: 'Vis alle scanninger',
            staging_area: 'Mellemlagringsområde',
            suggestion_queue: 'Forslagskø',
            records_reviewed: 'Gennemgåede poster',
            records_approved: 'Godkendte poster',
            exceptions_pending: 'Afventende undtagelser',
            view_as_chart: 'Vis som diagram',
            view_as_table: 'Vis som tabel',
        },
        scans: {
            title: 'Opdagelsesscanninger',
            new: 'Ny scanning',
            scan_id: 'Scannings-ID',
            scan_type: 'Scanningstype',
            progress: 'Fremskridt',
            discovered: 'Opdaget',
            no_scans: 'Ingen opdagelse scanninger fundet.',
            all_scan_types: 'Alle scanningstyper',
            source_config: 'Kilde Konfiguration (JSON)',
            invalid_json: 'Ugyldig JSON in kilde konfiguration',
            load_error: 'Kunne ikke indlæse opdagelsesscanninger. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette opdagelsesscanning',
            change_status: 'Skift status',
            new_status: 'Ny status',
            actions: 'Handlinger',
            type_endpoint: 'Slutpunkt',
            type_spreadsheet_import: 'Regneark-import',
            type_cmdb_sync: 'CMDB-synkronisering',
            type_cloud_sync: 'Sky-synkronisering',
            status_pending: 'Afventer',
            status_running: 'Kører',
            status_completed: 'Fuldført',
            status_failed: 'Mislykket',
            status_cancelled: 'Annulleret',
            source_config_placeholder: '{"host": "...", "port": 443}',
            adapter_not_implemented_toast: 'Denne scanningstype er endnu ikke tilgængelig. Der kan kun oprettes endpoint-scanninger.',
            adapter_not_implemented_warning: 'Endnu ikke tilgængelig. Scanninger af denne type kan ikke oprettes, før understøttelsen er frigivet.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Mellemstation',
            record_id: 'Post-ID',
            source_scan: 'Kildescanning',
            entity_type: 'Enhedstype',
            suggested_name: 'Foreslået navn',
            confidence: 'Tillid',
            no_records: 'Ingen staged records fundet.',
            promote: 'Forfrem',
            discard: 'Kassér',
            review: 'Gennemgang',
            merge: 'Flet',
            merge_title: 'Merge Record',
            merge_target_id: 'Flet Mål ID',
            reject_reason_placeholder: 'Årsag til afvisning...',
            promotion_status: 'Forfremmelsesstatus',
            dedup_status: 'Deduplicersstatus',
            all_source_types: 'Alle kildetyper',
            all_promotion_statuses: 'Alle forfremmelsesstatusser',
            all_dedup_statuses: 'Alle dedupliceringsstatusser',
            load_error: 'Kunne ikke indlæse staged poster. Prøv venligst igen.',
            promote_error: 'Kunne ikke forfremme post',
            reject_error: 'Kunne ikke afvise post',
            merge_error: 'Kunne ikke flette post',
            review_error: 'Kunne ikke markere post til gennemgang',
            bulk_promote_error: 'Kunne ikke masseforfremme records',
            bulk_reject_error: 'Kunne ikke masseafvise records',
            service_type_web_server: 'Webserver',
            service_type_database: 'Database',
            service_type_api_gateway: 'API-gateway',
            service_type_storage: 'Lagring',
            service_type_authentication: 'Godkendelse',
            service_type_analytics: 'Analyse',
            service_type_unknown: 'Ukendt',
            source_type_endpoint_scan: 'Slutpunktsscanning',
            source_type_spreadsheet: 'Regneark',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Sky-inventar',
            promotion_status_new: 'Ny',
            promotion_status_under_review: 'Under gennemgang',
            promotion_status_promoted: 'Forfremmet',
            promotion_status_rejected: 'Afvist',
            promotion_status_merged: 'Flettet',
            dedup_status_unmatched: 'Ingen match',
            dedup_status_potential_match: 'Potentielt match',
            dedup_status_exact_match: 'Eksakt match',
            dedup_status_resolved: 'Løst',
            confirm_bulk_reject: 'Er du sikker på, at du vil afvise de valgte poster?',
            scan_batch_id: 'Batch-ID',
            promote_to_registry_title: 'Fremme til dataregistret',
            promote_modal_instructions: 'Opretter et dataregisterelement med collection_method: observeret og herkomstforbindelse til denne opdagelsespost. Elementet starter i kladdestatus til gennemgang.',
            promote_label_category: 'Kategori',
            promote_label_name_override: 'Navn (valgfri tilsidesættelse)',
            placeholder_name_override: 'Som standard er registreringspostens navn/værtsnavn',
            promote_label_sensitivity: 'Foreslået følsomhed',
            inherit_from_category: 'Arv fra kategori',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Deduplikeringsregler',
            new: 'Ny regel',
            rule_name: 'Regelnavn',
            match_fields: 'Matchfelter',
            threshold: 'Tærskel',
            no_rules: 'Ingen deduplicering regler fundet.',
            seed_defaults: 'Initialiser standarder',
            match_type: 'Matchtype',
            similarity_threshold: 'Lighedstærskel',
            field_name: 'Feltnavn',
            load_error: 'Kunne ikke indlæse dedupliceringsregler. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette dedupliceringsregel',
            toggle_error: 'Kunne ikke skifte dedupliceringsregel',
            seed_error: 'Kunne ikke initialisere standarder',
            priority: 'Prioritet',
            enabled_status: 'Aktiveret',
            disabled_status: 'Deaktiveret',
            default_badge: 'Standard',
            enable: 'Aktiver',
            disable: 'Deaktiver',
            match_type_exact: 'Eksakt',
            match_type_fuzzy: 'Omtrentlig',
            match_type_regex: 'Regex',
            match_type_ip_range: 'IP-interval',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Klassifikationsregler',
            new: 'Ny regel',
            rule_name: 'Regelnavn',
            pattern: 'Mønster',
            classification: 'Klassificering',
            no_rules: 'Ingen classification rules fundet.',
            seed_defaults: 'Initialiser standarder',
            run_rules_now: 'Kør regler nu',
            run_rules_success: 'Genererede {count} nye klassificeringsforslag(er)',
            run_rules_no_results: 'Ingen nye forslag genereret – alle matchende regler har allerede afventende forslag',
            run_rules_error: 'Klassifikationsreglerne kunne ikke køres. Prøv venligst igen.',
            taxonomy_id: 'Taksonomi-ID',
            level_id: 'Niveau-ID',
            condition_logic: 'Betingelseslogik',
            weight: 'Vægt',
            conditions: 'Betingelser',
            condition_field: 'Felt',
            condition_operator: 'Operatør',
            condition_value: 'Værdi',
            effectiveness: 'Effektivitet',
            match_count: 'Matchninger',
            approval_count: 'Godkendelser',
            rejection_count: 'Afvisninger',
            filter_taxonomy: 'Filter efter Taksonomi ID...',
            load_error: 'Kunne ikke indlæse klassificeringsregler. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette klassificeringsregel',
            seed_error: 'Kunne ikke initialisere standarder',
            toggle_error: 'Kunne ikke skifte regel. Prøv venligst igen.',
            priority: 'Prioritet',
            enabled_status: 'Aktiveret',
            disabled_status: 'Deaktiveret',
            enable: 'Aktiver',
            disable: 'Deaktiver',
            logic_and: 'OG',
            logic_or: 'ELLER',
            operator_equals: 'Er lig med',
            operator_contains: 'Indeholder',
            operator_matches_regex: 'Matcher Regex',
            operator_in_list: 'På listen',
            operator_not_equals: 'Er ikke lig med',
            operator_not_contains: 'Indeholder ikke',
            enabled_label: 'Aktiveret',
            disabled_label: 'Deaktiveret',
            taxonomy_label: 'Taksonomi',
            operator_label: 'Operatør',
            value_label: 'Værdi',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Forslagskø',
            entity_type: 'Enhedstype',
            suggested_name: 'Foreslået navn',
            source: 'Kilde',
            confidence: 'Tillid',
            accept: 'Accepter',
            reject: 'Afvis',
            no_suggestions: 'Ingen forslag i kø.',
            modify_approve: 'Ændr & Godkend',
            delegate: 'Deleger',
            delegate_user_id: 'Deleger Bruger ID',
            new_level_id: 'Ny Niveau ID',
            reject_reason_placeholder: 'Årsag til afvisning...',
            current_level: 'Nuværende niveau',
            suggested_level: 'Foreslået niveau',
            filter_taxonomy: 'Filter efter Taksonomi ID...',
            all_confidence: 'Alle tillidsbånd',
            load_error: 'Kunne ikke indlæse forslag. Prøv venligst igen.',
            approve_error: 'Kunne ikke godkende forslag',
            reject_error: 'Kunne ikke afvise forslag',
            modify_error: 'Kunne ikke ændre og godkende forslag',
            delegate_error: 'Kunne ikke delegere forslag',
            bulk_approve_error: 'Kunne ikke massegodkende forslag',
            bulk_reject_error: 'Kunne ikke masseafvise forslag',
            suggestion_type_new_label: 'Ny etiket',
            suggestion_type_validation: 'Validering',
            suggestion_type_reclassification: 'Omklassificering',
            status_pending: 'Afventer',
            status_approved: 'Godkendt',
            status_rejected: 'Afvist',
            status_superseded: 'Erstattet',
            confidence_high: 'Høj',
            confidence_medium: 'Mellem',
            confidence_low: 'Lav',
            confirm_bulk_reject: 'Er du sikker på, at du vil afvise de valgte forslag?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Alle taksonomier',
        },
        analytics: {
            title: 'Forslagsanalyse',
            total_suggestions: 'Forslag i alt',
            accepted: 'Accepteret',
            rejected: 'Afvist',
            pending: 'Afventer',
            acceptance_rate: 'Acceptrate',
            by_entity_type: 'Efter enhedstype',
            by_source: 'Efter kilde',
            avg_decision_time: 'Gns. beslutningstid',
            rule_effectiveness: 'Regeleffektivitet',
            by_status: 'Efter status',
            by_confidence: 'Efter tillidsbånd',
            load_error: 'Kunne ikke indlæse forslagsanalyse. Prøv venligst igen.',
            confidence_high: 'Høj',
            confidence_medium: 'Mellem',
            confidence_low: 'Lav',
            by_confidence_band: 'Efter konfidensniveau',
            approval_rate: 'Godkendelsesrate',
            by_label_state: 'Efter etiketstatus',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Importskabeloner',
            new: 'Ny skabelon',
            template_name: 'Skabelonnavn',
            file_format: 'Filformat',
            column_mappings: 'Kolonnekortlægninger',
            no_templates: 'Ingen import skabeloner fundet.',
            source_column: 'Kildekolonne',
            target_field: 'Målfelt',
            transform: 'Transformér',
            source_column_label: 'Kildekolonne',
            target_field_label: 'Målfelt',
            transform_label: 'Transform',
            confirm_delete: 'Er du sikker på, at du vil slette denne importskabelon?',
            load_error: 'Kunne ikke indlæse importskabeloner. Prøv venligst igen.',
            save_error: 'Kunne ikke gemme importskabelon',
            delete_error: 'Kunne ikke slette importskabelon',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Connectorstyring',
            new: 'Ny konnektor',
            connector_name: 'Konnektornavn',
            connector_type: 'Type',
            last_sync: 'Seneste synkronisering',
            no_connectors: 'Ingen konnektorer fundet.',
            cmdb_syncs: 'CMDB-synkroniseringer',
            cloud_syncs: 'Sky-synkroniseringer',
            new_sync: 'Ny synkronisering',
            no_syncs: 'Ingen synkroniseringer fundet.',
            cmdb_error: 'Kunne ikke indlæse CMDB-scanninger. Prøv venligst igen.',
            cloud_error: 'Kunne ikke indlæse cloud-scanninger. Prøv venligst igen.',
            trigger_error: 'Kunne ikke udløse synkronisering',
            trigger_sync_error: 'Kunne ikke trigger {type}',
            cmdb_section_title: 'CMDB-synkroniseringer',
            cloud_section_title: 'Cloud-synkroniseringer',
            column_connector: 'Konnektor',
            action_start: 'Start',
            confirm_cancel_sync: 'Annuller denne synkronisering?',
            start_error: 'Kunne ikke starte synkronisering',
            cancel_error: 'Kunne ikke annullere synkronisering',
            type_endpoint: 'Endpoint-scan',
            type_spreadsheet_import: 'Regneark-import',
            type_cmdb_sync: 'CMDB-synkronisering',
            type_cloud_sync: 'Cloud-synkronisering',
        },
        discovery_cycles: {
            title: 'Opdagelsescyklusser',
            new_cycle: 'Ny opdagelsescyklus',
            cycle_name: 'Cyklusnavn',
            scope_type: 'Omfangstype',
            scope_ids: 'Omfangs-ID',
            cadence_days: 'Kadence (dage)',
            status: 'Status',
            no_cycles: 'Ingen opdagelsescyklusser fundet',
            compare: 'Sammenlign cyklusser',
            delta_report: 'Ændringsrapport',
            new_records: 'Nye poster',
            changed_records: 'Ændrede poster',
            removed_records: 'Fjernede poster',
            schedule: 'Planlæg scanning',
            scope_organization: 'Organisation',
            scope_department: 'afdeling',
            scope_system: 'System',
            scope_systems: 'Systemer',
            bulk_actions: 'Massehandlinger',
            selected: '{count} valgt',
            bulk_create: 'Masseopret',
            bulk_assign_scope: 'Massetildel omfang',
            bulk_advance: 'Masseforfremmelse',
            confirm_bulk_action: 'Bekræft massehandling',
            bulk_confirm_message: 'Er du sikker på, at du vil udføre denne handling?',
            cycles_affected: '{count} cyklusser vil blive påvirket',
            select_all: 'Vælg alle',
            select_cycle: 'Vælg cyklus {name}',
            from_template: 'fra skabelon',
            bulk_results: 'Masseresultater',
            failed_label: 'fejlet',
            template_id: 'Skabelon-ID',
            count: 'Antal',
            advance_to: 'Målstatus',
            bulk_success: 'Massehandling fuldført',
        },
        owner_review: {
            title: 'Ejergennemgangsopgaver',
            assign_owner: 'Tildel til ejer',
            owner_id: 'Ejer',
            decision: 'Beslutning',
            approve: 'Godkend',
            modify: 'Rediger',
            reject: 'Afvis',
            escalate: 'Eskalér',
            no_tasks: 'Ingen gennemgangsopgaver fundet',
            bulk_resolve: 'Masseløsning',
            exceptions: 'Undtagelsesposter',
            no_exceptions: 'Ingen undtagelsesposter fundet',
            record_id_label: 'Optag ID',
            selected: 'valgt',
            notes: 'Noter',
            select_all: 'Vælg Alle',
            select: 'Vælg',
            confirm_resolve: 'Bekræft',
        },
        classification_lineage: {
            title: 'Klassificeringshistorik',
            link_suggestion: 'Link til foreslået mærkat',
            suggestion_id: 'Forslag',
            proposed_label_id: 'Foreslået mærkat',
            no_lineage: 'Ingen historikdata fundet',
            full_lineage: 'Fuld historik',
            record_id_label: 'Optag ID',
            staged_record_label: 'Iscenesat rekord',
            enter_staged_record_id: 'Indtast trinvis post-id',
            scan_id_label: 'Scan ID',
            select_record: 'Vælg en post',
            select_suggestion: 'Vælg et forslag',
            select_label: 'Vælg en etiket',
        },
        common_labels: {
            n_rules: '{n} regler',
            one_rule: '1 regel',
            n_scans: '{n} scanninger',
            one_scan: '1 scanning',
            n_syncs: '{n} synkroniseres',
            one_sync: '1 synkronisering',
            n_records: '{n} poster',
            one_record: '1 rekord',
            n_templates: '{n} skabeloner',
            one_template: '1 skabelon',
            n_suggestions: '{n} forslag',
            one_suggestion: '1 forslag',
            n_selected: '{n} valgt',
            dashboard_loaded: 'Discovery-dashboard er indlæst',
            n_scans_loaded: '{n} scanninger indlæst',
            n_staged_records_loaded: '{n} iscenesatte poster indlæst',
            confirm_delete_import_template: 'Er du sikker på, at du vil slette denne importskabelon?',
            invalid_json_source_config: 'Ugyldig JSON i source_config',
            scope_organization: 'Organisation',
            scope_department: 'afdeling',
            scope_system: 'System',
            aria_filter_scans: 'Filterscanninger',
            aria_filter_staged_records: 'Filtrer iscenesatte poster',
            aria_filter_suggestions: 'Filtre',
            aria_filter_by_taxonomy_id: 'Filtrer efter taksonomi-id',
            aria_filter_by_enabled_status: 'Filtrer efter aktiveret status',
            aria_filter_by_source_type: 'Filtrer efter kildetype',
            aria_filter_by_promotion_status: 'Filtrer efter kampagnestatus',
            aria_filter_by_dedup_status: 'Filtrer efter dedup-status',
            aria_filter_by_scan_type: 'Filtrer efter scanningstype',
            aria_filter_by_status: 'Filtrer efter status',
            aria_filter_by_confidence: 'Filtrer efter konfidensbånd',
            aria_select_all_records: 'Vælg alle poster',
            aria_select_record: 'Vælg post {id}',
            aria_select_all_suggestions: 'Vælg alle forslag',
            aria_select_suggestion: 'Vælg forslag {id}',
            aria_promote_record: 'Fremme rekord {id}',
            aria_discard_record: 'Kasser post {id}',
            aria_merge_record: 'Flet post {id}',
            aria_review_record: 'Gennemgå post {id}',
            aria_accept_suggestion: 'Accepter forslag {id}',
            aria_reject_record: 'Afvis post',
            aria_merge_record_dialog: 'Flet post',
            aria_create_new_scan: 'Opret ny scanning',
            aria_sync_progress: 'Synkroniseringsforløb: {processed} af {total} elementer behandlet',
            aria_scan_progress: 'Scanningsforløb: {processed} af {total} elementer behandlet',
            aria_confidence: 'Tillid: {pct} %',
            aria_effectiveness: 'Effektivitet: {pct}',
            aria_promote_n_records: 'Fremme {n} valgte poster',
            aria_discard_n_records: 'Kassér {n} valgte poster',
            placeholder_pii_detection_rule: 'f.eks. PII-detektionsregel',
            placeholder_tax_id: 'f.eks. TAX-001',
            placeholder_lvl_id: 'f.eks. LVL-001',
            placeholder_service_type: 'f.eks. service_type',
            placeholder_database: 'fx database',
            placeholder_hostname_match: 'f.eks. Hostname Exact Match',
            placeholder_hostname: 'f.eks. værtsnavn',
            placeholder_hr_systems_csv: 'f.eks. HR Systems CSV import',
            placeholder_server_name: 'f.eks. servernavn',
            placeholder_hostname_field: 'f.eks. værtsnavn',
            placeholder_lowercase: 'f.eks. små bogstaver',
            placeholder_source_config: '{"host": "...", "port": 443}',
            placeholder_stg_id: 'f.eks. STG-000123',
            placeholder_lvl_002: 'f.eks. LVL-002',
            placeholder_usr_id: 'f.eks. USR-000123',
            decision_for: 'Beslutning om {id}',
        },
        exports: {
            title: 'Eksporter',
            inventory: 'Dataoversigt',
            classification_register: 'Klassifikationsregister',
            raci: 'RACI-matrix',
            exceptions: 'Undtagelsesrapport',
            evidence_pack: 'Bevissamling',
            delta_report: 'Ændringsrapport',
            executive_summary: 'Ledelsesresumé',
            export_button: 'Eksporter',
            downloading: 'Downloader...',
            download_complete: 'Download fuldført.',
            download_error: 'Kunne ikke downloade eksport. Prøv igen.',
        },
        flags: {
            title: 'Afledte markeringer',
            pending: 'Afventende markeringer',
            cross_border_review_needed: 'Grænseoverskridende gennemgang påkrævet',
            sharing_disclosure_needed: 'Delingsoffentliggørelse påkrævet',
            dpia_review_needed: 'DPIA-gennemgang påkrævet',
            acknowledge: 'Anerkend',
            resolve: 'Løs',
            no_flags: 'Ingen afledte markeringer.',
        },
        delta: {
            title: 'Ændringsoversigt',
            new_count: '{n} nye',
            removed_count: '{n} fjernede',
            changed_count: '{n} ændrede',
            re_review_triggered: 'Gengennemgang udløst',
            create_re_review: 'Opret gengennemgang',
            recent_alerts: 'Seneste ændringsadvarsler',
            no_alerts: 'Ingen seneste ændringsadvarsler.',
            threshold_exceeded: 'Ændringsgrænse overskredet (>10%)',
        },
        regulatory: {
            dashboard_governance_info: 'Dette dashboard viser styringsstatus på cyklusniveau: scannede systemer, iscenesatte registreringer, gennemgåede registreringer, godkendte registreringer og afventende undtagelser. Dette matcher succeskriterierne for ledelsen: "live dashboard over, hvem der har opdateret deres beholdning, klassificering og delingslogfiler" (P-VN-05).',
            export_artifacts_info: 'Discovery-eksporter inkluderer alle påkrævede artefakttyper pr. UC-VN-10 og DATA-EVD-01: godkendt dataopgørelse, klassifikationsregister, ejerskab/RACI-kortlægning, undtagelseskø, executive summary, bevispakke og deltarapport.',
            scan_inventory_pipeline_info: 'Hver fuldført scanning producerer poster, der er kompatible med vn_data_law_data_inventory_register-skabelonen (krav 88dd9877). Opdagede dataaktiver udfylder automatisk dataopgørelsesregisteret for at opfylde datalovens forpligtelse til at føre en digital dataopgørelse.',
            connector_linkage_info: 'Hver forbindelseskonfiguration skal omfatte: systemtype (f.eks. CMDB, cloud-platform), datalagerreference (database, fillager, SaaS) og forbindelse til forretningsprocesser (UC-VN-10 påkrævet understøttende registreringer). Disse felter sikrer, at opdagede aktiver kan spores til deres kildesystem og forretningskontekst.',
            owner_routing_info: 'Gennemgangsopgaver dirigeres til den korrekte afdeling eller applikationsejer baseret på system-/dataejerskab (UC-VN-10 trin 5). Routing bruger systemets ejerskabsregister, ikke ad-hoc tildeling.',
            owner_actions_audit_info: 'Ejere kan godkende, ændre, afvise eller eskalere hver klassificering (UC-VN-10 trin 6). Hver handling fanger aktør, tidsstempel og årsag, og producerer hændelser for ledelsesrevisionsbevis i henhold til krav f12fc090 (Årlig ledelsesattest). Revisionssporet viser, hvem der har gennemgået, hvad der blev ændret, og hvornår.',
            escalation_timers_info: 'Eskaleringstimere: Deadline for ejergennemgang er 5 hverdage; eskalering af undtagelsen er 48 timer (UC-VN-10 timere). Uafklarede emner eskalerer automatisk, når fristen udløber. Timere kan konfigureres i henhold til organisationens politik.',
            original_suggestion_preserved_info: 'Det originale maskin-/systemklassificeringsforslag bevares ved siden af enhver ejertilsidesættelse (UC-VN-10 beviskrav). Det oprindelige forslag overskrives aldrig og forbliver synligt til revisionsformål.',
            dedup_lineage_info: 'Deduplikeringsregler forhindrer dublerede beholdningsposter, når det samme dataaktiv opdages på tværs af flere scanninger eller connectors. De seneste scanningsmetadata bevares som den primære registrering, mens afstamningsreferencer til alle tidligere opdagelser for revisionssporbarhed bevares.',
            label_states_info: 'Analytics skelner mellem 4 etikettilstande til styringsgennemgang: maskinforeslået (afventer), ejergodkendt, ejerændret og afvist. Disse tilstande gør det muligt for revisorer at vurdere automatiseringsnøjagtighed og ejerengagement.',
            import_pipeline_info: 'Importskabeloner skal producere poster, der er kompatible med opdagelsespipelinen. Importerede poster går i stadie med de samme metadatakrav som scannede poster: kildesystemidentifikation, batch-id og tidsstempel. Kolonnetilknytninger skal målrettes mod felter defineret i vn_data_law_data_inventory_register.',
            lineage_full_path_info: 'Klassifikationslinjen sporer den fulde sti: scan/import -> iscenesættelse -> ejeranmeldelse -> godkendt etiket -> registerindtastning (UC-VN-10 trin 1-10). Hvert trin viser registrerings-id, skuespiller, tidsstempel og tilstandsovergang for fuldstændig revisionssporbarhed.',
            staging_workflow_info: 'Iscenesatte poster adskilles visuelt fra godkendte lagerposter ved deres promoveringsstatusbadge (UC-VN-10 trin 3). Hver post beholder herkomstmetadata: kildesystem, scanningsbatch-id og opdagelsestidsstempel for fuld revisionssporbarhed.',
            suggestion_data_d1_info: 'Forslag stemmer overens med datalovens klassificeringsprocedure DATA-D1: (1) katalogisere dataaktiver, (2) klassificere efter følsomhedsniveau, (3) definere håndteringsregler. Hvert forslag inkluderer følsomheds- og vigtighedsdimensioner pr. krav 18e4bfd1.',
            downstream_flags_info: 'Elementer, der er markeret til deling, grænseoverskridende overførsel eller højrisikobehandling, udløser forbundne downstream-arbejdsgange: vurdering af grænseoverskridende overførsel, vurdering af risikodeling og DPIA gennemgang (UC-VN-10 trin 8).',
            delta_detection_info: 'Opdagelsescyklusser understøtter deltadetektion pr. UC-VN-10 trin 11: hver cyklus viser, hvilke poster der er nye, ændrede eller fjernet siden den sidst godkendte baseline. Materialeændringer (>10 % delta) udløser automatisk gengennemgang af arbejdsgange.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Kunne ikke indlæse.',
            save: 'Kunne ikke gemme.',
            fetch_escalation_log_failed: 'Kunne ikke hente eskaleringslog.',
            fetch_activations_failed: 'Kunne ikke hente aktiveringer.',
            deactivate_failed: 'Kunne ikke deaktivere.',
            complete_activation_failed: 'Kunne ikke fuldføre activation.',
            fetch_playbook_templates_failed: 'Kunne ikke hente playbookskabeloner.',
            create_playbook_template_failed: 'Kunne ikke oprette playbookskabelon.',
            clone_template_failed: 'Kunne ikke klone skabelon.',
            update_status_failed: 'Kunne ikke opdatere status.',
            deprecate_template_failed: 'Kunne ikke udfase skabelon.',
            seed_defaults_failed: 'Kunne ikke initialisere standarder.',
            fetch_regulator_exports_failed: 'Kunne ikke hente tilsynseksporter.',
            create_regulator_export_failed: 'Kunne ikke oprette tilsynseksport.',
            update_submission_status_failed: 'Kunne ikke opdatere indsendelsesstatus.',
            fetch_response_metrics_failed: 'Kunne ikke hente svarmetrikker.',
            fetch_sla_dashboard_failed: 'Kunne ikke hente SLA-dashboard.',
            fetch_sla_definitions_failed: 'Kunne ikke hente SLA-definitioner.',
            create_sla_definition_failed: 'Kunne ikke oprette SLA-definition.',
            seed_sla_defaults_failed: 'Kunne ikke initialisere SLA-standarder.',
            fetch_vendor_coordination_failed: 'Kunne ikke hente leverandørkoordinering.',
            fetch_vendor_detail_failed: 'Kunne ikke hente leverandørdetalje.',
            create_vendor_coordination_failed: 'Kunne ikke oprette leverandørkoordinering.',
            add_communication_failed: 'Kunne ikke tilføje kommunikation.',
            add_info_request_failed: 'Kunne ikke tilføje infoanmodning.',
            add_action_request_failed: 'Kunne ikke tilføje handlingsanmodning.',
        },
        nav: {
            playbooks: 'Drejebogsskabeloner',
            sla_definitions: 'SLA-definitioner',
            sla_dashboard: 'SLA-oversigt',
            escalation_log: 'Eskaleringslog',
            regulator_exports: 'Myndighedseksporter',
            deadlines: 'Notifikationsfrister',
            vendor_coordination: 'Leverandørkoordinering',
            metrics: 'Svarmetrikker',
            playbook_links: 'Playbook-links',
            regulatory_sla: 'Regulatorisk SLA',
            coordination_dashboard: 'Koordinering',
            evidence_chain: 'Beviskæde',
            post_review: 'Efterhændelsesgennemgang',
        },
        playbooks: {
            page_title: 'Playbook skabeloner',
            activations_page_title: 'Playbook-aktiveringer',
            title: 'Drejebogsskabeloner',
            new: 'Ny skabelon',
            seed_defaults: 'Initialiser standarder',
            scenario_type: 'Scenarietype',
            severity_applicability: 'Alvorlighedsanvendelighed',
            all_scenario_types: 'Alle scenarietyper',
            new_playbook: 'Ny Playbook Skabelon',
            clone: 'Klon',
            clone_template: 'Klon skabelon',
            template_name: 'Skabelonnavn',
            deprecate: 'Udfas',
            confirm_deprecate: 'Udfas skabelon "{name}"?',
            create_template: 'Opret skabelon',
            update_status: 'Opdater status',
            no_templates: 'Ingen playbook skabeloner fundet',
            deactivation_reason_placeholder: 'Årsag til deaktivering...',
            description_placeholder: 'Playbookbeskrivelse...',
            name_placeholder: 'Playbooknavn...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'SLA-definitioner',
            title: 'SLA-definitioner',
            new: 'Ny SLA-definition',
            sla_name: 'SLA-navn',
            target_hours: 'Måltimer',
            escalation_after: 'Eskalering efter',
            applies_to: 'Gælder for',
            no_definitions: 'Ingen sla definitions fundet.',
            target_value_placeholder: 'Målværdi',
            name_placeholder: 'SLA-navn...',
            threshold_hours_label: 'Tærskeltimer',
            severity_label: 'Alvorlighedsgrad',
            all_types: 'Alle typer',
            all_severities: 'Alle alvorlighedsgrader',
            default_label: 'Standard',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Overensstemmende',
            page_title: 'SLA-oversigt',
            title: 'SLA-oversigt',
            loading: 'Indlæser SLA-data...',
            compliance_rate: 'Overholdelsesrate',
            breached: 'Overtrådt',
            at_risk: 'I risiko',
            on_track: 'På sporet',
            by_severity: 'Efter alvorlighed',
            total_records: 'Antal poster',
            met: 'Opfyldt',
            sla_records: 'SLA-poster',
            sla_reference: 'SLA-reference',
            record_id: 'Post-ID',
            metric_type: 'Metriktype',
            severity: 'Alvorlighed',
            elapsed: 'Forløbet',
            clock_start: 'Urstart',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Eskaleringslog',
            title: 'Eskaleringslog',
            incident_id: 'Hændelses-ID',
            escalated_by: 'Eskaleret af',
            escalated_to: 'Eskaleret til',
            reason: 'Årsag',
            escalated_at: 'Eskaleret den',
            no_escalations: 'Ingen eskaleringer fundet.',
            filter_incident_id: 'Filter efter Hændelse ID',
            escalation_id: 'Eskalerings-ID',
            level: 'Niveau',
            level_range: 'Niveau (L1-L4)',
            create_escalation: 'Opret eskalering',
            incident_id_placeholder: 'f.eks. INC-001',
            target_role_placeholder: 'f.eks. dpo, incident_manager',
            select_placeholder: '— vælg —',
        },
        regulator_exports: {
            page_title: 'Myndighedseksporter',
            title: 'Myndighedseksporter',
            new: 'Ny eksport',
            incident_id: 'Hændelses-ID',
            regulator: 'Tilsynsmyndighed',
            export_format: 'Format',
            exported_at: 'Eksporteret den',
            no_exports: 'Ingen regulator exports fundet.',
            enter_incident_id: 'Indtast Hændelse ID',
            export_id: 'Eksport-ID',
            watermark: 'Vandmærke',
            submission_status: 'Indsendelsesstatus',
            all_jurisdictions: 'Alle jurisdiktioner',
            no_transitions: 'Ingen yderligere overgange tilgængelige fra',
            select_new_status: '— vælg ny status —',
            sections_included: 'Inkluderede sektioner',
            form_classification: 'Klassifikation',
            submission_channel: 'Kanal',
        },
        deadlines: {
            title: 'Notifikationsfrister',
            incident_id: 'Hændelses-ID',
            deadline: 'Frist',
            regulator: 'Tilsynsmyndighed',
            remaining: 'Resterende',
            no_deadlines: 'Ingen notifikation deadlines fundet.',
        },
        vendor_coordination: {
            page_title: 'Leverandørkoordinering',
            title: 'Leverandørkoordinering',
            new: 'Ny koordinering',
            incident_id: 'Hændelses-ID',
            vendor_name: 'Leverandørnavn',
            contact: 'Kontakt',
            coordination_status: 'Koordineringsstatus',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Ingen leverandør koordinering elementer fundet.',
            assessment_placeholder: 'Indledende vurdering fra leverandør...',
            coordination_id: 'Koordinations-id',
            communications: 'Kommunikation',
            info_requests: 'Info anmodninger',
            action_requests: 'Handlingsanmodninger',
            direction: 'Retning',
            channel_label: 'Kanal',
            no_transitions: 'Ingen yderligere overgange tilgængelige.',
            vendor_ref_number: 'Referencenummer',
            report_date: 'Rapportdato',
            contact_email: 'E-mail',
            contact_phone: 'Telefon',
            channel_email: 'E-mail',
            channel_meeting: 'Møde',
            channel_phone: 'Telefon',
            channel_portal: 'Portal',
            incident_id_placeholder: 'Filter efter Hændelse ID...',
            priority_critical: 'Kritisk',
            priority_high: 'Høj',
            priority_low: 'Low',
            priority_medium: 'Middel',
            vendor_id_placeholder: 'Filter efter Leverandør ID...',
            description_placeholder: 'Beskrivelse...',
            filter_vendor_id: 'Filter efter Leverandør ID',
            inbound: 'Indgående',
            outbound: 'Udgående',
            summary_placeholder: 'Resumé...',
            priority_label: 'Prioritet',
            due_date_label: 'Forfaldsdato',
            info_request_desc: 'Beskrivelse af anmodning om oplysninger',
            action_request_desc: 'Beskrivelse af handlingsanmodning',
            content_label: 'Indhold',
            select_incident: 'Vælg Hændelse',
            select_vendor: 'Vælg Leverandør',
            discovery_date: 'Opdagelsesdato',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Aktør',
        },
        metrics: {
            page_title: 'Svarmetrikker',
            title: 'Svarmetrikker',
            loading: 'Indlæser metrikdata...',
            avg_response_time: 'Gns. svartid',
            avg_resolution_time: 'Gns. løsningstid',
            total_incidents: 'Hændelser i alt',
            by_severity: 'Efter alvorlighed',
            by_status: 'Efter status',
            trend: 'Tendens',
            start_sla_clock: 'Start SLA-ur',
            sla_records: 'SLA Optegnelser',
            no_sla_records: 'Ingen SLA-poster endnu.',
            record_id: 'Optag ID',
            severity: 'Sværhedsgrad',
            progress: 'Fremskridt',
            incident_id_required: 'Hændelses-ID er påkrævet',
            sla_definition_required: 'SLA-definition er påkrævet',
            metric_type_required: 'Metriktype er påkrævet',
            threshold_required: 'Tærskeltimer er påkrævet',
            sla_clock_started: 'SLA-ur startet',
            sla_definition: 'SLA-definition',
            no_definitions: 'Ingen SLA-definitioner fundet.',
            select_definition: 'Vælg SLA-definition...',
            select_metric_type: 'Vælg metriktype...',
            select_severity: 'Vælg alvorlighedsgrad...',
            threshold_hours: 'Tærskel (timer)',
            warning_threshold_pct: 'Advarselstærskel (%)',
            start_clock: 'Start ur',
            starting: 'Starter...',
            metric_label: 'Metrik',
            value_label: 'Værdi',
            incident_id: 'Hændelses-ID',
            threshold_placeholder: 'f.eks. 24',
            metric_types: {
                triage_time: 'Triagetid',
                containment_time: 'Inddæmningstid',
                notification_time: 'Notifikationstid',
                resolution_time: 'Løsningstid',
            },
            severities: {
                critical: 'Kritisk',
                high: 'Høj',
                medium: 'Middel',
                low: 'Low',
            },
            sla_reference: 'SLA-reference',
        },
        playbook_links: {
            page_title: 'Playbook-links',
            title: 'Playbook-notifikationslinks',
            new_link: 'Link playbook til notifikation',
            activation_id: 'Aktiverings-ID',
            notification_id: 'Notifikations-ID',
            select_activation: 'Vælg en aktivering...',
            select_notification: 'Vælg en notifikation...',
            no_links: 'Ingen playbook-links fundet',
            seed_pdpl: 'Tilføj skabelonlinks til playbooks',
            created: 'Playbook link oprettet',
            seeded: 'Skabelonlinks til playbooks tilføjet',
            create_failed: 'Kunne ikke oprette playbook-link',
            seed_failed: 'Kunne ikke tilføje skabelonlinks til playbooks',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Ingen installeret ramme regulerer hændelsesrespons, så der er ingen frister at tilføje.',
                declared_but_none_active: 'En ramme, der regulerer hændelsesrespons, er installeret, men ikke i kraft.',
                no_declared_deadlines: 'De installerede rammer for hændelsesrespons angiver ingen anmeldelsesfrister.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Frist',
            deadline_units: {
                hours: '{count} timer',
                working_days: '{count} arbejdsdage',
                calendar_days: '{count} kalenderdage',
                days: '{count} dage',
                weeks: '{count} uger',
                months: '{count} måneder',
                years: '{count} år',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Regulatoriske SLA-tilknytninger',
            title: 'Regulatoriske SLA-tilknytninger',
            new_mapping: 'Ny SLA-tilknytning',
            sla_definition: 'SLA-definition',
            select_sla_definition: 'Vælg en SLA-definition...',
            framework_code: 'Rammeværkskode',
            deadline_type: 'Fristtype',
            deadline_hours: 'Frist i timer',
            seed_pdpl: 'Tilføj tilknytninger fra installerede frister',
            no_mappings: 'Ingen regulatoriske SLA-tilknytninger fundet',
            dashboard: 'Regulatorisk SLA-oversigt',
            deadline_types: {
                initial_notification: 'Indledende meddelelse',
                supplement_response: 'Supplerende svar',
                authority_response: 'Myndighedens svar',
                remediation_report: 'Udbedringsrapport',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Lovhenvisning',
        },
        coordination_dashboard: {
            page_title: 'Koordineringsoversigt',
            title: 'Koordineringsoversigt',
            incident_id: 'Hændelses-ID',
            select_incident: 'Vælg en hændelse...',
            team_role: 'Teamrolle',
            members: 'Medlemmer',
            tasks: 'Opgaver',
            sla_status: 'SLA-status',
            no_teams: 'Ingen koordineringsteams fundet',
            new_team: 'Nyt koordineringsteam',
            enter_incident_id: 'Indtast et hændelses-ID for at se koordineringsteams.',
            member_ids_placeholder: 'bruger1, bruger2',
            edit_team: 'Rediger team',
            task_count: 'Opgavetælling',
            tasks_completed: 'Afsluttet',
            team_roles: {
                security: 'Sikkerhed',
                legal: 'Juridisk',
                it: 'IT',
                management: 'Ledelse',
                department_owner: 'Afdelingsejer',
                external: 'Ekstern',
            },
        },
        evidence_chain: {
            page_title: 'Beviskæde',
            title: 'Beviskæde',
            new_entry: 'Ny bevisindgang',
            select_incident: 'Vælg en hændelse...',
            actor: 'Aktør',
            select_actor: 'Vælg en aktør...',
            evidence_id: 'Bevis-ID',
            action: 'Handling',
            recipient: 'Modtager',
            no_entries: 'Ingen beviskædeindgange fundet',
            enter_incident_id: 'Indtast et hændelses-ID for at se beviskæde.',
            evidence_id_placeholder: 'f.eks. EVD-2026-0001',
            evidence_id_help: 'En unik reference til bevismaterialet – en logfil, et diskbillede eller et skærmbillede. Et forslag er udfyldt på forhånd; rediger det, så det passer til dit beviskatalog.',
            select_incident_first: 'Vælg en hændelse ovenfor, før du tilføjer en bevispost.',
            for_incident: 'For hændelse',
            actions: {
                collected: 'Samlet',
                shared: 'Delt',
                exported: 'Eksporteret',
                archived: 'Arkiveret',
            },
        },
        post_review: {
            page_title: 'Efterhændelsesgennemgang',
            title: 'Efterhændelsesgennemgang',
            new_review: 'Ny gennemgang',
            select_incident: 'Vælg en hændelse...',
            lessons_learned: 'Erfaringer',
            recommendations: 'Anbefalinger',
            improvement_actions: 'Forbedringstiltag',
            submit_for_review: 'Indsend til gennemgang',
            aria_submit_review: 'Indsend gennemgang {name} til gennemgang',
            aria_sign_off: 'Godkend gennemgang {name}',
            sign_off: 'Godkendelse',
            no_reviews: 'Ingen efterhændelsesgennemgange fundet',
            enter_incident_id: 'Indtast et hændelses-ID for at se efterhændelsesgennemgang.',
            one_per_line: 'Én per linje',
            statuses: {
                draft: 'Kladde',
                in_review: 'Under gennemgang',
                signed_off: 'Godkendt',
                closed: 'Lukket',
            },
        },
        activations: {
            title: 'Playbook-aktiveringer',
            activation_id: 'Aktiverings-id',
            phases: 'Faser',
            activated_at: 'Aktiveret kl',
            activated_by: 'Aktiveret af',
            deactivate: 'Deaktiver',
            complete: 'Fuldfør',
            deactivation_reason: 'Deaktiveringsårsag',
            evidence_button: 'Beviser',
            evidence_modal_title: 'Aktiveringsbevis',
            no_evidence: 'Ingen beviser knyttet til denne aktivering.',
            upload_evidence_label: 'Upload aktiveringsbevis',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Før mig gennem huller',
            running: 'Gåafstande...',
            results_heading: 'Køreplan for forbedring ({count} hul{plural})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'Ingen huller fundet – alle domæner ved eller over målet.',
            col_priority: 'Prioritet',
            col_domain: 'Domæne',
            col_levels: 'Aktuel → Mål',
            col_severity: 'Sværhedsgrad',
            col_actions: 'Udbedringsforanstaltninger',
            citations_label: 'Citater',
            accept_label: 'Accepter køreplan',
            reject_label: 'Afvis udkast',
            export_label: 'Eksporter DOCX',
            exported_label: 'Køreplan eksporteret',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Opløsning: {status}',
            resolve_status_accepted: 'Accepteret',
            resolve_status_rejected: 'Afvist',
            error_generic: 'Kunne ikke køre gap walkthrough. Prøv igen, eller kontroller vurderingsstatus.',
        },
        policy_gap: {
            run_label: 'Opdag politiske huller',
            running: 'Registrerer huller...',
            results_heading: 'Politikhuller ({count})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'Ingen dækningshuller fundet.',
            col_severity: 'Sværhedsgrad',
            col_kind: 'Venlig',
            col_clause: 'Klausul / krav',
            kind_missing: 'Manglende klausul',
            kind_weak: 'Svag dækning',
            kind_stale: 'Forældet (forsinket anmeldelse)',
            remediation_label: 'Foreslået afhjælpning',
            citations_label: 'Citater',
            accept_label: 'Accepter gap-rapport',
            reject_label: 'Afvis rapport',
            export_label: 'Eksporter DOCX',
            accept_hint: 'Accepter registrerer hulrapporten for det vedvarende udkast. Brug Export DOCX til at generere en politikrevisionsrapport; operatøren driver selve politikredigeringen.',
            resolve_summary: 'Opløsning: {status}',
            error_generic: 'Kunne ikke opdage politiske huller. Prøv igen, eller kontroller dokumentets status.',
        },
        remediation_plan_generator: {
            run_label: 'Generer plan fra huller',
            running: 'Genererer plan...',
            results_heading: 'Udbedringsplan ({phase_count} fase{plural})',
            tokens_label: '≈ {count} tokens',
            phase_label: 'Fase {index}',
            window_label: 'vindue',
            owner_label: 'Ejerrolle',
            evidence_label: 'Bevistyper påkrævet',
            tasks_label: 'Opgaver',
            no_phases: 'Ingen faser produceret — input gap-set kan være tomt.',
            citations_label: 'Citater',
            accept_label: 'Accepter planen',
            reject_label: 'Afvis plan',
            export_label: 'Eksporter DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Opløsning: {status} · {tasks} opgave(r) oprettet',
            error_generic: 'Kunne ikke generere afhjælpningsplan. Prøv igen, eller tjek gap-set input.',
            source_picker_label: 'Kilde',
            source_all: 'Alle',
            source_consent: 'Analyse af samtykkegab',
            source_policy: 'Opdagelse af politikgab',
            source_vendor: 'Leverandør onboarding',
            load_upstream_label: 'Belast fra opstrøms',
            loading_upstream: 'Indlæser...',
            upstream_count: '{count} hul(er) indlæst',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Modenhedsmodeller',
            maturity_assessments: 'Modenhedsvurderinger',
            trend_dashboard: 'Tendensorversigt',
            reporting_periods: 'Rapporteringsperioder',
            executive_summaries: 'Direktionsresuméer',
            audit_readiness: 'Revisionsparathed',
            evidence_gaps: 'Dokumentationsmangler',
            program_health: 'Programsundhed',
            obligation_mappings: 'Forpligtelsestilknytninger',
            governance_documents: 'Styrings­dokumenter',
            remediation_plans: 'Udbedringplaner',
            governance_model: 'Styringsmodel',
            document_drafting: 'Dokumentudkast',
            document_issuance: 'Dokumentudstedelse',
            acknowledgment_tracking: 'Kvitteringssporing',
            change_log: 'Ændringslog',
            review_cadence: 'Gennemgangskadence',
        },
        maturity_models: {
            title: 'Modenhedsmodeller',
            new: 'Ny model',
            seed_defaults: 'Initialiser standarder',
            domains: 'Domæner',
            activate: 'Aktiver',
            no_models: 'Ingen modenhed modeller fundet',
            new_maturity_model: 'Ny Modenhed Model',
            create_model: 'Opret model',
            load_error: 'Kunne ikke indlæse modenhedsmodeller. Prøv igen.',
            create_error: 'Kunne ikke oprette modenhedsmodel',
            status_error: 'Kunne ikke opdatere modelstatus',
            seed_error: 'Kunne ikke generere standardværdier',
            aria_seed_defaults: 'Generer standard modenhedsmodeller',
            aria_create_new: 'Opret ny modenhedsmodel',
            loading_sr: 'Indlæser modenhedsmodeller',
            model_name_placeholder: 'f.eks., Privatlivets modenhedsmodel',
            status_changed: 'Modelstatus ændret til {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Modenhedsvurderinger',
            new: 'Ny vurdering',
            model: 'Model',
            assessor: 'Vurderer',
            overall_score: 'Samlet score',
            overall_score_help: 'En vægtet modenhedsvurdering fra 0–5, der beregnes automatisk, når en vurdering afsluttes. Forøg den ved at give hvert domæne en højere score under evalueringen og derefter føre vurderingen gennem Indsend til gennemgang → Afslut.',
            assessed_at: 'Vurderet den',
            no_assessments: 'Ingen modenhed vurderinger fundet.',
            load_error: 'Kunne ikke indlæse modenhedsvurderinger. Prøv igen.',
            create_error: 'Kunne ikke oprette vurdering',
            start_error: 'Kunne ikke starte vurdering',
            archive_error: 'Kunne ikke arkivere vurdering',
            eval_error: 'Kunne ikke indlæse vurdering til evaluering',
            eval_min_score: 'Bedøm mindst ét domæne',
            eval_save_error: 'Kunne ikke gemme domænevurderinger',
            review_error: 'Kunne ikke indsende til gennemgang',
            finalize_error: 'Kunne ikke afslutte vurdering',
            start: 'Start',
            start_confirm_title: 'Start vurdering',
            start_confirm_message: 'Er du sikker på, at du vil starte denne vurdering?',
            archive_confirm_title: 'Arkivér vurdering',
            archive_confirm_message: 'Er du sikker på, at du vil arkivere denne vurdering?',
            evaluate_domains: 'Evaluér',
            evaluate_domains_title: 'Evaluér domæner',
            submit_for_review: 'Indsend til gennemgang',
            finalize: 'Afslut',
            archive: 'Arkivér',
            all_reviewers: 'Alle gennemgåere skal godkende',
            any_reviewer: 'Enhver gennemgåer kan godkende',
            select_level: 'Vælg modenhedsniveau...',
            findings_placeholder: 'Resultater, noter, evidensreferencer...',
            no_domains: 'Ingen domæner fundet for denne model.',
        },
        trend_dashboard: {
            title: 'Tendensorversigt',
            loading: 'Indlæser tendensdata...',
            score_trend: 'Scoretendens',
            domain_comparison: 'Domænesammenligning',
            period: 'Periode',
            load_error: 'Kunne ikke indlæse tendensopsummering. Prøv igen.',
            metrics_load_error: 'Kunne ikke indlæse tendensmålinger. Prøv igen.',
            module: 'Modul',
            metric: 'Måling',
            value: 'Værdi',
            trend: 'Tendens',
            aria_filters: 'Filtre for tendensmålinger',
            showing_chart: 'Viser diagramvisning',
            showing_table: 'Viser datatabelvisning',
            create_metric: 'Opret måling',
            data_points: 'Datapunkter',
            trend_direction: 'Tendensretning',
            unit: 'Enhed',
        },
        reporting_periods: {
            title: 'Rapporteringsperioder',
            new: 'Ny periode',
            period_name: 'Periodenavn',
            start_date: 'Startdato',
            end_date: 'Slutdato',
            no_periods: 'Ingen reporting periods fundet.',
            load_error: 'Kunne ikke indlæse rapporteringsperioder. Prøv igen.',
            create_error: 'Kunne ikke oprette rapporteringsperiode',
            auto_generate_error: 'Kunne ikke autogenerere rapporteringsperioder',
            status_error: 'Kunne ikke opdatere periodestatus',
            start_collecting: 'Begynd indsamling',
            mark_as_draft: 'Markér som kladde',
            submit_for_review: 'Indsend til gennemgang',
            publish: 'Publicér',
            auto_generate: 'Autogenerer rapporteringsperioder',
            period_label_placeholder: 'f.eks., K1 2026',
            status_updated: 'Periodestatus opdateret',
        },
        executive_summaries: {
            title: 'Direktionsresuméer',
            new: 'Nyt resumé',
            period: 'Periode',
            generated_at: 'Genereret den',
            generated_by: 'Genereret af',
            no_summaries: 'Ingen ledelsesmæssig resuméer fundet.',
            generate: 'Generer',
            load_error: 'Kunne ikke indlæse ledelsesresuméer. Prøv igen.',
            generate_error: 'Kunne ikke generere ledelsesresumé',
            save_error: 'Kunne ikke gemme ledelsesresumé',
        },
        audit_readiness: {
            title: 'Revisionsparathed',
            loading: 'Indlæser revisionsberedskabsdata...',
            overall_readiness: 'Samlet beredskab',
            by_domain: 'Efter domæne',
            evidence_coverage: 'Dokumentationsdækning',
            gaps: 'Mangler',
            load_error: 'Kunne ikke indlæse seneste parathed. Prøv igen.',
            history_error: 'Kunne ikke indlæse parathedshistorik. Prøv igen.',
            calculate_error: 'Kunne ikke beregne revisionsparathed',
            aria_recalculate: 'Genberegn revisionsparathedscore',
            aria_current_readiness: 'Aktuel revisionsparathed',
            aria_history: 'Historik for parathedsscorer',
            recalculated: 'Revisionsparathed genberegnet',
            loading_sr: 'Indlæser revisionsparathedsdata',
        },
        evidence_gaps: {
            title: 'Dokumentationsmangler',
            domain: 'Domæne',
            control: 'Kontrol',
            gap_type: 'Mangeltype',
            severity: 'Alvorlighed',
            no_gaps: 'Ingen dokumentation mangler fundet.',
            load_error: 'Kunne ikke indlæse evidenshuller. Prøv igen.',
            stats_error: 'Kunne ikke indlæse hulstatistikker. Prøv igen.',
            create_error: 'Kunne ikke oprette evidenshul',
            update_error: 'Kunne ikke opdatere evidenshul',
            status_error: 'Kunne ikke opdatere status',
            aria_create_new: 'Opret nyt evidenshul',
            aria_statistics: 'Statistikker over evidenshuller',
            loading_sr: 'Indlæser evidenshuller',
            status_updated: 'Hulstatus opdateret',
            entity_type_placeholder: 'f.eks., leverandør, system',
            all_modules: 'Alle domæner',
            all_severities: 'Alle alvorlighedsgrader',
        },
        program_health: {
            title: 'Programsundhed',
            loading: 'Indlæser programsundhedsdata...',
            overall_health: 'Samlet sundhed',
            by_module: 'Efter modul',
            risk_areas: 'Risikoområder',
            recommendations: 'Anbefalinger',
            load_error: 'Kunne ikke indlæse programsundhedsdata. Prøv igen.',
            aria_key_metrics: 'Nøglemålinger for programsundhed',
            aria_module_scorecard: 'Modulscorekortopdeling',
            loading_sr: 'Indlæser programsundhedsdata',
            trending_up: 'Stigende tendens',
            trending_down: 'Faldende tendens',
            trending_stable: 'Stabil',
            data_loaded: 'Programsundhedsdata indlæst',
        },
        obligation_mappings: {
            title: 'Forpligtelsestilknytninger',
            create_mapping: 'Opret forpligtelsestilknytning',
            framework: 'Rammeværk',
            select_framework: 'Vælg et rammeværk...',
            obligation: 'Forpligtelse',
            obligation_ref: 'Forpligtelsesreference',
            obligation_description: 'Forpligtelsesbeskrivelse',
            role: 'Rolle',
            assigned_role: 'Tildelt rolle',
            select_role: 'Vælg en rolle...',
            assigned_department: 'Tildelt afdeling',
            select_department: 'Vælg en afdeling...',
            control_ids: 'Kontrol-ID',
            control_ids_placeholder: 'f.eks. A.5.1, CC6.1',
            control_ids_hint: 'Kommaseparerede kontrolreferencekoder.',
            raci_assignments: 'RACI-tildelinger',
            create_raci: 'Opret RACI-tildeling',
            activity: 'Aktivitet',
            activity_name: 'Aktivitetsnavn',
            activity_description: 'Aktivitetsbeskrivelse',
            responsible: 'Ansvarlig',
            select_users: 'Vælg brugere...',
            accountable: 'Regnskabspligtig',
            select_user: 'Vælg en bruger...',
            consulted: 'Konsulteret',
            informed: 'Informeret',
            review_frequency: 'Gennemgangsfrekvens',
            frequency_monthly: 'Månedlig',
            frequency_quarterly: 'Kvartalsvis',
            frequency_semi_annual: 'Halvårlig',
            frequency_annual: 'Årlig',
            no_mappings_found: 'Ingen forpligtelsestilknytninger fundet.',
            no_raci_found: 'Ingen RACI-tildelinger fundet.',
            mapping_created: 'Forpligtelsestilknytning oprettet',
            mapping_updated: 'Forpligtelsestilknytning opdateret',
            mapping_deleted: 'Forpligtelsestilknytning slettet',
            raci_created: 'RACI-tildeling oprettet',
            raci_updated: 'RACI-tildeling opdateret',
            raci_deleted: 'RACI-tildeling slettet',
            aria_edit_mapping: 'Rediger tilknytning {name}',
            aria_delete_mapping: 'Slet tilknytning {name}',
            aria_edit_raci: 'Rediger RACI {name}',
            aria_delete_raci: 'Slet RACI {name}',
            confirm_delete_mapping: 'Slet denne forpligtelsestilknytning?',
            confirm_delete_raci: 'Slet denne RACI-tildeling?',
            table_view: 'Tabelvisning',
            matrix_view: 'Matrixvisning',
            print_export: 'Udskriv / Eksportér',
            filter_framework: 'Filtrer efter ramme',
            no_matrix_data: 'Ingen RACI-tildelinger at vise i matrixvisning.',
            aria_print_export: 'Udskriv eller eksportér RACI-matrix',
        },
        governance_documents: {
            title: 'Styrings­dokumenter',
            create_document: 'Opret styringsdokument',
            document_title: 'Dokumenttitel',
            document_type: 'Dokumenttype',
            content_url: 'Indholds-URL',
            content_url_placeholder: 'https://...',
            framework_codes: 'Rammekoder',
            select_frameworks: 'Vælg rammeværk...',
            effective_date: 'Ikrafttrædelsesdato',
            review_date: 'Gennemgangsdato',
            status: 'Status',
            version: 'version',
            acknowledgments: 'Bekræftelser',
            acknowledge: 'Bekræft',
            acknowledged_by: 'Bekræftet af',
            department: 'Afdeling',
            no_documents_found: 'Ingen styringsdokumenter fundet.',
            draft: 'Kladde',
            review: 'Gennemgang',
            approved: 'Godkendt',
            issued: 'Udstedt',
            superseded: 'Erstattet',
            document_created: 'Dokument oprettet',
            document_acknowledged: 'Dokument bekræftet',
            policy: 'Politik',
            procedure: 'Fremgangsmåde',
            standard: 'Standard',
            guideline: 'Retningslinje',
        },
        governance_model: {
            title: 'Styringsmodel',
            create_model: 'Styringsmodel',
            edit_model: 'Styringsmodel',
            model_name: 'Model',
            description: 'Beskrivelse',
            governance_type: 'Type',
            type_centralized: 'Centraliseret',
            type_federated: 'Forbundet',
            type_hybrid: 'Hybrid',
            framework_codes: 'Framework',
            select_frameworks: '...',
            review_frequency: 'Frequency',
            freq_monthly: 'Månedligt',
            freq_quarterly: 'Kvartalsvis',
            freq_semi_annual: 'Halvårligt',
            freq_annual: 'Årlig',
            committees: 'Udvalg',
            committee_name: 'Name',
            committee_members: 'Members',
            committee_mandate: 'Mandat',
            add_committee: '+',
            no_committees: '—',
            delegation_rules: 'Delegation',
            from_role: 'From',
            to_role: 'To',
            scope: 'Omfang',
            add_delegation_rule: '+',
            control_owner_summary: 'RACI',
            no_raci_data: '—',
            activity: 'Aktivitet',
            responsible: 'R',
            accountable: 'A',
            consulted: 'C',
            informed: 'I',
            no_models_found: '—',
            model_created: 'OK',
            model_updated: 'OK',
            model_deleted: 'OK',
            confirm_delete: '?',
            expand: '+',
            collapse: '-',
            aria_expand: '{name}',
            aria_edit: '{name}',
            aria_delete: '{name}',
            aria_export_csv: '{name} CSV',
            aria_export_json: '{name} JSON',
            framework_read_permission_warning: 'Du skal bruge regulatory_framework:læsetilladelsen for at vælge rammer. Bed en administrator om at give det.',
            select_members: 'Udvalgte udvalgsmedlemmer',
            select_from_role: 'Vælg delegerende rolle',
            select_to_role: 'Vælg modtagende rolle',
            mandate_markdown_hint: 'Markdown understøttet: **fed**, lister, links.',
            scope_tab_obligation: 'Forpligtelse',
            scope_tab_framework: 'Ramme',
            scope_tab_free_text: 'Fri tekst',
            select_obligation: 'Vælg en forpligtelse',
            select_framework: 'Vælg en ramme',
            aria_remove_committee: 'Fjern udvalg {n}',
            aria_remove_delegation_rule: 'Fjern delegeringsregel {n}',
            no_frameworks_available: 'Ingen rammer tilgængelige. Tilføj regulatoriske rammer først.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Dokumentudkast',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Status',
            drafter: 'Tegner',
            deadline: 'Deadline',
            assign_drafter: 'Assign',
            select_drafter: '...',
            assign: 'Tildel',
            submit_for_review: 'Submit',
            legal_review: 'Review',
            reviewer: 'Anmelder',
            select_reviewer: '...',
            comments: 'Kommentarer',
            review_action: 'Action',
            approve: 'Godkend',
            request_changes: 'Anmod om ændringer',
            submit_review: 'Submit',
            executive_approval: 'Approval',
            no_drafts_found: '—',
            drafter_assigned: 'OK',
            status_updated: 'OK',
            review_approved: 'OK',
            changes_requested: 'OK',
            document_approved: 'OK',
            aria_assign: '{name}',
            aria_submit_review: '{name}',
            aria_review: '{name}',
            aria_approve: '{name}',
        },
        document_issuance: {
            title: 'Dokumentudstedelse',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Status',
            version: 'version',
            effective_date: 'Date',
            distribution_scope: 'Distribution',
            distribution_placeholder: '...',
            issuance_number: 'Number',
            issuance_number_placeholder: 'ISS-2026-001',
            supersedes: 'Supersedes',
            none: '—',
            issue_document: 'Issue',
            ready_for_issuance: 'Ready',
            issued_documents: 'Issued',
            no_issued_documents: '—',
            view_lineage: 'Lineage',
            supersession_lineage: 'Supersession',
            document_issued: 'OK',
            document_superseded: 'OK',
            aria_issue: '{name}',
            aria_view_lineage: '{name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Kvitteringssporing',
            document_title: 'Dokument',
            acknowledged_count: 'Anerkendt',
            pending_count: 'Afventer',
            percentage: '%',
            details: 'Detaljer',
            send_reminder: 'Remind',
            acknowledgment_details: 'Details',
            user: 'Bruger',
            department: 'afdeling',
            acknowledged_at: 'Date',
            status: 'Status',
            acknowledged: 'OK',
            no_issued_documents: '—',
            no_acknowledgments: '—',
            reminder_sent: 'OK',
            aria_expand: '{name}',
            aria_send_reminder: '{name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Ændringslog',
            date: 'Dato',
            document: 'Dokument',
            change_type: 'Type',
            actor: 'Skuespiller',
            summary: 'Resumé',
            export_csv: 'Eksporter CSV',
            filters: 'Filtre',
            filter_document: 'Dokument',
            all_documents: 'Alle',
            filter_change_type: 'Type',
            all_types: 'Alle',
            date_from: 'Fra',
            date_to: 'Til',
            version_history: 'Historik',
            version_archive: 'Arkiv',
            no_entries: '—',
            no_history: '—',
            exported: 'OK',
            aria_export: 'CSV',
            aria_view_history: '{name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Gennemgangskadence',
            configure_cadence: 'Gennemgangskadence',
            edit_cadence: 'Gennemgangskadence',
            document: 'Dokument',
            select_document: '...',
            review_frequency: 'Frequency',
            freq_30: '30',
            freq_60: '60',
            freq_90: '90',
            freq_180: '180',
            freq_365: '365',
            last_reviewed: 'Last',
            next_review: 'Next',
            status: 'Status',
            overdue: 'Forfalden',
            on_track: 'OK',
            days: 'days',
            horizon: 'Horizon',
            overdue_warning: 'Overdue',
            overdue_count: '{count}',
            no_cadences: '—',
            cadence_created: 'OK',
            cadence_updated: 'OK',
            aria_edit: '{name}',
        },
        remediation_plans: {
            title: 'Udbedringplaner',
            create_plan: 'Opret udbedringplan',
            gap_id: 'Mangelreference',
            select_gap: 'Vælg en mangel...',
            plan_title: 'Titel',
            description: 'Beskrivelse',
            priority: 'Prioritet',
            priority_critical: 'Kritisk',
            priority_high: 'Høj',
            priority_medium: 'Middel',
            priority_low: 'Lav',
            assigned_to: 'Tildelt til',
            select_user: 'Vælg en bruger...',
            target_date: 'Måldato',
            framework_code: 'Rammeværk',
            select_framework: 'Vælg et rammeværk...',
            gap_finding: 'Mangelfund',
            action_items: 'Handlingspunkter',
            owner: 'Ejer',
            deadline: 'Frist',
            planned: 'Planlagt',
            in_progress: 'I gang',
            completed: 'Fuldført',
            verified: 'Verificeret',
            no_plans_found: 'Ingen udbedringplaner fundet.',
            evidence: 'Dokumentation',
            plan_created: 'Afhjælpningsplan oprettet',
            action_items_hint: 'Handlingspunkter (ét per linje)',
        },
        statuses: {
            identified: 'Identificeret',
            assigned: 'Tildelt',
            in_remediation: 'I Afhjælpning',
            resolved: 'Løst',
            accepted_risk: 'Accepteret risiko',
            planned: 'Planlagt',
            in_progress: 'Igangværende',
            under_review: 'Under gennemgang',
            finalized: 'Afsluttet',
            archived: 'Arkiveret',
            draft: 'Udkast',
            active: 'Aktiv',
            upcoming: 'Kommende',
            collecting: 'Indsamling',
            published: 'Udgivet',
            review: 'Gennemgå',
            approved: 'Godkendt',
            issued: 'Udstedt',
            superseded: 'Afløst',
            completed: 'Afsluttet',
            verified: 'Verificeret',
        },
        severities: {
            critical: 'Kritisk',
            high: 'Høj',
            medium: 'Medium',
            low: 'Low',
        },
        priorities: {
            critical: 'Kritisk',
            high: 'Høj',
            medium: 'Medium',
            low: 'Low',
        },
        gap_types: {
            missing: 'Mangler',
            expired: 'Udløbet',
            insufficient: 'Utilstrækkelig',
        },
        document_types: {
            policy: 'Politik',
            procedure: 'Fremgangsmåde',
            standard: 'Standard',
            guideline: 'Retningslinje',
            template: 'Skabelon (intern)',
        },
        maturity_levels: {
            level_1: 'Niveau 1 — Indledende',
            level_2: 'Niveau 2 — Udvikling',
            level_3: 'Niveau 3 — Defineret',
            level_4: 'Niveau 4 — Administreret',
            level_5: 'Niveau 5 — Optimering',
        },
        grades: {
            excellent: 'Fremragende',
            good: 'Godt',
            fair: 'Fair',
            poor: 'Dårlig',
            critical: 'Kritisk',
            needs_improvement: 'Trænger til forbedring',
            not_assessed: 'Ikke vurderet',
        },
        modules: {
            data_mapping: 'Datakortlægning',
            consent: 'Samtykke',
            governance: 'Governance',
            incidents: 'Hændelser',
            monitoring: 'Overvågning',
            classification: 'Klassifikation',
            vendor_risk: 'Leverandørrisiko',
            discovery: 'Opdagelse',
            deletion: 'Sletning',
        },
        cadences: {
            monthly: 'Månedligt',
            quarterly: 'Kvartalsvis',
            semi_annual: 'Halvårligt',
            annual: 'Årlig',
        },
        section_labels: {
            opening: 'Åbning',
            key_metrics: 'Nøglemålinger',
            highlights: 'Højdepunkter',
            concerns: 'Bekymringer',
            recommendations: 'Anbefalinger',
            outlook: 'Outlook',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} hul',
        count_gaps_plural: '{count} huller',
        count_summaries: '{count} oversigt',
        count_summaries_plural: '{count} oversigter',
        count_assessments: '{count} vurdering',
        count_assessments_plural: '{count} vurderinger',
        count_models: '{count} model',
        count_models_plural: '{count} modeller',
        count_periods: '{count} periode',
        count_periods_plural: '{count} perioder',
        count_metrics: '{count} metrisk',
        count_metrics_plural: '{count} metrics',
        unknown_user: 'Ukendt bruger',
        unknown_gap: 'Ukendt hul',
        no_permission_title: 'Utilstrækkelige tilladelser',
        no_permission_message: 'Du har ikke tilladelse til at få adgang til denne side.',
        ac_vn_frameworks_title: 'VN Framework Dækning',
        ac_vn_frameworks_body: 'Modenhedsvurderinger dækker alle 6 vietnamesiske lovgivningsrammer: PDPL, datalovgivning, telekommunikationslovgivning, cybersikkerhedslovgivning, AI lov og e-handelslovgivning – hver med rammespecifikke vurderingskriterier.',
        ac_controller_processor_title: 'Controller vs. processor',
        ac_controller_processor_body: 'Forpligtelseskortlægninger skelner mellem dataansvarlig og databehandleransvar. Tildel roller ved hjælp af RACI-matricen for at sikre, at hver forpligtelse har en klar ejer, uanset om organisationen fungerer som controller eller databehandler.',
        ac_versioned_evidence_title: 'Versioneret Evidence Trail',
        ac_versioned_evidence_body: 'Hver statusovergang (Udkast, Gennemgang, Godkendt, Udstedt) opretter en tidsstemplet, versioneret post, der fungerer som revideret bevis. Ændringsloggen fanger hele dokumentets livscyklushistorie.',
        ac_remediation_gap_link_title: 'Knyttet til Assessment Gaps',
        ac_remediation_gap_link_body: 'Hver afhjælpningsplan linker direkte til et identificeret vurderingsgab. Tildel en ejer og indstil en måldeadline for at spore opløsningsfremskridt fra hul til lukning.',
        ac_annual_attestation_title: 'Årlig attestationscyklus',
        ac_annual_attestation_body: 'Rapporteringsperioder stemmer overens med det årlige attestationskrav for vietnamesisk datalov. Brug den "årlige" kadence (365-dages cyklus) til automatisk at generere attestationsjusterede perioder.',
        ac_evidence_per_obligation_title: 'Bevis pr. rammeforpligtelse',
        ac_evidence_per_obligation_body: 'Bevishuller identificerer manglende, udløbne eller utilstrækkelige beviser for hver rammeforpligtelse. Filtrer efter domæne for at se, hvilke regulatoriske krav der mangler understøttende dokumentation.',
        ac_cross_framework_title: 'Fuldstændighed på tværs af rammer',
        ac_cross_framework_body: 'Revisionsparathed giver et komplet overblik over rammerne. Komponentscorer afspejler dækning på tværs af alle kortlagte rammer og fremhæver områder, der kræver opmærksomhed før en revision.',
        ac_cadence_default_title: 'Standard gennemgangskadence',
        ac_cadence_default_body: 'Standard gennemgangskadence er 180 dage. Forsinkede anmeldelser markeres automatisk. Juster frekvensen pr. dokument efter behov - valgmulighederne spænder fra 30 til 365 dage.',
        ac_templates_internal_title: 'Skabelon klassificering',
        ac_templates_internal_body: 'Skabeloner er klassificeret som interne dokumenter. Brug dokumenttypen "Skabelon" til at markere genbrugelige styringsartefakter med kun internt distributionsomfang.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Afventende skrivninger',
        subtitle: 'Eksterne Claude-klienter foreslår skrivninger her. Godkend eller afvis hver post — Forseti udfører først efter administrators godkendelse.',
        no_pending: 'Ingen afventende skrivninger.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Værktøj',
        col_preview: 'Forhåndsvisning',
        col_key: 'API tast',
        col_requested: 'Anmodet',
        col_expires: 'Udløber',
        col_status: 'Status',
        status_pending: 'Afventer',
        status_approved: 'Godkendt',
        status_declined: 'Afvist',
        status_expired: 'Udløbet',
        status_executed: 'Udført',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Godkend',
        decline_label: 'Afvis',
        refresh_label: 'Opdater',
        error_generic: 'Kunne ikke indlæse afventende skrivninger.',
        confirm_approve: 'Godkendelse vil udføre værktøjet på din organisation med API key-indehaverens tilladelser. Fortsæt?',
        confirm_decline: 'Afvis denne afventende skrivning? Dette kan ikke fortrydes.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Afventende skrivninger udløber 24 timer efter oprettelsen. Den timebaserede GC-gennemgang afviser udløbne poster automatisk.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Handlinger',
    },
    audit_walkthrough: {
        title: 'Forseti — Generator til revisionsgennemgang',
        subtitle: 'Generér en struktureret gennemgang til en revisionsopstart: åbningsspørgsmål pr. kontrol, forventet dokumentation, opfølgningsspørgsmål, potentielle huller.',
        audit_picker_label: 'Revision',
        audit_picker_hint: 'Vælg en planlagt revision for at generere en gennemgang.',
        run_label: 'Generér gennemgang',
        running: 'Genererer⬦',
        results_heading: 'Gennemgang — {count} kontrol(ler)',
        tokens_label: '≈ {count} tokens',
        no_controls: 'Ingen kontroller i omfanget. Tilføj kontroller til revisionsposten.',
        col_control: 'Kontrol',
        col_question: 'Åbningsspørgsmål',
        col_evidence: 'Dokumentation',
        col_gaps: 'Potentielle huller',
        expected_evidence_label: 'Forventet dokumentation',
        followup_probes_label: 'Opfølgningsspørgsmål',
        accept_label: 'Acceptér gennemgang',
        reject_label: 'Afvis',
        export_label: 'Eksportér DOCX',
        resolve_summary: 'Resultat: {status}',
        resolve_status_accepted: 'Accepteret',
        resolve_status_rejected: 'Afvist',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Kunne ikke generere gennemgangen. Prøv igen, eller kontroller revisionens status.',
    },
    privilege_creep: {
        title: 'Forseti — Registrering af rettighedsudvidelse',
        subtitle: 'Periodisk scanning af rolletildelinger. Fund er rådgivende — Forseti tilbagekalder aldrig automatisk.',
        run_label: 'Scan nu',
        running: 'Scanner⬦',
        findings_heading: '{count} fund',
        no_findings: 'Ingen rettighedsudvidelse markeret.',
        col_user: 'Bruger',
        col_job_function: 'Jobfunktion',
        col_severity: 'Alvorsgrad',
        col_unexpected: 'Uventede tilladelser',
        col_remediation: 'Foreslået afhjælpning',
        col_status: 'Status',
        severity_critical: 'Kritisk',
        severity_high: 'Høj',
        severity_medium: 'Mellem',
        severity_low: 'Lav',
        status_open: 'Åben',
        status_dismissed: 'Afvist',
        status_remediated: 'Afhjulpet',
        dismiss_label: 'Afvis',
        remediate_label: 'Markér som afhjulpet',
        note_label: 'Note (valgfri)',
        error_generic: 'Kunne ikke scanne for rettighedsudvidelse. Prøv igen senere.',
        advisory_banner: 'Disse fund er rådgivende. Forseti tilbagekalder aldrig tilladelser automatisk — gennemgå med rolleejeren før du handler.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Overholdelsesregel automatisk kortlægning',
            subtitle: 'Forseti gennemgår en installeret reguleringspakke og foreslår nye eller opdaterede overholdelsesregler baseret på kildeartiklerne.',
            pack_picker_label: 'Reguleringspakke',
            pack_picker_hint: 'Vælg en installeret pakke til at kortlægge.',
            run_label: 'Kort fra pakke',
            running: 'Kortlægning...',
            results_heading: '{count} forslag',
            tokens_label: '≈ {count} tokens',
            no_suggestions: 'Ingen forslag - pakkens indhold er muligvis allerede fuldt dækket af eksisterende regler.',
            kind_new: 'Ny regel',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Ingen ændring nødvendig',
            col_kind: 'Venlig',
            col_title: 'Foreslået titel',
            col_existing: 'Eksisterende regel',
            col_source: 'Kilde',
            proposed_body_label: 'Foreslået organ',
            source_quote_label: 'Kilde citat',
            accept_selected: 'Accepter valgt',
            reject_all: 'Afvis alle',
            accept_all: 'Accepter alle',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'Kunne ikke køre regeltilknytning. Prøv igen, eller tjek pakkekoden.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Sessionsoversigt',
            access_log: 'Adgangslog',
            justification_review: 'Begrundelsesgennemgang',
            investigations: 'Undersøgelser',
            pattern_analysis: 'Mønsteranalyse',
            anchoring_status: 'Forankringsstatus',
            configuration: 'Konfiguration',
            access_policies: 'Adgangspolitikker',
            review_campaigns: 'Gennemgangskampagner',
            evidence_packages: 'Dokumentationspakker',
        },
        session_dashboard: {
            title: 'Sessionsoversigt',
            loading: 'Indlæser oversigtsdata...',
            total_sessions: 'Sessioner i alt',
            active_sessions: 'Aktive sessioner',
            avg_duration: 'Gns. varighed',
            peak_day_sessions: 'Travleste dag',
            auth_method_breakdown: 'Autentificeringsmetodefordeling',
            geo_country_breakdown: 'Geografisk landefordeling',
            recent_session_events: 'Seneste sessionshændelser',
            user_id: 'Bruger-ID',
            session_id: 'Sessions-ID',
            ip_address: 'IP-adresse',
            auth_method: 'Autentificeringsmetode',
            started_at: 'Startet den',
            event_type: 'Hændelsestype',
            all_event_types: 'Alle hændelsestyper',
            filter_by_user: 'Filtrer efter bruger',
            no_active_sessions: 'Ingen aktive sessioner',
            no_session_events: 'Ingen sessionshændelser fundet',
            revoke: 'Tilbagekald',
            revoke_session: 'Tilbagekald session',
            aria_revoke_session: 'Tilbagekald session {user}',
            revoke_reason: 'Årsag',
            revoke_placeholder: 'Angiv en årsag for tilbagekaldelse af denne session...',
            session_label: 'Session:',
            load_failed: 'Kunne ikke indlæse sessionsoversigten. Prøv venligst igen.',
            events_load_failed: 'Kunne ikke indlæse sessionshændelser. Prøv venligst igen.',
            revoke_failed: 'Kunne ikke tilbagekalde session',
            session_events_loaded: '{n} sessionshændelser indlæst',
            event_types: {
                session_started: 'Session startet',
                session_refreshed: 'Session opdateret',
                session_expired: 'Session udløbet',
                session_revoked: 'Session tilbagekaldt',
                session_terminated: 'Session afsluttet',
            },
        },
        access_log: {
            title: 'Adgangslog',
            user_id: 'Bruger-ID',
            resource: 'Ressource',
            action: 'Handling',
            ip_address: 'IP-adresse',
            timestamp: 'Tidsstempel',
            no_logs: 'Ingen adgang log poster fundet.',
            filter_by_user: 'Filtrer efter bruger-ID...',
            load_failed: 'Kunne ikke indlæse adgangslog. Prøv venligst igen.',
            export_success: 'Eksport startet',
            export_failed: 'Kunne ikke eksportere adgangslog',
            record_count: 'post',
            record_count_plural: 'poster',
            unclassified: 'Ikke klassificeret',
            classification_missing: 'Klassifikation mangler',
            classification_level: 'Klassificeringsniveau',
            response_code: 'Svarkode',
            access_surface: 'Adgangskanal',
            api_key: 'API-nøgle',
            classification_levels: {
                public: 'Offentlig',
                internal: 'Internt',
                confidential: 'Fortroligt',
                restricted: 'Begrænset',
                unclassified: 'Uklassificeret',
            },
        },
        justification_review: {
            title: 'Begrundelsesgennemgang',
            user_id: 'Bruger-ID',
            resource: 'Ressource',
            justification: 'Begrundelse',
            submitted_at: 'Indsendt den',
            reviewer: 'Gennemgår',
            no_reviews: 'Ingen begrundelse gennemgange fundet.',
            load_failed: 'Kunne ikke indlæse begrundelser. Prøv venligst igen.',
            review_failed: 'Kunne ikke gennemgå begrundelse',
            decline_failed: 'Kunne ikke afvise adgang',
            justification_count: 'begrundelse',
            justification_count_plural: 'begrundelser',
            reviewed: 'Gennemgået',
            concerning: 'Vedrørende',
            escalated: 'Eskaleret',
            link_investigation_placeholder: 'Tilknyt til en undersøgelse (valgfrit)',
            review_queue: 'Gennemgangskø',
            access_id_label: 'Adgangs-ID',
            access_id_placeholder: 'Adgangslogpost-ID',
            resource_type_label: 'Ressourcetype',
            resource_type_placeholder: 'f.eks. document, database',
            resource_id_label: 'Ressource-ID',
            resource_id_placeholder: 'Ressourceidentifikator',
            reason_placeholder: 'Årsag til at tilgå denne ressource...',
            create_failed: 'Kunne ikke create justification',
            unclassified: 'Ikke klassificeret',
            review_statuses: {
                pending: 'Afventer',
                reviewed: 'Gennemgået',
                concerning: 'Vedrørende',
                escalated: 'Eskaleret',
            },
            reason_codes: {
                business_need: 'Forretningsbehov',
                legal_obligation: 'Lovlig forpligtelse',
                data_subject_request: 'Anmodning om datasubjekt',
                audit_requirement: 'Revisionskrav',
                incident_response: 'Hændelsesrespons',
                research: 'Forskning',
                administrative: 'Administrativ',
                other: 'Andet',
            },
            classification_level: 'Klassificeringsniveau',
            review_decision: 'Gennemgå beslutning',
        },
        investigations: {
            title: 'Undersøgelsesarbejdsområde',
            new: 'Ny undersøgelse',
            subject: 'Emne',
            investigator: 'Efterforsker',
            opened_at: 'Åbnet den',
            findings: 'Fund',
            no_investigations: 'Ingen undersøgelser fundet.',
            load_failed: 'Kunne ikke indlæse undersøgelser. Prøv venligst igen.',
            create_failed: 'Kunne ikke oprette undersøgelse',
            timeline_add_failed: 'Kunne ikke tilføje tidslinjepost',
            link_access_failed: 'Kunne ikke tilknytte adgangs-ID-er',
            link_justification_failed: 'Kunne ikke tilknytte begrundelses-ID-er',
            close_failed: 'Kunne ikke lukke undersøgelse',
            export_success: 'Eksport startet',
            export_failed: 'Kunne ikke eksportere undersøgelse',
            refresh_failed: 'Kunne ikke opdatere undersøgelsesdetalje. Prøv venligst igen.',
            update_failed: 'Kunne ikke opdatere efterforskning',
            investigation_count: 'undersøgelse',
            investigation_count_plural: 'undersøgelser',
            title_placeholder: 'Undersøgelsestitel',
            description_placeholder: 'Detaljeret beskrivelse af undersøgelse...',
            assignee_placeholder: 'Bruger-ID på modtager (valgfrit)',
            timeline_action_placeholder: 'f.eks. Gennemgåede adgangslogge',
            timeline_note_placeholder: 'Valgfri note',
            access_ids_placeholder: 'adgangs-id-1, adgangs-id-2',
            justification_ids_placeholder: 'begrundelses-id-1, begrundelses-id-2',
            findings_placeholder: 'Opsummer undersøgelsens fund...',
            by_actor: 'by',
            close_investigation: 'Luk undersøgelse',
            add_timeline_failed: 'Kunne ikke tilføje tidslinjepost.',
            fetch_failed: 'Kunne ikke hente undersøgelser.',
            change_status: 'Skift undersøgelsesstatus',
            details: 'Undersøgelsesdetaljer',
            link_evidence: 'Link evidens',
            link_justifications: 'Link begrundelser',
            access_ids_label: "Adgangs-ID'er",
            justification_ids_label: 'Begrundelse-IDer',
            access_ids_link_placeholder: 'adgang-id-1, adgangs-id-2',
            justification_ids_link_placeholder: 'begrundelse-id-1, begrundelse-id-2',
            investigations_loaded: '{n} investigations indlæst',
            status_changed_to: 'Status ændret til {status}',
            add_timeline_entry: 'Tilføj tidslinjepost',
            add_access_ids: 'Tilføj adgangs-IDer',
            add_justification_ids: 'Tilføj begrundelse-IDer',
            investigation_types: {
                anomaly_review: 'Anomali gennemgang',
                complaint_driven: 'Klagedrevet',
                scheduled_review: 'Planlagt gennemgang',
                whistleblower_report: 'Whistleblower-rapport',
            },
            severity_labels: {
                low: 'Lav',
                medium: 'Medium',
                high: 'Høj',
                critical: 'Kritisk',
            },
            status_labels: {
                open: 'Åbn',
                in_progress: 'Igangværende',
                pending_review: 'Afventer gennemgang',
                closed_substantiated: 'Lukket (underbygget)',
                closed_unsubstantiated: 'Lukket (udokumenteret)',
                closed_inconclusive: 'Lukket (uendeligt)',
            },
            disposition_labels: {
                substantiated: 'Underbygget',
                unsubstantiated: 'Udokumenteret',
                inconclusive: 'Inkonklusive',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Mønsteranalyse',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Indlæser mønsterdata...',
            anomalies_detected: 'Anomalier registreret',
            patterns: 'Mønstre',
            by_user: 'Efter bruger',
            by_resource: 'Efter ressource',
            time_range: 'Tidsinterval',
            load_failed: 'Kunne ikke analysere mønstre. Prøv venligst igen.',
            top_resources: 'Top 10',
            access_by_hour: 'Adgang efter time på dagen',
            access_by_day: 'Adgang efter ugedag',
            fetch_failed: 'Kunne ikke hente mønsteranalyse.',
            analysis_loaded: 'Mønsteranalyse indlæst for {user}, {n} sessioner fundet',
            date_format_placeholder: 'DD/MM/ÅÅÅÅ',
            access_by_hour_heading: 'Adgang efter tid på dagen',
            access_by_day_heading: 'Adgang efter ugedag',
            by_classification: 'Efter klassifikationsniveau',
            top_accessed_resources: 'Mest tilgåede ressourcer',
            user_access_summary: 'Brugeradgangsoversigt',
            days: {
                sunday: 'Søndag',
                monday: 'Mandag',
                tuesday: 'Tirsdag',
                wednesday: 'Onsdag',
                thursday: 'Torsdag',
                friday: 'Fredag',
                saturday: 'Lørdag',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Forankringsstatus',
            loading: 'Indlæser forankringsdata...',
            anchored: 'Forankret',
            pending: 'Afventer',
            failed: 'Mislykket',
            last_anchor: 'Seneste forankring',
            no_records: 'Ingen anchoring records fundet.',
            status_load_failed: 'Kunne ikke indlæse forankringsstatus. Prøv venligst igen.',
            proofs_load_failed: 'Kunne ikke indlæse adgangsbeviser. Prøv venligst igen.',
            verify_failed: 'Kunne ikke verificere beviskæde. Prøv venligst igen.',
            anchor_failed: 'Kunne ikke forankre adgangsbevis',
            batch_anchor_failed: 'Kunne ikke batchforankre adgangsbeviser',
            proof_count: 'bevis',
            proof_count_plural: 'beviser',
            anchor_single: 'Forankring enkelt',
            anchor_batch: 'Forankring batch',
            access_id_placeholder: 'Indtast adgangs-ID til forankring',
            batch_ids_placeholder: 'adgangs-id-1, adgangs-id-2, adgangs-id-3',
            verify_chain: 'Verificer kæde',
            chain_breaks: 'Kædebrud',
            total_checked: 'Samlet kontrolleret',
            anchor_placeholder: 'Indtast adgangs-ID til forankring',
            fetch_failed: 'Kunne ikke hente forankringsstatus.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Kunne ikke hente beviser.',
            access_id: 'Adgangs-ID',
            chain_sequence: 'Kædesekvens',
            proof_hash_label: 'Bevishash',
            batch_id: 'Batch-ID',
            proofs_loaded: '{n} access proofs indlæst',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Afventer',
                confirmed: 'Bekræftet',
                failed: 'Mislykkedes',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Kæde',
                blockchain_anchor: 'Blockchain anker',
                zkp_stub: 'ZKP Stub',
            },
        },
        configuration: {
            title: 'Konfiguration',
            session_settings: 'Sessionsindstillinger',
            log_retention: 'Logopbevaring (dage)',
            anomaly_threshold: 'Anomalitærskel',
            anchoring_enabled: 'Forankring aktiveret',
            save_config: 'Gem konfiguration',
            load_failed: 'Kunne ikke indlæse konfiguration. Prøv venligst igen.',
            save_success: 'Konfiguration gemt korrekt',
            save_failed: 'Kunne ikke gemme konfiguration. Prøv venligst igen.',
            justification_rules: 'Begrundelsesregler',
            reason_codes: 'Årsagskoder',
            anchoring_settings: 'Forankringsindstillinger',
            anchoring_method: 'Forankringsmetode',
            anchoring_frequency: 'Forankringsfrekvens',
            batch_interval: 'Batchinterval (minutter)',
            eligibility_criteria: 'Berettigelseskriterier',
            add_criterion_placeholder: 'Tilføj berettigelseskriterium...',
            retention_settings: 'Opbevaringsindstillinger',
            standard_retention: 'Standard opbevaring (dage)',
            sensitive_retention: 'Følsom opbevaring (dage)',
            working_hours: 'Arbejdstimer',
            work_start: 'Starttidspunkt',
            work_end: 'Sluttidspunkt',
            timezone: 'Tidszone',
            timezone_placeholder: 'f.eks. Asia/Ho_Chi_Minh',
            working_days: 'Arbejdsdage',
            concurrent_sessions: 'Samtidige sessionsgrænser',
            concurrent_threshold: 'Samtidige sessionstærskel',
            classification_level: 'Klassificeringsniveau',
            prompt_mode: 'Prompttilstand',
            grace_period: 'Frist Periode (s)',
            code_placeholder: 'kode',
            is_custom: 'Tilpasset',
            label_placeholder: 'Etiket',
            description_placeholder: 'Beskrivelse',
            custom: 'Brugerdefineret',
            fetch_failed: 'Kunne ikke hente konfiguration.',
            classification_levels: {
                public: 'Offentlig',
                internal: 'Internt',
                confidential: 'Fortroligt',
                restricted: 'Begrænset',
                unclassified: 'Uklassificeret',
            },
            prompt_modes: {
                reason_only: 'Kun årsag',
                reason_text: 'Årsag + tekst',
                reason_text_supervisor: 'Årsag + Tekst + Supervisor',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Kæde',
                blockchain_anchor: 'Blockchain anker',
                zkp_stub: 'ZKP Stub',
            },
            anchoring_frequencies: {
                realtime: 'Realtid',
                batched: 'Batchet',
                on_demand: 'On Demand',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Adgangspolitikker',
            create_policy: 'Opret adgangspolitik',
            policy_name: 'Politikens navn',
            framework_code: 'Rammeværk',
            select_framework: 'Vælg et rammeværk...',
            review_frequency: 'Gennemgangsfrekvens',
            scope: 'Omfang',
            no_policies_found: 'Ingen adgangspolitikker fundet.',
            policy_created: 'Adgangspolitik oprettet',
            frequency_labels: {
                monthly: 'Månedlig',
                quarterly: 'Kvartalsvis',
                semi_annual: 'Halvårlig',
                annual: 'Årlig',
            },
        },
        review_campaigns: {
            title: 'Adgangsgennemgangskampagner',
            create_campaign: 'Opret gennemgangskampagne',
            reviewer: 'Gennemgår',
            due_date: 'Forfaldsdato',
            certify: 'Certificer',
            no_campaigns_found: 'Ingen adgangsgennemgangskampagner fundet.',
            status_open: 'Åben',
            status_in_review: 'Under gennemgang',
            status_completed: 'Afsluttet',
            campaign_created: 'Gennemgangskampagne oprettet',
            review_certified: 'Adgangsgennemgang certificeret',
            certify_campaign: 'Certificér kampagne {id}',
            policy_id: 'Politik-ID',
            select_policy: 'Vælg en politik...',
            reviewer_ids_label: 'Bedømmere',
            select_reviewers: 'Vælg bedømmere...',
            status_labels: {
                open: 'Åben',
                in_review: 'Under gennemgang',
                in_progress: 'Under udførelse',
                cancelled: 'Annulleret',
                completed: 'Fuldført',
            },
        },
        evidence_packages: {
            title: 'Dokumentationspakker',
            generate_package: 'Generer dokumentationspakke',
            export_type: 'Eksporttype',
            package_type: 'Pakketype',
            framework_scope: 'Rammeværksomfang',
            period: 'Periode',
            generated_by: 'Genereret af',
            generated_at: 'Genereret den',
            no_packages_found: 'Ingen dokumentationspakker fundet.',
            package_generated: 'Evidenspakke genereret',
            export_type_full: 'Fuld eksport',
            export_type_summary: 'Resumé',
            export_type_certification_only: 'Kun certificering',
            package_type_access_review: 'Adgangsgennemgang',
            package_type_investigation: 'Undersøgelse',
            package_type_certification: 'Certificering',
            package_type_authority_request: 'Myndighedsanmodning',
            period_start: 'Periodestart',
            period_end: 'Periodeslut',
            select_frameworks: 'Vælg rammer...',
            completeness_col: 'Fuldstændighed',
            status_col: 'Status',
            generated_by_col: 'Genereret af',
        },
        evidence_package_detail: {
            title: 'Evidenspakkedetaljer',
            not_found: 'Evidenspakke ikke fundet.',
            package_metadata: 'Pakkemetadata',
            status: 'Status',
            item_count: 'Antal elementer',
            disposition: 'Disposition',
            closed_at: 'Lukket',
            completeness_dashboard: 'Fuldstændighedsoversigt',
            no_completeness_data: 'Ingen fuldstændighedsdata tilgængelig.',
            controls_covered: 'kontroller dækket',
            missing: 'Mangler',
            sign_off_tracker: 'Godkendelsessporing',
            stage_framework_owner: 'Rammeejer',
            stage_legal_compliance: 'Jura og Compliance',
            stage_internal_audit: 'Intern revision',
            approved: 'Godkendt',
            returned: 'Returneret',
            pending: 'Afventer',
            approve: 'Godkend',
            return_btn: 'Returner',
            sign_off_submitted: 'Godkendelse indsendt',
            artifacts: 'Artefakter',
            no_artifacts: 'Ingen artefakter fundet.',
            template_id: 'Skabelon-ID',
            description: 'Beskrivelse',
            classification: 'Klassifikation',
            evidence_type: 'Evidenstype',
            framework_section: 'Rammeafsnit',
            history: 'Godkendelseshistorik',
            expand: 'Udvid',
            collapse: 'Skjul',
            action: 'Handling',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
            comment: 'Kommentar',
            close_case: 'Luk sag',
            confirm_close: 'Bekræft lukning',
            closure_notes: 'Lukningsnoter',
            disposition_compliant: 'Overensstemmende',
            disposition_non_compliant: 'Ikke-overensstemmende',
            disposition_partial: 'Delvis',
            case_closed: 'Evidenspakke lukket',
            export_package: 'Eksporter pakke',
            export_complete: 'Evidenspakke eksporteret',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Oversigt',
            properties: 'Egenskaber',
            groups: 'Grupper',
            bindings: 'Bindinger',
            change_requests: 'Ændringsanmodninger',
            comparison: 'Sammenligning',
            inconsistencies: 'Uoverensstemmelser',
            snapshot_schedules: 'Øjebliksbilledplaner',
            golden_templates: 'Guldskabeloner',
            evidence_packs: 'Dokumentationspakker',
            audit: 'Revision',
        },
        select_property: 'Vælg ejendom⬦',
        select_policy: 'Vælg politik⬦',
        select_properties: 'Vælg ejendomme⬦',
        select_target_properties: 'Vælg målegenskaber⬦',
        select_owner: 'Vælg ejer⬦',
        select_technical_contact: 'Vælg teknisk kontakt⬦',
        property_statuses: {
            active: 'Aktiv',
            inactive: 'Inaktiv',
            pending: 'Afventende',
            decommissioned: 'Nedlagt',
        },
        dashboard: {
            title: 'Tværgående egenskabsoversigt',
            loading: 'Indlæser oversigtsdata...',
            total_properties: 'Samlede egenskaber',
            active_properties: 'Aktive egenskaber',
            snapshot_coverage: 'Snapshot-dækning',
            properties_covered: 'egenskaber dækket',
            total_bindings: 'Bindinger i alt',
            deployed_bindings: 'Implementeret',
            pending_bindings: 'Afventer implementering',
            deployment_progress: 'Implementeringsfremskridt',
            on_target: 'På mål',
            below_target: 'Under mål',
            target_label: 'Mål',
            total_groups: 'Samlede grupper',
            pending_changes: 'Afventende ændringer',
            inconsistencies: 'Uoverensstemmelser',
            recent_activity: 'Seneste aktivitet',
            no_recent_activity: 'Ingen seneste aktivitet.',
            fetch_error: 'Kunne ikke indlæse oversigt. Prøv igen.',
        },
        properties: {
            title: 'Egenskabsregister',
            new: 'Ny egenskab',
            edit: 'Rediger egenskab',
            property_name: 'Navn',
            property_type: 'Type',
            identifier: 'Identifikator',
            environment: 'Miljø',
            owner_id: 'Ejer-ID',
            technical_contact_id: 'Teknisk Kontakt ID',
            decommission: 'Nedlæg',
            confirm_decommission: 'Er du sikker på, at du vil nedlægge denne egenskab?',
            no_properties: 'Ingen egenskaber fundet.',
            all_environments: 'Alle miljøer',
            all_types: 'Alle typer',
            cmp_instance_id: 'CMP Forekomst-id',
            deployment_date: 'Implementeringsdato',
            identifier_placeholder: 'f.eks. www.example.com eller com.example.app',
            owner_name: 'Ejer',
            technical_contact_name: 'Teknisk kontakt',
            sensitive_data_categories: 'Følsomme datakategorier',
            sensitive_data_categories_placeholder: 'Vælg følsomme datakategorier',
            requires_enhanced_review: 'Kræver forbedret gennemgang',
            sensitive_review_status: 'Gennemgang af følsomme data',
            sensitive_review_pending: 'Afventer gennemgang',
            sensitive_review_approved: 'Godkendt',
            sensitive_review_rejected: 'Afvist',
            sensitive_review_submit: 'Send anmeldelse',
            sensitive_review_approve: 'Godkend',
            sensitive_review_reject: 'Afvis',
            sensitive_review_notes: 'Gennemgå noter',
            sensitive_review_notes_placeholder: 'Indtast anmeldelsesnotater...',
            record_withdrawal: 'Record tilbagetrækning',
            withdrawal_subject_id: 'Emne-id',
            withdrawal_consent_id: 'Samtykke-id',
            withdrawal_reason: 'Årsag',
            withdrawal_downstream_systems: 'Nedstrøms systemer',
            withdrawal_downstream_placeholder: 'system-1, system-2',
            withdrawal_success: 'Tilbagetrækningen blev spredt med succes.',
            withdrawal_error: 'Det lykkedes ikke at udbrede tilbagetrækningen.',
            withdrawal_data_subject_id: 'Datasubjekt-id',
            withdrawal_data_subject_id_placeholder: 'f.eks. usr_abc123',
            withdrawal_consent_record_id: 'ID for samtykkeregistrering',
            withdrawal_consent_record_id_placeholder: 'f.eks. consent_xyz789',
            withdrawal_purposes: 'Tilbagetrukket Formål',
            withdrawal_purposes_placeholder: 'Vælg formål for at trække samtykke tilbage til...',
            withdrawal_withdrawn_at: 'Tilbagetrækningsdato',
            withdrawal_tasks_created: 'Opgaver oprettet',
            withdrawal_affected_bindings: 'Berørte bindinger',
            withdrawal_affected_activities: 'Berørte behandlingsaktiviteter',
            withdrawal_history: 'Tilbagetrækningshistorie',
        },
        groups: {
            title: 'Egenskabsgrupper',
            new: 'Ny gruppe',
            group_name: 'Gruppenavn',
            member_count: 'Medlemmer',
            no_groups: 'Ingen egenskab grupper fundet.',
            policies: 'Politikker',
            confirm_delete: 'Er du sikker på, at du vil slette denne gruppe?',
        },
        bindings: {
            title: 'Bindingsmatrix',
            property: 'Egenskab',
            purpose: 'Formål',
            legal_basis: 'Retsgrundlag',
            bound: 'Bundet',
            no_bindings: 'Ingen bindings fundet.',
            bind_policy: 'Bind politik',
            bulk_bind: 'Bulk Bind',
            bind_policy_to_property: 'Bind politik til ejendom',
            bulk_bind_policy: 'Massebindingspolitik',
            unbound: 'Ubundet',
            policy_id: 'Politik-ID',
            version_placeholder: 'f.eks. 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Indbinding',
            property_name: 'Egenskabsnavn',
            policy_name: 'Politiknavn',
            bindings_list: 'Bindingsliste',
            binding_actions: {
                start_deploy: 'Start udrulning',
                mark_deployed: 'Markér som udrullet',
                rollback: 'Rul tilbage',
            },
        },
        change_requests: {
            title: 'Ændringsanmodningsproces',
            new: 'Ny Ændrings Anmodning',
            requester: 'Anmoder',
            change_type: 'Ændringstype',
            property: 'Egenskab',
            submitted_at: 'Indsendt den',
            no_requests: 'Ingen ændrings anmodninger fundet.',
            fetch_pipeline_error: 'Kunne ikke hente pipeline. Prøv venligst igen.',
            fetch_list_error: 'Kunne ikke hente ændringsanmodninger. Prøv venligst igen.',
            create_error: 'Kunne ikke oprette ændringsanmodning. Prøv venligst igen.',
            action_error: 'Kunne ikke udføre handling. Prøv venligst igen.',
            approver_name: 'Godkender',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'Alle',
            view_my_properties: 'Mine Egenskaber',
            stage_requests: '{stage}-anmodninger',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Sammenligningsarbejdsområde',
            select_properties: 'Vælg egenskaber til sammenligning',
            compare: 'Sammenlign',
            no_comparison: 'Vælg mindst to egenskaber til sammenligning.',
            fetch_error: 'Kunne ikke hente sammenligningsrapporter. Prøv venligst igen.',
            run_error: 'Kunne ikke køre sammenligning. Prøv venligst igen.',
            save_notes_error: 'Kunne ikke gemme noter. Prøv venligst igen.',
            scan_complete: 'Inkonsistensscanning fuldført.',
            scan_error: 'Kunne ikke køre inkonsekvensscanning. Prøv venligst igen.',
            min_two_properties: 'Angiv venligst mindst 2 egenskabs-ID-er til sammenligning.',
            view_inconsistencies: 'Se inkonsistenser',
            click_to_select: 'Klik for at vælge to eller flere egenskaber',
            no_properties_available: 'Ingen egenskaber tilgængelige',
            no_matching_properties: 'Ingen matchende egenskaber',
            select_at_least_two_props: 'Vælg mindst to egenskaber fra rullelisten ovenfor.',
            no_differences_found: 'Ingen forskelle fundet mellem de valgte egenskaber.',
            found_inconsistencies: 'Fandt {count} inkonsistenser på tværs af {properties} egenskaber og {bindings} bindinger.',
        },
        inconsistencies: {
            title: 'Liste over uoverensstemmelser',
            property: 'Egenskab',
            field: 'Felt',
            expected: 'Forventet',
            actual: 'Faktisk',
            severity: 'Alvorlighed',
            no_inconsistencies: 'Ingen inkonsistenser fundet.',
            trend: 'Inkonsekvenstendens',
            trend_chart_label: 'Inkonsekvenstrenddiagram, der viser antal over tid',
            acknowledge: 'Bekræft',
            resolve: 'Løs',
            suppress: 'Undertryk',
            cr_id_placeholder: 'CR-ID',
            view_all: 'Alle',
            view_my_properties: 'Mine Egenskaber',
            view_dpo_review: 'DPO Gennemgangskø',
            change_request_id_required: 'Ændring af anmodnings-id er påkrævet for at løse denne inkonsekvens.',
        },
        snapshot_schedules: {
            title: 'Øjebliksbilledplaner',
            new: 'Ny tidsplan',
            schedule_name: 'Tidsplannavn',
            frequency: 'Frekvens',
            next_run: 'Næste kørsel',
            last_run: 'Seneste kørsel',
            no_schedules: 'Ingen øjebliksbillede tidsplaner fundet.',
            target_type: 'Måltype',
            target_id: 'Mål-id',
            custom_frequency_days: 'Tilpasset frekvens (dage)',
            time_of_day: 'Tid på dagen',
            time: 'Tid',
            retention_days: 'Retention (dage)',
            execute_now: 'Udfør nu',
            placeholder_days: 'f.eks. 10',
            target_name: 'Målnavn',
        },
        golden_templates: {
            title: 'Guldskabeloner',
            new: 'Ny skabelon',
            template_name: 'Skabelonnavn',
            property_type: 'Egenskabstype',
            no_templates: 'Ingen gylden skabeloner fundet.',
            applicable_group_ids: "Gældende gruppe-id'er (kommasepareret)",
            applicable_groups: 'Gældende grupper',
            purposes: 'Formål',
            legal_bases: 'Retsgrundlag',
            banner_config: 'Banner Config',
            consent_settings: 'Samtykkeindstillinger',
            group_ids_placeholder: 'gruppe-id-1, gruppe-id-2',
            apply_to_property: 'Anvend på Egenskab',
            apply_to_group: 'Anvend på Gruppe',
            apply_success: 'Skabelon anvendt succesfuldt.',
            applications: 'Anvendelseshistorik',
            no_applications: 'Ingen skabelonanvendelser endnu.',
            no_configuration_details: 'Skabelonen har ingen konfigurationsdetaljer. Rediger skabelonen for at tilføje formål, retsgrundlag, bannerindstillinger eller samtykkeindstillinger.',
        },
        rollouts: {
            title: 'Skabelonudrulninger',
            rollout_status: 'Udrulningsstatus',
            confirmed: 'Bekræftet',
            pending: 'Afventer',
            failed: 'Mislykket',
            confirm: 'Bekræft Egenskab',
            confirm_success: 'Egenskab bekræftet i udrulning.',
            can_close: 'Alle egenskaber bekræftet — udrulning fuldført',
            cannot_close: 'Afventer egenskabsbekræftelser',
            no_rollouts: 'Ingen udrulninger fundet.',
            in_progress: 'I gang',
            completed: 'Fuldført',
            cancelled: 'Annulleret',
            reject: 'Afvis Egenskab',
            reject_success: 'Egenskab afvist fra udrulning.',
            confirm_all: 'Bekræft Alle',
            reject_all: 'Afvis Alle',
            confirm_label: 'Bekræft udrulning for',
            reject_label: 'Afvis udrulning for',
            confirm_all_label: 'Bekræft Alle Ventende Udrulninger',
            reject_all_label: 'Afvis Alle Ventende Udrulninger',
            no_properties_in_rollout: 'Denne udrulning har ingen egenskaber.',
        },
        analytics: {
            title: 'Egenskabsanalyse',
            opt_in_rate: 'Tilmeldingsrate',
            opt_out_rate: 'Frameldingsrate',
            withdrawal_rate: 'Tilbagetrækningsrate',
            total_consents: 'Samlede samtykker',
            total_withdrawals: 'Samlede tilbagetrækninger',
            compliance_score: 'Overholdelsesscore',
            non_compliant: 'Ikke-overensstemmende egenskaber',
            no_non_compliant: 'Alle egenskaber er i overensstemmelse.',
            threshold: 'Overholdelsestærskel',
            property_id: 'Ejendoms-id',
            compliant: 'Overensstemmende',
            non_compliant_status: 'Ikke-kompatibel',
            compliance_help_summary: 'Hvordan beregnes compliance-score, og hvordan retter jeg det?',
            compliance_help_intro: 'En ejendom er markeret som ikke-kompatibel, når dens score er under 80 %. Scoren udregnes som:',
            compliance_help_with_template_label: 'Hvis der anvendes en gylden skabelon:',
            compliance_help_with_template_text: '% af konfigurationsnøgler (formål, legal_bases, banner_config, consent_settings), der matcher skabelonen nøjagtigt.',
            compliance_help_no_template_label: 'Hvis der ikke anvendes en gylden skabelon:',
            compliance_help_no_template_text: '% af bindinger (samtykketyper tildelt ejendommen) med deployment_status = implementeret. En egenskab uden bindinger er som standard 100 %.',
            compliance_help_fix_intro: 'For at fjerne en ejendom fra denne liste skal du gøre et af følgende:',
            compliance_help_fix_deploy_label: 'Udsæt dens bindinger',
            compliance_help_fix_deploy_text: 'Åbn siden Binding Matrix, og indstil hver bindings implementeringsstatus til implementeret.',
            compliance_help_fix_template_label: 'Anvend en gylden skabelon',
            compliance_help_fix_template_text: 'brug Anvend på gruppe på skabelonen, og bekræft derefter udrulningen af denne ejendom. Sørg for, at ejendommens konfiguration matcher skabelonen.',
            compliance_help_fix_decommission_label: 'Afvikle ejendommen',
            compliance_help_fix_decommission_text: 'brug knappen Dekommissionering i ejendomsdetaljemodalen, hvis den ikke længere er i brug.',
            compliance_help_fix_no_bindings_label: 'Fjern alle bindinger',
            compliance_help_fix_no_bindings_text: 'hvis en ejendom ikke har nogen skabelon og ingen bindinger, scorer den 100 % som standard.',
        },
        evidence_packs: {
            title: 'Dokumentationspakker',
            new: 'Ny pakke',
            pack_name: 'Pakkenavn',
            properties: 'Egenskaber',
            generated_at: 'Genereret den',
            no_packs: 'Ingen dokumentation pakker fundet.',
            generate: 'Generer',
            content_type_consent_records: 'Samtykkeposter',
            content_type_policy_versions: 'Politikversioner',
            content_type_binding_history: 'Bindingshistorik',
            content_type_change_requests: 'Ændringsanmodninger',
            content_type_inconsistency_reports: 'Inkonsistensrapporter',
            content_type_snapshot_data: 'Øjebliksbillededata',
            content_type_audit_trail: 'Revisionsspor',
        },
        audit: {
            title: 'Revision af samtykkeegenskaber',
            event_type: 'Hændelsestype',
            entity_type: 'Enhedstype',
            entity_id: 'Enheds-ID',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
            no_events: 'Ingen revision hændelser fundet.',
            entity_type_property: 'Egenskab',
            entity_type_group: 'Gruppe',
            entity_type_binding: 'Indbinding',
            entity_type_change_request: 'Ændringsanmodning',
            entity_type_golden_template: 'Gylden skabelon',
            entity_type_snapshot_schedule: 'Snapshot-plan',
            entity_type_inconsistency: 'Inkonsistens',
            entity_type_evidence_pack: 'Bevispakke',
            action_created: 'Oprettet',
            action_updated: 'Opdateret',
            action_deleted: 'Slettet',
            action_decommissioned: 'Deaktiveret',
            action_status_changed: 'Status ændret',
            action_submitted: 'Indsendt',
            action_approved: 'Godkendt',
            action_rejected: 'Afvist',
            action_deployed: 'Implementeret',
            action_rolled_back: 'Rullet tilbage',
            action_paused: 'Sat på pause',
            action_resumed: 'Genoptaget',
            action_cancelled: 'Annulleret',
            action_acknowledged: 'Bekræftet',
            action_resolved: 'Løst',
            action_suppressed: 'Undertrykt',
            action_executed: 'Udført',
        },
        property_types: {
            website: 'Websted',
            mobile_app: 'Mobilapp',
            ott: 'OTT',
            iot_device: 'IoT-enhed',
            kiosk: 'Kiosk',
            api_service: 'API-tjeneste',
            sdk: 'SDK',
        },
        environments: {
            production: 'Produktion',
            staging: 'Iscenesættelse',
            development: 'Udvikling',
            testing: 'Test',
        },
        group_types: {
            brand: 'Mærke',
            region: 'Region',
            product_line: 'Produktlinje',
            business_unit: 'Forretningsenhed',
            custom: 'Brugerdefineret',
        },
        target_types: {
            property: 'Egenskab',
            group: 'Gruppe',
            all: 'Alle',
        },
        frequencies: {
            hourly: 'Hver time',
            daily: 'Dagligt',
            weekly: 'Ugentligt',
            monthly: 'Månedligt',
            custom: 'Brugerdefineret',
        },
        binding_statuses: {
            current: 'Aktuel',
            outdated: 'Forældet',
            pending: 'Afventer',
            deploying: 'Implementerer',
            rolled_back: 'Rullet tilbage',
        },
        sensitive_categories: {
            health: 'Sundhedsdata',
            biometric: 'Biometriske data',
            genetic: 'Genetiske data',
            racial_ethnic: 'Race/etnisk oprindelse',
            political_opinions: 'Politiske meninger',
            religious_beliefs: 'Religiøse overbevisninger',
            trade_union: 'Fagforeningsmedlemskab',
            sexual_orientation: 'Seksuel orientering',
            criminal_records: 'Kriminaloptegnelser',
            financial: 'Finansielle data',
            children: 'Børns data',
            geolocation: 'Præcis Geolocation',
        },
        export_csv: 'Eksporter CSV',
        exporting: 'Eksporterer...',
        page_x_of_y: 'Side {page} af {total}',
        regulatory: {
            capture_method_guidance: 'PDPL kræver samtykkebevis for at matche indsamlingskanalen. Ejendomstyper kortlægges til samtykkeindsamlingsmetoder: website/OTT = bannerbaseret samtykke, mobilapp/SDK = samtykkestrøm i appen, kiosk = fysisk/samtykkefangst på skærmen, API service = programmatisk samtykke, IoT-enhed = samtykkemekanisme på enhedsniveau.',
            dashboard_metrics_info: 'Dette dashboard giver DPO en enkelt visning af samtykkestilling på tværs af alle digitale ejendomme. Nøglemålinger omfatter: bindingsdækningsprocent (implementeret vs. samlede bindinger), skabelonoverholdelse via inkonsistenswidgetten, afventende ændringsanmodninger, der kræver gennemgang, og ejendomsstatusopdeling, der viser ejendomme med aktive, inaktive eller dekommissionerede tilstande. Ejendomme med følsomme datakategorier spores via ejendomsregistret.',
            change_request_impact_info: 'Ændringsanmodninger, der påvirker samtykkeegenskaber, skal dokumentere regulatorisk indvirkning: hvilke PDPL-krav, der berøres, om ændringen påvirker følsom databehandling (art. 2.4), og om ændringen kræver fornyet samtykke fra de registrerede. Konsekvensoversigten i hver ændringsanmodning beskriver PDPL lovmæssige referencer for revisionsbevis.',
            outdated_binding_warning: 'Bindinger med status "forældet" angiver, at en ejendom kører en politikversion, der ikke er aktuel. Disse egenskaber afspejler muligvis ikke de seneste PDPL krav eller opdateringer af organisationspolitikker. Gennemgå og opdater forældede bindinger omgående for at opretholde PDPL overholdelse på tværs af alle samtykkeegenskaber.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Oversigt',
            market_packs: 'Markedspakker',
            language_packs: 'Sprogpakker',
            frameworks: 'Regulatoriske rammer',
            mappings: 'Modultilknytninger',
            submissions: 'Pakkeindsendelser',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Oversættelsesforhåndsvisning',
            audit: 'Revisionsspor',
        },
        dashboard: {
            title: 'Lokaliseringsoversigt',
            loading: 'Indlæser dashboarddata...',
            active_market_packs: 'Aktive markedspakker',
            published_packs_available: 'Udgivne markedspakker tilgængelige',
            language_packs: 'Sprogpakker',
            total_language_packs: 'Sprogpakker registreret i alt',
            active_regulatory_frameworks: 'Aktive regulatoriske rammeværker',
            frameworks_active: 'Rammeværker med aktive status',
            pending_pack_submissions: 'Ventende Pakke Indsendelser',
            submissions_awaiting_review: 'Indsendelser afventer gennemgang',
            recent_audit_events: 'Seneste Revision Hændelser',
            no_recent_audit: 'Ingen seneste revision hændelser.',
            event_type: 'Hændelsestype',
            entity_type: 'Enhedstype',
            entity_id: 'Enheds-ID',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
        },
        market_packs: {
            title: 'Markedspakkekatalog',
            new: 'Ny Marked Pakke',
            pack_name: 'Pakkenavn',
            market: 'Marked',
            locale: 'Lokalitet',
            no_packs: 'Ingen marked pakker fundet.',
        },
        language_packs: {
            title: 'Sprogpakker',
            new: 'Ny Sprog Pakke',
            language: 'Sprog',
            locale_code: 'Lokalitetskode',
            completeness: 'Fuldstændighed',
            completeness_detail: 'Fuldstændighedsdetaljer',
            overall_completeness: 'Samlet fuldstændighed',
            total_keys: 'Totalnøgler',
            translated_keys: 'Oversatte nøgler',
            missing_keys: 'Manglende nøgler',
            namespace_breakdown: 'Navneområdeopdeling',
            namespace: 'Navnerum',
            translated: 'Oversat',
            no_packs: 'Ingen sprog pakker fundet.',
            install_language_pack: 'Installer sprogpakke',
            available_packs: 'Tilgængelige sprogpakker',
            install: 'Installer',
            all_installed: 'Alle sprogpakker er allerede installeret.',
            install_success: 'Sprogpakke installeret.',
            market: 'Marked',
            featured: 'Fremhævet',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Regulatoriske rammer',
            new: 'Nyt rammeværk',
            framework_name: 'Rammeværksnavn',
            jurisdiction: 'Jurisdiktion',
            effective_date: 'Ikrafttrædelsesdato',
            no_frameworks: 'Ingen regulatoriske rammeværker fundet.',
            view_requirements: 'Vis krav',
            hide_requirements: 'Skjul krav',
            show_legacy: 'Vis ældre rammeværker',
            effective_until: 'Gyldig indtil',
            effective_on: 'Gyldig fra',
            replaced_by: 'Erstattet af',
            replaces_label: 'Erstatter',
            status_active: 'Aktiv',
            status_draft: 'Udkast',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Ældre',
            status_upcoming: 'Kommende',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Overvågningsliste',
            watchlist_empty: 'Ingen udkast eller kommende rammer',
            watchlist_empty_desc: 'Rammer med status "draft" eller "coming" vises her til overvågning.',
            expected_effective: 'Forventes effektiv',
            effective_date_tbd: 'Ikrafttrædelsesdato: TBD',
            activate: 'Aktiver',
            ai_law_title: 'Vietnam AI Lov',
            ai_risk_classification: 'AI Risikoklassificering',
            ai_system_dossier: 'AI Systemdossier',
            ai_labeling_checklist: 'AI Mærkning og offentliggørelse',
            ai_incident_report: 'AI Hændelsesrapport',
            ai_impact_assessment: 'AI Konsekvensanalyse',
            ai_sandbox_application: 'Sandkasse applikation',
            ai_sandbox_monitoring: 'Sandkasse overvågning',
            ai_sandbox_completion: 'Sandkasse færdiggørelse',
            ai_submission_tracker: 'Submission Tracker',
            ai_evidence_capture: 'Bevisfangst',
            risk_level_minimal: 'Minimal risiko',
            risk_level_limited: 'Begrænset risiko',
            risk_level_high: 'Høj risiko',
            risk_level_unacceptable: 'Uacceptabel risiko',
            submission_status_draft: 'Udkast',
            submission_status_submitted: 'Indsendt',
            submission_status_returned: 'Returneret til ændring',
            submission_status_accepted: 'Accepteret',
            submission_status_rejected: 'Afvist',
            submission_status_closed: 'Lukket',
            ecommerce_law_title: 'Vietnam lov om e-handel',
            ecommerce_platform_classification: 'Platform klassificering',
            ecommerce_seller_register: 'Sælgerregister',
            ecommerce_complaint_register: 'Klageregister',
            ecommerce_takedown_register: 'Nedtagningsregister',
            ecommerce_submission_tracking: 'Indsendelsessporing',
            ecommerce_dossier_checklist: 'Dossier tjekliste',
            ecommerce_cross_border: 'Grænseoverskridende overholdelse',
            ecommerce_trustmark: 'Trustmark Program',
            ecommerce_notification: 'Meddelelse',
            ecommerce_registration: 'Registrering',
            ecommerce_termination: 'Opsigelse',
            ecommerce_reporting_readiness: 'Indberetningsparathed',
            ecommerce_evidence_capture: 'Bevisfangst',
            submission_status_approved: 'Godkendt',
            cybersec_law_2025_title: 'Vietnam Cybersecurity Law (2025)',
            cybersec_system_classification: 'Systemklassifikation',
            cybersec_risk_assessment: 'Risikovurdering',
            cybersec_incident_report: 'Hændelsesrapport',
            cybersec_authority_cooperation: 'Myndighedssamarbejde',
            cybersec_data_localization: 'Datalokalisering',
            cybersec_vendor_security: 'Sælgersikkerhed',
            cybersec_submission_tracker: 'Submission Tracker',
            cybersec_evidence_capture: 'Bevisfangst',
            cybersec_dossier_checklist: 'Dossier tjekliste',
            cybersec_classification_general: 'Generelt',
            cybersec_classification_important: 'Vigtigt',
            cybersec_classification_critical: 'Kritisk',
            data_law_title: 'Vietnams datalov',
            data_inventory: 'Dataopgørelse',
            data_classification: 'Dataklassifikation',
            data_sharing: 'Datadeling',
            data_governance: 'Data Governance',
            data_submission_tracker: 'Submission Tracker',
            data_evidence_capture: 'Bevisfangst',
            data_dossier_checklist: 'Dossier tjekliste',
            telecom_law_title: 'Vietnam telekommunikationslov',
            telecom_authority_request: 'Myndighedsanmodning',
            telecom_disclosure: 'Afsløring',
            telecom_confidentiality: 'Fortrolighed',
            telecom_submission_tracker: 'Submission Tracker',
            telecom_evidence_capture: 'Bevisfangst',
            telecom_dossier_checklist: 'Dossier tjekliste',
            pdpl_law_title: 'Vietnam lov om beskyttelse af persondata',
            pdpl_breach_notification: 'Underretning om brud',
            pdpl_dpo_notification: 'DPO Besked om aftale',
            pdpl_data_subject_notification: 'Underretning af den registrerede',
            pdpl_submission_tracker: 'Submission Tracker',
            pdpl_evidence_capture: 'Bevisfangst',
            pdpl_dossier_checklist: 'Dossier tjekliste',
            pdpl_authority_filing: 'Myndighedsarkivering',
            pdpl_mops_filing: 'MoPS-arkivering',
        },
        mappings: {
            title: 'Modultilknytninger',
            new: 'Ny kortlægning',
            module: 'Modul',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Samtykke',
            module_dpia: 'DPIA',
            module_incident: 'Hændelse',
            module_rights_request: 'Rettighedsanmodning',
            module_data_mapping: 'Datakortlægning',
            module_classification: 'Klassifikation',
            module_monitoring: 'Overvågning',
            module_governance: 'Governance',
            module_vendor: 'Sælger',
            module_risk: 'Risiko',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Markedspakke',
            mapping_status: 'Kortlægningsstatus',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Ingen modul kortlægninger fundet.',
            incomplete_entries_warning: '{count} kortlægning(er) skjult på grund af manglende framework-ID, krav-ID eller modul.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Pakkeindsendelser',
            new: 'Ny indsendelse',
            submitter: 'Indsender',
            pack: 'Pakke',
            submitted_at: 'Indsendt den',
            no_submissions: 'Ingen pakke indsendelser fundet.',
            installed_sector_overlays_title: 'Sektoroverlays ({count})',
            installed_sector_overlays_aria: 'Installerede sektoroverlays',
            installed_col_name: 'Navn',
            installed_col_code: 'Kode',
            installed_col_sector: 'Sektor',
            installed_col_mode: 'Tilstand',
            installed_col_status: 'Status',
            installed_refresh: 'Opdater',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Oversættelsesforhåndsvisning',
            select_locale: 'Vælg lokalitet',
            select_module: 'Vælg modul',
            key: 'Key',
            value: 'Værdi',
            no_translations: 'Ingen oversættelser fundet.',
        },
        audit: {
            title: 'Lokalisering Revision Spor',
            event_type: 'Hændelsestype',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Enhedstype',
            entity_id: 'Enheds-ID',
            entity_name: 'Enhedsnavn',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
            payload: 'Nyttelast',
            collapse_payload: 'Skjul nyttelastdetaljer',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Udvid nyttelastdetaljer',
            collapse: 'Skjul',
            expand: 'Udvid',
            all_entity_types: 'Alle enhedstyper',
            no_events: 'Ingen revision hændelser fundet.',
        },
        market_packs_detail: {
            installed_at: 'Installeret den',
            pack_details_label: 'Pakkedetaljer',
            market_code: 'Markedskode',
            supported_locales: 'Understøttede lokaliteter',
            min_platform_version: 'Min. platformversion',
            pack_information: 'Pakkeinformation',
            pack_contents: 'Pakkeindhold',
            included_frameworks: 'Inkluderede regelsæt',
            pack_artifacts: 'Pakkeartefakter',
            no_artifacts: 'Ingen artefakter fundet for denne pakke',
            pack_not_found: 'Pakke ikke fundet',
        },
        mappings_form: {
            framework_id: 'Rammeværks-ID',
            requirement_id: 'Krav-ID',
            module_label: 'modul',
            mapping_type: 'Tilknytningstype',
            configuration_json: 'Konfiguration (JSON)',
            conflict_resolution: 'Konfliktløsning',
            is_override: 'Er tilsidesættelse',
            all_modules: 'Alle moduler',
            all_mapping_types: 'Alle tilknytningstyper',
        },
        submissions_detail: {
            submission_id: 'Indsendelses-ID',
            validation: 'Validering',
            reviewers: 'Bedømmere',
            start_review: 'Start gennemgang',
            hide_comments: 'Skjul kommentarer',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Gennemgangskommentarer',
            loading_comments: 'Indlæser kommentarer...',
            no_comments: 'Ingen kommentarer endnu.',
            add_comment: 'Tilføj kommentar',
            reject_submission: 'Afvis indsendelse',
            reason_for_rejection: 'Årsag til afvisning',
            reviewer_ids: 'Bedømmer-IDer (kommaseparerede)',
            submit_new_pack: 'Indsend ny pakke',
            pack_type: 'Pakketype',
            market_code: 'Markedskode',
            target_locales: 'Mållokaliteter',
            pack_file: 'Pakkefil',
            pack_format_info: 'Pakkeformat: Markedspakker skal indeholde lokalitetsspecifikke JSON filer med navneområde/nøgleoversættelser. Sprogpakker skal give oversættelser til de angivne mållokaliteter. Se dokumentation for detaljeret skema.',
            accepted_formats: 'Accepterede formater: .json eller .zip. JSON filer skal indeholde et fladt eller navneafstand objekt af oversættelsesnøgleværdipar. ZIP-filer skal indeholde en eller flere JSON lokalitetsfiler.',
            comma_separated_locales: 'Kommaseparerede lokalkoder for denne sprogpakke',
            confirm_approval: 'Bekræft godkendelse',
            approve_message: 'Godkend denne pakkeindgivelse?',
            submission_label: 'Indsendelse',
            all_pack_types: 'Alle pakketyper',
            passed: 'bestået',
            failed_label: 'fejlet',
            market_code_required: 'Markedskode er påkrævet',
            version_required: 'Version er påkrævet',
            pack_type_regulatory: 'Regulatorisk',
            framework_code: 'Rammeværkskode',
            jurisdiction_code: 'Jurisdiktionskode',
            pack_source: 'Kilde',
            pack_source_manual: 'Manuel',
            pack_version: 'Pakkeversion',
            install_regulatory_pack: 'Installér regulatorisk pakke',
            regulatory_pack_desc: 'Upload en regulatorisk rammepakke (.zip)',
            framework_required: 'Rammeværkskode er påkrævet',
            jurisdiction_required: 'Jurisdiktionskode er påkrævet',
        },
        catalog_detail: {
            packs_installed_of: 'af {total} pakker installeret',
            install_all: 'Installér alle',
            installing: 'Installerer...',
            removing: 'Fjerner...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Kræver',
            tab_catalog: 'Katalog',
            tab_installed: 'Installeret',
            details: 'Detaljer',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Kildelokalitet',
            target_locale: 'Mållokalitet',
            locales_must_differ: 'Kilde- og mållokalitet skal være forskellige.',
            all_keys_translated: 'Alle nøgler oversat',
            keys_missing: '{count} nøgle(r) mangler',
            show_missing_only: 'Vis kun manglende',
            namespace: 'Navnerum',
            source: 'Kilde',
            target: 'Mål',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Rammeværker',
            tab_watchlist: 'Overvågningsliste',
            authority: 'Myndighed',
            requirements_count: 'Krav',
            all_types: 'Alle typer',
            collapse_all: 'Skjul alle',
            expand_all: 'Udvid alle',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Filtreret til',
            clear_filter: 'Ryd filter',
            no_requirements: 'Ingen krav fundet for dette rammeværk.',
            requirements_label: 'Krav',
            status_updated: 'Status opdateret',
            overview_tab: 'Oversigt',
            forms_tab: 'Formularer',
            procedures_tab: 'Procedurer',
            fines_risks_tab: 'Bøder og risici',
            deadlines_tab: 'Frister',
            guidance_tab: 'Vejledning',
            guidance_empty: 'Intet vejledningsindhold for denne ramme.',
            guidance_loading: 'Indlæser vejledning...',
            guidance_section: 'Vejledningssektion',
            guidance_view: 'Se vejledning',
            guidance_map_to_workflow: 'Kort til Workflow',
            guidance_link_evidence: 'Linkbeviser',
            guidance_filter_type: 'Type',
            guidance_filter_category: 'Kategori',
            guidance_filter_module: 'modul',
            guidance_filter_status: 'Status',
            guidance_filter_search: 'Søgevejledning',
            guidance_filter_all: 'Alle',
            guidance_table_reference: 'Reference',
            guidance_table_name: 'Vejledende navn',
            guidance_table_type: 'Type',
            guidance_table_category: 'Kategori',
            guidance_table_effective_date: 'Ikrafttrædelsesdato',
            guidance_table_modules: 'Relaterede moduler',
            guidance_table_actions: 'Handlinger',
            guidance_reference_only_banner: 'Kun reference – gælder for {{sector}} sektor, men ikke for din organisation.',
            guidance_open_official_source: 'Åbn officiel kilde',
            guidance_no_detail: 'Ingen yderligere detaljer tilgængelige for denne vejledning.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Relateret vejledning',
            deadlines_empty: 'Ingen lovgivningsmæssige frister fundet for dette regelsæt.',
            deadlines_sla_type: 'SLA-type',
            deadlines_value: 'Frist',
            deadlines_unit: 'Enhed',
            deadlines_extension: 'Forlængelse',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Juridisk reference',
            framework_stack: 'Rammeværksstruktur',
            layer_parent_law: 'Moderlov',
            layer_implementing_decree: 'Gennemførelsesbekendtgørelse',
            layer_enforcement_decree: 'Håndhævelsesbekendtgørelse',
            layer_admin_procedures: 'Administrative procedurer',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Underordnede instrumenter',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Aktiv',
            status_draft: 'Udkast',
            draft_enforcement_badge: 'Udkast — endnu ikke vedtaget',
            effective_date_draft: 'Udkast — endnu ikke trådt i kraft',
            effective_date: 'Ikrafttrædelsesdato',
            forms_table_id: 'Formular-ID',
            forms_table_name: 'Formularnavn',
            forms_table_source: 'Kilde',
            forms_table_category: 'Kategori',
            forms_table_type: 'Type',
            fill_form: 'Udfyld formular',
            view_reference: 'Se reference',
            forms_empty: 'Ingen formularer fundet for dette rammeværk.',
            forms_loading: 'Indlæser formularer...',
            filter_by_source_layer: 'Filtrer efter kildelag',
            all_layers: 'Alle lag',
            procedures_id: 'Procedure-ID',
            procedures_name: 'Fremgangsmåde',
            procedures_trigger: 'Udløser',
            procedures_steps: 'Trin',
            procedures_outputs: 'Resultater',
            procedures_related_forms: 'Relaterede formularer',
            procedures_submission_channels: 'Indsendelseskanaler',
            procedures_empty: 'Ingen procedurer fundet for dette rammeværk.',
            fines_area: 'Overtrædelsesområde',
            fines_articles: 'Artikler',
            fines_individual: 'Bøde (individuel)',
            fines_organization: 'Bøde (organisation)',
            fines_revenue: 'Omsætningseskalering',
            fines_deadlines: 'Frister',
            fines_remediation: 'Afhjælpning',
            fines_related: 'Relaterede procedurer',
            fines_empty: 'Ingen håndhævelsesdata tilgængelige for dette rammeværk.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Risikotype',
            fines_risk_triggers: 'Hvad udløser det',
            fines_risk_only_note: 'Disse poster beskriver compliance- og driftsrisiko, ikke bødestraf. Hvor bøder gælder, er de fastsat i den overordnede ramme.',
            fines_enforcement_pending: 'Håndhævelsesrammen er endnu ikke offentliggjort. Sanktionsdata vil blive tilføjet, når gennemførelsesinstrumentet er bekendtgjort.',
            view_details: 'Vis detaljer',
            back_to_list: 'Tilbage til listen',
            description: 'Beskrivelse',
            jurisdiction: 'Jurisdiktion',
            lineage: 'Afstamning',
            replaces: 'Erstatter',
            replaced_by: 'Erstattet af',
            requirements: 'Krav',
            tab_navigation: 'Rammeværksdetalje-faner',
            forms_table_actions: 'Handlinger',
            create_form: 'Opret formular',
            mark_promulgated: 'Markér som bekendtgjort',
            status_promulgated: 'Bekendtgjort',
            promulgated_success: 'Lagstatus opdateret til bekendtgjort',
            transition_review: 'Overgangsgennemgang',
            transition_review_desc: 'Denne ramme har en afventende overgangsgennemgang for vedtagelse af efterfølgerlov.',
            transition_review_by: 'Gennemgangsfrist',
            transition_review_status: 'Gennemgangsstatus',
            transition_review_notes: 'Noter',
            transition_review_pending: 'Afventende',
            transition_review_in_progress: 'I gang',
            transition_review_completed: 'Afsluttet',
            transition_review_successor: 'Efterfølgende rammeværk',
            transition_review_lead_time: 'Forberedelsestid',
            transition_review_lead_time_days: '{days} dage før ikrafttrædelsesdato',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Overgangsgennemgang af formularer',
            transition_review_forms_desc: 'Gennemgå hver operationel skabelon for relevans under det efterfølgende rammeværk.',
            transition_review_outcome: 'Resultat',
            transition_review_outcome_carry_forward: 'Videreføres',
            transition_review_outcome_carry_forward_revised: 'Videreføres (revideret)',
            transition_review_outcome_retire: 'Udgår',
            transition_review_outcome_split: 'Opdeles',
            transition_review_outcome_awaiting_decree: 'Afventer bekendtgørelse',
            transition_review_no_forms: 'Ingen overgangsgennemgange konfigureret for formularer.',
            transition_review_update_success: 'Overgangsgennemgang opdateret',
            transition_review_operational_note: 'Disse er processtøttende operationelle skabeloner, ikke lovbestemte formulartal. Juridisk tilskrivning forbliver under det gamle rammeværk, indtil efterfølgerloven træder i kraft.',
            rights_tab: 'Rettigheder',
            rights_empty: 'Ingen registreredes rettigheder defineret for denne ramme.',
            rights_code: 'Kode',
            rights_name: 'Rettighed',
            rights_legal_basis: 'Retsgrundlag',
            rights_elaborating_articles: 'Uddybende artikler',
            rights_description: 'Beskrivelse',
            requirements_reference: 'Reference',
            requirements_mandatory: 'Obligatorisk',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Kilde',
            category_legal_basis: 'Retsgrundlag',
            category_data_subject_rights: 'Datasubjektets rettigheder',
            category_notification: 'Meddelelse',
            category_assessment: 'Vurdering',
            category_governance: 'Governance',
            category_record_keeping: 'Registrering',
            category_cross_border: 'Grænseoverskridende',
            category_technical_measures: 'Tekniske foranstaltninger',
            category_incident_reporting: 'Hændelsesrapportering',
            category_risk_management: 'Risikostyring',
            category_supply_chain: 'Forsyningskæde',
            category_certification: 'Certificering',
            child_frameworks: '{count} børneramme',
            child_frameworks_plural: '{count} underordnede rammer',
            replaces_prefix: 'Erstatter:',
            replaced_by_prefix: 'Erstattet af:',
            responsible_prefix: 'Ansvarlig:',
            watchlist_empty_hint: 'Rammer med status "draft" eller "coming" vises her til overvågning.',
            framework_count: '{count} ramme',
            framework_count_plural: '{count} rammer',
            hiding_legacy: '(skjuler {count} arv)',
            label_VN_PDPD_DECREE_13: 'PDPD-dekret 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Lov 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Dekret 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Håndhævelsesudkast 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Dataloven',
            label_VN_DATA_LAW_DECREE_165_2025: 'Datalovsbekendtgørelse 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Udkast til databørser 2026',
            label_VN_TELECOM_LAW_2023: 'Telekommunikationslov 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Teledekret 163/2024',
            label_VN_CYBERSEC_LAW: 'Lov om cybersikkerhed (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Cybersec-dekret 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Lov om cybersikkerhed 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'E-handelslov 122/2025',
            label_VN_AI_LAW_2026: 'AI Lov 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Firma',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Myndighed',
            sla_type_rights_request_ack: 'Anmodning om anerkendelse af rettigheder',
            'sla_type_Access control review': 'Gennemgang af adgangskontrol',
            'sla_type_Annual governance attestation': 'Årlig ledelsesattest',
            'sla_type_Audit review cycle': 'Revisionsgennemgangscyklus',
            'sla_type_Authority request response': 'Myndighedsanmodningssvar',
            'sla_type_Change notification processing': 'Skift meddelelsesbehandling',
            'sla_type_Complaint acknowledgment': 'Anerkendelse af klage',
            'sla_type_Complaint resolution': 'Løsning af klage',
            'sla_type_Confidentiality policy review': 'Gennemgang af fortrolighedspolitikken',
            'sla_type_Cybersecurity incident full report': 'Fuldstændig rapport om cybersikkerhedshændelse',
            'sla_type_Cybersecurity incident initial report': 'Indledende rapport om cybersikkerhedshændelser',
            'sla_type_Data inventory review': 'Gennemgang af dataopgørelse',
            'sla_type_Disclosure log review cycle': 'Gennemgangscyklus for offentliggørelse af log',
            'sla_type_Incident classification decision': 'Beslutning om klassificering af hændelser',
            'sla_type_Information system classification review': 'Gennemgang af informationssystemklassificering',
            'sla_type_Notification processing (sales website/app)': 'Notifikationsbehandling (salgswebsted/app)',
            'sla_type_Pen test cycle': 'Pen testcyklus',
            'sla_type_Registration processing (service platform)': 'Registreringsbehandling (serviceplatform)',
            'sla_type_Risk assessment cycle': 'Risikovurderingscyklus',
            'sla_type_Takedown decision (after review)': 'Beslutning om fjernelse (efter gennemgang)',
            'sla_type_Termination notice (public)': 'Opsigelsesvarsel (offentlig)',
            sla_type_ai_impact_assessment_completion: 'AI konsekvensanalyse afsluttet',
            sla_type_ai_sandbox_application_review: 'AI gennemgang af sandbox-applikationer',
            sla_type_ai_serious_incident_report: 'AI rapport om alvorlig hændelse',
            sla_type_ai_system_registration: 'AI systemregistrering',
            sla_type_authority_request_response: 'Myndighedsanmodningssvar',
            sla_type_certificate_issuance_review: 'Gennemgang af certifikatudstedelse',
            sla_type_complaint_handling_approach_notice: 'Besked om klagebehandling',
            sla_type_complaint_incomplete_info_request: 'Ufuldstændig anmodning om klage',
            sla_type_data_governance_attestation: 'Attestering af datastyring',
            sla_type_dsr_acknowledgment: 'DSR-anerkendelse',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Træk tilbage / begrænse / gøre indsigelse',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Tredjepart',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Adgang / ret / give',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Tredjepart',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Sletning',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Sletning, tredjepart',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Beskyttelsesforanstaltninger',
            sla_type_telecom_authority_request_response: 'Svar fra telemyndigheden',
            sla_type_rights_request_completion: 'Rettighedsanmodning om udfyldelse',
            sla_type_incident_authority_notification: 'Underretning fra hændelsesmyndigheden',
            sla_type_incident_subject_notification: 'Underretning om hændelse',
            sla_type_dpia_completion: 'DPIA fuldførelse',
            sla_type_data_retention_review: 'Gennemgang af dataopbevaring',
            sla_type_authority_filing_review: 'Myndighedsindgivelsesgennemgang',
            sla_type_authority_supplement_response: 'Myndighedstillæg svar',
            sla_type_authority_resubmission: 'Genindsendelse af myndighed',
            sla_type_authority_filing_supplement_response: 'Myndighedens indgivelse af supplerende svar',
            sla_type_authority_filing_resubmission: 'Genindsendelse af myndighedens indgivelse',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            unit_working_days: 'arbejdsdage',
            unit_calendar_days: 'kalenderdage',
            unit_hours: 'timer',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Samtykke',
            form_category_privacy: 'Privatliv',
            form_category_breach: 'Overtrædelse',
            form_category_dpia: 'DPIA',
            form_category_register: 'Tilmeld dig',
            form_category_monitoring: 'Overvågning',
            form_category_vendor: 'Sælger',
            form_category_org: 'Organisatorisk',
            form_category_sector: 'Sektor',
            form_category_data_mapping: 'Datakortlægning',
            form_category_governance: 'Governance',
            form_category_incident: 'Hændelse',
            form_category_authority_submission: 'Myndighedsforelæggelse',
            form_category_notice: 'Bemærk',
            form_category_assessment_report: 'Vurderingsrapport',
            form_category_request: 'Anmodning',
            form_category_dossier: 'Dossier',
            form_category_filing: 'Arkivering',
            module_consent: 'Samtykke',
            module_rights_request: 'Rettighedsanmodning',
            module_classification: 'Klassifikation',
            module_dpia: 'DPIA',
            module_governance: 'Governance',
            module_monitoring: 'Overvågning',
            module_register: 'Tilmeld dig',
            module_risk: 'Risiko',
            module_data_mapping: 'Datakortlægning',
            module_incident: 'Hændelse',
            module_assessment: 'Vurdering',
            module_vendor: 'Sælger',
            module_policy: 'Politik',
            module_audit: 'Revision',
            module_training: 'Træning',
            module_discovery: 'Opdagelse',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Beregningsvejledning',
            enum_guidance_type_implementation_guidance: 'Implementeringsvejledning',
            enum_guidance_type_interpretive_note: 'Fortolkende note',
            enum_guidance_type_legal_guidance: 'Juridisk vejledning',
            enum_guidance_type_procedural_guidance: 'Procedurevejledning',
            enum_guidance_type_sector_guidance: 'Sektorvejledning',
            enum_guidance_type_technical_methodology: 'Teknisk Metodik',
            enum_guidance_category_annual_reporting: 'Årsrapportering',
            enum_guidance_category_authority_cooperation: 'Myndighedssamarbejde',
            enum_guidance_category_authority_coordination: 'Myndighedskoordinering',
            enum_guidance_category_business_continuity: 'Forretningskontinuitet',
            enum_guidance_category_capital_adequacy: 'Kapitaltilstrækkelighed',
            enum_guidance_category_complaint_handling: 'Behandling af klage',
            enum_guidance_category_consent: 'Samtykke',
            enum_guidance_category_consent_management: 'Samtykkestyring',
            enum_guidance_category_consumer_protection: 'Forbrugerbeskyttelse',
            enum_guidance_category_control_environment: 'Kontrolmiljø',
            enum_guidance_category_cross_border: 'Grænseoverskridende',
            enum_guidance_category_cross_border_transfers: 'Grænseoverskridende overførsler',
            enum_guidance_category_data_classification: 'Dataklassifikation',
            enum_guidance_category_data_localization: 'Datalokalisering',
            enum_guidance_category_data_protection_impact_assessment: 'Databeskyttelseskonsekvensvurdering',
            enum_guidance_category_direct_marketing: 'Direkte markedsføring',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Governance',
            enum_guidance_category_ict_risk_management: 'IKT Risikostyring',
            enum_guidance_category_incident_management: 'Hændelseshåndtering',
            enum_guidance_category_internal_audit: 'Intern revision',
            enum_guidance_category_isms_documentation: 'ISMS dokumentation',
            enum_guidance_category_lawful_basis: 'Lovligt grundlag',
            enum_guidance_category_licensing: 'Licensering',
            enum_guidance_category_local_presence: 'Lokal tilstedeværelse',
            enum_guidance_category_logging_and_retention: 'Logning og opbevaring',
            enum_guidance_category_logical_and_physical_access: 'Logisk og fysisk adgang',
            enum_guidance_category_operational_resilience_testing: 'Operationel modstandstest',
            enum_guidance_category_outsourcing: 'Outsourcing',
            enum_guidance_category_platform_registration: 'Platform registrering',
            enum_guidance_category_quality_of_service: 'Kvalitet af service',
            enum_guidance_category_risk_classification: 'Risikoklassificering',
            enum_guidance_category_risk_management: 'Risikostyring',
            enum_guidance_category_scope_definition: 'Definition af omfang',
            enum_guidance_category_sectoral_localisation: 'Sektor lokalisering',
            enum_guidance_category_subscriber_protection: 'Abonnent beskyttelse',
            enum_guidance_category_supply_chain: 'Forsyningskæde',
            enum_guidance_category_system_classification: 'Systemklassifikation',
            enum_guidance_category_system_operations: 'Systemdrift',
            enum_guidance_category_telecommunications_data: 'Telekommunikationsdata',
            enum_guidance_category_third_party_risk: 'Tredjepartsrisiko',
            enum_guidance_category_transitional_provisions: 'Overgangsbestemmelser',
            enum_guidance_category_vulnerability_management: 'Sårbarhedshåndtering',
            enum_guidance_status_active: 'Aktiv',
            enum_guidance_status_draft: 'Udkast',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Adgangsstyring',
            enum_module_ai_governance: 'AI Styring',
            enum_module_asset_management: 'Asset Management',
            enum_module_audit_trail: 'Revisionsspor',
            enum_module_breach_notification: 'Underretning om brud',
            enum_module_capital_adequacy: 'Kapitaltilstrækkelighed',
            enum_module_classification: 'Klassifikation',
            enum_module_consent: 'Samtykke',
            enum_module_consent_management: 'Samtykkestyring',
            enum_module_cross_border_transfers: 'Grænseoverskridende overførsler',
            enum_module_data_localization: 'Datalokalisering',
            enum_module_data_mapping: 'Datakortlægning',
            enum_module_data_protection: 'Databeskyttelse',
            enum_module_data_subject_rights: 'Datasubjektets rettigheder',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Kryptering',
            enum_module_governance: 'Governance',
            enum_module_incident: 'Hændelse',
            enum_module_incident_management: 'Hændelseshåndtering',
            enum_module_internal_audit: 'Intern revision',
            enum_module_isms_documentation: 'ISMS dokumentation',
            enum_module_isms_governance: 'ISMS Governance',
            enum_module_logging_and_monitoring: 'Logning og overvågning',
            enum_module_monitoring: 'Overvågning',
            enum_module_policy_management: 'Politikstyring',
            enum_module_risk: 'Risiko',
            enum_module_risk_management: 'Risikostyring',
            enum_module_training: 'Træning',
            enum_module_vendor: 'Sælger',
            enum_module_vendor_management: 'Leverandørstyring',
            enum_module_vendor_risk: 'Sælgerrisiko',
            enum_module_vulnerability_management: 'Sårbarhedshåndtering',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Adgang',
            category_availability: 'Tilgængelighed',
            category_change_management: 'Forandringsledelse',
            category_classification: 'Klassifikation',
            category_communication_and_information: 'Kommunikation & Information',
            category_compliance: 'Overholdelse',
            category_confidentiality: 'Fortrolighed',
            category_control_activities: 'Kontrolaktiviteter',
            category_control_environment: 'Kontrolmiljø',
            category_incident: 'Hændelse',
            category_internal_audit: 'Intern revision',
            category_internal_control: 'Intern kontrol',
            category_logical_and_physical_access: 'Logisk og fysisk adgang',
            category_mis_data: 'Management Information System Data',
            category_monitoring: 'Overvågning',
            category_monitoring_activities: 'Overvågningsaktiviteter',
            category_outsourcing: 'Outsourcing',
            category_privacy: 'Privatliv',
            category_processing_integrity: 'Behandlingsintegritet',
            category_reporting: 'Indberetning',
            category_risk_assessment: 'Risikovurdering',
            category_risk_mitigation: 'Risikobegrænsning',
            category_sector_applicability: 'Sektoranvendelse',
            category_system_operations: 'Systemdrift',
            category_training: 'Træning',
            category_vendor: 'Sælger',
            guidance_related_forms_advisory: 'Nogle formularer har relateret vejledning — se i',
            guidance_related_procedures_advisory: 'Nogle procedurer har relateret vejledning — se i',
            guidance_related_requirements_advisory: 'Nogle krav har relateret vejledning - se i',
        },
        channels: {
            title: 'Indsendelseskanaler og frister',
            channels: 'Indsendelseskanaler',
            add_channel: 'Tilføj kanal',
            channel_type: 'Type',
            name: 'Navn',
            instructions: 'Instruktioner',
            active: 'Aktiv',
            no_channels: 'Ingen indsendelseskanaler konfigureret',
            portal: 'Portal',
            in_person: 'Personligt',
            postal: 'Post',
            email: 'E-mail',
            add: 'Tilføj',
            deadlines: 'Lovgivningsmæssige frister',
            add_deadline: 'Tilføj frist',
            label: 'Etiket',
            days: 'Dage',
            day_type: 'Dagtype',
            calendar_days: 'Kalenderdage',
            working_days: 'Arbejdsdage',
            no_deadlines: 'Ingen frister konfigureret',
            description: 'Beskrivelse',
            failed_load: 'Kunne ikke load channel data',
            channel_added: 'Kanal tilføjet',
            channel_added_announce: 'Indsendelseskanal tilføjet',
            failed_add_channel: 'Kunne ikke add channel',
            deadline_added: 'Frist tilføjet',
            deadline_added_announce: 'Lovgivningsmæssig frist tilføjet',
            failed_add_deadline: 'Kunne ikke add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Rammeværkstilknytning',
            modules: 'Tilknyttede moduler',
            add_module: 'Tilføj modul',
            module_code: 'Modulkode',
            required: 'Påkrævet',
            link: 'Link',
            no_modules: 'Ingen moduler tilknyttet',
            notes: 'Noter',
            personas: 'Tilknyttede personaer',
            add_persona: 'Tilføj persona',
            persona_id: 'Persona-ID',
            role_description: 'Rollebeskrivelse',
            no_personas: 'Ingen personaer tilknyttet',
            failed_load: 'Kunne ikke load framework links',
            module_linked: 'Modul tilknyttet',
            module_linked_announce: 'Modul tilknyttet',
            failed_link_module: 'Kunne ikke link module',
            persona_linked: 'Persona tilknyttet',
            persona_linked_announce: 'Persona tilknyttet',
            failed_link_persona: 'Kunne ikke link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Afventer',
                assessed: 'Vurderet',
                applied: 'Anvendt',
                dismissed: 'Afvist',
            },
            title: 'Lovgivningsmæssige opdateringer og rammeværksdashboard',
            page_description: 'D?kning i overblik for hvert installeret lovgivningsrammev?rk samt den indbyggede ?ndringslog for lovgivningsopdateringer: ?ndringer, ikrafttr?delsesdatoer og konsekvensvurderinger.',
            coverage_dashboard: 'Dashboard for rammeværksdækning',
            no_frameworks: 'Ingen rammeværker fundet',
            framework: 'Rammeværk',
            modules: 'Moduler',
            templates: 'Skabeloner',
            forms: 'Formularer',
            channels: 'Kanaler',
            deadlines: 'Frister',
            pending: 'Ventende opdateringer',
            updates: 'Lovgivningsmæssige opdateringer',
            create_update: 'Opret opdatering',
            framework_id: 'Rammeværks-ID',
            update_title: 'Titel',
            description: 'Beskrivelse',
            effective_date: 'Ikrafttrædelsesdato',
            amendment_ref: 'Ændringsreference',
            create: 'Opret',
            no_updates: 'Ingen lovgivningsmæssige opdateringer',
            status: 'Status',
            impact: 'Påvirkning',
            change_summary: 'Ændringsoversigt',
            assess: 'Vurdér',
            failed_load: 'Kunne ikke load data',
            update_created: 'Opdatering oprettet',
            update_created_announce: 'Lovgivningsmæssig opdatering oprettet',
            failed_create: 'Kunne ikke create update',
            update_assessed: 'Opdatering vurderet',
            failed_assess: 'Kunne ikke assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Relaterede rammeværker',
            panel_intro: 'Hvordan dette rammeværk hænger sammen med andre. Et overlay kan gælde under flere overordnede rammeværker samtidig; hvert vises præcis én gang.',
            outgoing_heading: 'Dette rammeværk gælder for',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Gælder under dette rammeværk',
            outgoing_caption: 'Rammeværker som dette rammeværk peger på',
            incoming_caption: 'Rammeværker der hører under dette rammeværk',
            col_framework: 'Rammeværk',
            col_relationship: 'Relation',
            col_scope: 'Kilde',
            scope_global: 'Fra pakkeindhold',
            scope_org: 'Organisationsspecifik',
            load_failed: 'Kunne ikke indlæse relaterede rammeværker',
            relationship_kinds: {
                parent: 'Overordnet rammeværk',
                applies_to: 'Gælder for',
                cross_reference: 'Krydshenvisning',
                supersedes: 'Erstatter',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Bankvirksomhed',
                insurance: 'Forsikring',
                healthcare: 'Sundhedsv?sen',
                education: 'Education',
                ict_critical: 'Kritisk IKT',
                telecom: 'Telekommunikation',
                energy: 'Energi',
                transport: 'Transport',
                digital_services: 'Digitale tjenester',
                digital_technology_industry: 'Digital teknologiindustri',
                manufacturing: 'Manufacturing',
                public_administration: 'Offentlig administration',
                payments: 'Betalinger',
                publishing: 'Forlagsvirksomhed',
                crypto_assets: 'Kryptoaktiver',
                other: 'Andet',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sektor',
            direct_scope_label: 'Direkte juridisk anvendelsesomr?de',
            chip_direct: 'Direkte g?ldende',
            chip_reference: 'Kun til reference',
            banner_direct_copy: 'Direkte g?ldende for din organisation. Sektorrapporter og frister er aktive.',
            banner_reference_copy: 'Kun til reference ? din organisation er ikke klassificeret i denne sektor.',
            edit_mode_button: 'Redigeringstilstand',
            edit_form_title: 'Skift installationstilstand',
            mode_direct_label: 'Direkte — juridisk gældende',
            mode_direct_help: 'Planl?gger sektorfrister og dashboard-widgets.',
            mode_reference_label: 'Kun til reference',
            mode_reference_help: 'Kan gennemses, men uden frister eller widgets.',
            reason_note_label: '?rsag (valgfri)',
            reason_note_placeholder: 'f.eks. Bankdatterselskab overtaget',
            cancel: 'Annuller',
            confirm: 'Bekr?ft',
            confirming: 'Gemmer ...',
            save: 'Gem',
            saving: 'Gemmer ...',
            toast_mode_changed: 'Sektoroverlayets tilstand er opdateret.',
            toast_installed: 'Sektoroverlay installeret.',
            toast_sectors_saved: 'Sektorvalg for organisationen er gemt.',
            install_modal_title: 'Installer sektoroverlay',
            install_modal_intro: 'Sektoroverlays er frivillige lovgivningspakker, der er afgr?nset til en enkelt branche.',
            install_modal_footer: 'Du kan senere ?ndre installationstilstanden fra siden med rammev?rksdetaljer.',
            install_button: 'Installer',
            installed_label: 'Installeret',
            uninstall_button: 'Afinstaller',
            uninstall_confirm: 'Bekræft afinstallation',
            uninstall_cancel: 'Annuller',
            suggested_label: 'Foresl?et',
            catalog_page_title: 'Sektoroverlays',
            catalog_page_intro: 'Valgfrie lovgivningspakker, der er afgr?nset til en enkelt branche.',
            catalog_empty_title: 'Ingen sektoroverlays tilg?ngelige',
            catalog_empty_description: 'Sektoroverlay-pakker, der er udgivet i din platform, vises her.',
            authority_label: 'Myndighed',
            org_sectors_title: 'Branchesektorer',
            org_sectors_intro: 'V?lg de brancher, som din organisation opererer i.',
            widget_title: 'Sektoroverlay-rapporter med frist',
            widget_aria_label: 'Sektoroverlay-rapporter med frist',
            widget_empty: 'Ingen kommende frister for sektoroverlays.',
            audit_event_mode_changed: 'Installationstilstand for sektoroverlay ?ndret',
            audit_event_installed: 'Sektoroverlay installeret',
            view_details_button: 'Vis detaljer ?',
            banner_aria_label: 'Status for sektoroverlay',
            toast_load_failed: 'Kunne ikke indl?se sektoroverlays',
            toast_install_failed: 'Kunne ikke installere sektoroverlay',
            toast_uninstall_confirm_required: 'Skriv "{packCode}" for at bekr?fte afinstallationen.',
            toast_uninstalled: '{packCode} er afinstalleret.',
            toast_uninstall_failed: 'Kunne ikke afinstallere sektoroverlay',
            uninstall_warning_destructive_label: 'Destruktiv handling.',
            uninstall_warning_body: 'Afinstallation sletter rammev?rksposten og alle gemte lovgivningsfrister, der er knyttet til {frameworkCode}, fra denne organisation.',
            uninstall_warning_type_to_confirm: 'Skriv {packCode} for at bekr?fte.',
            uninstall_in_progress: 'Afinstallerer ...',
            widget_view_all: 'Vis alle sektoroverlays ?',
            widget_due_in_days: 'Forfalder om {days} d',
            widget_overdue_days: 'Forsinket med {days} d',
            widget_due_today: 'Forfalder i dag',
            widget_due_unknown: '—',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
                product_identification_certificate_receipt: 'Certificate synchronisation receipt',
                product_identification_connection_readiness: 'Connection and transition readiness record',
                product_identification_event_record: 'Significant supply-chain event record',
                product_identification_provider_agreement: 'Service-provider data-responsibility agreement',
                product_identification_scope_determination: 'Product scope and risk-tier determination',
                product_identification_uid_register: 'UID and master-data register entry',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_identification_scope_determinations: 'Product Scope Determinations',
                product_identification_sector_watch: 'Sector Watch',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Fasede forpligtelser',
            panel_aria_label: 'Fasede forpligtelser',
            panel_intro: 'Nogle klausuler i dette rammev?rk tr?der i kraft senere end selve rammev?rket.',
            phased_effective_date_label: 'Faset ikrafttr?delse:',
            status_pending: 'Afventer',
            status_active: 'Aktiv',
            status_early: 'Aktiv (tidligt implementeret)',
            notice_window_label: 'Varslingsvindue til myndighed:',
            days_label: 'dage',
            recipient_label: 'Modtager:',
            early_implement_button: 'Mark?r som tidligt implementeret',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Mark?r faset forpligtelse som tidligt implementeret',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent.',
            effective_at_label: 'G?ldende fra',
            reason_label: '?rsag (valgfri)',
            reason_placeholder: 'f.eks. Bestyrelsen godkendte tidlig indf?relse 2026-09-01',
            confirm: 'Bekr?ft',
            toast_early_implemented: 'Tidlig implementering er registreret.',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Faset forpligtelse markeret som tidligt implementeret',
            widget_rolling_window_label: 'Fasede aktiveringer inden for 90 dage',
            notice_due_label: 'Notice due by the end of',
            notice_due_time_suffix: '(organisation time)',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day.',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent.',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday.',
                day_after_is_day_one: 'Computed: the day after implementation is day 1.',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period.',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies.',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states.',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read.',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due.',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run.',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read.',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Oversigt',
            partner_orgs: 'Partnerorganisationer',
            service_tiers: 'Serviceniveauer',
            deliverable_templates: 'Leveranceskabeloner',
            deliverables: 'Leverancer',
            brand_profiles: 'Brandprofiler',
            support_tickets: 'Supportsager',
            workflow_templates: 'Arbejdsgangsskabeloner',
            workflows: 'Arbejdsgange',
            escalation: 'Eskalering',
            reports: 'Rapporter',
            audit: 'Revisionsspor',
        },
        dashboard: {
            title: 'Partneroversigt',
            loading: 'Indlæser dashboarddata...',
            total_managed_tenants: 'Administrerede lejere i alt',
            avg_health_score: 'Gns. sundhedsscore',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Åbne sager',
            upcoming_deliverables: 'Kommende leverancer',
            escalated_tickets: 'Eskalerede sager',
            managed_tenants: 'Administrerede lejere',
            partner_tier: 'Partnerniveau',
            managed_tenants_count: 'Antal administrerede lejere',
            onboarding_date: 'Onboardingdato',
            view_details: 'Vis detaljer',
            no_partner_orgs: 'Ingen partnerorganisationer fundet.',
        },
        partner_orgs: {
            title: 'Partnerorganisationer',
            new: 'Ny partnerorganisation',
            contact_name: 'Kontaktnavn',
            contact_email: 'Kontakt-e-mail',
            partner_tier: 'Partnerniveau',
            managed_orgs: 'Administrerede org.',
            onboarding_date: 'Onboardingdato',
            no_orgs: 'Ingen partnerorganisationer fundet.',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Kontakttelefon',
            metadata_json: 'Metadata (JSON)',
            change_status: 'Skift status',
            new_status: 'Ny status',
            invalid_json_metadata: 'Ugyldig JSON i metadatafelt',
            suspended: 'Suspenderet',
            offboarded: 'Offboardet',
            all_tiers: 'Alle niveauer',
            silver: 'Sølv',
            gold: 'Guld',
            platinum: 'Platin',
            status_offboarded: 'Offboardet',
            status_suspended: 'Suspenderet',
            tier_gold: 'Guld',
            tier_platinum: 'Platin',
            tier_silver: 'Sølv',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Serviceniveauer',
            new: 'Nyt niveau',
            tier_name: 'Niveaunavn',
            monthly_fee: 'Månedligt gebyr',
            features: 'Funktioner',
            no_tiers: 'Ingen service tiers fundet.',
            tier_code: 'Niveaukode',
            response_time_hrs: 'Svartid (timer)',
            resolution_time_hrs: 'Løsningstid (timer)',
            review_frequency: 'Gennemgangsfrekvens',
            sla_response_time: 'SLA-svartid (timer)',
            sla_resolution_time: 'SLA-løsningstid (timer)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Sammenligner...',
            compare: 'Sammenlign',
            comparison: 'Serviceniveausammenligning',
            attribute: 'Attribut',
            deprecate: 'Udfas',
            deprecated: 'Udfaset',
            select_compare: 'Vælg mindst 2 niveauer til sammenligning',
            comparison_title: 'Serviceniveausammenligning',
            status_deprecated: 'Udfaset',
            activate: 'Aktiver',
            resolution_time: 'Løsningstid',
            response_time: 'Svartid',
            select_at_least_two: 'Vælg mindst 2 niveauer',
        },
        deliverable_templates: {
            title: 'Leveranceskabeloner',
            new: 'Ny skabelon',
            template_name: 'Skabelonnavn',
            deliverable_type: 'Leverancetype',
            no_templates: 'Ingen leverance skabeloner fundet.',
            frequency: 'Frekvens',
            est_effort_hrs: 'Ansl. indsats (timer)',
            estimated_effort: 'Estimeret indsats (timer)',
            associated_module: 'Tilknyttet modul',
            linked_tiers_count: 'Tilknyttede Niveauer Antal',
            evidence_requirements: 'Dokumentationskrav (kommasepareret)',
            tier_ids: 'Niveau-ID-er (kommasepareret)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'Engangs',
            frequency_monthly: 'Månedligt',
            frequency_quarterly: 'Kvartalsvis',
            frequency_annually: 'Årligt',
            type_report: 'Rapport',
            type_review: 'Gennemgang',
            type_assessment: 'Vurdering',
            type_meeting: 'Møde',
            type_configuration: 'Konfiguration',
            freq_one_time: 'Engangs',
            freq_monthly: 'Månedligt',
            freq_quarterly: 'Kvartalsvis',
            freq_annually: 'Årligt',
            status_deprecated: 'Udfaset',
        },
        deliverables: {
            title: 'Leverancer',
            new: 'Ny leverance',
            partner: 'Partner',
            deliverable_type: 'Leverancetype',
            due_date: 'Forfaldsdato',
            no_deliverables: 'Ingen leverancer fundet.',
            assigned_to: 'Tildelt til',
            completion_date: 'Færdiggørelsesdato',
            update_status: 'Opdater status',
            hide_details: 'Skjul detaljer',
            view_details: 'Vis detaljer',
            evidence_ids: 'Dokumentations-ID-er',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Klientgodkendelse',
            bulk_update: 'Masseopdatering',
            bulk_update_status: 'Masseopdater status',
            generate_schedule: 'Generer tidsplan',
            generate_deliverable_schedule: 'Generer Leverance Planlæg',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organisations-ID',
            service_tier_id: 'Service Niveau ID',
            filter_assigned_to: 'Filter efter Tildelt Til',
            select_status: 'Vælg status...',
            new_status: 'Ny status',
            upcoming: 'Kommende',
            overdue: 'Forsinket',
            status_deferred: 'Udsat',
            status_overdue: 'Forsinket',
            status_upcoming: 'Kommende',
        },
        brand_profiles: {
            title: 'Brandprofiler',
            new: 'Ny Brand Profil',
            partner: 'Partner',
            brand_name: 'Brandnavn',
            primary_color: 'Primærfarve',
            logo_url: 'Logo-URL',
            no_profiles: 'Ingen brand profiles fundet.',
            secondary_color: 'Sekundærfarve',
            header_text: 'Sidehoved tekst',
            footer_text: 'Sidefodstekst',
            contact_email: 'Kontakt-e-mail',
            contact_phone: 'Kontakttelefon',
            contact_website: 'Kontakthjemmeside',
            contact_address: 'Kontaktadresse',
            is_default: 'Er standard',
            set_default: 'Sæt som standard',
            preview: 'Forhåndsvisning',
            report_header_preview: 'Rapport Sidehoved Forhåndsvisning',
            report_footer_preview: 'Rapport Sidefod Forhåndsvisning',
            preview_description: 'Dette er en forhåndsvisning af rapporthovedet med de valgte brandfarver.',
            contact_website_short: 'Hjemmeside',
            logo_preview: 'Logo forhåndsvisning',
        },
        support_tickets: {
            title: 'Supportsager',
            new: 'Ny sag',
            partner: 'Partner',
            subject: 'Emne',
            assigned_to: 'Tildelt til',
            no_tickets: 'Ingen support sager fundet.',
            category: 'Kategori',
            priority: 'Prioritet',
            escalation_tier: 'Eskaleringsniveau',
            sla_status: 'SLA-status',
            update_status: 'Opdater status',
            escalate: 'Eskaler',
            add_note: 'Tilføj note',
            escalate_ticket: 'Eskaler sag',
            escalate_to_tier: 'Eskaler To Niveau',
            notes_timeline: 'Notetidslinje',
            no_notes: 'Ingen noter endnu.',
            attachments: 'Vedhæftninger',
            no_attachments: 'Ingen vedhæftninger.',
            client_org_id: 'Klientorganisations-ID',
            content: 'Indhold',
            note_type: 'Type',
            view_detail: 'Vis detalje',
            hide: 'Skjul',
            new_status: 'Ny status',
            all_categories: 'Alle kategorier',
            all_priorities: 'Alle prioriteter',
            all_tiers: 'Alle niveauer',
            ticket_not_found: 'Sag ikke fundet',
            ticket_id: 'Sags-ID',
            client_org: 'Klientorganisation',
            deescalate: 'Deeskaler',
            deescalate_ticket: 'Deeskaler sag',
            deescalate_to_tier: 'Deeskaler To Niveau',
            add_attachment: 'Tilføj vedhæftning',
            file: 'Fil',
            linked_entity: 'Tilknyttet enhed',
            sla_target: 'Mål',
            breached: 'Overtrådt',
            remaining_hours: 'resterende',
            na: 'N/A',
            tickets_found: 'sager fundet',
            reason: 'Årsag',
            target: 'Mål',
            upload: 'Upload',
        },
        workflow_templates: {
            title: 'Arbejdsgangsskabeloner',
            new: 'Ny skabelon',
            template_name: 'Skabelonnavn',
            steps: 'Trin',
            no_templates: 'Ingen arbejdsgang skabeloner fundet.',
            category: 'Kategori',
            view_steps: 'Vis trin',
            hide_steps: 'Skjul trin',
            step: 'Trin',
            step_title: 'Titel',
            step_description: 'Beskrivelse',
            assignee_role: 'Modtagerrolle',
            sla_hours: 'SLA-timer',
            evidence_required: 'Dokumentation påkrævet',
            guidance_notes: 'Vejledningsnoter',
            add_step: 'Tilføj trin',
            no_steps: 'Ingen trin defineret.',
            no_steps_defined: 'Ingen trin defineret.',
            templates_found: 'skabeloner fundet',
            all_categories: 'Alle kategorier',
        },
        workflows: {
            client_org: 'Klientorganisation',
            title: 'Arbejdsgangsinstanser',
            new: 'Ny arbejdsgang',
            template: 'Skabelon',
            partner: 'Partner',
            current_step: 'Nuværende trin',
            progress: 'Fremskridt',
            no_workflows: 'Ingen arbejdsgang instanser fundet.',
            instances_found: 'instanser fundet',
        },
        escalation: {
            title: 'Eskaleringsdashboard',
            ticket: 'Sag',
            partner: 'Partner',
            escalated_at: 'Eskaleret den',
            reason: 'Årsag',
            no_escalations: 'Ingen eskaleringer fundet.',
            time_in_tier: 'Tid i Niveau',
            auto_evaluate: 'Autoevaluer',
            priority_critical: 'Kritisk',
            priority_high: 'Høj',
            priority_medium: 'Middel',
            priority_low: 'Low',
            deescalated_from_dashboard: 'Deeskaleret fra dashboard',
            sla_breached: 'SLA overtrådt',
            sla_remaining: 'SLA resterende: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Klientorganisation',
            title: 'Rapporter',
            partner: 'Partner',
            report_type: 'Rapporttype',
            generated_at: 'Genereret den',
            no_reports: 'Ingen rapporter fundet.',
            generate: 'Generer rapport',
        },
        audit: {
            title: 'Partner Revision Spor',
            event_type: 'Hændelsestype',
            entity_type: 'Enhedstype',
            entity: 'Enhed',
            entity_id: 'Enheds-ID',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
            no_events: 'Ingen revision hændelser fundet.',
            events_found: 'hændelser fundet',
            client_org: 'Kundeorganisation',
            all_actors: 'Alle aktører',
            event_type_search: 'Søg hændelsestype',
            event_type_created: 'Oprettet',
            event_type_updated: 'Opdateret',
            event_type_deleted: 'Slettet',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Partner Org',
            entity_type_service_tier: 'Serviceniveau',
            entity_type_deliverable_template: 'Leverbar skabelon',
            entity_type_deliverable_instance: 'Leverbar instans',
            entity_type_brand_profile: 'Mærkeprofil',
            entity_type_support_ticket: 'Support billet',
            entity_type_workflow_template: 'Workflow skabelon',
            entity_type_workflow_instance: 'Workflow-forekomst',
            entity_type_generated_report: 'Genereret rapport',
            entity_type_audit_export: 'Revisionseksport',
        },
        no_permission_title: 'Utilstrækkelige tilladelser',
        no_permission_message: 'Du har ikke tilladelse til at få adgang til partnerarbejdsområdet.',
        status_active: 'Aktiv',
        status_suspended: 'Suspenderet',
        status_offboarded: 'Offboardet',
        status_completed: 'Afsluttet',
        status_cancelled: 'Annulleret',
        status_deprecated: 'Forældet',
        status_new: 'Ny',
        status_triaged: 'Triageret',
        status_in_progress: 'Igangværende',
        status_waiting_on_client: 'Venter på klient',
        status_waiting_on_vendor: 'Venter på sælger',
        status_escalated: 'Eskaleret',
        status_resolved: 'Løst',
        status_closed: 'Lukket',
        status_pending: 'Afventer',
        status_skipped: 'Sprang over',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Onboarding',
        category_incident_escalation: 'Incident Eskalering',
        category_config_change: 'Konfigurationsændring',
        category_periodic_review: 'Periodisk gennemgang',
        category_offboarding: 'Offboarding',
        category_custom: 'Brugerdefineret',
        category_configuration: 'Konfiguration',
        category_incident: 'Hændelse',
        category_compliance_question: 'Overholdelsesspørgsmål',
        category_feature_request: 'Funktionsanmodning',
        category_bug_report: 'Fejlrapport',
        priority_critical: 'Kritisk',
        priority_high: 'Høj',
        priority_medium: 'Medium',
        priority_low: 'Lav',
        tier_silver: 'Sølv',
        tier_gold: 'Guld',
        tier_platinum: 'Platin',
        report_type_compliance_summary: 'Overholdelsesoversigt',
        report_type_risk_overview: 'Risikooversigt',
        report_type_incident_report: 'Hændelsesrapport',
        report_type_consent_status: 'Samtykkestatus',
        report_type_program_maturity: 'Program modenhed',
        report_type_executive_pack: 'Executive pakke',
        note_type_internal: 'Internt',
        note_type_client_visible: 'Klient synlig',
        status_updated: 'Status blev opdateret',
        deliverable_status_updated: 'Leveringsstatus blev opdateret',
        ticket_status_updated: 'Billetstatus blev opdateret',
        ticket_escalated: 'Billetten eskalerede med succes',
        note_added: 'Noten blev tilføjet',
        attachment_uploaded: 'Vedhæftet fil blev uploadet',
        ticket_deescalated: 'Billetten deeskalerede med succes',
        auto_evaluation_completed: 'Auto-evaluering afsluttet',
        template_created: 'Workflow-skabelon blev oprettet',
        template_updated: 'Workflow-skabelon blev opdateret',
        brand_set_default: 'Varemærkeprofil blev indstillet som standard',
        brand_created: 'Brandprofil blev oprettet',
        brand_updated: 'Brandprofilen blev opdateret',
        tier_created: 'Serviceniveau blev oprettet',
        tier_updated: 'Serviceniveauet blev opdateret',
        tier_deprecated: 'Serviceniveauet blev udfaset',
        tier_activated: 'Serviceniveau blev aktiveret',
        period: 'Periode',
        format: 'Format',
        period_start: 'Periode start',
        period_end: 'Periodeafslutning',
        reports_count: '{count} rapporterer',
        reports_count_one: '{count} rapport',
        metric: 'Metrisk',
        value: 'Værdi',
        showing_chart_view: 'Viser kortvisning',
        showing_table_view: 'Viser datatabelvisning',
        evidence_ids_placeholder: 'f.eks. ev-001, ev-002',
        confirm_cancel_workflow: 'Er du sikker på, at du vil annullere denne arbejdsgang? Denne handling kan ikke fortrydes.',
        export_csv: 'Eksporter CSV',
        compliance_qualifications_title: 'Overholdelsesservicekvalifikationer',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Regulatory Output Alignment',
        regulatory_output_desc: 'Hver skabelontype knytter sig til en regulatorisk outputkategori: Rapporter producerer overholdelsesdokumentation, anmeldelser validerer kontroleffektivitet, vurderinger dækker risikoevalueringer (DPIA, gap-analyse), møder dokumenterer styringssessioner. Beviskrav definerer de artefakter, som hver leverance skal referere til for at være klar til revision.',
        sla_compliance_title: 'SLA Sporing for overholdelseslevering',
        sla_compliance_desc: "SLA'er for svar og løsningstid er kontraktligt bindende for levering af compliance-tjenester. Gennemgangshyppighed bestemmer, hvor ofte partneroverholdelsesleverancer evalueres. SLA-brud på overholdelseskritiske billetter udløser automatisk eskalering og logges i Partner Audit Trail som lovgivningsmæssig dokumentation.",
        regulatory_engagement_title: 'Regulatoriske engagementsmønstre',
        regulatory_engagement_desc: 'Workflow-kategorier kortlægges til lovgivningsmæssige engagementstyper: Onboarding dækker indledende compliance-opsætning og gap-analyse, periodisk gennemgang håndterer tilbagevendende compliance-vurderinger, Incident Escalation administrerer regulatorisk overtrædelsesrespons, Offboarding sikrer, at dataopbevaringsforpligtelserne overholdes. Hvert trin kan kræve bevisindsamling for fuldstændigheden af revisionssporet.',
        compliance_escalation_title: 'Overholdelseskritiske eskaleringsprocedurer',
        compliance_escalation_desc: 'L1 håndterer standard operationelle problemer. L2 dækker overholdelsespåvirkende fejl (forpassede regulatoriske deadlines, kontrolhuller). L3 er reserveret til scenarier med lovovertrædelser, der kræver øjeblikkelig ledelses opmærksomhed. SLA brud på L2/L3-billetter genererer automatisk revisionssporposter. Alle eskalerings- og deeskaleringshandlinger logges permanent.',
        audit_regulatory_title: 'Regulatory Evidence Trail',
        audit_regulatory_desc: 'Dette revisionsspor giver manipulationssikre optegnelser for overholdelse af lovgivningen. Hver partnerhandling (opret, opdater, slet, eskalering, statusændring) logges med aktøridentitet, tidsstempel og fuld nyttelastdiff. Brug Entity Type-filtre til at isolere bevis efter overholdelsesdomæne. Eksporter filtrerede resultater for at producere revisionsklar dokumentation til lovmæssige inspektioner.',
        evidence_trail_title: 'Cross-Module Evidence Trail',
        evidence_trail_desc: "Hvert workflow-trin kan indsamle bevis-id'er, der linker til leverancer, revisionsbegivenheder og compliance-artefakter på tværs af moduler. Fremskridt med bevis-id'er skaber en verificerbar kæde, der forbinder partnerarbejdsgange med regulatorisk dokumentation. Fuldførte arbejdsgange med fulde beviskæder tjener som bevis på levering af compliance-engagement.",
        evidence_ids_label: "Bevis-id'er",
    },
    org_groups: {
        title: 'Organisationsgrupper',
        new: 'Opret gruppe',
        group_name: 'Gruppenavn',
        member_count: 'Medlemsantal',
        root_org: 'Rodorganisation',
        no_groups: 'Ingen org. grupper fundet.',
        add_organization: 'Tilføj Organisation',
        select_organization: 'Vælg en organisation...',
        no_available_orgs: 'Ingen tilgængelige organisationer at tilføje.',
        no_members: 'Ingen medlemsorganisationer i denne gruppe.',
        member_added: 'Organisation tilføjet til gruppen.',
        member_removed: 'Organisation fjernet fra gruppen.',
        already_in_group: 'Denne organisation er allerede i en gruppe.',
        org_not_found: 'Organisation ikke fundet.',
        add_member_error: 'Kunne ikke tilføje organisation til gruppen.',
        remove_member_error: 'Kunne ikke fjerne organisation fra gruppen.',
        confirm_remove_member: 'Er du sikker på, at du vil fjerne "{name}" fra denne gruppe?',
        load_error: 'Kunne ikke indlæse org. gruppedetaljer.',
        page_title_list: 'Organisationsgrupper',
        page_title_detail: 'Organisationsgruppedetaljer',
        page_title_create: 'Opret organisationsgruppe',
        page_title_settings: 'Organisationsgruppeindstillinger',
        page_title_users: 'Gruppebrugere',
        page_title_reports: 'Grupperapporter',
        page_title_dashboard: 'Gruppe Dashboard',
        page_title_audit: 'Koncernrevision',
        page_title_org_audit: 'Organisationsgrupperevision',
        page_title_shared_vendors: 'Delte leverandører',
        page_title_shared_frameworks: 'Delte rammer',
        page_title_shared_forms: 'Delte formularskabeloner',
        page_title_merged_vendors: 'Sammenlagte leverandører',
        page_title_settings_inheritance: 'Indstillinger Arv',
        announce_loaded_groups: 'Indlæste {count} organisationsgrupper',
        announce_loaded_group: 'Indlæst gruppe {name}',
        announce_loaded_users: 'Indlæste {count} gruppebrugere',
        announce_loaded_reports: 'Indlæste {count} rapporter',
        announce_loaded_vendors: 'Indlæste {count} delte leverandører',
        announce_loaded_frameworks: 'Indlæste {count} delte rammer',
        announce_loaded_templates: 'Indlæste {count} delte formularskabeloner',
        announce_loaded_audit: 'Indlæste {count} revisionsbegivenheder',
        announce_loaded_merged: 'Indlæste {shared} delte og {local} lokale leverandører',
        announce_dashboard_loaded: 'Dashboard er indlæst.',
        announce_dashboard_refreshed: 'Dashboard opdateret.',
        announce_dashboard_refresh_failed: 'Kunne ikke opdatere betjeningspanelet.',
        announce_refreshing: 'Opdaterer dashboard...',
        announce_settings_loaded: 'Indstillingsarv indlæst.',
        announce_settings_saved: 'Indstillinger blev gemt.',
        announce_saving_settings: 'Gemmer indstillinger...',
        announce_error_saving: 'Gemmer ...',
        announce_group_dissolved: 'Gruppen opløst.',
        announce_creating_group: 'Opretter organisationsgruppe...',
        announce_group_created: 'Organisationsgruppen blev oprettet.',
        announce_error_creating: 'Fejl ved oprettelse af organisationsgruppe.',
        announce_vendor_published: 'Leverandøren blev offentliggjort.',
        announce_vendor_retired: 'Sælger gik på pension.',
        announce_framework_published: 'Framework blev udgivet med succes.',
        announce_framework_retired: 'Framework pensioneret.',
        announce_template_published: 'Formularskabelon blev offentliggjort.',
        announce_template_retired: 'Formularskabelon er trukket tilbage.',
        announce_role_assigned: 'Rolle tildelt.',
        announce_error_assign_role: 'Fejl under tildeling af rolle.',
        announce_role_revoked: 'Rollen blev tilbagekaldt.',
        announce_report_generating: 'Genererer rapport...',
        announce_report_requested: 'Rapportgenerering anmodet.',
        announce_error_report: 'Fejl ved anmodning om rapport.',
        announce_report_deleted: 'Rapport slettet.',
        announce_search_applied: 'Søgning anvendt.',
        announce_search_cleared: 'Søgningen blev ryddet.',
        announce_tab_selected: 'Fanen {tab} valgt',
        announce_error_add_member: 'Fejl under tilføjelse af medlem til gruppen.',
        announce_category_reverted: '{category} vendt tilbage til nedarvet fra forælder.',
        announce_category_overridden: '{category} indstillet til lokal tilsidesættelse.',
        announce_override_saved: '{category} tilsidesættelse gemt.',
        announce_export_started: 'Eksporterer revision som {format}...',
        announce_export_completed: 'Eksporten som {format} fuldført.',
        announce_export_failed: 'Eksporten mislykkedes.',
        announce_filter_org: 'Filtreret efter organisation {name}',
        announce_filter_org_cleared: 'Organisationsfilter ryddet',
        announce_filter_event: 'Filtreret efter begivenhedstype {type}',
        announce_filter_cleared: 'Filter ryddet',
        announce_download: 'Downloader {name} som {format}',
        error_load_groups: 'Kunne ikke indlæse organisationsgrupper. Prøv venligst igen.',
        error_load_users: 'Gruppebrugere kunne ikke indlæses.',
        error_load_reports: 'Grupperapporter kunne ikke indlæses.',
        error_load_dashboard: 'Gruppekontrolpanelet kunne ikke indlæses.',
        error_load_settings: 'Gruppeindstillinger kunne ikke indlæses.',
        error_load_vendors: 'Kunne ikke indlæse flettede leverandører.',
        error_save_settings: 'Indstillingerne kunne ikke gemmes.',
        error_dissolve_group: 'Det lykkedes ikke at opløse gruppen.',
        error_assign_role: 'Rolle kunne ikke tildeles.',
        error_create_group: 'Kunne ikke oprette organisationsgruppe. Prøv venligst igen.',
        error_request_report: 'Kunne ikke anmode om rapport.',
        error_group_name_required: 'Gruppenavn er påkrævet.',
        success_settings_saved: 'Indstillinger blev gemt.',
        confirm_retire_vendor: 'Er du sikker på, at du vil trække leverandøren "{name}" tilbage?',
        confirm_retire_framework: 'Er du sikker på, at du vil trække rammen "{name}" tilbage?',
        confirm_retire_template: 'Er du sikker på, at du vil trække formularskabelonen "{name} tilbage?',
        confirm_dissolve_group: 'Er du sikker på, at du vil opløse "{name}"? Denne handling kan ikke fortrydes.',
        confirm_revoke_role: 'Er du sikker på, at du vil tilbagekalde rollen "{role}" fra "{name}"?',
        confirm_delete_report: 'Er du sikker på, at du vil slette rapporten "{name}"?',
        dissolve_group: 'Opløs gruppe',
        dissolve_warning: 'Dette vil permanent opløse organisationsgruppen. Alle medlemsorganisationer vil blive fjernet.',
        count_groups: '{count} gruppe',
        count_groups_plural: '{count} grupper',
        count_users: '{count} bruger',
        count_users_plural: '{count} brugere med grupperoller',
        count_events: '{count} begivenhed',
        count_events_plural: '{count} begivenheder',
        count_reports: '{count} rapport',
        count_reports_plural: '{count} rapporterer',
        count_vendors: '{count} leverandør delt på tværs af gruppen',
        count_vendors_plural: '{count} leverandører delt på tværs af gruppen',
        count_frameworks: '{count} rammer delt på tværs af gruppen',
        count_frameworks_plural: '{count} rammer deles på tværs af gruppen',
        count_templates: '{count} skabelon delt på tværs af gruppen',
        count_templates_plural: '{count} skabeloner delt på tværs af gruppen',
        count_shared_vendors: '{count} delte leverandører',
        count_local_vendors: '{count} lokale leverandører',
        merged_combined_view: 'Kombineret visning af {shared} delte og {local} lokale leverandører',
        dashboard: {
            title: 'Gruppeoversigt',
            loading: 'Indlæser dashboarddata...',
            total_groups: 'Grupper i alt',
            total_members: 'Medlemmer i alt',
            active_groups: 'Aktive grupper',
            overall_compliance_score: 'Samlet overholdelsesscore',
            group_risk_level: 'Grupperisikoniveau',
            sla_compliance: 'SLA Overholdelse',
            compliance_posture: 'Overholdelsesstilling',
            risk_heatmap: 'Risk Heatmap',
            sla_performance: 'SLA Ydeevne',
            recent_incidents: 'Seneste hændelser',
            recent_activity: 'Seneste aktivitet',
            refresh_label: 'Opdater dashboarddata',
            score_good: 'Godt',
            score_needs_attention: 'Har brug for opmærksomhed',
            score_critical: 'Kritisk',
            trend_up: 'Trender op',
            trend_down: 'Trend nedad',
            trend_stable: 'Stabil',
            risk_low: 'Low',
            risk_medium: 'Medium',
            risk_high: 'Høj',
            risk_critical: 'Kritisk',
            risk_unknown: 'Ukendt',
            col_organization: 'Organisation',
            col_score: 'Score',
            col_trend: 'Trend',
            col_open_issues: 'Åbne numre',
            col_critical_issues: 'Kritiske spørgsmål',
            col_health: 'Sundhed',
            col_vendor_risk: 'Sælgerrisiko',
            col_data_risk: 'Datarisiko',
            col_compliance_risk: 'Overholdelsesrisiko',
            col_total_cases: 'Sager i alt',
            col_on_time: 'Til tiden',
            col_breached: 'Overtrådt',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Gns. dage',
            sr_vendor_risk: 'Leverandørrisikoscore: {score}',
            sr_data_risk: 'Datarisikoscore: {score}',
            sr_compliance_risk: 'Overholdelsesrisikoscore: {score}',
            sr_sla_breaches: 'Advarsel: {count} SLA brud',
            sr_severity: 'Sværhedsgrad: {severity}',
        },
        detail: {
            title: 'Gruppedetaljer',
            members: 'Medlemmer',
            settings: 'Indstillinger',
            audit: 'Revision',
            sr_loading: 'Indlæser organisationsgruppeoplysninger',
            sr_loading_hierarchy: 'Indlæser hierarki',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'medlemsorganisation',
            root_org_label: 'rodorganisation',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Gruppebrugere',
            add_user: 'Tilføj bruger',
            no_users: 'Ingen brugere i dette gruppe.',
            role_group_admin: 'Gruppe Admin',
            role_group_reader: 'Gruppelæser',
            sr_loading: 'Indlæser gruppebrugere',
        },
        reports: {
            title: 'Grupperapporter',
            no_reports: 'Ingen rapporter fundet.',
            generate_report: 'Generer ny rapport',
            report_type_compliance: 'Overholdelsesoversigt',
            report_type_risk: 'Risikooversigt',
            report_type_sla: 'SLA Ydeevne',
            report_type_incident: 'Hændelsesrapport',
            report_type_executive: 'Executive Brief',
            report_type_board: 'Bestyrelsens beretning',
            status_queued: 'I kø',
            status_generating: 'Genererer',
            status_completed: 'Afsluttet',
            status_failed: 'Mislykkedes',
            period_start: 'Periode start',
            period_end: 'Periodeafslutning',
            sr_loading: 'Indlæser grupperapporter',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Delte leverandører',
            no_vendors: 'Ingen delte leverandører fundet.',
            merged_vendors: 'Flettede leverandører',
            publish_form_label: 'Udgiv delt leverandør',
            sr_loading: 'Indlæser delte leverandører',
            sr_loading_merged: 'Indlæser flettede leverandører',
            local_vendors: 'Lokale sælgere',
            source_shared: 'Delt fra gruppe',
            source_local: 'Lokal leverandør',
            category_technology: 'Teknologi',
            category_consulting: 'Rådgivning',
            category_outsourcing: 'Outsourcing',
            category_cloud: 'Sky',
            category_data_processing: 'Databehandling',
            category_other: 'Andet',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Medium',
            risk_tier_high: 'Høj',
            risk_tier_critical: 'Kritisk',
            compliance_compliant: 'Overensstemmende',
            compliance_non_compliant: 'Ikke-kompatibel',
            compliance_pending: 'Afventer',
            compliance_under_review: 'Under gennemgang',
            status_active: 'Aktiv',
            status_retired: 'Pensioneret',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Rammeværkskode',
            title: 'Delte rammer',
            no_frameworks: 'Ingen delte rammeværker fundet.',
            publish_form_label: 'Udgiv delte rammer',
            sr_loading: 'Indlæser delte rammer',
            status_active: 'Aktiv',
            status_retired: 'Pensioneret',
        },
        shared_forms: {
            title: 'Delte formularskabeloner',
            no_forms: 'Ingen delte formular skabeloner fundet.',
            publish_form_label: 'Udgiv delt formularskabelon',
            sr_loading: 'Indlæser delte formularskabeloner',
            status_active: 'Aktiv',
            status_retired: 'Pensioneret',
        },
        audit: {
            title: 'Grupperevision',
            consolidated_title: 'Konsolideret revisionsspor',
            consolidated_subtitle: 'Tværorganisatoriske revisionshændelser for denne gruppe',
            audit_log_title: 'Gruppe Revision Log',
            event_type: 'Hændelsestype',
            actor: 'Aktør',
            timestamp: 'Tidsstempel',
            source_org: 'Kildeorganisation',
            org: 'Org',
            summary: 'Resumé',
            details: 'Detaljer',
            no_events: 'Ingen revision hændelser fundet.',
            all_events: 'Alle hændelser',
            all_orgs: 'Alle org.',
            organization: 'Organisation',
            from: 'Fra',
            to: 'To',
            search_events: 'Søg hændelser...',
            clear: 'Ryd',
            events_24h: 'Hændelser (24t)',
            events_7d: 'Hændelser (7d)',
            active_orgs: 'Aktive org.',
            exporting: 'Eksporterer...',
            export_csv: 'Eksporter CSV',
            export_json: 'Eksporter JSON',
            back_to_group: 'Tilbage til gruppe',
            back_to_detail: 'Tilbage til detalje',
            sr_loading: 'Indlæser revisionsbegivenheder',
            sr_loading_summary: 'Indlæser revisionsoversigt',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Gruppeindstillinger',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Indstillingsarv',
            inheritance_subtitle: 'Konfigurer, hvilke indstillingskategorier der arver fra gruppen i forhold til at bruge lokale tilsidesættelser',
            sla_settings: 'SLA-indstillinger',
            calendar_settings: 'Kalenderindstillinger',
            notification_settings: 'Notifikationsindstillinger',
            intake_settings: 'Indtagsindstillinger',
            cmp_integration: 'CMP-integration',
            overridden: 'Tilsidesat',
            inheriting: 'Nedarvet',
            hide_defaults: 'Skjul standarder',
            show_group_defaults: 'Vis gruppestandarder',
            group_defaults: 'Gruppestandarder',
            inherit_from_group: 'Nedarv fra gruppe',
            use_local_override: 'Use Lokal Tilsidesættelse',
            local_override_json: 'Lokal Tilsidesættelse (JSON)',
            save_override: 'Gem tilsidesættelse',
            saving: 'Gemmer ...',
            sr_loading: 'Indlæser indstillinger arv',
            sr_loading_settings: 'Indlæser gruppeindstillinger',
            sr_enabled: 'Aktiveret',
            sr_disabled: 'Deaktiveret',
            sr_override_active: 'Lokal tilsidesættelse aktiv',
            sr_inherited: 'Nedarvet fra forælder',
            danger_zone: 'Farezone',
            sharing_options: 'Delingsmuligheder',
            advanced_options: 'Avancerede indstillinger',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Aktiv',
        status_dissolved: 'Opløst',
        status_pending: 'Afventer',
        status_suspended: 'Suspenderet',
    },
    integrations: {
        nav: {
            webhooks: 'Webhooks',
            domains: 'Domæner',
            export_schedules: 'Eksportplaner',
            email_log: 'E-maillog',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Brugere',
            roles: 'Roller',
            profile: 'Min Profil',
        },
        security: {
            mfa: 'MFA-indstillinger',
            sso: 'SSO-konfiguration',
            api_keys: 'API-nøgler',
            service_accounts: 'Tjenestekonti',
            ip_allowlist: 'IP-hvidliste',
            delegations: 'Delegeringer',
            break_glass: 'Nødadgang',
            session_settings: 'Sessionsindstillinger',
            active_sessions: 'Aktive Sessioner',
            access_reviews: 'Adgangsgennemgange',
            my_reviews: 'Mine Gennemgange',
            role_analytics: 'Rolleanalyse',
            attestation_schedules: 'Attesteringsplaner',
            sod_rules: 'SoD-regler',
            sod_violations: 'SoD-overtrædelser',
        },
        module_config: {
            title: 'Modulkonfiguration',
            subtitle: 'Aktivér eller deaktivér funktionsmoduler for din organisation. Deaktiverede moduler skjules fra navigationen.',
            enabled: 'Aktiveret',
            disabled: 'Deaktiveret',
            save: 'Gem Konfiguration',
            saving: 'Gemmer ...',
            saved: 'Konfiguration gemt med succes',
            nav_label: 'Modulkonfiguration',
            settings: 'Indstillinger',
            webhooks: 'Webhooks',
            domains: 'Domæner',
            export_schedules: 'Eksportplaner',
            email_log: 'E-maillog',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Filer',
            configuration: 'Konfiguration',
            browser: 'Filbrowser',
            dashboard: 'Dashboard for filer',
        },
        immutable_audit_trails: {
            nav_group_label: 'Revision & Bevis',
            nav_settings: 'Indstillinger',
            nav_status: 'Bevisstatus',
            nav_integrity_center: 'Integritetscenter',
            nav_usage: 'Brug & Credits',
            settings_title: 'Uforanderlige revisionsspor — Indstillinger',
            settings_subtitle: 'Per-org-indstillinger for tilføjelsen Immutable Audit Trails.',
            settings_banner: 'Tilføjelsen Immutable Audit Trails er ikke aktiveret for din organisation. Aktiver den her for at begynde at forankre bevis for Tier-A- og Tier-B-revisionsbegivenheder.',
            status_title: 'Uforanderlige revisionsspor — Bevisstatus',
            status_subtitle: 'Per-event bevis relationer på tværs af moduler.',
            status_banner: 'Tilføjelsen Immutable Audit Trails er ikke aktiveret for din organisation. Besøg Indstillinger for at aktivere det.',
            integrity_center_title: 'Uforanderlige revisionsspor — Integritetscenter',
            integrity_center_subtitle: 'Bekræft beviser og eksporter integritetspakker.',
            integrity_center_banner: 'Tilføjelsen Immutable Audit Trails er ikke aktiveret for din organisation. Besøg Indstillinger for at aktivere det, før du kører verifikation eller eksporterer integritetspakker.',
            usage_title: 'Uforanderlige revisionsspor — brug og kreditter',
            usage_subtitle: 'Kreditsaldo, pakkekøb og brugshistorik.',
            usage_banner: 'Aktiver Immutable Audit Trails i Indstillinger for at begynde at forbruge beviskreditter.',
            module_tips_placeholder: 'Tips og vejledning til denne overflade er tilgængelige i hjælpecenteret.',
            column_label: 'Bevis',
            no_proof_aria: 'Intet bevis registreret',
            proof_status_receipt_label: 'kvittering {id}',
            in_progress_hint: 'I gang — opdateres automatisk',
            status_pills: {
                pending: 'Afventer',
                submitted: 'Indsendt',
                anchored: 'Forankret',
                confirmed: 'Bekræftet',
                retrying: 'Prøver igen',
                paused_cap: 'sat på pause (cap)',
                failed: 'Mislykkedes',
                failed_auth: 'Godkendelse mislykkedes',
            },
            status_tooltips: {
                pending: 'Afventer — i kø til korrekturtjenesten. Flytter til Indsendt snart; ingen handling nødvendig.',
                submitted: 'Indsendt - din begivenhed blev sendt til korrekturtjenesten. Til batch-tilstand venter den på at blive forseglet og forankret på kæden. Opdaterer automatisk; ingen handling nødvendig.',
                anchored: 'Forankret — batchen er skrevet på kæden og afventer endelig bekræftelse. Opdaterer automatisk; ingen handling nødvendig.',
                confirmed: 'Bekræftet - beviset er forankret på kæden og kan verificeres.',
                retrying: 'Prøver igen - der opstod en forbigående fejl. Afstemningen prøver automatisk igen; ingen handling nødvendig.',
                paused_cap: 'Pause – dit månedlige beviskreditloft er nået. Indløs flere kreditter, eller vent til næste faktureringsmåned.',
                failed: 'Mislykkedes — forankring lykkedes ikke. Afstemningen vil automatisk prøve igen; kontakt support, hvis det fortsætter.',
                failed_auth: 'Godkendelse mislykkedes - bevistjenesten afviste vores legitimationsoplysninger. Tjek siden Indstillinger for uforanderlige revisionsspor.',
            },
            mode_pills: {
                direct: 'Direkte',
                batch: 'Batch',
            },
            drawer: {
                title: 'Bevis detaljer',
                close: 'Luk',
                section_proof: 'Bevis',
                section_batch: 'Batch medlemskab',
                status: 'Status',
                proof_reference: 'Bevis reference',
                receipt_id: 'Kvitterings-id',
                taxonomy_class: 'Taksonomi klasse',
                units_consumed: 'Forbrugte enheder',
                created_at: 'Oprettet',
                verification_ref: 'Kædebekræftelsesreference',
                chain: 'Kæde',
                tx_hash: 'Tx hash',
                block_ref: 'Blok ref',
                batch_reference: 'Batch reference',
                leaf_index: 'Bladindeks',
                local_event_hash: 'Lokal begivenhed hash',
                show_inclusion: 'Vis inklusionssti',
                hide_inclusion: 'Skjul inklusionssti',
                privacy_note: 'Nyttelast er hashed; ingen personlige data forlod ComplianceOne til denne begivenhed.',
                verify_now: 'Bekræft nu',
                verify_pending: 'Bekræftelse i gang...',
                verify_verified: 'Verificeret - lokal + kædesidekontrol bestået.',
                verify_issue_prefix: 'Bekræftelsesproblem: ',
                verify_failed: 'Bekræftelsesopkald mislykkedes.',
                verify_result_prefix: 'Bekræftelsesdom: ',
                verify_pending_note: 'Verifikation afventer: dette bevis afventer stadig forankring på kæden. Prøv igen, når batchen er forseglet.',
                awaiting_anchor_note: 'Dette bevis er en del af en åben batch. Verifikation bliver tilgængelig, når batchen er forseglet og forankret på kæden - typisk inden for organisationens konfigurerede batchvindue. Statusmærket opdateres automatisk.',
                terminal_error_note: 'Dette bevis kunne ikke forankres. Bevis-statusafstemningen vil automatisk prøve igen; manuel verifikation har intet at kontrollere endnu.',
            },
            integrity_center: {
                section_run: 'Kør verifikation',
                section_progress: 'Kør fremskridt',
                section_issues: 'Problemer',
                period_start: 'Periode start',
                period_end: 'Periodeafslutning',
                module_filter: 'Moduler (kommasepareret, valgfrit)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Kør verifikation',
                export_button: 'Eksporter integritetspakke',
                export_success: 'Integrity Pack eksporteret.',
                export_failed: 'Eksporten mislykkedes.',
                empty_disabled: 'Aktiver uforanderlige revisionsspor i Indstillinger for at køre verifikationspas.',
                error_period_required: 'Vælg en start- og slutdato.',
                error_start_run: 'Kunne ikke starte kørslen.',
                processed: 'Behandlet: {processed} af {total}',
                col_verdict: 'Dom',
                col_count: 'Tæl',
                col_proof_link: 'Bevis link',
                col_module: 'modul',
                col_event_type: 'Begivenhedstype',
                col_expected: 'Forventet hash',
                col_recomputed: 'Genberegnet hash',
                col_raised_at: 'Rejst kl',
                any_verdict: 'Enhver dom',
                no_issues: 'Ingen problemer fundet.',
            },
            settings_page: {
                load_error: 'Indstillinger kunne ikke indlæses.',
                save: 'Gem',
                save_success: 'Indstillinger gemt.',
                save_error: 'Gem mislykkedes.',
                discard: 'Kassér ændringer',
                read_only_banner: 'Du har skrivebeskyttet adgang. Kontakt en platformsadministrator for at foretage ændringer.',
                section_status: 'Status og aktivering',
                enabled_label: 'Tilføjelse aktiveret',
                enabled_help: 'Når den er aktiveret, er hver Tier-A- og Tier-B-revisionsbegivenhed hashed og forankret.',
                section_mode: 'Standard korrekturtilstand',
                mode_direct: 'Direkte (et anker pr. begivenhed)',
                mode_batch: 'Batch (Merkle-root anker pr. gruppe)',
                mode_hybrid: 'Hybrid (standard i registreringsdatabasen pr. klasse)',
                batch_window_label: 'Batchvindue (minutter, 1–60)',
                section_taxonomy: 'Beskyttet begivenhedstaksonomi',
                taxonomy_help: 'Tilsidesættelser pr. begivenhedsklasse. Tier A → direkte tilstand, Tier B → batch-tilstand, Fra → ikke korrektur.',
                taxonomy_search: 'Søgning tilsidesætter',
                taxonomy_reset: 'Nulstil til standardindstillinger',
                taxonomy_event_class: 'Event klasse',
                taxonomy_override: 'Tilsidesæt',
                taxonomy_actions: 'Handlinger',
                taxonomy_clear: 'Klar',
                taxonomy_no_overrides: 'Ingen tilsidesættelser - ved hjælp af registreringsstandarder.',
                taxonomy_add_class: 'event_class.kode',
                taxonomy_add: 'Tilføj tilsidesættelse',
                section_caps: 'Caps & limits',
                cap_label: 'Månedligt loft (enheder; blank = ubegrænset)',
                warn_label: 'Advarselstærskel (enheder; skal være mindre end grænsen)',
                auto_pause_label: 'Sæt automatisk nye korrektur på pause, når loftet er nået',
                section_credentials: 'Legitimationsoplysninger',
                credential_id: 'Legitimations-id',
                credential_rotated_at: 'Sidst roteret',
                rotate_button: 'Roter legitimationsoplysninger',
                rotate_success: 'Legitimationsoplysninger roteret. Gammel legitimation gyldig i {n} minutter mere.',
                rotate_failed: 'Rotation af legitimationsoplysninger mislykkedes.',
                rotate_modal_title: 'Vil du rotere AesirX legitimationsoplysninger?',
                rotate_modal_body: 'Rotation udsteder en ny legitimation. De gamle legitimationsoplysninger forbliver accepteret af AesirX i en periode på 60 minutter, så flyarbejdere kan afslutte deres nuværende job. Efter 60 minutter er den gamle legitimation tilbagekaldt.',
                rotate_modal_confirm: 'Roter nu',
                tier_a_off_modal_title: 'Deaktivere korrektur for en begivenhedsklasse?',
                tier_a_off_modal_body: 'Hvis du indstiller en hændelsesklasse til "Fra", stopper prøvetrykket i at forankre disse hændelser. Arkitektur §6 forklarer den regulatoriske implikation: Tier-A hændelser er regulatorrelevante; Hvis du slår dem fra, kan det ødelægge dit revisionsspors forsvarlighed.',
                tier_a_off_modal_confirm: 'Jeg forstår - anvend tilsidesættelse',
                error_warn_lt_cap: 'Advarselstærskel skal være mindre end den månedlige grænse.',
                error_window_range: 'Batchvinduet skal være mellem 1 og 60 minutter.',
                error_run_in_progress: 'En verifikationskørsel er i gang; kan ikke deaktivere tilføjelsen lige nu.',
            },
            status_page: {
                filter_status: 'Status',
                filter_mode: 'Bevistilstand',
                filter_module: 'modul',
                filter_source_collection: 'Kildesamling',
                filter_from_date: 'Fra',
                filter_to_date: 'Til',
                filter_search: 'Søg',
                any_status: 'Enhver status',
                any_mode: 'Enhver tilstand',
                col_module: 'modul',
                col_event_type: 'Begivenhedstype',
                col_proof_status: 'Status',
                col_proof_mode: 'tilstand',
                col_created_at: 'Oprettet',
                col_units: 'Enheder',
                empty_disabled: 'Tilføjelsen Immutable Audit Trails er ikke aktiveret for din organisation. Besøg Indstillinger for at aktivere det.',
                empty_enabled_no_rows: 'Ingen bevislinks matcher de aktuelle filtre.',
                export_csv: 'Eksporter CSV',
                export_xlsx: 'Eksporter XLSX',
                export_success: 'Eksporten er fuldført',
                export_failed: 'Eksporten mislykkedes',
            },
            usage_page: {
                section_alerts: 'Advarsler',
                alert_dismiss: 'Afvis',
                alert_dismiss_aria: 'Afvis underretning {id}',
                alert_dismissed: 'Advarsel afvist',
                alert_dismiss_failed: 'Kunne ikke afvise underretningen',
                section_balance: 'Kreditsaldo',
                balance_label: 'Tilgængelige kreditter',
                balance_hint: 'Enheder tilbage (1 enhed = 1 beskyttet hændelsesbevis).',
                section_current_month: 'Nuværende måned',
                consumed_label: 'Forbrugt',
                granted_label: 'Indrømmet',
                refunded_label: 'Refunderet',
                net_label: 'Netto',
                section_history: 'Brugshistorik',
                history_kind: 'Venlig',
                history_units: 'Enheder',
                history_balance_after: 'Balance efter',
                history_notes: 'Noter',
                history_at: 'Hvornår',
                cap_warning_banner: 'Du har overskredet din advarselsgrænse for denne faktureringsmåned. Overvej at indløse yderligere kreditter.',
                cap_paused_banner: 'Månedligt loft nået. Bevisindsendelser sættes på pause indtil næste måned, eller indtil grænsen øges.',
                section_redeem: 'Indløs kreditpakke',
                redeem_purchase_id: 'Fakturanummer',
                redeem_package: 'Pakke',
                redeem_button: 'Indløs',
                redeem_pending: 'Indløser...',
                redeem_success: 'Kreditter indløst',
                redeem_failed: 'Indløsning mislykkedes',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'enheder',
                alert_dismissing: 'Afviser...',
                alert_kind: {
                    cap_reached: 'Månedligt loft nået',
                    low_balance: 'Lav balance',
                    auth_failure: 'Godkendelsesfejl',
                    merkle_mismatch: 'Merkle misforhold',
                    batch_terminal_failure: 'Batch-terminalfejl',
                    direct_terminal_failure: 'Direkte indsendelsesterminalfejl',
                },
                usage_kind: {
                    consume: 'Forbruge',
                    refund: 'Tilbagebetaling',
                    grant: 'Grant',
                    adjustment: 'Justering',
                    reservation: 'Reservation',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Starter',
                package_growth: 'Vækst',
                package_enterprise: 'Enterprise',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Køb flere kreditter',
                    purchase_title: 'Køb flere kreditter',
                    purchase_subtitle: 'Vælg en pakke. Du bliver ført til sikker Stripe-betaling, og din faktura sendes til dig på e-mail.',
                    checkout_button: 'Fortsæt til betaling',
                    checkout_pending: 'Åbner betaling…',
                    cancel_button: 'Annuller',
                    purchase_error: 'Kunne ikke starte betalingen. Prøv igen om lidt.',
                    redeem_subtitle: "Har du allerede betalt? Vælg den pakke, du har købt, og indtast faktura-id'et fra din e-mail for at tilføje kreditterne.",
                    error_not_found: 'Der blev ikke fundet nogen faktura med det nummer. Kontrollér fakturanummeret, og prøv igen.',
                    error_not_paid: 'Denne faktura er endnu ikke markeret som betalt.',
                    error_org_mismatch: 'Denne faktura blev ikke udstedt til din organisation.',
                    error_package_mismatch: 'Denne faktura passer ikke til den valgte pakke.',
                    error_billing_unavailable: 'Kunne ikke nå faktureringstjenesten. Prøv igen om lidt.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Filkonfiguration',
            subtitle: 'Konfigurer grænser for upload af filer, opbevaringspolitikker og tilsidesættelser af typen pr. ejer for platformens vedhæftede overflade.',
            section_global: 'Globale standardindstillinger',
            section_global_help: 'Disse begrænsninger gælder for alle filer, der uploades af brugere i din organisation, medmindre en tilsidesættelse af den enkelte ejertype nedenfor ændrer dem.',
            section_overrides: 'Per-ejer-type tilsidesættelser',
            section_overrides_help: 'Tilsidesæt de globale grænser for en bestemt ejertype. Værdier pr. ejer vinder altid over den globale indstilling.',
            max_file_size_label: 'Maks filstørrelse',
            max_file_size_hint: 'Per individuel upload (MiB)',
            max_total_attachments_label: 'Max samlede vedhæftede filer',
            max_total_attachments_hint: 'På tværs af hele organisationen',
            max_total_size_label: 'Max samlet lagerplads',
            max_total_size_hint: 'På tværs af hele organisationen (MiB)',
            allowed_mime_types_label: 'Tilladte filtyper',
            allowed_mime_types_placeholder: 'Vælg MIME-typer',
            retention_days_label: 'Opbevaringsperiode (dage)',
            retention_days_hint: 'Hvor længe filer skal opbevares, før de er berettiget til sletning. Lad stå tomt for ingen opbevaringspolitik.',
            legal_hold_label: 'Lovligt hold',
            legal_hold_hint: 'Når det er aktiveret, kræver hård sletning af en fil af denne ejertype tilladelse til tilsidesættelse af juridisk tilbageholdelse og eksplicit operatørtilmelding.',
            save: 'Gem ændringer',
            saving: 'Gemmer ...',
            reset: 'Nulstil',
            save_success: 'Indstillinger gemt',
            empty_state_title: 'Brug af platformens standardindstillinger',
            empty_state_body: 'Din organisation kører i øjeblikket på platformens standardfilpolitik. Rediger en værdi nedenfor for at oprette en tilsidesættelse på organisationsniveau.',
            read_only_banner: 'Du har skrivebeskyttet adgang til filkonfiguration. Kontakt en platformsadministrator for at foretage ændringer.',
            owner_type_default_badge: 'Standard',
            owner_type_overridden_badge: 'Tilsidesat',
            owner_type_label: 'Ejertype',
            owner_type_collapse: 'Kollaps',
            owner_type_expand: 'Udvid',
            override_save: 'Gem tilsidesættelse',
            override_remove: 'Fjern tilsidesættelse',
            mime_labels: {
                'application/pdf': 'PDF dokument',
                'image/png': 'PNG billede',
                'image/jpeg': 'JPEG billede',
                'image/jpg': 'JPG billede',
                'application/msword': 'Word-dokument (legacy)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word dokument',
                'application/vnd.ms-excel': 'Excel-regneark (legacy)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel regneark',
                'text/csv': 'CSV fil',
                'text/plain': 'Almindelig tekst',
                'message/rfc822': 'E-mail besked',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Filbrowser',
            subtitle: 'Søg, filtrer og administrer alle vedhæftede filer, der er uploadet på tværs af organisationen.',
            filter_owner_type: 'Ejertype',
            filter_uploader: 'Uploadet af',
            filter_from: 'Uploadet efter',
            filter_to: 'Uploadet før',
            filter_size_min: 'Min størrelse (MiB)',
            filter_size_max: 'Maksimal størrelse (MiB)',
            filter_mime: 'Filtype',
            filter_deleted_state: 'Sletningstilstand',
            filter_deleted_any: 'Alle filer',
            filter_deleted_only: 'Kun slettet',
            filter_deleted_exclude: 'Udelad slettet',
            filter_orphaned: 'Kun forældreløse',
            filter_search_placeholder: 'Søg filnavn...',
            filter_apply: 'Anvend filtre',
            filter_clear: 'Ryd filtre',
            col_filename: 'Filnavn',
            col_owner_type: 'Ejertype',
            col_owner_entity: 'Ejer',
            col_uploader: 'Uploadet af',
            col_uploaded_at: 'Uploadet',
            col_size: 'Størrelse',
            col_mime: 'Type',
            col_state: 'stat',
            col_actions: 'Handlinger',
            state_active: 'Aktiv',
            state_deleted: 'Slettet',
            state_orphaned: 'Forældreløs',
            action_view_detail: 'Se detaljer',
            action_download: 'Download',
            action_ai_edits: 'AI Redigeringer',
            action_soft_delete: 'Blød sletning',
            action_restore: 'Gendan',
            action_hard_delete: 'Hård sletning',
            bulk_soft_delete: 'Soft-delete valgt',
            bulk_export_csv: 'Eksporter CSV',
            bulk_select_all: 'Vælg alle',
            reason_label: 'Årsag',
            reason_min_10_chars: 'Årsag er påkrævet (mindst 10 tegn)',
            reason_min_20_chars: 'Årsag er påkrævet (mindst 20 tegn)',
            legal_hold_override_label: 'Tilsidesæt juridisk hold',
            legal_hold_override_warning: 'Denne fil er under juridisk hold. Hard-sletning vil blive registreret i revisionssporet med dit grund- og tilsidesættelsesflag.',
            empty_state_title: 'Ingen filer matcher dine filtre',
            empty_state_body: 'Juster dine filtre for at se resultater, eller ryd dem for at se alle filer.',
            pagination_prev: 'Forrige',
            pagination_next: 'Næste',
            pagination_page: 'Side {page} af {total}',
            pagination_size_label: 'Per side',
            pagination_total: 'I alt: {count}',
            toast_soft_deleted: 'Filen blev blødt slettet',
            toast_restored: 'Filen blev gendannet',
            toast_hard_deleted: 'Fil slettet permanent',
            toast_bulk_deleted: '{count} fil(er) slettet',
            toast_error: 'Handlingen mislykkedes',
            confirm_soft_delete_title: 'Blød slette fil',
            confirm_restore_title: 'Gendan fil',
            confirm_hard_delete_title: 'Slet fil permanent',
            confirm_bulk_soft_delete_title: 'Soft slet valgte filer',
            confirm_submit: 'Bekræft',
            confirm_cancel: 'Annuller',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Fildetaljer',
            close: 'Luk',
            meta_filename: 'Filnavn',
            meta_size: 'Størrelse',
            meta_mime: 'Type',
            meta_owner_type: 'Ejertype',
            meta_owner_entity: 'Ejer enhed',
            meta_uploader: 'Uploadet af',
            meta_uploaded_at: 'Uploadet',
            meta_storage_path: 'Opbevaringssti',
            meta_deleted_state: 'Sletningstilstand',
            audit_section_title: 'Revisionsspor',
            audit_load_more: 'Indlæs flere begivenheder',
            audit_empty: 'Ingen revisionsbegivenheder registreret for denne fil.',
            audit_config_changed: 'Konfiguration ændret',
            audit_soft_deleted: 'Blødt slettet',
            audit_restored: 'Restaureret',
            audit_hard_deleted: 'Slettes permanent',
            audit_downloaded: 'Downloadet',
            audit_quota_breached: 'Kvotebrud forsøgt',
            audit_owner_type_download_denied: 'Hentning afvist',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} af {actor} den {at}',
            action_download: 'Download',
            action_soft_delete: 'Blød sletning',
            action_restore: 'Gendan',
            action_hard_delete: 'Hård sletning',
            effective_settings_title: 'Effektiv politik',
            effective_max_file_size: 'Maks filstørrelse',
            effective_retention_days: 'Fastholdelse',
            effective_legal_hold: 'Lovligt hold',
            effective_legal_hold_yes: 'Aktiv',
            effective_legal_hold_no: 'Ingen',
        },
        files_dashboard: {
            title: 'Dashboard for filer',
            subtitle: 'Lagerbrug, vækst og forældreløse filer på tværs af organisationen.',
            kpi_total_files: 'Samlede filer',
            kpi_total_size: 'Samlet lagerplads',
            kpi_near_quota: 'Kvoteforbrug',
            kpi_orphaned: 'Forældreløse filer',
            chart_by_owner_type: 'Filer efter ejertype',
            chart_growth_30d: '30 dages vækst',
            toggle_count: 'Ved optælling',
            toggle_size: 'Efter størrelse',
            orphaned_panel_title: 'Forældreløse filer (top 20)',
            orphaned_view_all: 'Se alle forældreløse filer',
            orphaned_empty: 'Ingen forældreløse filer fundet.',
            growth_empty: 'Ingen uploadaktivitet inden for de seneste 30 dage.',
            retention_n_days: '{days} dag(e)',
            retention_none: 'Ingen politik',
        },
    },
    category_labels: {
        'user-guide': 'Brugervejledning',
        'admin-guide': 'Admin guide',
        'getting-started': 'Kom godt i gang',
        faq: 'FAQ',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Taster',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Admin',
        aesirx: 'AesirX',
        purposes: 'Formål',
        preferences: 'Præferencer',
        onboarding: 'Onboarding',
        'first-steps': 'Første skridt',
        login: 'Log ind',
        navigation: 'Navigation',
        users: 'Brugere',
        user: 'Bruger',
        roles: 'Roller',
        role: 'Rolle',
        'access-control': 'Adgangskontrol',
        'access-management': 'Adgangsstyring',
        permissions: 'Tilladelser',
        permission: 'Tilladelse',
        consent: 'Samtykke',
        'consent-management': 'Samtykkestyring',
        privacy: 'Privatliv',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Revision',
        auditing: 'Revision',
        breach: 'Overtrædelse',
        incident: 'Hændelse',
        incidents: 'Hændelser',
        notification: 'Meddelelse',
        notifications: 'Meddelelser',
        reporting: 'Indberetning',
        risk: 'Risiko',
        vendor: 'Sælger',
        vendors: 'Sælgere',
        'vendor-risk': 'Sælgerrisiko',
        assessment: 'Vurdering',
        assessments: 'Vurderinger',
        dpia: 'DPIA',
        'cross-border': 'Grænseoverskridende',
        transfer: 'Overførsel',
        transfers: 'Overførsler',
        deletion: 'Sletning',
        retention: 'Fastholdelse',
        'data-mapping': 'Datakortlægning',
        discovery: 'Opdagelse',
        classification: 'Klassifikation',
        workflow: 'Arbejdsgang',
        workflows: 'Arbejdsgange',
        template: 'Skabelon',
        templates: 'Skabeloner',
        catalog: 'Katalog',
        monitoring: 'Overvågning',
        tasks: 'Opgaver',
        task: 'Opgave',
        'task-management': 'Opgavestyring',
        organizations: 'Organisationer',
        org: 'Organisation',
        governance: 'Governance',
        compliance: 'Overholdelse',
        forms: 'Formularer',
        form: 'Form',
        connectors: 'Stik',
        connector: 'Stik',
        integration: 'Integration',
        integrations: 'Integrationer',
        security: 'Sikkerhed',
        encryption: 'Kryptering',
        backup: 'Backup',
        recovery: 'Genopretning',
        maintenance: 'Vedligeholdelse',
        settings: 'Indstillinger',
        configuration: 'Konfiguration',
        sla: 'SLA',
        escalation: 'Eskalering',
        remediation: 'Udbedring',
        evidence: 'Beviser',
        'case-management': 'Sagsbehandling',
        profile: 'Profil',
        delegation: 'Delegation',
        'service-accounts': 'Servicekonti',
        license: 'Licens',
        'license-management': 'Licenshåndtering',
        plan: 'Planlæg',
        plans: 'Planer',
        dashboard: 'Dashboard',
        'web-consent': 'Websamtykke',
        'partner-marketplace': 'Partnermarkedsplads',
        partner: 'Partner',
        partners: 'Partnere',
        deliverables: 'Leverancer',
        'service-tiers': 'Serviceniveauer',
        'support-tickets': 'Support billetter',
        'audit-trail': 'Revisionsspor',
        'first-time-setup': 'Førstegangsopsætning',
        setup: 'Opsætning',
        quickstart: 'Hurtig start',
        tour: 'Rundvisning',
        tours: 'Ture',
        'sample-data': 'Eksempeldata',
        'data-subject': 'Datasubjekt',
        'data-subjects': 'Datasubjekter',
        pseudonymous: 'Pseudonym',
        identified: 'Identificeret',
        'help-center': 'Hjælpecenter',
        article: 'Artikel',
        articles: 'Artikler',
        search: 'Søg',
        'platform-settings': 'Platformindstillinger',
        'platform-config': 'Platform konfiguration',
        'module-config': 'Modul konfiguration',
        'module-configuration': 'Modul konfiguration',
        'rights-requests': 'Rettighedsanmodninger',
        'access-accountability': 'Få adgang til ansvarlighed',
        'data-discovery': 'Dataopdagelse',
        'incident-ops': 'Incident Operations',
        'deletion-orchestration': 'Sletning Orkestrering',
        'compliance-forms': 'Overholdelsesformularer',
        'program-governance': 'Programstyring',
        localization: 'Lokalisering',
        'privacy-scanner': 'Privacy Scanner',
        'consent-properties': 'Samtykkeegenskaber',
        acceptances: 'Accepter',
        access: 'Adgang',
        'access-review': 'Adgang gennemgang',
        'access-reviews': 'Få adgang til anmeldelser',
        accountability: 'Ansvarlighed',
        acknowledgment: 'Anerkendelse',
        'action-plans': 'Handlingsplaner',
        activation: 'Aktivering',
        activations: 'Aktiveringer',
        'active-response': 'Aktiv respons',
        activity: 'Aktivitet',
        adequacy: 'Tilstrækkelighed',
        'admin-guide': 'Admin guide',
        aggregation: 'Aggregation',
        agreements: 'Aftaler',
        ai: 'AI',
        alerts: 'Advarsler',
        analytics: 'Analytics',
        anchoring: 'Forankring',
        approval: 'Godkendelse',
        approvals: 'Godkendelser',
        apps: 'Apps',
        assets: 'Aktiver',
        assignments: 'Opgaver',
        attestation: 'Attestation',
        'audit-log': 'Revisionslog',
        'audit-pack': 'Revisionspakke',
        authentication: 'Autentificering',
        authority: 'Myndighed',
        'auto-play': 'Automatisk afspilning',
        automated: 'Automatiseret',
        automation: 'Automatisering',
        baseline: 'Baseline',
        batches: 'Batcher',
        bidirectional: 'Tovejs',
        bilingual: 'Tosproget',
        bindings: 'Indbindinger',
        blockchain: 'Blockchain',
        board: 'bestyrelse',
        brand: 'Mærke',
        brds: "BRD'er",
        'break-glass': 'Break-Glass',
        builder: 'Bygmester',
        bulk: 'Bulk',
        'bulk-sync': 'Bulk Sync',
        bundles: 'Bunter',
        calendar: 'Kalender',
        campaigns: 'Kampagner',
        cases: 'Sager',
        categories: 'Kategorier',
        category: 'Kategori',
        certification: 'Certificering',
        chain: 'Kæde',
        change: 'Skift',
        'change-awareness': 'Forandringsbevidsthed',
        'change-log': 'Skift log',
        'change-management': 'Forandringsledelse',
        'change-requests': 'Ændringsanmodninger',
        'cmp-validation': 'CMP Validering',
        collection: 'Samling',
        comparison: 'Sammenligning',
        completeness: 'Fuldstændighed',
        completion: 'Fuldførelse',
        'compliance-posture': 'Overholdelsesstilling',
        'compliance-proof': 'Overholdelsesbevis',
        'compliance-reviews': 'Overholdelsesanmeldelser',
        config: 'Konfig',
        confirmation: 'Bekræftelse',
        conflicts: 'Konflikter',
        consolidation: 'Konsolidering',
        continuous: 'Kontinuerlig',
        contracts: 'Kontrakter',
        controls: 'Kontrolelementer',
        cookies: 'Cookies',
        cooperation: 'Samarbejde',
        coordination: 'Koordinering',
        'corporate-governance': 'Corporate Governance',
        'corrective-actions': 'Korrigerende handlinger',
        coverage: 'Dækning',
        'credential-vault': 'Credential Vault',
        credentials: 'Legitimationsoplysninger',
        cycles: 'Cykler',
        dashboards: 'Dashboards',
        'data-assets': 'Dataaktiver',
        'data-flows': 'Datastrømme',
        'data-sensitivity': 'Datafølsomhed',
        'data-steward': 'Data Steward',
        deadlines: 'Deadlines',
        'decree-356': 'Dekret 356',
        dedup: 'Dedup',
        definitions: 'Definitioner',
        deliverable: 'Leveres',
        departments: 'Afdelinger',
        'dependency-order': 'Afhængighedsrækkefølge',
        deployment: 'Implementering',
        detection: 'Detektion',
        diff: 'Diff',
        'digital-properties': 'Digitale egenskaber',
        dimensions: 'Dimensioner',
        disable: 'Deaktiver',
        'disaster-recovery': 'Disaster Recovery',
        disclosure: 'Afsløring',
        documentation: 'Dokumentation',
        documents: 'Dokumenter',
        domains: 'Domæner',
        dossier: 'Dossier',
        dpa: 'DPA',
        drift: 'Drift',
        'drift-detection': 'Driftsdetektering',
        'due-diligence': 'Due Diligence',
        email: 'Email',
        emergency: 'Nødsituation',
        enable: 'Aktiver',
        'enterprise-map': 'Virksomhedskort',
        erasure: 'Sletning',
        evaluation: 'Evaluering',
        'event-bus': 'Event Bus',
        exceptions: 'Undtagelser',
        execution: 'Udførelse',
        executive: 'Executive',
        exercises: 'Øvelser',
        export: 'Eksporter',
        exports: 'Eksport',
        filing: 'Arkivering',
        filter: 'Filter',
        filtering: 'Filtrering',
        findings: 'Fund',
        flows: 'Strømme',
        'follow-up': 'Opfølgning',
        forensic: 'Retsmedicinsk',
        forensics: 'Retsmedicin',
        'form-filler': 'Form Filler',
        framework: 'Ramme',
        frameworks: 'Rammer',
        'gap-analysis': 'Gab Analyse',
        'gap-report': 'Gap rapport',
        gaps: 'Huller',
        gates: 'Porte',
        'getting-started': 'Kom godt i gang',
        'golden-templates': 'Gyldne skabeloner',
        grc: 'GRC',
        'group-management': 'Koncernledelse',
        groups: 'Grupper',
        'guided-tours': 'Guidede ture',
        guides: 'Vejledninger',
        handling: 'Håndtering',
        'hands-on': 'Hands-On',
        health: 'Sundhed',
        help: 'Hjælp',
        hierarchy: 'Hierarki',
        history: 'Historie',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Identitetsudbyder',
        implementation: 'Implementering',
        import: 'Importer',
        improvement: 'Forbedring',
        'incident-response': 'Hændelsesrespons',
        inconsistencies: 'Uoverensstemmelser',
        instances: 'Forekomster',
        intake: 'Indtag',
        'intake-key': 'Indtagsnøgle',
        interactive: 'Interaktiv',
        international: 'International',
        inventory: 'Inventar',
        investigation: 'Undersøgelse',
        investigations: 'Undersøgelser',
        'ip-allowlist': 'IP-tilladelsesliste',
        issues: 'Problemer',
        justification: 'Begrundelse',
        justifications: 'Begrundelser',
        kanban: 'Kanban',
        keyboard: 'Tastatur',
        keys: 'Nøgler',
        'knowledge-base': 'Vidensbase',
        koncernstyring: 'Corporate Governance',
        kpi: 'KPI',
        labeling: 'Mærkning',
        language: 'Sprog',
        'language-packs': 'Sprogpakker',
        languages: 'Sprog',
        lawfulness: 'Lovlighed',
        layout: 'Layout',
        'legal-basis': 'Retsgrundlag',
        'lessons-learned': 'Erfaringer',
        levels: 'Niveauer',
        library: 'Bibliotek',
        lifecycle: 'Livscyklus',
        lineage: 'Slægt',
        links: 'Links',
        listings: 'Fortegnelser',
        live: 'Live',
        locale: 'Lokalitet',
        log: 'Log',
        logging: 'Logning',
        'managed-service': 'Administreret Service',
        mapping: 'Kortlægning',
        mappings: 'Kortlægninger',
        'market-packs': 'Markedspakker',
        marketplace: 'Markedsplads',
        maturity: 'Modenhed',
        metrics: 'Metrics',
        models: 'Modeller',
        module: 'modul',
        modules: 'Moduler',
        mps: 'MPS',
        'multi-factor': 'Multifaktor',
        'multi-market': 'Multimarked',
        'multi-org': 'Multi-Org',
        'multi-property': 'Multi-ejendom',
        'my-tasks': 'Mine opgaver',
        obligations: 'Forpligtelser',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orkestrering',
        'org-groups': 'Organisationsgrupper',
        organization: 'Organisation',
        overdue: 'Forfalden',
        overview: 'Oversigt',
        'owner-review': 'Ejer anmeldelse',
        package: 'Pakke',
        packages: 'Pakker',
        packs: 'Pakker',
        pagination: 'Sideinddeling',
        'parent-child': 'Forælder-Barn',
        patterns: 'Mønstre',
        performance: 'Ydeevne',
        periodic: 'Periodisk',
        periods: 'Perioder',
        personal: 'Personligt',
        'personal-data': 'Personlige data',
        personas: 'Personas',
        planning: 'Planlægning',
        platform: 'Platform',
        playbooks: 'Playbooks',
        poc: 'PoC',
        'point-in-time': 'Point-in-Time',
        policies: 'Politikker',
        portfolio: 'Portefølje',
        'post-incident': 'Efter hændelsen',
        'power-user': 'Power User',
        practice: 'Øv',
        pricing: 'Prissætning',
        'privacy-impact': 'Indvirkning på privatlivets fred',
        procedures: 'Procedurer',
        processes: 'Processer',
        processing: 'Behandling',
        'processing-activities': 'Behandlingsaktiviteter',
        procurement: 'Indkøb',
        productivity: 'Produktivitet',
        profiles: 'Profiler',
        programs: 'Programmer',
        progress: 'Fremskridt',
        proof: 'Bevis',
        propagation: 'Formering',
        properties: 'Egenskaber',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Offentlig Form',
        quality: 'Kvalitet',
        questionnaires: 'Spørgeskemaer',
        'quick-links': 'Hurtige links',
        'quick-start': 'Hurtig start',
        'quick-starts': 'Hurtige starter',
        raci: 'RACI',
        readiness: 'Beredskab',
        'real-time': 'Realtid',
        reassessments: 'Revurderinger',
        reconciliation: 'Forsoning',
        record: 'Optag',
        records: 'Optegnelser',
        recurring: 'Tilbagevendende',
        register: 'Tilmeld dig',
        registry: 'Register',
        regulations: 'Forskrifter',
        regulator: 'Regulator',
        regulatory: 'Regulatorisk',
        'regulatory-frameworks': 'Regulatoriske rammer',
        remap: 'Omkort',
        reminders: 'Påmindelser',
        reports: 'Rapporter',
        requests: 'Forespørgsler',
        requirements: 'Krav',
        response: 'Svar',
        'response-plans': 'Reaktionsplaner',
        'response-team': 'Respons Team',
        'response-templates': 'Svarskabeloner',
        'response-time': 'Svartid',
        responses: 'Svar',
        responsibility: 'Ansvar',
        results: 'Resultater',
        review: 'Gennemgå',
        reviews: 'Anmeldelser',
        'right-to-delete': 'Ret til at slette',
        rights: 'Rettigheder',
        'risk-tiers': 'Risikoniveauer',
        rollouts: 'Udrulninger',
        rollup: 'Rollup',
        ropa: 'ROPA',
        routing: 'Routing',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Regler',
        runs: 'Løber',
        saml: 'SAML',
        scan: 'Scan',
        scanning: 'Scanning',
        scc: 'SCC',
        schedule: 'Tidsplan',
        scheduled: 'Planlagt',
        schedules: 'Tidsplaner',
        scheduling: 'Planlægning',
        scores: 'Scorer',
        scoring: 'Scoring',
        scripts: 'Scripts',
        'security-reviews': 'Sikkerhedsanmeldelser',
        'sensitive-data': 'Følsomme data',
        sensitivity: 'Følsomhed',
        'separation-of-duties': 'Adskillelse af opgaver',
        service: 'Service',
        sessions: 'Sessioner',
        'setup-wizard': 'Opsætningsguide',
        severity: 'Sværhedsgrad',
        'shared-governance': 'Delt styring',
        'shared-settings': 'Delte indstillinger',
        shortcuts: 'Genveje',
        sidebar: 'Sidebjælke',
        signals: 'Signaler',
        'single-sign-on': 'Single Sign-On',
        snapshots: 'Snapshots',
        sod: 'SoD',
        sorting: 'Sortering',
        sources: 'Kilder',
        staging: 'Iscenesættelse',
        standards: 'Standarder',
        status: 'Status',
        structure: 'Struktur',
        'sub-processor': 'Underprocessor',
        'sub-processors': 'Underprocessorer',
        submissions: 'Indsendelser',
        subprocessors: 'Underbehandlere',
        subscription: 'Abonnement',
        suggestions: 'Forslag',
        summaries: 'Resuméer',
        supplements: 'Kosttilskud',
        support: 'Support',
        sync: 'Synkroniser',
        system: 'System',
        systems: 'Systemer',
        tables: 'Tabeller',
        tagging: 'Tagging',
        'tamper-proof': 'Indgrebssikker',
        taxonomies: 'Taksonomier',
        taxonomy: 'Taksonomi',
        tenants: 'Lejere',
        termination: 'Opsigelse',
        'third-party': 'Tredjepart',
        'ticket-sync': 'Billetsynkronisering',
        tickets: 'Billetter',
        tiers: 'Etager',
        tokens: 'Poletter',
        totp: 'TOTP',
        traceability: 'Sporbarhed',
        tracking: 'Sporing',
        trail: 'Trail',
        training: 'Træning',
        translation: 'Oversættelse',
        translations: 'Oversættelser',
        transparency: 'Gennemsigtighed',
        trends: 'Trends',
        unified: 'Forenet',
        updates: 'Opdateringer',
        validation: 'Validering',
        vault: 'Vault',
        'vendor-requests': 'Leverandøranmodninger',
        verification: 'Verifikation',
        versioning: 'Versionering',
        versions: 'Versioner',
        vietnam: 'Vietnam',
        violations: 'Overtrædelser',
        visualization: 'Visualisering',
        waivers: 'Afkald',
        'web-records': 'Web Records',
        website: 'Hjemmeside',
        websites: 'Hjemmesider',
        weights: 'Vægte',
        'white-label': 'White-Label',
        withdrawal: 'Tilbagetrækning',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Anmodning om Rettigheder',
        data_mapping: 'Datakortlægning',
        governance: 'Styring',
        consent: 'Samtykkestyring',
        consent_properties: 'Samtykkeegenskaber',
        assessments: 'Konsekvensanalyser og Vurderinger',
        classification: 'Dataklassificering',
        monitoring: 'Overvågningsprogrammer',
        program: 'Overvågningsprogram',
        incidents: 'Hændelsesrespons',
        connectors: 'Konnektorer',
        vendor_risk: 'Sælgerrisiko',
        deletion_orchestration: 'Sletteorkestrering',
        discovery: 'Dataopdagelse',
        incident_ops: 'Hændelsesdrift',
        program_governance: 'Programstyring',
        access_accountability: 'Adgangsansvarlighed',
        localization: 'Lokalisering',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Partnerarbejdsområde',
        privacy_scanner: 'Privatlivsscanner',
        onboarding: 'Onboarding',
        compliance_forms: 'Overholdelses Formularer',
        audit_trail: 'Revisionslog',
        data_subjects: 'Registrerede',
        organizations: 'Organisationer',
        task_management: 'Opgavestyring',
        deletion_orch: 'Sletteorkestrering',
        forms: 'Overholdelsesskemaer',
        partner_marketplace: 'Partnermarkedsplads',
        dashboard: 'Kontrolpanel',
        platform_settings: 'Platformindstillinger',
        help_center: 'Hjælpecenter',
    },
    module_descriptions: {
        rights_requests: 'Administrer anmodninger om registreredes rettigheder, herunder adgang, sletning og dataportabilitet. Spor anmodningens livscyklus, tildel behandlere og overhold lovpligtige frister.',
        data_mapping: 'Kortlæg datastrømme på tværs af din organisation. Spor behandlingsaktiviteter, datakategorier, formål, opbevaringspolitikker og systemintegrationer.',
        governance: 'Tilsyn og dokumentationen bag det. Administrer leverandørregistret og dets livscyklus, godkend de beslutninger, dette modul kontrollerer, hold øje med compliance-afvigelser, og før registrene over tillid og identitet, regulatoriske artefakter, betalinger og eKYC samt platformsdokumentation.',
        consent: 'Administrer indsamling, opbevaring og tilbagetrækning af samtykke. Spor samtykkeregistreringer, formål og integration med samtykkeplatforme.',
        consent_properties: 'Konfigurer samtykkeegenskaber, grupper og gyldne skabeloner. Definer genanvendelige samtykkekonfigurationer til ensartet udrulning.',
        assessments: 'Gennemfør konsekvensanalyser (DPIA) og privatlivsrisikovurderinger. Evaluer behandlingsaktiviteter for overholdelsesrisici.',
        classification: 'Klassificer dataaktiver efter følsomhedsniveau. Anvend taksonomi-etiketter, spor livscyklusposter og identificer klassificeringsgab.',
        monitoring: 'Opsæt overvågningsprogrammer til at spore overholdelsesmetrikker, centrale risikoindikatorer og kontroleffektivitet over tid.',
        incidents: 'Rapporter og administrer privatlivshændelser og databrud. Spor undersøgelses-, underretnings- og afhjælpningsarbejdsgange.',
        connectors: 'Konfigurer datakildekonnektorer og integrationer. Forbind eksterne systemer til automatiseret dataopdagelse og overvågning.',
        vendor_risk: 'Vurder og overvåg tredjepartsleverandørrisici. Bedøm leverandører, spor risikoreduktion og administrer leverandørrisikokonfigurationer.',
        deletion_orchestration: 'Orkestrér datasletning på tværs af systemer. Spor sletningsanmodninger, verificer beviskæder og sikr fuldstændig datafjernelse.',
        discovery: 'Opdag persondata på tværs af forbundne systemer. Scan datakilder for at opbygge en komplet dataoversigt.',
        incident_ops: 'Operationel hændelseshåndtering med håndbøger, eskaleringsgange og SLA-sporing til hændelsesløsning.',
        program_governance: 'Spor det overordnede privatlivsprograms modenhed, overholdelsesscore, manglende evidens og ledelsesrapportering på tværs af din organisation.',
        access_accountability: 'Undersøg og revidér brugeradgangsmønstre. Spor adgangskonfigurationer, overvåg mistænkelig aktivitet og håndhæv ansvarlighed.',
        localization: 'Installer og administrer markedslokaliserings­pakker. Tilføj regulatoriske rammer, sprogpakker og regionsspecifikt overholdelsesindhold.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Samarbejd med eksterne partnere. Del overholdelsesdata, spor partnerforpligtelser og administrer partnerrelationer.',
        privacy_scanner: 'Scan hjemmesider for privatlivsoverholdelsesproblemer. Overvåg cookiebrug, sporingsscripts og tilstedeværelse af privatlivspolitik i realtid.',
        onboarding: 'Guide nye organisationer gennem platformsopsætning. Trinvis onboarding-guide til initial konfiguration og dataimport.',
        compliance_forms: 'Opret og administrer overholdelses­formularer fra et katalog af skabeloner. Udfyld lovpligtige formularer, spor indsendelser og generer rapporter.',
        audit_trail: 'Se en komplet revisionslog over alle systemhandlinger. Spor hvem der gjorde hvad, hvornår og hvor på tværs af alle moduler.',
        organizations: 'Administrer organisationer og multi-tenant-indstillinger. Konfigurer organisationsprofiler, hierarkier og relationer på tværs af organisationer.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Forklar uregelmæssigheder',
            explaining: 'Forklarer...',
            results_heading: 'Anomaliforklaringer ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} elementer',
            remediation_label: 'Udbedringstrin',
            items_detail_label: 'Berørte varer',
            accept_label: 'Accepter forklaring',
            reject_label: 'Afvis forklaring',
            error_generic: 'Kunne ikke forklare uregelmæssighederne. Prøv igen, eller kontroller, at scanningen er gennemført.',
        },
        cookie_insight: {
            insight_label: 'Cookie og tracker AI indsigt',
            producing: 'Producerer indsigt...',
            results_heading: 'Cookie- og trackerindsigt ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} elementer',
            classification_label: 'Klassifikation',
            severity_label: 'Sværhedsgrad',
            banner_copy_label: 'Foreslået bannerkopi',
            retention_label: 'Råd om fastholdelse',
            citations_label: 'Citater',
            kind_cookie: 'Cookie',
            kind_tracker: 'Tracker',
            class_necessary: 'Nødvendigt',
            class_analytics: 'Analytics',
            class_marketing: 'Markedsføring',
            class_social: 'Socialt',
            accept_label: 'Accepter indsigt',
            reject_label: 'Afvis indsigt',
            no_items: 'Ingen cookies eller trackere blev fundet i denne scanning.',
            error_generic: 'Kunne ikke producere cookie-indsigten. Prøv igen, eller kontroller, at scanningen er gennemført.',
        },
        monitoring: {
            title: 'Privatlivsovervågning',
            new_scan: 'Ny Scanning',
            scan_url: 'URL til Scanning',
            scan_frequency: 'Scanningsfrekvens',
            last_scan: 'Seneste Scanning',
            score: 'Privatlivsscore',
            issues: 'Fundne Problemer',
            history: 'Scanningshistorik',
            settings: 'Overvågningsindstillinger',
        },
        real_time: {
            title: 'Realtidsscanner',
            scan_now: 'Scan Nu',
            scanning: 'Scanner...',
            results: 'Scanningsresultater',
            no_results: 'Ingen scanningsresultater endnu.',
            cookies: 'Cookies',
            trackers: 'Sporingsværktøjer',
            consent_banners: 'Samtykkebannere',
            third_party: 'Tredjepartsscripts',
            archive: 'Resultatarkiv',
            settings: 'Scannerindstillinger',
        },
        dashboard: {
            page_title: 'Dashboard for privatlivsovervågning',
            privacy_monitoring: 'Overvågning af privatlivets fred',
            plan_label: '{package} plan',
            expires_label: 'udløber {date}',
            settings: 'Indstillinger',
            refresh: 'Opdater',
            refreshing_dashboard: 'Forfriskende overvågningsdashboard',
            refresh_aria: 'Opdater overvågningsdashboard',
            not_configured_title: 'Privatlivsovervågning ikke konfigureret',
            not_configured_desc: 'Tilslut din AesirX privatlivsovervågningstjeneste for at begynde at spore overholdelse af websteder.',
            configure_monitoring: 'Konfigurer overvågning',
            license_expired_title: 'Licens udløbet',
            license_expired_desc: 'Din AesirX licens til privatlivsovervågning er udløbet. Forny den for at undgå serviceafbrydelse.',
            compliance_score: 'Overholdelsesscore',
            cookies_detected: 'Cookies fundet',
            trackers_label: 'Sporer',
            open_issues: 'Åbne numre',
            score_good: 'Godt',
            score_needs_improvement: 'Trænger til forbedring',
            score_critical: 'Kritisk',
            action_required: 'Handling påkrævet',
            cookies_sr: 'cookies fundet',
            trackers_sr: 'trackere fundet',
            open_issues_sr: 'åbne spørgsmål',
            action_required_sr: 'handling påkrævet',
            metric_label: 'Metrisk',
            value_label: 'Værdi',
            status_label: 'Status',
            aria_metrics: 'Metrics for privatlivsovervågning',
            plan_limit_warning: 'Scanner URL: {current}/{max} — du har nået din plangrænse.',
            manage_domains: 'Administrer domæner eller opgrader din plan.',
            monitored_domains: 'Overvågede domæner',
            domain_added: 'Domæne {domain} tilføjet',
            domain_removed: 'Domæne {domain} fjernet',
            failed_add_domain: 'Kunne ikke tilføje domæne',
            failed_remove_domain: 'Domænet kunne ikke fjernes',
            failed_load: 'Kunne ikke indlæse overvågningsdashboard',
            new_domain_aria: 'Nyt domæne URL',
            add_domain: 'Tilføj domæne',
            adding_domain: 'Tilføjer...',
            no_domains: 'Ingen overvågede domæner konfigureret endnu.',
            aria_domains_table: 'Overvågede domæner',
            col_domain: 'Domæne',
            col_compliance_score: 'Overholdelsesscore',
            col_status: 'Status',
            col_cookies: 'Cookies',
            col_trackers: 'Sporer',
            col_issues: 'Problemer',
            col_last_scan: 'Sidste scanning',
            col_action: 'Handling',
            compliance_status_sr: 'overholdelsesstatus',
            showing_chart: 'Viser kortvisning',
            showing_table: 'Viser datatabelvisning',
            loading_sr: 'Indlæser overvågnings dashboard',
            col_framework_tags: 'Rammetags',
            col_last_monitored: 'Sidst overvåget',
            framework_tags_sr: 'gældende lovgivningsmæssige rammer',
            last_monitored_sr: 'tidsstempel for kontinuerlig overvågning',
            audit_trail_heading: 'Revisionsspor',
            audit_trail_info: 'Hver overvågningsscanning er tidsstemplet og logget. Ændringer i overholdelse af domæneniveau producerer en uforanderlig revisionspost, der kan eksporteres til regulatorisk gennemgang.',
            cybersecurity_law_info_heading: 'Detektion af lov om cybersikkerhed',
            cybersecurity_law_info: 'Overvågede domæner kontrolleres automatisk i forhold til cybersikkerhedslovens krav (f.eks. Vietnam Cybersecurity Law, NIS2 direktiv). Rammemærker angiver, hvilke regler der gælder. Problemer er kategoriseret efter lovgivningsmæssige rammer for målrettet afhjælpning.',
        },
        scan: {
            page_title: 'Realtidsscanning',
            title: 'Real-Time Privacy Scanner',
            settings: 'Indstillinger',
            results_archive: 'Resultatarkiv',
            launch_scan: 'Start Scan',
            target_url: 'Målret mod URL',
            protocol_aria: 'Protokol',
            url_required: 'URL er påkrævet',
            url_whitespace: 'Fjern mellemrum fra URL',
            url_invalid: 'Angiv et gyldigt domæne (f.eks. example.com)',
            add_subpages: 'Tilføj undersider',
            add_subpages_aria: 'Tilføj undersider, {count} tilføjet',
            start_scan: 'Start scanning',
            launching: 'Lancerer...',
            scan_launched: 'Scanningen blev startet',
            scan_failed: 'Kunne ikke starte scanning',
            scan_progress: 'Scanningsfremskridt',
            scanning_in_progress: 'Scanning i gang...',
            scan_id: 'Scan ID',
            status_label: 'Status',
            status_unknown: 'unknown',
            progress_label: 'Fremskridt',
            pages_label: 'Sider',
            refresh_status: 'Opdater status',
            new_scan: 'Ny scanning',
            scan_completed: 'Scanning afsluttet, resultater tilgængelige',
            failed_check_status: 'Kunne ikke kontrollere scanningsstatus',
            scan_results: 'Scanningsresultater',
            main_page: 'Hovedside',
            page_n: 'Side {n}',
            result_pages_nav: 'Resultatsider',
            page_url: 'Side URL',
            risk_score: 'Risikoscore',
            risk_score_sr: 'risikoscore',
            cookies_label: 'Cookies',
            cookies_sr: 'cookies fundet',
            scripts_label: 'Scripts',
            scripts_sr: 'scripts fundet',
            issues_label: 'Problemer',
            issues_sr: 'problemer fundet',
            issues_found: 'Problemer fundet',
            aria_issues_table: 'Problemer fundet under scanning',
            col_severity: 'Sværhedsgrad',
            col_category: 'Kategori',
            col_title: 'Titel',
            col_remediation: 'Udbedring',
            severity_sr: 'severity',
            scan_result_metrics: 'Scan resultatmålinger',
            view_full_detail: 'Se alle detaljer',
            col_consent_category: 'Samtykkekategori',
            consent_category_sr: 'samtykke kategorisering',
            compliance_evidence_heading: 'Overholdelsesbevis',
            compliance_evidence_info: 'Scanningsresultater kobles automatisk til overholdelsessager. Hvert problem kan spores til en samtykkekategori (essentiel, funktionel, analytisk, markedsføring) og producerer revisionsklare beviser til regulatorisk gennemgang.',
            rule_config_heading: 'Regelkonfiguration',
            rule_config_info: 'Scanningsregler kan konfigureres i henhold til lovgivningsmæssige rammer. Cookietjek, scriptdetektion og leverandøranalyse kan aktiveres eller deaktiveres via Indstillinger. Brugerdefinerede regler kan defineres, så de matcher din organisations overholdelseskrav.',
            case_linkage_heading: 'Sammenkædning af overholdelsessag',
            case_linkage_info: 'Problemer fundet under realtidsscanninger kan knyttes til overholdelsessager til sporing og afhjælpning. Naviger til siden med detaljer om scanningsresultat for at se sagstilknytninger.',
            subpages_modal_title: 'Tilføj undersider',
            subpages_modal_desc: "Tilføj op til 5 underside-URL'er for at scanne sammen med hoveddomænet.",
            subpage_n: 'Underside {n}',
        },
        result_detail: {
            page_title: 'Scanningsresultatdetaljer',
            title: 'Scanningsresultat',
            back_to_archive: 'Tilbage til Arkiv',
            failed_load: 'Scanningsresultatet kunne ikke indlæses',
            loading_sr: 'Indlæser scanningsresultat...',
            scan_id: 'Scan ID',
            target_url: 'Målret mod URL',
            status_label: 'Status',
            status_sr: 'status',
            started: 'Startede',
            completed: 'Afsluttet',
            pages_scanned: 'Scannede sider',
            https_label: 'HTTPS',
            highest_risk_score: 'Højeste risikoscore',
            most_cookies: 'De fleste cookies',
            most_vendors: 'De fleste tredjepartsleverandører',
            most_issues: 'De fleste numre',
            across_pages: 'på tværs af sider',
            highest_values_aria: 'Højeste værdier på tværs af alle sider',
            scanned_pages_nav: 'Scannede sider',
            main_page: 'Hovedside',
            page_n: 'Side {n}',
            page_url: 'Side URL',
            risk_score: 'Risikoscore',
            overall_risk_sr: 'samlet risikoscore',
            cookies_label: 'Cookies',
            cookies_sr: 'cookies fundet',
            vendors_label: 'Tredjepartsleverandører',
            vendors_sr: 'tredjepartsleverandører fundet',
            issues_label: 'Problemer',
            issues_sr: 'problemer fundet',
            risk_breakdown_aria: 'Fordeling af risikoscore',
            issues_found: 'Problemer fundet',
            aria_issues_table: 'Problemer fundet under scanning',
            col_severity: 'Sværhedsgrad',
            col_category: 'Kategori',
            col_title: 'Titel',
            col_remediation: 'Udbedring',
            severity_sr: 'severity',
            cookies_heading: 'Cookies ({count})',
            aria_cookies_table: 'Cookies fundet',
            col_name: 'Navn',
            col_domain: 'Domæne',
            col_path: 'Sti',
            col_secure: 'Sikker',
            col_httponly: 'Kun Http',
            col_samesite: 'SammeSite',
            vendors_heading: 'Tredjepartsleverandører ({count})',
            aria_vendors_table: 'Tredjepartsleverandører fundet',
            col_hostname: 'Værtsnavn',
            scripts_heading: 'Scripts / Beacons ({count})',
            aria_scripts_table: 'Scripts og beacons fundet',
            col_url: 'URL',
            col_type: 'Type',
            links_heading: 'Links',
            first_party: 'Første part',
            third_party: 'Tredjepart',
            col_risk_category: 'Risikokategori',
            col_regulatory_framework: 'Reguleringsramme',
            col_actionable_detail: 'Handlingsbar detalje',
            risk_category_sr: 'regulatorisk risikokategori',
            risk_category_mapping_heading: 'Risikokategorikortlægning',
            risk_category_mapping_info: 'Hvert scanningsfund er kortlagt til en regulatorisk risikokategori (dataindsamling, grænseoverskridende overførsel, samtykkestyring, dataopbevaring, sikkerhedskontrol). Denne kortlægning muliggør målrettet afhjælpning prioriteret efter lovgivningsmæssig påvirkning.',
            compliance_case_heading: 'Compliance Case Association',
            compliance_case_info: 'Scanningsresultater er knyttet til overholdelsessager til ende-til-ende-sporing. Problemer kan eskaleres direkte fra scanningsresultater til overholdelsessagens workflow.',
        },
        results_archive: {
            page_title: 'Scan resultatarkiv',
            title: 'Scan resultatarkiv',
            back_to_scanner: 'Tilbage til Scanner',
            failed_load: 'Scanningsresultaterne kunne ikke indlæses',
            results_loaded: 'Scanningsresultater er indlæst, {count} poster',
            total_label: 'I alt: {count}',
            aria_table: 'Scan resultatarkiv',
            col_scan_date: 'Scanningsdato',
            col_target_url: 'Målret mod URL',
            col_duration: 'Varighed',
            col_issues_found: 'Problemer fundet',
            col_risk_score: 'Risikoscore',
            col_status: 'Status',
            col_actions: 'Handlinger',
            scan_status_sr: 'scanningsstatus',
            view_result: 'Visning',
            no_results: 'Ingen scanningsresultater fundet.',
            loading_sr: 'Indlæser scanningsresultater',
            pagination_aria: 'Scanningsresultatpaginering',
            col_risk_category: 'Risikokategori',
            risk_category_sr: 'regulatorisk risikokategori',
            archive_retention_heading: 'Arkivopbevaring',
            archive_retention_info: 'Scanningsresultater bevares i den varighed, der er angivet i din organisations dataopbevaringspolitik. Arkiverede resultater inkluderer fuld scanningskontekst (ramme-tags, risikokategorier, vejledning om afhjælpning) og kan eksporteres som CSV for regulatorisk dokumentation.',
            export_context_info: 'Eksporterede CSV filer inkluderer regulatorisk kontekst: risikokategori, gældende rammer og handlingsrettede afhjælpningsdetaljer for hvert fund.',
        },
        scanner_settings: {
            page_title: 'Scannerindstillinger',
            title: 'Scannerindstillinger',
            back_to_scanner: 'Tilbage til Scanner',
            failed_load: 'Kunne ikke indlæse scannerkonfiguration',
            endpoint_url: 'Slutpunkt URL',
            save_success: 'Scannerkonfigurationen blev gemt.',
            save_failed: 'Kunne ikke gemme scannerkonfigurationen',
            test_connection: 'Test forbindelse',
            testing: 'Tester...',
            saving: 'Gemmer ...',
            loading_sr: 'Indlæser scannerindstillinger',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Indstillinger for privatlivsovervågning',
            title: 'Overvågningsindstillinger',
            back_to_dashboard: 'Tilbage til Dashboard',
            failed_load: 'Kunne ikke indlæse overvågningskonfiguration',
            credentials_heading: 'AesirX Legitimationsoplysninger',
            connected_as: 'Forbundet som {username}',
            credentials_desc: 'Indtast loginoplysningerne til din AesirX-konto for at forbinde privatlivsovervågning. Find dit medlems-id på profilsiden for din AesirX-konto.',
            client_id: 'Klient-id',
            client_secret: 'Klientens hemmelighed',
            username: 'AesirX Brugernavn (e-mail)',
            password: 'AesirX Adgangskode',
            placeholder_keep: '(lad tomt for at bevare eksisterende)',
            credential_form_aria: 'AesirX legitimationsformular',
            test_connection: 'Test forbindelse',
            testing: 'Tester...',
            test_aria: 'Test AesirX forbindelsen',
            testing_aria: 'Test af forbindelse',
            save_configuration: 'Gem konfiguration',
            saving: 'Gemmer ...',
            save_aria: 'Gem legitimationsoplysninger',
            saving_aria: 'Gemmer legitimationsoplysninger',
            credentials_saved: 'AesirX legitimationsoplysninger blev gemt.',
            credentials_saving: 'Gemmer ...',
            credentials_save_failed: 'Kunne ikke gemme loginoplysninger',
            client_id_required: 'Klient-id og brugernavn er påkrævet.',
            all_fields_required: 'Alle legitimationsfelter er obligatoriske.',
            email_heading: 'E-mail-meddelelser',
            receive_emails: 'Modtag e-mails med scanningsrapporter',
            email_toggle_aria: 'Slå e-mails til scanningsrapporter til eller fra',
            only_on_change: 'Kun e-mail, når risikostatus ændres',
            only_on_change_aria: 'Skift kun e-mail ved ændring af risikostatus',
            emails_enabled: 'Scan rapport e-mails aktiveret',
            emails_disabled: 'Scanningsrapport-e-mails er deaktiveret',
            only_change_on: 'Sender kun e-mail om risikoændringer',
            only_change_off: 'E-mail ved hver scanning',
            email_failed: 'Kunne ikke opdatere e-mail-indstillingen',
            email_settings_aria: 'Indstillinger for e-mailbeskeder',
            loading_sr: 'Indlæser overvågningsindstillinger',
        },
        entity_types: {
            user: 'Bruger',
            case: 'Sag',
            incident: 'Hændelse',
            assessment: 'Vurdering',
            vendor: 'Sælger',
            consent: 'Samtykke',
            policy: 'Politik',
            data_map: 'Datakort',
        },
        actions: {
            create: 'Opret',
            update: 'Opdatering',
            delete: 'Slet',
            view: 'Visning',
            export: 'Eksporter',
            status_change: 'Statusændring',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Formularkontrolpanel',
            total_templates: 'Samlede Skabeloner',
            installed: 'Installerede',
            available: 'Tilgængelige',
            by_market: 'Efter Marked',
            back_dashboard: 'Tilbage til dashboard',
            browse_templates: 'Gennemse skabeloner',
            catalog_coverage: 'Katalogdækning',
            quick_start: 'Hurtig start',
            recent_instances: 'Seneste instanser',
            total_instances: 'Instanser i alt',
            fetch_failed: 'Kunne ikke hente dashboarddata.',
            no_instances: 'Ingen instanser fundet.',
            form_metrics: 'Oversigt over formularmetrikker',
            templates_total: 'skabeloner i alt',
            instances_total: 'forekomster i alt',
            installed_of_available: '{installed} af {available} installeret',
            templates_in_category: 'skabeloner i {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Formularskabeloner',
            new: 'Ny Skabelon',
            edit: 'Rediger Skabelon',
            name: 'Skabelonnavn',
            form_type: 'Formulartype',
            market: 'Marked',
            language: 'Sprog',
            schema: 'Formularskema',
            preview: 'Forhåndsvisning',
            duplicate: 'Duplikér Skabelon',
            grid_view: 'Gittervisning',
            list_view: 'Listevisning',
            fetch_failed: 'Kunne ikke hente skabeloner.',
            filter_templates: 'Filtrer skabeloner',
            version_history: 'Versionshistorik',
            no_version_history: 'Ingen versionshistorik — dette er den første version.',
            form_id: 'Formular-ID',
            source_instrument: 'Kildeinstrument',
            issuer: 'Udsteder',
            all_frameworks: 'Alle rammeværker',
            view_mode: 'Visningstilstand',
            classification: 'Klassifikation',
        },
        catalog: {
            title: 'Formularkatalog',
            install: 'Installér',
            uninstall: 'Afinstallér',
            open_form: 'Open form',
            install_all: 'Installér Alle',
            installed_tab: 'Installerede',
            catalog_tab: 'Katalog',
            search_placeholder: 'Søg i skabeloner...',
            no_templates: 'Ingen skabeloner fundet.',
            template_count: '{count} skabeloner',
            all_markets: 'Alle markeder',
            sections: 'Sektioner',
            all_categories: 'Alle kategorier',
            category: 'Kategori',
            category_install_failed: 'Kunne ikke installere kategoriskabeloner.',
            category_install_success: 'Kategoriskabeloner installeret.',
            fetch_failed: 'Kunne ikke hente skabeloner.',
            fetch_installed_failed: 'Kunne ikke hente installerede skabeloner.',
            fields: 'Felter',
            install_failed: 'Kunne ikke installere skabelon.',
            install_success: 'Skabelon installeret.',
            installing: 'Installerer...',
            removing: 'Fjerner...',
            uninstall_confirm: 'Er du sikker på, at du vil afinstallere denne skabelon?',
            uninstall_failed: 'Kunne ikke afinstallere skabelon.',
            uninstall_success: 'Skabelon afinstalleret.',
            installation_summary: 'Installationsoversigt',
            filter_catalog: 'Filtrer katalog',
            all_frameworks: 'Alle rammeværker',
            templates_installed_count: '{count} skabelon(er) er installeret',
            all_already_installed: 'Alle skabeloner i denne kategori er allerede installeret',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'Formular-ID',
            issuer: 'Udsteder',
            catalog_tabs: 'Formularkatalogets faner',
        },
        filler: {
            back_templates: 'Tilbage til skabeloner',
            no_sections: 'Ingen sektioner fundet.',
            draft_created: 'Kladde oprettet.',
            draft_saved: 'Kladde gemt.',
            fetch_template_failed: 'Kunne ikke hente skabelon.',
            save_draft: 'Gem kladde',
            save_draft_failed: 'Kunne ikke gemme kladde.',
            saving: 'Gemmer ...',
            select_option: 'Vælg an mulighed',
            submit: 'Indsend',
            submit_failed: 'Kunne ikke indsende.',
            submitting: 'Indsender...',
            title: 'Formularguide',
            form_id_label: 'Formular-ID',
            schema_label: 'Skema',
            form_number_label: 'Form',
            framework_label: 'Rammeværk',
            issuer_label: 'Udsteder',
            source_label: 'Kilde',
            market_label: 'Marked',
            section_of: 'Sektion {current} af {total}',
            form_navigation: 'Formularnavigation',
            form_progress: 'Formularfremskridt: Sektion {current} af {total}',
            removed_item: 'Element {index} fjernet fra {field}',
            added_item: 'Nyt element tilføjet til {field}',
            go_to_previous: 'Gå til forrige sektion, sektion {current} af {total}',
            go_to_next: 'Gå til næste sektion, sektion {current} af {total}',
            risk_matrix_placeholder: '1-5',
        },
        filing: {
            title: 'Indsendelsesstatus',
            advance: 'Opdater indsendelsesstatus...',
            not_started: 'Ikke startet',
            drafting: 'Under udkast',
            internal_review: 'Intern gennemgang',
            approved_for_submission: 'Godkendt til indsendelse',
            submitted_to_authority: 'Indsendt til myndighed',
            supplement_requested: 'Supplering anmodet',
            supplement_in_progress: 'Supplering i gang',
            resubmitted: 'Genindsendt',
            accepted: 'Accepteret',
            rejected: 'Afvist',
            closed: 'Lukket',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Godkendt af',
            form_answers: 'Formularsvar',
            column_header: 'Form',
            no_answers: 'Ingen svar fundet.',
            view_outputs: 'Vis outputs',
            approve: 'Godkend',
            approve_failed: 'Kunne ikke godkende.',
            approving: 'Godkender...',
            detail_title: 'Instansdetalje',
            fetch_failed: 'Kunne ikke hente instanser.',
            generate_failed: 'Kunne ikke generere output.',
            generate_output: 'Generer output',
            generating: 'Genererer...',
            submitted_at: 'Indsendt den',
            instance_metadata: 'Formularforekomstmetadata',
            filing_status_steps: 'Arkiveringsstatusstrin',
            current_status: 'aktuel status',
            filing_status_update_failed: 'Kunne ikke opdatere indsendelsesstatus',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Tilbage til instans',
            no_outputs_msg: 'Der er endnu ikke genereret nogen outputs.',
            no_outputs_title: 'Ingen outputs',
            output_name: 'Outputnavn',
            download: 'Hent',
            download_failed: 'Kunne ikke downloade.',
            fetch_failed: 'Kunne ikke hente outputs.',
            format: 'Format',
            generated_at: 'Genereret den',
            generated_by: 'Genereret af',
            size: 'Størrelse',
            title: 'Formularoutput',
            print: 'Udskriv',
            print_outputs: 'Udskriv formularresultater',
            printing: 'Udskriver formularresultater',
            pdf_preview: 'PDF-forhåndsvisning',
            downloading: 'Downloader {name}',
            preview_in_new_window: 'Forhåndsvis {name} ({format}) i nyt vindue',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Omfang og anvendelighed',
            risk: 'Risikovurdering og kontrolkortlægning',
            policy: 'Organisatoriske sikkerhedspolitikker',
            localization: 'Datalokalisering og lokal tilstedeværelse',
            vendor: 'Tredjeparter og forsyningskæde',
            authority: 'Håndtering af myndighedsanmodninger',
            incident: 'Hændelsesrespons og bevissikring',
            training: 'Uddannelse og beredskab',
        },
        market_finalization_domains: {
            data_law: 'Datalov — Governance og Operationelle Kontroller',
            pdpl_2025: 'PDPL 2025 — Privatlivsstyring og Registreredes Rettigheder',
            decree_356: 'Dekret 356/2025 — Leverandør- og Grænseoverskridende Kontroller',
            telecom: 'Telelov — Fortrolighed og Myndighedsanmodninger',
        },
        nav: {
            filing_case_links: 'Indberetningssagslinks',
            section_routing: 'Sektionsruting',
            submission_packages: 'Indsendelsespakker',
        },
        filing_case_links: {
            title: 'Indberetningssagslinks',
            link_form: 'Knyt formular til indberetningssag',
            filing_case_type: 'Indberetningssagstype',
            form_instance: 'Formularinstans',
            select_form_instance: 'Vælg en formularinstans...',
            select_filing_case: 'Vælg en indberetningssag...',
            no_links_found: 'Ingen indberetningssagslinks fundet.',
            linked: 'Knyttet',
            filing_case_id: 'Arkiveringssag-ID',
            link_created: 'Arkiveringssaglink oprettet',
            link_deleted: 'Indberetningssagslink slettet',
            aria_delete_link: 'Slet link {name}',
            case_type_dpia_dossier: 'DPIA-dossier',
            case_type_cross_border_filing: 'Grænseoverskridende arkivering',
            case_type_breach_notification: 'Brudmeddelelse',
            no_instances_guidance: 'Ingen formularinstanser endnu. Opret en fra Formularskabeloner.',
            no_filings_guidance: 'Ingen indberetningssager endnu. Opret en fra Vurderinger.',
        },
        section_routing: {
            title: 'Sektionsruting',
            assign_section: 'Tildel sektion',
            department: 'Afdeling',
            select_department: 'Vælg en afdeling...',
            assignee: 'Tildelt til',
            select_assignee: 'Vælg en tildelt...',
            section: 'Sektion',
            select_section: 'Vælg en sektion...',
            select_form_instance: 'Vælg en formularinstans...',
            no_assignments_found: 'Ingen sektionstildelinger fundet.',
            select_instance_prompt: 'Vælg en formularinstans for at se sektionstildelinger.',
            complete_section: 'Fuldfør sektion',
            status_pending: 'Afventer',
            status_in_progress: 'I gang',
            status_completed: 'Fuldført',
            status_reviewed: 'Gennemgået',
            form_instance_id: 'Formularforekomst-ID',
            assignment_created: 'Sektionstildeling oprettet',
            assignment_deleted: 'Sektionstildeling slettet',
            status_updated: 'Sektionsstatus opdateret',
            aria_delete_assignment: 'Slet tildeling {name}',
            deadline: 'Frist',
            deadline_help: 'Valgfri. Tildelinger efter denne dato vises som forsinkede.',
            overdue: 'Forsinket',
            days_remaining: 'dage tilbage',
            approval_requested: 'Indsendt til gennemgang',
            form_approved: 'Formular godkendt',
            form_rejected: 'Formular afvist',
            no_departments_guidance: 'Ingen afdelinger konfigureret. Opsæt dem i Organisationsindstillinger.',
            no_sections_guidance: 'Ingen sektioner defineret i skabelonen. Sørg for, at formularskabelonen har sektioner.',
            select_instance_first: 'Vælg en formularinstans først for at indlæse tilgængelige sektioner.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Indsendelsespakker',
            assemble_package: 'Sammensæt indsendelsespakke',
            dossier: 'Dossier',
            select_dossier: 'Vælg et dossier...',
            package_label: 'Pakkeetiket',
            cover_letter: 'Følgebrev',
            checklist: 'Tjekliste',
            evidence_index: 'Bevisindeks',
            completeness: 'Fuldstændighed',
            incomplete_warning: 'Kan ikke samle: formularen har ufuldstændige sektioner.',
            no_packages_found: 'Ingen indsendelsespakker fundet.',
            version: 'version',
            package_assembled: 'Indsendelsespakke samlet',
            package_deleted: 'Indsendelsespakke slettet',
            aria_delete_package: 'Slet pakke {name}',
            status_draft: 'Kladde',
            status_complete: 'Fuldført',
            status_submitted: 'Indsendt',
            status_accepted: 'Accepteret',
            status_rejected: 'Afvist',
            package_updated: 'Indsendelsespakke opdateret',
            document_generated: 'Dokument genereret',
            documents: 'Dokumenter',
            generate: 'Generer',
            preview: 'Forhåndsvisning',
            download: 'Download',
            download_all: 'Download alle',
            no_dossiers_guidance: 'Ingen dossiers endnu. Opret en fra Vurderinger.',
            package_plan_help: 'Inkluder i pakkeplan (automatisk dokumentgenerering tilgængelig):',
            cover_letter_help: 'Officielt følgebrev adresseret til den modtagende myndighed',
            checklist_help: 'Overholdelsestjekliste der verificerer, at alle indleveringskrav er opfyldt',
            evidence_index_help: 'Indeks over understøttende dokumentation refereret i indsendelsen',
            completeness_skipped: 'Fuldstændighedstjek sprunget over — ingen formularinstans knyttet til dette dossier.',
            completeness_unavailable: 'Fuldstændighedstjek ikke tilgængeligt — fortsætter med pakkesamling.',
            duplicate_package_error: 'Der findes allerede en indsendelsespakke for dette dossier.',
            doc_cover_letter: 'Følgebrev',
            doc_checklist: 'Tjekliste',
            doc_evidence_index: 'Evidensindeks',
            export_csv: 'Eksportér CSV',
            export_success: 'Links til arkiveringssager eksporteret med succes',
            no_assignments_guidance: 'Ingen sektionsopgaver endnu. Tildel sektioner til afdelinger og teammedlemmer for at starte routing.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Vælg en skabelon',
            hint: 'Vælg en lovgivningsmæssig formularskabelon eller start med en tom formular.',
            market_filter: 'Filtrer efter marked',
            framework_filter: 'Filtrer efter rammeværk',
            all_markets: 'Alle markeder',
            all_frameworks: 'Alle rammeværker',
            search_placeholder: 'Søg efter skabeloner...',
            blank_form: 'Tom / generisk formular',
            blank_form_desc: 'Opret uden skabelon — udfyld felter manuelt.',
            no_templates: 'Ingen skabeloner fundet for dette filter.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Vælg',
            link_error: 'Skabelonlink mislykkedes — du kan tilknytte en skabelon senere fra Redigeringsvisningen.',
            internal_badge: 'Intern skabelon',
            legacy_badge: 'Forældet',
            draft_badge: 'Kladde',
            upcoming_badge: 'Kommende',
            hidden_badge: 'Skjult',
            show_hidden_toggle: 'Vis skjulte / forældede skabeloner',
            show_hidden_toggle_hint: 'Administratorvisning: inkluderer skabeloner markeret som internal_only, hidden, legacy eller draft.',
        },
        cover_letter_modal: {
            title: 'Følgebrev: forhåndsvisning og redigering',
            iframe_title: 'Forhåndsvisning af følgebrev',
            stage_label: 'Stadie',
            stage_initial: 'Første indsendelse',
            stage_supplement: 'Supplerende svar',
            stage_resubmission: 'Genindsendelse',
            subject_label: 'Emne',
            body_label: 'Hovedafsnit',
            notes_label: 'Yderligere noter',
            refresh_preview: 'Opdater forhåndsvisning',
            confirm: 'Bekræft og gem',
            regenerate: 'Regenerer',
            snapshot_saved: 'Følgebrev gemt',
        },
        related_panel: {
            title: 'Relaterede lovgivningsformularer',
            open_form: 'Udfyld formular',
            no_forms: 'Ingen relaterede formularer fundet.',
            hint_vn: 'VN-specifikke lovgivningsformularer tilgængelige — vælg en formular for at åbne overholdelsesguiden.',
            internal_badge: 'Intern',
            official_badge: 'Officiel',
            loading: 'Indlæser relaterede formularer...',
            error: 'Kunne ikke indlæse relaterede formularer.',
            form_id: 'Formular-ID',
            form_name: 'Formularnavn',
            framework: 'Rammeværk',
        },
        view_form: 'Vis formular',
        edit_form: 'Rediger formular',
        save_draft: 'Gem kladde',
        module_settings: 'Modulindstillinger',
        validation_errors: 'Ret venligst valideringsfejl',
        custom_name_help: 'Valgfrit — standard er skabelonnavnet hvis tomt',
        select_placeholder: 'Vælg...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Forrige',
        next_section: 'Næste',
        loading: 'Indlæser...',
        array_add: 'Tilføj',
        array_remove: 'Fjern',
        array_item_label: '{label} element {index}',
        array_add_item: 'Tilføj element til {label}',
        array_remove_item: 'Fjern element {index} fra {label}',
        legacy_banner: 'Denne post blev oprettet før forenede formularer. Nogle data kan afvige mellem entiteten og formularinstansen.',
        field_required: '{label} er påkrævet',
        section_announcement: 'Sektion {current} af {total}: {title}',
        sections_nav_label: 'Form sektioner',
        no_sections: 'Ingen tilgængelige skabelonsektioner.',
        section_label_tpl: 'Sektion {index}: {title}',
        field_shown_announcement: '{field} vises nu.',
        field_required_announcement: '{field} er nu påkrævet.',
        submit_blocked_hint_one: '1 påkrævet felt mangler stadig et svar.',
        submit_blocked_hint_many: '{count} påkrævede felter mangler stadig et svar.',
        module_settings_auto_applied: 'Modulspecifikke indstillinger anvendes automatisk.',
        operation_failed: 'Handlingen mislykkedes',
        failed_to_load_form: 'Det lykkedes ikke at indlæse formularen',
        failed_to_save_form: 'Det lykkedes ikke at gemme formularen',
        failed_to_submit_form: 'Det lykkedes ikke at indsende formularen',
        failed_to_load_template: 'Det lykkedes ikke at indlæse skabelonen',
        failed_to_save_draft: 'Det lykkedes ikke at gemme kladden',
        failed_to_create: 'Det lykkedes ikke at oprette',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Pakkekatalog',
        install: 'Installér Pakke',
        uninstall: 'Afinstallér Pakke',
        install_batch: 'Installér Klynge',
        installed_tab: 'Installerede',
        catalog_tab: 'Katalog',
        pack_details: 'Pakkedetaljer',
        frameworks: 'Rammeværker',
        requirements: 'Krav',
        artifacts: 'Artefakter',
        language_pack: 'Sprogpakke',
        dependencies: 'Afhængigheder',
        no_packs: 'Ingen pakker fundet.',
    },
    platform_pages: {
        users: {
            title: 'Brugere',
            new: 'Ny Bruger',
            invite: 'Invitér Bruger',
            edit: 'Rediger Bruger',
            name: 'Navn',
            email: 'E-mail',
            role: 'Rolle',
            status: 'Status',
            identity_type: 'Identitetstype',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Seneste Login',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Deaktivér',
            reactivate: 'Genaktivér',
            unlock: 'Lås Op',
            reset_password: 'Nulstil Adgangskode',
            remove: 'Fjern',
            remove_confirm: 'Fjern "{name}"? Denne handling er permanent, og brugeren mister al adgang. Posten bevares til revisionsformål.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Roller',
            new: 'Ny Rolle',
            edit: 'Rediger Rolle',
            name: 'Rollenavn',
            description: 'Beskrivelse',
            permissions: 'Tilladelser',
            users_count: 'Brugere',
            detail: 'Rolledetaljer',
            all_tiers: 'Alle niveauer',
            tier: 'Niveau',
            type_system: 'System',
            type_custom: 'Brugerdefineret',
            role_name_placeholder: 'f.eks. regional compliance-leder',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'Min Profil',
            name: 'Navn',
            display_name: 'Visningsnavn',
            email: 'E-mail',
            phone: 'Telefon',
            department: 'Afdeling',
            job_title: 'Stillingsbetegnelse',
            language: 'Sprog',
            role: 'Rolle',
            persona: 'Registreret persona',
            last_login: 'Seneste Login',
            save: 'Gem ændringer',
            save_success: 'Profil opdateret',
            module_tips_section: 'Modultips',
            tips_toggle: 'Vis lovgivningstips i moduler',
            dismissed_tips: 'Afviste tips',
            reset_dismissed: 'Nulstil alle afviste tips',
            notification_prefs: 'Notifikationspræferencer',
            notification_prefs_link: 'Administrer notifikationspræferencer →',
            change_password: 'Skift Adgangskode',
            current_password: 'Nuværende Adgangskode',
            new_password: 'Ny Adgangskode',
            confirm_password: 'Bekræft Adgangskode',
            accessibility: 'Tilgaengelighed',
            high_contrast: 'Hojkontrasttilstand',
            high_contrast_desc: 'Foroeg kontrasten for bedre laeseevne. Opfylder WCAG 2.1 AAA.',
            passwords_no_match: 'Adgangskoderne stemmer ikke overens',
            password_changed_logout: 'Adgangskode ændret. Du bliver logget ud.',
            failed_update: 'Kunne ikke opdatere profil',
            failed_change_password: 'Kunne ikke ændre adgangskode',
            none: 'Ingen',
            never: 'Aldrig',
            password_requirements: 'Min. 12 tegn, stort bogstav, lille bogstav, ciffer, specialtegn',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'MFA-indstillinger',
            mfa_enable: 'Aktivér MFA',
            mfa_disable: 'Deaktivér MFA',
            mfa_setup: 'Scan denne QR-kode med din autentificeringsapp',
            sso_title: 'SSO-konfiguration',
            sso_provider: 'Udbyder',
            sso_client_id: 'Klient-ID',
            sso_redirect: 'Omdirigerings-URI',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API-nøgler',
            api_keys_new: 'Ny API-nøgle',
            api_keys_name: 'Nøglenavn',
            api_keys_last_used: 'Sidst Brugt',
            api_keys_expires: 'Udløber',
            api_keys_revoke: 'Tilbagekald',
            service_accounts_title: 'Tjenestekonti',
            ip_allowlist_title: 'IP-hvidliste',
            ip_add: 'Tilføj IP-adresse',
            ip_address: 'IP-adresse',
            ip_description: 'Beskrivelse',
            delegations_title: 'Delegeringer',
            delegations_new: 'Ny Delegering',
            delegations_from: 'Fra Bruger',
            delegations_to: 'Til Bruger',
            delegations_expires: 'Udløber',
            break_glass_title: 'Nødadgang',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Anmod om Adgang',
            break_glass_reason: 'Årsag til Adgang',
            break_glass_duration: 'Varighed',
            session_settings_title: 'Sessionsindstillinger',
            eviction_policy_section: 'Fjernelsespolitik',
            session_timeout: 'Sessionstimeout (minutter)',
            session_max_concurrent: 'Maks. Samtidige Sessioner',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Aktive Sessioner',
            active_sessions_device: 'Enhed',
            active_sessions_ip: 'IP-adresse',
            active_sessions_started: 'Startet',
            active_sessions_revoke: 'Tilbagekald Session',
            active_sessions_confirm_terminate: 'Er du sikker på, at du vil afslutte denne session?',
            active_sessions_confirm_terminate_current: 'Dette er din nuværende session. Hvis du afslutter det, logges du ud med det samme. Fortsætte?',
            active_sessions_confirm_terminate_all: 'Er du sikker på, at du vil afslutte alle andre sessioner? Kun din nuværende session forbliver aktiv.',
            active_sessions_terminated: 'Sessionen blev afsluttet.',
            active_sessions_all_terminated: 'Alle andre sessioner er blevet afsluttet.',
            active_sessions_terminate_all: 'Afslut alle andre sessioner',
            active_sessions_last_activity: 'Sidste aktivitet',
            active_sessions_current: 'Nuværende session',
            active_sessions_revoke_selected: 'Tilbagekald valgte',
            active_sessions_confirm_terminate_selected: 'Er du sikker på, at du vil afslutte de valgte sessioner?',
            active_sessions_selected_terminated: 'Valgte sessioner afsluttet.',
            ip_allowlist_desc: 'Administrer tilladte IP-adresser for API adgang.',
            ip_no_entries: 'Ingen IP-adresser konfigureret.',
            session_settings_desc: 'Konfigurer sessionstimeout og grænser for samtidige sessioner.',
            session_settings_save_policy: 'Gem sessionspolitik',
            session_settings_saved: 'Sessionsindstillingerne blev opdateret.',
            session_settings_idle_timeout: 'Inaktiv timeout (minutter)',
            session_settings_absolute_timeout: 'Absolut timeout (minutter)',
            session_settings_help_idle: 'Sessionen udløber efter denne periode med inaktivitet.',
            session_settings_help_absolute: 'Session vil udløbe efter denne samlede varighed uanset aktivitet.',
            session_settings_help_max: 'Maksimalt antal samtidige sessioner pr. bruger.',
            sso_note: 'Ændringer i SSO-konfigurationen kan kræve, at brugerne genautentificerer.',
            service_accounts_warning: 'Denne API-tast vil kun blive vist én gang. Kopier det nu og gem det sikkert.',
            service_accounts_new: 'Ny servicekonto',
            service_accounts_name: 'Kontonavn',
            service_accounts_role: 'Rolle',
            service_accounts_created: 'Oprettet',
            service_accounts_desc: 'Administrer maskine-til-maskine-tjenestekonti for API integrationer.',
            break_glass_warning: 'Break-glas adgang revideres og logges. Brug kun til ægte nødsituationer.',
            break_glass_min_chars: '{count} / {min} minimumstegn',
            break_glass_permission_count: '{count} tilladelse(r) valgt',
            break_glass_submitted: 'Glasbrudsanmodning indsendt. Afventer godkendelse.',
            break_glass_approved: 'Ønske om glasbrud godkendt.',
            break_glass_denied: 'Glasbrudsanmodning afvist.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Bekræft aktivering',
            break_glass_pending: 'Afventende anmodninger',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Tid tilbage',
            break_glass_expired: 'Udløbet',
            break_glass_approved_denied_by: 'Godkendt/Nægtet af',
            delegations_desc: 'Administrer midlertidige tilladelsesdelegeringer mellem brugere.',
            delegations_my: 'Mine delegationer',
            delegations_to_me: 'Delegeret til mig',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Alle tilladelser',
            delegations_scope_specific: 'Specifikke tilladelser',
            delegations_created: 'Delegationen blev oprettet.',
            delegations_revoked: 'Delegationen tilbagekaldt.',
            delegations_confirm_revoke: 'Er du sikker på, at du vil tilbagekalde denne delegation?',
            delegations_confirm_revoke_title: 'Bekræft tilbagekald',
            delegations_permission_count: '{count} tilladelse(r)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Status',
            api_keys_prefix: 'Nøglepræfiks',
            api_keys_permissions_count: 'Tilladelser',
            api_keys_ip_count: 'IP-begrænsninger',
            api_keys_created: 'Oprettet',
            api_keys_confirm_revoke_title: 'Bekræft tilbagekald',
            api_keys_confirm_revoke_message: 'Er du sikker på, at du vil tilbagekalde API-tasten "{name}"? Denne handling kan ikke fortrydes.',
            api_keys_confirm_rotate_title: 'Bekræft rotation',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'API nøgle oprettet.',
            api_keys_revoked_success: 'API-tasten "{name}" er blevet tilbagekaldt.',
            api_keys_rotated_success: 'API-tasten "{name}" er blevet roteret. Kopier den nye nøgle nedenfor.',
            api_keys_load_error: 'Kunne ikke indlæse API nøgler.',
            api_keys_create_error: 'Kunne ikke oprette API nøgle.',
            api_keys_revoke_error: 'Kunne ikke tilbagekalde API-nøglen.',
            api_keys_rotate_error: 'Kunne ikke dreje tasten API.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minutter',
            break_glass_duration_30: '30 minutter',
            break_glass_duration_60: '60 minutter (1 time)',
            break_glass_duration_120: '120 minutter (2 timer)',
            break_glass_duration_240: '240 minutter (4 timer)',
            sod_rules_title: 'Regler om funktionsadskillelse',
            sod_violations_report: 'Rapport om overtrædelser',
            sod_create_rule: 'Opret regel',
            sod_col_name: 'Navn',
            sod_col_description: 'Beskrivelse',
            sod_col_conflict_count: 'Konflikttælling',
            sod_col_severity: 'Sværhedsgrad',
            sod_col_status: 'Status',
            sod_col_actions: 'Handlinger',
            sod_no_rules: 'Ingen SoD-regler fundet',
            sod_severity_warning: 'Advarsel',
            sod_severity_blocking: 'Blokering',
            sod_active: 'Aktiv',
            sod_inactive: 'Inaktiv',
            sod_edit: 'Rediger',
            sod_exceptions: 'Undtagelser',
            sod_deactivate: 'Deaktiver',
            sod_confirm_deactivate_title: 'Bekræft deaktivering',
            sod_confirm_deactivate_message: 'Vil du deaktivere reglen "{name}"? Det vil ikke længere markere overtrædelser.',
            sod_deactivate_error: 'Reglen kunne ikke deaktiveres',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Adgangsgennemgange',
            campaigns: 'Gennemgangskampagner',
            new_campaign: 'Ny Kampagne',
            campaign_name: 'Kampagnenavn',
            scope: 'Omfang',
            reviewers: 'Bedømmere',
            due_date: 'Forfaldsdato',
            progress: 'Fremgang',
            my_reviews: 'Mine Gennemgange',
            approve: 'Godkend',
            revoke: 'Tilbagekald',
            analytics: 'Rolleanalyse',
            analytics_title: 'Rolleanalyse',
            permission_usage: 'Tilladelsesanvendelse',
            role_coverage: 'Rolledækning',
            campaign_detail: 'Kampagnedetaljer',
            campaign_description: 'Beskrivelse',
            campaign_status: 'Status',
            campaign_deadline: 'Deadline',
            campaign_scope_type: 'Omfang Type',
            campaign_completion: 'Fuldførelse',
            campaign_total_items: 'Varer i alt',
            campaign_pending_items: 'Afventer',
            campaign_confirmed_items: 'Bekræftet',
            campaign_revoked_items: 'Tilbagekaldt',
            campaign_escalated_items: 'Eskaleret',
            campaign_launch: 'Start kampagne',
            campaign_cancel: 'Annuller kampagne',
            campaign_confirm_launch: 'Er du sikker på, at du vil lancere denne kampagne?',
            campaign_confirm_cancel: 'Er du sikker på, at du vil annullere denne kampagne?',
            campaign_launched: 'Kampagnen blev lanceret.',
            campaign_cancelled: 'Kampagne annulleret.',
            review_items: 'Gennemgå varer',
            review_item_user: 'Bruger',
            review_item_role: 'Rolle',
            review_item_reviewer: 'Anmelder',
            review_item_decision: 'Beslutning',
            review_item_justify: 'Begrundelse',
            review_item_confirm: 'Bekræft',
            review_item_revoke: 'Tilbagekald',
            review_item_escalate: 'Eskalere',
            all_decisions: 'Alle beslutninger',
            bulk_action: 'Massehandling',
            bulk_confirm: 'Massebekræftelse',
            bulk_revoke: 'Massetilbagekald',
            selected_count: '{count} valgt',
            justify_required: 'Begrundelse er påkrævet',
            scope_all_users: 'Alle brugere',
            scope_role_owner: 'Rolleejer',
            scope_department: 'afdeling',
            back_to_campaigns: 'Tilbage til Kampagner',
            recommendations: 'Anbefalinger',
            refreshing: 'Forfriskende...',
            decide: 'Beslut dig',
            decision_by: 'Beslutning af',
            loading_items: 'Indlæser elementer...',
            loading_review_items: 'Indlæser anmeldelseselementer...',
            campaign_not_found: 'Kampagnen blev ikke fundet',
            loading_campaign: 'Indlæser kampagneoplysninger...',
            campaign_completed: 'Kampagnen blev gennemført.',
            confirm_cancel_title: 'Bekræft Annuller',
            justify_bulk_confirm: 'Begrundelse for massebekræftelse:',
            justify_bulk_revoke: 'Begrundelse for massetilbagekaldelse:',
            items_selected: '{count} element(er) valgt',
            justify_bulk_required: 'Begrundelse er nødvendig for bulkaktioner',
            create_campaign_title: 'Opret adgangsvurderingskampagne',
            name_required: 'Navn er påkrævet',
            scope_type_required: 'Omfangstype er påkrævet',
            reviewer_strategy_required: 'Reviewerstrategi er påkrævet',
            deadline_required: 'Deadline er påkrævet',
            reviewer_id_required: 'Anmelder-id er påkrævet til specifik brugerstrategi',
            invalid_json: 'Ugyldig JSON',
            scope_filter_label: 'Omfangsfilter (JSON)',
            reviewer_strategy_label: 'Anmelderstrategi',
            reviewer_id_label: 'Anmelder-id',
            deadline_label: 'Deadline',
            create_campaign_btn: 'Opret kampagne',
            scope_type_label: 'Omfang Type',
            scope_by_role: 'Efter rolle',
            scope_by_department: 'Efter afdeling',
            scope_custom: 'Brugerdefineret',
            reviewer_manager: 'Manager',
            reviewer_specific_user: 'Specifik bruger',
            enter_json_filter: 'Indtast JSON filter',
            hint_label: 'Tip:',
            role_usage: 'Rollebrug',
            over_privileged_users: 'Overprivilegerede brugere',
            role_name_col: 'Rollenavn',
            assigned_users: 'Tildelte brugere',
            active_users_30d: 'Aktive brugere (30d)',
            total_perms: 'Total Perms',
            used_perms: 'Brugte Perms',
            unused_perms: 'Ubrugte Perms',
            no_data_available: 'Ingen tilgængelige data',
            roles_col: 'Roller',
            total_col: 'I alt',
            used_col: 'Brugt',
            unused_pct: 'Ubrugt %',
            current_count: 'Nuværende optælling',
            recommended_count: 'Anbefalet antal',
            rationale: 'Begrundelse',
            loading_analytics: 'Indlæser analyser...',
            loading_role_analytics: 'Indlæser rolleanalysedata...',
            computed_at: 'Beregnet til:',
            form_errors: 'Formularen har {count} fejl. Ret dem venligst.',
            scope_filter_invalid_json: 'Omfangsfilter indeholder ugyldige JSON',
            scope_hint_all_users: 'Intet filter nødvendigt - alle brugere vil blive inkluderet.',
            review_type_label: 'Gennemgangstype',
            review_type_required: 'Gennemgangstype er påkrævet',
            reviewer_ids_label: "Gennemgangs-ID'er",
            reviewer_ids_hint: "Indtast bruger-ID'er adskilt af kommaer",
            reviewer_ids_required: 'Mindst ét gennemgangs-ID er påkrævet',
            start_date_label: 'Startdato',
            start_date_required: 'Startdato er påkrævet',
            end_date_label: 'Slutdato',
            end_date_required: 'Slutdato er påkrævet',
            review_type_user_access: 'Brugeradgang',
            review_type_role_permissions: 'Rolletilladelser',
            review_type_service_accounts: 'Servicekonti',
            review_type_privileged_access: 'Privilegeret adgang',
            review_type_application_access: 'Applikationsadgang',
            auto_revoke_label: 'Auto-tilbagekald ved afslag',
            campaigns_title: 'Få adgang til anmeldelseskampagner',
            create_campaign_label: '+ Opret kampagne',
            no_campaigns_found: 'Ingen kampagner fundet',
            loading_campaigns: 'Indlæser kampagner...',
            no_reviews_assigned: 'Ingen anmeldelser er tildelt dig',
            loading_reviews: 'Indlæser anmeldelser...',
            review_decision_title: 'Gennemgå beslutning',
            justification_label: 'Begrundelse',
            announce_loaded_review_items: 'Indlæste {count} anmeldelsesartikler',
            announce_loaded_campaigns: 'Indlæste {count} kampagner',
            announce_decision_submitted: 'Beslutning "{decision}" indsendt til gennemgang',
            announce_decision_submitted_for: 'Beslutning "{decision}" indgivet for {name}',
            announce_bulk_decision: 'Masse {decision} anvendt på {count} varer',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Attesteringsplaner',
            new: 'Ny Plan',
            edit: 'Rediger Plan',
            create_title: 'Opret attesteringsplan',
            edit_title: 'Rediger attesteringsplan',
            schedule_history: 'Planhistorik',
            next_run_date: 'Næste kørselsdato',
            schedule_name: 'Plannavn',
            frequency: 'Frekvens',
            next_run: 'Næste Kørsel',
            last_run: 'Seneste Kørsel',
            history: 'Attesteringshistorik',
            description: 'Beskrivelse',
            scope: 'Omfang',
            status: 'Status',
            created_at: 'Oprettet',
            updated_at: 'Opdateret',
            trigger_now: 'Udløs nu',
            back_to_schedules: 'Tilbage til skemaer',
            confirm_trigger: 'Udløse denne tidsplan nu?',
            confirm_delete: 'Slette denne tidsplan?',
            triggered: 'Tidsplan blev udløst.',
            deleted: 'Tidsplan slettet.',
            saved: 'Tidsplan blev gemt.',
            campaigns_generated: 'Genererede kampagner',
            run_date: 'Kørsdato',
            run_status: 'Kør status',
            create_schedule: 'Opret tidsplan',
            view_history: 'Se historik',
            deactivate: 'Deaktiver',
            overdue: 'Forsinket',
            overdue_sr: 'Tidsplanen er forsinket',
            confirm_trigger_title: 'Bekræft handling',
            confirm_trigger_message: 'Er du sikker på, at du vil udløse denne tidsplan nu?',
            confirm_deactivate_title: 'Bekræft deaktivering',
            confirm_deactivate_message: 'Er du sikker på, at du vil deaktivere denne tidsplan?',
            triggered_success: 'Tidsplan blev udløst.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Tidsplan deaktiveret.',
            name_label: 'Navn *',
            frequency_label: 'Frekvens *',
            scope_type_label: 'Omfangstype *',
            scope_filter_label: 'Omfangsfilter (JSON)',
            reviewer_strategy_label: 'Anmelderstrategi *',
            reviewer_id_label: 'Anmelder-id *',
            frequency_quarterly: 'Kvartalsvis',
            frequency_semi_annual: 'Halvårligt',
            frequency_annual: 'Årlig',
            scope_all_users: 'Alle brugere',
            scope_by_role: 'Efter rolle',
            scope_by_department: 'Efter afdeling',
            scope_custom: 'Brugerdefineret',
            reviewer_manager: 'Manager',
            reviewer_role_owner: 'Rolleejer',
            reviewer_specific_user: 'Specifik bruger',
            computed_next_run_date: 'Beregnet næste kørselsdato',
            current_scheduled_date: 'Aktuel planlagt dato',
            recomputed_frequency_changed: '(genberegnet -- frekvens ændret)',
            estimated_days_hint: 'Estimeret baseret på {days} dage fra i dag',
            validation_name_required: 'Navn er påkrævet',
            validation_frequency_required: 'Hyppighed er påkrævet',
            validation_scope_type_required: 'Omfangstype er påkrævet',
            validation_reviewer_strategy_required: 'Reviewerstrategi er påkrævet',
            validation_reviewer_id_required: 'Anmelder-id er påkrævet til specifik brugerstrategi',
            validation_invalid_json: 'Ugyldig JSON',
            validation_scope_filter_invalid_json: 'Omfangsfilter indeholder ugyldige JSON',
            form_errors: 'Formularen har {count} fejl. Ret dem venligst.',
            create_schedule_btn: 'Opret tidsplan',
            schedule_history_title: 'Tidsplan historie',
            campaign_name_col: 'Kampagnenavn',
            items_col: 'genstande',
            no_campaigns_yet: 'Ingen kampagner genereret endnu',
            loading_history: 'Indlæser historik...',
            loading_schedule: 'Indlæser tidsplan...',
            total_campaigns: 'Samlet antal kampagner: {count}',
            results_per_page: 'Resultater pr. side',
            page_size_option: '{size} / side',
        },
        sod: {
            title: 'Funktionsadskillelsesegler',
            new: 'Ny Regel',
            edit: 'Rediger Regel',
            create_title: 'Opret SoD-regel',
            edit_title: 'Rediger SoD-regel',
            rule_name: 'Regelnavn',
            conflicting_permissions: 'Modstridende Tilladelser',
            permission_a: 'Tilladelse A',
            permission_b: 'Tilladelse B',
            violations: 'SoD-overtrædelser',
            violation_user: 'Bruger',
            violation_rule: 'Overtrådt Regel',
            violation_date: 'Registreret Den',
            exceptions: 'SoD-undtagelser',
            exception_reason: 'Undtagelsesårsag',
            exception_approver: 'Godkendt Af',
            exception_status: 'Undtagelsesstatus',
            has_exception: 'Har Undtagelse',
            no_exception: 'Ingen undtagelse',
            grant_exception: 'Bevilling undtagelse',
            granting: 'Giver...',
            grant_new_exception: 'Giv ny undtagelse',
            exceptions_for: 'Undtagelser for: {name}',
            back_to_rules: 'Tilbage til Regler',
            no_violations: 'Ingen overtrædelser fundet',
            no_exceptions_found: 'Der blev ikke fundet nogen undtagelser for denne regel',
            all_exception_statuses: 'Alle undtagelsesstatusser',
            user_id_label: 'Bruger-id',
            justification_label: 'Begrundelse',
            compensating_controls_label: 'Kompenserende kontroller',
            expires_at_label: 'Udløber kl',
            granting_exception_for: 'Tildeling af undtagelse for regel: {name}',
            severity: 'Sværhedsgrad',
            severity_warning: 'Advarsel',
            severity_blocking: 'Blokering',
            violation_count: '{count} overtrædelse(r)',
            exception_count: '{count} undtagelse(r)',
            rule_description: 'Beskrivelse',
            confirm_delete: 'Er du sikker på, at du vil slette denne regel?',
            deleted: 'Regel slettet.',
            framework_code: 'Reguleringsramme',
            name_label: 'Navn *',
            severity_label: 'Sværhedsgrad *',
            conflict_pairs_legend: 'Konflikttilladelser',
            conflict_pairs_hint: 'Hvert par definerer to tilladelser, som ikke bør være i besiddelse af den samme bruger.',
            placeholder_permission_a: '-- Tilladelse A --',
            placeholder_permission_b: '-- Tilladelse B --',
            permission_a_for_pair: 'Tilladelse A for par {index}',
            permission_b_for_pair: 'Tilladelse B for par {index}',
            conflict_pair_label: 'Konfliktpar {index}',
            remove_conflict_pair: 'Fjern konfliktpar {index}',
            add_pair: '+ Tilføj par',
            placeholder_name: 'f.eks. Betalingsgodkendelsesadskillelse',
            create_rule: 'Opret regel',
            loading_rule: 'Indlæser SoD-regel...',
            failed_load_rule: 'Reglen kunne ikke indlæses.',
            failed_create_rule: 'Kunne ikke oprette SoD-regel.',
            failed_update_rule: 'Kunne ikke opdatere SoD-reglen.',
            validation_name_required: 'Navn er påkrævet.',
            validation_pair_required: 'Der kræves mindst ét konflikttilladelsespar.',
            validation_pair_both_required: 'Par {index}: Begge tilladelser skal vælges.',
            validation_pair_must_differ: 'Par {index}: Tilladelse A og Tilladelse B skal være forskellige.',
            added_conflict_pair: 'Tilføjet konfliktpar {index}',
            removed_conflict_pair: 'Fjernet konfliktpar {index}',
            announce_loaded_violations: 'Indlæste {count} overtrædelser',
            announce_loaded_exceptions: 'Indlæste {count} undtagelser',
            announce_loaded_rules: 'Indlæste {count} SoD-regler',
            announce_loading_exceptions: 'Indlæser undtagelser...',
            regulatory_info: {
                sod_context: 'SoD-regler haandhaever den lovgivningsmaessige rolleseparation kraevet af PDPL. Overtraedelser knyttet til en framework_code kan medtages i rammespecifikke evidenspakker (UC-VN-09).',
                violation_export_context: 'SoD-overtraedelseseksporter indeholder framework_code, modstridende roller, alvorlighed, opretter og registreringstidspunkt for evidenspakker (P-VN-10).',
                exception_context: 'SoD-undtagelser producerer revisionsdokumentation: godkender, begrundelse, kompenserende kontroller og udloebsdato. Udloebne undtagelser forbliver synlige (UC-VN-09 Trin 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'CMP-integration',
            status: 'Forbindelsesstatus',
            endpoint: 'CMP-endepunkt',
            last_sync: 'Seneste Synkronisering',
            configure: 'Konfigurér',
        },
        purpose_mapping: {
            title: 'Formålskortlægning',
            cmp_purpose: 'CMP-formål',
            grc_purpose: 'GRC-formål',
            mapping_status: 'Kortlægningsstatus',
            auto_map: 'Automatisk Kortlægning',
            new_mapping: 'Ny Kortlægning',
        },
        webhook: {
            title: 'Webhook-konfiguration',
            webhook_url: 'Webhook-URL',
            secret: 'Webhook-hemmelighed',
            events: 'Hændelsestyper',
            test_webhook: 'Test webhook',
        },
        bulk_sync: {
            title: 'Massesynkronisering',
            sync_now: 'Synkronisér Nu',
            last_run: 'Seneste Kørsel',
            records_synced: 'Synkroniserede Poster',
            sync_history: 'Synkroniseringshistorik',
            syncing: 'Synkroniserer...',
        },
        sync_settings: {
            sync_failures: 'Synkroniseringsfejl',
            sync_secret_warning: 'Synkroniseringshemmelighed (gem dette nu — det vises ikke igen):',
            push_description: 'Konfigurer GRC Suite til at sende sagsopdateringer til AesirX CMP.',
            technical_description: 'Denne konfiguration styrer, hvordan GRC Suite sender sagsstatusopdateringer til AesirX CMP.',
            direction_label: 'Retning',
            direction_value: 'GRC Suite → AesirX CMP (udgående)',
            auth_label: 'Autentificering',
            auth_value: 'HMAC-SHA256-signatur i X-GRC-Signature-header',
            retry_label: 'Genforsøgspolitik',
            retry_value: '3 genforsøg med eksponentiel ventetid (30s, 2min, 10min)',
            inbound_label: 'Indgående konfiguration',
            inbound_value: 'Indgående webhook (CMP → GRC) er konfigureret i',
            cmp_webhook_settings: 'CMP webhook-indstillinger',
            page_title: 'CMP Synkroniseringsindstillinger',
            outbound_sync_configured: 'Udgående synkronisering konfigureret.',
            configure_sync_failed: 'Kunne ikke konfigurere synkronisering',
            configuration_updated: 'Konfiguration opdateret.',
            update_configuration_failed: 'Kunne ikke opdatere konfiguration',
            connection_test_failed: 'Forbindelsestest mislykkedes',
            test_result_message: 'Test sendt til {endpoint}. Status: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Ny → Under gennemgang (tildelt)',
                under_review_to_identity_verification: 'Under gennemgang → Identitetsverifikation',
                under_review_to_in_progress: 'Under gennemgang → I gang',
                in_progress_to_completed: 'I gang → Fuldført',
                in_progress_to_denied: 'I gang → Afvist',
                any_to_closed: 'Alle → Lukket',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhooks',
            new: 'Ny Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Hændelser',
            secret: 'Hemmelighed',
            active: 'Aktiv',
            test: 'Test',
            create_failed: 'Kunne ikke oprette webhook.',
            create_success: 'Webhook oprettet.',
            deactivate_failed: 'Kunne ikke deaktivere webhook.',
            deactivate_success: 'Webhook deaktiveret.',
            fetch_failed: 'Kunne ikke hente webhooks.',
            send_test_failed: 'Kunne ikke sende test.',
            test_failed: 'Webhook test mislykkede.',
            test_success: 'Webhook-test lykkedes.',
            edit_title: 'Rediger webhook',
            announce_loaded: 'Webhooks indlæst',
            update_success: 'Webhook opdateret.',
            update_failed: 'Kunne ikke opdatere webhook.',
            activate_success: 'Webhook aktiveret.',
            activate_failed: 'Kunne ikke aktivere webhook.',
        },
        domains: {
            title: 'Domæner',
            new: 'Nyt Domæne',
            domain_name: 'Domænenavn',
            verified: 'Verificeret',
            verify: 'Verificér',
            add_failed: 'Kunne ikke tilføje domæne.',
            add_success: 'Domæne tilføjet.',
            domain_placeholder: 'f.eks. grc.example.com',
            fetch_failed: 'Kunne ikke hente domæner.',
            remove_failed: 'Kunne ikke fjerne domæne.',
            remove_success: 'Domæne fjernet.',
            txt_record: 'TXT-post',
            verify_dns_failed: 'DNS verifikation mislykkede.',
            verify_failed: 'Kunne ikke verificere domæne.',
            verify_success: 'Domæne verificeret.',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Eksportplaner',
            new: 'Ny Plan',
            schedule_name: 'Navn',
            frequency: 'Frekvens',
            format: 'Format',
            last_run: 'Seneste Kørsel',
            next_run: 'Næste Kørsel',
            create_failed: 'Kunne ikke oprette tidsplan.',
            create_success: 'Tidsplan oprettet.',
            day_of_month: 'Dag i måneden',
            day_of_week: 'Dag af Uge',
            deactivate_failed: 'Kunne ikke deaktivere tidsplan.',
            deactivate_success: 'Tidsplan deaktiveret.',
            fetch_failed: 'Kunne ikke hente tidsplaner.',
            freq_daily: 'Dagligt',
            freq_monthly: 'Månedligt',
            freq_weekly: 'Ugentligt',
            recipients: 'Modtagere',
            time_utc: 'Tid (UTC)',
            trigger_failed: 'Kunne ikke udløse eksport.',
            trigger_success: 'Eksport udløst.',
            type_audit_trail: 'Revisionsspor',
            type_compliance_report: 'Overholdelsesrapport',
            type_custom: 'Brugerdefineret',
            type_incident_report: 'Hændelsesrapport',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'E-maillog',
            recipient: 'Modtager',
            subject: 'Emne',
            sent_at: 'Sendt Den',
            status: 'Status',
            resend: 'Send Igen',
            fetch_failed: 'Kunne ikke hente e-maillogs.',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organisationer',
        create: 'Opret organisation',
        edit: 'Rediger organisation',
        name: 'Navn',
        slug: 'Snegl',
        timezone: 'Tidszone',
        locale: 'Standardlokalitet',
        parent: 'Overordnet organisation',
        group: 'Gruppe',
        org_group: 'Organisationsgruppe',
        no_group: '(Ingen - ikke i en gruppe)',
        created: 'Oprettet',
        no_parent: '(Ingen - selvstændig)',
        no_orgs: 'Ingen organisationer fundet',
        load_error: 'Kunne ikke indlæse organisationer',
        save_error: 'Kunne ikke gemme organisation',
        created_msg: 'Organisation oprettet',
        updated: 'Organisation opdateret',
        deactivate: 'Deaktiver',
        deactivated: 'Organisation deaktiveret',
        confirm_deactivate: 'Er du sikker på, at du vil deaktivere denne organisation?',
        loaded_results: 'Organizations indlæst, {count} resultater',
        form_loaded: 'Organization-formular indlæst',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'engelsk (da-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Alle rammer',
        assigned_at: 'Tildelt den',
    },
    intake_configs: {
        title: 'Offentlig Indtag Konfigurationer',
        create: 'Opret konfiguration',
        edit: 'Rediger Indtags Konfiguration',
        label_col: 'Etiket',
        key: 'Indtagsnøgle',
        key_hint: 'Offentlig URL vil være: /rights-request?key=',
        mapped_org: 'Målorganisation',
        status: 'Status',
        enabled: 'Aktiveret',
        disabled: 'Deaktiveret',
        request_types: 'Aktiveret Anmodning Typer',
        language: 'Standardsprog',
        public_url: 'Offentlig URL',
        no_configs: 'Ingen indtagskonfigurationer fundet. Opret en for at aktivere offentlige rettighedsanmodningsformularer.',
        load_error: 'Kunne ikke indlæse indtagskonfigurationer',
        save_error: 'Kunne ikke gemme',
        delete_error: 'Kunne ikke slette',
        created_msg: 'Konfiguration oprettet',
        updated: 'Konfiguration opdateret',
        deleted: 'Indtagskonfiguration slettet',
        confirm_delete: 'Slet denne indtagskonfiguration?',
        select_types: 'Vælg mindst én anmodningstype',
        select_org: 'Vælg a mål organisation',
        routing_mode: 'Rutetilstand',
        single_org: 'Enkelt Organisation',
        org_group_mode: 'Organisationsgruppe',
        org_group: 'Organisationsgruppe',
        select_group: 'Vælg en organisationsgruppe...',
        group_root_hint: 'Indstilles automatisk til gruppens rodorganisation, når gruppen vælges.',
        loaded_results: 'Intake configurations indlæst, {count} resultater',
        form_loaded: 'Intake configuration-formular indlæst',
    },
    frequency: {
        weekly: 'Ugentligt',
        monthly: 'Månedligt',
        quarterly: 'Kvartalsvist',
        semi_annually: 'Halvårligt',
        annually: 'Årligt',
        custom: 'Tilpasset',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Kalender',
            all_tasks: 'Alle opgaver',
            my_tasks: 'Mine opgaver',
            overdue: 'Forsinkede',
            board: 'Tavle',
            workflows: 'Arbejdsgange',
        },
        list: {
            title: 'Alle opgaver',
            my_title: 'Mine opgaver',
            overdue_title: 'Forsinkede opgaver',
            new_task: 'Opret opgave',
            no_tasks: 'Ingen opgaver fundet.',
            empty_state: 'Ingen opgaver fundet. Opgaver genereres fra modularbejdsgange som vurderinger, hændelser og gennemgangskampagner.',
            total: 'Samlede opgaver',
            search_placeholder: 'Søg opgaver...',
            bulk_actions: 'Massehandlinger',
            change_status: 'Skift status',
            change_priority: 'Skift prioritet',
            assign: 'Tildel',
            sr_my_tasks_count: '{count} opgaver tildelt dig',
            sr_days_overdue: '{count} dage forsinket',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Opgavedetaljer',
            description: 'Beskrivelse',
            workflow_progress: 'Arbejdsgangsforløb',
            checklist: 'Tjekliste',
            dependencies: 'Afhængigheder',
            comments: 'Kommentarer',
            activity_timeline: 'Aktivitetstidslinje',
            assignees: 'Tildelte',
            add_assignee: 'Tilføj tildelt',
            remove_assignee: 'Fjern tildelt',
            due_date: 'Forfaldsdato',
            start_date: 'Startdato',
            sla_status: 'SLA-status',
            sla_on_track: 'På sporet',
            sla_at_risk: 'I fare',
            sla_breached: 'Overtrådt',
            tags: 'Tags',
            source_entity: 'Kildeentitet',
            view_source: 'Se kilde',
            add_dependency: 'Tilføj afhængighed',
            remove_dependency: 'Fjern afhængighed',
            write_comment: 'Skriv en kommentar...',
            post_comment: 'Send',
            edit_comment: 'Rediger',
            delete_comment: 'Slet',
            add_checklist_item: 'Tilføj element',
            no_dependencies: 'Ingen afhængigheder',
            no_comments: 'Ingen kommentarer endnu',
            blocks: 'Blokerer',
            blocked_by: 'Blokeret af',
            related: 'Relateret',
            working_days_remain: 'arbejdsdage tilbage',
            created_by: 'Oprettet af',
            completed_at: 'Fuldført den',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Ingen tildelte',
            add_assignee_placeholder: 'Indtast bruger-ID...',
            edited: 'redigeret',
            timeline: 'Aktivitetstidslinje',
            no_timeline: 'Ingen aktivitet endnu',
            timeline_reason: 'Reason',
            no_workflow: 'Ingen arbejdsgang tildelt',
            add_dependency_placeholder: 'Indtast opgave-ID...',
            sr_status_updated: 'Status opdateret til {status}',
            sr_priority_updated: 'Prioritet opdateret til {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Kør som arbejdsgang',
            convert_modal_title: 'Konverter opgave til workflowkørsel',
            convert_workflow_label: 'Arbejdsgang',
            convert_workflow_placeholder: 'Vælg en arbejdsgang...',
            convert_attachments_label: 'Vedhæftede filer',
            convert_attachments_empty: 'Ingen vedhæftede filer til denne opgave.',
            convert_persona_label: 'Persona tilsidesættelse (valgfrit)',
            convert_submit: 'Start workflowkørsel',
            convert_starting: 'Starter kørsel af arbejdsgang...',
            convert_started: 'Workflow-kørsel startet — åbningskørsel...',
            convert_disabled_in_flight: 'En arbejdsgang, der kører fra denne opgave, er allerede i gang.',
            linked_workflow_chip: 'Fra {workflow_title}',
            linked_chat_chip: 'Fra AI Chat',
            linked_more: '+{count} mere',
        },
        calendar: {
            title: 'Opgavekalender',
            page_title: 'Kalender',
            create_task: 'Opret opgave',
            month: 'Måned',
            week: 'Uge',
            day: 'Dag',
            today: 'I dag',
            more_tasks: '+{count} mere',
            no_tasks_day: 'Ingen opgaver denne dag',
            all_day: 'Hele dagen',
            quick_create_tooltip: 'Opret opgave for denne dag',
            view_changed: 'Kalendervisning ændret til {view}',
            tasks_in_view: '{count} opgaver i visning',
            task_in_view: '{count} opgave i udsigt',
            filter_my_tasks: 'Mine opgaver',
            filter_overdue: 'Forfaldne',
            filter_high_priority: 'Høj prioritet',
            filter_all: 'Alle',
        },
        filters: {
            assignee: 'Tildelt',
            me: 'Mig',
            role: 'Rolle',
            status: 'Status',
            priority: 'Prioritet',
            task_type: 'Opgavetype',
            source_module: 'Kildemodul',
            organization: 'Organisation',
            sla_breached_only: 'Vis kun SLA-overtrådte',
            clear_filters: 'Ryd filtre',
            apply_filters: 'Anvend',
            source_label: 'Kilde',
            source_manual: 'Manual',
            source_module_kind: 'Modul-genereret',
            source_chat: 'Fra AI Chat',
            source_workflow_run: 'Fra AI Workflow Run',
        },
        status: {
            pending: 'Afventer',
            in_progress: 'I gang',
            blocked: 'Blokeret',
            completed: 'Fuldført',
            overdue: 'Forsinkede',
            escalated: 'Eskaleret',
            cancelled: 'Annulleret',
        },
        priority: {
            critical: 'Kritisk',
            high: 'Høj',
            medium: 'Middel',
            low: 'Lav',
        },
        task_types: {
            manual_task: 'Manuel opgave',
            dsr_case: 'DSR-sag',
            dsr_review: 'DSR-gennemgang',
            dsr_escalation: 'DSR-eskalering',
            dpia_review: 'DPIA-gennemgang',
            dpia_approval: 'DPIA-godkendelse',
            transfer_review: 'Overførselsgennemgang',
            incident_task: 'Hændelsesopgave',
            incident_notification: 'Hændelsesnotifikation',
            playbook_step: 'Playbook-trin',
            regulator_deadline: 'Myndighedsfrist',
            monitoring_task: 'Overvågningsopgave',
            kpi_alert: 'KPI-alarm',
            vendor_review: 'Leverandørgennemgang',
            vendor_questionnaire: 'Leverandørspørgeskema',
            governance_review: 'Styringsgennemgang',
            drift_alert: 'Afvigelsesalarm',
            governance_approval: 'Styringsgodkendelse',
            deletion_task: 'Sletteopgave',
            deletion_verification: 'Sletteverifikation',
            access_review: 'Adgangsgennemgang',
            sod_alert: 'SoD-alarm',
            consent_workflow: 'Samtykkearbejdsgang',
            consent_alert: 'Samtykkealarm',
            property_review: 'Egenskabsgennemgang',
            inconsistency_alert: 'Uoverensstemmelsesalarm',
            form_review: 'Formulargennemgang',
            dossier_deadline: 'Dossierfrist',
            submission_task: 'Indsendelsesopgave',
            translation_review: 'Oversættelsesgennemgang',
            governance_task: 'Styringsopgave',
            reporting_deadline: 'Rapporteringsfrist',
            scanner_alert: 'Scanneralarm',
            classification_review: 'Klassificeringsgennemgang',
            lifecycle_task: 'Livscyklusopgave',
            discovery_review: 'Opdagelsesgennemgang',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Uddannelsestildeling',
        task_type_groups: {
            training: 'Uddannelse',
            manual: 'Manuel',
            dsr: 'DSR / Rettighedsanmodninger',
            assessments: 'Vurderinger',
            incidents: 'Hændelser',
            incident_ops: 'Hændelsesdrift',
            monitoring: 'Overvågning',
            vendor_risk: 'Leverandørrisiko',
            governance: 'Styring',
            deletion: 'Sletning',
            access_review: 'Adgangsgennemgang',
            consent: 'Samtykke',
            consent_properties: 'Samtykkeegenskaber',
            forms: 'Formularer',
            localization: 'Lokalisering',
            program_governance: 'Programstyring',
            privacy_scanner: 'Privatlivsscanner',
            classification: 'Klassificering',
            discovery: 'Opdagelse',
            forseti: 'Forseti',
        },
        create: {
            title: 'Opret opgave',
            task_title: 'Titel',
            task_description: 'Beskrivelse',
            task_type: 'Opgavetype',
            task_type_label: 'Opgavetype',
            task_type_placeholder: 'Vælg en opgavetype',
            priority: 'Prioritet',
            assignees: 'Tildelte',
            assignee_search_placeholder: 'Søg brugere...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Fjern {name}',
            due_date: 'Forfaldsdato',
            start_date: 'Startdato',
            checklist: 'Tjekliste',
            tags: 'Tags',
            add_checklist_item: 'Tilføj tjeklistepunkt',
            validation: {
                title_required: 'Titel er påkrævet',
                title_max_length: 'Titel skal være 500 tegn eller færre',
                description_max_length: 'Beskrivelse skal være 5000 tegn eller færre',
                due_date_before_start: 'Forfaldsdato skal være efter startdato',
                max_reminders: 'Maksimalt 5 påmindelser pr. opgave',
            },
            reminders: {
                title: 'Påmindelser',
                add: 'Tilføj påmindelse',
                at_due_date: 'Ved forfaldsdato',
                minutes_before: '{count} minutter før',
                hours_before: '{count} timer før',
                days_before: '{count} dage før',
                weeks_before: '{count} uger før',
                custom: 'Brugerdefineret',
                channel_platform: 'Platform',
                channel_email: 'E-mail',
                channel_both: 'Begge',
                offset_label: 'Påmindelse {n}: tidspunkt',
                channel_label: 'Påmindelse {n}: kanal',
                remove_label: 'Fjern påmindelse {n}',
            },
            workflow: {
                label: 'Arbejdsgang',
                none: 'Ingen (manuel statusstyring)',
                steps_count: '{count} trin',
                preview_label: 'Arbejdsgangstrin',
            },
        },
        errors: {
            fetch_failed: 'Kunne ikke indlæse opgaver.',
            save_failed: 'Kunne ikke gemme opgave.',
            delete_failed: 'Kunne ikke slette opgave.',
            status_change_failed: 'Kunne ikke ændre opgavestatus.',
            assign_failed: 'Kunne ikke tildele opgave.',
            comment_failed: 'Kunne ikke gemme kommentar.',
            checklist_failed: 'Kunne ikke opdatere tjekliste.',
            dependency_failed: 'Kunne ikke opdatere afhængighed.',
            workflow_failed: 'Kunne ikke gemme arbejdsgang.',
            workflow_activate_failed: 'Kunne ikke aktivere arbejdsgang.',
            workflow_delete_failed: 'Kunne ikke slette arbejdsgang.',
            template_install_failed: 'Kunne ikke installere skabelon.',
            search_failed: 'Kunne ikke søge opgaver.',
            convert_failed: 'Kunne ikke starte arbejdsproceskørslen.',
            convert_quota_exhausted: 'Token spand lav. Fyld op inden du kører igen.',
        },
        dependency_types: {
            blocks: 'Blokerer',
            blocked_by: 'Blokeret af',
            related: 'Relateret',
        },
        workflow: {
            current_step: 'Nuværende trin',
            transition: 'Overgang',
            history: 'Trinhistorik',
            no_workflow: 'Ingen arbejdsgang tildelt',
            detail_title: 'Arbejdsgangdetaljer',
            list_title: 'Arbejdsgangsdefinitioner',
            create_workflow: 'Opret arbejdsgang',
            browse_templates: 'Gennemse skabeloner',
            template_catalog_title: 'Arbejdsgangsskabelonkatalog',
            name: 'Navn',
            description: 'Beskrivelse',
            task_types: 'Opgavetyper',
            steps: 'Trin',
            version: 'version',
            created: 'Oprettet',
            actions: 'Handlinger',
            status_draft: 'Kladde',
            status_active: 'Aktiv',
            status_archived: 'Arkiveret',
            activate: 'Aktiver',
            archive: 'Arkivér',
            save: 'Gem',
            edit: 'Rediger',
            delete: 'Slet',
            clone: 'Klon',
            install: 'Installer',
            installed: 'Installeret',
            system: 'System',
            general_settings: 'Generelle indstillinger',
            default_assignment: 'Standardtildeling',
            default_sla: 'Standard SLA',
            workflow_steps: 'Arbejdsgangstrin',
            add_step: 'Tilføj trin',
            step_name: 'Trinnavn',
            step_status: 'Statusværdi',
            assignment_strategy: 'Tildelingsstrategi',
            assignment_role: 'Tildelingsrolle',
            sla_hours: 'SLA-timer',
            sla_calendar_type: 'Kalendertype',
            working_days: 'Arbejdsdage',
            calendar_days: 'Kalenderdage',
            is_terminal: 'Terminaltrin',
            required_fields: 'Påkrævede felter',
            checklist_template: 'Tjeklisteskabelon',
            transitions: 'Overgange',
            add_transition: 'Tilføj overgang',
            from_step: 'Fra',
            to_step: 'Til',
            permission: 'Tilladelse',
            sla_action: 'SLA-handling',
            notify: 'Notificer',
            escalation_rules: 'Eskaleringsregler',
            add_rule: 'Tilføj regel',
            trigger: 'Udløser',
            threshold: 'Tærskel',
            action: 'Handling',
            target_role: 'Målrolle',
            trigger_sla_threshold: 'SLA-tærskel',
            trigger_sla_breach: 'SLA-brud',
            trigger_blocked_duration: 'Blokeret varighed',
            trigger_manual: 'Manuel',
            action_notify: 'Notificer',
            action_escalate: 'Eskaler niveau',
            action_reassign: 'Gentildel',
            sla_start: 'Start',
            sla_pause: 'Pause',
            sla_resume: 'Genoptag',
            sla_stop: 'Stop',
            strategy_manual: 'Manuel',
            strategy_role_based: 'Rollebaseret',
            strategy_round_robin: 'Round Robin',
            strategy_least_loaded: 'Mindst belastet',
            strategy_creator: 'Opretter',
            confirm_delete: 'Er du sikker på, at du vil slette denne arbejdsgang?',
            confirm_archive: 'Er du sikker på, at du vil arkivere denne arbejdsgang?',
            cannot_delete_active: 'Aktive arbejdsgange kan ikke slettes. Arkiver først.',
            validation_errors: 'Valideringsfejl i arbejdsgang',
            step_count: '{count} trin',
            no_workflows: 'Ingen arbejdsgange fundet.',
            no_templates: 'Ingen skabeloner tilgængelige.',
            sr_templates_count: '{count} skabeloner tilgængelige',
            template_installed: 'Skabelon installeret.',
            activate_success: 'Arbejdsgang aktiveret.',
            archive_success: 'Arbejdsgang arkiveret.',
            save_success: 'Arbejdsgang gemt.',
            delete_success: 'Arbejdsgang slettet.',
            applicable_task_types: 'Gældende opgavetyper',
            steps_column: 'Trin',
            status_value_hint: 'Nøgle til arbejdsgangens tilstand, som bruges af overgange. Små bogstaver, tal og understregninger.',
            from_status: 'Fra status',
            to_status: 'Til status',
            required_permissions: 'Påkrævede tilladelser',
            transition_required_fields: 'Påkrævede felter (overgang)',
            auto_assign: 'Tildel automatisk',
            auto_assign_role: 'Rolle til automatisk tildeling',
            threshold_percent: 'Tærskel (%)',
            blocked_hours: 'Blokerede timer',
            notify_roles: 'Underret roller',
            escalate_to_role: 'Eskalér til rolle',
            notification_template: 'Notifikationsskabelon',
            action_escalate_tier: 'Eskalér niveau',
            create_modal_title: 'Opret arbejdsgang',
            create_modal_description: 'Definér trin, overgange og eskaleringsregler. Arbejdsgangen oprettes som et udkast.',
            create_modal_footer_note: 'Oprettet som et udkast. Aktivér den, når grafen er valideret.',
            validation_name_required: 'Navn på arbejdsgang er påkrævet.',
            validation_task_types_required: 'Vælg mindst én gældende opgavetype.',
            validation_min_one_step: 'Tilføj mindst ét trin.',
            validation_min_one_transition: 'Tilføj mindst én overgang.',
            validation_step_name_required: 'Trinnavn er påkrævet.',
            validation_status_value_required: 'Statusværdi er påkrævet.',
            validation_status_value_duplicate: 'Statusværdi skal være unik på tværs af trin.',
            validation_transition_endpoints_required: 'Vælg både en fra-status og en til-status.',
            validation_transition_unknown_status: 'Denne overgang henviser til en status, som intet trin definerer.',
            validation_sla_positive: 'SLA-timer skal være større end nul. Lad feltet stå tomt for ingen SLA.',
            validation_threshold_percent_range: 'Tærsklen skal være mellem 0 og 100.',
            validation_blocked_hours_positive: 'Blokerede timer skal være større end nul.',
            warning_no_terminal_step: 'Intet afsluttende trin. Aktivering mislykkes, indtil ét trin er markeret som afsluttende.',
            warning_step_no_outgoing: 'Ikke-afsluttende trin uden udgående overgange: {statuses}',
            warning_unreachable_steps: 'Trin, der ikke kan nås fra det første trin: {statuses}',
            orphaned_transitions_warning: '{count} overgange henviser til et slettet trin.',
            remove_orphaned_transitions: 'Fjern forældreløse overgange',
            transitions_renamed_announcement: '{count} overgange opdateret til den nye statusværdi.',
            add_checklist_item: 'Tilføj tjeklisteelement',
            checklist_item_label: 'Tjeklisteelement',
        },
        columns: {
            priority: 'Prioritet',
            title: 'Titel',
            status: 'Status',
            type: 'Type',
            assignees: 'Tildelt(e)',
            due_date: 'Forfaldsdato',
            source: 'Kilde',
            actions: 'Handlinger',
        },
        groups: {
            overdue: 'Forsinkede',
            due_today: 'Forfalder i dag',
            due_this_week: 'Forfalder denne uge',
            due_later: 'Forfalder senere',
            no_due_date: 'Ingen forfaldsdato',
            closed: 'Afsluttet eller annulleret',
        },
        board: {
            title: 'Opgavetavle',
            drag_hint: 'Træk opgaver mellem kolonner for at ændre status',
            empty_column: 'Ingen opgaver',
            keyboard_hint: 'Tastatur: piletaster til at navigere, mellemrum for at samle en opgave op, pil venstre/højre for at vælge kolonne, Enter for at slippe, Escape for at annullere.',
            sr_moving: 'Flytter {title}. Over {column}. Tryk Enter for at slippe, Escape for at annullere.',
            sr_tasks_shown: '{count} opgaver vist',
            sr_picked_up: '{title} samlet op',
            sr_returned: '{title} vendte tilbage til {column}',
            sr_dropped: '{title} placeret i {column}, position {position} af {total}',
            sr_move_cancelled: 'Flytning annulleret',
            sr_over_column: 'Over {column}',
            sr_moved_to: '{title} flyttet til {column}',
            invalid_transition: 'Kan ikke flytte opgave fra {from} til {to}.',
            terminal_status: 'Denne opgave er i en afsluttet status og kan ikke flyttes.',
            invalid_drop_target: 'Ugyldigt slipmal',
        },
        team: {
            title: 'Teamarbejdsbelastning',
            assignee: 'Tildelt',
            active: 'Aktiv',
            overdue: 'Forsinkede',
            blocked: 'Blokeret',
            completed_7d: 'Fuldført (7d)',
            load: 'Belastning',
            load_high: 'Høj',
            load_medium: 'Middel',
            load_normal: 'Normal',
        },
        graph: {
            title: 'Afhængighedsgraf',
            list_view: 'Listevisning',
            graph_view: 'Grafvisning',
            zoom_in: 'Zoom ind',
            zoom_out: 'Zoom ud',
            reset_zoom: 'Nulstil',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Ingen afhængigheder at visualisere.',
        },
        add_dependency_modal: {
            title: 'Tilføj afhængighed',
            search_placeholder: 'Søg opgaver efter titel eller id...',
            no_results: 'Ingen opgaver fundet.',
            this_blocks: 'Denne opgave blokerer valgte',
            this_blocked_by: 'Denne opgave er blokeret af valgt',
            related: 'Relateret',
            max_dependencies: 'Maksimalt 10 afhængigheder pr. opgave.',
            circular_error: 'Kan ikke tilføje: ville skabe en cirkulær afhængighed.',
        },
        conflict_detection: {
            title: 'Konfliktdetektion',
            unresolved: 'uløst',
            no_conflicts: 'Ingen konflikter fundet',
            section: 'Sektion',
            field: 'Felt',
            dept_a: 'Afdeling A',
            value_a: 'Værdi A',
            dept_b: 'Afdeling B',
            value_b: 'Værdi B',
            severity: 'Alvorlighed',
            status: 'Status',
            resolved: 'Løst',
            open: 'Åben',
            resolve: 'Løs',
            load_failed: 'Kunne ikke load conflicts',
            resolve_success: 'Konflikt løst',
            resolve_failed: 'Kunne ikke resolve conflict',
            severity_critical: 'Kritisk',
            severity_warning: 'Advarsel',
            severity_info: 'Info',
        },
        consolidation: {
            title: 'Konsolideringsfremskridt',
            add_child: 'Tilføj underopgave',
            child_task_id: 'Underopgave-ID',
            department_id: 'Afdelings-ID',
            link: 'Link',
            overall_completion: 'Samlet færdiggørelse',
            departments_complete: 'Afdelinger fuldført',
            overdue: 'Forsinket',
            ready: 'Klar',
            not_ready: 'Ikke klar',
            master_status: 'Overordnet status',
            department_breakdown: 'Afdelingsfordeling',
            department: 'Afdeling',
            status: 'Status',
            completion: 'Færdiggørelse',
            due_date: 'Forfaldsdato',
            overdue_flag: 'Forsinket',
            child_tasks: 'Underopgaver',
            task: 'Opgave',
            load_failed: 'Kunne ikke load consolidation data',
            child_linked: 'Underopgave tilknyttet',
            link_failed: 'Kunne ikke link child task',
            confirm_unlink: 'Er du sikker på, at du vil fjerne linket til denne underordnede opgave?',
            child_unlinked: 'Underordnet opgave fjernet',
            unlink_failed: 'Kunne ikke fjerne linket til den underordnede opgave',
            unlink: 'Fjern linket',
            status_completed: 'Fuldført',
            status_in_progress: 'I gang',
            status_pending: 'Afventende',
            select_child_task: 'Vælg underopgave',
            select_department: 'Vælg afdeling',
        },
        section_assignments: {
            title: 'Sektionstildelinger',
            assign_section: 'Tildel sektion',
            section_ref: 'Sektionsref.',
            section_label: 'Sektionsetiket',
            department_id: 'Afdelings-ID',
            department: 'Afdeling',
            assignee_id: 'Tildelt-ID',
            assignee: 'Tildelt',
            mandatory: 'Obligatorisk',
            assign: 'Tildel',
            no_assignments: 'Ingen sektionstildelinger',
            status: 'Status',
            completion: 'Færdiggørelse',
            mark_complete: 'Fuldfør',
            load_failed: 'Kunne ikke load section assignments',
            assign_success: 'Sektion tildelt',
            assign_failed: 'Kunne ikke assign section',
            status_updated: 'Status opdateret',
            status_update_failed: 'Kunne ikke opdatere status',
            section_ref_placeholder: 'f.eks. 3.2',
            status_completed: 'Fuldført',
            status_in_progress: 'I gang',
            status_pending: 'Afventende',
            select_department: 'Vælg afdeling',
            select_assignee: 'Vælg tildelt',
        },
        overdue_columns: {
            days_overdue: 'Dage forsinket',
        },
        regulatory_info: {
            deadline_context: 'Ved oprettelse af DPIA-opgaver bor forfaldsdatoer overholde PDPL-indleveringsfrister: afdelingsinput = 10 arbejdsdage. Cybersikkerheds- og telekomfrister gaelder ogsa.',
            overdue_deadline_context: 'Lovmaessige frister fra pakkedata: PDPL afdelingsinput = 10 arbejdsdage, tillaegssvar = 15 arbejdsdage, genindsendelse = 10 arbejdsdage. SLA-overtradte opgaver markeres visuelt.',
            section_assignment_context: 'DPIA multi-afdelingsproces (PDPL-P1): DPO tildeler dossiersektioner til afdelinger. Afdelingsinputfrist: 10 arbejdsdage.',
            template_catalog_context: 'Systemskabeloner daekker rammespecifikke processer: PDPL DPIA, Data Law attestering, Cybersikkerheds haendelsesrespons, Telekom myndighedsanmodning og AI-registrering. Systemskabeloner kan ikke redigeres.',
            workflow_instantiation_context: 'Installerede skabeloner kan instantieres til live processer. Opgaver oprettes automatisk med beregnede forfaldsdatoer.',
        },
        overdue: {
            days_overdue: 'dage forsinket',
        },
    },
    notifications: {
        bell: {
            title: 'Meddelelser',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Meddelelser',
            mark_all_read: 'Marker alle som læst',
            no_notifications: 'Ingen meddelelser',
            filter: {
                all: 'Alle',
                unread: 'Ulæst',
                tasks: 'Opgaver',
                approvals: 'Godkendelser',
                alerts: 'Advarsler',
            },
        },
        preferences: {
            title: 'Notifikationspræferencer',
            description: 'Konfigurer, hvordan du modtager meddelelser',
            category: {
                label: 'Kategori',
                task_assigned: 'Opgave tildelt',
                task_sla_approaching: 'SLA Nærmer sig',
                task_overdue: 'Opgave for sent',
                task_completed: 'Opgave afsluttet',
                task_escalated: 'Opgave eskaleret',
                workflow_transition: 'Workflow overgang',
                approval_requested: 'Godkendelse anmodet',
                comment_added: 'Kommentar tilføjet',
                weekly_digest: 'Ugentlig Digest',
                sla_breach_report: 'SLA Rapport om brud',
                system_alert: 'Systemalarm',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Platform',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Stille Timer',
                start: 'Start',
                end: 'End',
                timezone: 'Tidszone',
            },
            digest: {
                title: 'Fordøjelsesfrekvens',
                daily: 'Dagligt',
                weekly: 'Ugentligt',
                none: 'Ingen',
            },
            watched: {
                title: 'Overvågede Enheder',
                empty: 'Ingen overvågede enheder',
            },
            save: 'Gem præferencer',
            saving: 'Gemmer ...',
            saved_success: 'Præferencer blev gemt',
            load_error: 'Kunne ikke indlæse gemte præferencer. Viser standardindstillinger — ændringer gemmes, når du klikker på Gem.',
            enabled: 'Aktiveret',
            disabled: 'Deaktiveret',
            unwatch: 'Fjern overvågning',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Notifikationsadministrationskø',
            description: 'Overvåg e-mailleveringsstatus og prøv mislykkede beskeder igen',
            load_error: 'Kunne ikke indlæse e-mailkødata',
            retry_error: 'Kunne ikke prøve e-mail igen',
            filter_status: 'Filtrer efter status',
            all_statuses: 'Alle',
            refresh: 'Opdater',
            status_queued: 'I kø',
            status_sent: 'Sendt',
            status_failed: 'Mislykket',
            status_bounced: 'Afvist',
            status_sending: 'Sending',
            col_recipient: 'Modtager',
            col_category: 'Kategori',
            col_template: 'Skabelon',
            col_status: 'Status',
            col_retries: 'Genforsøg',
            col_created: 'Oprettet',
            col_sent: 'Sendt den',
            col_actions: 'Handlinger',
            no_items: 'Ingen e-mailkøelementer fundet',
            retry: 'Prøv igen',
            retrying: 'Prøver igen...',
            prev: 'Forrige',
            next: 'Næste',
            access_denied_title: 'Administratoradgang påkrævet',
            access_denied: 'Denne side er kun tilgængelig for administratorer. Kontakt venligst din administrator, hvis du har brug for adgang.',
            announce_queue_loaded: 'E-mailkø er indlæst, {count} elementer',
            announce_processed: 'Behandlede {count} e-mail(s)',
            process_now: 'Behandle nu',
            processing_ellipsis: 'Behandler...',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'UI Assist',
            activate: 'Aktiver',
            deactivate: 'Deaktiver',
            connected: 'Forbundet',
            disconnected: 'Afbrudt',
            disconnected_hint: "Afbrudt — kør 'npm run relay' for at oprette forbindelse til Claude CLI",
            open_panel: 'Åbn UI Assist-panelet',
            history: 'Historie',
            settings: 'Indstillinger',
        },
        prompt: {
            placeholder: 'Beskriv den ændring, du vil foretage...',
            send: 'Send',
            cancel: 'Annuller',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Behandler...',
            complete: 'Komplet',
            error: 'Fejl',
            abort: 'Afbryd',
            abort_confirm: 'Er du sikker på, at du vil abortere?',
            aborted_hint: 'Sessionen blev afbrudt — du kan fortsætte herfra.',
            undo: 'Fortryd',
            redo: 'Gentag',
            retry: 'Prøv igen',
            dismiss: 'Afvis',
        },
        label: {
            component: 'Komponent',
            element: 'Element',
            file: 'Fil',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'Ingen beskeder endnu. Vælg elementer, og send en prompt for at komme i gang.',
        },
        panel: {
            tab_conversation: 'Chat',
            tab_tasks: 'Opgaver',
            tab_sprint: 'Sprint',
            tab_sessions: 'Sessioner',
            tab_lobby: 'Lobbyen',
            follow_up: 'Følg op',
            followup_placeholder: 'Ask a follow-up question⬦',
            interrupt_placeholder: 'Skriv for at afbryde og omdirigere⬦',
            send_interrupt: 'Send & Afbryd',
            will_interrupt: 'Afbryder det aktuelle svar og sender dette i stedet',
            expand: 'Udvid til fuld skærm',
            collapse: 'Fold sammen til sidepanel',
            no_sessions: 'Ingen tidligere sessioner. Vælg elementer og send en besked for at begynde.',
            session_active: 'Aktiv',
            session_ended: 'Afsluttet',
            session_messages: '{count} beskeder',
            switch_session: 'Skift til denne session',
            load_session: 'Indlæs sessionshistorik',
            session_restored: 'Session restored from history',
            archive_session: 'Arkiv session',
            archive_confirm: 'Vil du arkivere denne session? The session file will be deleted.',
            new_session: 'Ny session',
            new_session_desc: 'Brug Aktiver til at vælge et UI-element og starte en ny session',
            start_new_session: 'Start ny session',
            no_active_session_chat: 'Start en ny session, eller brug Aktiver til at vælge et UI-element på siden for at begynde.',
            no_active_session_tasks: 'Start en ny session, eller vælg en eksisterende session for at oprette opgaver.',
            no_active_session_sprint: 'Start en ny session, eller vælg en eksisterende session for at planlægge en sprint.',
            previous_sessions: 'Tidligere sessioner',
        },
        tasks: {
            none_found: 'Ingen opgaver fundet i svaret. Bed Claude om at foreslå opgaver.',
            selected: 'selected',
            create_selected: 'Opret opgaver',
            execute_tasks: 'Udfør opgaver',
            creating: 'Opretter...',
            created: 'oprettede opgaver',
            create_error: 'Opgaven kunne ikke oprettes',
            plan_sprint: 'Planlæg sprint',
            priority_critical_desc: 'Blokering — ret straks',
            priority_high_desc: 'Vigtig — løs i denne sprint',
            priority_medium_desc: 'Normal — planlæg til kommende sprint',
            priority_low_desc: 'Rart at have — backlog-kandidat',
            add_custom: 'Tilføj opgave',
            add_title_placeholder: 'Opgavetitel⬦',
            add_desc_placeholder: 'Beskrivelse (valgfrit)',
            add_save: 'Tilføj',
            add_cancel: 'Annuller',
        },
        sprint: {
            title: 'Sprint planlægger',
            assigned: 'assigned',
            new_placeholder: 'Sprintnavn (f.eks. Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Efterslæb',
            move_to: 'Flyt til...',
            empty: 'Opret en sprintgruppe og tildel opgaver til den.',
            create_all: 'Opret alle opgaver',
            remove: 'Fjern',
            drop_here: 'Tildel opgaver fra backlog nedenfor',
            add_task: 'Tilføj opgave fra backlog...',
        },
        feature_disabled: 'UI Assist er ikke aktiveret',
        permission_denied: 'Du har ikke tilladelse til at bruge UI Assist',
    },
    license: {
        title: 'Licens & Pakke',
        current_license: 'Nuværende licens',
        license_key: 'Licensnøgle',
        activate: 'Aktiver licens',
        deactivate: 'Deaktiver licens',
        validate_now: 'Bekræft nu',
        status: 'Licensstatus',
        active: 'Aktiv',
        expired: 'Udløbet',
        suspended: 'Suspenderet',
        invalid: 'Ugyldig',
        pending: 'Afventer',
        no_license: 'Ingen licens',
        expires_at: 'Udløber',
        last_validated: 'Sidst valideret',
        domain: 'Licenseret domæne',
        activation_success: 'Licens aktiveret.',
        activation_failed: 'Licensaktivering mislykkedes.',
        domain_mismatch: 'Licensdomæne matcher ikke det aktuelle domæne.',
        deactivation_success: 'Licens deaktiveret.',
        validation_success: 'Licens valideret.',
        validation_failed: 'Licensvalidering mislykkedes.',
        enter_key: 'Indtast din licensnøgle',
        confirm_deactivate: 'Er du sikker på, at du vil deaktivere denne licens? Modulbegrænsninger vil gælde baseret på standardniveauet.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Pakke',
        module_column: 'modul',
        current_tier: 'Nuværende pakke',
        light: 'Lys',
        standard: 'Standard',
        enterprise_unlimited: 'Enterprise Unlimited',
        change_package: 'Skift pakke',
        upgrade: 'Opgrader',
        downgrade: 'Nedgrader',
        target_tier: 'Målpakke',
        new_license_key: 'Licensnøgle til Target Package',
        confirm_change: 'Bekræft pakkeændring',
        change_success: 'Pakken blev ændret.',
        change_failed: 'Pakkeændring mislykkedes.',
        modules_included: 'Moduler inkluderet',
        modules_restricted: 'Ikke tilgængelig i den nuværende pakke',
        upgrade_to_enable: 'Opgrader for at aktivere dette modul.',
        module_availability: 'Modultilgængelighed',
        comparison: 'Pakkesammenligning',
        power_users: 'Superbrugere',
        unlimited: 'Ubegrænset',
        impact_warning: 'Nedgradering vil deaktivere følgende moduler:',
        invalid_license: 'Den medfølgende licensnøgle er ikke gyldig for denne pakke.',
        tier_mismatch: 'Licensnøglen er til et andet pakkeniveau.',
    },
    board_report: {
        title: 'Bestyrelsesrapport om Overholdelse',
        subtitle: 'Ledelsesresumé af overholdelse på tværs af alle moduler',
        date_from: 'Fra dato',
        date_to: 'Til dato',
        language: 'Rapportsprog',
        generate: 'Generer rapport',
        generating: 'Genererer rapport...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'Download PDF',
        close: 'Luk rapport',
        open_board_report: 'Generer bestyrelsesrapport',
        close_board_report: 'Luk bestyrelsesrapport',
        report_period: 'Rapportperiode',
        generated_at: 'Genereret den',
        generated_by: 'Genereret af',
        organization: 'Organisation',
        section_rights_requests: 'Rettigheds­anmodninger',
        section_vendors: 'Leverandører',
        section_dpias: "DPIA'er",
        section_consent: 'Samtykkepolitikker',
        section_data_mapping: 'Datakortlægning',
        section_incidents: 'Hændelser',
        section_classification: 'Dataklassifikation',
        section_governance: 'Ledelse',
        open: 'Åben',
        closed: 'Lukket',
        sla_compliance: 'SLA-overholdelse',
        total: 'I alt',
        expired_dpa: 'Udløbet DPA',
        high_risk: 'Høj risiko',
        completed: 'Afsluttet',
        overdue: 'Forsinket',
        policies_active: 'Aktive politikker',
        coverage: 'Dækning',
        systems: 'Systemer',
        completeness: 'Fuldstændighed',
        avg_resolution_days: 'Gns. løsningstid (dage)',
        labeled: 'Mærket',
        unlabeled_systems: 'Umærkede systemer',
        compliance_score: 'Overholdelsesscore',
        drift_events_open: 'Åbne afvigelser',
        no_data: 'Ingen data tilgængelig for den valgte periode.',
        error_date_range: 'Vælg venligst både start- og slutdato.',
        table_category: 'Kategori',
        table_metric: 'Metrik',
        table_value: 'Værdi',
        showing_chart_view: 'Viser diagramvisning',
        showing_table_view: 'Viser datatabelvisning',
        error_generate: 'Bestyrelsesrapporten kunne ikke genereres.',
        playbook_evidence_title: 'Dokumentation for playbook-udførelse',
        playbook_evidence_description: 'Udførelsesaktivitet pr. playbook og vedhæftede dokumentationsfiler for rapporteringsperioden.',
        playbook_name: 'Legebog',
        playbook_execution_count: 'Udførelser',
        playbook_steps_total: 'Trin',
        playbook_steps_required: 'Dokumentation påkrævet',
        playbook_steps_attached: 'Dokumentation vedhæftet',
        playbook_coverage_pct: 'Dækning',
        playbook_evidence_files: 'Dokumentationsfiler',
        playbook_no_evidence: 'Ingen playbook-udførelser i denne periode.',
        playbook_download_file: 'Download fil',
    },
    framework_module_links: {
        title: 'Rammetilknytning',
        modules: 'Tilknyttede moduler',
        add_module: 'Tilføj modul',
        module_code: 'Modulkode',
        required: 'Påkrævet',
        notes: 'Notater',
        link: 'Link',
        no_modules: 'Ingen moduler tilknyttet',
        personas: 'Tilknyttede personas',
        add_persona: 'Tilføj persona',
        persona_id: 'Persona-id',
        role_description: 'Rollebeskrivelse',
        no_personas: 'Ingen personas tilknyttet',
    },
    framework_channels: {
        title: 'Indsendelsekanaler & Frister',
        channels: 'Indsendelsekanaler',
        add_channel: 'Tilføj kanal',
        channel_type: 'Type',
        name: 'Navn',
        instructions: 'Instruktioner',
        active: 'Aktiv',
        no_channels: 'Ingen indsendelsekanaler konfigureret',
        portal: 'Portal',
        in_person: 'Personligt',
        postal: 'Post',
        email: 'E-mail',
        add: 'Tilføj',
        deadlines: 'Lovgivningsmæssige frister',
        add_deadline: 'Tilføj frist',
        label: 'Etiket',
        days: 'Dage',
        day_type: 'Dagtype',
        calendar_days: 'Kalenderdage',
        working_days: 'Arbejdsdage',
        description: 'Beskrivelse',
        no_deadlines: 'Ingen frister konfigureret',
    },
    regulatory_updates: {
        title: 'Lovgivningsopdateringer & Ramme-dashboard',
        coverage_dashboard: 'Rammeforsikringsdashboard',
        no_frameworks: 'Ingen rammer fundet',
        framework: 'Ramme',
        status: 'Status',
        modules: 'Moduler',
        templates: 'Skabeloner',
        forms: 'Formularer',
        channels: 'Kanaler',
        deadlines: 'Frister',
        pending: 'Afventende opdateringer',
        updates: 'Lovgivningsopdateringer',
        create_update: 'Opret opdatering',
        framework_id: 'Ramme-id',
        update_title: 'Titel',
        description: 'Beskrivelse',
        effective_date: 'Ikrafttrædelsesdato',
        create: 'Opret',
        no_updates: 'Ingen lovgivningsopdateringer',
        impact: 'Påvirkning',
        assess: 'Vurder',
    },
    vehicle_monitoring: enVehicleMonitoring,
    record_links: enRecordLinks,
    module_tips: daModuleTips,
    framework_rights: {
        be_informed: 'Ret til at blive informeret',
        consent: 'Ret til samtykke',
        refuse_consent: 'Ret til at nægte samtykke',
        withdraw_consent: 'Ret til at trække samtykke tilbage',
        access: 'Ret til indsigt',
        rectification: 'Ret til berigtigelse',
        data_provision: 'Ret til dataudlevering',
        deletion: 'Ret til sletning',
        restriction_of_processing: 'Ret til begrænsning af behandling',
        object_to_processing: 'Ret til indsigelse mod behandling',
        complaint: 'Ret til at indgive klage',
        denunciation: 'Ret til anmeldelse',
        lawsuit: 'Ret til at anlægge retssag',
        claim_damages: 'Ret til erstatning',
        request_protection_measures: 'Ret til at anmode om beskyttelsesforanstaltninger',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Direktiv 2002/58/EF (e-databeskyttelsesdirektivet)',
            description: 'Overordnet direktiv for national e-privatlivsgennemførelse. Dækker cookies, privatliv i elektronisk kommunikation, direkte markedsføring. Hvert medlemsland skal gennemføre i national lovgivning.',
        },
        EU_GDPR: {
            display_name: 'Generel databeskyttelsesforordning (EU) 2016/679',
            description: 'Den generelle databeskyttelsesforordning (GDPR) er en EU-forordning om databeskyttelse og privatliv. Den gælder direkte i alle EU/EØS-medlemslande.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Direktiv (EU) 2022/2555 (NIS2)',
            description: 'Overordnet direktiv for nationale NIS2 gennemførelser. Dækker cybersikkerhedsrisikostyring, hændelsesrapportering, forsyningskædesikkerhed for væsentlige og vigtige enheder.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Vietnam AI lov (134/2025/QH15) – gældende 1. marts 2026',
            description: 'Vietnam AI Lov (Luật Trí tuệ nhân tạo 134/2025/QH15), der regulerer udvikling, implementering og brug af kunstige intelligenssystemer. Etablerer risikoklassificering, gennemsigtighed og mærkningspligter, krav til hændelsesrapportering, konsekvensvurderingsforpligtelser og en kontrolleret testmekanisme (sandbox) for AI aktiviteter i Vietnam.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Vietnams lov om cybersikkerhed (24/2018-æra)',
            description: 'Vietnams lov om cybersikkerhed (lov nr. 24/2018/QH14), der fastlægger krav til cybersikkerhed, datalokalisering og hændelsesrapportering. Aktiv indtil afløst af lov 116/2025/QH15 med virkning fra 2026-07-01.',
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Dekret 53 — Implementering af lov om cybersikkerhed',
            description: 'Dekret 53/2022/ND-CP om implementering af cybersikkerhedsloven, der dækker bestemmelse inden for rammerne, datalokaliseringsudløsere, krav til lokal tilstedeværelse og arbejdsgange for myndighedssamarbejde.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Lov om cybersikkerhed (116/2025/QH15)',
            description: 'Vietnams konsoliderede lov om cybersikkerhed (lov 116/2025/QH15), der erstatter lov 24/2018/QH14, med virkning fra 1. juli 2026. Konsoliderer anvendelsesområdet for dekret 53/2022/NĐ-CP (se VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Vietnams datalov',
            description: 'Vietnams datalov (lov 60/2024/QH15), der etablerer omfattende datastyringskrav, herunder dataopgørelse, klassificering, delingsstyring og grænseoverskridende dataoverførselsstyring. Overlapper med VN_PDPL_LAW_2025 (persondataaspekter) og VN_CYBERSEC_LAW_2025 (datalokalisering/klassificering).',
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Dekret 165/2025/NĐ-CPĐ-CP (Data Law Implementation)',
            description: 'Dekret 165/2025/NĐ-CP, der implementerer Vietnams datalov (lov 60/2024/QH15). Leverer officielle formularer til grænseoverskridende dataoverførselskonsekvensvurdering, årlig risikovurderingsrapportering og national databaseadgang. Definerer 11 officielle Mẫu số-former.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Udkast til dekret om databørser 2026',
            description: 'Udkast til dekret om driften af dataudvekslingsplatforme (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Regulerer platformsklassificering, deltagerberettigelse, kontrol af dataprodukter og -tjenester, test af sandkasse, transaktionsworkflow, elektroniske kontrakter, certifikater for ejendomsrettigheder over data og håndtering af klager/tvister. I øjeblikket i offentlig høring; endnu ikke offentliggjort. Citerer lov om data 60/2024/QH15, lov om beskyttelse af persondata 91/2025/QH15, lov om cybersikkerhed 116/2025/QH15 og lov om elektroniske transaktioner 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Lov om elektronisk handel (122/2025/QH15)',
            description: 'Vietnams lov om elektronisk handel (Luật Thương mại điện tử 122/2025/QH15), der etablerer en samlet national ordning for elektronisk handel, der dækker indenlandske og grænseoverskridende platformsoperationer, platformsstyring og ansvarlighed, forbrugerbeskyttelse, sælger og tilknyttede selskaber, håndtering af indhold, håndtering af klage og nedskrivning af indhold, kontrol, klage- og nedskrivningsprocedurer. statens e-handelsstyringssystem.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Dekret 13/2023/NĐ-CP',
            description: 'Dekret 13/2023/ND-CP, det operationelle instrument for PDPL opgaver, herunder dossierarkivering, forvaltningsartefakter, grænseoverskridende mekanik og administrative procedurer for MoPS.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Lov om beskyttelse af personoplysninger (91/2025/QH15)',
            description: 'Vietnams lov om beskyttelse af personoplysninger (lov 91/2025/QH15), der etablerer omfattende rammer for beskyttelse af persondata, med virkning fra 1. januar 2026. Dækker overholdelsestemaer A-J: lovlige behandlingsbaser, samtykke til følsomme data, registreredes rettigheder (adgang/berigtigelse/sletning/begrænsning/indsigelse/indsigelse mod meddelelse/portabilitet), og datasubjektets rettigheder. DPIA til højrisikobehandling, grænseoverskridende overførselsstyring, DPO betegnelse, registreringer af behandlingsaktiviteter, processorkontraktlige forpligtelser. Myndighedsindgivelsesforpligtelser via MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Krydsreferencer: VN_PDPL_DECREE_356_2025 (gennemførelsesdekret), VN_CYBERSEC_LAW_2025 (cybersikkerhedsoverlapninger), VN_DATA_LAW (dataforvaltning).',
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Dekret 356/2025/ND-CP (PDPL Implementering)',
            description: 'Dekret 356/2025/ND-CP om implementering af loven om beskyttelse af personoplysninger (lov 91/2025/QH15). Dækker dossierarkivering, styringsartefakter, grænseoverskridende mekanik og serviceudbydercertificering. Bilagsformular fortegnelse: Phụ lục I–VIII (konsekvensvurdering, grænseoverskridende overførsel, certificering). MoPS administrative procedurer via VN_PDPL_ADMIN_PROCEDURES_778_2026 (afgørelse 778/QĐ-BCA-A05). Certificeringsarbejdsgang: ansøgning → udstedelse → ændringsanmodning → genudstedelse → tilbagekaldelse. Krydsreferencer: VN_PDPL_LAW_2025 (overordnet lov), VN_CYBERSEC_LAW_2025 (datalokalisering), VN_DATA_LAW (datastyring overlapper).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 Administrative procedurer (afgørelse 778/QĐ-BCA-A05)',
            description: 'Afgørelse 778/QĐ-BCA-A05 (februar 2026) — Administrative procedurer for beskyttelse af personoplysninger i henhold til A05 (MPS). Definerer procedurer for indsendelse, behandling og resultatudstedelse, herunder DLCN-HC formularer og lovpligtige formularer.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Udkast til håndhævelsesdekret 2026',
            description: 'Udkast til håndhævelses- og sanktionsdekret for overtrædelser af persondataloven (lov 91/2025/QH15). Definerer strafintervaller, skærpende/formildende faktorer og håndhævelsesprocedurer. I øjeblikket i udkaststatus afventer offentliggørelse.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Vietnams telekommunikationslov 2023',
            description: 'Vietnam Telecommunications Law 2023 (Luật Viễn thông), en sektoroverlejring for telekommunikations-/ISP-/platformsvirksomheder med fortrolighed, afsløringskontrol og håndtering af myndighedsanmodninger. Krydsreferencer: VN_CYBERSEC_LAW_2025 (informationssystemsikkerhed), VN_PDPL_LAW_2025 (brugerdatabehandling), VN_DATA_LAW (datastyring).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Dekret 163/2024/NĐ-CPĐ-CP (Telecom Law Implementation)',
            description: 'Dekret 163/2024/NĐ-CP om gennemførelse af telekommunikationsloven (lov 24/2023/QH15). Leverer officielle formularer til meddelelser om teletjenester, licensering, registrering og udviklingsplaner for datacenter. Definerer 7 officielle Mẫu số-former.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Klager, tvister og risikostyring',
            description: 'Platformoperatører skal sørge for klageindtag, tvisthåndteringsarbejdsgange med bevisbevaring, risikoidentifikation og tidlig advarselskontrol og periodisk gennemgang af risici for privatliv, cybersikkerhed og adgangsstyring.',
        },
        vn_dp_draft_e: {
            title: 'Transaktionsarbejdsgang og certifikat for ejendomsrettigheder over data',
            description: 'Transaktioner kræver kontoregistrering, noteringsgodkendelse, elektronisk kontraktindgåelse, betalingsbekræftelse, leveringsregistrering og udstedelse af et certifikat for ejendomsrettigheder over data, hvor det er relevant. Transaktionshistorik og sporbarhed skal bevares i et transaktionsregister.',
        },
        vn_dp_draft_d: {
            title: 'Sandbox og testmiljø kontroller',
            description: 'Testmiljøer skal være isoleret, adgangsbegrænset, sikkerhedsovervåget og gennemgået, før noget output udtrækkes. Der må ikke udtrækkes kildedata fra testmiljøet, og lukning/oprydning af testsessioner skal registreres.',
        },
        vn_dp_draft_c: {
            title: 'Kontrol af dataprodukter og servicelister',
            description: 'Fortegnelser skal understøttes af lovlig kilde- og ejerskabs-/brugsrettighedsbeviser, tekniske valideringsregistreringer, kvalitetsoplysninger og optegnelser om anmeldelse og godkendelse af platformssider. Forbudte data skal filtreres før optagelse.',
        },
        vn_dp_draft_b: {
            title: 'Deltagerberettigelse og onboarding',
            description: 'Platformoperatører skal verificere juridisk status, juridisk kapacitet og betingede forretningskrav til sælgere og købere, registrere transaktions- og betalingskonti og vedligeholde et deltagerregister.',
        },
        vn_dp_draft_a: {
            title: 'Dataplatformsklassificering og driftsmodel',
            description: 'Platformoperatører skal etablere en klassifikation af platformstype (national dataplatform vs. andre dataplatforme), definere operatørens berettigelse og ansvar og dokumentere priser, gebyrer, driftsregler og teknisk styring.',
        },
        d165_r5: {
            title: 'National database information adgang',
            description: 'Anmodninger om at udtrække/bruge oplysninger fra den nationale digitale datadatabase anvender foreskrevne formularer (Mẫu số 07a/07b).',
        },
        d165_r4: {
            title: 'Årlig risikovurderingsrapportering',
            description: 'Årlige risikovurderingsrapporter skal indgives ved hjælp af foreskrevne formularer (Mẫu số 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Opsigelse af grænseoverskridende overførsel',
            description: 'Opsigelse af grænseoverskridende overførsel skal meddeles ved hjælp af foreskrevne formularer (Mẫu số 04a/04b).',
        },
        d165_r2: {
            title: 'Meddelelse om vurderingsresultat',
            description: 'Myndigheden udsteder meddelelser om vurderingsresultater ved hjælp af den foreskrevne formular (Mẫu số 03).',
        },
        d165_r1: {
            title: 'Indgivelse af konsekvensanalyse for grænseoverskridende dataoverførsel',
            description: 'Organisationer skal indgive grænseoverskridende dataoverførsel/behandling af konsekvensvurderingsdossierer ved hjælp af foreskrevne formularer (Mẫu số 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Samtykke til cookies og lignende teknologier',
            description: 'Lagring eller adgang til information på et terminaludstyr er kun tilladt med forudgående informeret samtykke, bortset fra teknisk nødvendig opbevaring.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Fortrolighed af kommunikation',
            description: 'Medlemsstaterne sikrer fortroligheden af kommunikation og relaterede trafikdata ved hjælp af et offentligt kommunikationsnet og offentligt tilgængelige elektroniske kommunikationstjenester.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Samtykke til direkte markedsføring (opt-in)',
            description: 'Brug af elektroniske kommunikationssystemer til direkte markedsføring kræver forudgående samtykke fra abonnenten.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Soft opt-in for eksisterende kunder',
            description: 'Direkte markedsføring til eksisterende kunder for lignende produkter/tjenester er tilladt uden samtykke, hvis der er givet fravalg.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Misligholdelsesmeddelelse til teleudbydere',
            description: 'Udbydere af offentligt tilgængelige elektroniske kommunikationstjenester skal underrette den kompetente nationale myndighed om brud på persondatasikkerheden.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Ret til at gøre indsigelse mod direkte markedsføring',
            description: 'Abonnenter skal til enhver tid og gratis have mulighed for at gøre indsigelse mod at modtage direkte markedsføringsmeddelelser.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Opbevaring og sletning af trafikdata',
            description: 'Trafikdata vedrørende abonnenter og brugere skal slettes eller gøres anonyme, når de ikke længere er nødvendige for transmission, undtagen for fakturering eller samtrafikbetalinger.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Betingelser for behandling af steddata',
            description: 'Andre lokalitetsdata end trafikdata må kun behandles, når de er gjort anonyme eller med brugerens samtykke, i det omfang og i den varighed, det er nødvendigt.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Behandlingens lovlighed — samtykke',
            description: 'Behandlingen er kun lovlig, hvis den registrerede har givet samtykke til behandlingen af deres personoplysninger til et eller flere specifikke formål.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Behandlingens lovlighed — kontrakt',
            description: 'Behandling er nødvendig for opfyldelsen af en kontrakt, som den registrerede er part i.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Behandlingens lovlighed — juridisk forpligtelse',
            description: 'Behandling er nødvendig for at overholde en retlig forpligtelse, som den dataansvarlige er underlagt.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Behandlingens lovlighed — vitale interesser',
            description: 'Behandling er nødvendig for at beskytte den registreredes eller en anden fysisk persons vitale interesser.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Behandlingens lovlighed — offentlig interesse',
            description: 'Behandlingen er nødvendig af hensyn til varetagelsen af en opgave, der udføres i samfundets interesse eller under udøvelse af offentlig myndighed.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Behandlingens lovlighed — legitime interesser',
            description: 'Behandling er nødvendig af hensyn til de legitime interesser, som den dataansvarlige eller en tredjepart forfølger.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Behandling af særlige kategorier (følsomme data)',
            description: 'Behandling af særlige kategorier af personoplysninger (raceoprindelse, politiske holdninger, sundhed osv.) er forbudt, medmindre særlige betingelser i art. 2, er opfyldt.',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Betingelser for samtykke',
            description: 'Den registeransvarlige skal påvise, at der er givet samtykke. Samtykke skal være frit givet, specifikt, informeret og utvetydigt. Tilbagetrækning skal være lige så let som at give samtykke.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Barns samtykke — digitale tjenester',
            description: 'Behandling af personoplysninger om et barn under 16 år (eller lavere alder fastsat af medlemsstaten, ikke under 13) kræver forældres samtykke til informationssamfundstjenester.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Gennemsigtig information og kommunikation',
            description: 'Den registeransvarlige skal give oplysninger vedrørende behandlingen i en kortfattet, gennemsigtig, forståelig og let tilgængelig form.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Information til direkte afhentning',
            description: 'Hvor personoplysninger indsamles fra den registrerede, skal den dataansvarlige give specificerede oplysninger på tidspunktet for indsamlingen.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Oplysninger til indirekte indsamling',
            description: 'Hvis der ikke er indhentet personoplysninger fra den registrerede, skal den dataansvarlige give specificerede oplysninger inden for en rimelig frist.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Adgangsret',
            description: 'Den registrerede har ret til at få bekræftelse på behandlingen og adgang til personoplysningerne og supplerende oplysninger.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Ret til berigtigelse',
            description: 'Den registrerede har ret til at få berigtigelse af unøjagtige personoplysninger uden unødig forsinkelse.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Ret til sletning (retten til at blive glemt)',
            description: 'Den registrerede skal have ret til at få slettet personoplysninger uden unødig forsinkelse, hvis nærmere angivne grunde gør sig gældende.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Ret til begrænsning af behandlingen',
            description: 'Den registrerede har ret til at opnå begrænsning af behandlingen, hvor nærmere angivne betingelser gør sig gældende.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Anmeldelsespligt — berigtigelse/sletning/begrænsning',
            description: 'Den dataansvarlige skal meddele enhver berigtigelse, sletning eller begrænsning til hver modtager, til hvem data er blevet videregivet.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Ret til dataportabilitet',
            description: 'Den registrerede har ret til at modtage sine personoplysninger i et struktureret, almindeligt anvendt, maskinlæsbart format.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Ret til at gøre indsigelse',
            description: 'Den registrerede har ret til at gøre indsigelse mod behandling baseret på offentlig interesse eller legitime interesser, herunder profilering.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Automatiseret individuel beslutningstagning, herunder profilering',
            description: 'Den registrerede har ret til ikke at blive underlagt en afgørelse, der udelukkende er baseret på automatiseret behandling, som har juridiske eller tilsvarende væsentlige virkninger.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Underretning om brud til tilsynsmyndigheden (72 timer)',
            description: 'I tilfælde af et brud på persondatasikkerheden skal den dataansvarlige underrette tilsynsmyndigheden inden for 72 timer efter at have fået kendskab til dette, medmindre det er usandsynligt, at det medfører risiko.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Underretning om brud til registrerede (høj risiko)',
            description: 'Når et brud sandsynligvis vil resultere i en høj risiko for rettigheder og friheder, skal den dataansvarlige meddele bruddet til den registrerede uden unødig forsinkelse.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Svar på anmodninger fra registrerede (30 dage)',
            description: 'Den registeransvarlige skal svare på den registreredes anmodninger uden unødig forsinkelse og inden for en måned efter modtagelsen, som kan forlænges med yderligere to måneder for komplekse anmodninger.',
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Konsekvensvurdering af databeskyttelse',
            description: 'Hvor behandlingen sandsynligvis vil resultere i en høj risiko for rettigheder og friheder, skal den dataansvarlige foretage en DPIA før behandlingen.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Forudgående høring af tilsynsmyndigheden',
            description: 'Den registeransvarlige skal rådføre sig med tilsynsmyndigheden, hvis en DPIA angiver, at behandlingen ville resultere i høj risiko uden afværgeforanstaltninger.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'DPIA obligatorisk behandlingsliste',
            description: 'DPIA er påkrævet til systematisk evaluering/profilering, storstilet behandling af særlige kategorier og systematisk overvågning af offentlige områder.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Optegnelser over behandlingsaktiviteter (controller)',
            description: 'Hver registeransvarlig skal føre en fortegnelse over behandlingsaktiviteter under dens ansvar, herunder formål, kategorier, modtagere, overførsler og opbevaring.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Optegnelser over behandlingsaktiviteter (behandler)',
            description: 'Hver databehandler skal føre en fortegnelse over alle kategorier af behandlingsaktiviteter, der udføres på vegne af en dataansvarlig.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Sikkerhed ved behandling',
            description: 'Den dataansvarlige og databehandleren skal implementere passende tekniske og organisatoriske foranstaltninger for at sikre en passende sikkerhed i forhold til risikoen.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Databeskyttelse ved design og som standard',
            description: 'Den registeransvarlige skal implementere passende foranstaltninger til at sikre, at kun personoplysninger, der er nødvendige for hvert specifikt formål, behandles (dataminimering).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Udpegning af databeskyttelsesansvarlig',
            description: 'Den dataansvarlige og databehandler skal udpege en DPO, når behandlingen udføres af en offentlig myndighed, involverer regelmæssig og systematisk overvågning eller storstilet behandling af særlige kategorier.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'Generelt princip for overførsler',
            description: 'Enhver overførsel af personoplysninger til et tredjeland må kun finde sted, hvis betingelserne i kapitel V er overholdt.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Overførsler baseret på tilstrækkelighedsbeslutning',
            description: 'En overførsel kan finde sted, hvis Kommissionen har besluttet, at tredjelandet sikrer et passende beskyttelsesniveau.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Overførsler er underlagt passende sikkerhedsforanstaltninger',
            description: "Hvis der ikke træffes en beslutning om tilstrækkelighed, kan overførsler finde sted, hvor der er givet passende garantier, herunder SCC'er eller BCR'er.",
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Standard kontraktklausuler',
            description: 'Standardkontraktbestemmelser vedtaget af Kommissionen eller af en tilsynsmyndighed giver passende garantier for internationale overførsler.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Bindende virksomhedsregler',
            description: 'Bindende virksomhedsregler godkendt af en kompetent tilsynsmyndighed giver passende garantier for koncerninterne internationale overførsler.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Undtagelser for specifikke situationer',
            description: 'I mangel af tilstrækkelighed eller garantier kan overførsler finde sted baseret på udtrykkeligt samtykke, kontraktnødvendighed, offentlig interesse, juridiske krav, vitale interesser eller offentligt register.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Ledelsesorgansansvar og uddannelse',
            description: 'Ledelsesorganer for væsentlige og vigtige enheder skal godkende risikostyringsforanstaltninger for cybersikkerhed og gennemgå regelmæssig træning.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Risikostyringsforanstaltninger for cybersikkerhed',
            description: 'Væsentlige og vigtige enheder skal træffe passende og forholdsmæssige tekniske, operationelle og organisatoriske foranstaltninger til at styre cybersikkerhedsrisici.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Hændelseshåndtering politikker og procedurer',
            description: 'Enheder skal implementere politikker for håndtering af hændelser, herunder detektering, analyse, indeslutning og reaktionsprocedurer.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Forretningskontinuitet og krisehåndtering',
            description: 'Enheder skal implementere forretningskontinuitetsforanstaltninger, herunder backup-styring, katastrofe-gendannelse og krisestyring.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Sikkerhed i forsyningskæden',
            description: 'Enheder skal behandle sikkerhedsrelaterede aspekter af forholdet til direkte leverandører og tjenesteudbydere, herunder risikovurderinger af forsyningskæden.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Sikkerhed i netværk og informationssystemer',
            description: 'Enheder skal implementere sikkerhed i forbindelse med anskaffelse, udvikling og vedligeholdelse af netværk og informationssystemer, herunder sårbarhedshåndtering.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Sårbarhedshåndtering og afsløring',
            description: 'Enheder skal implementere politikker og procedurer for koordineret afsløring og håndtering af sårbarheder.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Effektivitet i vurdering af cybersikkerhedsrisiko',
            description: 'Enheder skal implementere politikker og procedurer til at vurdere effektiviteten af risikostyringsforanstaltninger for cybersikkerhed.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Kryptografi og kryptering politikker',
            description: 'Enheder skal implementere politikker og procedurer vedrørende brugen af kryptografi og, hvor det er relevant, kryptering.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'HR-sikkerhed, adgangskontrol, asset management',
            description: 'Enheder skal implementere sikkerhed for menneskelige ressourcer, adgangskontrolpolitikker og aktivstyring.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Multi-faktor autentificering',
            description: 'Enheder skal bruge multifaktorautentificering eller kontinuerlige autentificeringsløsninger, sikret stemme-/video-/tekstkommunikation og sikrede nødkommunikationssystemer.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Tidlig advarsel til CSIRT (24 timer)',
            description: 'Enheder skal indsende en tidlig advarsel til det kompetente CSIRT inden for 24 timer efter, at de er blevet opmærksomme på en væsentlig hændelse.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Hændelsesmeddelelse til CSIRT (72 timer)',
            description: 'Enheder skal indsende en hændelsesmeddelelse til det kompetente CSIRT inden for 72 timer efter, at de er blevet opmærksomme på en væsentlig hændelse.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Endelig rapport til CSIRT (1 måned)',
            description: 'Enheder skal indsende en endelig rapport til det kompetente CSIRT senest en måned efter hændelsesmeddelelsen.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Anvendelighedsbestemmelse for AI aktiviteter',
            description: 'Bestem, om organisationen udfører AI aktiviteter underlagt Vietnam AI-loven, og dokumentér anvendelighedsbeslutningen.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'AI systemrisikoklassificering efter risikoniveau',
            description: 'Klassificer hvert AI system efter risikoniveau (minimalt, begrænset, højt, uacceptabelt) ved hjælp af den guidede klassificeringsmetodologi.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'AI Vedligeholdelse af systemregisterindtastning',
            description: 'Vedligehold et register over AI systemer med systemnavn, ejer, formål, implementeringskontekst, risikoniveau og leverandørinvolvering.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'AI Systemdossier med artefakt-tjekliste og beviser',
            description: 'Byg og vedligehold et systemdossier med påkrævet artefakt-tjekliste, bevisbibliotek og versionshistorik.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'Gennemsigtighedsforpligtelser for AI systemoperatører',
            description: 'Implementer gennemsigtighedsforanstaltninger for AI systemer, herunder brugermeddelelse, offentliggørelse af AI involvering og systemdokumentation.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'AI outputmærkning, vandmærkning og metadatasignering',
            description: 'Implementer mærkning og vandmærkning for AI-genereret indhold, herunder metadatasignering, hvor det er relevant.',
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Oplysningskrav for AI-genereret indhold',
            description: 'Oplys til brugere, når indhold er AI-genereret, og giv brugere mekanismer til at identificere AI involvering.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Forklarlighed og brugervendte gennemsigtighedspligter',
            description: 'Giv forklarlige output for AI beslutninger, der påvirker enkeltpersoner, og vedligehold brugervendt gennemsigtighedsdokumentation.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Menneskelige tilsynsforpligtelser for højrisiko-AI-systemer',
            description: 'Sørg for, at menneskelige tilsynsmekanismer er på plads for højrisiko-AI-systemer, herunder eskalerings- og tilsidesættelsesfunktioner.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'AI hændelsesovervågning og -detektering',
            description: 'Implementer overvågningsfunktioner for at opdage AI systemhændelser, fejlfunktioner og uønskede resultater.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'Alvorlig AI hændelse rapportering til myndigheden',
            description: 'Rapportér alvorlige AI hændelser til den kompetente myndighed inden for den foreskrevne tidslinje (72 timer).',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'AI overholdelse af tidslinje for hændelsesnotifikationer',
            description: 'Overhold meddelelsestidslinjer for AI hændelser, herunder indledende meddelelser, opdateringer og lukningsrapporter.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'AI konsekvensanalyse for højrisikosystemer',
            description: 'Udfør konsekvensanalyser for højrisiko-AI-systemer, der dækker risici, afbødninger, menneskeligt tilsyn og overvågningsplaner.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Kontrolleret test/fastsættelse af sandkasseberettigelse',
            description: 'Bestem berettigelse til kontrolleret testdeltagelse (sandkasse) og dokumenter berettigelsesvurderingen.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Sandbox ansøgning indsendelse og godkendelse sporing',
            description: 'Indsend sandkasseansøgninger til den kompetente myndighed og spor godkendelsesstatus gennem indsendelsens livscyklus.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Sandkasse overvågning checkpoints og færdiggørelse/udgang',
            description: 'Udfør periodiske overvågningskontrolpunkter under sandkassedeltagelse og udarbejde færdiggørelses-/afslutningsrapporter.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Mærkningsfritagelse/undtagelsesdokumentation med begrundelse',
            description: 'Dokumenter eventuelle mærkningsfritagelser eller undtagelser med begrundelse og understøttende dokumentation.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Datalokalisering for vietnamesiske brugere',
            description: 'Data fra vietnamesiske brugere om tjenester leveret i Vietnam skal opbevares inden for Vietnams territorium.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Indberetning af cybersikkerhedshændelser',
            description: 'Cybersikkerhedshændelser skal rapporteres til de kompetente myndigheder rettidigt.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Klassificering af informationssystem',
            description: 'Informationssystemer skal klassificeres efter betydningsniveau, og passende sikkerhedsforanstaltninger skal anvendes.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Risikovurdering af cybersikkerhed',
            description: 'Der skal udføres regelmæssige cybersikkerhedsrisikovurderinger for informationssystemer.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'Bestemmelse inden for rammerne (beslutningstræ)',
            description: 'Organisationer skal afgøre, om de er omfattet af dekret 53 ved hjælp af det foreskrevne beslutningstræ.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Datalokaliseringsvurdering og parathed',
            description: 'Organisationer skal vurdere datalokaliseringskrav og demonstrere parathed.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Tjekliste for opbevaring og opbevaringsberedskab',
            description: 'En tjekliste for lagring og opbevaringsberedskab skal udfyldes for overholdelse af datalokalisering.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Vurdering af lokal tilstedeværelseskrav',
            description: 'Organisationer skal vurdere, om krav til lokal tilstedeværelse gælder og planlægge i overensstemmelse hermed.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Myndighedsanmodning modtagelse og verifikation',
            description: 'Myndighedsanmodninger skal modtages, verificeres og behandles i overensstemmelse med foreskrevne procedurer.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Myndighed anmoder om svar inden for fristen',
            description: 'Svar på myndighedsanmodninger skal gives inden for den fastsatte frist.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Log for håndtering af myndighedsanmodninger',
            description: 'Der skal føres en log over alle myndighedsanmodningshåndteringsaktiviteter.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Anvendelighed og bestemmelse inden for omfanget (konsolideret)',
            description: 'Organisationer skal afgøre, om de er omfattet af den konsoliderede cybersikkerhedslov, der dækker både den gamle lov 24/2018 og dekret 53/2022.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Informationssystemklassificering efter vigtighedsniveau',
            description: 'Informationssystemer skal klassificeres efter vigtighedsniveau (generelt, vigtigt, kritisk) med tilsvarende sikkerhedsforpligtelser.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Cybersikkerhedsstyringsstruktur og ansvarligt personale',
            description: 'Organisationer skal etablere cybersikkerhedsstyringsstrukturer og udpege ansvarligt personale.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Periodisk vurdering af cybersikkerhedsrisiko',
            description: 'Der skal udføres regelmæssige cybersikkerhedsrisikovurderinger for alle klassificerede informationssystemer.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Sikkerhedsforanstaltninger pr. informationssystemklassifikation',
            description: 'Der skal anvendes tekniske sikkerhedsforanstaltninger, der er passende for hvert informationssystemklassifikationsniveau.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Adgangskontrol og identitetsstyring',
            description: 'Adgangskontrol og identitetsstyringsforanstaltninger skal implementeres for informationssystemer.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Logning, overvågning og revisionsspor',
            description: 'Informationssystemer skal vedligeholde lognings-, overvågnings- og revisionssporfunktioner.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Sårbarhedshåndtering og patching',
            description: 'Sårbarhedshåndtering og patching-processer skal etableres og vedligeholdes.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Datalokalisering for vietnamesiske brugerdata',
            description: 'Data fra vietnamesiske brugere om tjenester leveret i Vietnam skal opbevares inden for Vietnams territorium.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Lokal tilstedeværelseskrav for udenlandske tjenesteudbydere',
            description: 'Udenlandske tjenesteudbydere, der opererer i Vietnam, skal opretholde lokal tilstedeværelse som foreskrevet.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Lokal lagerarkitektur og grænseoverskridende flowdokumentation',
            description: 'Lokal lagerarkitektur skal dokumenteres, og grænseoverskridende datastrømme skal kortlægges og kontrolleres.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Detektion og reaktion på cybersikkerhedshændelser',
            description: 'Organisationer skal have kapacitet til at opdage og reagere på cybersikkerhedshændelser.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Obligatorisk hændelsesrapportering til kompetent myndighed',
            description: 'Kvalificerende cybersikkerhedshændelser skal rapporteres til MPS/MIC inden for foreskrevne tidsfrister.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Overholdelse af tidslinje for hændelsesmeddelelse',
            description: 'Hændelsesmeddelelser skal overholde foreskrevne tidslinjekrav (indledende rapport inden for 24 timer, fuldstændig rapport inden for 72 timer).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Myndighedsanmodningsbehandling og samarbejde',
            description: 'Myndighedsanmodninger skal behandles hurtigt, og samarbejde skal ydes i overensstemmelse med foreskrevne procedurer.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Myndighedsanmodningssagsregister og oplysningslog',
            description: 'Alle myndighedsanmodninger og videregivelser skal logges i et sagsregister med fuldt revisionsspor.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Sikkerhedsvurdering af leverandør og tredjepart',
            description: 'Tredjepartsleverandører med adgang til informationssystemer skal gennemgå en sikkerhedsvurdering.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Kontrolelementer for leverandørdataadgang og hændelsesmeddelelser',
            description: 'Adgang til leverandørdata skal dokumenteres, og kontraktlige hændelsesforpligtelser skal etableres.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Sikkerhedsbevidsthedstræning og øvelser',
            description: 'Der skal gennemføres regelmæssig cybersikkerhedsbevidsthedstræning og bordøvelser.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Periodisk pentest og sikkerhedsrevision',
            description: 'Periodiske penetrationstest og sikkerhedsaudit skal udføres på klassificerede informationssystemer.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Vedligeholde digital dataopgørelse',
            description: 'Organisationer skal opretholde en omfattende digital dataopgørelse, der katalogiserer alle dataaktiver.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Dataklassificering efter følsomhed og vigtighed',
            description: 'Data skal klassificeres efter følsomheds- og vigtighedsniveauer som defineret i dataloven.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Definer håndteringsregler efter klassifikationsniveau',
            description: 'Der skal etableres håndteringsregler for hvert dataklassifikationsniveau.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Spor datadeling og offentliggørelse med godkendelser',
            description: 'Alle datadelings- og videregivelsesaktiviteter skal spores med passende godkendelser.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Grænseoverskridende dataoverførselsstyring',
            description: 'Grænseoverskridende dataoverførsler skal overholde styringskrav i henhold til dataloven.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Vedligeholde styringspolitikker og -ansvar',
            description: 'Organisationer skal opretholde datastyringspolitikker og tildele styringsansvar.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Årlig ledelsesattest',
            description: 'En årlig ledelsesattest skal udfyldes for at bekræfte den løbende overholdelse.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Datarisikovurdering',
            description: 'En datarisikovurdering skal udføres for at identificere og afbøde datastyringsrisici.',
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Platformoperatørens ansvar og styringsforpligtelser',
            description: 'Etabler platformsoperatøransvarlighedsstrukturer, herunder styringspolitikker, overvågningsansvar og håndhævelsesforpligtelser.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Platformregler offentliggørelse og gennemsigtighed af information',
            description: 'Offentliggør platformsregler, handelsbetingelser og operationelle oplysninger på en gennemsigtig og tilgængelig måde.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'E-handel salgshjemmeside/app notifikation til MOIT',
            description: 'Indsend meddelelse til MOIT for e-handelssalgswebsteder og -applikationer via Online.gov.vn eller udpegede kanaler.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'E-handelsserviceplatform/markedspladsregistrering hos MOIT',
            description: 'Registrer e-handelstjenesteplatforme og markedspladser med MOIT, inklusive indsendelse af dossierer og godkendelsessporing.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Vedligeholdelse af sælgerregister (indenlandsk og udenlandsk)',
            description: 'Oprethold et register over alle sælgere på platformen inklusive indenlandske og udenlandske sælgere med onboarding, verifikation og livscyklussporing.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Livestream sælgerregister og overholdelseskontrol',
            description: 'Oprethold et register over livestream-sælgere med identitetsbekræftelse, aktivitetssporing og overholdelseskontrol.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Affiliate- og marketingpartnerregister',
            description: 'Vedligehold et register over affiliate marketingfolk og henvisningspartnere med kontraktstatus, indholdsretningslinjer og overholdelsesbekræftelser.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Produktliste og håndhævelse af forbudte/betingede varer',
            description: 'Implementer kontrol med produktliste, herunder screening, overvågning og håndhævelse af forbudte og betingede varer.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Forbrugerklager registreres med indtag, handlinger og revisionsspor',
            description: 'Vedligehold et klageregister med indtagsregistreringer, undersøgelseshandlinger, løsningsresultater og komplet revisionsspor.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Klagebehandlingsprocedure og tidsfrister for løsning',
            description: 'Etabler klagebehandlingsprocedurer med definerede tidslinjer for bekræftelse, undersøgelse og løsning.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Moderering af indhold, overvågning og håndhævelse af fjernelse',
            description: 'Implementer procedurer for moderering, overvågning og fjernelse af indhold for ulovligt, forbudt eller krænkende indhold.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Nedtagnings- og håndhævelseshandlinger registreres med bevisbevaring',
            description: 'Vedligeholde et register over fjernelses- og håndhævelseshandlinger med beslutningsregistre, bevisbevaring og sælgermeddelelser.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Platformpolitik og oplysningsregister (versioner, udgivelsesdatoer, accept)',
            description: 'Vedligehold et register over platformspolitikker og offentliggørelser med versionshistorik, udgivelsesdatoer og acceptbeviser.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Forbrugeroplysningspligter - nøjagtig produktinformation og anti-bedrag',
            description: 'Sørg for nøjagtige, klare produkt- og serviceoplysninger og implementer foranstaltninger for at forhindre vildledende praksis på platformen.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Udenlandske platforms driftsforhold og registrering/meddelelse',
            description: 'Overhold driftsbetingelserne for udenlandske platforme, der betjener vietnamesiske brugere, inklusive registrerings- eller underretningskrav.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Lokal repræsentation eller autorisation for udenlandske platforme',
            description: 'Udpeg en lokal repræsentant eller autoriseret enhed i Vietnam for udenlandske platforme som krævet ved lov.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Grænseoverskridende sælger- og reklamationskoordinering',
            description: 'Koordinere grænseoverskridende sælgerhåndtering og klageløsning inklusive eskalering og bevishåndtering.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Statslig e-handelsstyringssystem interaktion og rapporteringsparathed',
            description: 'Forbered dig på interaktion med statens e-handelsstyringssystem, herunder datafeltkortlægning og rapporteringskapacitet.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Meddelelse om væsentlig ændring efter godkendelse',
            description: 'Indsend meddelelser om væsentlige ændringer af platformsdetaljer, politikker eller operationer efter den første godkendelse.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Opsigelse og deaktiveringsmeddelelse',
            description: 'Indsend meddelelser om opsigelse eller deaktivering, når du ophører med e-handel, herunder offentlig meddelelse og håndtering af forbrugerbeskyttelse.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Tillidsmærke og troværdighedsvurderingsprograms berettigelse og vedligeholdelse',
            description: 'Vurder berettigelse til trustmark- og troværdighedsvurderingsprogrammer og vedligehold overholdelsesbevis for løbende deltagelse.',
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Samtykke til behandling af personoplysninger',
            description: 'Behandling af personoplysninger kræver samtykke fra den registrerede, medmindre der gælder en dispensation.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Adgangsret',
            description: 'Registrerede har ret til at få adgang til deres personoplysninger, som den dataansvarlige opbevarer.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Ret til berigtigelse',
            description: 'Registrerede har ret til at anmode om berigtigelse af unøjagtige personoplysninger.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Ret til sletning',
            description: 'Registrerede har ret til at anmode om sletning af deres personoplysninger.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Ret til at begrænse behandlingen',
            description: 'Registrerede har ret til at anmode om begrænsning af behandlingen af deres personoplysninger.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Ret til at gøre indsigelse',
            description: 'Registrerede har ret til at gøre indsigelse mod behandlingen af deres personoplysninger.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Ret til at trække samtykke tilbage',
            description: 'Registrerede har til enhver tid ret til at trække tidligere givet samtykke tilbage.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Underretning om brud til myndighed (72 timer)',
            description: 'Dataansvarlig skal underrette Ministeriet for Offentlig Sikkerhed inden for 72 timer efter opdagelsen af et brud på persondatasikkerheden.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Underretning om brud til registrerede',
            description: 'Dataansvarlig skal uden unødig forsinkelse underrette berørte registrerede om et brud på persondatasikkerheden.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA til højrisikobehandling',
            description: 'En databeskyttelseskonsekvensvurdering skal udføres for behandlingsaktiviteter, der sandsynligvis vil resultere i høj risiko.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Vurdering af grænseoverskridende overførsler',
            description: 'Der skal gennemføres en konsekvensanalyse, før der overføres personoplysninger uden for Vietnam.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Registrering af behandlingsaktiviteter',
            description: 'Dataansvarlige skal føre et register over behandlingsaktiviteter (RoPA), der dokumenterer alle behandlingsaktiviteter.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Databehandleraftaler',
            description: 'Der skal føres registre over databehandleraftaler med databehandlere og underdatabehandlere.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Grænseoverskridende overførselsgarantier',
            description: 'Persondataoverførsler uden for Vietnam kræver passende sikkerhedsforanstaltninger og dokumenterede vurderinger.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Kvittering inden for 2 hverdage',
            description: 'Anmodninger fra den registrerede skal bekræftes inden for 2 arbejdsdage efter modtagelsen.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Færdig inden for 15 arbejdsdage',
            description: 'Anmodninger fra den registrerede skal udfyldes inden for 15 arbejdsdage med en eventuel forlængelse på 5 arbejdsdage.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'MoPS konsekvensvurderingsdossier',
            description: 'Organisationer, der udfører højrisikobehandling, skal indgive et konsekvensanalysedossier til Ministeriet for Offentlig Sikkerhed efter den foreskrevne administrative procedure.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'MoPS grænseoverskridende overførselsdossier',
            description: 'Organisationer, der overfører personoplysninger uden for Vietnam, skal indgive en grænseoverskridende overførselskonsekvensvurderingsdossier til ministeriet for offentlig sikkerhed.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Behandler serviceudbydercertificering',
            description: 'Organisationer, der leverer persondatabehandling som en service, skal indhente et servicebehandlercertifikat fra Ministeriet for Offentlig Sikkerhed.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Behandling af konsekvensanalysedossier, når triggere er opfyldt',
            description: 'Der skal indgives et dossier til behandling af konsekvensanalyse, når de foreskrevne udløsere er opfyldt.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Indvirkningsdossier for grænseoverskridende overførsler',
            description: 'Et indvirkningsdossier for grænseoverskridende overførsel skal udarbejdes og arkiveres til internationale dataoverførsler.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Udpegning af databeskyttelsesansvarlig',
            description: 'En databeskyttelsesansvarlig skal udpeges som krævet i dekret 13.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Governance-artefakter (politikker, roller, procedurer)',
            description: 'Governance-artefakter, herunder politikker, roller og procedurer, skal vedligeholdes.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Bevispakkens fuldstændighed og uforanderligt revisionsspor',
            description: 'Bevispakker skal være komplette, og et uforanderligt revisionsspor skal opretholdes.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Understøttelse af arbejdsgange til arkivering',
            description: 'Sagsarbejdsgange skal understøttes til konsekvensanalyse og grænseoverskridende sager.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Behandler kontraktlige krav',
            description: 'Der skal etableres og overvåges kontraktlige krav til databehandlere.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Årlig compliance review',
            description: 'En årlig overholdelsesgennemgang skal udføres for at verificere den løbende overholdelse.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Myndighedsarkivering af indsendelsesoptegnelser',
            description: 'Optegnelser over alle myndighedsindgivelser skal vedligeholdes.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Lovligt grundlag for behandling af personoplysninger',
            description: 'Behandling af personoplysninger skal være baseret på lovlige grundlag som defineret af PDPL.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Registrerede rettigheder (adgang, berigtigelse, sletning, begrænsning, objekt, portabilitet)',
            description: 'Registrerede har omfattende rettigheder, herunder adgang, berigtigelse, sletning, begrænsning, indsigelse og portabilitet.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Overtrædelsesmeddelelse til myndighed',
            description: 'Dataansvarlige skal underrette den kompetente myndighed om brud på persondatasikkerheden inden for den foreskrevne tidsramme.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Underretning om brud til registrerede',
            description: 'Dataansvarlige skal underrette berørte registrerede om brud på persondatasikkerheden.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Konsekvensvurdering af databeskyttelse',
            description: 'En databeskyttelseskonsekvensvurdering skal udføres for højrisikobehandlingsaktiviteter.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Grænseoverskridende overførselsstyring',
            description: 'Grænseoverskridende overførsler af personoplysninger skal overholde styringskravene.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Udpegning af databeskyttelsesansvarlig',
            description: 'Organisationer skal udpege en databeskyttelsesansvarlig efter behov.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Registrering af behandlingsaktiviteter',
            description: 'Organisationer skal føre optegnelser over alle behandlingsaktiviteter.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Databehandlerens kontraktlige forpligtelser',
            description: 'Der skal etableres kontraktlige forpligtelser med databehandlere.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Indlevering af konsekvensanalysedossier',
            description: 'Konsekvensanalysedossierer skal indgives til den kompetente myndighed som foreskrevet.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Indvirkningsdossier for grænseoverskridende overførsler',
            description: 'Et indvirkningsdossier for grænseoverskridende overførsel skal udarbejdes og arkiveres til internationale dataoverførsler.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'DPO aftale og meddelelse',
            description: 'Der skal udpeges en databeskyttelsesansvarlig og myndigheden underrettes.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Governance politikker og procedurer',
            description: 'Governance-politikker og -procedurer skal etableres og vedligeholdes.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Bevispakke og uforanderligt revisionsspor',
            description: 'Bevispakker skal være komplette, og et uforanderligt revisionsspor skal opretholdes.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Indleveringssags livscyklus',
            description: 'Sagens livscyklus skal styres gennem foreskrevne faser.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Behandler kontraktlige krav',
            description: 'Der skal etableres og overvåges kontraktlige krav til databehandlere.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Certificering af tjenesteydere',
            description: 'Organisationer, der leverer databehandlingstjenester, skal opnå certificering.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Myndighedsarkivering af indsendelsesoptegnelser',
            description: 'Optegnelser over alle myndighedsindgivelser skal vedligeholdes.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Årlig compliance review',
            description: 'En årlig overholdelsesgennemgang skal udføres for at verificere den løbende overholdelse.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Tekniske datasikkerhedsforanstaltninger',
            description: 'Tekniske foranstaltninger til datasikkerhed skal implementeres og vedligeholdes.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Dekret 356 officielle Mẫu số formkrav',
            description: 'De 13 officielle Mẫu số administrative procedureformularer (Mẫu số 01a–10) foreskrevet ved dekret 356/2025/NĐ-CPĐ-CP skal bruges til de tilsvarende indleveringer til Ministeriet for Offentlig Sikkerhed. Phụ lục I–VIII-emnerne er interne datastrukturkortlægninger, ikke officielle reguleringsformer.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'DPIA indsendelse af dossier til A05',
            description: 'Organisationer skal indsende DPIA-dossierer til A05/MPS for behandlingsaktiviteter, der involverer følsomme personoplysninger eller storstilet behandling.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Indsendelse af dossier for grænseoverskridende overførsel',
            description: 'Organisationer skal indsende grænseoverskridende dataoverførselsdossierer til A05/MPS gennemgang og godkendelse.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Behandler ansøgning om servicecertifikat',
            description: 'Udbydere af databehandlingstjenester skal ansøge om certifikater fra A05/MPS.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Underretning om behandling af personoplysninger',
            description: 'Dataansvarlige skal underrette A05/MPS om persondatabehandlingsaktiviteter ved hjælp af foreskrevne formularer (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Årlig situationsrapportering',
            description: 'Dataansvarlige skal indsende årlige situationsrapporter for behandling af persondata (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'Brugeroplysninger fortrolighed som standard',
            description: 'Brugeroplysninger skal som standard behandles som fortrolige.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Minimumskontrol af organisatorisk og teknisk beskyttelse',
            description: 'Der skal implementeres minimale organisatoriske og tekniske beskyttelseskontroller.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Offentliggørelse kun med korrekt grundlag',
            description: 'Videregivelse af brugeroplysninger er kun tilladt med et ordentligt retsgrundlag.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Obligatorisk oplysningslogning og auditabilitet',
            description: 'Alle oplysninger skal logges og kan revideres.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Intern godkendelsesworkflow for offentliggørelse',
            description: 'Der skal etableres en intern godkendelsesworkflow for alle oplysninger.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Behandling af svar fra myndighedsanmodninger',
            description: 'Myndighedsanmodninger skal behandles og besvares i overensstemmelse med foreskrevne procedurer.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'Den registrerede har ret til at blive informeret om persondatabehandlingsaktiviteter.',
        consent: 'Den registrerede har ret til at give samtykke til behandling af deres personoplysninger.',
        refuse_consent: 'Den registrerede har ret til ikke at give samtykke til behandling af deres personoplysninger.',
        withdraw_consent: 'Den registrerede har ret til at anmode om tilbagetrækning af tidligere givet samtykke.',
        access: 'Den registrerede har ret til at se og få adgang til deres personoplysninger, som den dataansvarlige opbevarer.',
        rectification: 'Den registrerede har ret til at rette eller anmode om berigtigelse af unøjagtige personoplysninger.',
        data_provision: 'Den registrerede har ret til at anmode om udlevering af sine personlige data (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'Den registrerede har ret til at anmode om sletning eller destruktion af personoplysninger med forbehold af lovens undtagelser.',
        restriction_of_processing: 'Den registrerede har ret til at anmode om begrænsning af behandlingen af sine personoplysninger.',
        object_to_processing: 'Den registrerede har ret til at sende en anmodning, der gør indsigelse mod behandlingen af deres personoplysninger.',
        complaint: 'Den registrerede har ret til at indgive en klage (khiếu nại) vedrørende behandling af personoplysninger.',
        denunciation: 'Den registrerede har ret til at foretage en opsigelse (tố cáo) vedrørende krænkelser af persondatabeskyttelsen.',
        lawsuit: 'Den registrerede har ret til at anlægge sag (khởi kiện) for krænkelser af deres databeskyttelsesrettigheder.',
        claim_damages: 'Den registrerede har ret til at kræve erstatning for skade forårsaget af krænkelser af persondatabeskyttelsen.',
        request_protection_measures: 'Den registrerede har ret til at anmode kompetente myndigheder eller relevante organisationer/personer om at implementere foranstaltninger til beskyttelse af deres personoplysninger.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Risikostyring og overvågning (udkast)',
            steps: {
                '1': 'Identificer risikokategorier',
                '2': 'Oprethold tidlig advarselskontrol',
                '3': 'Gennemgå indsamling, opbevaring, behandling, deling og transaktionsrisici',
                '4': 'Registrer risici for privatliv, cybersikkerhed og adgangsstyring',
                '5': 'Udløs afhjælpende handlinger',
                '6': 'Bevar anmeldelseshistorikken',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Klager og tvister (udkast)',
            steps: {
                '1': 'Modtag klage/tvistindtag',
                '2': 'Tjek fuldstændigheden af de nødvendige oplysninger; anmod om tillæg inden for 24 timer, hvis ufuldstændig',
                '3': 'Inden 3 arbejdsdage efter at have modtaget en fuldstændig anmodning, underrette parterne om håndteringstilgang og forventet tidslinje',
                '4': 'Anmod om svar og understøttende dokumenter',
                '5': 'Vurder tvistfakta og platformsansvar',
                '6': 'Rekordopløsningsresultat',
                '7': 'Bevar beviser og afslut sag',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Levering og attest af ejendomsrettigheder over data (udkast)',
            steps: {
                '1': 'Levere produkt/ydelse efter kontraktmetode',
                '2': 'Registrer leveringsbegivenhed og tidspunkt',
                '3': 'Generer certifikat for ejendomsrettigheder over data',
                '4': 'Registrer transaktionsgennemførelse',
                '5': 'Aktiver senere opslag/bekræftelse, hvor det er nødvendigt',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Transaktionsinitiering og kontraktindgåelse (udkast)',
            steps: {
                '1': 'Gennemgå notering og evalueringsresultat',
                '2': 'Start købs-, forhandlings- eller auktionsarbejdsgang',
                '3': 'Opret elektronisk kontrakt',
                '4': 'Sikre begge parters accept',
                '5': 'Registrer betalingsgennemførelse',
                '6': 'Tillad levering',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Adgang til sandkasse/testmiljø (udkast)',
            steps: {
                '1': 'Indsend sandkasseanmodning',
                '2': 'Definer omfang, tilladte data, tidsperiode og behandlingstilladelser',
                '3': 'Tilvejebringelse af isoleret teknisk miljø',
                '4': 'Overvåg sikkerhed og adgang under test',
                '5': 'Gennemgå output før ekstraktion',
                '6': 'Godkend kun udvinding, hvor det er tilladt',
                '7': 'Luk testsession og ryd op i miljøet',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Dataprodukt-/tjenesteliste (udkast)',
            steps: {
                '1': 'Opret noteringsapplikation',
                '2': 'Indfang kilde og ejerskab / brugsretlige beviser',
                '3': 'Registrer tekniske karakteristika, format, API detaljer og begrænsninger',
                '4': 'Oplysninger om rekordkvalitet',
                '5': 'Indsend noteringsdossier til platformgennemgang',
                '6': 'Platform udfører fuldstændighed, lovlighed og tekniske kontroller',
                '7': 'Godkend eller afvis opslag',
                '8': 'Hvis det er godkendt, offentliggøres listen med vedhæftet evalueringsresultat',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Deltager onboarding (udkast)',
            steps: {
                '1': 'Bestem deltagertype: sælger eller køber',
                '2': 'Bekræft juridisk status og retsevne',
                '3': 'Bekræft yderligere betingelser, hvis betingede forretningsområder er gældende',
                '4': 'Registrer transaktionskonto',
                '5': 'Link betalingskonto',
                '6': 'Godkend platformdeltagelse',
                '7': 'Opbevar deltagerregistrering og revisionsspor',
            },
        },
        AI_A1: {
            name: 'AI Systemregistrering/meddelelse',
            steps: {
                '1': 'Anvendelseskontrol og AI systemklassificering',
                '2': 'Byg registrerings-/meddelelsesdossier',
                '3': 'Intern godkendelse',
                '4': 'Send via kanal',
                '5': 'Ændringsløkke — hvis returneret, opdater og indsend igen',
                '6': 'Luk og gem bekræftelse',
            },
        },
        AI_A2: {
            name: 'AI Arbejdsgang for risikoklassificering',
            steps: {
                '1': 'Opret AI System Register-indgang',
                '2': 'Kør klassificeringsguiden',
                '3': 'Vedhæft beviser',
                '4': 'Godkend klassificering',
                '5': 'Link output til systemdossier',
            },
        },
        AI_A3: {
            name: 'Overholdelse af mærkning og offentliggørelse',
            steps: {
                '1': 'Identificer mærkningsforpligtelser efter system og use case',
                '2': 'Implementer mærkningsmeddelelser',
                '3': 'Indfang beviser - skærmbilleder, UI-kopi, tekniske metadata',
                '4': 'Håndtere dispensationer og registrer begrundelse',
                '5': 'Godkend og version',
            },
        },
        AI_A4: {
            name: 'Indberetning af alvorlige hændelser',
            steps: {
                '1': 'Opret AI Incident Record',
                '2': 'Klassificer sværhedsgrad',
                '3': 'Udfør indeslutningshandlinger og bevisfangst',
                '4': 'Eksport af skabelon for alvorlig hændelsesrapport',
                '5': 'Indsend til myndighed via kanal',
                '6': 'Spor myndighedernes svar og lukning',
            },
        },
        AI_A5: {
            name: 'AI Konsekvensanalyse',
            steps: {
                '1': 'Opret vurderingsjournal',
                '2': 'Udfyld påkrævede sektioner',
                '3': 'Vedhæft beviser — datastrømme, modeldokumentation, kontroller',
                '4': 'Intern godkendelse',
                '5': 'Eksporter rapport og opbevar i dossier',
            },
        },
        AI_A6: {
            name: 'Controlled Testing / Sandbox Deltagelse',
            steps: {
                '1': 'Fuldfør berettigelses- og beredskabsrekord',
                '2': 'Forbered ansøgning og eksport',
                '3': 'Send via kanal',
                '4': 'Spor godkendelse',
                '5': 'Udfør overvågningskontrolpunkter og logs',
                '6': 'Komplet færdiggørelsesrapport og lukkepakke',
            },
        },
        CYBERSEC_C1: {
            name: 'Sikkerhedsvurdering af kritisk informationssystem',
            steps: {
                '1': 'Klassificer informationssystemer i henhold til nationale cybersikkerhedskrav',
                '2': 'Udfør sikkerhedsvurdering og risikoevaluering for klassificerede systemer',
                '3': 'Implementer nødvendige sikkerhedsforanstaltninger og baseline kontroller',
                '4': 'Dokumenter bevis for overholdelse og planlæg periodiske gennemgange',
            },
        },
        CYBERSEC_C2: {
            name: 'Underretning om cybersikkerhedshændelser',
            steps: {
                '1': 'Opdag, klassificer og vurder cybersikkerhedshændelsen',
                '2': 'Udfør øjeblikkelige reaktionshandlinger og bevaring af beviser',
                '3': 'Rapportere til myndighed og deltage i national hændelse-respons koordinering',
                '4': 'Udfør gennemgang efter hændelsen og dokumenter erfaringer',
            },
        },
        CYBERSEC_C3: {
            name: 'Verifikation af overholdelse af datalokalisering',
            steps: {
                '1': 'Vurder, om datalokaliseringsudløsere gælder baseret på tjenestetype og datakategorier',
                '2': 'Bekræft, at lokal lagerarkitektur og grænseoverskridende flowkontrol er på plads',
                '3': 'Vurder lokale tilstedeværelseskrav (filial / repræsentationskontor), hvor det er relevant',
                '4': 'Dokumenter overholdelsesbevis og vedligehold til myndighedsgennemgang',
            },
        },
        CYBERSEC_C4: {
            name: 'Myndighedssamarbejdsanmodningssvar',
            steps: {
                '1': 'Modtag og verificer legitimiteten og omfanget af myndighedsanmodningen',
                '2': 'Indsamle og forberede de ønskede oplysninger eller udfør nødvendige handlinger',
                '3': 'Send svar til myndigheden inden for den krævede tidsplan',
                '4': 'Log afsløringen og luk sagen',
            },
        },
        CYBERSEC_C5: {
            name: 'Verifikation af cybersikkerhedspersonalets kvalifikation',
            steps: {
                '1': 'Vurder leverandørens sikkerhedsposition ved hjælp af cybersikkerhedsvurderingsramme',
                '2': 'Bekræft leverandørdataadgangsomfang og registrer i leverandørdataadgangslog',
                '3': 'Gennemgå leverandørhændelsesklausul og kontraktlige krav til cybersikkerhed',
                '4': 'Planlæg periodiske leverandørsikkerhedsgennemgange og overvåg overholdelse',
            },
        },
        DATA_D1: {
            name: 'Dataopgørelse og klassifikationsopsætning',
            steps: {
                '1': 'Katalogisere alle dataaktiver med typer og kilder i dataopgørelsesregister',
                '2': 'Klassificer hvert aktiv efter følsomhedsniveau',
                '3': 'Definer håndteringsregler pr. klassifikationsniveau',
                '4': 'Indfang beviser - skærmbilleder, arkitekturdiagrammer, adgangskontrol',
                '5': 'Planlæg periodiske gennemgange',
            },
        },
        DATA_D2: {
            name: 'Datadelingsstyring',
            steps: {
                '1': 'Evaluer nye delingsordninger via risikovurdering for datadeling',
                '2': 'Fuldfør intern godkendelsesworkflow',
                '3': 'Registrer delingshændelse i datadelings-/oplysningslog',
                '4': 'Indhent beviser — godkendelsesdokumenter, kontraktlige dokumenter',
                '5': 'Periodisk gennemgang af deleordninger',
            },
        },
        DATA_D3: {
            name: 'Vurdering af grænseoverskridende dataoverførsel',
            steps: {
                '1': 'Identificer grænseoverskridende overførsler i dataopgørelse',
                '2': 'Udfør overførselsrisikovurdering',
                '3': 'Anvend sikkerhedsforanstaltninger og styringsforanstaltninger',
                '4': 'Dokument i grænseoverskridende del af godkendelsesattest',
                '5': 'Eksportvurderingspakke til revision',
            },
        },
        DATA_D4: {
            name: 'Årlig ledelsesattest',
            steps: {
                '1': 'Gennemgå governance RACI matrix',
                '2': 'Bekræft politikstatus (styring, klassificering, deling, fastholdelse)',
                '3': 'Komplet attestpakke',
                '4': 'Få intern godkendelse',
                '5': 'Eksport og arkiveringsattest',
            },
        },
        DATA_D5: {
            name: 'Håndtering af datahændelser',
            steps: {
                '1': 'Klassificer hændelsen ved hjælp af beslutningsjournal',
                '2': 'Udfør hændelsessvar pr. playbook',
                '3': 'Indfang beviser - logfiler, skærmbilleder, kommunikation',
                '4': 'Udfør gennemgang efter hændelsen',
                '5': 'Hvis myndighedsmeddelelse kræves: Opret indsendelsessag og indsend til MIC',
                '6': 'Luk og arkivér',
            },
        },
        ECOM_P1: {
            name: 'Meddelelse (salgswebsted/app)',
            steps: {
                '1': 'Bestem klassificering: salgssite/app vs markedsplads/serviceplatform',
                '2': 'Opret intern dossierpost med virksomhedsidentitet, websted/app-info og påkrævede oplysninger',
                '3': "Generer bevispakke - skærmbilleder af afsløringer, politik-URL'er, versionshistorik",
                '4': 'Send via Online.gov.vn - upload dokumenter, optag tidsstempel og bekræftelse',
                '5': 'Spor behandling – hvis den returneres til ændring, skal du registrere årsagerne og sende dem igen',
                '6': 'Luk — butiksbekræftelse, link beviser og revisionsspor',
            },
        },
        ECOM_P2: {
            name: 'Registrering (serviceplatforme/markedspladser)',
            steps: {
                '1': 'Bestem platformsmodel og forpligtelser - markedsplads vs blandet vs social handel',
                '2': "Byg registreringsdossier — ansøgningsskema, tjenesteydelsesordning, T&C'er, platformsregler",
                '3': 'Interne godkendelser — tildel juridisk/compliance-ejer, godkend til indsendelse',
                '4': 'Send via Online.gov.vn — upload dossier, optag tidsstempel',
                '5': 'Behandlingssløjfe — hvis returneret til ændring, opdater og genindsend',
                '6': 'Godkendelse og operationalisering — butiksbekræftelse, vedligehold løbende overholdelsesregistre',
            },
        },
        ECOM_P3: {
            name: 'Opdatering/ændringsmeddelelse',
            steps: {
                '1': 'Identificer ændringstrigger — væsentlig ændring af dossierelementer',
                '2': 'Generer ændringspakke — ændringsoversigt, opdaterede dokumenter, bevisopdateringer',
                '3': 'Indsend opdatering og registrer indsendelsesstatus',
                '4': 'Spor behandling og luk',
            },
        },
        ECOM_P4: {
            name: 'Opsigelse / deaktivering',
            steps: {
                '1': 'Forbered opsigelsespakke - ophørsdato, årsag, håndteringsplan for forbrugerbeskyttelse, bevis for offentlig meddelelse',
                '2': 'Send opsigelse — optag tidsstempel og bekræftelse',
                '3': 'Luk — gem endelig status og beviser',
            },
        },
        ECOM_P5: {
            name: 'Trustmark / Troværdighedsvurderingsprogram',
            steps: {
                '1': 'Gennemgang af berettigelse og beredskab',
                '2': 'Forbered dossier ved hjælp af officiel formular',
                '3': 'Send og spor',
                '4': 'Oprethold løbende overholdelsesbeviser',
            },
        },
        PDPL_P1: {
            name: 'DPIA Indsendelsesarkivering til MPS',
            steps: {
                '1': 'Udfyld DPIA ved hjælp af dekret 356 formularvej (Mẫu số 02a/02b)',
                '2': 'Saml omslagsdokumenter og dokumentation',
                '5': 'Opbevar dossier og bevis for indsendelse til revisionsspor',
            },
        },
        PDPL_P2: {
            name: 'Ansøgning om grænseoverskridende overførsel',
            steps: {
                '1': 'Udfyld grænseoverskridende overførselsdossier ved hjælp af dekret 356-formularer (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Ansøgning om databehandlingstjenestecertifikat',
            steps: {
                '1': 'Forbered certificeringsansøgningsdossier i henhold til dekret 356 krav',
                '2': 'Indsend ansøgning til MPS med påkrævede understøttende dokumenter',
                '3': 'Svar på MPS anmeldelseskommentarer og supplerende anmodninger',
            },
        },
        PDPL_P4: {
            name: 'Behandling af anmodninger om datasubjektrettigheder',
            steps: {
                '1': 'Modtag og log anmodning; verificere den registreredes identitet inden for 2 arbejdsdage',
                '2': 'Vurder anmodningstype og bestem gældende frist (10/15/20 dage efter type)',
                '3': 'Udfør opfyldelseshandlinger på tværs af relevante systemer og afdelinger',
                '4': 'Forbered og send svar til den registrerede med resultat og begrundelse',
                '5': 'Luk sagen og gem bevis til revision; dokumentere enhver begrundelse for afslag',
            },
        },
        PDPL_P5: {
            name: 'Underretning om brud til MPS',
            steps: {
                '1': 'Opdag og vurder bruddet; fastlæg om underretningspligten gælder, og udled den gældende underretningsfrist fra de installerede fristposter',
                '2': 'Forbered notifikationspakke i henhold til PDPL krav',
                '3': 'Send meddelelse til MPS inden for den underretningsfrist, der følger af de installerede fristposter, regnet fra opdagelsen',
                '4': 'Svar på MPS opfølgningsanmodninger; indsende tillæg og koordineringsbevis',
            },
        },
        TELECOM_T1: {
            name: 'Håndtering af myndighedsanmodninger',
            steps: {
                '1': 'Modtag og log myndighedsanmodningen via indtagsjournal',
                '2': 'Bekræft myndighedsidentitet, retsgrundlag og anmodningens omfang',
                '3': 'Foretag intern juridisk gennemgang og opnå ledelsesgodkendelse',
                '4': 'Forbered svarpakke med samlet bevis',
                '5': 'Udarbejde og godkende svarbrev',
                '6': 'Opret indsendelsessag og indsend svar via kanal',
                '7': 'Logafsløring og sporingsmyndighedsbekræftelse',
                '8': 'Luk sagen og gem bevis til revisionsspor',
            },
        },
        TELECOM_T2: {
            name: 'Disclosure Management',
            steps: {
                '1': 'Modtag anmodning om offentliggørelse (brugersamtykke, juridisk forpligtelse eller myndighedsanmodning)',
                '2': 'Fuldfør arbejdsgang for godkendelse af offentliggørelse',
                '3': 'Udfør offentliggørelse og registrer i logregister',
                '4': 'Indhent beviser — godkendelsesregistre, kommunikationsregistre',
                '5': 'Periodisk gennemgang af offentliggørelseslog',
            },
        },
        TELECOM_T3: {
            name: 'Opsætning af fortrolighedspolitik og adgangskontrol',
            steps: {
                '1': 'Udarbejde og godkende fortrolighedspolitik',
                '2': 'Udarbejd og godkend adgangskontrol- og logningspolitik',
                '3': 'Dokumentbehandlingsaktiviteter i brugerinformationsbehandlingsregister',
                '4': 'Indhent beviser — politiske dokumenter, implementeringsbeviser',
                '5': 'Planlæg periodiske gennemgange',
            },
        },
        TELECOM_T4: {
            name: 'Telecom Incident Assessment',
            steps: {
                '1': 'Klassificere og vurdere hændelsen',
                '2': 'Bestem underretningskrav',
                '3': 'Hvis myndighedsmeddelelse kræves: Opret indsendelsessag',
                '4': 'Indfang og bevar beviser',
                '5': 'Opløsning og lukning',
            },
        },
        TELECOM_T5: {
            name: 'Håndtering af opbevaring og sletning',
            steps: {
                '1': 'Definer tidsplan for opbevaring og sletning pr. datakategori',
                '2': 'Implementer sletningsprocedurer',
                '3': 'Indhent beviser — sletning af optegnelser',
                '4': 'Periodisk gennemgang af tidsplaner',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Registrering / Anmeldelse / Administrativt beredskab',
            'ECOM-FINE-B': 'Klager og forbrugerbeskyttelse',
            'ECOM-FINE-C': 'Overvågning og håndhævelse af fjernelse',
            'ECOM-FINE-D': 'Grænseoverskridende operationer',
            'ECOM-FINE-E': 'Platformregler og gennemsigtighed',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Håndtering af myndighedsanmodninger',
            'TELECOM-FINE-B': 'Fortrolighed og uautoriseret offentliggørelse',
            'TELECOM-FINE-C': 'Adgangskontrol og logføringsfejl',
            'TELECOM-FINE-D': 'Overtrædelser af opbevaring og sletning',
            'TELECOM-FINE-E': 'Fejl ved vurdering af telekomhændelser',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Op til 100 millioner VND',
                fine_org: 'Op til 200 millioner VND',
                deadlines: 'Straks efter opdagelse',
                remediation: [
                    'Obligatorisk hændelsesklassificering',
                    'Obligatoriske registreringer af alvorlighed og beslutninger',
                    'Obligatorisk bevaring af beviser ved fortrolighedshændelser',
                    'Obligatorisk afslutning efter hændelsen',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Op til 100 millioner VND',
                fine_org: 'Op til 200 millioner VND',
                deadlines: 'Løbende forpligtelse',
                remediation: [
                    'Obligatorisk implementering af opbevaringsplan',
                    'Obligatorisk bevaring af dokumentation for sletning',
                    'Obligatoriske beslutningsregistreringer for undtagelser fra opbevaring',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Op til 100 millioner VND',
                fine_org: 'Op til 200 millioner VND',
                deadlines: 'Løbende forpligtelse',
                remediation: [
                    'Obligatorisk implementering af politik for adgangskontrol',
                    'Obligatorisk logning af adgangshændelser',
                    'Obligatorisk gennemgang af adgangsrettigheder',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Op til 100 millioner VND',
                fine_org: 'Op til 200 millioner VND',
                deadlines: 'Løbende forpligtelse',
                remediation: [
                    'Obligatorisk implementering af fortrolighedspolitik',
                    'Obligatorisk workflow for godkendelse af videregivelse',
                    'Obligatorisk vedligeholdelse af spor for videregivelse',
                    'Obligatorisk adgangskontrol til brugeroplysninger',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Op til 100 millioner VND',
                fine_org: 'Op til 200 millioner VND',
                deadlines: 'Som angivet i myndighedsanmodningen',
                remediation: [
                    'Obligatorisk modtagelse og verifikation af myndighedsanmodning',
                    'Obligatorisk svar inden for den fastsatte frist',
                    'Obligatorisk logregistrering af videregivelse',
                    'Obligatorisk bevaring af beviser',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Op til 80 millioner VND',
                fine_org: 'Op til 160 millioner VND',
                deadlines: 'Løbende forpligtelse',
                remediation: [
                    'Obligatorisk offentliggørelse og versionsstyring af platformsregler',
                    'Obligatoriske brugerrettede oplysninger',
                    'Obligatorisk dokumentation for forpligtelser for sælger/affiliate/livestream',
                    'Obligatorisk historik over kontroller og godkendelseskæde',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Op til 80 millioner VND',
                fine_org: 'Op til 160 millioner VND',
                deadlines: 'Løbende forpligtelse',
                remediation: [
                    'Obligatorisk vurdering af grænseoverskridende forpligtelser',
                    'Obligatorisk beredskab hos autoriseret repræsentant',
                    'Obligatorisk register over grænseoverskridende aktiviteter',
                    'Obligatorisk dokumentation for compliancehandlinger',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Op til 80 millioner VND',
                fine_org: 'Op til 160 millioner VND',
                deadlines: 'Løbende forpligtelse',
                remediation: [
                    'Obligatorisk gennemgang af forbudte eller ulovlige opslag',
                    'Obligatoriske registreringer af nedtagningsbeslutninger',
                    'Obligatorisk bevaring af beviser',
                    'Obligatorisk meddelelse til sælger og afslutningsspor',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Op til 80 millioner VND',
                fine_org: 'Op til 160 millioner VND',
                deadlines: 'Som specificeret pr. klagebehandlingsforpligtelser',
                remediation: [
                    'Obligatorisk procedure for modtagelse af klager',
                    'Obligatorisk registrering af løsning',
                    'Obligatorisk revisionsspor for klager',
                    'Obligatorisk eskalering ved problemer med grænseoverskridende sælgere',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Op til 80 millioner VND',
                fine_org: 'Op til 160 millioner VND',
                deadlines: 'Før du påbegynder e-handel',
                remediation: [
                    'Obligatorisk platformsklassificering',
                    'Obligatorisk gennemførelse af anmeldelse eller registrering',
                    'Obligatorisk dokumentation for portalindsendelse',
                    'Obligatorisk sporing af opdatering/ophør',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Løbende drift af platformen',
            outputs: [
                'Risikovurderingsregistrering',
                'Log over tidlige advarsler',
                'Afhjælpningsregistrering',
                'Gennemgangsspor',
            ],
            channels: ['Platformworkflow'],
            responsible: {
                '1': 'Platformoperatør/risiko',
                '2': 'Platformoperatør/sikkerhed',
                '3': 'Platformoperatør/risiko',
                '4': 'Platformoperatør/sikkerhed',
                '5': 'Platform operatør',
                '6': 'Overholdelse',
            },
            step_outputs: {
                '1': ['Risikovurderingsregistrering'],
                '2': ['Log over tidlige advarsler'],
                '3': ['Resultater af risikogennemgang'],
                '4': ['Poster i risikoregister'],
                '5': ['Afhjælpningsregistrering'],
                '6': ['Gennemgangsspor'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'En deltager indgiver en klage eller en anmodning om tvist',
            outputs: [
                'Registrering af klagemodtagelse',
                'Registrering af tvistløsning',
                'Bevispakke',
                'Registrering af sagslukning',
            ],
            channels: ['Platformworkflow'],
            responsible: {
                '1': 'Platform operatør',
                '2': 'Platform operatør',
                '3': 'Platform operatør',
                '4': 'Platform operatør',
                '5': 'Platformoperatør / Juridisk',
                '6': 'Platform operatør',
                '7': 'Overholdelse',
            },
            step_outputs: {
                '1': ['Registrering af klagemodtagelse'],
                '2': ['Fuldstændighedskontrol', 'Anmodning om supplement (hvis relevant)'],
                '3': ['Varsel om håndteringstilgang'],
                '4': ['Bevispakke'],
                '5': ['Vurderingsresultater'],
                '6': ['Registrering af tvistløsning'],
                '7': ['Registrering af sagslukning', 'Arkiveret bevispakke'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'Levering er gennemført efter kontrakt og betalingsbetingelser er opfyldt',
            outputs: [
                'Leveringsregistrering',
                'Certifikat for ejendomsrettigheder til data',
                'Post i transaktionsregister',
            ],
            channels: ['Platformworkflow'],
            responsible: {
                '1': 'Sælger / platformsoperatør',
                '2': 'Platform operatør',
                '3': 'Platform operatør',
                '4': 'Platform operatør',
                '5': 'Platform operatør',
            },
            step_outputs: {
                '1': ['Leveringsregistrering'],
                '2': ['Logpost for leveringshændelse'],
                '3': ['Certifikat for ejendomsrettigheder'],
                '4': ['Post i transaktionsregister'],
                '5': ['Registrering af verifikationsadgang'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'En køber igangsætter en transaktion for data, produkt eller service',
            outputs: [
                'Registrering af transaktionsstart',
                'Elektronisk kontrakt',
                'Betalingsbekræftelse',
                'Registrering af leveringsgodkendelse',
            ],
            channels: ['Platformworkflow'],
            responsible: {
                '1': 'Køber',
                '2': 'Køber / platformsoperatør',
                '3': 'Platform operatør',
                '4': 'Køber / Sælger',
                '5': 'Platformoperatør / Betaling',
                '6': 'Platform operatør',
            },
            step_outputs: {
                '1': ['Beslutning om gennemgang af opslag'],
                '2': ['Registrering af transaktionsstart'],
                '3': ['Registrering af elektronisk kontrakt'],
                '4': ['Registrering af kontraktaccept'],
                '5': ['Betalingsbekræftelse'],
                '6': ['Registrering af leveringsgodkendelse'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'En deltager ønsker at evaluere dataprodukter/tjenester i det isolerede miljø',
            outputs: [
                'Sandbox-anmodning',
                'Registrering af sandbox-omfang',
                'Registrering af outputgennemgang',
                'Registrering af sandbox-afslutning',
            ],
            channels: ['Platformworkflow'],
            responsible: {
                '1': 'Deltager',
                '2': 'Platformoperatør/deltager',
                '3': 'Platformoperatør / IT',
                '4': 'Platformoperatør/sikkerhed',
                '5': 'Platform operatør',
                '6': 'Platform operatør',
                '7': 'Platformoperatør / IT',
            },
            step_outputs: {
                '1': ['Sandbox-anmodning'],
                '2': ['Registrering af sandbox-omfang'],
                '3': ['Klargjort miljø'],
                '4': ['Log over sikkerhedsovervågning'],
                '5': ['Registrering af outputgennemgang'],
                '6': ['Beslutning om godkendelse af udtræk'],
                '7': ['Registrering af sandbox-afslutning'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'En sælger søger at liste data, produkter eller datatjenester til handel',
            outputs: [
                'Ansøgning om notering',
                'Registrering af verifikation af lovlig kilde',
                'Registrering af teknisk validering',
                'Registrering af kvalitetsoplysninger',
                'Registrering af godkendelse af notering',
                'Post i noteringsregister',
            ],
            channels: ['Platformworkflow'],
            responsible: {
                '1': 'Sælger',
                '2': 'Sælger / Juridisk',
                '3': 'Sælger / IT',
                '4': 'Sælger',
                '5': 'Sælger',
                '6': 'Platform operatør',
                '7': 'Platform operatør',
                '8': 'Platform operatør',
            },
            step_outputs: {
                '1': ['Ansøgning om notering'],
                '2': ['Registrering af verifikation af lovlig kilde'],
                '3': ['Registrering af teknisk validering'],
                '4': ['Registrering af kvalitetsoplysninger'],
                '5': ['Indsendt noteringsdossier'],
                '6': ['Gennemgangsresultater'],
                '7': ['Registrering af godkendelse af notering'],
                '8': ['Offentliggjort notering', 'Post i noteringsregister'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'En sælger eller køber tilslutter sig dataplatformen',
            outputs: [
                'Onboardingregistrering for sælger / køber',
                'Post i deltagerregister',
                'Dokumentation for kontoopsætning',
                'Godkendelsesregistrering',
            ],
            channels: ['Platformworkflow'],
            responsible: {
                '1': 'Platform operatør',
                '2': 'Platformoperatør / Juridisk',
                '3': 'Platformoperatør / Juridisk',
                '4': 'Platform operatør',
                '5': 'Platform operatør',
                '6': 'Platform operatør',
                '7': 'Overholdelse',
            },
            step_outputs: {
                '1': ['Beslutning om deltagertype'],
                '2': ['Registrering af verifikation af retlig handleevne'],
                '3': ['Registrering af kontrol af betinget erhverv'],
                '4': ['Registrering af transaktionskonto'],
                '5': ['Registrering af tilknytning af betalingskonto'],
                '6': ['Registrering af godkendelse af deltagelse'],
                '7': ['Post i deltagerregister', 'Revisionsspor'],
            },
        },
        PDPL_P1: {
            trigger: 'Påbegyndelse af behandling af personoplysninger, der kræver konsekvensanalyse',
            outputs: ['DPIA-dossier', 'Indsendelsesbekræftelse', 'Supplerende pakke'],
            channels: ['MPS-portal', 'Personlig indlevering hos MPS A05'],
            responsible: {
                '1': 'DPO / Privacy Team',
                '2': 'Privacy Team',
                '3': 'Juridisk / Overholdelse',
                '4': 'DPO / Privacy Team',
                '5': 'Overholdelsesteam',
            },
            step_outputs: {
                '1': ['Færdiggjort DPIA-dossier'],
                '2': ['Dossierpakke', 'Følgebrev'],
                '3': ['Indsendelsesbekræftelse', 'Sporingsreference'],
                '4': ['Supplerende pakke', 'Opdateret dossier'],
                '5': ['Arkiveret dossier', 'Revisionsbevispakke'],
            },
        },
        PDPL_P2: {
            trigger: 'Hensigt om at overføre personlige data uden for Vietnam',
            outputs: ['Overførselsdossier', 'Indsendelsesbekræftelse', 'Supplerende svar'],
            channels: ['MPS-portal', 'Personlig indlevering hos MPS A05'],
            responsible: {
                '1': 'DPO / Privacy Team',
                '2': 'Juridisk / Overholdelse',
                '3': 'DPO / Privacy Team',
            },
            step_outputs: {
                '1': ['Pakke med overførselsdossier'],
                '2': ['Indsendelsesbekræftelse', 'Sporingsreference'],
                '3': ['Supplerende svar', 'Opdateret dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organisation, der leverer databehandlingstjenester, der kræver certificering',
            outputs: ['Certificeringsdossier', 'Certifikat', 'Re-issue and exchange record'],
            channels: ['MPS-portal', 'Personlig indlevering hos MPS A05'],
            responsible: {
                '1': 'Juridisk / Overholdelse',
                '2': 'Juridisk / Overholdelse',
                '3': 'DPO / Privacy Team',
                '4': 'Overholdelsesteam',
            },
            step_outputs: {
                '1': ['Ansøgningsdossier til certificering'],
                '2': ['Indsendelsesbekræftelse'],
                '3': ['Supplerende svar'],
                '4': ['Certifikatregistrering', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Modtagelse af en anmodning om registrerede rettigheder',
            outputs: [
                'Registrering af DSR-modtagelse',
                'Identitetsverifikation',
                'Svarbrev',
                'Rapport om sagslukning',
            ],
            channels: ['Onlineportal', 'E-mail', 'Personligt fremmøde'],
            responsible: {
                '1': 'Privacy Team / Kundeservice',
                '2': 'DPO / Privacy Team',
                '3': 'Afdelingsledere / IT',
                '4': 'DPO / Privacy Team',
                '5': 'Overholdelsesteam',
            },
            step_outputs: {
                '1': ['Registrering af DSR-modtagelse', 'Registrering af identitetsverifikation'],
                '2': ['Vurdering af anmodning', 'Tildeling af frist'],
                '3': ['Dokumentation for opfyldelse', 'Bekræftelser fra afdelinger'],
                '4': ['Svarbrev'],
                '5': ['Rapport om sagslukning', 'Revisionsbevispakke'],
            },
        },
        PDPL_P5: {
            trigger: 'Opdagelse af en krænkelse eller brud på persondatabeskyttelsen',
            outputs: [
                'Registrering af brudvurdering',
                'Underretningspakke',
                'Indsendelsesbekræftelse',
                'Supplerende pakke',
            ],
            channels: ['MPS-portal', 'Personlig indlevering hos MPS A05'],
            responsible: {
                '1': 'Incident Response Team / DPO',
                '2': 'DPO / Privacy Team',
                '3': 'Juridisk / Overholdelse',
                '4': 'DPO / Incident Response Team',
            },
            step_outputs: {
                '1': ['Registrering af brudvurdering', 'Registrering af beslutning om underretning'],
                '2': ['Underretningspakke om brud'],
                '3': ['Indsendelsesbekræftelse', 'Sporingsreference'],
                '4': ['Supplerende pakke', 'Dokumentation for koordinering'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Dekret 13 Art. 24',
        'MoPS Admin Procedures': 'MoPS Admin Procedurer',
        'Annual cycle': 'Årlig cyklus',
        'Decree 13/2023/NĐ-CP Art. 24': 'Dekret 13/2023/NĐ-CPĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2-5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Udkast til dekret om dataplatforme 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Lov 122/2025/QH15 Kap. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Lov 122/2025/QH15 Kap. IV (Forbrugerbeskyttelse)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Lov 122/2025/QH15 — Online.gov.vn operationelle procedure',
        'Law 134/2025/QH15 Article 12': 'Lov 134/2025/QH15 Artikel 12',
        'Law 134/2025/QH15 Article 21': 'Lov 134/2025/QH15 Artikel 21',
        'Law 134/2025/QH15 Article 27': 'Lov 134/2025/QH15 Artikel 27',
        'Law 134/2025/QH15 Articles 9–10': 'Lov 134/2025/QH15 Artikel 9-10',
        'Law 24/2023/QH15 — article mapping pending': 'Lov 24/2023/QH15 — artikelkortlægning afventer',
        'Law 60/2024/QH15 (internal control target)': 'Lov 60/2024/QH15 (intern kontrolmål)',
        'Law 91/2025/QH15 Art. 21': 'Lov 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Lov 91/2025/QH15 Art. 23',
        'Policy-defined': 'Politik defineret',
        Quarterly: 'Kvartalsvis',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 administrativ procedure',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'intern kontrolmål (lov 116/2025/QH15 art. 24(2)(b), 41(5) — nøjagtig tidslinje afventer implementeringsinstrument)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'intern kontrolmål (lov 116/2025/QH15 art. 41(3) — nøjagtig tidslinje afventer implementeringsinstrument)',
        'source verification pending': 'kildebekræftelse afventer',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Ad hoc-rapportering fra intern revision til SBV (Lag 5)',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Årlig ICAAP-rapport (Phụ lục III) (Lag 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Årligt bilag I (Lag 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Årligt bilag IV (Lag 5)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Årlig risikostyringsrapport (Phụ lục II) (Lag 5)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Ansvar for kapitalforvaltning (Lag 2, tema C)',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": 'Circular 83/2025/TT-NHNN — Tilsyn fra bestyrelse / medlemsråd (Lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Bestyrelsesudvalg (Lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Bestyrelsesbeslutninger og referater (Lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Kontrolprocedurer for filialer (Lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliancevurdering af interne regler (Lag 2, tema D)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliancefunktion (Lag 2, tema D)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Overvågning af compliance-udbedring (Lag 2, tema D)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Koncentrationsrisiko (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Interessekonflikter (Lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Kontrolkultur (Lag 2, tema A/B)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Kreditrisiko (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegerede beføjelser (Lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Varsling om tidlig implementering (Lag 5)',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Generaldirektørens ansvar (Lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Renterisiko i bankbogen (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Instruks for intern revision (Lag 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Interne revisionsopgaver (Tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Formidling af resultater fra intern revision (Lag 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Resultater fra intern revision og korrigerende handlinger (Tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Opfølgning fra intern revision (Tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Styring af intern revision',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Uafhængighed for intern revision (Lag 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Plan for intern revision (Lag 2/tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Rapportering fra intern revision til SBV (Lag 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Styring af det interne kontrolsystem (Lag 2)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Procedure for intern rapportering (Lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Register over interne rapporter (Lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Overvågning af lovændringer (Lag 2, tema D)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Juridisk risiko og omdømmerisiko (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Eskalering af limitoverskridelser (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Likviditetsrisiko (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Årlig gennemgang af MIS (Lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS-backup og kontinuitet (Lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Eskaleringsmekanismer for MIS (Lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS-pålidelighed og fortrolighed (Lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS-datakilder for risiko (Lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Ledelsesinformationssystem (Lag 4)',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Markedsrisiko (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Register over væsentlige risici (Lag 3)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Modelrisiko (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Gennemgang af nye produkter (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operationel risiko (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Beredskabsplan for outsourcing (Lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcingkontrakter (Lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Kontroller for outsourcing',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Due diligence for outsourcing (Lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Reserveleverandør ved outsourcing (Lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Væsentlighed ved outsourcing (Lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Overvågning af outsourcing (Lag 2, tema G)',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Tekniske referencer for Phụ lục V/VI (Lag 5)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Politikregister (Lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Professionel etik (Lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risikovillighed (Lag 3)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Koordination i risikokomitéen (Lag 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Styring af risikodata (Lag 3, tema E/F) — faset til 2028-01-01',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Gennemgang af risikolimiter (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Rammeværk for risikolimiter (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikostyringsfunktion (Lag 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Metoder og modeller til risikostyring (Lag 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risikorapportering (Lag 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Kadence for risikorapportering (Lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — Indsendelse af afhjælpning til SBV (Lag 5)',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — Indsendelseskanaler til SBV (Lag 5)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sektorrelevans (Lag 1)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Funktionsadskillelse (Lag 2, tema C)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Tilsyn fra den øverste ledelse (Lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategisk risiko (Lag 3, tema E)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stresstest (Lag 3, tema E) — faset til 2028-01-01',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Tilsynsråd (Lag 2, tema A)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Register over tilsynsresultater (Lag 4, tema F)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Roller og eskalering i de tre forsvarslinjer (Lag 2, tema B)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — De tre forsvarslinjer (Lag 2)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Eskalering af overtrædelser (Lag 2, tema D)',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (Nationalforsamlingen)',
        'Ministry of Public Security / A05 Department': 'Ministeriet for Offentlig Sikkerhed / A05-afdelingen',
        'Ministry of Public Security': 'Ministeriet for Offentlig Sikkerhed',
        'Ministry of Information and Communications': 'Ministeriet for information og kommunikation',
        'Government of Vietnam': 'Vietnams regering',
        'European Data Protection Board (EDPB)': 'Det Europæiske Databeskyttelsesråd (EDPB)',
        'ENISA / National CSIRTs': "ENISA / Nationale CSIRT'er",
        'State Bank of Vietnam': 'Vietnams statsbank',
    },
    tour_titles: {
        access_accountability: 'Få adgang til ansvarlighedstur',
        assessments: 'Vurderingsrundvisning',
        audit_trail: 'Audit Trail Tour',
        classification: 'Klassifikationstur',
        compliance_forms: 'Overholdelsesformular rundvisning',
        connectors: 'Forbindelsesrundvisning',
        consent_properties: 'Samtykke egenskaber Tour',
        consent: 'Samtykkestur',
        dashboard: 'Dashboard tur',
        data_mapping: 'Datakortlægningstur',
        data_subjects: 'Dataemner Tour',
        deletion_orchestration: 'Sletning Orchestration Tour',
        discovery: 'Opdagelsestur',
        governance: 'Governance Tour',
        help_center: 'Rundvisning i Hjælpecenteret',
        incident_ops: 'Incident Operations Tour',
        incidents: 'Incidents Tour',
        localization: 'Lokaliseringstur',
        monitoring: 'Overvågningstur',
        onboarding: 'Onboarding Tour',
        organizations: 'Organisationer Tour',
        partner_workspace: 'Partner Workspace Tour',
        platform_settings: 'Rundvisning på platformsindstillinger',
        privacy_scanner: 'Privacy Scanner Tour',
        program_governance: 'Program Governance Tour',
        rights_requests: 'Rettighedsanmodninger Rundvisning',
        task_management: 'Task Management Tour',
        vendor_risk: 'Sælgerrisikorundvisning',
    },
    tour_descriptions: {
        access_accountability: 'As the Internal Audit Lead (P-VN-10), review access evidence, audit permission usage, and investigate anomalous patterns to demonstrate GDPR Article 5(1)(f) integrity and confidentiality.',
        assessments: 'As the Enterprise DPO (P-VN-01), conduct DPIAs following Decree 356 Mau so 02a, identify compliance gaps, and assess cross-border transfer risks under PDPL.',
        audit_trail: 'Som Internal Audit Lead (P-VN-10) kan du forespørge beviser på tværs af moduler, samle revisionspakker og eksportere manipulationssikre logfiler til lovmæssige indsendelser.',
        classification: 'As the Data Steward (P-VN-05), define data classification taxonomies, assign sensitivity labels, enforce retention policies, and resolve coverage gaps required for PDPL compliance.',
        compliance_forms: 'As the Enterprise DPO (P-VN-01), browse, complete, and submit official Vietnamese regulatory forms including Phu luc I, Phu luc II, and Mau so templates for PDP Committee filings.',
        connectors: 'Som IT Systems Lead (P-VN-05) skal du registrere forretningskrav til integrationer, kortlægge forbindelser til compliance-datastrømme, og verificere POC-beredskab, før du går live.',
        consent_properties: 'As the Enterprise DPO (P-VN-01), configure CMP deployment: digital property registration, golden templates for consistent PDPL consent banners, and binding matrices that link properties to processing activities.',
        consent: 'As the Enterprise DPO (P-VN-01), manage the consent policy lifecycle: define collection purposes, enforce PDPL withdrawal rights, and integrate with your Consent Management Platform.',
        dashboard: 'As the Enterprise DPO (P-VN-01), orient yourself around the compliance posture command center that surfaces PDPL deadlines, breach SLAs, and audit-readiness KPIs.',
        data_mapping: 'As the Data Steward (P-VN-05), build your ROPA inventory, map cross-border data flows, and maintain the processing activity register required by PDPL.',
        data_subjects: 'Som Enterprise DPO eller Education Compliance Lead, administrer forenede dataemneprofiler, der forbinder samtykkeregistreringer, rettighedsanmodninger og tidslinjeaktivitet på tværs af platformen.',
        deletion_orchestration: 'As the DPO (P-VN-01), verify end-to-end data deletion across internal systems and third-party vendors, with cryptographic proof chains for GDPR Article 17 right-to-erasure compliance.',
        discovery: 'As the Data Steward (P-VN-05), run automated scans to discover personal data across connected systems, review staged findings, and feed results into the Classification module for PDPL labeling.',
        governance: 'As the Vendor Risk Manager (P-VN-08), maintain the vendor registry, track subprocessor chains, assess cross-border transfer risks, and enforce governance playbooks under PDPL.',
        help_center: "For alle personas -- DPO'er, revisorer, it-leads og compliance managers -- find svar, how-to-vejledninger og modulspecifik dokumentation til at understøtte dine daglige compliance-arbejdsgange.",
        incident_ops: 'As the Incident Commander (P-VN-09), activate response playbooks, track SLA compliance against the 72-hour PDPL notification window, manage escalations, and coordinate vendor response.',
        incidents: 'As the Incident Commander (P-VN-09), detect and report data breaches with 72-hour PDPL notification obligations to the PDP Committee, triage by severity, and build audit-grade incident dossiers.',
        localization: 'Som Enterprise DPO (P-VN-01), skal du administrere den lovgivningsmæssige rammelivscyklus: installer vietnamesiske markedspakker, aktiver PDPL- og dekret 13-rammerne, kortlæg krav til platformsmoduler og administrer oversættelser.',
        monitoring: "Som Enterprise DPO (P-VN-01), etablere løbende overholdelsesovervågningsprogrammer, spor reguleringsændringer, mål KPI'er og administrer regulatoriske interaktionssager under PDPL.",
        onboarding: 'Som compliance-træner (P-VN-02), lever træning via guidede ture, indlæs realistiske eksempeldata til praktiske øvelser, og kør opsætningsguiden til hurtigstartsscenarier.',
        organizations: 'Som DPO (P-VN-01), konfigurer overholdelsesstrukturer for flere organisationer, opret afdelingshierarkier og diriger anmodninger om datasubjekter på tværs af din virksomhedsgruppe.',
        partner_workspace: 'Som Partner Account Manager kan du indbygge partnerorganisationer, konfigurere serviceniveauer, spore overholdelsesleverancer og administrere partnermarkedspladsen.',
        platform_settings: 'Som platformsadministrator skal du administrere al operationel konfiguration -- identitet, sikkerhed, integrationer og moduladgang, der understøtter dit compliance-program.',
        privacy_scanner: 'Som privatlivsanalytiker (P-VN-06), skal du køre kontinuerlige overvågningsscanninger for at opdage overtrædelser af cookie-samtykke, uautoriserede sporere og huller i politikken – og generere bevis for ePrivacy og GDPR overholdelse.',
        program_governance: 'Naviger mellem modenhedsmodeller, vurderinger, huller i beviser, resuméer og mere. Hvert undermodul bidrager til et holistisk syn på din interne programsundhed.',
        rights_requests: 'Som Enterprise DPO (P-VN-01), kan du administrere anmodninger om registrerede rettigheder i henhold til PDPL med 15-dages lovbestemte frister, identitetsbekræftelse og revisionsgradsløsningsspor.',
        task_management: 'Rut compliance-opgaver på tværs af personaer, spor SLA deadlines, og automatiser arbejdsgange, der holder dit team ansvarligt.',
        vendor_risk: 'Som Vendor Risk Manager (P-VN-08) kan du score tredjepartsrisiko, håndhæve gate-gennemgange og udløse revurderinger – til forskel fra Program Governance, som sporer din interne modenhed.',
    },
    consolidation: {
        title: 'Konsolideringssager',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Opret konsolideringssag',
        case_count: 'case',
        created: 'Konsolideringssag oprettet',
        create: 'Opret sag',
        creating: 'Opretter...',
        cancel: 'Annuller',
        no_cases: 'Ingen konsolideringssager fundet.',
        all_statuses: 'Alle statusser',
        all_purposes: 'Alle Formål',
        filter_status: 'Filtrer efter status',
        filter_purpose: 'Filtrer efter formål',
        col_title: 'Titel',
        col_purpose: 'Formål',
        col_status: 'Status',
        col_progress: 'Fremskridt',
        col_deadline: 'Deadline',
        col_department: 'afdeling',
        col_version: 'version',
        col_evidence: 'Beviser',
        sections_accepted: 'accepted',
        prev: 'Forrige',
        next: 'Næste',
        page: 'Side',
        prev_page: 'Forrige side',
        next_page: 'Næste side',
        field_title: 'Titel',
        field_purpose: 'Formål',
        field_framework: 'Rammekode',
        field_deadline: 'Deadline',
        field_reviewer: 'Anmelderens bruger-id',
        title_placeholder: 'Indtast sagens titel',
        framework_placeholder: 'f.eks. PDPD-2023',
        reviewer_placeholder: 'Indtast anmelderens bruger-id',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Arkivering',
            cross_border_filing: 'Grænseoverskridende arkivering',
            annual_governance_attestation: 'Årlig ledelsesattest',
            breach_package: 'Overtrædelsespakke',
            audit_inspection_pack: 'Revisionsinspektionspakke',
        },
        statuses: {
            draft: 'Udkast',
            collecting: 'Indsamling',
            consolidating: 'Konsoliderer',
            review: 'Gennemgå',
            corrections: 'Rettelser',
            pending_approval: 'Afventer godkendelse',
            approved: 'Godkendt',
            completed: 'Afsluttet',
            closed: 'Lukket',
            rejected: 'Afvist',
        },
        section_statuses: {
            pending: 'Afventer',
            in_progress: 'Igangværende',
            submitted: 'Indsendt',
            accepted: 'Accepteret',
            needs_correction: 'Trænger til rettelse',
            corrected: 'Rettet',
        },
        flag_types: {
            missing_section: 'Manglende afsnit',
            conflicting_entry: 'Modstridende adgang',
            stale_entry: 'Forældet indgang',
            missing_evidence: 'Manglende beviser',
            incomplete_data: 'Ufuldstændige data',
            duplicate_entry: 'Dublet indtastning',
        },
        flag_severities: {
            critical: 'Kritisk',
            warning: 'Advarsel',
            info: 'Info',
        },
        sections_title: 'Afsnit',
        no_sections: 'Ingen sektioner tildelt endnu.',
        flags_title: 'Flag',
        no_flags: 'Ingen flag fundet.',
        open: 'open',
        approval_chain: 'Godkendelseskæde',
        pending: 'Afventer',
        audit_trail: 'Revisionsspor',
        no_audit: 'Ingen revisionsbegivenheder.',
        auto_assign: 'Auto-tildel sektioner',
        start_collection: 'Start indsamling',
        review_case: 'Gennemgå sag',
        generate_export: 'Generer eksport',
        review_title: 'Konsolidering gennemgang',
        advance_approval: 'Gå videre til godkendelse',
        back_to_case: 'Tilbage til Case',
        accept: 'Accepter',
        return_section: 'Retur',
        return_comment: 'Returner kommentar...',
        resolve: 'Løs',
        dismiss: 'Afvis',
        resolution_notes: 'Opløsningsnotater...',
        notes: 'Noter',
        section_accepted: 'Afsnit accepteret',
        section_returned: 'Afsnit returneret til rettelse',
        advanced_to_approval: 'Sagen afventer godkendelse',
        section_data: 'Sektionsdata',
        save_progress: 'Gem fremskridt',
        submit_section: 'Indsend sektion',
        submitting: 'Indsender...',
        resubmit: 'Indsend igen',
        progress_saved: 'Fremskridt er gemt',
        section_submitted: 'Afsnit indsendt',
        correction_required: 'Rettelse påkrævet',
        evidence_title: 'Beviser',
        no_evidence: 'Ingen beviser vedhæftet.',
        add_evidence: 'Tilføj bevis',
        evidence_added: 'Bevis tilføjet',
        evidence_files: 'bevisfiler',
        uploaded_by: 'uploadet af',
        comments_title: 'Kommentarer',
        no_comments: 'Ingen kommentarer endnu.',
        comment_placeholder: 'Tilføj en kommentar...',
        add_comment: 'Tilføj kommentar',
        comment_added: 'Kommentar tilføjet',
        view_data: 'Se indsendte data',
        export_title: 'Dossier eksport',
        export_generated: 'Eksportpakke genereret',
        regenerate: 'Regenerer',
        regenerating: 'Regenererer...',
        generated_at: 'Genereret kl',
        artifacts_title: 'Eksporter artefakter',
        artifact_type: 'Artefakt',
        file_name: 'Filnavn',
        file_size: 'Størrelse',
        generated: 'Genereret',
        download: 'Download',
        artifacts: {
            master_report: 'Hovedrapport',
            evidence_bundle: 'Bevispakke (ZIP)',
            contributor_lineage: 'Bidragyders slægt',
            conflict_resolution: 'Konfliktløsning',
            approval_history: 'Godkendelseshistorie',
        },
        official_forms: 'Officielle formularer',
        forms_description: 'Følgende officielle formularer kan genereres ud fra de konsoliderede data. Officielle formularer er adskilt fra interne arbejdspapirer.',
        announce_loaded_cases: 'Indlæste {count} konsolideringssager',
        announce_case_detail_loaded: 'Sagsdetaljer indlæst',
        announce_case_transitioned: 'Sagen blev overført til {status}',
        announce_flag_resolved: 'Flag løst',
        announce_flag_dismissed: 'Flag afvist',
        case_detail_loaded: 'Sagsdetaljer indlæst',
        sections_auto_assigned: 'Sektioner automatisk tildelt fra afdelingshierarki',
        errors: {
            load_failed: 'Kunne ikke indlæse data. Prøv venligst igen.',
            create_failed: 'Sagen kunne ikke oprettes. Prøv venligst igen.',
            required_fields: 'Udfyld venligst alle obligatoriske felter.',
            not_found: 'Ikke fundet.',
            comment_required: 'Kommentar er påkrævet, når du returnerer et afsnit.',
            notes_required: 'Noter er påkrævet, når du løser eller afviser et flag.',
        },
    },
    rights_requests: {
        import: {
            title: 'Importér sager',
            nav_title: 'Masseimport',
            description: 'Upload en CSV-fil for at masseimportere rettighedsanmodningssager.',
            select_file: 'Vælg CSV-fil',
            file_help: 'CSV med overskrifter der matcher sagsfelter.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Filen er for stor. Maksimal størrelse er 2MB.',
            preview: 'Forhåndsvisning',
            columns_detected: 'Kolonner fundet: {count}',
            column_mapping: 'Kolonnetilknytning',
            import_button: 'Importér',
            import_complete: 'Import fuldført',
            import_error: 'Import mislykkedes',
            result: 'Importresultat',
            total: 'I alt',
            imported: 'Importeret',
            skipped: 'Sprunget over',
            failed: 'Fejlet',
            errors: 'Fejl',
            error_row: 'Række',
            error_field: 'Felt',
            error_message: 'Besked',
            history: 'Importhistorik',
            no_history: 'Ingen importhistorik.',
            history_file: 'Filnavn',
            history_date: 'Dato',
        },
        batch: {
            nav_title: 'Batch proces',
            page_title: 'DSR Batch Process',
            page_subtitle: "Upload en CSV af afventende DSR'er. Forseti udarbejder et bekræftelsessvar pr. anmodning og sætter en verifikationsopgave i kø pr. række. DPO anmeldelser + accepterer hver før afsendelse.",
            upload_heading: 'Upload',
            csv_hint: 'Påkrævede kolonner: request_id, requester_email, request_type. Valgfrit: deadline (ISO-8601), noter.',
            rows_heading: '{count} rækker parset',
            submit_label: 'Kør batch',
            submitting: 'Udarbejdelse...',
            col_request_id: 'Anmodnings-id',
            col_email: 'Anmoderens e-mail',
            col_type: 'Type',
            col_status: 'Status',
            col_actions: 'Handlinger',
            status_queued: 'I kø',
            status_drafted: 'Udarbejdet',
            status_failed: 'Mislykkedes',
            status_accepted: 'Accepteret',
            status_rejected: 'Afvist',
            status_parsing_error: 'Parse fejl',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Accepter',
            reject: 'Afvis',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Vurderingsudløsere',
        subtitle: 'Konfigurer automatisk vurderingsoprettelse og DPO meddelelser for arbejdsgange til beskyttelse af personlige oplysninger.',
        auto_create_dpia_label: 'Opret DPIA automatisk, når behandlingsaktivitet markeret som DPIA påkrævet',
        auto_notify_new_purpose_label: 'Giv automatisk DPO besked, når nyt formål oprettes med følsomme datakategorier',
        auto_notify_cross_border_label: 'Giv automatisk DPO besked, når grænseoverskridende overførsel tilføjes til behandlingsaktivitet',
        default_dpo_label: 'Standard DPO',
        default_dpo_placeholder: 'Vælg standard DPO...',
        notification_channels_label: 'Notifikationskanaler',
        channel_in_app: 'In-App',
        channel_email: 'Email',
        save_success: 'Indstillinger for vurderingstrigger gemt.',
        save_error: 'Kunne ikke gemme indstillingerne for vurderingsudløser.',
        fetch_error: 'Kunne ikke indlæse indstillinger for vurderingsudløser.',
        fetch_users_error: 'Kunne ikke indlæse brugere.',
        announce_loaded: 'Indstillinger for vurderingstrigger er indlæst',
        dpia_status: 'DPIA Status',
        dpia_linked: 'DPIA Linket',
        dpia_required_no_dpia: 'DPIA Påkrævet – ikke oprettet',
        create_dpia: 'Opret DPIA',
        create_dpia_success: 'DPIA oprettet og knyttet til aktivitet.',
        create_dpia_error: 'Kunne ikke oprette DPIA til aktivitet.',
        view_dpia: 'Se DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Registrerede personers profiler',
        nav_title: 'Registrerede personer',
        nav: {
            profiles: 'Profiler',
        },
        create: 'Opret profil',
        edit: 'Rediger profil',
        delete: 'Slet profil',
        delete_confirm: 'Er du sikker på, at du vil slette denne registrerede persons profil?',
        display_name: 'Vist navn',
        email: 'Email',
        phone: 'Telefon',
        employee_id: 'Medarbejder ID',
        student_id: 'Studie-id',
        notes: 'Noter',
        external_refs: 'Eksterne referencer',
        cases_count: 'Sager',
        workflows_count: 'Samtykkeregistreringer',
        last_activity: 'Sidste aktivitet',
        timeline: 'Tidslinje',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'Ingen registrerede profiler fundet.',
        search_placeholder: 'Søg efter navn, e-mail, telefon eller id...',
        create_success: 'Registreret profil oprettet.',
        create_error: 'Kunne ikke oprette den registrerede profil.',
        update_success: 'Den registrerede profil er opdateret.',
        update_error: 'Kunne ikke opdatere den registrerede profil.',
        delete_success: 'Den registrerede profil er slettet.',
        delete_error: 'Kunne ikke slette den registrerede profil.',
        link_case_success: 'Sag knyttet til profil.',
        link_case_error: 'Sagen kunne ikke linkes til profilen.',
        link_workflow_success: 'Arbejdsgang knyttet til profil.',
        link_workflow_error: 'Kunne ikke linke arbejdsgangen til profilen.',
        load_error: 'Kunne ikke indlæse registrerede profiler.',
        detail_load_error: 'Kunne ikke indlæse den registrerede profil.',
        timeline_load_error: 'Tidslinjen kunne ikke indlæses.',
        tab_cases: 'DSR Sager',
        tab_consent: 'Samtykkeregistreringer',
        tab_timeline: 'Tidslinje',
        tab_consent_workflows: 'Samtykke arbejdsgange',
        tab_web_consent: 'CMP Samtykke',
        web_consent_empty: 'Ingen CMP samtykkeregistreringer fundet for dette emne.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'Kunne ikke indlæse CMP samtykkeposter.',
        col_policy_version: 'Politikversion',
        col_versions: 'Versioner',
        col_consent_version: 'Samtykke',
        col_cookie_declaration_version: 'Cookie-erklæring',
        col_purposes: 'Formål',
        purpose_granted: 'Indrømmet',
        purpose_denied: 'Afvist',
        granted_domains: 'Indrømmet',
        denied_domains: 'Afvist',
        col_action: 'Handling',
        col_timestamp: 'Tidsstempel',
        col_domain: 'Domæne',
        no_cases: 'Ingen forbundne sager.',
        no_workflows: 'Ingen tilknyttede samtykkearbejdsgange.',
        no_timeline: 'Ingen tidslinjebegivenheder.',
        case_id: 'Sags-id',
        case_status: 'Status',
        case_type: 'Anmodningstype',
        case_created: 'Oprettet',
        workflow_id: 'Workflow ID',
        workflow_type: 'Type',
        workflow_status: 'Status',
        workflow_created: 'Oprettet',
        timeline_date: 'Dato',
        timeline_event: 'Begivenhed',
        timeline_details: 'Detaljer',
        timeline_time: 'Tid',
        timeline_type: 'Type',
        timeline_summary: 'Resumé',
        type_case: 'DSR Sag',
        type_consent_workflow: 'Samtykke Workflow',
        profile_info: 'Profiloplysninger',
        announce_loaded: 'Registrerede profiler indlæst',
        announce_detail_loaded: 'Den registrerede profil er indlæst',
        select_case: '— Vælg en sag —',
        select_workflow: '— Vælg et workflow —',
        no_cases_hint: 'Link sager for at se dem her.',
        no_workflows_hint: 'Link samtykkeprocesser for at se dem her.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} systemer · {flows} flows · {crossBorder} grænseoverskridende · {highRisk} høj/kritisk risiko',
            switch_to_list: 'Skift til listevisning',
            switch_to_list_aria: 'Skift til tabelvisning',
            svg_aria: 'Dataflowkort: {systems} systemer, {flows} flows, {crossBorder} grænseoverskridende',
            selected_system: 'Valgt system',
            drill_hint: "Åbn listefanen for at redigere de sammenkædede flows, DPIA'er og leverandørtildelinger for dette system.",
        },
    },
    packages: {
        light: {
            name: 'Lys',
            description: 'Vigtige privatlivs-compliance-værktøjer til små teams',
        },
        standard: {
            name: 'Standard',
            description: 'Fuldt udstyret compliance-platform til voksende organisationer',
        },
        enterprise_unlimited: {
            name: 'Enterprise Unlimited',
            description: 'Compliance på enterprise-niveau med multi-org-understøttelse og dedikerede tjenester',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Udbyderindstillinger',
            subtitle: 'Konfigurer Claude-udbyderen til Forseti AI juridisk rådgiver.',
            healthHeading: 'Udbyder sundhed',
            statusLabel: 'Status',
            statusOk: 'OK',
            statusFailed: 'Ikke tilgængelig',
            providerLabel: 'Udbyder',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Claude CLI relæ',
            providerAesirManaged: 'Aesir-administrerede tokens',
            lastCheckedLabel: 'Sidst tjekket',
            reasonLabel: 'Årsag',
            refreshButton: 'Opdater',
            apiKeyHeading: 'Claude API nøgle',
            apiKeyHint: 'Indstilling af en API-tast aktiverer Claude API-tilstand. Lad være deaktiveret for at bruge det lokale CLI-relæ (udvikler/selv-hostet tilstand).',
            apiKeyLabel: 'API tast',
            apiKeyMinLength: 'Minimum 32 tegn. Krypteret i hvile; aldrig vist igen.',
            apiKeyTooShort: 'API-tasten skal være på mindst 32 tegn.',
            apiKeySaved: 'API nøgle gemt. Forfriskende sundhed...',
            apiKeyCleared: 'API-tasten blev ryddet. Forseti bruger nu det lokale CLI-relæ.',
            setKeyButton: 'Gem tasten API',
            clearKeyButton: 'Ryd API-tasten (brug CLI-relæ)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Brug',
            recentHeading: 'Seneste aktivitet',
            historyHeading: 'Sidste 12 perioder',
            noActivity: 'Ingen aktivitet endnu i denne periode.',
            featureLabel: 'Feature',
            whenLabel: 'Hvornår',
            periodLabel: 'Periode',
            byFeatureHeading: 'Denne måned efter funktion',
            byFeatureEmpty: 'Ingen funktionsaktivitet endnu i denne periode.',
            featureColumn: 'Feature',
            featureUnitsColumn: 'Enheder',
            featureRunsColumn: 'Løber',
            byUserHeading: 'Denne måned af bruger (admin visning)',
            byUserEmpty: 'Ingen aktivitet pr. bruger endnu i denne periode.',
            userColumn: 'Bruger',
            reconciliationLinkLabel: 'Se rapport om afstemningsdrift',
            reconciliationLinkHint: 'Kun Aesir-intern administrator.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Chat',
                workflow: 'Kørsel af arbejdsgang',
                tabular: 'Tabeludtræk',
                redline: 'Redline anmeldelse',
                title: 'Samtale titel',
                unknown: 'Andet',
            },
        },
        reconciliation: {
            title: 'Forseti-kvoteafstemning',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Tilbage til Forseti-brug',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'Denne side er begrænset til Aesir-interne administratorer. Hvis du mener, du skal have adgang, skal du kontakte din AesirX-repræsentant.',
        },
        projects: {
            title: 'Projekter',
            subtitle: 'Arbejdsrum til dokumenter, samtaler, arbejdsgange og anmeldelser.',
            modeLabel: 'Visning',
            tabMine: 'Mine projekter',
            tabShared: 'Delt med mig',
            searchLabel: 'Søg efter projekter',
            searchPlaceholder: 'Søg...',
            newButton: 'Nyt projekt',
            colName: 'Navn',
            colVisibility: 'Synlighed',
            colDocs: 'Dokumenter',
            colMembers: 'Medlemmer',
            colUpdated: 'Opdateret',
            visibilityOwn: 'Kun egen',
            visibilityShared: 'Delt',
            emptyMine: 'Ingen projekter endnu. Opret en for at komme i gang.',
            emptyShared: 'Ingen projekter er blevet delt med dig.',
            pageHint: 'Viser {shown} af {total}.',
            createTitle: 'Nyt projekt',
            createSubmit: 'Opret projekt',
            fieldName: 'Navn',
            fieldDescription: 'Beskrivelse (valgfrit)',
            fieldCmNumber: 'Sags-/sagsnummer (valgfrit)',
            fieldVisibility: 'Synlighed',
            errorNameRequired: 'Navn er påkrævet.',
            errorSelectorRequired: 'Vælg en bruger eller organisationsgruppe, der skal tilføjes.',
            renameButton: 'Omdøb',
            renamePrompt: 'Omdøb projekt',
            deleteButton: 'Slet',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Vil du slette denne mappe? Dokumenter indeni forbliver i projektet, bare uarkiveret.',
            removeMemberConfirm: 'Vil du fjerne dette medlem?',
            foldersHeading: 'Mapper',
            foldersEmpty: 'Ingen mapper endnu.',
            addFolderButton: 'Ny mappe',
            addFolderTitle: 'Ny mappe',
            documentsHeading: 'Dokumenter',
            documentsEmpty: 'Ingen vedhæftede dokumenter endnu.',
            colFilename: 'Filnavn',
            colAdded: 'Tilføjet',
            unattachButton: 'Frigør',
            membersHeading: 'Medlemmer',
            membersEmpty: 'Kun ejer.',
            addMemberButton: 'Tilføj medlem',
            addMemberTitle: 'Tilføj medlem',
            addMemberSubmit: 'Tilføj medlem',
            colMember: 'Medlem',
            colRole: 'Rolle',
            selectorTypeLabel: 'Tilføj ved',
            selectorUser: 'Bruger-id',
            selectorGroup: 'Organisationsgruppe-id',
            fieldUserId: 'Bruger',
            fieldGroupId: 'Organisationsgruppe',
            fieldRole: 'Rolle',
            roleAdmin: 'Admin',
            roleEditor: 'Redaktør',
            roleViewer: 'Seer',
            notFound: 'Projekt ikke fundet.',
            selectUserPlaceholder: 'Vælg en bruger...',
            selectGroupPlaceholder: 'Vælg en organisationsgruppe...',
            noUsersAvailable: 'Ingen tilgængelige brugere.',
            noGroupsAvailable: 'Ingen tilgængelige organisationsgrupper.',
            loadFailed: 'Valgmulighederne kunne ikke indlæses.',
            deleteProjectTitle: 'Vil du slette projektet?',
            deleteFolderTitle: 'Vil du slette mappen?',
            removeMemberTitle: 'Vil du fjerne medlem?',
            removeMemberConfirmGeneric: 'Vil du fjerne dette medlem fra projektet?',
            addDocumentButton: 'Tilføj dokument',
            addDocumentTitle: 'Tilføj dokument til projekt',
            fieldDocument: 'Vælg dokument',
            selectDocPlaceholder: 'Søg efter et dokument...',
            ownerLabel: 'Systemejer',
        },
        chat: {
            sidebarLabel: 'Chats',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Din AI juridiske og overholdelsesrådgiver — baseret på dine lejerdata og dine installerede reguleringspakker.',
            personaSelectorLabel: 'Persona',
            personaHeading: 'Vælg en persona',
            startChatButton: 'Start en ny chat',
            startingChat: 'Starter chat...',
            recentHeading: 'Seneste chats',
            recentEmpty: 'Ingen chats endnu - start en ovenfor.',
            untitledChat: 'Ny chat',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Samtale',
            detailSubtitle: 'Persona: {persona}',
            composeHeading: 'Send en besked',
            composeLabel: 'Besked Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Persona',
            presetPersona: 'Forudindstillet persona',
            changePersona: 'Skift persona',
            hidePersonaOptions: 'Skjul personavalg',
            starterHeading: 'Start med en af disse handlinger',
            renameButton: 'Omdøb',
            renamePrompt: 'Omdøb chat',
            deleteButton: 'Slet',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Chat blev ikke fundet.',
            emptyChat: 'Ingen beskeder endnu – sig hej for at starte en samtale.',
            roleUser: 'Dig',
            roleAssistant: 'Forseti',
            roleSystem: 'System',
            thinkingLabel: 'Tænker',
            toolCallPlaceholder: 'Værktøjskald:',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Dokumentredigeringer foreslået (V{version}).',
            disclaimer: 'Forseti støtter din dømmekraft – den erstatter ikke autoriseret advokat.',
            sendButton: 'Send',
            sending: 'Sender...',
            composePlaceholder: 'Spørg Forseti...',
            composingPlaceholder: 'Forseti svarer...',
            streamingPlaceholder: 'Forseti skriver...',
            promptInjectionTitle: 'Forseti tabte nogle kontrolpoletter.',
            promptInjectionBody: 'Strippede mønstre: {patterns}. Forseti vil kun se din beskedtekst.',
            rateLimitTitle: 'Du sender beskeder for hurtigt.',
            rateLimitRetryAfter: 'Prøv igen om {seconds}s.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti er usikker her.',
            counselReviewBody: 'Bekræft med en autoriseret rådgiver, før du handler på dette svar.',
            legalConclusionRefused: 'Forseti nægtede at udstede en juridisk konklusion uden begrundelse. Prøv search_regulatory først eller eskalér til rådgivning.',
            toolCallDone: 'Færdig.',
            toolCallArgsLabel: 'Argumenter',
            toolCallResultLabel: 'Resultat',
            toolResultLogged: 'Værktøjsresultat fanget i revisionslog.',
            toolResultAria: 'værktøjsresultat',
            documentEditAria: 'dokumentredigering',
            createTaskFromMessage: 'Opret opgave ud fra dette',
            createTaskHeading: 'Opret opgave fra denne besked',
            createTaskTitleLabel: 'Titel',
            createTaskTitleRequired: 'Titel er påkrævet.',
            createTaskDescriptionLabel: 'Beskrivelse',
            createTaskAssigneeLabel: 'Opdragsgiver',
            createTaskAssigneePlaceholder: 'Vælg modtager...',
            createTaskAssigneeNone: '— ikke tildelt —',
            createTaskDueDateLabel: 'Forfaldsdato',
            createTaskWorkflowLabel: 'Linket arbejdsgang (valgfrit)',
            createTaskWorkflowPlaceholder: 'Vælg en arbejdsgang...',
            createTaskWorkflowNone: '— ingen —',
            renameModalSubmit: 'Omdøb',
            renameModalSaving: 'Gemmer...',
            deleteConfirmTitle: 'Vil du slette chatten?',
            createTaskSubmit: 'Opret opgave',
            createTaskSubmitting: 'Opretter...',
            cancelButton: 'Annuller',
            autoRouteHintMessage: 'Dette spørgsmål er mere i {persona}s styrehus.',
            autoRouteSwitchButton: 'Skift til {persona}',
            autoRouteSwitching: 'Skifter...',
            handoffSuggestionMessage: 'Eller åbn en ny chat med {persona} og viderebring dette spørgsmål.',
            handoffSuggestionButton: 'Start en ny chat med {persona}',
            handoffSuggestionWorking: 'Åbner...',
            handoffSuggestionDismiss: 'Ikke nu',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Compliance-specialist',
                'forseti-legal': 'Juridisk rådgiver',
                'forseti-dpo': 'DPO-rådgiver',
                'forseti-risk': 'Risikoanalytiker',
                'forseti-auditor': 'Revisorperspektiv',
            },
            exportButton: 'Eksportér PDF',
            exportRunning: 'Eksporterer⬦',
            exportSuccess: 'Eksporteret: {filename} (åbn filbrowseren).',
            exportOpenFile: 'Åbn i filbrowseren',
            exportDownload: 'Download PDF',
            citation: {
                verifyBadge: '[BEKRÆFT]',
                verifyAria: 'citatet kunne ikke verificeres - gennemse før du stoler',
                popoverAria: 'Kilde til {label}',
                openSource: 'Open source side',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-genereret hændelse fra Forseti-revisionssporet.',
        },
        rowAction: {
            ask: 'Spørg Forseti',
            tooltipOpen: 'Spørg Forseti om dette {recordType}',
            tooltipLocked: 'Denne {recordType} er låst — Forseti kan besvare spørgsmål, men kan ikke foretage ændringer.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti — om denne rekord',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Resumé',
            suggestedPersona: 'Foreslået persona',
            askForseti: 'Spørg Forseti',
            runWorkflow: 'Kør en arbejdsgang',
            viewLineage: 'Se Audit Lineage',
            createPdplEvidencePack: 'Opret PDPL-dokumentationspakke',
            prepareBreachDraft: 'Forbered meddelelsesudkast',
            primaryActions: {
                projectSummary: 'Opsummer projektdokument',
                supplementSummary: 'Opsummer supplerende anmodning',
                authoritySummary: 'Opsummer myndighedsanmodning',
                rightsRequestResponse: 'Udarbejd DSR-svar',
                dpiaQuickReview: 'Kør hurtig DPIA-gennemgang',
                taskControlWalkthrough: 'Gennemgå kontrolevidens',
                vendorRiskNarrative: 'Generér leverandørrisiko-narrativ',
                incidentLetter: 'Udarbejd PDPL-brudbrev',
                ropaSufficiency: 'Kør RoPA-tilstrækkelighedstjek',
                vendorDpaReview: 'Kør leverandør-DPA-gennemgang',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti udarbejder kun udkast til det tilknyttede Mẫu số 08. Det afgør ikke, om anmeldelse er påkrævet, og indsender eller sender ikke formularen.',
            breachDraftReady: 'Udkast til Mẫu số 08 klar til gennemgang',
            breachDraftReviewBeforeSelecting: 'Gennemgå før valg',
            workflowHeading: 'Relevante arbejdsgange',
            workflowLoading: 'Indlæser relevante arbejdsgange…',
            workflowEmpty: 'Ingen egnet arbejdsgang fundet. Opret en guidet arbejdsgang for denne post.',
            workflowError: 'Kunne ikke indlæse relevante arbejdsgange.',
            selectWorkflow: 'Vælg {title}',
            workflowSelected: 'Arbejdsgangen er valgt og klar til at køre for denne post.',
            workflowRunSelected: 'Kør den valgte arbejdsgang',
            workflowStarting: 'Starter arbejdsgang…',
            workflowRunError: 'Denne arbejdsgang kunne ikke startes.',
            workflowCreateGuided: 'Opret en guidet arbejdsgang for denne post',
            lineageHeading: 'Revisionsforløb for denne post',
            lineageLoading: 'Indlæser revisionsforløb…',
            lineageEmpty: 'Der er ikke registreret nogen Forseti-forløbshændelser for denne post.',
            lineageError: 'Kunne ikke indlæse revisionsforløb.',
            retry: 'Prøv igen',
            chatContextLoading: 'Indlæser postens kontekst…',
            chatGuidance: {
                general: 'Forseti kan opsummere denne post, identificere manglende oplysninger og foreslå gennemgangstrin.',
                privacy: 'Forseti kan gennemgå privatlivsoplysninger, kontrollere forpligtelser i installerede regelsæt og hjælpe med at udarbejde et udkast til menneskelig gennemgang.',
                compliance: 'Forseti kan identificere compliance-mangler, forankre krav i installerede kilder og foreslå ansvarlige næste skridt.',
                legal: 'Forseti kan opsummere posten, finde dokumenteret kildemateriale og hjælpe med at udarbejde et udkast til juridisk gennemgang.',
                risk: 'Forseti kan vurdere registrerede risici, markere manglende fakta og foreslå forholdsmæssige gennemgangstrin.',
                audit: 'Forseti kan undersøge huller i evidensen, knytte posten til installerede kontroller og foreslå revisionsklare opfølgninger.',
            },
            starterPrompts: {
                identifyGaps: 'Hvilke oplysninger eller hvilken evidens mangler i denne post?',
                reviewFrameworks: 'Hvilke forpligtelser fra vores installerede regelsæt bør jeg gennemgå for denne post?',
                prepareDraft: 'Hjælp mig med at udarbejde et udkast udelukkende baseret på denne post og dokumenterede installerede kilder.',
                nextSteps: 'Hvilke gennemgangstrin bør jeg tage som de næste?',
                summarizeRecord: 'Opsummér denne post, og markér punkter, der kræver menneskelig gennemgang.',
                assessRisk: 'Vurder de registrerede risici, identificer manglende fakta, og foreslå gennemgangstrin.',
            },
            disclaimer: 'Forseti støtter din dømmekraft – den erstatter ikke autoriseret advokat.',
            recordType: {
                project: 'Projekt',
                dpia: 'DPIA',
                dossier_filing: 'Dossierarkivering',
                transfer_assessment: 'Grænseoverskridende overførsel',
                vendor: 'Sælger',
                vendor_contract: 'Leverandørkontrakt',
                vendor_acceptance: 'Leverandørrisikoaccept',
                incident: 'Hændelse',
                incident_op: 'Incident Operation',
                breach_notification: 'Underretning om brud',
                authority_request: 'Myndighedsanmodning',
                supplement_request: 'Tillægsanmodning',
                rights_request: 'Rettighedsanmodning',
                contract: 'Kontrakt',
                task: 'Opgave',
                data_mapping_activity: 'RoPA aktivitet',
                compliance_rule: 'Overholdelsesregel',
                program: 'Program',
                governance_document: 'Styringsdokument',
                remediation_plan: 'Afhjælpningsplan',
                cross_border_filing: 'Grænseoverskridende arkivering',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Bed Forseti om at udarbejde dette',
            drafting: 'Udarbejdelse...',
            hint_pick_activity: 'Vælg en tilknyttet behandlingsaktivitet for at aktivere Forseti-udkast.',
            draft_origin_label: 'Forseti-udkast — gennemgang pr. felt',
            tokens_label: '≈ {count} tokens',
            accept_selected: 'Accepter valgt',
            reject_all: 'Afvis udkast',
            field_checkbox_label: 'Ansøg Forseti-udkast til {field}',
            field_labels: {
                description: 'Beskrivelse',
                purpose: 'Formål',
                data_subjects: 'Datasubjekter',
                data_types: 'Datatyper',
                necessity_justification: 'Nødvendighed og proportionalitet',
                consultation_notes: 'Høringsnotater',
                dpo_opinion: 'DPO udtalelse',
                system_ids: 'Sammenkædede systemer',
                vendor_ids: 'Tilknyttede leverandører',
            },
            error_generic: 'Forseti kunne ikke udarbejde denne DPIA. Prøv igen, eller juster aktivitetsregistreringen.',
            variant_legend: 'Rammeramme',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Ingen installerede regelsæt er tilgængelige',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Bemærk: {count} konflikt(er) på tværs af rammer gælder for denne variant. Gennemgå før du accepterer.',
        },
        formDraft: {
            button_label: 'Bed Forseti om at udarbejde denne formular',
            drafting: 'Udarbejder…',
            draft_origin_label: 'Forseti-udkast — gennemgå hvert felt',
            tokens_label: '≈ {count} tokens fra din månedlige pulje',
            accept_selected: 'Accepter valgte',
            reject_all: 'Afvis udkast',
            field_checkbox_label: 'Anvend Forseti-udkast for {field}',
            excluded_note: '{count} felt(er) kan ikke udarbejdes (referencer, vedhæftninger eller skrivebeskyttet).',
            error_generic: 'Forseti kunne ikke udarbejde denne formular. Prøv igen.',
            empty_draft: 'Forseti udarbejdede ingen feltudkast til denne formular.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Bed Forseti om at udarbejde denne TIA',
            drafting: 'Udarbejder TIA...',
            hint_pick_destination: 'Indstil destinationslandet på overførselsposten, før Forseti kan udkast.',
            draft_origin_label: 'Forseti TIA-udkast — gennemgang pr. felt',
            tokens_label: '≈ {count} tokens',
            accept_selected: 'Accepter valgt',
            reject_all: 'Afvis udkast',
            field_checkbox_label: 'Ansøg Forseti-udkast til {field}',
            error_generic: 'Forseti kunne ikke udarbejde denne TIA. Prøv igen, eller juster overførselsposten.',
            usage_details: 'AI-brugsdetaljer',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Anbefalede posteringsopdateringer',
            mechanism_recommendation: 'Overførselsmekanisme: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Angiv en gennemgangsfrist, før du opdaterer denne følsomme overførsel.',
            processing_activity_warning: 'Tilknyt mindst én behandlingsaktivitet / ROPA-post, før du fuldfører TIA.',
            go_to_field: 'Gå til felt',
            adequacy_label: 'Destinationsregime',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Overgangs-/EØS-genvej',
            adequacy_status_unknown: 'Ukendt — indstil destination',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Bemærk: {count} konflikt(er) på tværs af rammer gælder for denne overførselsordning. Gennemgå før du accepterer.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Manglende påkrævede oplysninger',
            missing_facts_hint: 'Forseti markerede oplysninger, der ikke findes i posten. Felter, der afhænger af dem, er ikke forvalgt — angiv oplysningerne eller acceptér feltet bevidst.',
            risk_label: 'Kalibreret risiko',
            risk_low: 'Lav',
            risk_medium: 'Middel',
            risk_high: 'Høj',
            jurisdiction_label: 'Jurisdiktionsudløsere',
            jurisdiction_none_installed: 'Ingen installeret ramme dækker denne vurdering — der er ikke vurderet nogen retlig ordning. Installér en relevant reguleringspakke, før du forlader dig på de juridiske konklusioner i dette udkast.',
            jurisdiction_none_assessed: 'Forseti returnerede ingen jurisdiktionsvurdering. Intet blev vurderet — dette er ikke en konklusion om, at ingen ordning gælder.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Indlæser navnene på de vurderede ordninger…',
            trigger_applicable: 'Gælder',
            trigger_not_applicable: 'Ikke udløst',
            trigger_unknown: 'Ukendt — oplysning mangler',
            recipient_role_label: 'Modtagerens rolle',
            recipient_role_controller: 'Dataansvarlig',
            recipient_role_processor: 'Databehandler',
            recipient_role_joint_controller: 'Fælles dataansvarlig',
            recipient_role_unknown: 'Ukendt — ikke angivet',
            opt_in_hint: 'Juridisk konklusion — vælg kun til, hvis du accepterer den med de tilgængelige oplysninger.',
        },
        mcp: {
            pageTitle: 'MCP-stiknøgler',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Udsted ny nøgle',
            issueModalTitle: 'Udsted MCP-tasten API',
            labelField: 'Etiket',
            labelHint: 'En kort beskrivelse (f.eks. "AI kapacitetslaboratorium — regulatorisk forskning").',
            permittedToolsField: 'Tilladte værktøjer',
            expiresAtField: 'Udløber (valgfrit)',
            expiresAtHint: 'Lad det være tomt for aldrig udløber.',
            submit: 'Udsted nøgle',
            cancel: 'Annuller',
            keyDisplayedOnce: 'Kopier denne nøgle nu - du vil IKKE kunne se den igen. Gem det i din hemmelige manager (1Password, AWS Secrets Manager, Vault osv.).',
            keyDisplayedOnceClose: 'Jeg kopierede det - tæt på',
            colLabel: 'Etiket',
            colPrefix: 'Præfiks',
            colStatus: 'Status',
            colTools: 'Værktøjer',
            colCreatedAt: 'Oprettet',
            colLastUsed: 'Sidst brugt',
            colCalls: 'Opkald',
            colActions: 'Handlinger',
            statusActive: 'Aktiv',
            statusRevoked: 'Tilbagekaldt',
            statusExpired: 'Udløbet',
            revokeAction: 'Tilbagekald',
            rotateAction: 'Roter',
            confirmRevokeTitle: 'Vil du tilbagekalde denne MCP-nøgle?',
            confirmRevokeBody: 'Eksterne Claude-klienter, der bruger denne nøgle, mister adgang med det samme. Dette kan ikke fortrydes.',
            confirmRevoke: 'Tilbagekald nøgle',
            rotatedTitle: 'Nøglen drejet',
            rotatedBody: 'Den nye klartekstnøgle er nedenfor. Den gamle tast fortsætter med at virke i 5 minutter, så alle opkald under flyvningen afsluttes.',
            emptyState: 'Ingen MCP-nøgler udstedt endnu.',
            errorIssue: 'Kunne ikke udstede MCP-nøglen.',
            errorList: 'MCP-nøgler kunne ikke indlæses.',
            errorRevoke: 'Nøglen kunne ikke tilbagekaldes.',
            errorRotate: 'Kunne ikke dreje nøglen.',
            neverUsed: '—',
            neverExpires: 'Aldrig',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Forseti hukommelse',
            pageSubtitle: 'Gennemse og kurater den organisationsspecifikke kontekst, Forseti har akkumuleret på tværs af samtaler og beslutninger. Kurerede indgange låser mod fastholdelse om natten og forbliver tilgængelige for enhver person.',
            filterCategory: 'Kategori',
            filterAllCategories: 'Alle kategorier',
            totalCount: '{count} poster',
            colAnchor: 'Anker',
            colCategory: 'Kategori',
            colSource: 'Kilde',
            colContent: 'Indhold',
            colCreatedAt: 'Oprettet',
            colActions: 'Handlinger',
            editButton: 'Rediger',
            deleteButton: 'Slet',
            promoteButton: 'Fremme til kurateret',
            emptyState: 'Ingen minder endnu - Forseti vil akkumulere kontekst, mens du chatter.',
            categories: {
                framework_interpretation: 'Rammefortolkning',
                process_preference: 'Proces præference',
                escalation_threshold: 'Eskaleringstærskel',
                decision_context: 'Beslutningssammenhæng',
                glossary_term: 'Ordlistebegreb',
                auto_inferred: 'Auto-udledt',
            },
            sources: {
                admin_curated: 'Kurateret',
                user_explicit: 'Eksplicit',
                auto: 'Udledt',
            },
            deleteConfirmTitle: 'Vil du slette denne hukommelse?',
            deleteConfirmBody: 'Sletning vil fjerne dette anker fra enhver fremtidig Forseti-chatstart. Anker: {anchor}',
            deleteConfirmLabel: 'Slet',
            deleteCancelLabel: 'Annuller',
            editModalTitle: 'Rediger hukommelse',
            editAnchorLabel: 'Anker',
            editCategoryLabel: 'Kategori',
            editContentLabel: 'Indhold',
            editContentCount: '{n}/{max} tegn',
            editEmptyError: 'Indholdet må ikke være tomt.',
            editTooLongError: 'Indholdet skal være ≤1000 tegn.',
            editAutoPromoteHint: 'Hvis du gemmer denne automatisk udledte hukommelse, markeres den som brugerbekræftet og fjernes fra tilbageholdelsessweep.',
            editCancelButton: 'Annuller',
            editSaveButton: 'Gem',
            editSaving: 'Gemmer...',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Revisionsslægt',
            subtitle: 'Spor hver AI-genererede artefakt, der er knyttet til en post. Chats, værktøjsopkald, opgaver, bevisudkast og dokumentversioner.',
            filterForm: 'Filtre',
            recordType: 'Record type',
            recordId: 'Optag ID',
            dateStart: 'Fra',
            dateEnd: 'Til',
            search: 'Søg',
            loading: 'Indlæser...',
            exportPdf: 'Eksporter PDF',
            exportCsv: 'Eksporter CSV',
            nodesHeading: 'Linjeknudepunkter',
            colType: 'Type',
            colLabel: 'Etiket',
            colOccurredAt: 'Opstod kl',
            colActor: 'Skuespiller',
            colId: 'ID',
            noResults: 'Ingen rådgiveraktivitet for denne registrering.',
            draftBadge: 'AI-genereret kladde',
            prev: 'Forrige',
            next: 'Næste',
            viewToggleTable: 'Se som tabel',
            viewToggleGraph: 'Se som graf',
            graphAriaLabel: 'Afstamningsgraf (visuel). Brug tabelvisningen til hjælpemidler.',
            graphLegendDraft: 'Udkast til AI bevis',
            graphEmpty: 'Ingen noder til graf.',
            paginationAriaLabel: 'Sideinddeling',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Eksport mislykkedes: {message}',
            truncatedNotice: 'Denne afstamning er ikke komplet. Platformen læser højst et fast antal rækker fra hver kilde, og grænsen blev nået for: {sources}. Rækker ud over en grænse, hvis der er nogen, vises ikke. Indsnævr datointervallet for at få dem med.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Projekt',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Sælger',
                incident: 'Hændelse',
                incident_op: 'Incident operation',
                rights_request: 'Rettighedsanmodning',
                contract: 'Kontrakt',
                task: 'Opgave',
                data_mapping_activity: 'Datakortlægningsaktivitet',
                compliance_rule: 'Overholdelsesregel',
                program: 'Program',
            },
            nodeTypes: {
                chat: 'Chat',
                tool_call: 'Værktøjskald',
                workflow_run: 'Kørsel af arbejdsgang',
                task: 'Opgave',
                evidence: 'Beviser',
                document_version: 'Dokumentversion',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'OK',
                drifted: 'Driftede',
                hash_mismatch: 'Hash uoverensstemmelse',
                merkle_mismatch: 'Merkle misforhold',
                source_missing: 'Kilde mangler',
                not_found: 'Ikke fundet',
                chain_unreachable: 'Kæde uopnåelig',
                service_unreachable: 'Service uopnåelig',
                pending: 'Afventer forankring',
            },
        },
        evidenceDraft: {
            draftBadge: 'AI-genereret KLADDE',
            pendingReviewBadge: 'Afventer gennemgang',
            approvedBadge: 'Godkendt',
            rejectedBadge: 'Afvist',
            moveToReviewButton: 'Markér klar til anmeldelse',
            approveButton: 'Godkend',
            rejectButton: 'Afvis',
            rejectReasonLabel: 'Grund til afvisning',
            humanSignoffBanner: 'Forseti støtter din dømmekraft – den erstatter ikke autoriseret advokat. AI-genererede beviser kræver menneskelig godkendelse, før det tæller som formelt bevis.',
            pageTitle: 'AI bevisudkast',
            pageSubtitle: 'Gennemgå, godkend eller afvis AI-genererede beviser. Udkast skal godkendes af et menneske, før de tæller som formelt bevis.',
            filterAll: 'Alle handlingsrettede',
            filterDrafts: 'Kun udkast',
            filterPending: 'Afventer gennemgang',
            filterApproved: 'For nylig godkendt',
            filterRejected: 'For nylig afvist',
            colId: 'Bevis ID',
            colStatus: 'Status',
            colSource: 'Kilde',
            colCreatedAt: 'Oprettet',
            colActions: 'Handlinger',
            empty: 'Ingen AI bevisudkast i denne status.',
            confirmApprove: 'Vil du godkende dette AI-genererede bevis som formelt?',
            confirmReject: 'Afvis dette bevisudkast. Dette kan ikke fortrydes.',
            reasonRequired: 'Der kræves en begrundelse for afvisning.',
            rejectModalTitle: 'Afvis udkast til bevis',
            approveModalTitle: 'Godkend udkast til bevis',
            operationFailed: 'Handlingen mislykkedes. Prøv venligst igen.',
            moveToReviewSuccess: 'Markeret klar til gennemgang.',
            approveSuccess: 'Godkendt.',
            rejectSuccess: 'Afvist.',
            sourceLabelWorkflowRunTitle: 'Arbejdsgang: {title}',
            sourceLabelWorkflowRunId: 'Workflow-kørsel {id}',
            sourceLabelChatTitle: 'Chat: {title}',
            sourceLabelChatId: 'Forseti chat {id}',
            sourceLabelEmpty: '—',
            filtersAriaLabel: 'Filterstatus',
        },
        workflows: {
            pageTitle: 'AI-arbejdsgange',
            pageSubtitle: 'Gemte AI-promptskabeloner, der producerer opgaver og udkast til evidens.',
            newWorkflow: 'Ny arbejdsgang',
            newWorkflowHeading: 'Ny AI-arbejdsgang',
            tabAll: 'Alle',
            tabBuiltin: 'Indbygget pakke',
            tabMine: 'Mine arbejdsgange',
            tabDetail: 'Detaljer',
            tabLinkedTasks: 'Tilknyttede opgaver',
            builtinBadge: 'Indbygget',
            builtinSubtitle: 'Indbygget arbejdsgang.',
            userSubtitle: 'Tilpasset arbejdsgang.',
            emptyHint: 'Ingen arbejdsgange endnu.',
            loadTimeoutError: 'Kunne ikke indlæse arbejdsgange efter 10 sekunder. Prøv igen, eller tjek browserens netværksfane for en hængende anmodning.',
            loadError: 'Kunne ikke indlæse arbejdsgange.',
            hideError: 'Skjul mislykkedes.',
            unhideError: 'Vis mislykkedes.',
            retry: 'Prøv igen',
            producesTasks: 'Producerer opgaver',
            producesEvidence: 'Producerer udkast til evidens',
            hideButton: 'Skjul fra min liste',
            unhideButton: 'Vis igen',
            runButton: 'Kør',
            editButton: 'Rediger',
            shareButton: 'Del',
            backToList: 'Tilbage til listen',
            requireTitleAndPrompt: 'Titel og prompt er påkrævet.',
            fieldTitle: 'Titel',
            fieldDescription: 'Beskrivelse',
            fieldType: 'Type',
            fieldPersona: 'Personaaffinitet',
            fieldPractice: 'Praksis',
            fieldPrompt: 'Spørg (Markdown)',
            fieldProducesTasks: 'Producerer opgaver',
            fieldProducesEvidence: 'Producerer udkast til evidens',
            fieldProjectId: 'Projekt (valgfrit)',
            fieldAttachmentIds: 'Vedhæftninger',
            fieldParentRecord: 'Overordnet post (valgfrit)',
            fieldPersonaOverride: 'Personatilsidesættelse (valgfrit)',
            typeAssistant: 'Assistent',
            typeTabular: 'Tabel',
            personaNone: '— ingen —',
            parentNone: '— ingen —',
            parentIdPlaceholder: 'Overordnet post-ID',
            selectProjectPlaceholder: 'Vælg et projekt⬦',
            selectProjectFirst: 'Vælg et projekt for at vælge vedhæftninger.',
            selectAttachmentsPlaceholder: 'Vælg dokumenter⬦',
            noAttachmentsInProject: 'Dette projekt har ingen vedhæftede dokumenter.',
            selectParentRecordPlaceholder: 'Vælg en post⬦',
            selectParentRecordFirst: 'Vælg først en posttype.',
            noRecordsForType: 'Ingen poster af denne type er tilgængelige.',
            shareUserSelectorLabel: 'Modtagere (brugere)',
            shareGroupSelectorLabel: 'Modtagere (organisationsgrupper)',
            selectUsersPlaceholder: 'Vælg brugere⬦',
            selectOrgGroupsPlaceholder: 'Vælg organisationsgrupper⬦',
            loadingOptions: 'Indlæser⬦',
            loadFailed: 'Kunne ikke indlæse muligheder.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Leverandør',
                incident: 'Hændelse',
                rights_request: 'Rettighedsanmodning',
                contract: 'Kontrakt',
                data_mapping_activity: 'RoPA-aktivitet',
                compliance_rule: 'Compliance-regel',
                program: 'Program',
            },
            cancelButton: 'Annuller',
            closeButton: 'Luk',
            createButton: 'Opret',
            creating: 'Opretter⬦',
            saveButton: 'Gem',
            saving: 'Gemmer ...',
            shareWorkflowHeading: 'Del arbejdsgang',
            shareIntro: 'Deler: {title}',
            shareUserIds: "Bruger-id'er (adskilt med mellemrum eller komma)",
            shareGroupIds: "Organisationsgruppe-id'er (adskilt med mellemrum eller komma)",
            shareAllowEdit: 'Tillad modtagere at redigere',
            shareRequireRecipient: 'Angiv mindst ét bruger-id eller gruppe-id.',
            shareSuccess: 'Delt.',
            sharing: 'Deler⬦',
            runWorkflowHeading: 'Kør: {title}',
            runStarting: 'Starter⬦',
            runStatusLabel: 'Status: {status}',
            runProducedCounts: 'Opgaver: {tasks} · Udkast til evidens: {evidence} · Vedhæftninger: {attachments}',
            runPanelHeading: 'Arbejdsgangskørsel',
            runPanelHeadingWithTitle: 'Arbejdsgangskørsel: {title}',
            runViewTasks: 'Se genererede opgaver',
            runViewEvidence: 'Se udkast til evidens',
            runResultHeading: 'Rådgiverresultat',
            runNoResult: 'Kørslen sluttede, men returnerede ikke et resultat, der kan gennemgås. Der blev ikke oprettet opgaver, evidens eller vedhæftninger. Prøv igen med den påkrævede post eller de påkrævede dokumenter valgt.',
            runCitationWarning: 'Nogle citater kunne ikke verificeres automatisk. Gennemgå dem, før du forlader dig på dette resultat.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'Arbejdsgangskørslen kunne ikke indlæses.',
            runStreamFailed: 'Forbindelsen til arbejdsgangens status mislykkedes.',
            runStatusValues: {
                pending: 'Afventer',
                running: 'Kører',
                completed: 'Fuldført',
                succeeded: 'Lykkedes',
                failed: 'Mislykkedes',
                cancelled: 'Annulleret',
            },
            runProgressStatus: 'Status: {status}',
            runProgressTaskCreated: 'Opgave oprettet.',
            runProgressEvidenceDrafted: 'Udkast til evidens oprettet.',
            runProgressAttachmentGenerated: 'Vedhæftning genereret.',
            guidedWorkflowTitle: 'Guidet arbejdsgang for {recordType}',
            guidedWorkflowDescription: 'Oprettet til poster af typen {recordType}. Gennemgå og tilpas arbejdsgangen, før du gemmer den.',
            guidedWorkflowPrompt: 'Gennemgå den angivne post af typen {recordType}, identificer manglende fakta, og foreslå dokumenterede næste skridt til menneskelig gennemgang.',
            promptHeading: 'Spørg',
            howItWorksHeading: 'Hvad der sker, når du kører denne arbejdsgang',
            builtinGuidance: 'Forseti gennemgår de poster og dokumenter, du vælger, returnerer et resultat, der kan gennemgås af operatøren, og opretter kun opgaver eller udkast til evidens, når resultatet understøtter det. Intet godkendes eller indsendes automatisk.',
            editWorkflowHeading: 'Rediger arbejdsgang',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Arbejdsgang ikke fundet.',
            schedules: {
                tabTitle: 'Tidsplaner',
                sectionHeading: 'Planlagte kørsler',
                empty: 'Ingen tidsplaner endnu. Opret en for at køre denne arbejdsgang automatisk.',
                addButton: 'Tilføj tidsplan',
                addHeading: 'Planlæg arbejdsgang',
                editHeading: 'Rediger tidsplan',
                fieldFrequency: 'Frekvens',
                fieldDayOfWeek: 'Ugedag',
                fieldDayOfMonth: 'Dag i måneden',
                fieldTime: 'Tidspunkt (UTC)',
                fieldTimezone: 'Tidszone',
                fieldProject: 'Projekt (valgfrit)',
                fieldAttachments: 'Standardvedhæftninger',
                fieldPersonaOverride: 'Personatilsidesættelse (valgfrit)',
                frequencyDaily: 'Daglig',
                frequencyWeekly: 'Ugentlig',
                frequencyMonthly: 'Månedlig',
                weekdaySun: 'Søndag',
                weekdayMon: 'Mandag',
                weekdayTue: 'Tirsdag',
                weekdayWed: 'Onsdag',
                weekdayThu: 'Torsdag',
                weekdayFri: 'Fredag',
                weekdaySat: 'Lørdag',
                statusActive: 'Aktiv',
                statusPaused: 'På pause',
                nextRunLabel: 'Næste kørsel',
                lastRunLabel: 'Sidste kørsel',
                pauseButton: 'Pause',
                resumeButton: 'Genoptag',
                deleteButton: 'Slet',
                triggerButton: 'Kør nu',
                savingButton: 'Gemmer⬦',
                saveButton: 'Gem',
                triggered: 'Kørsel startet — tjek kørselshistorikken.',
            },
            branching: {
                helpHeading: 'Betinget prompt-syntaks',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Konflikter på tværs af rammer',
            pageSubtitle: 'Udvalgt katalog over, hvor dine installerede reguleringspakker interagerer på modstridende måder. Forseti overflader; mennesker bestemmer.',
            noInstalledPacks: 'Ingen regulatoriske pakker installeret for denne organisation – installer mindst to pakker for at løse konflikter på tværs af rammer.',
            noConflicts: 'Ingen konflikter matcher de aktuelle filtre.',
            allAcknowledged: 'Enhver synlig konflikt er blevet anerkendt. Godt arbejde.',
            columnSeverity: 'Sværhedsgrad',
            columnTitle: 'Titel',
            columnFrameworks: 'Rammer',
            columnTopic: 'Emne',
            columnStatus: 'Status',
            filterFrameworkPlaceholder: 'Alle rammer',
            filterTopicPlaceholder: 'Alle emner',
            filterSeverityPlaceholder: 'Alle sværhedsgrader',
            filterStatusPlaceholder: 'Alle statusser',
            filterStatusAcknowledged: 'Anerkendt',
            filterStatusUnacknowledged: 'Ikke anerkendt',
            severityCritical: 'Kritisk',
            severityHigh: 'Høj',
            severityMedium: 'Medium',
            severityLow: 'Lav',
            statusAcknowledged: 'Anerkendt',
            statusInReview: 'I anmeldelse',
            statusResolved: 'Løst',
            statusWontFix: 'Vil ikke ordne',
            statusUnacknowledged: 'Ikke anerkendt',
            drawerSummaryHeading: 'Resumé',
            drawerSideAHeading: 'Side A',
            drawerSideBHeading: 'Side B',
            drawerRequirementsLabel: 'Anførte krav',
            drawerProvisionsLabel: 'Citerede bestemmelser',
            drawerSuggestedResolutionHeading: 'Foreslået opløsning',
            drawerLegalCounselBadge: 'Juridisk gennemgang påkrævet',
            drawerAckHeading: 'Anerkendelse',
            drawerAckStatusLabel: 'Status',
            drawerAckAssigneeLabel: 'Tildelt til',
            drawerAckDueDateLabel: 'Forfaldsdato',
            drawerAckNotesLabel: 'Noter',
            drawerAckButtonAcknowledge: 'Anerkend',
            drawerAckButtonUpdate: 'Opdatering',
            drawerAckButtonUnacknowledge: 'Afbekræft',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Søg i titel eller oversigt...',
            searchButton: 'Søg',
            searchButtonAriaLabel: 'Kør konfliktsøgning',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Eksport mislykkedes: {message}',
            exportAriaLabel: 'Eksportkonflikter',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Tabulariske gennemgange',
            pageSubtitle: 'Sammenlign N dokumenter på tværs af M attributter med begrundede citater.',
            newReview: 'Ny gennemgang',
            newReviewHeading: 'Ny tabularisk gennemgang',
            emptyHint: 'Ingen tabulariske gennemgange endnu.',
            docsLabel: '{n} dokumenter',
            colsLabel: '{n} kolonner',
            detailSubtitle: '{docs} dokumenter · {cols} kolonner',
            addColumn: 'Tilføj kolonne',
            addColumnHeading: 'Tilføj kolonne',
            columnInfo: 'Vis prompt og format',
            columnInfoAria: 'Vis prompt og format for kolonnen {name}',
            columnInfoPrompt: 'Prompt',
            columnInfoFormat: 'Returformat',
            columnInfoPresets: 'Tilladte værdier',
            columnInfoCitations: 'Kildehenvisninger',
            columnInfoCitationsRequired: 'Påkrævet',
            removeColumn: 'Fjern kolonne',
            removeColumnAria: 'Fjern kolonne {name}',
            removeColumnConfirm: 'Fjern kolonnen "{name}"? Dens udtrukne celler slettes.',
            deleteReview: 'Slet',
            deleteReviewAria: 'Slet gennemgang {title}',
            deleteReviewConfirm: 'Slet gennemgangen "{title}"? Dette fjerner permanent dens kolonner, celler og diskussioner.',
            runAll: 'Kør alle',
            running: 'Kører⬦',
            cancel: 'Annuller',
            exportXlsx: 'Eksportér Excel',
            exportCsv: 'Eksportér CSV',
            exporting: 'Eksporterer⬦',
            backToList: 'Tilbage til listen',
            notFound: 'Tabularisk gennemgang ikke fundet.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Vedhæft dokumenter for at starte gennemgangen.',
            noColumns: 'Tilføj kolonner for at definere, hvad der skal udtrækkes.',
            tableAriaLabel: 'Udtrækningsgitter',
            threadsCountAria: '{n} diskussionsindlæg',
            startDiscussionAria: 'Start diskussion',
            docColumn: 'Dokument',
            statusPending: 'Afventer',
            statusRunning: 'Kører⬦',
            statusFailed: 'Mislykkedes: {msg}',
            statusCancelled: 'Annulleret',
            citationsCount: '{n} citation(er)',
            citationsView: 'Vis kildepassager',
            citationPage: 'Side {n}',
            titleRequired: 'Titel er påkrævet.',
            fieldTitle: 'Titel',
            fieldProjectId: 'Projekt (valgfrit)',
            fieldAttachmentIds: 'Vedhæftninger',
            fieldColumnName: 'Kolonnenavn',
            fieldColumnNameHelp: 'En kort, beskrivende etiket for denne kolonne i udtrækningsgitteret.',
            columnNamePlaceholder: 'f.eks. Kontraktstartdato',
            fieldExtractionPrompt: 'Udtrækningsprompt',
            fieldExtractionPromptHelp: 'Beskriv de oplysninger, du vil udtrække. Vær specifik for at få bedre resultater.',
            columnPromptPlaceholder: 'Hvad skal Forseti lede efter i dokumenterne?',
            fieldReturnFormat: 'Returformat',
            fieldReturnFormatHelp: 'Styrer, hvordan AIen formaterer sit svar (f.eks. som en dato eller et tal).',
            fieldPresets: 'Tilladte værdier (én pr. linje)',
            fieldPresetsPlaceholder: 'ja\nnej\ndelvist',
            fieldCitationsRequired: 'Citation påkrævet',
            formatShortText: 'Kort tekst',
            formatLongText: 'Lang tekst',
            formatEnum: 'Enum',
            formatNumber: 'Tal',
            formatDate: 'Dato',
            formatList: 'Liste',
            columnRequireFields: 'Navn og prompt er påkrævet.',
            addColumnError: 'Kunne ikke tilføje kolonne.',
            cancelButton: 'Annuller',
            createButton: 'Opret',
            creating: 'Opretter⬦',
            addButton: 'Tilføj',
            adding: 'Tilføjer⬦',
            runConfirmHeading: 'Kør udtrækning',
            estimateBody: 'Estimeret ~{units} fakturerbare enheder ({confidence} tillid).',
            starting: 'Starter⬦',
            confirmRun: 'Bekræft kørsel',
            charts: {
                title: 'Kolonnegrafer',
                tabsAria: 'Grafkolonner',
                enumAria: 'Fordeling af enum-værdier',
                numberAria: 'Histogram over talfordeling',
                dateAria: 'Datotidslinje',
                enumEmpty: 'Ingen fuldførte celler.',
                numberEmpty: 'Ingen numeriske værdier.',
                numberExcluded: '{n} celle(r) ekskluderet (tomme / ikke-numeriske / ikke fuldførte).',
                dateEmpty: 'Ingen datoværdier.',
                dateExcluded: '{n} celle(r) ekskluderet.',
                unsupportedFormat: 'Ikke-understøttet kolonneformat.',
            },
            threads: {
                drawerTitle: 'Diskussion',
                loading: 'Indlæser⬦',
                empty: 'Ingen indlæg endnu. Start samtalen.',
                composerLabel: 'Ny kommentar',
                composerPlaceholder: 'Tilføj en kommentar⬦',
                post: 'Send',
                posting: 'Sender⬦',
                edit: 'Rediger',
                editLabel: 'Rediger indlæg',
                delete: 'Slet',
                save: 'Gem',
                cancel: 'Annuller',
                close: 'Luk',
                editedBadge: 'redigeret',
                loadError: 'Kunne ikke indlæse tråden',
                postError: 'Kunne ikke sende',
                editError: 'Kunne ikke redigere',
                deleteError: 'Kunne ikke slette',
            },
            compare: {
                pageTitle: 'Sammenlign gennemgange',
                leftReviewLabel: 'Venstre gennemgang',
                rightReviewLabel: 'Højre gennemgang',
                pickReview: 'Vælg en gennemgang at sammenligne med',
                noSharedDocuments: 'Disse gennemgange deler ingen dokumenter — sammenligning kræver mindst én fælles vedhæftning.',
                sharedDocumentsHeading: 'Delte dokumenter',
                leftOnlyHeading: 'Kolonner kun i venstre gennemgang',
                rightOnlyHeading: 'Kolonner kun i højre gennemgang',
                differs: 'Adskiller sig',
                identical: 'Identisk',
                emptyValue: '—',
                compareButton: 'Sammenlign med⬦',
                documentHeading: 'Dokument',
                errorMissingIds: "Både venstre og højre gennemgangs-id'er er påkrævet.",
                errorComparisonFailed: 'Sammenligning mislykkedes.',
                errorLoadFailed: 'Kunne ikke indlæse gennemgange.',
                pickerLoading: 'Indlæser⬦',
                pickerNoOtherReviews: 'Ingen andre gennemgange tilgængelige til sammenligning.',
                pickerCancel: 'Annuller',
            },
        },
        documentReview: {
            pageTitle: 'Dokumentgennemgang',
            pageSubtitle: 'Accepter eller afvis AI-foreslåede redigeringer af dette dokument.',
            sidePanelLabel: 'AI røde linjer',
            sidePanelHeading: 'AI røde linjer',
            noPendingEdits: 'Ingen afventende redigeringer.',
            pendingCount: '{count} afventer',
            acceptAllButton: 'Accepter alle',
            rejectAllButton: 'Afvis alle',
            acceptAllTitle: 'Vil du acceptere alle afventende redigeringer?',
            acceptAllConfirm: 'Dette vil acceptere {count} afventende redigeringer og oprette en eller flere nye versioner. Fortsætte?',
            editsHeading: 'Redigeringer',
            editsEmpty: 'Ingen redigeringer at gennemgå.',
            versionsHeading: 'Versionshistorik',
            versionsEmpty: 'Ingen versioner endnu.',
            editLabel: 'Rediger',
            editLabelWithId: 'Rediger #{id}',
            acceptButton: 'Accepter',
            rejectButton: 'Afvis',
            accepting: 'Accepterer...',
            rejecting: 'Afviser...',
            diffDeletedAria: 'Slettet',
            diffInsertedAria: 'Indsat',
            statusPending: 'Afventer',
            statusAccepted: 'Accepteret',
            statusRejected: 'Afvist',
            versionLabel: 'version',
            versionAriaLabel: 'Version {n}',
            versionAriaLabelCurrent: 'Version {n} ({current})',
            versionCurrentTag: 'nuværende',
            versionSourceLabel: 'Kilde',
            versionCreatedLabel: 'Oprettet',
            versionModelLabel: 'Model',
            sourceUpload: 'Upload',
            sourceUserUpload: 'Bruger upload',
            sourceAssistantEdit: 'Assistent-redigering',
            sourceUserAccept: 'Bruger accepteret redigering',
            sourceUserReject: 'Bruger afviste redigering',
            sourceGenerated: 'Genereret',
            extractionExtracted: 'Tekst udtrukket',
            extractionFailed: 'Tekstudtrækning mislykkedes',
            extractionUnsupported: 'Teksten kunne ikke udtrækkes',
            extractionPages: '{n} sider',
            extractionWords: '{n} ord',
            extractionReasonScanned: 'Dette ligner et scannet dokument eller et dokument med kun billeder. Forseti kan ikke læse det endnu — upload en tekstbaseret fil, eller indsæt teksten direkte.',
            extractionReasonEncrypted: 'Filen er adgangskodebeskyttet. Fjern beskyttelsen, og upload den igen.',
            extractionReasonMalformed: 'Filen kunne ikke parses og er muligvis beskadiget.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'Filen er for stor til at blive behandlet.',
            extractionReasonTimeout: 'Behandlingen tog for lang tid. Prøv en mindre eller enklere fil.',
            extractionReasonUnknown: 'Der er ingen yderligere detaljer tilgængelige.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Dokumenttekst',
            textPaneTitle: 'Dokumenttekst',
            textPaneVersion: 'Version {n}',
            textPaneEmpty: 'Der er ingen tekst tilgængelig for denne version. Scannede PDF-filer og visse dokumenttyper har ingen tekst, der kan udtrækkes.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Dokumentgennemgang',
            pageTitle: 'Dokumentgennemgang',
            pageSubtitle: 'Dokumenter i din organisation, der har AI-foreslåede redigeringer afventende gennemgang eller Forseti-baserede versioner.',
            colDocument: 'Dokument',
            colLinkedRecord: 'Tilknyttet post',
            colPendingEdits: 'Afventende redigeringer',
            colLastActivity: 'Seneste aktivitet',
            colOpen: 'Åbn',
            open: 'Åbn',
            empty: 'Ingen dokumenter under Forseti-gennemgang endnu. Dokumenter kommer i denne kø, når Forseti foreslår en redigering under en chat, når en AI-arbejdsgang producerer en redline, eller når du sender et dokument til gennemgang via knappen ovenfor.',
            loading: 'Indlæser⬦',
            loadError: 'Kunne ikke indlæse dokumenter under gennemgang.',
            retryButton: 'Prøv igen',
            noLinkedRecord: '—',
            pendingZero: 'Ingen',
            sendForReviewButton: 'Send et dokument til gennemgang',
            sendForReviewTitle: 'Send et dokument til gennemgang',
            sendForReviewSubtitle: 'Upload et .txt-, .md-, .pdf- eller .docx-dokument (op til 50 MB). Det føjes til gennemgangskøen, og Forseti kører en redline-gennemgang på det.',
            sendForReviewTabUpload: 'Upload nyt',
            sendForReviewPickerLabel: 'Søg vedhæftninger',
            sendForReviewPickerPlaceholder: 'Skriv for at søge⬦',
            sendForReviewPickerEmpty: 'Ingen matchende vedhæftninger. Prøv en anden søgning.',
            sendForReviewSubmit: 'Send til gennemgang',
            sendForReviewCancel: 'Annuller',
            sendForReviewSubmitting: 'Sender⬦',
            sendForReviewError: 'Kunne ikke sende dokumentet til gennemgang. Prøv igen.',
            sendForReviewSuccess: 'Dokument sendt til gennemgang.',
            sendForReviewAcceptedExt: 'Accepteret: {ext}.',
            sendForReviewMaxSize: 'Maks. størrelse {mb} MB.',
        },
        chats: {
            exportError: 'Eksport mislykkedes.',
        },
    },
    pdplEvidencePacks: {
        title: 'PDPL-bevismaterialepakker',
        subtitle: 'Kundestyret indsendelse til MoPS A05',
        nav: { emailProviders: 'E-mail-udbydere' },
        providers: {
            pageTitle: 'E-mail-udbydere',
            pageSubtitle: 'Konfigurer din organisations udgående mailudbyder til indsendelse af PDPL-bevismaterialepakker til MoPS A05.',
            architectureRuleHeading: 'Arkitekturregel (låst)',
            architectureRuleBody: 'AesirX er ikke afsender af bevismaterialemails. Hver indsendelse bruger en udbyder, du selv ejer og kontrollerer. AesirX sender aldrig på dine vegne.',
            empty: 'Ingen udbydere konfigureret endnu.',
            createCta: 'Opret udbyder',
            statusPendingConsent: 'Afventer samtykke',
            statusPendingConnectionCheck: 'Afventer forbindelsestjek',
            statusActive: 'Aktiv',
            statusConnectionCheckFailed: 'Forbindelsestjek mislykkedes',
            statusDisabled: 'Deaktiveret',
            acceptConsentCta: 'Acceptér samtykke',
            runConnectionCheckCta: 'Kør forbindelsestjek',
            disableCta: 'Deaktivér',
            disableConfirm: 'Deaktivering af denne udbyder standser direkte afsendelser, indtil en ny udbyder er konfigureret og samtykket er bekræftet igen. Fortsæt?',
            columnDisplayName: 'Navn',
            columnFromAddress: 'Afsenderadresse',
            columnStatus: 'Status',
            columnLastCheck: 'Seneste tjek',
            columnActions: 'Handlinger',
            createModalTitle: 'Ny SMTP-udbyder',
            createDisplayNameLabel: 'Visningsnavn',
            createFromAddressLabel: 'Afsenderadresse',
            createReplyToLabel: 'Svaradresse (valgfri)',
            createSmtpHostLabel: 'SMTP-vært',
            createSmtpPortLabel: 'SMTP-port',
            createSmtpUsernameLabel: 'SMTP-brugernavn',
            createSmtpPasswordLabel: 'SMTP-adgangskode',
            createSmtpEncryptionLabel: 'Kryptering',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implicit)',
            createSmtpEncryptionNone: 'Ingen',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Kommer snart',
            createOauthDisabledHint: 'OAuth-udbydere er endnu ikke aktiveret. Brug SMTP indtil videre.',
            createSubmitCta: 'Opret udbyder',
            createCancelCta: 'Annullér',
            createErrorInvalidFromAddress: 'Afsenderadressen skal være en gyldig e-mailpostkasse.',
            createErrorDuplicateFromAddress: 'En anden udbyder er allerede konfigureret med denne afsenderadresse.',
            createErrorGeneric: 'Udbyderen kunne ikke oprettes. Gennemgå formularen og prøv igen.',
            consentModalTitle: 'Bekræft arkitekturregel',
            consentLocaleToggleEn: 'Engelsk',
            consentLocaleToggleVi: 'Vietnamesisk',
            consentAcknowledgeCta: 'Jeg bekræfter — accepter samtykke',
            consentCancelCta: 'Annullér',
            consentErrorMismatch: 'Samtykketeksten blev ændret og matcher ikke længere den kanoniske disclaimer. Åbn dialogen igen og send på ny.',
            testModalTitle: 'Forbindelsestjek',
            testRunningCta: 'Tester forbindelse…',
            testRunCta: 'Kør forbindelsestjek',
            testRetryCta: 'Prøv forbindelsestjek igen',
            testSuccessHeading: 'Forbindelse oprettet',
            testSuccessBody: 'Godkendt mod {host}:{port} på {latencyMs} ms. Udbyderen er klar til direkte afsendelser.',
            testFailureHeading: 'Forbindelse mislykkedes',
            testFailureBody: 'Tjekket kunne ikke gennemføres. Gennemgå årsagen nedenfor og prøv igen, når den er rettet.',
            testFailureReasonLabel: 'Fejlårsag',
            testCloseCta: 'Luk',
            detailDrawerTitle: 'Udbyderdetaljer',
            detailDrawerHistoryHeading: 'Historik for forbindelsestjek',
            detailDrawerConsentHeading: 'Samtykkebekræftelse',
            detailDrawerNoHistory: 'Ingen forbindelsestjek registreret endnu.',
            detailDrawerNoConsent: 'Samtykke ikke bekræftet endnu.',
            outcomeSuccess: 'Vellykket',
            outcomeFailure: 'Mislykket',
        },
        builder: {
            pageTitle: 'Saml bevismaterialepakke',
            pageSubtitle: 'Upload de 6 delfiler, vælg pakketype, og saml den deterministiske 8-dele ZIP til indsendelse.',
            packTypeLabel: 'Pakketype',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Del 01 — Følgebrev (PDF)',
            part02AuthorityForm: 'Del 02 — Myndighedsformular (PDF)',
            part03Supporting: 'Del 03 — Understøttende bevismateriale (≥1)',
            part04Safeguards: 'Del 04 — Beskyttelsesforanstaltninger',
            part04SafeguardsRequiredOtia: '(påkrævet for OTIA)',
            part05PriorCorrespondence: 'Del 05 — Tidligere korrespondance (valgfri)',
            part06Signature: 'Del 06 — Underskrift + stempelbevis (≥1)',
            uploadPickFile: 'Vælg fil',
            uploadPickFiles: 'Vælg filer',
            uploadRemoveFile: 'Fjern',
            requiredMissing: 'Påkrævet del mangler',
            assembleCta: 'Saml pakke',
            assembling: 'Samler…',
            assembleSuccessHeading: 'Pakke samlet',
            assembleSuccessSummary: '{fileCount} filer · {bytes} bytes · SHA-256 {shaPrefix}…',
            composeNextCta: 'Skriv e-mailudkast',
            warningsHeading: 'Valideringsadvarsler',
            assembleError: 'Pakken kunne ikke samles. Gennemgå dataene og prøv igen.',
            forsetiAssistHeading: 'Forseti AI-assistance (valgfri)',
            forsetiAssistHint: 'Kør PDPL Evidence Pack Assembly Advisor for at fremhæve manglende dele, udarbejde forsidebrevsprosa og markere sikkerhedshuller. AesirX er ikke afsenderen — AI-en rådgiver kun; du godkender hvert artefakt.',
            forsetiAssistCta: 'Åbn Forseti workflow-bibliotek',
            forsetiAiRunIdLabel: 'Tilknyttet Forseti AI-kørsels-id (indsæt for at registrere AI-assistance)',
        },
        draft: {
            pageTitle: 'E-mailudkast',
            pageSubtitle: 'Skriv og rediger e-mailudkastet. Direkte afsendelse kommer i en senere udgivelse.',
            composeCta: 'Skriv udkast',
            composing: 'Skriver…',
            noDraftYet: 'Intet udkast endnu. Skriv et for at komme i gang.',
            localeToggleLabel: 'Sprog',
            localeEn: 'Engelsk',
            localeVi: 'Vietnamesisk',
            subjectLabel: 'Emne',
            bodyLabel: 'Brødtekst',
            saveCta: 'Gem ændringer',
            saving: 'Gemmer…',
            saveError: 'Udkastet kunne ikke gemmes. Prøv venligst igen.',
            manifestSummaryLabel: 'Manifest-oversigt',
            coverLetterLabel: 'Følgebrev (PDF)',
            coverLetterDownloadCta: 'Hent PDF',
            editHistoryHeading: 'Redigeringshistorik',
            editHistoryEmpty: 'Ingen redigeringer endnu.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 uofficiel kanal-besked',
            uploadSentProofCta: 'Upload manuel afsendelsesbevis',
            versionLabel: 'Version',
            statusDraft: 'Udkast',
            statusEdited: 'Redigeret',
            statusFinalized: 'Færdiggjort',
        },
        sentProof: {
            modalTitle: 'Upload manuel afsendelsesbevis',
            architectureLockNote: 'AesirX er ikke afsenderen. Dette bevis fanger den manuelle afsendelse, du udførte via dit eget mailprogram.',
            fileLabel: 'Bevisfil (.eml, .png, .jpg)',
            envelopeFromLabel: 'Fra',
            envelopeToLabel: 'Til',
            envelopeSubjectLabel: 'Emne (ved afsendelse)',
            envelopeSentAtLabel: 'Afsendt kl.',
            envelopeAttachmentShaLabel: 'Vedhæftet SHA-256 (ved afsendelse)',
            uploadCta: 'Upload bevis',
            uploading: 'Uploader…',
            uploadSuccess: 'Afsendelsesbevis uploadet.',
            uploadErrorInvalidMime: 'Filtype ikke tilladt. Brug .eml, .png, .jpg eller .jpeg.',
            uploadErrorGeneric: 'Beviset kunne ikke uploades. Prøv venligst igen.',
            existingProofsHeading: 'Registrerede afsendelsesbeviser',
            existingProofsEmpty: 'Endnu ingen afsendelsesbeviser registreret.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Bevismaterialepakke',
            notFoundMessage: 'Pakken blev ikke fundet — den kan være fjernet eller linket kan være forkert.',
            manifestHeading: 'Manifest',
            dossierLabel: 'Sag',
            packShaLabel: 'Pakke SHA-256',
            fileCountLabel: 'Filer',
            bytesLabel: 'Størrelse',
            composeDraftCta: 'Skriv udkast',
            sendComingSoonCta: 'Direkte afsendelse: kommer snart',
            sendComingSoonHint: 'Det direkte afsendelsesflow er under aktiv udvikling. Brug "Upload manuel afsendelsesbevis" på udkastssiden, indtil det leveres.',
            sendCta: 'Send via konfigureret udbyder',
            sendDisabledNoProviderHint: 'Konfigurér først en kundestyret e-mailudbyder. AesirX sender aldrig på dine vegne.',
            timelineHeading: 'Afsendelseshistorik',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Send bevismaterialepakke',
            providerPickerLabel: 'Konfigureret e-mailudbyder',
            providerPickerHint: 'AesirX sender aldrig på dine vegne. Vælg en af dine konfigurerede udbydere nedenfor.',
            providerPickerEmpty: 'Ingen aktive udbydere er konfigureret. Konfigurér én på siden E-mailudbydere, før du sender.',
            providerPickerEmptyCta: 'Åbn E-mailudbydere',
            draftPickerLabel: 'E-mailudkast',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Der er endnu ikke skrevet udkast til denne pakke. Skriv først ét på udkastssiden.',
            recipientToLabel: 'Til',
            recipientToHint: 'En eller flere e-mailadresser, adskilt med komma.',
            recipientCcLabel: 'Cc (kommasepareret)',
            recipientCcHint: 'Valgfrit. Kommasepareret liste over e-mailadresser.',
            recipientBccLabel: 'Bcc (kommasepareret)',
            recipientBccHint: 'Valgfrit. Kommasepareret liste over e-mailadresser.',
            consentReaffirmHeading: 'Bekræft udbydersamtykke igen',
            submitCta: 'Send nu',
            sending: 'Sender…',
            cancelCta: 'Annullér',
            sendSuccess: 'Afsendelse registreret. Pakken er sendt via din konfigurerede udbyder.',
            sendErrorGeneric: 'Pakken kunne ikke sendes. Gennemgå fejlen nedenfor, og prøv igen.',
            sendErrorAlreadyFinalized: 'Dette udkast er allerede brugt til en vellykket afsendelse. Skriv et nyt udkast, før du sender igen.',
            sendErrorInvalidRecipients: 'En eller flere modtageradresser er ikke gyldige e-mailpostkasser.',
            // Timeline
            timelineEmpty: 'Ingen afsendelser er registreret endnu. Brug "Send via konfigureret udbyder" ovenfor for at sende via din kundestyrede SMTP / Microsoft 365 / Google Workspace-udbyder.',
            timelineProviderLabel: 'Sendt via din konfigurerede udbyder',
            timelineSentAtLabel: 'Sendt',
            timelineRecipientLabel: 'Modtager',
            timelineOutcomeSent: 'Sendt',
            timelineOutcomeFailed: 'Mislykkedes',
            timelineViewDetailsCta: 'Vis detaljer',
            // Authority-response chips
            authorityChipNone: 'Afventer svar',
            authorityChipAcknowledgement: 'Kvittering',
            authorityChipAcceptance: 'Godkendelse',
            authorityChipRejection: 'Afvisning',
            authorityChipSupplementRequest: 'Anmodning om supplement',
            authorityChipNoResponse: 'Intet svar',
            // Hard-copy chips
            hardCopyChipNone: 'Ingen fysisk opfølgning',
            hardCopyChipPending: 'Afsendt — levering afventer',
            hardCopyChipDelivered: 'Leveret',
            // Drawer
            drawerTitle: 'Afsendelsesregistrering',
            drawerEnvelopeHeading: 'Konvolut',
            drawerEnvelopeFromLabel: 'Fra',
            drawerEnvelopeToLabel: 'Til',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Bcc',
            drawerEnvelopeSubjectLabel: 'Emne',
            drawerEnvelopeMessageIdLabel: 'Besked-ID',
            drawerOutcomeHeading: 'Resultat',
            drawerSnapshotHeading: 'Øjebliksbillede af forvaringskæde',
            drawerSnapshotPackShaLabel: 'Pakke-SHA-256 ved afsendelse',
            drawerSnapshotDraftVersionLabel: 'Udkastversion ved afsendelse',
            drawerSnapshotAttachmentLabel: 'Sendt ZIP',
            drawerSnapshotAttachmentDownloadCta: 'Download ZIP',
            drawerAuthorityResponseHeading: 'Myndighedssvar',
            drawerHardCopyHeading: 'Fysisk opfølgning',
            drawerSentByLabel: 'Sendt af',
            drawerSendIdLabel: 'Afsendelses-id',
            drawerSmtpResponseLabel: 'SMTP-svar',
            drawerFailureReasonLabel: 'Fejlårsag',
            drawerLatencyLabel: 'Latens',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Accepteret',
            drawerRejectedLabel: 'Afvist',
            drawerCloseCta: 'Luk',
            // Authority-response panel
            authorityRecordCta: 'Registrér svar',
            authorityReclassifyCta: 'Omklassificér',
            authorityClassificationLabel: 'Klassifikation',
            authorityReceivedAtLabel: 'Modtaget den',
            authorityFreeTextLabel: 'Noter (valgfrit)',
            authorityEvidenceLabel: 'Bevisfil (.eml / .pdf / .png / .jpg — valgfrit)',
            authorityReasonLabel: 'Årsag til omklassificering',
            authorityHistoryHeading: 'Historik for omklassificering',
            authorityHistoryEmpty: 'Ingen omklassificeringer registreret.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Gem svar',
            authoritySubmitting: 'Gemmer…',
            authorityErrorInvalidMime: 'Bevisfiltype ikke tilladt. Brug .eml, .pdf, .png eller .jpg.',
            authorityErrorGeneric: 'Svaret kunne ikke gemmes. Prøv igen.',
            authorityRecordedAtLabel: 'Registreret den',
            authorityRecordedByLabel: 'Registreret af',
            authorityEvidenceFilenameLabel: 'Bevis',
            authorityEvidenceDownloadCta: 'Hent bevis',
            // Hard-copy panel
            hardCopyRecordCta: 'Log fysisk opfølgning',
            hardCopyConfirmCta: 'Bekræft levering',
            hardCopyEditNotesCta: 'Redigér leveringsnoter',
            hardCopyPostedAtLabel: 'Afsendt den',
            hardCopyCourierLabel: 'Kurer',
            hardCopyTrackingLabel: 'Sporingsnummer',
            hardCopyRecipientLabel: 'Modtager på etiketten',
            hardCopyReceiptLabel: 'Kvitteringsbevis (.pdf / .png / .jpg — valgfrit)',
            hardCopyReceiptDownloadCta: 'Hent kvittering',
            hardCopyDeliveredAtLabel: 'Leveret den',
            hardCopyDeliveryNotesLabel: 'Leveringsnoter (valgfrit)',
            hardCopySubmitCta: 'Gem opfølgning',
            hardCopySubmitting: 'Gemmer…',
            hardCopyErrorInvalidMime: 'Kvitteringsfiltype ikke tilladt. Brug .pdf, .png eller .jpg.',
            hardCopyErrorGeneric: 'Opfølgningen kunne ikke gemmes. Prøv igen.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    // Sprint 62.47.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    product_identification: {
        common: {
            retry: 'Retry',
            loading: 'Loading…',
            cancel: 'Cancel',
            close: 'Close',
            no_access_title: 'No access',
            no_access_message: 'Your role cannot read this register.',
            install_direct: 'Installed Direct',
            install_reference: 'Installed for reference',
            reference_mode_body: 'The overlay that declares this register is installed for reference. Scope determinations can still be recorded and read here, and the sector watch read; no duty and no clock is run from them until the overlay is installed Direct from the Overlay Catalog.',
        },
        participation: {
            compulsory: 'Compulsory',
            voluntary: 'Voluntary',
            outside_scope: 'Outside the scope',
            compulsory_rules_disapplied: 'Compulsory rules disapplied',
            not_determined: 'Open determination',
            unclassified: 'No class declared',
        },
        scope: {
            title: 'Product Scope Determinations',
            subtitle: 'A person’s recorded reading, per product line, of whether the identification and traceability regime reaches it, with the instrument relied on and a second person’s review. This register records the reading; it never decides it.',
            page_guide: 'Record one determination per product line from the options the installed pack declares. Nothing is preselected or suggested. Recording again keeps the earlier version, and compulsory and voluntary lines are always counted separately.',
            load_failed: 'The scope determinations could not be loaded.',
            none_declared: 'No installed framework declares the scope-determination register.',
            applicability_heading: 'Who the regime reaches, as the installed pack states it',
            mandatory_scope: 'Compulsory',
            voluntary_scope: 'Voluntary',
            out_of_scope: 'Outside the scope',
            mandatory_rules_disapplied: 'Compulsory rules disapplied',
            list_status_note: 'Status of the lists',
            counts_heading: 'Product lines by class',
            filter_label: 'Class',
            filter_all: 'All classes',
            table_caption: 'Current determination per product line',
            col_product: 'Product line',
            col_class: 'Class',
            col_option: 'Determination',
            col_instrument: 'Instrument relied on',
            col_determined_by: 'Determined by',
            col_reviewed_by: 'Reviewed by',
            col_determined_on: 'Determined on',
            col_version: 'Version',
            col_actions: 'Actions',
            empty: 'No determination is recorded yet.',
            empty_filtered: 'No product line is in this class.',
            record_button: 'Record a determination',
            record_again: 'Record again',
            history_button: 'History',
            duties_button: 'Duties',
            form_title: 'Record a scope determination',
            field_product: 'Product line',
            field_framework: 'Framework',
            choose: 'Choose…',
            no_default_note: 'Nothing is preselected and nothing is suggested: the reading is yours.',
            instrument_required: 'Required for the determination chosen.',
            cited_basis_note_heading: 'The installed pack’s reading of the lists',
            reviewer_same: 'The reviewer must be a person other than the one who determined it.',
            field_required: 'This field is required.',
            save: 'Save',
            saving: 'Saving…',
            save_failed: 'The determination could not be saved.',
            saved: 'Determination recorded.',
            products_failed: 'The product lines could not be loaded.',
            no_products: 'No product line is in the product register yet.',
            conflict: 'Someone recorded a determination for this line meanwhile. Reload the register and try again.',
            history_title: 'Determination history — {product}',
            history_failed: 'The history could not be loaded.',
            status_current: 'Current',
            status_superseded: 'Superseded',
            duties_title: 'Duties for {product}',
            duties_failed: 'The duties could not be loaded.',
            duties_no_task: "No task or deadline is created here from these duties. Where the decree states a period, the requirement's own text states it.",
            duties_reference: 'Installed for reference: no duty is shown. Install the overlay Direct to run them.',
            duties_compulsory_heading: 'Compulsory',
            duties_voluntary_heading: 'Voluntary',
            duties_role_heading: 'Attached to a role',
            duties_open: 'Open determination: the scope of this line is still to be determined. This is not a finding that no duty applies.',
            duties_counts: 'Compulsory: {applies} · Applies to how you take part (voluntary): {voluntary}',
            duties_none: 'No requirement is declared.',
            col_requirement: 'Requirement',
            col_state: 'Status for this line',
            col_reference: 'Reference',
        },
        duty_state: {
            applies: 'Applies (compulsory)',
            applies_voluntarily: 'Applies to how you take part (voluntary)',
            not_raised_by_option: 'Not raised by this determination',
            open_determination: 'Open determination',
            role_attached: 'Attached to a role; read its condition',
            voluntary_practice: 'Voluntary practice',
        },
        watch: {
            title: 'Sector Watch',
            subtitle: 'What the installed pack tracks as still to be issued by an authority, with the status it records and when it must be checked again, and the clocks the pack places on authorities. Nothing here is owed by your organisation.',
            page_guide: 'Read each tracked output as the installed pack states it. A recheck date that has passed reads as recheck overdue; nothing is re-dated. The clocks on authorities run on the authority, not on your organisation.',
            load_failed: 'The sector watch could not be loaded.',
            items_heading: 'Outputs still to be issued',
            items_caption: 'Tracked outputs, as the installed pack states them',
            col_output: 'Output',
            col_status: 'Status',
            col_basis: 'Basis of the status',
            col_checked: 'Checked on',
            col_recheck: 'Recheck due',
            col_install: 'Install mode',
            status_not_issued: 'Not issued',
            status_not_retrieved: 'Named, text not retrieved',
            status_announced_operational: 'Announced operational',
            status_issued: 'Issued',
            recheck_overdue: 'Recheck overdue',
            existing_basis: 'Instruments recorded as the existing basis',
            note_label: 'Note',
            items_none: 'No installed framework tracks an output for this register.',
            clocks_heading: 'Clocks on authorities and periods not stated',
            clocks_caption: 'Records the installed pack places on authorities, or whose period it does not state',
            col_clock: 'Record',
            col_kind: 'What it means for your organisation',
            col_period: 'Period as stated',
            col_binds: 'Applies from',
            authority_clock: 'Clock on the ministry — nothing is owed by the organisation',
            set_by_instrument: 'Set by the ministry instrument or sector law',
            no_period: 'No period stated',
            period_months: '{n} months',
            period_days: '{n} days',
            period_other: '{n} ({unit})',
            not_stated: 'Not stated',
            clocks_none: 'No clock on an authority is declared.',
        },
    },
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Færdige rapporter og meddelelser til sagen',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=da.js.map